import Vue from 'vue';
import { computed, ref, inject as inject$1, onMounted, provide as provide$1, watch, toRaw, reactive, toRef, watchEffect, unref, del, set, readonly, toRefs, nextTick, onUnmounted, defineComponent } from 'vue-demi';
import { raf, useIntersectionRef, useConvertCentValue, fileTypeGroups } from '@teamwork/use';
import { until, debouncedWatch, useIntersectionObserver, useEventListener } from '@vueuse/core';
import Draggable from 'vuedraggable';
import Uppy from '@uppy/core';
import { Dashboard } from '@uppy/vue';
import DropTarget from '@uppy/drop-target';
import ImageEditor from '@uppy/image-editor';
import AwsS3 from '@uppy/aws-s3';
import VueFormulate from '@braid/vue-formulate';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const Themeable = Vue.extend().extend({
  name: "themeable",
  provide() {
    return {
      theme: this.themeableProvide
    };
  },
  inject: {
    theme: {
      default: {
        isDark: false
      }
    }
  },
  props: {
    dark: {
      type: Boolean,
      default: null
    },
    light: {
      type: Boolean,
      default: null
    }
  },
  data() {
    return {
      themeableProvide: {
        isDark: false
      }
    };
  },
  computed: {
    appIsDark() {
      return this.$vuetify.theme.dark || false;
    },
    isDark() {
      if (this.dark === true) {
        return true;
      } else if (this.light === true) {
        return false;
      } else {
        return this.theme.isDark;
      }
    },
    themeClasses() {
      return {
        "theme--dark": this.isDark,
        "theme--light": !this.isDark
      };
    },
    rootIsDark() {
      if (this.dark === true) {
        return true;
      } else if (this.light === true) {
        return false;
      } else {
        return this.appIsDark;
      }
    },
    rootThemeClasses() {
      return {
        "theme--dark": this.rootIsDark,
        "theme--light": !this.rootIsDark
      };
    }
  },
  watch: {
    isDark: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.themeableProvide.isDark = this.isDark;
        }
      },
      immediate: true
    }
  }
});
var Themeable$1 = Themeable;
function functionalThemeClasses(context) {
  const vm = __spreadValues(__spreadValues({}, context.props), context.injections);
  const isDark = Themeable.options.computed.isDark.call(vm);
  return Themeable.options.computed.themeClasses.call({
    isDark
  });
}
function mixins(...args) {
  return Vue.extend({
    mixins: args
  });
}
var VApp = mixins(Themeable$1).extend({
  name: "v-app",
  props: {
    dark: {
      type: Boolean,
      default: void 0
    },
    id: {
      type: String,
      default: "app"
    },
    light: {
      type: Boolean,
      default: void 0
    }
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    }
  },
  beforeCreate() {
    if (!this.$vuetify || this.$vuetify === this.$root) {
      throw new Error("Vuetify is not properly initialized, see https://vuetifyjs.com/getting-started/quick-start#bootstrapping-the-vuetify-object");
    }
  },
  render(h) {
    const wrapper = h("div", {
      staticClass: "v-application--wrap"
    }, this.$slots.default);
    return h("div", {
      staticClass: "v-application",
      class: __spreadValues({
        "v-application--is-rtl": this.$vuetify.rtl,
        "v-application--is-ltr": !this.$vuetify.rtl
      }, this.themeClasses),
      attrs: {
        "data-app": true
      },
      domProps: {
        id: this.id
      }
    }, [wrapper]);
  }
});
function makeWatcher(property) {
  return function(val, oldVal) {
    for (const attr in oldVal) {
      if (!Object.prototype.hasOwnProperty.call(val, attr)) {
        this.$delete(this.$data[property], attr);
      }
    }
    for (const attr in val) {
      this.$set(this.$data[property], attr, val[attr]);
    }
  };
}
var BindsAttrs = Vue.extend({
  data: () => ({
    attrs$: {},
    listeners$: {}
  }),
  created() {
    this.$watch("$attrs", makeWatcher("attrs$"), {
      immediate: true
    });
    this.$watch("$listeners", makeWatcher("listeners$"), {
      immediate: true
    });
  }
});
function install(Vue$1, args = {}) {
  if (install.installed)
    return;
  install.installed = true;
  if (Vue !== Vue$1) {
    consoleError(`Multiple instances of Vue detected
See https://github.com/vuetifyjs/vuetify/issues/4068

If you're seeing "$attrs is readonly", it's caused by this`);
  }
  const components = args.components || {};
  const directives = args.directives || {};
  for (const name in directives) {
    const directive2 = directives[name];
    Vue$1.directive(name, directive2);
  }
  (function registerComponents(components2) {
    if (components2) {
      for (const key in components2) {
        const component = components2[key];
        if (component && !registerComponents(component.$_vuetify_subcomponents)) {
          Vue$1.component(key, component);
        }
      }
      return true;
    }
    return false;
  })(components);
  if (Vue$1.$_vuetify_installed)
    return;
  Vue$1.$_vuetify_installed = true;
  Vue$1.mixin({
    beforeCreate() {
      const options2 = this.$options;
      if (options2.vuetify) {
        options2.vuetify.init(this, this.$ssrContext);
        this.$vuetify = Vue$1.observable(options2.vuetify.framework);
      } else {
        this.$vuetify = options2.parent && options2.parent.$vuetify || this;
      }
    },
    beforeMount() {
      if (this.$options.vuetify && this.$el && this.$el.hasAttribute("data-server-rendered")) {
        this.$vuetify.isHydrating = true;
        this.$vuetify.breakpoint.update(true);
      }
    },
    mounted() {
      if (this.$options.vuetify && this.$vuetify.isHydrating) {
        this.$vuetify.isHydrating = false;
        this.$vuetify.breakpoint.update();
      }
    }
  });
}
class Service {
  constructor() {
    this.framework = {};
  }
  init(root, ssrContext) {
  }
}
class Application extends Service {
  constructor() {
    super(...arguments);
    this.bar = 0;
    this.top = 0;
    this.left = 0;
    this.insetFooter = 0;
    this.right = 0;
    this.bottom = 0;
    this.footer = 0;
    this.application = {
      bar: {},
      top: {},
      left: {},
      insetFooter: {},
      right: {},
      bottom: {},
      footer: {}
    };
  }
  register(uid, location, size) {
    this.application[location] = {
      [uid]: size
    };
    this.update(location);
  }
  unregister(uid, location) {
    if (this.application[location][uid] == null)
      return;
    delete this.application[location][uid];
    this.update(location);
  }
  update(location) {
    this[location] = Object.values(this.application[location]).reduce((acc, cur) => acc + cur, 0);
  }
}
Application.property = "application";
class Breakpoint extends Service {
  constructor(preset2) {
    super();
    this.xs = false;
    this.sm = false;
    this.md = false;
    this.lg = false;
    this.xl = false;
    this.xsOnly = false;
    this.smOnly = false;
    this.smAndDown = false;
    this.smAndUp = false;
    this.mdOnly = false;
    this.mdAndDown = false;
    this.mdAndUp = false;
    this.lgOnly = false;
    this.lgAndDown = false;
    this.lgAndUp = false;
    this.xlOnly = false;
    this.name = "xs";
    this.height = 0;
    this.width = 0;
    this.mobile = true;
    this.resizeTimeout = 0;
    const {
      mobileBreakpoint,
      scrollBarWidth,
      thresholds
    } = preset2[Breakpoint.property];
    this.mobileBreakpoint = mobileBreakpoint;
    this.scrollBarWidth = scrollBarWidth;
    this.thresholds = thresholds;
  }
  init() {
    this.update();
    if (typeof window === "undefined")
      return;
    window.addEventListener("resize", this.onResize.bind(this), {
      passive: true
    });
  }
  update(ssr = false) {
    const height = ssr ? 0 : this.getClientHeight();
    const width = ssr ? 0 : this.getClientWidth();
    const xs = width < this.thresholds.xs;
    const sm = width < this.thresholds.sm && !xs;
    const md2 = width < this.thresholds.md - this.scrollBarWidth && !(sm || xs);
    const lg = width < this.thresholds.lg - this.scrollBarWidth && !(md2 || sm || xs);
    const xl = width >= this.thresholds.lg - this.scrollBarWidth;
    this.height = height;
    this.width = width;
    this.xs = xs;
    this.sm = sm;
    this.md = md2;
    this.lg = lg;
    this.xl = xl;
    this.xsOnly = xs;
    this.smOnly = sm;
    this.smAndDown = (xs || sm) && !(md2 || lg || xl);
    this.smAndUp = !xs && (sm || md2 || lg || xl);
    this.mdOnly = md2;
    this.mdAndDown = (xs || sm || md2) && !(lg || xl);
    this.mdAndUp = !(xs || sm) && (md2 || lg || xl);
    this.lgOnly = lg;
    this.lgAndDown = (xs || sm || md2 || lg) && !xl;
    this.lgAndUp = !(xs || sm || md2) && (lg || xl);
    this.xlOnly = xl;
    switch (true) {
      case xs:
        this.name = "xs";
        break;
      case sm:
        this.name = "sm";
        break;
      case md2:
        this.name = "md";
        break;
      case lg:
        this.name = "lg";
        break;
      default:
        this.name = "xl";
        break;
    }
    if (typeof this.mobileBreakpoint === "number") {
      this.mobile = width < parseInt(this.mobileBreakpoint, 10);
      return;
    }
    const breakpoints2 = {
      xs: 0,
      sm: 1,
      md: 2,
      lg: 3,
      xl: 4
    };
    const current = breakpoints2[this.name];
    const max = breakpoints2[this.mobileBreakpoint];
    this.mobile = current <= max;
  }
  onResize() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = window.setTimeout(this.update.bind(this), 200);
  }
  getClientWidth() {
    if (typeof document === "undefined")
      return 0;
    return Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
  }
  getClientHeight() {
    if (typeof document === "undefined")
      return 0;
    return Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
  }
}
Breakpoint.property = "breakpoint";
const linear = (t) => t;
const easeInQuad = (t) => t ** 2;
const easeOutQuad = (t) => t * (2 - t);
const easeInOutQuad = (t) => t < 0.5 ? 2 * t ** 2 : -1 + (4 - 2 * t) * t;
const easeInCubic = (t) => t ** 3;
const easeOutCubic = (t) => --t ** 3 + 1;
const easeInOutCubic = (t) => t < 0.5 ? 4 * t ** 3 : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
const easeInQuart = (t) => t ** 4;
const easeOutQuart = (t) => 1 - --t ** 4;
const easeInOutQuart = (t) => t < 0.5 ? 8 * t * t * t * t : 1 - 8 * --t * t * t * t;
const easeInQuint = (t) => t ** 5;
const easeOutQuint = (t) => 1 + --t ** 5;
const easeInOutQuint = (t) => t < 0.5 ? 16 * t ** 5 : 1 + 16 * --t ** 5;
var easingPatterns = /* @__PURE__ */ Object.freeze({
  __proto__: null,
  [Symbol.toStringTag]: "Module",
  linear,
  easeInQuad,
  easeOutQuad,
  easeInOutQuad,
  easeInCubic,
  easeOutCubic,
  easeInOutCubic,
  easeInQuart,
  easeOutQuart,
  easeInOutQuart,
  easeInQuint,
  easeOutQuint,
  easeInOutQuint
});
function getOffset(target) {
  if (typeof target === "number") {
    return target;
  }
  let el = $(target);
  if (!el) {
    throw typeof target === "string" ? new Error(`Target element "${target}" not found.`) : new TypeError(`Target must be a Number/Selector/HTMLElement/VueComponent, received ${type(target)} instead.`);
  }
  let totalOffset = 0;
  while (el) {
    totalOffset += el.offsetTop;
    el = el.offsetParent;
  }
  return totalOffset;
}
function getContainer(container) {
  const el = $(container);
  if (el)
    return el;
  throw typeof container === "string" ? new Error(`Container element "${container}" not found.`) : new TypeError(`Container must be a Selector/HTMLElement/VueComponent, received ${type(container)} instead.`);
}
function type(el) {
  return el == null ? el : el.constructor.name;
}
function $(el) {
  if (typeof el === "string") {
    return document.querySelector(el);
  } else if (el && el._isVue) {
    return el.$el;
  } else if (el instanceof HTMLElement) {
    return el;
  } else {
    return null;
  }
}
function goTo(_target, _settings = {}) {
  const settings = __spreadValues({
    container: document.scrollingElement || document.body || document.documentElement,
    duration: 500,
    offset: 0,
    easing: "easeInOutCubic",
    appOffset: true
  }, _settings);
  const container = getContainer(settings.container);
  if (settings.appOffset && goTo.framework.application) {
    const isDrawer = container.classList.contains("v-navigation-drawer");
    const isClipped = container.classList.contains("v-navigation-drawer--clipped");
    const {
      bar,
      top
    } = goTo.framework.application;
    settings.offset += bar;
    if (!isDrawer || isClipped)
      settings.offset += top;
  }
  const startTime = performance.now();
  let targetLocation;
  if (typeof _target === "number") {
    targetLocation = getOffset(_target) - settings.offset;
  } else {
    targetLocation = getOffset(_target) - getOffset(container) - settings.offset;
  }
  const startLocation = container.scrollTop;
  if (targetLocation === startLocation)
    return Promise.resolve(targetLocation);
  const ease = typeof settings.easing === "function" ? settings.easing : easingPatterns[settings.easing];
  if (!ease)
    throw new TypeError(`Easing function "${settings.easing}" not found.`);
  return new Promise((resolve) => requestAnimationFrame(function step(currentTime) {
    const timeElapsed = currentTime - startTime;
    const progress = Math.abs(settings.duration ? Math.min(timeElapsed / settings.duration, 1) : 1);
    container.scrollTop = Math.floor(startLocation + (targetLocation - startLocation) * ease(progress));
    const clientHeight = container === document.body ? document.documentElement.clientHeight : container.clientHeight;
    if (progress === 1 || clientHeight + container.scrollTop === container.scrollHeight) {
      return resolve(targetLocation);
    }
    requestAnimationFrame(step);
  }));
}
goTo.framework = {};
goTo.init = () => {
};
class Goto extends Service {
  constructor() {
    super();
    return goTo;
  }
}
Goto.property = "goTo";
function createSimpleFunctional(c, el = "div", name) {
  return Vue.extend({
    name: name || c.replace(/__/g, "-"),
    functional: true,
    render(h, {
      data,
      children
    }) {
      data.staticClass = `${c} ${data.staticClass || ""}`.trim();
      return h(el, data, children);
    }
  });
}
function addOnceEventListener(el, eventName, cb, options2 = false) {
  var once = (event) => {
    cb(event);
    el.removeEventListener(eventName, once, options2);
  };
  el.addEventListener(eventName, once, options2);
}
let passiveSupported = false;
try {
  if (typeof window !== "undefined") {
    const testListenerOpts = Object.defineProperty({}, "passive", {
      get: () => {
        passiveSupported = true;
      }
    });
    window.addEventListener("testListener", testListenerOpts, testListenerOpts);
    window.removeEventListener("testListener", testListenerOpts, testListenerOpts);
  }
} catch (e) {
  console.warn(e);
}
function addPassiveEventListener(el, event, cb, options2) {
  el.addEventListener(event, cb, passiveSupported ? options2 : false);
}
function getNestedValue(obj, path, fallback2) {
  const last = path.length - 1;
  if (last < 0)
    return obj === void 0 ? fallback2 : obj;
  for (let i = 0; i < last; i++) {
    if (obj == null) {
      return fallback2;
    }
    obj = obj[path[i]];
  }
  if (obj == null)
    return fallback2;
  return obj[path[last]] === void 0 ? fallback2 : obj[path[last]];
}
function deepEqual(a, b) {
  if (a === b)
    return true;
  if (a instanceof Date && b instanceof Date && a.getTime() !== b.getTime()) {
    return false;
  }
  if (a !== Object(a) || b !== Object(b)) {
    return false;
  }
  const props2 = Object.keys(a);
  if (props2.length !== Object.keys(b).length) {
    return false;
  }
  return props2.every((p) => deepEqual(a[p], b[p]));
}
function getObjectValueByPath(obj, path, fallback2) {
  if (obj == null || !path || typeof path !== "string")
    return fallback2;
  if (obj[path] !== void 0)
    return obj[path];
  path = path.replace(/\[(\w+)\]/g, ".$1");
  path = path.replace(/^\./, "");
  return getNestedValue(obj, path.split("."), fallback2);
}
function getPropertyFromItem(item, property, fallback2) {
  if (property == null)
    return item === void 0 ? fallback2 : item;
  if (item !== Object(item))
    return fallback2 === void 0 ? item : fallback2;
  if (typeof property === "string")
    return getObjectValueByPath(item, property, fallback2);
  if (Array.isArray(property))
    return getNestedValue(item, property, fallback2);
  if (typeof property !== "function")
    return fallback2;
  const value = property(item, fallback2);
  return typeof value === "undefined" ? fallback2 : value;
}
function createRange(length) {
  return Array.from({
    length
  }, (v, k) => k);
}
function getZIndex(el) {
  if (!el || el.nodeType !== Node.ELEMENT_NODE)
    return 0;
  const index = +window.getComputedStyle(el).getPropertyValue("z-index");
  if (!index)
    return getZIndex(el.parentNode);
  return index;
}
const tagsToReplace = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;"
};
function escapeHTML(str) {
  return str.replace(/[&<>]/g, (tag) => tagsToReplace[tag] || tag);
}
function filterObjectOnKeys(obj, keys2) {
  const filtered = {};
  for (let i = 0; i < keys2.length; i++) {
    const key = keys2[i];
    if (typeof obj[key] !== "undefined") {
      filtered[key] = obj[key];
    }
  }
  return filtered;
}
function convertToUnit(str, unit = "px") {
  if (str == null || str === "") {
    return void 0;
  } else if (isNaN(+str)) {
    return String(str);
  } else {
    return `${Number(str)}${unit}`;
  }
}
function kebabCase(str) {
  return (str || "").replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
}
function isObject$1(obj) {
  return obj !== null && typeof obj === "object";
}
const keyCodes = Object.freeze({
  enter: 13,
  tab: 9,
  delete: 46,
  esc: 27,
  space: 32,
  up: 38,
  down: 40,
  left: 37,
  right: 39,
  end: 35,
  home: 36,
  del: 46,
  backspace: 8,
  insert: 45,
  pageup: 33,
  pagedown: 34
});
function remapInternalIcon(vm, iconName) {
  const component = vm.$vuetify.icons.component;
  if (iconName.startsWith("$")) {
    const iconPath = `$vuetify.icons.values.${iconName.split("$").pop().split(".").pop()}`;
    const override = getObjectValueByPath(vm, iconPath, iconName);
    if (typeof override === "string")
      iconName = override;
    else
      return override;
  }
  if (component == null) {
    return iconName;
  }
  return {
    component,
    props: {
      icon: iconName
    }
  };
}
function keys$1(o) {
  return Object.keys(o);
}
const camelizeRE = /-(\w)/g;
const camelize = (str) => {
  return str.replace(camelizeRE, (_, c) => c ? c.toUpperCase() : "");
};
function arrayDiff(a, b) {
  const diff = [];
  for (let i = 0; i < b.length; i++) {
    if (a.indexOf(b[i]) < 0)
      diff.push(b[i]);
  }
  return diff;
}
function upperFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
function groupItems(items, groupBy, groupDesc) {
  const key = groupBy[0];
  const groups = [];
  let current;
  for (var i = 0; i < items.length; i++) {
    const item = items[i];
    const val = getObjectValueByPath(item, key, null);
    if (current !== val) {
      var _val;
      current = val;
      groups.push({
        name: (_val = val) != null ? _val : "",
        items: []
      });
    }
    groups[groups.length - 1].items.push(item);
  }
  return groups;
}
function wrapInArray(v) {
  return v != null ? Array.isArray(v) ? v : [v] : [];
}
function sortItems(items, sortBy, sortDesc, locale, customSorters) {
  if (sortBy === null || !sortBy.length)
    return items;
  const stringCollator = new Intl.Collator(locale, {
    sensitivity: "accent",
    usage: "sort"
  });
  return items.sort((a, b) => {
    for (let i = 0; i < sortBy.length; i++) {
      const sortKey = sortBy[i];
      let sortA = getObjectValueByPath(a, sortKey);
      let sortB = getObjectValueByPath(b, sortKey);
      if (sortDesc[i]) {
        [sortA, sortB] = [sortB, sortA];
      }
      if (customSorters && customSorters[sortKey]) {
        const customResult = customSorters[sortKey](sortA, sortB);
        if (!customResult)
          continue;
        return customResult;
      }
      if (sortA === null && sortB === null) {
        continue;
      }
      [sortA, sortB] = [sortA, sortB].map((s) => (s || "").toString().toLocaleLowerCase());
      if (sortA !== sortB) {
        if (!isNaN(sortA) && !isNaN(sortB))
          return Number(sortA) - Number(sortB);
        return stringCollator.compare(sortA, sortB);
      }
    }
    return 0;
  });
}
function defaultFilter(value, search, item) {
  return value != null && search != null && typeof value !== "boolean" && value.toString().toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;
}
function searchItems(items, search) {
  if (!search)
    return items;
  search = search.toString().toLowerCase();
  if (search.trim() === "")
    return items;
  return items.filter((item) => Object.keys(item).some((key) => defaultFilter(getObjectValueByPath(item, key), search)));
}
function getSlotType(vm, name, split) {
  if (vm.$slots[name] && vm.$scopedSlots[name] && vm.$scopedSlots[name].name) {
    return split ? "v-slot" : "scoped";
  }
  if (vm.$slots[name])
    return "normal";
  if (vm.$scopedSlots[name])
    return "scoped";
}
function debounce(fn, delay) {
  let timeoutId = 0;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}
function throttle(fn, limit) {
  let throttling = false;
  return (...args) => {
    if (!throttling) {
      throttling = true;
      setTimeout(() => throttling = false, limit);
      return fn(...args);
    }
  };
}
function getPrefixedScopedSlots(prefix, scopedSlots) {
  return Object.keys(scopedSlots).filter((k) => k.startsWith(prefix)).reduce((obj, k) => {
    obj[k.replace(prefix, "")] = scopedSlots[k];
    return obj;
  }, {});
}
function getSlot(vm, name = "default", data, optional = false) {
  if (vm.$scopedSlots[name]) {
    return vm.$scopedSlots[name](data instanceof Function ? data() : data);
  } else if (vm.$slots[name] && (!data || optional)) {
    return vm.$slots[name];
  }
  return void 0;
}
function clamp(value, min = 0, max = 1) {
  return Math.max(min, Math.min(max, value));
}
function padEnd(str, length, char = "0") {
  return str + char.repeat(Math.max(0, length - str.length));
}
function chunk(str, size = 1) {
  const chunked = [];
  let index = 0;
  while (index < str.length) {
    chunked.push(str.substr(index, size));
    index += size;
  }
  return chunked;
}
function humanReadableFileSize(bytes, binary = false) {
  const base2 = binary ? 1024 : 1e3;
  if (bytes < base2) {
    return `${bytes} B`;
  }
  const prefix = binary ? ["Ki", "Mi", "Gi"] : ["k", "M", "G"];
  let unit = -1;
  while (Math.abs(bytes) >= base2 && unit < prefix.length - 1) {
    bytes /= base2;
    ++unit;
  }
  return `${bytes.toFixed(1)} ${prefix[unit]}B`;
}
function camelizeObjectKeys(obj) {
  if (!obj)
    return {};
  return Object.keys(obj).reduce((o, key) => {
    o[camelize(key)] = obj[key];
    return o;
  }, {});
}
function mergeDeep(source = {}, target = {}) {
  for (const key in target) {
    const sourceProperty = source[key];
    const targetProperty = target[key];
    if (isObject$1(sourceProperty) && isObject$1(targetProperty)) {
      source[key] = mergeDeep(sourceProperty, targetProperty);
      continue;
    }
    source[key] = targetProperty;
  }
  return source;
}
function fillArray(length, obj) {
  return Array(length).fill(obj);
}
const icons$4 = {
  complete: "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z",
  cancel: "M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z",
  close: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z",
  delete: "M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z",
  clear: "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z",
  success: "M12,2C17.52,2 22,6.48 22,12C22,17.52 17.52,22 12,22C6.48,22 2,17.52 2,12C2,6.48 6.48,2 12,2M11,16.5L18,9.5L16.59,8.09L11,13.67L7.91,10.59L6.5,12L11,16.5Z",
  info: "M13,9H11V7H13M13,17H11V11H13M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2Z",
  warning: "M11,4.5H13V15.5H11V4.5M13,17.5V19.5H11V17.5H13Z",
  error: "M13,14H11V10H13M13,18H11V16H13M1,21H23L12,2L1,21Z",
  prev: "M15.41,16.58L10.83,12L15.41,7.41L14,6L8,12L14,18L15.41,16.58Z",
  next: "M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z",
  checkboxOn: "M10,17L5,12L6.41,10.58L10,14.17L17.59,6.58L19,8M19,3H5C3.89,3 3,3.89 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.89 20.1,3 19,3Z",
  checkboxOff: "M19,3H5C3.89,3 3,3.89 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19Z",
  checkboxIndeterminate: "M17,13H7V11H17M19,3H5C3.89,3 3,3.89 3,5V19C3,20.1 3.9,21 5,21H19C20.1,21 21,20.1 21,19V5C21,3.89 20.1,3 19,3Z",
  delimiter: "M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2Z",
  sort: "M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z",
  expand: "M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z",
  menu: "M3,6H21V8H3V6M3,11H21V13H3V11M3,16H21V18H3V16Z",
  subgroup: "M7,10L12,15L17,10H7Z",
  dropdown: "M7,10L12,15L17,10H7Z",
  radioOn: "M12,20C7.58,20 4,16.42 4,12C4,7.58 7.58,4 12,4C16.42,4 20,7.58 20,12C20,16.42 16.42,20 12,20M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M12,7C9.24,7 7,9.24 7,12C7,14.76 9.24,17 12,17C14.76,17 17,14.76 17,12C17,9.24 14.76,7 12,7Z",
  radioOff: "M12,20C7.58,20 4,16.42 4,12C4,7.58 7.58,4 12,4C16.42,4 20,7.58 20,12C20,16.42 16.42,20 12,20M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2Z",
  edit: "M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z",
  ratingEmpty: "M12,15.39L8.24,17.66L9.23,13.38L5.91,10.5L10.29,10.13L12,6.09L13.71,10.13L18.09,10.5L14.77,13.38L15.76,17.66M22,9.24L14.81,8.63L12,2L9.19,8.63L2,9.24L7.45,13.97L5.82,21L12,17.27L18.18,21L16.54,13.97L22,9.24Z",
  ratingFull: "M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z",
  ratingHalf: "M12,15.4V6.1L13.71,10.13L18.09,10.5L14.77,13.39L15.76,17.67M22,9.24L14.81,8.63L12,2L9.19,8.63L2,9.24L7.45,13.97L5.82,21L12,17.27L18.18,21L16.54,13.97L22,9.24Z",
  loading: "M19,8L15,12H18C18,15.31 15.31,18 12,18C11,18 10.03,17.75 9.2,17.3L7.74,18.76C8.97,19.54 10.43,20 12,20C16.42,20 20,16.42 20,12H23M6,12C6,8.69 8.69,6 12,6C13,6 13.97,6.25 14.8,6.7L16.26,5.24C15.03,4.46 13.57,4 12,4C7.58,4 4,7.58 4,12H1L5,16L9,12",
  first: "M18.41,16.59L13.82,12L18.41,7.41L17,6L11,12L17,18L18.41,16.59M6,6H8V18H6V6Z",
  last: "M5.59,7.41L10.18,12L5.59,16.59L7,18L13,12L7,6L5.59,7.41M16,6H18V18H16V6Z",
  unfold: "M12,18.17L8.83,15L7.42,16.41L12,21L16.59,16.41L15.17,15M12,5.83L15.17,9L16.58,7.59L12,3L7.41,7.59L8.83,9L12,5.83Z",
  file: "M16.5,6V17.5C16.5,19.71 14.71,21.5 12.5,21.5C10.29,21.5 8.5,19.71 8.5,17.5V5C8.5,3.62 9.62,2.5 11,2.5C12.38,2.5 13.5,3.62 13.5,5V15.5C13.5,16.05 13.05,16.5 12.5,16.5C11.95,16.5 11.5,16.05 11.5,15.5V6H10V15.5C10,16.88 11.12,18 12.5,18C13.88,18 15,16.88 15,15.5V5C15,2.79 13.21,1 11,1C8.79,1 7,2.79 7,5V17.5C7,20.54 9.46,23 12.5,23C15.54,23 18,20.54 18,17.5V6H16.5Z",
  plus: "M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z",
  minus: "M19,13H5V11H19V13Z"
};
var mdiSvg = icons$4;
const icons$3 = {
  complete: "check",
  cancel: "cancel",
  close: "close",
  delete: "cancel",
  clear: "clear",
  success: "check_circle",
  info: "info",
  warning: "priority_high",
  error: "warning",
  prev: "chevron_left",
  next: "chevron_right",
  checkboxOn: "check_box",
  checkboxOff: "check_box_outline_blank",
  checkboxIndeterminate: "indeterminate_check_box",
  delimiter: "fiber_manual_record",
  sort: "arrow_upward",
  expand: "keyboard_arrow_down",
  menu: "menu",
  subgroup: "arrow_drop_down",
  dropdown: "arrow_drop_down",
  radioOn: "radio_button_checked",
  radioOff: "radio_button_unchecked",
  edit: "edit",
  ratingEmpty: "star_border",
  ratingFull: "star",
  ratingHalf: "star_half",
  loading: "cached",
  first: "first_page",
  last: "last_page",
  unfold: "unfold_more",
  file: "attach_file",
  plus: "add",
  minus: "remove"
};
var md = icons$3;
const icons$2 = {
  complete: "mdi-check",
  cancel: "mdi-close-circle",
  close: "mdi-close",
  delete: "mdi-close-circle",
  clear: "mdi-close",
  success: "mdi-check-circle",
  info: "mdi-information",
  warning: "mdi-exclamation",
  error: "mdi-alert",
  prev: "mdi-chevron-left",
  next: "mdi-chevron-right",
  checkboxOn: "mdi-checkbox-marked",
  checkboxOff: "mdi-checkbox-blank-outline",
  checkboxIndeterminate: "mdi-minus-box",
  delimiter: "mdi-circle",
  sort: "mdi-arrow-up",
  expand: "mdi-chevron-down",
  menu: "mdi-menu",
  subgroup: "mdi-menu-down",
  dropdown: "mdi-menu-down",
  radioOn: "mdi-radiobox-marked",
  radioOff: "mdi-radiobox-blank",
  edit: "mdi-pencil",
  ratingEmpty: "mdi-star-outline",
  ratingFull: "mdi-star",
  ratingHalf: "mdi-star-half-full",
  loading: "mdi-cached",
  first: "mdi-page-first",
  last: "mdi-page-last",
  unfold: "mdi-unfold-more-horizontal",
  file: "mdi-paperclip",
  plus: "mdi-plus",
  minus: "mdi-minus"
};
var mdi = icons$2;
const icons$1 = {
  complete: "fas fa-check",
  cancel: "fas fa-times-circle",
  close: "fas fa-times",
  delete: "fas fa-times-circle",
  clear: "fas fa-times-circle",
  success: "fas fa-check-circle",
  info: "fas fa-info-circle",
  warning: "fas fa-exclamation",
  error: "fas fa-exclamation-triangle",
  prev: "fas fa-chevron-left",
  next: "fas fa-chevron-right",
  checkboxOn: "fas fa-check-square",
  checkboxOff: "far fa-square",
  checkboxIndeterminate: "fas fa-minus-square",
  delimiter: "fas fa-circle",
  sort: "fas fa-sort-up",
  expand: "fas fa-chevron-down",
  menu: "fas fa-bars",
  subgroup: "fas fa-caret-down",
  dropdown: "fas fa-caret-down",
  radioOn: "far fa-dot-circle",
  radioOff: "far fa-circle",
  edit: "fas fa-edit",
  ratingEmpty: "far fa-star",
  ratingFull: "fas fa-star",
  ratingHalf: "fas fa-star-half",
  loading: "fas fa-sync",
  first: "fas fa-step-backward",
  last: "fas fa-step-forward",
  unfold: "fas fa-arrows-alt-v",
  file: "fas fa-paperclip",
  plus: "fas fa-plus",
  minus: "fas fa-minus"
};
var fa = icons$1;
const icons = {
  complete: "fa fa-check",
  cancel: "fa fa-times-circle",
  close: "fa fa-times",
  delete: "fa fa-times-circle",
  clear: "fa fa-times-circle",
  success: "fa fa-check-circle",
  info: "fa fa-info-circle",
  warning: "fa fa-exclamation",
  error: "fa fa-exclamation-triangle",
  prev: "fa fa-chevron-left",
  next: "fa fa-chevron-right",
  checkboxOn: "fa fa-check-square",
  checkboxOff: "fa fa-square-o",
  checkboxIndeterminate: "fa fa-minus-square",
  delimiter: "fa fa-circle",
  sort: "fa fa-sort-up",
  expand: "fa fa-chevron-down",
  menu: "fa fa-bars",
  subgroup: "fa fa-caret-down",
  dropdown: "fa fa-caret-down",
  radioOn: "fa fa-dot-circle-o",
  radioOff: "fa fa-circle-o",
  edit: "fa fa-pencil",
  ratingEmpty: "fa fa-star-o",
  ratingFull: "fa fa-star",
  ratingHalf: "fa fa-star-half-o",
  loading: "fa fa-refresh",
  first: "fa fa-step-backward",
  last: "fa fa-step-forward",
  unfold: "fa fa-angle-double-down",
  file: "fa fa-paperclip",
  plus: "fa fa-plus",
  minus: "fa fa-minus"
};
var fa4 = icons;
function convertToComponentDeclarations(component, iconSet) {
  const result = {};
  for (const key in iconSet) {
    result[key] = {
      component,
      props: {
        icon: iconSet[key].split(" fa-")
      }
    };
  }
  return result;
}
var faSvg = convertToComponentDeclarations("font-awesome-icon", fa);
var presets = Object.freeze({
  mdiSvg,
  md,
  mdi,
  fa,
  fa4,
  faSvg
});
class Icons extends Service {
  constructor(preset2) {
    super();
    const {
      iconfont,
      values,
      component
    } = preset2[Icons.property];
    this.component = component;
    this.iconfont = iconfont;
    this.values = mergeDeep(presets[iconfont], values);
  }
}
Icons.property = "icons";
const LANG_PREFIX = "$vuetify.";
const fallback = Symbol("Lang fallback");
function getTranslation(locale, key, usingDefault = false, defaultLocale) {
  const shortKey = key.replace(LANG_PREFIX, "");
  let translation = getObjectValueByPath(locale, shortKey, fallback);
  if (translation === fallback) {
    if (usingDefault) {
      consoleError(`Translation key "${shortKey}" not found in fallback`);
      translation = key;
    } else {
      consoleWarn(`Translation key "${shortKey}" not found, falling back to default`);
      translation = getTranslation(defaultLocale, key, true, defaultLocale);
    }
  }
  return translation;
}
class Lang extends Service {
  constructor(preset2) {
    super();
    this.defaultLocale = "en";
    const {
      current,
      locales,
      t
    } = preset2[Lang.property];
    this.current = current;
    this.locales = locales;
    this.translator = t || this.defaultTranslator;
  }
  currentLocale(key) {
    const translation = this.locales[this.current];
    const defaultLocale = this.locales[this.defaultLocale];
    return getTranslation(translation, key, false, defaultLocale);
  }
  t(key, ...params) {
    if (!key.startsWith(LANG_PREFIX))
      return this.replace(key, params);
    return this.translator(key, ...params);
  }
  defaultTranslator(key, ...params) {
    return this.replace(this.currentLocale(key), params);
  }
  replace(str, params) {
    return str.replace(/\{(\d+)\}/g, (match, index) => {
      return String(params[+index]);
    });
  }
}
Lang.property = "lang";
var en = {
  badge: "Badge",
  close: "Close",
  dataIterator: {
    noResultsText: "No matching records found",
    loadingText: "Loading items..."
  },
  dataTable: {
    itemsPerPageText: "Rows per page:",
    ariaLabel: {
      sortDescending: "Sorted descending.",
      sortAscending: "Sorted ascending.",
      sortNone: "Not sorted.",
      activateNone: "Activate to remove sorting.",
      activateDescending: "Activate to sort descending.",
      activateAscending: "Activate to sort ascending."
    },
    sortBy: "Sort by"
  },
  dataFooter: {
    itemsPerPageText: "Items per page:",
    itemsPerPageAll: "All",
    nextPage: "Next page",
    prevPage: "Previous page",
    firstPage: "First page",
    lastPage: "Last page",
    pageText: "{0}-{1} of {2}"
  },
  datePicker: {
    itemsSelected: "{0} selected",
    nextMonthAriaLabel: "Next month",
    nextYearAriaLabel: "Next year",
    prevMonthAriaLabel: "Previous month",
    prevYearAriaLabel: "Previous year"
  },
  noDataText: "No data available",
  carousel: {
    prev: "Previous visual",
    next: "Next visual",
    ariaLabel: {
      delimiter: "Carousel slide {0} of {1}"
    }
  },
  calendar: {
    moreEvents: "{0} more"
  },
  fileInput: {
    counter: "{0} files",
    counterSize: "{0} files ({1} in total)"
  },
  timePicker: {
    am: "AM",
    pm: "PM"
  },
  pagination: {
    ariaLabel: {
      wrapper: "Pagination Navigation",
      next: "Next page",
      previous: "Previous page",
      page: "Goto Page {0}",
      currentPage: "Current Page, Page {0}"
    }
  },
  rating: {
    ariaLabel: {
      icon: "Rating {0} of {1}"
    }
  }
};
const preset = {
  breakpoint: {
    mobileBreakpoint: 1264,
    scrollBarWidth: 16,
    thresholds: {
      xs: 600,
      sm: 960,
      md: 1280,
      lg: 1920
    }
  },
  icons: {
    iconfont: "mdi",
    values: {}
  },
  lang: {
    current: "en",
    locales: {
      en
    },
    t: void 0
  },
  rtl: false,
  theme: {
    dark: false,
    default: "light",
    disable: false,
    options: {
      cspNonce: void 0,
      customProperties: void 0,
      minifyTheme: void 0,
      themeCache: void 0,
      variations: true
    },
    themes: {
      light: {
        primary: "#1976D2",
        secondary: "#424242",
        accent: "#82B1FF",
        error: "#FF5252",
        info: "#2196F3",
        success: "#4CAF50",
        warning: "#FB8C00"
      },
      dark: {
        primary: "#2196F3",
        secondary: "#424242",
        accent: "#FF4081",
        error: "#FF5252",
        info: "#2196F3",
        success: "#4CAF50",
        warning: "#FB8C00"
      }
    }
  }
};
class Presets extends Service {
  constructor(parentPreset, parent) {
    super();
    const defaultPreset = mergeDeep({}, preset);
    const {
      userPreset
    } = parent;
    const _a = userPreset, {
      preset: globalPreset = {}
    } = _a, preset$1 = __objRest(_a, [
      "preset"
    ]);
    if (globalPreset.preset != null) {
      consoleWarn("Global presets do not support the **preset** option, it can be safely omitted");
    }
    parent.preset = mergeDeep(mergeDeep(defaultPreset, globalPreset), preset$1);
  }
}
Presets.property = "presets";
const srgbForwardMatrix = [[3.2406, -1.5372, -0.4986], [-0.9689, 1.8758, 0.0415], [0.0557, -0.204, 1.057]];
const srgbForwardTransform = (C) => C <= 31308e-7 ? C * 12.92 : 1.055 * C ** (1 / 2.4) - 0.055;
const srgbReverseMatrix = [[0.4124, 0.3576, 0.1805], [0.2126, 0.7152, 0.0722], [0.0193, 0.1192, 0.9505]];
const srgbReverseTransform = (C) => C <= 0.04045 ? C / 12.92 : ((C + 0.055) / 1.055) ** 2.4;
function fromXYZ$1(xyz) {
  const rgb = Array(3);
  const transform2 = srgbForwardTransform;
  const matrix = srgbForwardMatrix;
  for (let i = 0; i < 3; ++i) {
    rgb[i] = Math.round(clamp(transform2(matrix[i][0] * xyz[0] + matrix[i][1] * xyz[1] + matrix[i][2] * xyz[2])) * 255);
  }
  return (rgb[0] << 16) + (rgb[1] << 8) + (rgb[2] << 0);
}
function toXYZ$1(rgb) {
  const xyz = [0, 0, 0];
  const transform2 = srgbReverseTransform;
  const matrix = srgbReverseMatrix;
  const r = transform2((rgb >> 16 & 255) / 255);
  const g = transform2((rgb >> 8 & 255) / 255);
  const b = transform2((rgb >> 0 & 255) / 255);
  for (let i = 0; i < 3; ++i) {
    xyz[i] = matrix[i][0] * r + matrix[i][1] * g + matrix[i][2] * b;
  }
  return xyz;
}
function isCssColor(color) {
  return !!color && !!color.match(/^(#|var\(--|(rgb|hsl)a?\()/);
}
function colorToInt(color) {
  let rgb;
  if (typeof color === "number") {
    rgb = color;
  } else if (typeof color === "string") {
    let c = color[0] === "#" ? color.substring(1) : color;
    if (c.length === 3) {
      c = c.split("").map((char) => char + char).join("");
    }
    if (c.length !== 6) {
      consoleWarn(`'${color}' is not a valid rgb color`);
    }
    rgb = parseInt(c, 16);
  } else {
    throw new TypeError(`Colors can only be numbers or strings, recieved ${color == null ? color : color.constructor.name} instead`);
  }
  if (rgb < 0) {
    consoleWarn(`Colors cannot be negative: '${color}'`);
    rgb = 0;
  } else if (rgb > 16777215 || isNaN(rgb)) {
    consoleWarn(`'${color}' is not a valid rgb color`);
    rgb = 16777215;
  }
  return rgb;
}
function intToHex(color) {
  let hexColor = color.toString(16);
  if (hexColor.length < 6)
    hexColor = "0".repeat(6 - hexColor.length) + hexColor;
  return "#" + hexColor;
}
function colorToHex(color) {
  return intToHex(colorToInt(color));
}
function HSVAtoRGBA(hsva) {
  const {
    h,
    s,
    v,
    a
  } = hsva;
  const f = (n) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  };
  const rgb = [f(5), f(3), f(1)].map((v2) => Math.round(v2 * 255));
  return {
    r: rgb[0],
    g: rgb[1],
    b: rgb[2],
    a
  };
}
function RGBAtoHSVA(rgba) {
  if (!rgba)
    return {
      h: 0,
      s: 1,
      v: 1,
      a: 1
    };
  const r = rgba.r / 255;
  const g = rgba.g / 255;
  const b = rgba.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  if (max !== min) {
    if (max === r) {
      h = 60 * (0 + (g - b) / (max - min));
    } else if (max === g) {
      h = 60 * (2 + (b - r) / (max - min));
    } else if (max === b) {
      h = 60 * (4 + (r - g) / (max - min));
    }
  }
  if (h < 0)
    h = h + 360;
  const s = max === 0 ? 0 : (max - min) / max;
  const hsv = [h, s, max];
  return {
    h: hsv[0],
    s: hsv[1],
    v: hsv[2],
    a: rgba.a
  };
}
function HSVAtoHSLA(hsva) {
  const {
    h,
    s,
    v,
    a
  } = hsva;
  const l = v - v * s / 2;
  const sprime = l === 1 || l === 0 ? 0 : (v - l) / Math.min(l, 1 - l);
  return {
    h,
    s: sprime,
    l,
    a
  };
}
function HSLAtoHSVA(hsl) {
  const {
    h,
    s,
    l,
    a
  } = hsl;
  const v = l + s * Math.min(l, 1 - l);
  const sprime = v === 0 ? 0 : 2 - 2 * l / v;
  return {
    h,
    s: sprime,
    v,
    a
  };
}
function RGBAtoCSS(rgba) {
  return `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`;
}
function RGBtoCSS(rgba) {
  return RGBAtoCSS(__spreadProps(__spreadValues({}, rgba), {
    a: 1
  }));
}
function RGBAtoHex(rgba) {
  const toHex = (v) => {
    const h = Math.round(v).toString(16);
    return ("00".substr(0, 2 - h.length) + h).toUpperCase();
  };
  return `#${[toHex(rgba.r), toHex(rgba.g), toHex(rgba.b), toHex(Math.round(rgba.a * 255))].join("")}`;
}
function HexToRGBA(hex) {
  const rgba = chunk(hex.slice(1), 2).map((c) => parseInt(c, 16));
  return {
    r: rgba[0],
    g: rgba[1],
    b: rgba[2],
    a: Math.round(rgba[3] / 255 * 100) / 100
  };
}
function HexToHSVA(hex) {
  const rgb = HexToRGBA(hex);
  return RGBAtoHSVA(rgb);
}
function HSVAtoHex(hsva) {
  return RGBAtoHex(HSVAtoRGBA(hsva));
}
function parseHex(hex) {
  if (hex.startsWith("#")) {
    hex = hex.slice(1);
  }
  hex = hex.replace(/([^0-9a-f])/gi, "F");
  if (hex.length === 3 || hex.length === 4) {
    hex = hex.split("").map((x) => x + x).join("");
  }
  if (hex.length === 6) {
    hex = padEnd(hex, 8, "F");
  } else {
    hex = padEnd(padEnd(hex, 6), 8, "F");
  }
  return `#${hex}`.toUpperCase().substr(0, 9);
}
function RGBtoInt(rgba) {
  return (rgba.r << 16) + (rgba.g << 8) + rgba.b;
}
function contrastRatio(c1, c2) {
  const [, y1] = toXYZ$1(RGBtoInt(c1));
  const [, y2] = toXYZ$1(RGBtoInt(c2));
  return (Math.max(y1, y2) + 0.05) / (Math.min(y1, y2) + 0.05);
}
const delta = 0.20689655172413793;
const cielabForwardTransform = (t) => t > delta ** 3 ? Math.cbrt(t) : t / (3 * delta ** 2) + 4 / 29;
const cielabReverseTransform = (t) => t > delta ? t ** 3 : 3 * delta ** 2 * (t - 4 / 29);
function fromXYZ(xyz) {
  const transform2 = cielabForwardTransform;
  const transformedY = transform2(xyz[1]);
  return [116 * transformedY - 16, 500 * (transform2(xyz[0] / 0.95047) - transformedY), 200 * (transformedY - transform2(xyz[2] / 1.08883))];
}
function toXYZ(lab) {
  const transform2 = cielabReverseTransform;
  const Ln = (lab[0] + 16) / 116;
  return [transform2(Ln + lab[1] / 500) * 0.95047, transform2(Ln), transform2(Ln - lab[2] / 200) * 1.08883];
}
function parse(theme2, isItem = false, variations = true) {
  const _a = theme2, {
    anchor
  } = _a, variant = __objRest(_a, [
    "anchor"
  ]);
  const colors2 = Object.keys(variant);
  const parsedTheme = {};
  for (let i = 0; i < colors2.length; ++i) {
    const name = colors2[i];
    const value = theme2[name];
    if (value == null)
      continue;
    if (!variations) {
      parsedTheme[name] = {
        base: intToHex(colorToInt(value))
      };
    } else if (isItem) {
      if (name === "base" || name.startsWith("lighten") || name.startsWith("darken")) {
        parsedTheme[name] = colorToHex(value);
      }
    } else if (typeof value === "object") {
      parsedTheme[name] = parse(value, true, variations);
    } else {
      parsedTheme[name] = genVariations(name, colorToInt(value));
    }
  }
  if (!isItem) {
    parsedTheme.anchor = anchor || parsedTheme.base || parsedTheme.primary.base;
  }
  return parsedTheme;
}
const genBaseColor = (name, value) => {
  return `
.v-application .${name} {
  background-color: ${value} !important;
  border-color: ${value} !important;
}
.v-application .${name}--text {
  color: ${value} !important;
  caret-color: ${value} !important;
}`;
};
const genVariantColor = (name, variant, value) => {
  const [type2, n] = variant.split(/(\d)/, 2);
  return `
.v-application .${name}.${type2}-${n} {
  background-color: ${value} !important;
  border-color: ${value} !important;
}
.v-application .${name}--text.text--${type2}-${n} {
  color: ${value} !important;
  caret-color: ${value} !important;
}`;
};
const genColorVariableName = (name, variant = "base") => `--v-${name}-${variant}`;
const genColorVariable = (name, variant = "base") => `var(${genColorVariableName(name, variant)})`;
function genStyles(theme2, cssVar = false) {
  const _a = theme2, {
    anchor
  } = _a, variant = __objRest(_a, [
    "anchor"
  ]);
  const colors2 = Object.keys(variant);
  if (!colors2.length)
    return "";
  let variablesCss = "";
  let css = "";
  const aColor = cssVar ? genColorVariable("anchor") : anchor;
  css += `.v-application a { color: ${aColor}; }`;
  cssVar && (variablesCss += `  ${genColorVariableName("anchor")}: ${anchor};
`);
  for (let i = 0; i < colors2.length; ++i) {
    const name = colors2[i];
    const value = theme2[name];
    css += genBaseColor(name, cssVar ? genColorVariable(name) : value.base);
    cssVar && (variablesCss += `  ${genColorVariableName(name)}: ${value.base};
`);
    const variants = keys$1(value);
    for (let i2 = 0; i2 < variants.length; ++i2) {
      const variant2 = variants[i2];
      const variantValue = value[variant2];
      if (variant2 === "base")
        continue;
      css += genVariantColor(name, variant2, cssVar ? genColorVariable(name, variant2) : variantValue);
      cssVar && (variablesCss += `  ${genColorVariableName(name, variant2)}: ${variantValue};
`);
    }
  }
  if (cssVar) {
    variablesCss = `:root {
${variablesCss}}

`;
  }
  return variablesCss + css;
}
function genVariations(name, value) {
  const values = {
    base: intToHex(value)
  };
  for (let i = 5; i > 0; --i) {
    values[`lighten${i}`] = intToHex(lighten(value, i));
  }
  for (let i = 1; i <= 4; ++i) {
    values[`darken${i}`] = intToHex(darken(value, i));
  }
  return values;
}
function lighten(value, amount) {
  const lab = fromXYZ(toXYZ$1(value));
  lab[0] = lab[0] + amount * 10;
  return fromXYZ$1(toXYZ(lab));
}
function darken(value, amount) {
  const lab = fromXYZ(toXYZ$1(value));
  lab[0] = lab[0] - amount * 10;
  return fromXYZ$1(toXYZ(lab));
}
class Theme extends Service {
  constructor(preset2) {
    super();
    this.disabled = false;
    this.isDark = null;
    this.unwatch = null;
    this.vueMeta = null;
    const {
      dark,
      disable,
      options: options2,
      themes
    } = preset2[Theme.property];
    this.dark = Boolean(dark);
    this.defaults = this.themes = themes;
    this.options = options2;
    if (disable) {
      this.disabled = true;
      return;
    }
    this.themes = {
      dark: this.fillVariant(themes.dark, true),
      light: this.fillVariant(themes.light, false)
    };
  }
  set css(val) {
    if (this.vueMeta) {
      if (this.isVueMeta23) {
        this.applyVueMeta23();
      }
      return;
    }
    this.checkOrCreateStyleElement() && (this.styleEl.innerHTML = val);
  }
  set dark(val) {
    const oldDark = this.isDark;
    this.isDark = val;
    oldDark != null && this.applyTheme();
  }
  get dark() {
    return Boolean(this.isDark);
  }
  applyTheme() {
    if (this.disabled)
      return this.clearCss();
    this.css = this.generatedStyles;
  }
  clearCss() {
    this.css = "";
  }
  init(root, ssrContext) {
    if (this.disabled)
      return;
    if (root.$meta) {
      this.initVueMeta(root);
    } else if (ssrContext) {
      this.initSSR(ssrContext);
    }
    this.initTheme(root);
  }
  setTheme(theme2, value) {
    this.themes[theme2] = Object.assign(this.themes[theme2], value);
    this.applyTheme();
  }
  resetThemes() {
    this.themes.light = Object.assign({}, this.defaults.light);
    this.themes.dark = Object.assign({}, this.defaults.dark);
    this.applyTheme();
  }
  checkOrCreateStyleElement() {
    this.styleEl = document.getElementById("vuetify-theme-stylesheet");
    if (this.styleEl)
      return true;
    this.genStyleElement();
    return Boolean(this.styleEl);
  }
  fillVariant(theme2 = {}, dark) {
    const defaultTheme = this.themes[dark ? "dark" : "light"];
    return Object.assign({}, defaultTheme, theme2);
  }
  genStyleElement() {
    if (typeof document === "undefined")
      return;
    this.styleEl = document.createElement("style");
    this.styleEl.type = "text/css";
    this.styleEl.id = "vuetify-theme-stylesheet";
    if (this.options.cspNonce) {
      this.styleEl.setAttribute("nonce", this.options.cspNonce);
    }
    document.head.appendChild(this.styleEl);
  }
  initVueMeta(root) {
    this.vueMeta = root.$meta();
    if (this.isVueMeta23) {
      root.$nextTick(() => {
        this.applyVueMeta23();
      });
      return;
    }
    const metaKeyName = typeof this.vueMeta.getOptions === "function" ? this.vueMeta.getOptions().keyName : "metaInfo";
    const metaInfo = root.$options[metaKeyName] || {};
    root.$options[metaKeyName] = () => {
      metaInfo.style = metaInfo.style || [];
      const vuetifyStylesheet = metaInfo.style.find((s) => s.id === "vuetify-theme-stylesheet");
      if (!vuetifyStylesheet) {
        metaInfo.style.push({
          cssText: this.generatedStyles,
          type: "text/css",
          id: "vuetify-theme-stylesheet",
          nonce: (this.options || {}).cspNonce
        });
      } else {
        vuetifyStylesheet.cssText = this.generatedStyles;
      }
      return metaInfo;
    };
  }
  applyVueMeta23() {
    const {
      set
    } = this.vueMeta.addApp("vuetify");
    set({
      style: [{
        cssText: this.generatedStyles,
        type: "text/css",
        id: "vuetify-theme-stylesheet",
        nonce: this.options.cspNonce
      }]
    });
  }
  initSSR(ssrContext) {
    const nonce = this.options.cspNonce ? ` nonce="${this.options.cspNonce}"` : "";
    ssrContext.head = ssrContext.head || "";
    ssrContext.head += `<style type="text/css" id="vuetify-theme-stylesheet"${nonce}>${this.generatedStyles}</style>`;
  }
  initTheme(root) {
    if (typeof document === "undefined")
      return;
    if (this.unwatch) {
      this.unwatch();
      this.unwatch = null;
    }
    root.$once("hook:created", () => {
      const obs = Vue.observable({
        themes: this.themes
      });
      this.unwatch = root.$watch(() => obs.themes, () => this.applyTheme(), {
        deep: true
      });
    });
    this.applyTheme();
  }
  get currentTheme() {
    const target = this.dark ? "dark" : "light";
    return this.themes[target];
  }
  get generatedStyles() {
    const theme2 = this.parsedTheme;
    const options2 = this.options || {};
    let css;
    if (options2.themeCache != null) {
      css = options2.themeCache.get(theme2);
      if (css != null)
        return css;
    }
    css = genStyles(theme2, options2.customProperties);
    if (options2.minifyTheme != null) {
      css = options2.minifyTheme(css);
    }
    if (options2.themeCache != null) {
      options2.themeCache.set(theme2, css);
    }
    return css;
  }
  get parsedTheme() {
    return parse(this.currentTheme || {}, void 0, getNestedValue(this.options, ["variations"], true));
  }
  get isVueMeta23() {
    return typeof this.vueMeta.addApp === "function";
  }
}
Theme.property = "theme";
class Vuetify {
  constructor(userPreset = {}) {
    this.framework = {
      isHydrating: false
    };
    this.installed = [];
    this.preset = {};
    this.userPreset = {};
    this.userPreset = userPreset;
    this.use(Presets);
    this.use(Application);
    this.use(Breakpoint);
    this.use(Goto);
    this.use(Icons);
    this.use(Lang);
    this.use(Theme);
  }
  init(root, ssrContext) {
    this.installed.forEach((property) => {
      const service = this.framework[property];
      service.framework = this.framework;
      service.init(root, ssrContext);
    });
    this.framework.rtl = Boolean(this.preset.rtl);
  }
  use(Service2) {
    const property = Service2.property;
    if (this.installed.includes(property))
      return;
    this.framework[property] = new Service2(this.preset, this);
    this.installed.push(property);
  }
}
Vuetify.install = install;
Vuetify.installed = false;
Vuetify.version = "2.4.9";
Vuetify.config = {
  silent: false
};
function createMessage(message, vm, parent) {
  if (Vuetify.config.silent)
    return;
  if (parent) {
    vm = {
      _isVue: true,
      $parent: parent,
      $options: vm
    };
  }
  if (vm) {
    vm.$_alreadyWarned = vm.$_alreadyWarned || [];
    if (vm.$_alreadyWarned.includes(message))
      return;
    vm.$_alreadyWarned.push(message);
  }
  return `[Vuetify] ${message}` + (vm ? generateComponentTrace(vm) : "");
}
function consoleWarn(message, vm, parent) {
  const newMessage = createMessage(message, vm, parent);
  newMessage != null && console.warn(newMessage);
}
function consoleError(message, vm, parent) {
  const newMessage = createMessage(message, vm, parent);
  newMessage != null && console.error(newMessage);
}
function deprecate(original, replacement, vm, parent) {
  consoleWarn(`[UPGRADE] '${original}' is deprecated, use '${replacement}' instead.`, vm, parent);
}
function breaking(original, replacement, vm, parent) {
  consoleError(`[BREAKING] '${original}' has been removed, use '${replacement}' instead. For more information, see the upgrade guide https://github.com/vuetifyjs/vuetify/releases/tag/v2.0.0#user-content-upgrade-guide`, vm, parent);
}
function removed(original, vm, parent) {
  consoleWarn(`[REMOVED] '${original}' has been removed. You can safely omit it.`, vm, parent);
}
const classifyRE = /(?:^|[-_])(\w)/g;
const classify = (str) => str.replace(classifyRE, (c) => c.toUpperCase()).replace(/[-_]/g, "");
function formatComponentName(vm, includeFile) {
  if (vm.$root === vm) {
    return "<Root>";
  }
  const options2 = typeof vm === "function" && vm.cid != null ? vm.options : vm._isVue ? vm.$options || vm.constructor.options : vm || {};
  let name = options2.name || options2._componentTag;
  const file = options2.__file;
  if (!name && file) {
    const match = file.match(/([^/\\]+)\.vue$/);
    name = match && match[1];
  }
  return (name ? `<${classify(name)}>` : `<Anonymous>`) + (file && includeFile !== false ? ` at ${file}` : "");
}
function generateComponentTrace(vm) {
  if (vm._isVue && vm.$parent) {
    const tree = [];
    let currentRecursiveSequence = 0;
    while (vm) {
      if (tree.length > 0) {
        const last = tree[tree.length - 1];
        if (last.constructor === vm.constructor) {
          currentRecursiveSequence++;
          vm = vm.$parent;
          continue;
        } else if (currentRecursiveSequence > 0) {
          tree[tree.length - 1] = [last, currentRecursiveSequence];
          currentRecursiveSequence = 0;
        }
      }
      tree.push(vm);
      vm = vm.$parent;
    }
    return "\n\nfound in\n\n" + tree.map((vm2, i) => `${i === 0 ? "---> " : " ".repeat(5 + i * 2)}${Array.isArray(vm2) ? `${formatComponentName(vm2[0])}... (${vm2[1]} recursive calls)` : formatComponentName(vm2)}`).join("\n");
  } else {
    return `

(found in ${formatComponentName(vm)})`;
  }
}
var Colorable = Vue.extend({
  name: "colorable",
  props: {
    color: String
  },
  methods: {
    setBackgroundColor(color, data = {}) {
      if (typeof data.style === "string") {
        consoleError("style must be an object", this);
        return data;
      }
      if (typeof data.class === "string") {
        consoleError("class must be an object", this);
        return data;
      }
      if (isCssColor(color)) {
        data.style = __spreadProps(__spreadValues({}, data.style), {
          "background-color": `${color}`,
          "border-color": `${color}`
        });
      } else if (color) {
        data.class = __spreadProps(__spreadValues({}, data.class), {
          [color]: true
        });
      }
      return data;
    },
    setTextColor(color, data = {}) {
      if (typeof data.style === "string") {
        consoleError("style must be an object", this);
        return data;
      }
      if (typeof data.class === "string") {
        consoleError("class must be an object", this);
        return data;
      }
      if (isCssColor(color)) {
        data.style = __spreadProps(__spreadValues({}, data.style), {
          color: `${color}`,
          "caret-color": `${color}`
        });
      } else if (color) {
        const [colorName, colorModifier] = color.toString().trim().split(" ", 2);
        data.class = __spreadProps(__spreadValues({}, data.class), {
          [colorName + "--text"]: true
        });
        if (colorModifier) {
          data.class["text--" + colorModifier] = true;
        }
      }
      return data;
    }
  }
});
var Elevatable = Vue.extend({
  name: "elevatable",
  props: {
    elevation: [Number, String]
  },
  computed: {
    computedElevation() {
      return this.elevation;
    },
    elevationClasses() {
      const elevation = this.computedElevation;
      if (elevation == null)
        return {};
      if (isNaN(parseInt(elevation)))
        return {};
      return {
        [`elevation-${this.elevation}`]: true
      };
    }
  }
});
var Measurable = Vue.extend({
  name: "measurable",
  props: {
    height: [Number, String],
    maxHeight: [Number, String],
    maxWidth: [Number, String],
    minHeight: [Number, String],
    minWidth: [Number, String],
    width: [Number, String]
  },
  computed: {
    measurableStyles() {
      const styles = {};
      const height = convertToUnit(this.height);
      const minHeight = convertToUnit(this.minHeight);
      const minWidth = convertToUnit(this.minWidth);
      const maxHeight = convertToUnit(this.maxHeight);
      const maxWidth = convertToUnit(this.maxWidth);
      const width = convertToUnit(this.width);
      if (height)
        styles.height = height;
      if (minHeight)
        styles.minHeight = minHeight;
      if (minWidth)
        styles.minWidth = minWidth;
      if (maxHeight)
        styles.maxHeight = maxHeight;
      if (maxWidth)
        styles.maxWidth = maxWidth;
      if (width)
        styles.width = width;
      return styles;
    }
  }
});
var Roundable = Vue.extend({
  name: "roundable",
  props: {
    rounded: [Boolean, String],
    tile: Boolean
  },
  computed: {
    roundedClasses() {
      const composite = [];
      const rounded = typeof this.rounded === "string" ? String(this.rounded) : this.rounded === true;
      if (this.tile) {
        composite.push("rounded-0");
      } else if (typeof rounded === "string") {
        const values = rounded.split(" ");
        for (const value of values) {
          composite.push(`rounded-${value}`);
        }
      } else if (rounded) {
        composite.push("rounded");
      }
      return composite.length > 0 ? {
        [composite.join(" ")]: true
      } : {};
    }
  }
});
var VSheet = mixins(BindsAttrs, Colorable, Elevatable, Measurable, Roundable, Themeable$1).extend({
  name: "v-sheet",
  props: {
    outlined: Boolean,
    shaped: Boolean,
    tag: {
      type: String,
      default: "div"
    }
  },
  computed: {
    classes() {
      return __spreadValues(__spreadValues(__spreadValues({
        "v-sheet": true,
        "v-sheet--outlined": this.outlined,
        "v-sheet--shaped": this.shaped
      }, this.themeClasses), this.elevationClasses), this.roundedClasses);
    },
    styles() {
      return this.measurableStyles;
    }
  },
  render(h) {
    const data = {
      class: this.classes,
      style: this.styles,
      on: this.listeners$
    };
    return h(this.tag, this.setBackgroundColor(this.color, data), this.$slots.default);
  }
});
function inserted$4(el, binding) {
  const modifiers = binding.modifiers || {};
  const value = binding.value;
  const {
    handler,
    options: options2
  } = typeof value === "object" ? value : {
    handler: value,
    options: {}
  };
  const observer = new IntersectionObserver((entries = [], observer2) => {
    if (!el._observe)
      return;
    if (handler && (!modifiers.quiet || el._observe.init)) {
      const isIntersecting = Boolean(entries.find((entry) => entry.isIntersecting));
      handler(entries, observer2, isIntersecting);
    }
    if (el._observe.init && modifiers.once)
      unbind$5(el);
    else
      el._observe.init = true;
  }, options2);
  el._observe = {
    init: false,
    observer
  };
  observer.observe(el);
}
function unbind$5(el) {
  if (!el._observe)
    return;
  el._observe.observer.unobserve(el);
  delete el._observe;
}
const Intersect = {
  inserted: inserted$4,
  unbind: unbind$5
};
var intersect = Intersect;
var VResponsive = mixins(Measurable).extend({
  name: "v-responsive",
  props: {
    aspectRatio: [String, Number],
    contentClass: String
  },
  computed: {
    computedAspectRatio() {
      return Number(this.aspectRatio);
    },
    aspectStyle() {
      return this.computedAspectRatio ? {
        paddingBottom: 1 / this.computedAspectRatio * 100 + "%"
      } : void 0;
    },
    __cachedSizer() {
      if (!this.aspectStyle)
        return [];
      return this.$createElement("div", {
        style: this.aspectStyle,
        staticClass: "v-responsive__sizer"
      });
    }
  },
  methods: {
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-responsive__content",
        class: this.contentClass
      }, this.$slots.default);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-responsive",
      style: this.measurableStyles,
      on: this.$listeners
    }, [this.__cachedSizer, this.genContent()]);
  }
});
const pattern = {
  styleList: /;(?![^(]*\))/g,
  styleProp: /:(.*)/
};
function parseStyle(style) {
  const styleMap = {};
  for (const s of style.split(pattern.styleList)) {
    let [key, val] = s.split(pattern.styleProp);
    key = key.trim();
    if (!key) {
      continue;
    }
    if (typeof val === "string") {
      val = val.trim();
    }
    styleMap[camelize(key)] = val;
  }
  return styleMap;
}
function mergeData() {
  const mergeTarget = {};
  let i = arguments.length;
  let prop;
  while (i--) {
    for (prop of Object.keys(arguments[i])) {
      switch (prop) {
        case "class":
        case "directives":
          if (arguments[i][prop]) {
            mergeTarget[prop] = mergeClasses(mergeTarget[prop], arguments[i][prop]);
          }
          break;
        case "style":
          if (arguments[i][prop]) {
            mergeTarget[prop] = mergeStyles(mergeTarget[prop], arguments[i][prop]);
          }
          break;
        case "staticClass":
          if (!arguments[i][prop]) {
            break;
          }
          if (mergeTarget[prop] === void 0) {
            mergeTarget[prop] = "";
          }
          if (mergeTarget[prop]) {
            mergeTarget[prop] += " ";
          }
          mergeTarget[prop] += arguments[i][prop].trim();
          break;
        case "on":
        case "nativeOn":
          if (arguments[i][prop]) {
            mergeTarget[prop] = mergeListeners(mergeTarget[prop], arguments[i][prop]);
          }
          break;
        case "attrs":
        case "props":
        case "domProps":
        case "scopedSlots":
        case "staticStyle":
        case "hook":
        case "transition":
          if (!arguments[i][prop]) {
            break;
          }
          if (!mergeTarget[prop]) {
            mergeTarget[prop] = {};
          }
          mergeTarget[prop] = __spreadValues(__spreadValues({}, arguments[i][prop]), mergeTarget[prop]);
          break;
        default:
          if (!mergeTarget[prop]) {
            mergeTarget[prop] = arguments[i][prop];
          }
      }
    }
  }
  return mergeTarget;
}
function mergeStyles(target, source) {
  if (!target)
    return source;
  if (!source)
    return target;
  target = wrapInArray(typeof target === "string" ? parseStyle(target) : target);
  return target.concat(typeof source === "string" ? parseStyle(source) : source);
}
function mergeClasses(target, source) {
  if (!source)
    return target;
  if (!target)
    return source;
  return target ? wrapInArray(target).concat(source) : source;
}
function mergeListeners(...args) {
  if (!args[0])
    return args[1];
  if (!args[1])
    return args[0];
  const dest = {};
  for (let i = 2; i--; ) {
    const arg = args[i];
    for (const event in arg) {
      if (!arg[event])
        continue;
      if (dest[event]) {
        dest[event] = [].concat(arg[event], dest[event]);
      } else {
        dest[event] = arg[event];
      }
    }
  }
  return dest;
}
const hasIntersect = typeof window !== "undefined" && "IntersectionObserver" in window;
var VImg = mixins(VResponsive, Themeable$1).extend({
  name: "v-img",
  directives: {
    intersect
  },
  props: {
    alt: String,
    contain: Boolean,
    eager: Boolean,
    gradient: String,
    lazySrc: String,
    options: {
      type: Object,
      default: () => ({
        root: void 0,
        rootMargin: void 0,
        threshold: void 0
      })
    },
    position: {
      type: String,
      default: "center center"
    },
    sizes: String,
    src: {
      type: [String, Object],
      default: ""
    },
    srcset: String,
    transition: {
      type: [Boolean, String],
      default: "fade-transition"
    }
  },
  data() {
    return {
      currentSrc: "",
      image: null,
      isLoading: true,
      calculatedAspectRatio: void 0,
      naturalWidth: void 0,
      hasError: false
    };
  },
  computed: {
    computedAspectRatio() {
      return Number(this.normalisedSrc.aspect || this.calculatedAspectRatio);
    },
    normalisedSrc() {
      return this.src && typeof this.src === "object" ? {
        src: this.src.src,
        srcset: this.srcset || this.src.srcset,
        lazySrc: this.lazySrc || this.src.lazySrc,
        aspect: Number(this.aspectRatio || this.src.aspect)
      } : {
        src: this.src,
        srcset: this.srcset,
        lazySrc: this.lazySrc,
        aspect: Number(this.aspectRatio || 0)
      };
    },
    __cachedImage() {
      if (!(this.normalisedSrc.src || this.normalisedSrc.lazySrc || this.gradient))
        return [];
      const backgroundImage = [];
      const src = this.isLoading ? this.normalisedSrc.lazySrc : this.currentSrc;
      if (this.gradient)
        backgroundImage.push(`linear-gradient(${this.gradient})`);
      if (src)
        backgroundImage.push(`url("${src}")`);
      const image = this.$createElement("div", {
        staticClass: "v-image__image",
        class: {
          "v-image__image--preload": this.isLoading,
          "v-image__image--contain": this.contain,
          "v-image__image--cover": !this.contain
        },
        style: {
          backgroundImage: backgroundImage.join(", "),
          backgroundPosition: this.position
        },
        key: +this.isLoading
      });
      if (!this.transition)
        return image;
      return this.$createElement("transition", {
        attrs: {
          name: this.transition,
          mode: "in-out"
        }
      }, [image]);
    }
  },
  watch: {
    src() {
      if (!this.isLoading)
        this.init(void 0, void 0, true);
      else
        this.loadImage();
    },
    "$vuetify.breakpoint.width": "getSrc"
  },
  mounted() {
    this.init();
  },
  methods: {
    init(entries, observer, isIntersecting) {
      if (hasIntersect && !isIntersecting && !this.eager)
        return;
      if (this.normalisedSrc.lazySrc) {
        const lazyImg = new Image();
        lazyImg.src = this.normalisedSrc.lazySrc;
        this.pollForSize(lazyImg, null);
      }
      if (this.normalisedSrc.src)
        this.loadImage();
    },
    onLoad() {
      this.getSrc();
      this.isLoading = false;
      this.$emit("load", this.src);
      if (this.image && (this.normalisedSrc.src.endsWith(".svg") || this.normalisedSrc.src.startsWith("data:image/svg+xml"))) {
        if (this.image.naturalHeight && this.image.naturalWidth) {
          this.naturalWidth = this.image.naturalWidth;
          this.calculatedAspectRatio = this.image.naturalWidth / this.image.naturalHeight;
        } else {
          this.calculatedAspectRatio = 1;
        }
      }
    },
    onError() {
      this.hasError = true;
      this.$emit("error", this.src);
    },
    getSrc() {
      if (this.image)
        this.currentSrc = this.image.currentSrc || this.image.src;
    },
    loadImage() {
      const image = new Image();
      this.image = image;
      image.onload = () => {
        if (image.decode) {
          image.decode().catch((err) => {
            consoleWarn(`Failed to decode image, trying to render anyway

src: ${this.normalisedSrc.src}` + (err.message ? `
Original error: ${err.message}` : ""), this);
          }).then(this.onLoad);
        } else {
          this.onLoad();
        }
      };
      image.onerror = this.onError;
      this.hasError = false;
      image.src = this.normalisedSrc.src;
      this.sizes && (image.sizes = this.sizes);
      this.normalisedSrc.srcset && (image.srcset = this.normalisedSrc.srcset);
      this.aspectRatio || this.pollForSize(image);
      this.getSrc();
    },
    pollForSize(img, timeout = 100) {
      const poll = () => {
        const {
          naturalHeight,
          naturalWidth
        } = img;
        if (naturalHeight || naturalWidth) {
          this.naturalWidth = naturalWidth;
          this.calculatedAspectRatio = naturalWidth / naturalHeight;
        } else if (!img.complete && this.isLoading && !this.hasError && timeout != null) {
          setTimeout(poll, timeout);
        }
      };
      poll();
    },
    genContent() {
      const content = VResponsive.options.methods.genContent.call(this);
      if (this.naturalWidth) {
        this._b(content.data, "div", {
          style: {
            width: `${this.naturalWidth}px`
          }
        });
      }
      return content;
    },
    __genPlaceholder() {
      if (this.$slots.placeholder) {
        const placeholder = this.isLoading ? [this.$createElement("div", {
          staticClass: "v-image__placeholder"
        }, this.$slots.placeholder)] : [];
        if (!this.transition)
          return placeholder[0];
        return this.$createElement("transition", {
          props: {
            appear: true,
            name: this.transition
          }
        }, placeholder);
      }
    }
  },
  render(h) {
    const node = VResponsive.options.render.call(this, h);
    const data = mergeData(node.data, {
      staticClass: "v-image",
      attrs: {
        "aria-label": this.alt,
        role: this.alt ? "img" : void 0
      },
      class: this.themeClasses,
      directives: hasIntersect ? [{
        name: "intersect",
        modifiers: {
          once: true
        },
        value: {
          handler: this.init,
          options: this.options
        }
      }] : void 0
    });
    node.children = [this.__cachedSizer, this.__cachedImage, this.__genPlaceholder(), this.genContent()];
    return h(node.tag, data, node.children);
  }
});
var VToolbar = VSheet.extend({
  name: "v-toolbar",
  props: {
    absolute: Boolean,
    bottom: Boolean,
    collapse: Boolean,
    dense: Boolean,
    extended: Boolean,
    extensionHeight: {
      default: 48,
      type: [Number, String]
    },
    flat: Boolean,
    floating: Boolean,
    prominent: Boolean,
    short: Boolean,
    src: {
      type: [String, Object],
      default: ""
    },
    tag: {
      type: String,
      default: "header"
    }
  },
  data: () => ({
    isExtended: false
  }),
  computed: {
    computedHeight() {
      const height = this.computedContentHeight;
      if (!this.isExtended)
        return height;
      const extensionHeight = parseInt(this.extensionHeight);
      return this.isCollapsed ? height : height + (!isNaN(extensionHeight) ? extensionHeight : 0);
    },
    computedContentHeight() {
      if (this.height)
        return parseInt(this.height);
      if (this.isProminent && this.dense)
        return 96;
      if (this.isProminent && this.short)
        return 112;
      if (this.isProminent)
        return 128;
      if (this.dense)
        return 48;
      if (this.short || this.$vuetify.breakpoint.smAndDown)
        return 56;
      return 64;
    },
    classes() {
      return __spreadProps(__spreadValues({}, VSheet.options.computed.classes.call(this)), {
        "v-toolbar": true,
        "v-toolbar--absolute": this.absolute,
        "v-toolbar--bottom": this.bottom,
        "v-toolbar--collapse": this.collapse,
        "v-toolbar--collapsed": this.isCollapsed,
        "v-toolbar--dense": this.dense,
        "v-toolbar--extended": this.isExtended,
        "v-toolbar--flat": this.flat,
        "v-toolbar--floating": this.floating,
        "v-toolbar--prominent": this.isProminent
      });
    },
    isCollapsed() {
      return this.collapse;
    },
    isProminent() {
      return this.prominent;
    },
    styles() {
      return __spreadProps(__spreadValues({}, this.measurableStyles), {
        height: convertToUnit(this.computedHeight)
      });
    }
  },
  created() {
    const breakingProps = [["app", "<v-app-bar app>"], ["manual-scroll", '<v-app-bar :value="false">'], ["clipped-left", "<v-app-bar clipped-left>"], ["clipped-right", "<v-app-bar clipped-right>"], ["inverted-scroll", "<v-app-bar inverted-scroll>"], ["scroll-off-screen", "<v-app-bar scroll-off-screen>"], ["scroll-target", "<v-app-bar scroll-target>"], ["scroll-threshold", "<v-app-bar scroll-threshold>"], ["card", "<v-app-bar flat>"]];
    breakingProps.forEach(([original, replacement]) => {
      if (this.$attrs.hasOwnProperty(original))
        breaking(original, replacement, this);
    });
  },
  methods: {
    genBackground() {
      const props2 = {
        height: convertToUnit(this.computedHeight),
        src: this.src
      };
      const image = this.$scopedSlots.img ? this.$scopedSlots.img({
        props: props2
      }) : this.$createElement(VImg, {
        props: props2
      });
      return this.$createElement("div", {
        staticClass: "v-toolbar__image"
      }, [image]);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-toolbar__content",
        style: {
          height: convertToUnit(this.computedContentHeight)
        }
      }, getSlot(this));
    },
    genExtension() {
      return this.$createElement("div", {
        staticClass: "v-toolbar__extension",
        style: {
          height: convertToUnit(this.extensionHeight)
        }
      }, getSlot(this, "extension"));
    }
  },
  render(h) {
    this.isExtended = this.extended || !!this.$scopedSlots.extension;
    const children = [this.genContent()];
    const data = this.setBackgroundColor(this.color, {
      class: this.classes,
      style: this.styles,
      on: this.$listeners
    });
    if (this.isExtended)
      children.push(this.genExtension());
    if (this.src || this.$scopedSlots.img)
      children.unshift(this.genBackground());
    return h(this.tag, data, children);
  }
});
function inserted$3(el, binding) {
  const {
    self = false
  } = binding.modifiers || {};
  const value = binding.value;
  const options2 = typeof value === "object" && value.options || {
    passive: true
  };
  const handler = typeof value === "function" || "handleEvent" in value ? value : value.handler;
  const target = self ? el : binding.arg ? document.querySelector(binding.arg) : window;
  if (!target)
    return;
  target.addEventListener("scroll", handler, options2);
  el._onScroll = {
    handler,
    options: options2,
    target: self ? void 0 : target
  };
}
function unbind$4(el) {
  if (!el._onScroll)
    return;
  const {
    handler,
    options: options2,
    target = el
  } = el._onScroll;
  target.removeEventListener("scroll", handler, options2);
  delete el._onScroll;
}
const Scroll = {
  inserted: inserted$3,
  unbind: unbind$4
};
var Scroll$1 = Scroll;
const availableProps = {
  absolute: Boolean,
  bottom: Boolean,
  fixed: Boolean,
  left: Boolean,
  right: Boolean,
  top: Boolean
};
function factory$3(selected = []) {
  return Vue.extend({
    name: "positionable",
    props: selected.length ? filterObjectOnKeys(availableProps, selected) : availableProps
  });
}
var Positionable = factory$3();
function applicationable(value, events = []) {
  return mixins(factory$3(["absolute", "fixed"])).extend({
    name: "applicationable",
    props: {
      app: Boolean
    },
    computed: {
      applicationProperty() {
        return value;
      }
    },
    watch: {
      app(x, prev) {
        prev ? this.removeApplication(true) : this.callUpdate();
      },
      applicationProperty(newVal, oldVal) {
        this.$vuetify.application.unregister(this._uid, oldVal);
      }
    },
    activated() {
      this.callUpdate();
    },
    created() {
      for (let i = 0, length = events.length; i < length; i++) {
        this.$watch(events[i], this.callUpdate);
      }
      this.callUpdate();
    },
    mounted() {
      this.callUpdate();
    },
    deactivated() {
      this.removeApplication();
    },
    destroyed() {
      this.removeApplication();
    },
    methods: {
      callUpdate() {
        if (!this.app)
          return;
        this.$vuetify.application.register(this._uid, this.applicationProperty, this.updateApplication());
      },
      removeApplication(force = false) {
        if (!force && !this.app)
          return;
        this.$vuetify.application.unregister(this._uid, this.applicationProperty);
      },
      updateApplication: () => 0
    }
  });
}
function defaultConditional() {
  return true;
}
function directive$1(e, el, binding) {
  const handler = typeof binding.value === "function" ? binding.value : binding.value.handler;
  const isActive = typeof binding.value === "object" && binding.value.closeConditional || defaultConditional;
  if (!e || isActive(e) === false)
    return;
  const elements = (typeof binding.value === "object" && binding.value.include || (() => []))();
  elements.push(el);
  !elements.some((el2) => el2.contains(e.target)) && setTimeout(() => {
    isActive(e) && handler && handler(e);
  }, 0);
}
const ClickOutside = {
  inserted(el, binding) {
    const onClick = (e) => directive$1(e, el, binding);
    const app = document.querySelector("[data-app]") || document.body;
    app.addEventListener("click", onClick, true);
    el._clickOutside = onClick;
  },
  unbind(el) {
    if (!el._clickOutside)
      return;
    const app = document.querySelector("[data-app]") || document.body;
    app && app.removeEventListener("click", el._clickOutside, true);
    delete el._clickOutside;
  }
};
var ClickOutside$1 = ClickOutside;
function inserted$1(el, binding) {
  const callback = binding.value;
  const options2 = binding.options || {
    passive: true
  };
  window.addEventListener("resize", callback, options2);
  el._onResize = {
    callback,
    options: options2
  };
  if (!binding.modifiers || !binding.modifiers.quiet) {
    callback();
  }
}
function unbind$2(el) {
  if (!el._onResize)
    return;
  const {
    callback,
    options: options2
  } = el._onResize;
  window.removeEventListener("resize", callback, options2);
  delete el._onResize;
}
const Resize = {
  inserted: inserted$1,
  unbind: unbind$2
};
var Resize$1 = Resize;
const rippleStop = Symbol("rippleStop");
const DELAY_RIPPLE = 80;
function transform(el, value) {
  el.style.transform = value;
  el.style.webkitTransform = value;
}
function opacity(el, value) {
  el.style.opacity = value.toString();
}
function isTouchEvent(e) {
  return e.constructor.name === "TouchEvent";
}
function isKeyboardEvent(e) {
  return e.constructor.name === "KeyboardEvent";
}
const calculate = (e, el, value = {}) => {
  let localX = 0;
  let localY = 0;
  if (!isKeyboardEvent(e)) {
    const offset = el.getBoundingClientRect();
    const target = isTouchEvent(e) ? e.touches[e.touches.length - 1] : e;
    localX = target.clientX - offset.left;
    localY = target.clientY - offset.top;
  }
  let radius = 0;
  let scale = 0.3;
  if (el._ripple && el._ripple.circle) {
    scale = 0.15;
    radius = el.clientWidth / 2;
    radius = value.center ? radius : radius + Math.sqrt((localX - radius) ** 2 + (localY - radius) ** 2) / 4;
  } else {
    radius = Math.sqrt(el.clientWidth ** 2 + el.clientHeight ** 2) / 2;
  }
  const centerX = `${(el.clientWidth - radius * 2) / 2}px`;
  const centerY = `${(el.clientHeight - radius * 2) / 2}px`;
  const x = value.center ? centerX : `${localX - radius}px`;
  const y = value.center ? centerY : `${localY - radius}px`;
  return {
    radius,
    scale,
    x,
    y,
    centerX,
    centerY
  };
};
const ripples = {
  show(e, el, value = {}) {
    if (!el._ripple || !el._ripple.enabled) {
      return;
    }
    const container = document.createElement("span");
    const animation = document.createElement("span");
    container.appendChild(animation);
    container.className = "v-ripple__container";
    if (value.class) {
      container.className += ` ${value.class}`;
    }
    const {
      radius,
      scale,
      x,
      y,
      centerX,
      centerY
    } = calculate(e, el, value);
    const size = `${radius * 2}px`;
    animation.className = "v-ripple__animation";
    animation.style.width = size;
    animation.style.height = size;
    el.appendChild(container);
    const computed = window.getComputedStyle(el);
    if (computed && computed.position === "static") {
      el.style.position = "relative";
      el.dataset.previousPosition = "static";
    }
    animation.classList.add("v-ripple__animation--enter");
    animation.classList.add("v-ripple__animation--visible");
    transform(animation, `translate(${x}, ${y}) scale3d(${scale},${scale},${scale})`);
    opacity(animation, 0);
    animation.dataset.activated = String(performance.now());
    setTimeout(() => {
      animation.classList.remove("v-ripple__animation--enter");
      animation.classList.add("v-ripple__animation--in");
      transform(animation, `translate(${centerX}, ${centerY}) scale3d(1,1,1)`);
      opacity(animation, 0.25);
    }, 0);
  },
  hide(el) {
    if (!el || !el._ripple || !el._ripple.enabled)
      return;
    const ripples2 = el.getElementsByClassName("v-ripple__animation");
    if (ripples2.length === 0)
      return;
    const animation = ripples2[ripples2.length - 1];
    if (animation.dataset.isHiding)
      return;
    else
      animation.dataset.isHiding = "true";
    const diff = performance.now() - Number(animation.dataset.activated);
    const delay = Math.max(250 - diff, 0);
    setTimeout(() => {
      animation.classList.remove("v-ripple__animation--in");
      animation.classList.add("v-ripple__animation--out");
      opacity(animation, 0);
      setTimeout(() => {
        const ripples3 = el.getElementsByClassName("v-ripple__animation");
        if (ripples3.length === 1 && el.dataset.previousPosition) {
          el.style.position = el.dataset.previousPosition;
          delete el.dataset.previousPosition;
        }
        animation.parentNode && el.removeChild(animation.parentNode);
      }, 300);
    }, delay);
  }
};
function isRippleEnabled(value) {
  return typeof value === "undefined" || !!value;
}
function rippleShow(e) {
  const value = {};
  const element = e.currentTarget;
  if (!element || !element._ripple || element._ripple.touched || e[rippleStop])
    return;
  e[rippleStop] = true;
  if (isTouchEvent(e)) {
    element._ripple.touched = true;
    element._ripple.isTouch = true;
  } else {
    if (element._ripple.isTouch)
      return;
  }
  value.center = element._ripple.centered || isKeyboardEvent(e);
  if (element._ripple.class) {
    value.class = element._ripple.class;
  }
  if (isTouchEvent(e)) {
    if (element._ripple.showTimerCommit)
      return;
    element._ripple.showTimerCommit = () => {
      ripples.show(e, element, value);
    };
    element._ripple.showTimer = window.setTimeout(() => {
      if (element && element._ripple && element._ripple.showTimerCommit) {
        element._ripple.showTimerCommit();
        element._ripple.showTimerCommit = null;
      }
    }, DELAY_RIPPLE);
  } else {
    ripples.show(e, element, value);
  }
}
function rippleHide(e) {
  const element = e.currentTarget;
  if (!element || !element._ripple)
    return;
  window.clearTimeout(element._ripple.showTimer);
  if (e.type === "touchend" && element._ripple.showTimerCommit) {
    element._ripple.showTimerCommit();
    element._ripple.showTimerCommit = null;
    element._ripple.showTimer = setTimeout(() => {
      rippleHide(e);
    });
    return;
  }
  window.setTimeout(() => {
    if (element._ripple) {
      element._ripple.touched = false;
    }
  });
  ripples.hide(element);
}
function rippleCancelShow(e) {
  const element = e.currentTarget;
  if (!element || !element._ripple)
    return;
  if (element._ripple.showTimerCommit) {
    element._ripple.showTimerCommit = null;
  }
  window.clearTimeout(element._ripple.showTimer);
}
let keyboardRipple = false;
function keyboardRippleShow(e) {
  if (!keyboardRipple && (e.keyCode === keyCodes.enter || e.keyCode === keyCodes.space)) {
    keyboardRipple = true;
    rippleShow(e);
  }
}
function keyboardRippleHide(e) {
  keyboardRipple = false;
  rippleHide(e);
}
function focusRippleHide(e) {
  if (keyboardRipple === true) {
    keyboardRipple = false;
    rippleHide(e);
  }
}
function updateRipple(el, binding, wasEnabled) {
  const enabled = isRippleEnabled(binding.value);
  if (!enabled) {
    ripples.hide(el);
  }
  el._ripple = el._ripple || {};
  el._ripple.enabled = enabled;
  const value = binding.value || {};
  if (value.center) {
    el._ripple.centered = true;
  }
  if (value.class) {
    el._ripple.class = binding.value.class;
  }
  if (value.circle) {
    el._ripple.circle = value.circle;
  }
  if (enabled && !wasEnabled) {
    el.addEventListener("touchstart", rippleShow, {
      passive: true
    });
    el.addEventListener("touchend", rippleHide, {
      passive: true
    });
    el.addEventListener("touchmove", rippleCancelShow, {
      passive: true
    });
    el.addEventListener("touchcancel", rippleHide);
    el.addEventListener("mousedown", rippleShow);
    el.addEventListener("mouseup", rippleHide);
    el.addEventListener("mouseleave", rippleHide);
    el.addEventListener("keydown", keyboardRippleShow);
    el.addEventListener("keyup", keyboardRippleHide);
    el.addEventListener("blur", focusRippleHide);
    el.addEventListener("dragstart", rippleHide, {
      passive: true
    });
  } else if (!enabled && wasEnabled) {
    removeListeners(el);
  }
}
function removeListeners(el) {
  el.removeEventListener("mousedown", rippleShow);
  el.removeEventListener("touchstart", rippleShow);
  el.removeEventListener("touchend", rippleHide);
  el.removeEventListener("touchmove", rippleCancelShow);
  el.removeEventListener("touchcancel", rippleHide);
  el.removeEventListener("mouseup", rippleHide);
  el.removeEventListener("mouseleave", rippleHide);
  el.removeEventListener("keydown", keyboardRippleShow);
  el.removeEventListener("keyup", keyboardRippleHide);
  el.removeEventListener("dragstart", rippleHide);
  el.removeEventListener("blur", focusRippleHide);
}
function directive(el, binding, node) {
  updateRipple(el, binding, false);
}
function unbind$1(el) {
  delete el._ripple;
  removeListeners(el);
}
function update(el, binding) {
  if (binding.value === binding.oldValue) {
    return;
  }
  const wasEnabled = isRippleEnabled(binding.oldValue);
  updateRipple(el, binding, wasEnabled);
}
const Ripple = {
  bind: directive,
  unbind: unbind$1,
  update
};
var ripple = Ripple;
const handleGesture = (wrapper) => {
  const {
    touchstartX,
    touchendX,
    touchstartY,
    touchendY
  } = wrapper;
  const dirRatio = 0.5;
  const minDistance = 16;
  wrapper.offsetX = touchendX - touchstartX;
  wrapper.offsetY = touchendY - touchstartY;
  if (Math.abs(wrapper.offsetY) < dirRatio * Math.abs(wrapper.offsetX)) {
    wrapper.left && touchendX < touchstartX - minDistance && wrapper.left(wrapper);
    wrapper.right && touchendX > touchstartX + minDistance && wrapper.right(wrapper);
  }
  if (Math.abs(wrapper.offsetX) < dirRatio * Math.abs(wrapper.offsetY)) {
    wrapper.up && touchendY < touchstartY - minDistance && wrapper.up(wrapper);
    wrapper.down && touchendY > touchstartY + minDistance && wrapper.down(wrapper);
  }
};
function touchstart(event, wrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchstartX = touch.clientX;
  wrapper.touchstartY = touch.clientY;
  wrapper.start && wrapper.start(Object.assign(event, wrapper));
}
function touchend(event, wrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchendX = touch.clientX;
  wrapper.touchendY = touch.clientY;
  wrapper.end && wrapper.end(Object.assign(event, wrapper));
  handleGesture(wrapper);
}
function touchmove(event, wrapper) {
  const touch = event.changedTouches[0];
  wrapper.touchmoveX = touch.clientX;
  wrapper.touchmoveY = touch.clientY;
  wrapper.move && wrapper.move(Object.assign(event, wrapper));
}
function createHandlers(value) {
  const wrapper = {
    touchstartX: 0,
    touchstartY: 0,
    touchendX: 0,
    touchendY: 0,
    touchmoveX: 0,
    touchmoveY: 0,
    offsetX: 0,
    offsetY: 0,
    left: value.left,
    right: value.right,
    up: value.up,
    down: value.down,
    start: value.start,
    move: value.move,
    end: value.end
  };
  return {
    touchstart: (e) => touchstart(e, wrapper),
    touchend: (e) => touchend(e, wrapper),
    touchmove: (e) => touchmove(e, wrapper)
  };
}
function inserted(el, binding, vnode) {
  const value = binding.value;
  const target = value.parent ? el.parentElement : el;
  const options2 = value.options || {
    passive: true
  };
  if (!target)
    return;
  const handlers = createHandlers(binding.value);
  target._touchHandlers = Object(target._touchHandlers);
  target._touchHandlers[vnode.context._uid] = handlers;
  keys$1(handlers).forEach((eventName) => {
    target.addEventListener(eventName, handlers[eventName], options2);
  });
}
function unbind(el, binding, vnode) {
  const target = binding.value.parent ? el.parentElement : el;
  if (!target || !target._touchHandlers)
    return;
  const handlers = target._touchHandlers[vnode.context._uid];
  keys$1(handlers).forEach((eventName) => {
    target.removeEventListener(eventName, handlers[eventName]);
  });
  delete target._touchHandlers[vnode.context._uid];
}
const Touch = {
  inserted,
  unbind
};
var Touch$1 = Touch;
var Scrollable = Vue.extend({
  name: "scrollable",
  directives: {
    Scroll
  },
  props: {
    scrollTarget: String,
    scrollThreshold: [String, Number]
  },
  data: () => ({
    currentScroll: 0,
    currentThreshold: 0,
    isActive: false,
    isScrollingUp: false,
    previousScroll: 0,
    savedScroll: 0,
    target: null
  }),
  computed: {
    canScroll() {
      return typeof window !== "undefined";
    },
    computedScrollThreshold() {
      return this.scrollThreshold ? Number(this.scrollThreshold) : 300;
    }
  },
  watch: {
    isScrollingUp() {
      this.savedScroll = this.savedScroll || this.currentScroll;
    },
    isActive() {
      this.savedScroll = 0;
    }
  },
  mounted() {
    if (this.scrollTarget) {
      this.target = document.querySelector(this.scrollTarget);
      if (!this.target) {
        consoleWarn(`Unable to locate element with identifier ${this.scrollTarget}`, this);
      }
    }
  },
  methods: {
    onScroll() {
      if (!this.canScroll)
        return;
      this.previousScroll = this.currentScroll;
      this.currentScroll = this.target ? this.target.scrollTop : window.pageYOffset;
      this.isScrollingUp = this.currentScroll < this.previousScroll;
      this.currentThreshold = Math.abs(this.currentScroll - this.computedScrollThreshold);
      this.$nextTick(() => {
        if (Math.abs(this.currentScroll - this.savedScroll) > this.computedScrollThreshold)
          this.thresholdMet();
      });
    },
    thresholdMet() {
    }
  }
});
var SSRBootable = Vue.extend({
  name: "ssr-bootable",
  data: () => ({
    isBooted: false
  }),
  mounted() {
    window.requestAnimationFrame(() => {
      this.$el.setAttribute("data-booted", "true");
      this.isBooted = true;
    });
  }
});
function factory$2(prop = "value", event = "input") {
  return Vue.extend({
    name: "toggleable",
    model: {
      prop,
      event
    },
    props: {
      [prop]: {
        required: false
      }
    },
    data() {
      return {
        isActive: !!this[prop]
      };
    },
    watch: {
      [prop](val) {
        this.isActive = !!val;
      },
      isActive(val) {
        !!val !== this[prop] && this.$emit(event, val);
      }
    }
  });
}
const Toggleable = factory$2();
var Toggleable$1 = Toggleable;
const baseMixins$t = mixins(VToolbar, Scrollable, SSRBootable, Toggleable$1, applicationable("top", ["clippedLeft", "clippedRight", "computedHeight", "invertedScroll", "isExtended", "isProminent", "value"]));
var VAppBar = baseMixins$t.extend({
  name: "v-app-bar",
  directives: {
    Scroll: Scroll$1
  },
  provide() {
    return {
      VAppBar: this
    };
  },
  props: {
    clippedLeft: Boolean,
    clippedRight: Boolean,
    collapseOnScroll: Boolean,
    elevateOnScroll: Boolean,
    fadeImgOnScroll: Boolean,
    hideOnScroll: Boolean,
    invertedScroll: Boolean,
    scrollOffScreen: Boolean,
    shrinkOnScroll: Boolean,
    value: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isActive: this.value
    };
  },
  computed: {
    applicationProperty() {
      return !this.bottom ? "top" : "bottom";
    },
    canScroll() {
      return Scrollable.options.computed.canScroll.call(this) && (this.invertedScroll || this.elevateOnScroll || this.hideOnScroll || this.collapseOnScroll || this.isBooted || !this.value);
    },
    classes() {
      return __spreadProps(__spreadValues({}, VToolbar.options.computed.classes.call(this)), {
        "v-toolbar--collapse": this.collapse || this.collapseOnScroll,
        "v-app-bar": true,
        "v-app-bar--clipped": this.clippedLeft || this.clippedRight,
        "v-app-bar--fade-img-on-scroll": this.fadeImgOnScroll,
        "v-app-bar--elevate-on-scroll": this.elevateOnScroll,
        "v-app-bar--fixed": !this.absolute && (this.app || this.fixed),
        "v-app-bar--hide-shadow": this.hideShadow,
        "v-app-bar--is-scrolled": this.currentScroll > 0,
        "v-app-bar--shrink-on-scroll": this.shrinkOnScroll
      });
    },
    scrollRatio() {
      const threshold = this.computedScrollThreshold;
      return Math.max((threshold - this.currentScroll) / threshold, 0);
    },
    computedContentHeight() {
      if (!this.shrinkOnScroll)
        return VToolbar.options.computed.computedContentHeight.call(this);
      const min = this.dense ? 48 : 56;
      const max = this.computedOriginalHeight;
      return min + (max - min) * this.scrollRatio;
    },
    computedFontSize() {
      if (!this.isProminent)
        return void 0;
      const min = 1.25;
      const max = 1.5;
      return min + (max - min) * this.scrollRatio;
    },
    computedLeft() {
      if (!this.app || this.clippedLeft)
        return 0;
      return this.$vuetify.application.left;
    },
    computedMarginTop() {
      if (!this.app)
        return 0;
      return this.$vuetify.application.bar;
    },
    computedOpacity() {
      if (!this.fadeImgOnScroll)
        return void 0;
      return this.scrollRatio;
    },
    computedOriginalHeight() {
      let height = VToolbar.options.computed.computedContentHeight.call(this);
      if (this.isExtended)
        height += parseInt(this.extensionHeight);
      return height;
    },
    computedRight() {
      if (!this.app || this.clippedRight)
        return 0;
      return this.$vuetify.application.right;
    },
    computedScrollThreshold() {
      if (this.scrollThreshold)
        return Number(this.scrollThreshold);
      return this.computedOriginalHeight - (this.dense ? 48 : 56);
    },
    computedTransform() {
      if (!this.canScroll || this.elevateOnScroll && this.currentScroll === 0 && this.isActive)
        return 0;
      if (this.isActive)
        return 0;
      const scrollOffScreen = this.scrollOffScreen ? this.computedHeight : this.computedContentHeight;
      return this.bottom ? scrollOffScreen : -scrollOffScreen;
    },
    hideShadow() {
      if (this.elevateOnScroll && this.isExtended) {
        return this.currentScroll < this.computedScrollThreshold;
      }
      if (this.elevateOnScroll) {
        return this.currentScroll === 0 || this.computedTransform < 0;
      }
      return (!this.isExtended || this.scrollOffScreen) && this.computedTransform !== 0;
    },
    isCollapsed() {
      if (!this.collapseOnScroll) {
        return VToolbar.options.computed.isCollapsed.call(this);
      }
      return this.currentScroll > 0;
    },
    isProminent() {
      return VToolbar.options.computed.isProminent.call(this) || this.shrinkOnScroll;
    },
    styles() {
      return __spreadProps(__spreadValues({}, VToolbar.options.computed.styles.call(this)), {
        fontSize: convertToUnit(this.computedFontSize, "rem"),
        marginTop: convertToUnit(this.computedMarginTop),
        transform: `translateY(${convertToUnit(this.computedTransform)})`,
        left: convertToUnit(this.computedLeft),
        right: convertToUnit(this.computedRight)
      });
    }
  },
  watch: {
    canScroll: "onScroll",
    computedTransform() {
      if (!this.canScroll || !this.clippedLeft && !this.clippedRight)
        return;
      this.callUpdate();
    },
    invertedScroll(val) {
      this.isActive = !val || this.currentScroll !== 0;
    }
  },
  created() {
    if (this.invertedScroll)
      this.isActive = false;
  },
  methods: {
    genBackground() {
      const render = VToolbar.options.methods.genBackground.call(this);
      render.data = this._b(render.data || {}, render.tag, {
        style: {
          opacity: this.computedOpacity
        }
      });
      return render;
    },
    updateApplication() {
      return this.invertedScroll ? 0 : this.computedHeight + this.computedTransform;
    },
    thresholdMet() {
      if (this.invertedScroll) {
        this.isActive = this.currentScroll > this.computedScrollThreshold;
        return;
      }
      if (this.hideOnScroll) {
        this.isActive = this.isScrollingUp || this.currentScroll < this.computedScrollThreshold;
      }
      if (this.currentThreshold < this.computedScrollThreshold)
        return;
      this.savedScroll = this.currentScroll;
    }
  },
  render(h) {
    const render = VToolbar.options.render.call(this, h);
    render.data = render.data || {};
    if (this.canScroll) {
      render.data.directives = render.data.directives || [];
      render.data.directives.push({
        arg: this.scrollTarget,
        name: "scroll",
        value: this.onScroll
      });
    }
    return render;
  }
});
var Sizeable = Vue.extend({
  name: "sizeable",
  props: {
    large: Boolean,
    small: Boolean,
    xLarge: Boolean,
    xSmall: Boolean
  },
  computed: {
    medium() {
      return Boolean(!this.xSmall && !this.small && !this.large && !this.xLarge);
    },
    sizeableClasses() {
      return {
        "v-size--x-small": this.xSmall,
        "v-size--small": this.small,
        "v-size--default": this.medium,
        "v-size--large": this.large,
        "v-size--x-large": this.xLarge
      };
    }
  }
});
var SIZE_MAP;
(function(SIZE_MAP2) {
  SIZE_MAP2["xSmall"] = "12px";
  SIZE_MAP2["small"] = "16px";
  SIZE_MAP2["default"] = "24px";
  SIZE_MAP2["medium"] = "28px";
  SIZE_MAP2["large"] = "36px";
  SIZE_MAP2["xLarge"] = "40px";
})(SIZE_MAP || (SIZE_MAP = {}));
function isFontAwesome5(iconType) {
  return ["fas", "far", "fal", "fab", "fad"].some((val) => iconType.includes(val));
}
function isSvgPath(icon) {
  return /^[mzlhvcsqta]\s*[-+.0-9][^mlhvzcsqta]+/i.test(icon) && /[\dz]$/i.test(icon) && icon.length > 4;
}
const VIcon = mixins(BindsAttrs, Colorable, Sizeable, Themeable$1).extend({
  name: "v-icon",
  props: {
    dense: Boolean,
    disabled: Boolean,
    left: Boolean,
    right: Boolean,
    size: [Number, String],
    tag: {
      type: String,
      required: false,
      default: "i"
    }
  },
  computed: {
    medium() {
      return false;
    },
    hasClickListener() {
      return Boolean(this.listeners$.click || this.listeners$["!click"]);
    }
  },
  methods: {
    getIcon() {
      let iconName = "";
      if (this.$slots.default)
        iconName = this.$slots.default[0].text.trim();
      return remapInternalIcon(this, iconName);
    },
    getSize() {
      const sizes = {
        xSmall: this.xSmall,
        small: this.small,
        medium: this.medium,
        large: this.large,
        xLarge: this.xLarge
      };
      const explicitSize = keys$1(sizes).find((key) => sizes[key]);
      return explicitSize && SIZE_MAP[explicitSize] || convertToUnit(this.size);
    },
    getDefaultData() {
      return {
        staticClass: "v-icon notranslate",
        class: {
          "v-icon--disabled": this.disabled,
          "v-icon--left": this.left,
          "v-icon--link": this.hasClickListener,
          "v-icon--right": this.right,
          "v-icon--dense": this.dense
        },
        attrs: __spreadValues({
          "aria-hidden": !this.hasClickListener,
          disabled: this.hasClickListener && this.disabled,
          type: this.hasClickListener ? "button" : void 0
        }, this.attrs$),
        on: this.listeners$
      };
    },
    getSvgWrapperData() {
      const fontSize = this.getSize();
      const wrapperData = __spreadProps(__spreadValues({}, this.getDefaultData()), {
        style: fontSize ? {
          fontSize,
          height: fontSize,
          width: fontSize
        } : void 0
      });
      this.applyColors(wrapperData);
      return wrapperData;
    },
    applyColors(data) {
      data.class = __spreadValues(__spreadValues({}, data.class), this.themeClasses);
      this.setTextColor(this.color, data);
    },
    renderFontIcon(icon, h) {
      const newChildren = [];
      const data = this.getDefaultData();
      let iconType = "material-icons";
      const delimiterIndex = icon.indexOf("-");
      const isMaterialIcon = delimiterIndex <= -1;
      if (isMaterialIcon) {
        newChildren.push(icon);
      } else {
        iconType = icon.slice(0, delimiterIndex);
        if (isFontAwesome5(iconType))
          iconType = "";
      }
      data.class[iconType] = true;
      data.class[icon] = !isMaterialIcon;
      const fontSize = this.getSize();
      if (fontSize)
        data.style = {
          fontSize
        };
      this.applyColors(data);
      return h(this.hasClickListener ? "button" : this.tag, data, newChildren);
    },
    renderSvgIcon(icon, h) {
      const svgData = {
        class: "v-icon__svg",
        attrs: {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: "0 0 24 24",
          role: "img",
          "aria-hidden": true
        }
      };
      const size = this.getSize();
      if (size) {
        svgData.style = {
          fontSize: size,
          height: size,
          width: size
        };
      }
      return h(this.hasClickListener ? "button" : "span", this.getSvgWrapperData(), [h("svg", svgData, [h("path", {
        attrs: {
          d: icon
        }
      })])]);
    },
    renderSvgIconComponent(icon, h) {
      const data = {
        class: {
          "v-icon__component": true
        }
      };
      const size = this.getSize();
      if (size) {
        data.style = {
          fontSize: size,
          height: size,
          width: size
        };
      }
      this.applyColors(data);
      const component = icon.component;
      data.props = icon.props;
      data.nativeOn = data.on;
      return h(this.hasClickListener ? "button" : "span", this.getSvgWrapperData(), [h(component, data)]);
    }
  },
  render(h) {
    const icon = this.getIcon();
    if (typeof icon === "string") {
      if (isSvgPath(icon)) {
        return this.renderSvgIcon(icon, h);
      }
      return this.renderFontIcon(icon, h);
    }
    return this.renderSvgIconComponent(icon, h);
  }
});
var VIcon$1 = Vue.extend({
  name: "v-icon",
  $_wrapperFor: VIcon,
  functional: true,
  render(h, {
    data,
    children
  }) {
    let iconName = "";
    if (data.domProps) {
      iconName = data.domProps.textContent || data.domProps.innerHTML || iconName;
      delete data.domProps.textContent;
      delete data.domProps.innerHTML;
    }
    return h(VIcon, data, iconName ? [iconName] : children);
  }
});
var VProgressCircular = Colorable.extend({
  name: "v-progress-circular",
  props: {
    button: Boolean,
    indeterminate: Boolean,
    rotate: {
      type: [Number, String],
      default: 0
    },
    size: {
      type: [Number, String],
      default: 32
    },
    width: {
      type: [Number, String],
      default: 4
    },
    value: {
      type: [Number, String],
      default: 0
    }
  },
  data: () => ({
    radius: 20
  }),
  computed: {
    calculatedSize() {
      return Number(this.size) + (this.button ? 8 : 0);
    },
    circumference() {
      return 2 * Math.PI * this.radius;
    },
    classes() {
      return {
        "v-progress-circular--indeterminate": this.indeterminate,
        "v-progress-circular--button": this.button
      };
    },
    normalizedValue() {
      if (this.value < 0) {
        return 0;
      }
      if (this.value > 100) {
        return 100;
      }
      return parseFloat(this.value);
    },
    strokeDashArray() {
      return Math.round(this.circumference * 1e3) / 1e3;
    },
    strokeDashOffset() {
      return (100 - this.normalizedValue) / 100 * this.circumference + "px";
    },
    strokeWidth() {
      return Number(this.width) / +this.size * this.viewBoxSize * 2;
    },
    styles() {
      return {
        height: convertToUnit(this.calculatedSize),
        width: convertToUnit(this.calculatedSize)
      };
    },
    svgStyles() {
      return {
        transform: `rotate(${Number(this.rotate)}deg)`
      };
    },
    viewBoxSize() {
      return this.radius / (1 - Number(this.width) / +this.size);
    }
  },
  methods: {
    genCircle(name, offset) {
      return this.$createElement("circle", {
        class: `v-progress-circular__${name}`,
        attrs: {
          fill: "transparent",
          cx: 2 * this.viewBoxSize,
          cy: 2 * this.viewBoxSize,
          r: this.radius,
          "stroke-width": this.strokeWidth,
          "stroke-dasharray": this.strokeDashArray,
          "stroke-dashoffset": offset
        }
      });
    },
    genSvg() {
      const children = [this.indeterminate || this.genCircle("underlay", 0), this.genCircle("overlay", this.strokeDashOffset)];
      return this.$createElement("svg", {
        style: this.svgStyles,
        attrs: {
          xmlns: "http://www.w3.org/2000/svg",
          viewBox: `${this.viewBoxSize} ${this.viewBoxSize} ${2 * this.viewBoxSize} ${2 * this.viewBoxSize}`
        }
      }, children);
    },
    genInfo() {
      return this.$createElement("div", {
        staticClass: "v-progress-circular__info"
      }, this.$slots.default);
    }
  },
  render(h) {
    return h("div", this.setTextColor(this.color, {
      staticClass: "v-progress-circular",
      attrs: {
        role: "progressbar",
        "aria-valuemin": 0,
        "aria-valuemax": 100,
        "aria-valuenow": this.indeterminate ? void 0 : this.normalizedValue
      },
      class: this.classes,
      style: this.styles,
      on: this.$listeners
    }), [this.genSvg(), this.genInfo()]);
  }
});
function generateWarning(child, parent) {
  return () => consoleWarn(`The ${child} component must be used inside a ${parent}`);
}
function inject(namespace, child, parent) {
  const defaultImpl = child && parent ? {
    register: generateWarning(child, parent),
    unregister: generateWarning(child, parent)
  } : null;
  return Vue.extend({
    name: "registrable-inject",
    inject: {
      [namespace]: {
        default: defaultImpl
      }
    }
  });
}
function provide(namespace, self = false) {
  return Vue.extend({
    name: "registrable-provide",
    provide() {
      return {
        [namespace]: self ? this : {
          register: this.register,
          unregister: this.unregister
        }
      };
    }
  });
}
function factory$1(namespace, child, parent) {
  return inject(namespace, child, parent).extend({
    name: "groupable",
    props: {
      activeClass: {
        type: String,
        default() {
          if (!this[namespace])
            return void 0;
          return this[namespace].activeClass;
        }
      },
      disabled: Boolean
    },
    data() {
      return {
        isActive: false
      };
    },
    computed: {
      groupClasses() {
        if (!this.activeClass)
          return {};
        return {
          [this.activeClass]: this.isActive
        };
      }
    },
    created() {
      this[namespace] && this[namespace].register(this);
    },
    beforeDestroy() {
      this[namespace] && this[namespace].unregister(this);
    },
    methods: {
      toggle() {
        this.$emit("change");
      }
    }
  });
}
factory$1("itemGroup");
var Routable = Vue.extend({
  name: "routable",
  directives: {
    Ripple: ripple
  },
  props: {
    activeClass: String,
    append: Boolean,
    disabled: Boolean,
    exact: {
      type: Boolean,
      default: void 0
    },
    exactActiveClass: String,
    link: Boolean,
    href: [String, Object],
    to: [String, Object],
    nuxt: Boolean,
    replace: Boolean,
    ripple: {
      type: [Boolean, Object],
      default: null
    },
    tag: String,
    target: String
  },
  data: () => ({
    isActive: false,
    proxyClass: ""
  }),
  computed: {
    classes() {
      const classes = {};
      if (this.to)
        return classes;
      if (this.activeClass)
        classes[this.activeClass] = this.isActive;
      if (this.proxyClass)
        classes[this.proxyClass] = this.isActive;
      return classes;
    },
    computedRipple() {
      var _this$ripple;
      return (_this$ripple = this.ripple) != null ? _this$ripple : !this.disabled && this.isClickable;
    },
    isClickable() {
      if (this.disabled)
        return false;
      return Boolean(this.isLink || this.$listeners.click || this.$listeners["!click"] || this.$attrs.tabindex);
    },
    isLink() {
      return this.to || this.href || this.link;
    },
    styles: () => ({})
  },
  watch: {
    $route: "onRouteChange"
  },
  methods: {
    click(e) {
      this.$emit("click", e);
    },
    generateRouteLink() {
      let exact = this.exact;
      let tag;
      const data = {
        attrs: {
          tabindex: "tabindex" in this.$attrs ? this.$attrs.tabindex : void 0
        },
        class: this.classes,
        style: this.styles,
        props: {},
        directives: [{
          name: "ripple",
          value: this.computedRipple
        }],
        [this.to ? "nativeOn" : "on"]: __spreadProps(__spreadValues({}, this.$listeners), {
          click: this.click
        }),
        ref: "link"
      };
      if (typeof this.exact === "undefined") {
        exact = this.to === "/" || this.to === Object(this.to) && this.to.path === "/";
      }
      if (this.to) {
        let activeClass = this.activeClass;
        let exactActiveClass = this.exactActiveClass || activeClass;
        if (this.proxyClass) {
          activeClass = `${activeClass} ${this.proxyClass}`.trim();
          exactActiveClass = `${exactActiveClass} ${this.proxyClass}`.trim();
        }
        tag = this.nuxt ? "nuxt-link" : "router-link";
        Object.assign(data.props, {
          to: this.to,
          exact,
          activeClass,
          exactActiveClass,
          append: this.append,
          replace: this.replace
        });
      } else {
        tag = this.href && "a" || this.tag || "div";
        if (tag === "a" && this.href)
          data.attrs.href = this.href;
      }
      if (this.target)
        data.attrs.target = this.target;
      return {
        tag,
        data
      };
    },
    onRouteChange() {
      if (!this.to || !this.$refs.link || !this.$route)
        return;
      const activeClass = `${this.activeClass} ${this.proxyClass || ""}`.trim();
      const path = `_vnode.data.class.${activeClass}`;
      this.$nextTick(() => {
        if (getObjectValueByPath(this.$refs.link, path)) {
          this.toggle();
        }
      });
    },
    toggle: () => {
    }
  }
});
const baseMixins$s = mixins(VSheet, Routable, Positionable, Sizeable, factory$1("btnToggle"), factory$2("inputValue"));
var VBtn = baseMixins$s.extend().extend({
  name: "v-btn",
  props: {
    activeClass: {
      type: String,
      default() {
        if (!this.btnToggle)
          return "";
        return this.btnToggle.activeClass;
      }
    },
    block: Boolean,
    depressed: Boolean,
    fab: Boolean,
    icon: Boolean,
    loading: Boolean,
    outlined: Boolean,
    plain: Boolean,
    retainFocusOnClick: Boolean,
    rounded: Boolean,
    tag: {
      type: String,
      default: "button"
    },
    text: Boolean,
    tile: Boolean,
    type: {
      type: String,
      default: "button"
    },
    value: null
  },
  data: () => ({
    proxyClass: "v-btn--active"
  }),
  computed: {
    classes() {
      return __spreadValues(__spreadValues(__spreadValues(__spreadValues(__spreadProps(__spreadValues({
        "v-btn": true
      }, Routable.options.computed.classes.call(this)), {
        "v-btn--absolute": this.absolute,
        "v-btn--block": this.block,
        "v-btn--bottom": this.bottom,
        "v-btn--disabled": this.disabled,
        "v-btn--is-elevated": this.isElevated,
        "v-btn--fab": this.fab,
        "v-btn--fixed": this.fixed,
        "v-btn--has-bg": this.hasBg,
        "v-btn--icon": this.icon,
        "v-btn--left": this.left,
        "v-btn--loading": this.loading,
        "v-btn--outlined": this.outlined,
        "v-btn--plain": this.plain,
        "v-btn--right": this.right,
        "v-btn--round": this.isRound,
        "v-btn--rounded": this.rounded,
        "v-btn--router": this.to,
        "v-btn--text": this.text,
        "v-btn--tile": this.tile,
        "v-btn--top": this.top
      }), this.themeClasses), this.groupClasses), this.elevationClasses), this.sizeableClasses);
    },
    computedElevation() {
      if (this.disabled)
        return void 0;
      return Elevatable.options.computed.computedElevation.call(this);
    },
    computedRipple() {
      var _this$ripple;
      const defaultRipple = this.icon || this.fab ? {
        circle: true
      } : true;
      if (this.disabled)
        return false;
      else
        return (_this$ripple = this.ripple) != null ? _this$ripple : defaultRipple;
    },
    hasBg() {
      return !this.text && !this.plain && !this.outlined && !this.icon;
    },
    isElevated() {
      return Boolean(!this.icon && !this.text && !this.outlined && !this.depressed && !this.disabled && !this.plain && (this.elevation == null || Number(this.elevation) > 0));
    },
    isRound() {
      return Boolean(this.icon || this.fab);
    },
    styles() {
      return __spreadValues({}, this.measurableStyles);
    }
  },
  created() {
    const breakingProps = [["flat", "text"], ["outline", "outlined"], ["round", "rounded"]];
    breakingProps.forEach(([original, replacement]) => {
      if (this.$attrs.hasOwnProperty(original))
        breaking(original, replacement, this);
    });
  },
  methods: {
    click(e) {
      !this.retainFocusOnClick && !this.fab && e.detail && this.$el.blur();
      this.$emit("click", e);
      this.btnToggle && this.toggle();
    },
    genContent() {
      return this.$createElement("span", {
        staticClass: "v-btn__content"
      }, this.$slots.default);
    },
    genLoader() {
      return this.$createElement("span", {
        class: "v-btn__loader"
      }, this.$slots.loader || [this.$createElement(VProgressCircular, {
        props: {
          indeterminate: true,
          size: 23,
          width: 2
        }
      })]);
    }
  },
  render(h) {
    const children = [this.genContent(), this.loading && this.genLoader()];
    const {
      tag,
      data
    } = this.generateRouteLink();
    const setColor = this.hasBg ? this.setBackgroundColor : this.setTextColor;
    if (tag === "button") {
      data.attrs.type = this.type;
      data.attrs.disabled = this.disabled;
    }
    data.attrs.value = ["string", "number"].includes(typeof this.value) ? this.value : JSON.stringify(this.value);
    return h(tag, this.disabled ? data : setColor(this.color, data), children);
  }
});
var VAppBarNavIcon = Vue.extend({
  name: "v-app-bar-nav-icon",
  functional: true,
  render(h, {
    slots,
    listeners,
    props: props2,
    data
  }) {
    const d = Object.assign(data, {
      staticClass: `v-app-bar__nav-icon ${data.staticClass || ""}`.trim(),
      props: __spreadProps(__spreadValues({}, props2), {
        icon: true
      }),
      on: listeners
    });
    const defaultSlot = slots().default;
    return h(VBtn, d, defaultSlot || [h(VIcon$1, "$menu")]);
  }
});
const base = inject("VAppBar", "v-app-bar-title", "v-app-bar");
var VAppBarTitle = base.extend().extend({
  name: "v-app-bar-title",
  data: () => ({
    contentWidth: 0,
    left: 0,
    width: 0
  }),
  watch: {
    "$vuetify.breakpoint.width": "updateDimensions"
  },
  computed: {
    styles() {
      if (!this.contentWidth)
        return {};
      const min = this.width;
      const max = this.contentWidth;
      const ratio = easeInOutCubic(Math.min(1, this.VAppBar.scrollRatio * 1.5));
      return {
        width: convertToUnit(min + (max - min) * ratio),
        visibility: this.VAppBar.scrollRatio ? "visible" : "hidden"
      };
    }
  },
  mounted() {
    this.updateDimensions();
  },
  methods: {
    updateDimensions() {
      const dimensions = this.$refs.placeholder.getBoundingClientRect();
      this.width = dimensions.width;
      this.left = dimensions.left;
      this.contentWidth = this.$refs.content.scrollWidth;
    }
  },
  render(h) {
    return h("div", {
      class: "v-toolbar__title v-app-bar-title"
    }, [h("div", {
      class: "v-app-bar-title__content",
      style: this.styles,
      ref: "content"
    }, [this.$slots.default]), h("div", {
      class: "v-app-bar-title__placeholder",
      style: {
        visibility: this.VAppBar.scrollRatio ? "hidden" : "visible"
      },
      ref: "placeholder"
    }, [this.$slots.default])]);
  }
});
var Transitionable = Vue.extend({
  name: "transitionable",
  props: {
    mode: String,
    origin: String,
    transition: String
  }
});
var VAlert = mixins(VSheet, Toggleable$1, Transitionable).extend({
  name: "v-alert",
  props: {
    border: {
      type: String,
      validator(val) {
        return ["top", "right", "bottom", "left"].includes(val);
      }
    },
    closeLabel: {
      type: String,
      default: "$vuetify.close"
    },
    coloredBorder: Boolean,
    dense: Boolean,
    dismissible: Boolean,
    closeIcon: {
      type: String,
      default: "$cancel"
    },
    icon: {
      default: "",
      type: [Boolean, String],
      validator(val) {
        return typeof val === "string" || val === false;
      }
    },
    outlined: Boolean,
    prominent: Boolean,
    text: Boolean,
    type: {
      type: String,
      validator(val) {
        return ["info", "error", "success", "warning"].includes(val);
      }
    },
    value: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    __cachedBorder() {
      if (!this.border)
        return null;
      let data = {
        staticClass: "v-alert__border",
        class: {
          [`v-alert__border--${this.border}`]: true
        }
      };
      if (this.coloredBorder) {
        data = this.setBackgroundColor(this.computedColor, data);
        data.class["v-alert__border--has-color"] = true;
      }
      return this.$createElement("div", data);
    },
    __cachedDismissible() {
      if (!this.dismissible)
        return null;
      const color = this.iconColor;
      return this.$createElement(VBtn, {
        staticClass: "v-alert__dismissible",
        props: {
          color,
          icon: true,
          small: true
        },
        attrs: {
          "aria-label": this.$vuetify.lang.t(this.closeLabel)
        },
        on: {
          click: () => this.isActive = false
        }
      }, [this.$createElement(VIcon$1, {
        props: {
          color
        }
      }, this.closeIcon)]);
    },
    __cachedIcon() {
      if (!this.computedIcon)
        return null;
      return this.$createElement(VIcon$1, {
        staticClass: "v-alert__icon",
        props: {
          color: this.iconColor
        }
      }, this.computedIcon);
    },
    classes() {
      const classes = __spreadProps(__spreadValues({}, VSheet.options.computed.classes.call(this)), {
        "v-alert--border": Boolean(this.border),
        "v-alert--dense": this.dense,
        "v-alert--outlined": this.outlined,
        "v-alert--prominent": this.prominent,
        "v-alert--text": this.text
      });
      if (this.border) {
        classes[`v-alert--border-${this.border}`] = true;
      }
      return classes;
    },
    computedColor() {
      return this.color || this.type;
    },
    computedIcon() {
      if (this.icon === false)
        return false;
      if (typeof this.icon === "string" && this.icon)
        return this.icon;
      if (!["error", "info", "success", "warning"].includes(this.type))
        return false;
      return `$${this.type}`;
    },
    hasColoredIcon() {
      return this.hasText || Boolean(this.border) && this.coloredBorder;
    },
    hasText() {
      return this.text || this.outlined;
    },
    iconColor() {
      return this.hasColoredIcon ? this.computedColor : void 0;
    },
    isDark() {
      if (this.type && !this.coloredBorder && !this.outlined)
        return true;
      return Themeable$1.options.computed.isDark.call(this);
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("outline")) {
      breaking("outline", "outlined", this);
    }
  },
  methods: {
    genWrapper() {
      const children = [this.$slots.prepend || this.__cachedIcon, this.genContent(), this.__cachedBorder, this.$slots.append, this.$scopedSlots.close ? this.$scopedSlots.close({
        toggle: this.toggle
      }) : this.__cachedDismissible];
      const data = {
        staticClass: "v-alert__wrapper"
      };
      return this.$createElement("div", data, children);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-alert__content"
      }, this.$slots.default);
    },
    genAlert() {
      let data = {
        staticClass: "v-alert",
        attrs: {
          role: "alert"
        },
        on: this.listeners$,
        class: this.classes,
        style: this.styles,
        directives: [{
          name: "show",
          value: this.isActive
        }]
      };
      if (!this.coloredBorder) {
        const setColor = this.hasText ? this.setTextColor : this.setBackgroundColor;
        data = setColor(this.computedColor, data);
      }
      return this.$createElement("div", data, [this.genWrapper()]);
    },
    toggle() {
      this.isActive = !this.isActive;
    }
  },
  render(h) {
    const render = this.genAlert();
    if (!this.transition)
      return render;
    return h("transition", {
      props: {
        name: this.transition,
        origin: this.origin,
        mode: this.mode
      }
    }, [render]);
  }
});
function mergeTransitions(dest = [], ...transitions) {
  return Array().concat(dest, ...transitions);
}
function createSimpleTransition(name, origin = "top center 0", mode) {
  return {
    name,
    functional: true,
    props: {
      group: {
        type: Boolean,
        default: false
      },
      hideOnLeave: {
        type: Boolean,
        default: false
      },
      leaveAbsolute: {
        type: Boolean,
        default: false
      },
      mode: {
        type: String,
        default: mode
      },
      origin: {
        type: String,
        default: origin
      }
    },
    render(h, context) {
      const tag = `transition${context.props.group ? "-group" : ""}`;
      const data = {
        props: {
          name,
          mode: context.props.mode
        },
        on: {
          beforeEnter(el) {
            el.style.transformOrigin = context.props.origin;
            el.style.webkitTransformOrigin = context.props.origin;
          }
        }
      };
      if (context.props.leaveAbsolute) {
        data.on.leave = mergeTransitions(data.on.leave, (el) => el.style.position = "absolute");
      }
      if (context.props.hideOnLeave) {
        data.on.leave = mergeTransitions(data.on.leave, (el) => el.style.display = "none");
      }
      return h(tag, mergeData(context.data, data), context.children);
    }
  };
}
function createJavascriptTransition(name, functions, mode = "in-out") {
  return {
    name,
    functional: true,
    props: {
      mode: {
        type: String,
        default: mode
      }
    },
    render(h, context) {
      return h("transition", mergeData(context.data, {
        props: {
          name
        },
        on: functions
      }), context.children);
    }
  };
}
function ExpandTransitionGenerator(expandedParentClass = "", x = false) {
  const sizeProperty = x ? "width" : "height";
  const offsetProperty = `offset${upperFirst(sizeProperty)}`;
  return {
    beforeEnter(el) {
      el._parent = el.parentNode;
      el._initialStyle = {
        transition: el.style.transition,
        overflow: el.style.overflow,
        [sizeProperty]: el.style[sizeProperty]
      };
    },
    enter(el) {
      const initialStyle = el._initialStyle;
      el.style.setProperty("transition", "none", "important");
      el.style.overflow = "hidden";
      const offset = `${el[offsetProperty]}px`;
      el.style[sizeProperty] = "0";
      void el.offsetHeight;
      el.style.transition = initialStyle.transition;
      if (expandedParentClass && el._parent) {
        el._parent.classList.add(expandedParentClass);
      }
      requestAnimationFrame(() => {
        el.style[sizeProperty] = offset;
      });
    },
    afterEnter: resetStyles,
    enterCancelled: resetStyles,
    leave(el) {
      el._initialStyle = {
        transition: "",
        overflow: el.style.overflow,
        [sizeProperty]: el.style[sizeProperty]
      };
      el.style.overflow = "hidden";
      el.style[sizeProperty] = `${el[offsetProperty]}px`;
      void el.offsetHeight;
      requestAnimationFrame(() => el.style[sizeProperty] = "0");
    },
    afterLeave,
    leaveCancelled: afterLeave
  };
  function afterLeave(el) {
    if (expandedParentClass && el._parent) {
      el._parent.classList.remove(expandedParentClass);
    }
    resetStyles(el);
  }
  function resetStyles(el) {
    const size = el._initialStyle[sizeProperty];
    el.style.overflow = el._initialStyle.overflow;
    if (size != null)
      el.style[sizeProperty] = size;
    delete el._initialStyle;
  }
}
const VCarouselTransition = createSimpleTransition("carousel-transition");
const VCarouselReverseTransition = createSimpleTransition("carousel-reverse-transition");
const VTabTransition = createSimpleTransition("tab-transition");
const VTabReverseTransition = createSimpleTransition("tab-reverse-transition");
const VMenuTransition = createSimpleTransition("menu-transition");
const VFabTransition = createSimpleTransition("fab-transition", "center center", "out-in");
const VDialogTransition = createSimpleTransition("dialog-transition");
const VDialogBottomTransition = createSimpleTransition("dialog-bottom-transition");
const VDialogTopTransition = createSimpleTransition("dialog-top-transition");
const VFadeTransition = createSimpleTransition("fade-transition");
const VScaleTransition = createSimpleTransition("scale-transition");
const VScrollXTransition = createSimpleTransition("scroll-x-transition");
const VScrollXReverseTransition = createSimpleTransition("scroll-x-reverse-transition");
const VScrollYTransition = createSimpleTransition("scroll-y-transition");
const VScrollYReverseTransition = createSimpleTransition("scroll-y-reverse-transition");
const VSlideXTransition = createSimpleTransition("slide-x-transition");
const VSlideXReverseTransition = createSimpleTransition("slide-x-reverse-transition");
const VSlideYTransition = createSimpleTransition("slide-y-transition");
const VSlideYReverseTransition = createSimpleTransition("slide-y-reverse-transition");
const VExpandTransition = createJavascriptTransition("expand-transition", ExpandTransitionGenerator());
const VExpandXTransition = createJavascriptTransition("expand-x-transition", ExpandTransitionGenerator("", true));
var VChip = mixins(Colorable, Sizeable, Routable, Themeable$1, factory$1("chipGroup"), factory$2("inputValue")).extend({
  name: "v-chip",
  props: {
    active: {
      type: Boolean,
      default: true
    },
    activeClass: {
      type: String,
      default() {
        if (!this.chipGroup)
          return "";
        return this.chipGroup.activeClass;
      }
    },
    close: Boolean,
    closeIcon: {
      type: String,
      default: "$delete"
    },
    closeLabel: {
      type: String,
      default: "$vuetify.close"
    },
    disabled: Boolean,
    draggable: Boolean,
    filter: Boolean,
    filterIcon: {
      type: String,
      default: "$complete"
    },
    label: Boolean,
    link: Boolean,
    outlined: Boolean,
    pill: Boolean,
    tag: {
      type: String,
      default: "span"
    },
    textColor: String,
    value: null
  },
  data: () => ({
    proxyClass: "v-chip--active"
  }),
  computed: {
    classes() {
      return __spreadValues(__spreadValues(__spreadValues(__spreadProps(__spreadValues({
        "v-chip": true
      }, Routable.options.computed.classes.call(this)), {
        "v-chip--clickable": this.isClickable,
        "v-chip--disabled": this.disabled,
        "v-chip--draggable": this.draggable,
        "v-chip--label": this.label,
        "v-chip--link": this.isLink,
        "v-chip--no-color": !this.color,
        "v-chip--outlined": this.outlined,
        "v-chip--pill": this.pill,
        "v-chip--removable": this.hasClose
      }), this.themeClasses), this.sizeableClasses), this.groupClasses);
    },
    hasClose() {
      return Boolean(this.close);
    },
    isClickable() {
      return Boolean(Routable.options.computed.isClickable.call(this) || this.chipGroup);
    }
  },
  created() {
    const breakingProps = [["outline", "outlined"], ["selected", "input-value"], ["value", "active"], ["@input", "@active.sync"]];
    breakingProps.forEach(([original, replacement]) => {
      if (this.$attrs.hasOwnProperty(original))
        breaking(original, replacement, this);
    });
  },
  methods: {
    click(e) {
      this.$emit("click", e);
      this.chipGroup && this.toggle();
    },
    genFilter() {
      const children = [];
      if (this.isActive) {
        children.push(this.$createElement(VIcon$1, {
          staticClass: "v-chip__filter",
          props: {
            left: true
          }
        }, this.filterIcon));
      }
      return this.$createElement(VExpandXTransition, children);
    },
    genClose() {
      return this.$createElement(VIcon$1, {
        staticClass: "v-chip__close",
        props: {
          right: true,
          size: 18
        },
        attrs: {
          "aria-label": this.$vuetify.lang.t(this.closeLabel)
        },
        on: {
          click: (e) => {
            e.stopPropagation();
            e.preventDefault();
            this.$emit("click:close");
            this.$emit("update:active", false);
          }
        }
      }, this.closeIcon);
    },
    genContent() {
      return this.$createElement("span", {
        staticClass: "v-chip__content"
      }, [this.filter && this.genFilter(), this.$slots.default, this.hasClose && this.genClose()]);
    }
  },
  render(h) {
    const children = [this.genContent()];
    let {
      tag,
      data
    } = this.generateRouteLink();
    data.attrs = __spreadProps(__spreadValues({}, data.attrs), {
      draggable: this.draggable ? "true" : void 0,
      tabindex: this.chipGroup && !this.disabled ? 0 : data.attrs.tabindex
    });
    data.directives.push({
      name: "show",
      value: this.active
    });
    data = this.setBackgroundColor(this.color, data);
    const color = this.textColor || this.outlined && this.color;
    return h(tag, this.setTextColor(color, data), children);
  }
});
var VThemeProvider = Themeable$1.extend({
  name: "v-theme-provider",
  props: {
    root: Boolean
  },
  computed: {
    isDark() {
      return this.root ? this.rootIsDark : Themeable$1.options.computed.isDark.call(this);
    }
  },
  render() {
    return this.$slots.default && this.$slots.default.find((node) => !node.isComment && node.text !== " ");
  }
});
var Delayable = Vue.extend().extend({
  name: "delayable",
  props: {
    openDelay: {
      type: [Number, String],
      default: 0
    },
    closeDelay: {
      type: [Number, String],
      default: 0
    }
  },
  data: () => ({
    openTimeout: void 0,
    closeTimeout: void 0
  }),
  methods: {
    clearDelay() {
      clearTimeout(this.openTimeout);
      clearTimeout(this.closeTimeout);
    },
    runDelay(type2, cb) {
      this.clearDelay();
      const delay = parseInt(this[`${type2}Delay`], 10);
      this[`${type2}Timeout`] = setTimeout(cb || (() => {
        this.isActive = {
          open: true,
          close: false
        }[type2];
      }), delay);
    }
  }
});
const baseMixins$r = mixins(Delayable, Toggleable$1);
var Activatable = baseMixins$r.extend({
  name: "activatable",
  props: {
    activator: {
      default: null,
      validator: (val) => {
        return ["string", "object"].includes(typeof val);
      }
    },
    disabled: Boolean,
    internalActivator: Boolean,
    openOnHover: Boolean,
    openOnFocus: Boolean
  },
  data: () => ({
    activatorElement: null,
    activatorNode: [],
    events: ["click", "mouseenter", "mouseleave", "focus"],
    listeners: {}
  }),
  watch: {
    activator: "resetActivator",
    openOnFocus: "resetActivator",
    openOnHover: "resetActivator"
  },
  mounted() {
    const slotType = getSlotType(this, "activator", true);
    if (slotType && ["v-slot", "normal"].includes(slotType)) {
      consoleError(`The activator slot must be bound, try '<template v-slot:activator="{ on }"><v-btn v-on="on">'`, this);
    }
    this.addActivatorEvents();
  },
  beforeDestroy() {
    this.removeActivatorEvents();
  },
  methods: {
    addActivatorEvents() {
      if (!this.activator || this.disabled || !this.getActivator())
        return;
      this.listeners = this.genActivatorListeners();
      const keys2 = Object.keys(this.listeners);
      for (const key of keys2) {
        this.getActivator().addEventListener(key, this.listeners[key]);
      }
    },
    genActivator() {
      const node = getSlot(this, "activator", Object.assign(this.getValueProxy(), {
        on: this.genActivatorListeners(),
        attrs: this.genActivatorAttributes()
      })) || [];
      this.activatorNode = node;
      return node;
    },
    genActivatorAttributes() {
      return {
        role: "button",
        "aria-haspopup": true,
        "aria-expanded": String(this.isActive)
      };
    },
    genActivatorListeners() {
      if (this.disabled)
        return {};
      const listeners = {};
      if (this.openOnHover) {
        listeners.mouseenter = (e) => {
          this.getActivator(e);
          this.runDelay("open");
        };
        listeners.mouseleave = (e) => {
          this.getActivator(e);
          this.runDelay("close");
        };
      } else {
        listeners.click = (e) => {
          const activator = this.getActivator(e);
          if (activator)
            activator.focus();
          e.stopPropagation();
          this.isActive = !this.isActive;
        };
      }
      if (this.openOnFocus) {
        listeners.focus = (e) => {
          this.getActivator(e);
          e.stopPropagation();
          this.isActive = !this.isActive;
        };
      }
      return listeners;
    },
    getActivator(e) {
      if (this.activatorElement)
        return this.activatorElement;
      let activator = null;
      if (this.activator) {
        const target = this.internalActivator ? this.$el : document;
        if (typeof this.activator === "string") {
          activator = target.querySelector(this.activator);
        } else if (this.activator.$el) {
          activator = this.activator.$el;
        } else {
          activator = this.activator;
        }
      } else if (this.activatorNode.length === 1 || this.activatorNode.length && !e) {
        const vm = this.activatorNode[0].componentInstance;
        if (vm && vm.$options.mixins && vm.$options.mixins.some((m) => m.options && ["activatable", "menuable"].includes(m.options.name))) {
          activator = vm.getActivator();
        } else {
          activator = this.activatorNode[0].elm;
        }
      } else if (e) {
        activator = e.currentTarget || e.target;
      }
      this.activatorElement = activator;
      return this.activatorElement;
    },
    getContentSlot() {
      return getSlot(this, "default", this.getValueProxy(), true);
    },
    getValueProxy() {
      const self = this;
      return {
        get value() {
          return self.isActive;
        },
        set value(isActive) {
          self.isActive = isActive;
        }
      };
    },
    removeActivatorEvents() {
      if (!this.activator || !this.activatorElement)
        return;
      const keys2 = Object.keys(this.listeners);
      for (const key of keys2) {
        this.activatorElement.removeEventListener(key, this.listeners[key]);
      }
      this.listeners = {};
    },
    resetActivator() {
      this.removeActivatorEvents();
      this.activatorElement = null;
      this.getActivator();
      this.addActivatorEvents();
    }
  }
});
function searchChildren(children) {
  const results = [];
  for (let index = 0; index < children.length; index++) {
    const child = children[index];
    if (child.isActive && child.isDependent) {
      results.push(child);
    } else {
      results.push(...searchChildren(child.$children));
    }
  }
  return results;
}
var Dependent = mixins().extend({
  name: "dependent",
  data() {
    return {
      closeDependents: true,
      isActive: false,
      isDependent: true
    };
  },
  watch: {
    isActive(val) {
      if (val)
        return;
      const openDependents = this.getOpenDependents();
      for (let index = 0; index < openDependents.length; index++) {
        openDependents[index].isActive = false;
      }
    }
  },
  methods: {
    getOpenDependents() {
      if (this.closeDependents)
        return searchChildren(this.$children);
      return [];
    },
    getOpenDependentElements() {
      const result = [];
      const openDependents = this.getOpenDependents();
      for (let index = 0; index < openDependents.length; index++) {
        result.push(...openDependents[index].getClickableDependentElements());
      }
      return result;
    },
    getClickableDependentElements() {
      const result = [this.$el];
      if (this.$refs.content)
        result.push(this.$refs.content);
      if (this.overlay)
        result.push(this.overlay.$el);
      result.push(...this.getOpenDependentElements());
      return result;
    }
  }
});
var Bootable = Vue.extend().extend({
  name: "bootable",
  props: {
    eager: Boolean
  },
  data: () => ({
    isBooted: false
  }),
  computed: {
    hasContent() {
      return this.isBooted || this.eager || this.isActive;
    }
  },
  watch: {
    isActive() {
      this.isBooted = true;
    }
  },
  created() {
    if ("lazy" in this.$attrs) {
      removed("lazy", this);
    }
  },
  methods: {
    showLazyContent(content) {
      return this.hasContent && content ? content() : [this.$createElement()];
    }
  }
});
function validateAttachTarget(val) {
  const type2 = typeof val;
  if (type2 === "boolean" || type2 === "string")
    return true;
  return val.nodeType === Node.ELEMENT_NODE;
}
var Detachable = mixins(Bootable).extend({
  name: "detachable",
  props: {
    attach: {
      default: false,
      validator: validateAttachTarget
    },
    contentClass: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    activatorNode: null,
    hasDetached: false
  }),
  watch: {
    attach() {
      this.hasDetached = false;
      this.initDetach();
    },
    hasContent() {
      this.$nextTick(this.initDetach);
    }
  },
  beforeMount() {
    this.$nextTick(() => {
      if (this.activatorNode) {
        const activator = Array.isArray(this.activatorNode) ? this.activatorNode : [this.activatorNode];
        activator.forEach((node) => {
          if (!node.elm)
            return;
          if (!this.$el.parentNode)
            return;
          const target = this.$el === this.$el.parentNode.firstChild ? this.$el : this.$el.nextSibling;
          this.$el.parentNode.insertBefore(node.elm, target);
        });
      }
    });
  },
  mounted() {
    this.hasContent && this.initDetach();
  },
  deactivated() {
    this.isActive = false;
  },
  beforeDestroy() {
    try {
      if (this.$refs.content && this.$refs.content.parentNode) {
        this.$refs.content.parentNode.removeChild(this.$refs.content);
      }
      if (this.activatorNode) {
        const activator = Array.isArray(this.activatorNode) ? this.activatorNode : [this.activatorNode];
        activator.forEach((node) => {
          node.elm && node.elm.parentNode && node.elm.parentNode.removeChild(node.elm);
        });
      }
    } catch (e) {
      console.log(e);
    }
  },
  methods: {
    getScopeIdAttrs() {
      const scopeId = getObjectValueByPath(this.$vnode, "context.$options._scopeId");
      return scopeId && {
        [scopeId]: ""
      };
    },
    initDetach() {
      if (this._isDestroyed || !this.$refs.content || this.hasDetached || this.attach === "" || this.attach === true || this.attach === "attach")
        return;
      let target;
      if (this.attach === false) {
        target = document.querySelector("[data-app]");
      } else if (typeof this.attach === "string") {
        target = document.querySelector(this.attach);
      } else {
        target = this.attach;
      }
      if (!target) {
        consoleWarn(`Unable to locate target ${this.attach || "[data-app]"}`, this);
        return;
      }
      target.appendChild(this.$refs.content);
      this.hasDetached = true;
    }
  }
});
var Stackable = Vue.extend().extend({
  name: "stackable",
  data() {
    return {
      stackElement: null,
      stackExclude: null,
      stackMinZIndex: 0,
      isActive: false
    };
  },
  computed: {
    activeZIndex() {
      if (typeof window === "undefined")
        return 0;
      const content = this.stackElement || this.$refs.content;
      const index = !this.isActive ? getZIndex(content) : this.getMaxZIndex(this.stackExclude || [content]) + 2;
      if (index == null)
        return index;
      return parseInt(index);
    }
  },
  methods: {
    getMaxZIndex(exclude = []) {
      const base2 = this.$el;
      const zis = [this.stackMinZIndex, getZIndex(base2)];
      const activeElements = [...document.getElementsByClassName("v-menu__content--active"), ...document.getElementsByClassName("v-dialog__content--active")];
      for (let index = 0; index < activeElements.length; index++) {
        if (!exclude.includes(activeElements[index])) {
          zis.push(getZIndex(activeElements[index]));
        }
      }
      return Math.max(...zis);
    }
  }
});
const baseMixins$q = mixins(Stackable, Positionable, Activatable);
var Menuable = baseMixins$q.extend().extend({
  name: "menuable",
  props: {
    allowOverflow: Boolean,
    light: Boolean,
    dark: Boolean,
    maxWidth: {
      type: [Number, String],
      default: "auto"
    },
    minWidth: [Number, String],
    nudgeBottom: {
      type: [Number, String],
      default: 0
    },
    nudgeLeft: {
      type: [Number, String],
      default: 0
    },
    nudgeRight: {
      type: [Number, String],
      default: 0
    },
    nudgeTop: {
      type: [Number, String],
      default: 0
    },
    nudgeWidth: {
      type: [Number, String],
      default: 0
    },
    offsetOverflow: Boolean,
    openOnClick: Boolean,
    positionX: {
      type: Number,
      default: null
    },
    positionY: {
      type: Number,
      default: null
    },
    zIndex: {
      type: [Number, String],
      default: null
    }
  },
  data: () => ({
    absoluteX: 0,
    absoluteY: 0,
    activatedBy: null,
    activatorFixed: false,
    dimensions: {
      activator: {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        offsetTop: 0,
        scrollHeight: 0,
        offsetLeft: 0
      },
      content: {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        width: 0,
        height: 0,
        offsetTop: 0,
        scrollHeight: 0
      }
    },
    hasJustFocused: false,
    hasWindow: false,
    inputActivator: false,
    isContentActive: false,
    pageWidth: 0,
    pageYOffset: 0,
    stackClass: "v-menu__content--active",
    stackMinZIndex: 6
  }),
  computed: {
    computedLeft() {
      const a = this.dimensions.activator;
      const c = this.dimensions.content;
      const activatorLeft = (this.attach !== false ? a.offsetLeft : a.left) || 0;
      const minWidth = Math.max(a.width, c.width);
      let left = 0;
      left += this.left ? activatorLeft - (minWidth - a.width) : activatorLeft;
      if (this.offsetX) {
        const maxWidth = isNaN(Number(this.maxWidth)) ? a.width : Math.min(a.width, Number(this.maxWidth));
        left += this.left ? -maxWidth : a.width;
      }
      if (this.nudgeLeft)
        left -= parseInt(this.nudgeLeft);
      if (this.nudgeRight)
        left += parseInt(this.nudgeRight);
      return left;
    },
    computedTop() {
      const a = this.dimensions.activator;
      const c = this.dimensions.content;
      let top = 0;
      if (this.top)
        top += a.height - c.height;
      if (this.attach !== false)
        top += a.offsetTop;
      else
        top += a.top + this.pageYOffset;
      if (this.offsetY)
        top += this.top ? -a.height : a.height;
      if (this.nudgeTop)
        top -= parseInt(this.nudgeTop);
      if (this.nudgeBottom)
        top += parseInt(this.nudgeBottom);
      return top;
    },
    hasActivator() {
      return !!this.$slots.activator || !!this.$scopedSlots.activator || !!this.activator || !!this.inputActivator;
    }
  },
  watch: {
    disabled(val) {
      val && this.callDeactivate();
    },
    isActive(val) {
      if (this.disabled)
        return;
      val ? this.callActivate() : this.callDeactivate();
    },
    positionX: "updateDimensions",
    positionY: "updateDimensions"
  },
  beforeMount() {
    this.hasWindow = typeof window !== "undefined";
  },
  methods: {
    absolutePosition() {
      return {
        offsetTop: 0,
        offsetLeft: 0,
        scrollHeight: 0,
        top: this.positionY || this.absoluteY,
        bottom: this.positionY || this.absoluteY,
        left: this.positionX || this.absoluteX,
        right: this.positionX || this.absoluteX,
        height: 0,
        width: 0
      };
    },
    activate() {
    },
    calcLeft(menuWidth) {
      return convertToUnit(this.attach !== false ? this.computedLeft : this.calcXOverflow(this.computedLeft, menuWidth));
    },
    calcTop() {
      return convertToUnit(this.attach !== false ? this.computedTop : this.calcYOverflow(this.computedTop));
    },
    calcXOverflow(left, menuWidth) {
      const xOverflow = left + menuWidth - this.pageWidth + 12;
      if ((!this.left || this.right) && xOverflow > 0) {
        left = Math.max(left - xOverflow, 0);
      } else {
        left = Math.max(left, 12);
      }
      return left + this.getOffsetLeft();
    },
    calcYOverflow(top) {
      const documentHeight = this.getInnerHeight();
      const toTop = this.pageYOffset + documentHeight;
      const activator = this.dimensions.activator;
      const contentHeight = this.dimensions.content.height;
      const totalHeight = top + contentHeight;
      const isOverflowing = toTop < totalHeight;
      if (isOverflowing && this.offsetOverflow && activator.top > contentHeight) {
        top = this.pageYOffset + (activator.top - contentHeight);
      } else if (isOverflowing && !this.allowOverflow) {
        top = toTop - contentHeight - 12;
      } else if (top < this.pageYOffset && !this.allowOverflow) {
        top = this.pageYOffset + 12;
      }
      return top < 12 ? 12 : top;
    },
    callActivate() {
      if (!this.hasWindow)
        return;
      this.activate();
    },
    callDeactivate() {
      this.isContentActive = false;
      this.deactivate();
    },
    checkForPageYOffset() {
      if (this.hasWindow) {
        this.pageYOffset = this.activatorFixed ? 0 : this.getOffsetTop();
      }
    },
    checkActivatorFixed() {
      if (this.attach !== false)
        return;
      let el = this.getActivator();
      while (el) {
        if (window.getComputedStyle(el).position === "fixed") {
          this.activatorFixed = true;
          return;
        }
        el = el.offsetParent;
      }
      this.activatorFixed = false;
    },
    deactivate() {
    },
    genActivatorListeners() {
      const listeners = Activatable.options.methods.genActivatorListeners.call(this);
      const onClick = listeners.click;
      listeners.click = (e) => {
        if (this.openOnClick) {
          onClick && onClick(e);
        }
        this.absoluteX = e.clientX;
        this.absoluteY = e.clientY;
      };
      return listeners;
    },
    getInnerHeight() {
      if (!this.hasWindow)
        return 0;
      return window.innerHeight || document.documentElement.clientHeight;
    },
    getOffsetLeft() {
      if (!this.hasWindow)
        return 0;
      return window.pageXOffset || document.documentElement.scrollLeft;
    },
    getOffsetTop() {
      if (!this.hasWindow)
        return 0;
      return window.pageYOffset || document.documentElement.scrollTop;
    },
    getRoundedBoundedClientRect(el) {
      const rect = el.getBoundingClientRect();
      return {
        top: Math.round(rect.top),
        left: Math.round(rect.left),
        bottom: Math.round(rect.bottom),
        right: Math.round(rect.right),
        width: Math.round(rect.width),
        height: Math.round(rect.height)
      };
    },
    measure(el) {
      if (!el || !this.hasWindow)
        return null;
      const rect = this.getRoundedBoundedClientRect(el);
      if (this.attach !== false) {
        const style = window.getComputedStyle(el);
        rect.left = parseInt(style.marginLeft);
        rect.top = parseInt(style.marginTop);
      }
      return rect;
    },
    sneakPeek(cb) {
      requestAnimationFrame(() => {
        const el = this.$refs.content;
        if (!el || el.style.display !== "none") {
          cb();
          return;
        }
        el.style.display = "inline-block";
        cb();
        el.style.display = "none";
      });
    },
    startTransition() {
      return new Promise((resolve) => requestAnimationFrame(() => {
        this.isContentActive = this.hasJustFocused = this.isActive;
        resolve();
      }));
    },
    updateDimensions() {
      this.hasWindow = typeof window !== "undefined";
      this.checkActivatorFixed();
      this.checkForPageYOffset();
      this.pageWidth = document.documentElement.clientWidth;
      const dimensions = {
        activator: __spreadValues({}, this.dimensions.activator),
        content: __spreadValues({}, this.dimensions.content)
      };
      if (!this.hasActivator || this.absolute) {
        dimensions.activator = this.absolutePosition();
      } else {
        const activator = this.getActivator();
        if (!activator)
          return;
        dimensions.activator = this.measure(activator);
        dimensions.activator.offsetLeft = activator.offsetLeft;
        if (this.attach !== false) {
          dimensions.activator.offsetTop = activator.offsetTop;
        } else {
          dimensions.activator.offsetTop = 0;
        }
      }
      this.sneakPeek(() => {
        this.$refs.content && (dimensions.content = this.measure(this.$refs.content));
        this.dimensions = dimensions;
      });
    }
  }
});
var Returnable = Vue.extend({
  name: "returnable",
  props: {
    returnValue: null
  },
  data: () => ({
    isActive: false,
    originalValue: null
  }),
  watch: {
    isActive(val) {
      if (val) {
        this.originalValue = this.returnValue;
      } else {
        this.$emit("update:return-value", this.originalValue);
      }
    }
  },
  methods: {
    save(value) {
      this.originalValue = value;
      setTimeout(() => {
        this.isActive = false;
      });
    }
  }
});
const baseMixins$p = mixins(Dependent, Delayable, Detachable, Menuable, Returnable, Roundable, Toggleable$1, Themeable$1);
var VMenu = baseMixins$p.extend({
  name: "v-menu",
  directives: {
    ClickOutside: ClickOutside$1,
    Resize: Resize$1
  },
  provide() {
    return {
      isInMenu: true,
      theme: this.theme
    };
  },
  props: {
    auto: Boolean,
    closeOnClick: {
      type: Boolean,
      default: true
    },
    closeOnContentClick: {
      type: Boolean,
      default: true
    },
    disabled: Boolean,
    disableKeys: Boolean,
    maxHeight: {
      type: [Number, String],
      default: "auto"
    },
    offsetX: Boolean,
    offsetY: Boolean,
    openOnClick: {
      type: Boolean,
      default: true
    },
    openOnHover: Boolean,
    origin: {
      type: String,
      default: "top left"
    },
    transition: {
      type: [Boolean, String],
      default: "v-menu-transition"
    }
  },
  data() {
    return {
      calculatedTopAuto: 0,
      defaultOffset: 8,
      hasJustFocused: false,
      listIndex: -1,
      resizeTimeout: 0,
      selectedIndex: null,
      tiles: []
    };
  },
  computed: {
    activeTile() {
      return this.tiles[this.listIndex];
    },
    calculatedLeft() {
      const menuWidth = Math.max(this.dimensions.content.width, parseFloat(this.calculatedMinWidth));
      if (!this.auto)
        return this.calcLeft(menuWidth) || "0";
      return convertToUnit(this.calcXOverflow(this.calcLeftAuto(), menuWidth)) || "0";
    },
    calculatedMaxHeight() {
      const height = this.auto ? "200px" : convertToUnit(this.maxHeight);
      return height || "0";
    },
    calculatedMaxWidth() {
      return convertToUnit(this.maxWidth) || "0";
    },
    calculatedMinWidth() {
      if (this.minWidth) {
        return convertToUnit(this.minWidth) || "0";
      }
      const minWidth = Math.min(this.dimensions.activator.width + Number(this.nudgeWidth) + (this.auto ? 16 : 0), Math.max(this.pageWidth - 24, 0));
      const calculatedMaxWidth = isNaN(parseInt(this.calculatedMaxWidth)) ? minWidth : parseInt(this.calculatedMaxWidth);
      return convertToUnit(Math.min(calculatedMaxWidth, minWidth)) || "0";
    },
    calculatedTop() {
      const top = !this.auto ? this.calcTop() : convertToUnit(this.calcYOverflow(this.calculatedTopAuto));
      return top || "0";
    },
    hasClickableTiles() {
      return Boolean(this.tiles.find((tile) => tile.tabIndex > -1));
    },
    styles() {
      return {
        maxHeight: this.calculatedMaxHeight,
        minWidth: this.calculatedMinWidth,
        maxWidth: this.calculatedMaxWidth,
        top: this.calculatedTop,
        left: this.calculatedLeft,
        transformOrigin: this.origin,
        zIndex: this.zIndex || this.activeZIndex
      };
    }
  },
  watch: {
    isActive(val) {
      if (!val)
        this.listIndex = -1;
    },
    isContentActive(val) {
      this.hasJustFocused = val;
    },
    listIndex(next, prev) {
      if (next in this.tiles) {
        const tile = this.tiles[next];
        tile.classList.add("v-list-item--highlighted");
        this.$refs.content.scrollTop = tile.offsetTop - tile.clientHeight;
      }
      prev in this.tiles && this.tiles[prev].classList.remove("v-list-item--highlighted");
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("full-width")) {
      removed("full-width", this);
    }
  },
  mounted() {
    this.isActive && this.callActivate();
  },
  methods: {
    activate() {
      this.updateDimensions();
      requestAnimationFrame(() => {
        this.startTransition().then(() => {
          if (this.$refs.content) {
            this.calculatedTopAuto = this.calcTopAuto();
            this.auto && (this.$refs.content.scrollTop = this.calcScrollPosition());
          }
        });
      });
    },
    calcScrollPosition() {
      const $el = this.$refs.content;
      const activeTile = $el.querySelector(".v-list-item--active");
      const maxScrollTop = $el.scrollHeight - $el.offsetHeight;
      return activeTile ? Math.min(maxScrollTop, Math.max(0, activeTile.offsetTop - $el.offsetHeight / 2 + activeTile.offsetHeight / 2)) : $el.scrollTop;
    },
    calcLeftAuto() {
      return parseInt(this.dimensions.activator.left - this.defaultOffset * 2);
    },
    calcTopAuto() {
      const $el = this.$refs.content;
      const activeTile = $el.querySelector(".v-list-item--active");
      if (!activeTile) {
        this.selectedIndex = null;
      }
      if (this.offsetY || !activeTile) {
        return this.computedTop;
      }
      this.selectedIndex = Array.from(this.tiles).indexOf(activeTile);
      const tileDistanceFromMenuTop = activeTile.offsetTop - this.calcScrollPosition();
      const firstTileOffsetTop = $el.querySelector(".v-list-item").offsetTop;
      return this.computedTop - tileDistanceFromMenuTop - firstTileOffsetTop - 1;
    },
    changeListIndex(e) {
      this.getTiles();
      if (!this.isActive || !this.hasClickableTiles) {
        return;
      } else if (e.keyCode === keyCodes.tab) {
        this.isActive = false;
        return;
      } else if (e.keyCode === keyCodes.down) {
        this.nextTile();
      } else if (e.keyCode === keyCodes.up) {
        this.prevTile();
      } else if (e.keyCode === keyCodes.end) {
        this.lastTile();
      } else if (e.keyCode === keyCodes.home) {
        this.firstTile();
      } else if (e.keyCode === keyCodes.enter && this.listIndex !== -1) {
        this.tiles[this.listIndex].click();
      } else {
        return;
      }
      e.preventDefault();
    },
    closeConditional(e) {
      const target = e.target;
      return this.isActive && !this._isDestroyed && this.closeOnClick && !this.$refs.content.contains(target);
    },
    genActivatorAttributes() {
      const attributes = Activatable.options.methods.genActivatorAttributes.call(this);
      if (this.activeTile && this.activeTile.id) {
        return __spreadProps(__spreadValues({}, attributes), {
          "aria-activedescendant": this.activeTile.id
        });
      }
      return attributes;
    },
    genActivatorListeners() {
      const listeners = Menuable.options.methods.genActivatorListeners.call(this);
      if (!this.disableKeys) {
        listeners.keydown = this.onKeyDown;
      }
      return listeners;
    },
    genTransition() {
      const content = this.genContent();
      if (!this.transition)
        return content;
      return this.$createElement("transition", {
        props: {
          name: this.transition
        }
      }, [content]);
    },
    genDirectives() {
      const directives = [{
        name: "show",
        value: this.isContentActive
      }];
      if (!this.openOnHover && this.closeOnClick) {
        directives.push({
          name: "click-outside",
          value: {
            handler: () => {
              this.isActive = false;
            },
            closeConditional: this.closeConditional,
            include: () => [this.$el, ...this.getOpenDependentElements()]
          }
        });
      }
      return directives;
    },
    genContent() {
      const options2 = {
        attrs: __spreadProps(__spreadValues({}, this.getScopeIdAttrs()), {
          role: "role" in this.$attrs ? this.$attrs.role : "menu"
        }),
        staticClass: "v-menu__content",
        class: __spreadProps(__spreadValues(__spreadValues({}, this.rootThemeClasses), this.roundedClasses), {
          "v-menu__content--auto": this.auto,
          "v-menu__content--fixed": this.activatorFixed,
          menuable__content__active: this.isActive,
          [this.contentClass.trim()]: true
        }),
        style: this.styles,
        directives: this.genDirectives(),
        ref: "content",
        on: {
          click: (e) => {
            const target = e.target;
            if (target.getAttribute("disabled"))
              return;
            if (this.closeOnContentClick)
              this.isActive = false;
          },
          keydown: this.onKeyDown
        }
      };
      if (this.$listeners.scroll) {
        options2.on = options2.on || {};
        options2.on.scroll = this.$listeners.scroll;
      }
      if (!this.disabled && this.openOnHover) {
        options2.on = options2.on || {};
        options2.on.mouseenter = this.mouseEnterHandler;
      }
      if (this.openOnHover) {
        options2.on = options2.on || {};
        options2.on.mouseleave = this.mouseLeaveHandler;
      }
      return this.$createElement("div", options2, this.getContentSlot());
    },
    getTiles() {
      if (!this.$refs.content)
        return;
      this.tiles = Array.from(this.$refs.content.querySelectorAll(".v-list-item"));
    },
    mouseEnterHandler() {
      this.runDelay("open", () => {
        if (this.hasJustFocused)
          return;
        this.hasJustFocused = true;
      });
    },
    mouseLeaveHandler(e) {
      this.runDelay("close", () => {
        if (this.$refs.content.contains(e.relatedTarget))
          return;
        requestAnimationFrame(() => {
          this.isActive = false;
          this.callDeactivate();
        });
      });
    },
    nextTile() {
      const tile = this.tiles[this.listIndex + 1];
      if (!tile) {
        if (!this.tiles.length)
          return;
        this.listIndex = -1;
        this.nextTile();
        return;
      }
      this.listIndex++;
      if (tile.tabIndex === -1)
        this.nextTile();
    },
    prevTile() {
      const tile = this.tiles[this.listIndex - 1];
      if (!tile) {
        if (!this.tiles.length)
          return;
        this.listIndex = this.tiles.length;
        this.prevTile();
        return;
      }
      this.listIndex--;
      if (tile.tabIndex === -1)
        this.prevTile();
    },
    lastTile() {
      const tile = this.tiles[this.tiles.length - 1];
      if (!tile)
        return;
      this.listIndex = this.tiles.length - 1;
      if (tile.tabIndex === -1)
        this.prevTile();
    },
    firstTile() {
      const tile = this.tiles[0];
      if (!tile)
        return;
      this.listIndex = 0;
      if (tile.tabIndex === -1)
        this.nextTile();
    },
    onKeyDown(e) {
      if (e.keyCode === keyCodes.esc) {
        setTimeout(() => {
          this.isActive = false;
        });
        const activator = this.getActivator();
        this.$nextTick(() => activator && activator.focus());
      } else if (!this.isActive && [keyCodes.up, keyCodes.down].includes(e.keyCode)) {
        this.isActive = true;
      }
      this.$nextTick(() => this.changeListIndex(e));
    },
    onResize() {
      if (!this.isActive)
        return;
      this.$refs.content.offsetWidth;
      this.updateDimensions();
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(this.updateDimensions, 100);
    }
  },
  render(h) {
    const data = {
      staticClass: "v-menu",
      class: {
        "v-menu--attached": this.attach === "" || this.attach === true || this.attach === "attach"
      },
      directives: [{
        arg: "500",
        name: "resize",
        value: this.onResize
      }]
    };
    return h("div", data, [!this.activator && this.genActivator(), this.showLazyContent(() => [this.$createElement(VThemeProvider, {
      props: {
        root: true,
        light: this.light,
        dark: this.dark
      }
    }, [this.genTransition()])])]);
  }
});
var VSimpleCheckbox = Vue.extend({
  name: "v-simple-checkbox",
  functional: true,
  directives: {
    ripple
  },
  props: __spreadProps(__spreadValues(__spreadValues({}, Colorable.options.props), Themeable$1.options.props), {
    disabled: Boolean,
    ripple: {
      type: Boolean,
      default: true
    },
    value: Boolean,
    indeterminate: Boolean,
    indeterminateIcon: {
      type: String,
      default: "$checkboxIndeterminate"
    },
    onIcon: {
      type: String,
      default: "$checkboxOn"
    },
    offIcon: {
      type: String,
      default: "$checkboxOff"
    }
  }),
  render(h, {
    props: props2,
    data,
    listeners
  }) {
    const children = [];
    let icon = props2.offIcon;
    if (props2.indeterminate)
      icon = props2.indeterminateIcon;
    else if (props2.value)
      icon = props2.onIcon;
    children.push(h(VIcon$1, Colorable.options.methods.setTextColor(props2.value && props2.color, {
      props: {
        disabled: props2.disabled,
        dark: props2.dark,
        light: props2.light
      }
    }), icon));
    if (props2.ripple && !props2.disabled) {
      const ripple2 = h("div", Colorable.options.methods.setTextColor(props2.color, {
        staticClass: "v-input--selection-controls__ripple",
        directives: [{
          name: "ripple",
          value: {
            center: true
          }
        }]
      }));
      children.push(ripple2);
    }
    return h("div", mergeData(data, {
      class: {
        "v-simple-checkbox": true,
        "v-simple-checkbox--disabled": props2.disabled
      },
      on: {
        click: (e) => {
          e.stopPropagation();
          if (data.on && data.on.input && !props2.disabled) {
            wrapInArray(data.on.input).forEach((f) => f(!props2.value));
          }
        }
      }
    }), [h("div", {
      staticClass: "v-input--selection-controls__input"
    }, children)]);
  }
});
var VDivider = Themeable$1.extend({
  name: "v-divider",
  props: {
    inset: Boolean,
    vertical: Boolean
  },
  render(h) {
    let orientation;
    if (!this.$attrs.role || this.$attrs.role === "separator") {
      orientation = this.vertical ? "vertical" : "horizontal";
    }
    return h("hr", {
      class: __spreadValues({
        "v-divider": true,
        "v-divider--inset": this.inset,
        "v-divider--vertical": this.vertical
      }, this.themeClasses),
      attrs: __spreadValues({
        role: "separator",
        "aria-orientation": orientation
      }, this.$attrs),
      on: this.$listeners
    });
  }
});
var VSubheader = mixins(Themeable$1).extend({
  name: "v-subheader",
  props: {
    inset: Boolean
  },
  render(h) {
    return h("div", {
      staticClass: "v-subheader",
      class: __spreadValues({
        "v-subheader--inset": this.inset
      }, this.themeClasses),
      attrs: this.$attrs,
      on: this.$listeners
    }, this.$slots.default);
  }
});
var VList = VSheet.extend().extend({
  name: "v-list",
  provide() {
    return {
      isInList: true,
      list: this
    };
  },
  inject: {
    isInMenu: {
      default: false
    },
    isInNav: {
      default: false
    }
  },
  props: {
    dense: Boolean,
    disabled: Boolean,
    expand: Boolean,
    flat: Boolean,
    nav: Boolean,
    rounded: Boolean,
    subheader: Boolean,
    threeLine: Boolean,
    twoLine: Boolean
  },
  data: () => ({
    groups: []
  }),
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VSheet.options.computed.classes.call(this)), {
        "v-list--dense": this.dense,
        "v-list--disabled": this.disabled,
        "v-list--flat": this.flat,
        "v-list--nav": this.nav,
        "v-list--rounded": this.rounded,
        "v-list--subheader": this.subheader,
        "v-list--two-line": this.twoLine,
        "v-list--three-line": this.threeLine
      });
    }
  },
  methods: {
    register(content) {
      this.groups.push(content);
    },
    unregister(content) {
      const index = this.groups.findIndex((g) => g._uid === content._uid);
      if (index > -1)
        this.groups.splice(index, 1);
    },
    listClick(uid) {
      if (this.expand)
        return;
      for (const group of this.groups) {
        group.toggle(uid);
      }
    }
  },
  render(h) {
    const data = {
      staticClass: "v-list",
      class: this.classes,
      style: this.styles,
      attrs: __spreadValues({
        role: this.isInNav || this.isInMenu ? void 0 : "list"
      }, this.attrs$)
    };
    return h(this.tag, this.setBackgroundColor(this.color, data), [this.$slots.default]);
  }
});
const baseMixins$o = mixins(Colorable, Routable, Themeable$1, factory$1("listItemGroup"), factory$2("inputValue"));
var VListItem = baseMixins$o.extend().extend({
  name: "v-list-item",
  directives: {
    Ripple: ripple
  },
  inject: {
    isInGroup: {
      default: false
    },
    isInList: {
      default: false
    },
    isInMenu: {
      default: false
    },
    isInNav: {
      default: false
    }
  },
  inheritAttrs: false,
  props: {
    activeClass: {
      type: String,
      default() {
        if (!this.listItemGroup)
          return "";
        return this.listItemGroup.activeClass;
      }
    },
    dense: Boolean,
    inactive: Boolean,
    link: Boolean,
    selectable: {
      type: Boolean
    },
    tag: {
      type: String,
      default: "div"
    },
    threeLine: Boolean,
    twoLine: Boolean,
    value: null
  },
  data: () => ({
    proxyClass: "v-list-item--active"
  }),
  computed: {
    classes() {
      return __spreadValues(__spreadProps(__spreadValues({
        "v-list-item": true
      }, Routable.options.computed.classes.call(this)), {
        "v-list-item--dense": this.dense,
        "v-list-item--disabled": this.disabled,
        "v-list-item--link": this.isClickable && !this.inactive,
        "v-list-item--selectable": this.selectable,
        "v-list-item--three-line": this.threeLine,
        "v-list-item--two-line": this.twoLine
      }), this.themeClasses);
    },
    isClickable() {
      return Boolean(Routable.options.computed.isClickable.call(this) || this.listItemGroup);
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("avatar")) {
      removed("avatar", this);
    }
  },
  methods: {
    click(e) {
      if (e.detail)
        this.$el.blur();
      this.$emit("click", e);
      this.to || this.toggle();
    },
    genAttrs() {
      const attrs = __spreadValues({
        "aria-disabled": this.disabled ? true : void 0,
        tabindex: this.isClickable && !this.disabled ? 0 : -1
      }, this.$attrs);
      if (this.$attrs.hasOwnProperty("role"))
        ;
      else if (this.isInNav)
        ;
      else if (this.isInGroup) {
        attrs.role = "option";
        attrs["aria-selected"] = String(this.isActive);
      } else if (this.isInMenu) {
        attrs.role = this.isClickable ? "menuitem" : void 0;
        attrs.id = attrs.id || `list-item-${this._uid}`;
      } else if (this.isInList) {
        attrs.role = "listitem";
      }
      return attrs;
    }
  },
  render(h) {
    let {
      tag,
      data
    } = this.generateRouteLink();
    data.attrs = __spreadValues(__spreadValues({}, data.attrs), this.genAttrs());
    data[this.to ? "nativeOn" : "on"] = __spreadProps(__spreadValues({}, data[this.to ? "nativeOn" : "on"]), {
      keydown: (e) => {
        if (e.keyCode === keyCodes.enter)
          this.click(e);
        this.$emit("keydown", e);
      }
    });
    if (this.inactive)
      tag = "div";
    if (this.inactive && this.to) {
      data.on = data.nativeOn;
      delete data.nativeOn;
    }
    const children = this.$scopedSlots.default ? this.$scopedSlots.default({
      active: this.isActive,
      toggle: this.toggle
    }) : this.$slots.default;
    return h(tag, this.setTextColor(this.color, data), children);
  }
});
var VListItemIcon = Vue.extend({
  name: "v-list-item-icon",
  functional: true,
  render(h, {
    data,
    children
  }) {
    data.staticClass = `v-list-item__icon ${data.staticClass || ""}`.trim();
    return h("div", data, children);
  }
});
const baseMixins$n = mixins(BindsAttrs, Bootable, Colorable, inject("list"), Toggleable$1);
var VListGroup = baseMixins$n.extend().extend({
  name: "v-list-group",
  directives: {
    ripple
  },
  props: {
    activeClass: {
      type: String,
      default: ""
    },
    appendIcon: {
      type: String,
      default: "$expand"
    },
    color: {
      type: String,
      default: "primary"
    },
    disabled: Boolean,
    group: String,
    noAction: Boolean,
    prependIcon: String,
    ripple: {
      type: [Boolean, Object],
      default: true
    },
    subGroup: Boolean
  },
  computed: {
    classes() {
      return {
        "v-list-group--active": this.isActive,
        "v-list-group--disabled": this.disabled,
        "v-list-group--no-action": this.noAction,
        "v-list-group--sub-group": this.subGroup
      };
    }
  },
  watch: {
    isActive(val) {
      if (!this.subGroup && val) {
        this.list && this.list.listClick(this._uid);
      }
    },
    $route: "onRouteChange"
  },
  created() {
    this.list && this.list.register(this);
    if (this.group && this.$route && this.value == null) {
      this.isActive = this.matchRoute(this.$route.path);
    }
  },
  beforeDestroy() {
    this.list && this.list.unregister(this);
  },
  methods: {
    click(e) {
      if (this.disabled)
        return;
      this.isBooted = true;
      this.$emit("click", e);
      this.$nextTick(() => this.isActive = !this.isActive);
    },
    genIcon(icon) {
      return this.$createElement(VIcon$1, icon);
    },
    genAppendIcon() {
      const icon = !this.subGroup ? this.appendIcon : false;
      if (!icon && !this.$slots.appendIcon)
        return null;
      return this.$createElement(VListItemIcon, {
        staticClass: "v-list-group__header__append-icon"
      }, [this.$slots.appendIcon || this.genIcon(icon)]);
    },
    genHeader() {
      return this.$createElement(VListItem, {
        staticClass: "v-list-group__header",
        attrs: {
          "aria-expanded": String(this.isActive),
          role: "button"
        },
        class: {
          [this.activeClass]: this.isActive
        },
        props: {
          inputValue: this.isActive
        },
        directives: [{
          name: "ripple",
          value: this.ripple
        }],
        on: __spreadProps(__spreadValues({}, this.listeners$), {
          click: this.click
        })
      }, [this.genPrependIcon(), this.$slots.activator, this.genAppendIcon()]);
    },
    genItems() {
      return this.showLazyContent(() => [this.$createElement("div", {
        staticClass: "v-list-group__items",
        directives: [{
          name: "show",
          value: this.isActive
        }]
      }, getSlot(this))]);
    },
    genPrependIcon() {
      const icon = this.subGroup && this.prependIcon == null ? "$subgroup" : this.prependIcon;
      if (!icon && !this.$slots.prependIcon)
        return null;
      return this.$createElement(VListItemIcon, {
        staticClass: "v-list-group__header__prepend-icon"
      }, [this.$slots.prependIcon || this.genIcon(icon)]);
    },
    onRouteChange(to) {
      if (!this.group)
        return;
      const isActive = this.matchRoute(to.path);
      if (isActive && this.isActive !== isActive) {
        this.list && this.list.listClick(this._uid);
      }
      this.isActive = isActive;
    },
    toggle(uid) {
      const isActive = this._uid === uid;
      if (isActive)
        this.isBooted = true;
      this.$nextTick(() => this.isActive = isActive);
    },
    matchRoute(to) {
      return to.match(this.group) !== null;
    }
  },
  render(h) {
    return h("div", this.setTextColor(this.isActive && this.color, {
      staticClass: "v-list-group",
      class: this.classes
    }), [this.genHeader(), h(VExpandTransition, this.genItems())]);
  }
});
function factory(prop = "value", event = "change") {
  return Vue.extend({
    name: "proxyable",
    model: {
      prop,
      event
    },
    props: {
      [prop]: {
        required: false
      }
    },
    data() {
      return {
        internalLazyValue: this[prop]
      };
    },
    computed: {
      internalValue: {
        get() {
          return this.internalLazyValue;
        },
        set(val) {
          if (val === this.internalLazyValue)
            return;
          this.internalLazyValue = val;
          this.$emit(event, val);
        }
      }
    },
    watch: {
      [prop](val) {
        this.internalLazyValue = val;
      }
    }
  });
}
const Proxyable = factory();
var Proxyable$1 = Proxyable;
const BaseItemGroup = mixins(Proxyable$1, Themeable$1).extend({
  name: "base-item-group",
  props: {
    activeClass: {
      type: String,
      default: "v-item--active"
    },
    mandatory: Boolean,
    max: {
      type: [Number, String],
      default: null
    },
    multiple: Boolean,
    tag: {
      type: String,
      default: "div"
    }
  },
  data() {
    return {
      internalLazyValue: this.value !== void 0 ? this.value : this.multiple ? [] : void 0,
      items: []
    };
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-item-group": true
      }, this.themeClasses);
    },
    selectedIndex() {
      return this.selectedItem && this.items.indexOf(this.selectedItem) || -1;
    },
    selectedItem() {
      if (this.multiple)
        return void 0;
      return this.selectedItems[0];
    },
    selectedItems() {
      return this.items.filter((item, index) => {
        return this.toggleMethod(this.getValue(item, index));
      });
    },
    selectedValues() {
      if (this.internalValue == null)
        return [];
      return Array.isArray(this.internalValue) ? this.internalValue : [this.internalValue];
    },
    toggleMethod() {
      if (!this.multiple) {
        return (v) => this.internalValue === v;
      }
      const internalValue = this.internalValue;
      if (Array.isArray(internalValue)) {
        return (v) => internalValue.includes(v);
      }
      return () => false;
    }
  },
  watch: {
    internalValue: "updateItemsState",
    items: "updateItemsState"
  },
  created() {
    if (this.multiple && !Array.isArray(this.internalValue)) {
      consoleWarn("Model must be bound to an array if the multiple property is true.", this);
    }
  },
  methods: {
    genData() {
      return {
        class: this.classes
      };
    },
    getValue(item, i) {
      return item.value == null || item.value === "" ? i : item.value;
    },
    onClick(item) {
      this.updateInternalValue(this.getValue(item, this.items.indexOf(item)));
    },
    register(item) {
      const index = this.items.push(item) - 1;
      item.$on("change", () => this.onClick(item));
      if (this.mandatory && !this.selectedValues.length) {
        this.updateMandatory();
      }
      this.updateItem(item, index);
    },
    unregister(item) {
      if (this._isDestroyed)
        return;
      const index = this.items.indexOf(item);
      const value = this.getValue(item, index);
      this.items.splice(index, 1);
      const valueIndex = this.selectedValues.indexOf(value);
      if (valueIndex < 0)
        return;
      if (!this.mandatory) {
        return this.updateInternalValue(value);
      }
      if (this.multiple && Array.isArray(this.internalValue)) {
        this.internalValue = this.internalValue.filter((v) => v !== value);
      } else {
        this.internalValue = void 0;
      }
      if (!this.selectedItems.length) {
        this.updateMandatory(true);
      }
    },
    updateItem(item, index) {
      const value = this.getValue(item, index);
      item.isActive = this.toggleMethod(value);
    },
    updateItemsState() {
      this.$nextTick(() => {
        if (this.mandatory && !this.selectedItems.length) {
          return this.updateMandatory();
        }
        this.items.forEach(this.updateItem);
      });
    },
    updateInternalValue(value) {
      this.multiple ? this.updateMultiple(value) : this.updateSingle(value);
    },
    updateMandatory(last) {
      if (!this.items.length)
        return;
      const items = this.items.slice();
      if (last)
        items.reverse();
      const item = items.find((item2) => !item2.disabled);
      if (!item)
        return;
      const index = this.items.indexOf(item);
      this.updateInternalValue(this.getValue(item, index));
    },
    updateMultiple(value) {
      const defaultValue = Array.isArray(this.internalValue) ? this.internalValue : [];
      const internalValue = defaultValue.slice();
      const index = internalValue.findIndex((val) => val === value);
      if (this.mandatory && index > -1 && internalValue.length - 1 < 1)
        return;
      if (this.max != null && index < 0 && internalValue.length + 1 > this.max)
        return;
      index > -1 ? internalValue.splice(index, 1) : internalValue.push(value);
      this.internalValue = internalValue;
    },
    updateSingle(value) {
      const isSame = value === this.internalValue;
      if (this.mandatory && isSame)
        return;
      this.internalValue = isSame ? void 0 : value;
    }
  },
  render(h) {
    return h(this.tag, this.genData(), this.$slots.default);
  }
});
var VItemGroup = BaseItemGroup.extend({
  name: "v-item-group",
  provide() {
    return {
      itemGroup: this
    };
  }
});
var VListItemGroup = mixins(BaseItemGroup, Colorable).extend({
  name: "v-list-item-group",
  provide() {
    return {
      isInGroup: true,
      listItemGroup: this
    };
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, BaseItemGroup.options.computed.classes.call(this)), {
        "v-list-item-group": true
      });
    }
  },
  methods: {
    genData() {
      return this.setTextColor(this.color, __spreadProps(__spreadValues({}, BaseItemGroup.options.methods.genData.call(this)), {
        attrs: {
          role: "listbox"
        }
      }));
    }
  }
});
var VListItemAction = Vue.extend({
  name: "v-list-item-action",
  functional: true,
  render(h, {
    data,
    children = []
  }) {
    data.staticClass = data.staticClass ? `v-list-item__action ${data.staticClass}` : "v-list-item__action";
    const filteredChild = children.filter((VNode) => {
      return VNode.isComment === false && VNode.text !== " ";
    });
    if (filteredChild.length > 1)
      data.staticClass += " v-list-item__action--stack";
    return h("div", data, children);
  }
});
var VAvatar = mixins(Colorable, Measurable, Roundable).extend({
  name: "v-avatar",
  props: {
    left: Boolean,
    right: Boolean,
    size: {
      type: [Number, String],
      default: 48
    }
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-avatar--left": this.left,
        "v-avatar--right": this.right
      }, this.roundedClasses);
    },
    styles() {
      return __spreadValues({
        height: convertToUnit(this.size),
        minWidth: convertToUnit(this.size),
        width: convertToUnit(this.size)
      }, this.measurableStyles);
    }
  },
  render(h) {
    const data = {
      staticClass: "v-avatar",
      class: this.classes,
      style: this.styles,
      on: this.$listeners
    };
    return h("div", this.setBackgroundColor(this.color, data), this.$slots.default);
  }
});
var VListItemAvatar = VAvatar.extend({
  name: "v-list-item-avatar",
  props: {
    horizontal: Boolean,
    size: {
      type: [Number, String],
      default: 40
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({
        "v-list-item__avatar--horizontal": this.horizontal
      }, VAvatar.options.computed.classes.call(this)), {
        "v-avatar--tile": this.tile || this.horizontal
      });
    }
  },
  render(h) {
    const render = VAvatar.options.render.call(this, h);
    render.data = render.data || {};
    render.data.staticClass += " v-list-item__avatar";
    return render;
  }
});
const VListItemActionText = createSimpleFunctional("v-list-item__action-text", "span");
const VListItemContent = createSimpleFunctional("v-list-item__content", "div");
const VListItemTitle = createSimpleFunctional("v-list-item__title", "div");
const VListItemSubtitle = createSimpleFunctional("v-list-item__subtitle", "div");
var VSelectList = mixins(Colorable, Themeable$1).extend({
  name: "v-select-list",
  directives: {
    ripple
  },
  props: {
    action: Boolean,
    dense: Boolean,
    hideSelected: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    itemDisabled: {
      type: [String, Array, Function],
      default: "disabled"
    },
    itemText: {
      type: [String, Array, Function],
      default: "text"
    },
    itemValue: {
      type: [String, Array, Function],
      default: "value"
    },
    noDataText: String,
    noFilter: Boolean,
    searchInput: null,
    selectedItems: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    parsedItems() {
      return this.selectedItems.map((item) => this.getValue(item));
    },
    tileActiveClass() {
      return Object.keys(this.setTextColor(this.color).class || {}).join(" ");
    },
    staticNoDataTile() {
      const tile = {
        attrs: {
          role: void 0
        },
        on: {
          mousedown: (e) => e.preventDefault()
        }
      };
      return this.$createElement(VListItem, tile, [this.genTileContent(this.noDataText)]);
    }
  },
  methods: {
    genAction(item, inputValue) {
      return this.$createElement(VListItemAction, [this.$createElement(VSimpleCheckbox, {
        props: {
          color: this.color,
          value: inputValue,
          ripple: false
        },
        on: {
          input: () => this.$emit("select", item)
        }
      })]);
    },
    genDivider(props2) {
      return this.$createElement(VDivider, {
        props: props2
      });
    },
    genFilteredText(text) {
      text = text || "";
      if (!this.searchInput || this.noFilter)
        return escapeHTML(text);
      const {
        start,
        middle,
        end
      } = this.getMaskedCharacters(text);
      return `${escapeHTML(start)}${this.genHighlight(middle)}${escapeHTML(end)}`;
    },
    genHeader(props2) {
      return this.$createElement(VSubheader, {
        props: props2
      }, props2.header);
    },
    genHighlight(text) {
      return `<span class="v-list-item__mask">${escapeHTML(text)}</span>`;
    },
    getMaskedCharacters(text) {
      const searchInput = (this.searchInput || "").toString().toLocaleLowerCase();
      const index = text.toLocaleLowerCase().indexOf(searchInput);
      if (index < 0)
        return {
          start: text,
          middle: "",
          end: ""
        };
      const start = text.slice(0, index);
      const middle = text.slice(index, index + searchInput.length);
      const end = text.slice(index + searchInput.length);
      return {
        start,
        middle,
        end
      };
    },
    genTile({
      item,
      index,
      disabled = null,
      value = false
    }) {
      if (!value)
        value = this.hasItem(item);
      if (item === Object(item)) {
        disabled = disabled !== null ? disabled : this.getDisabled(item);
      }
      const tile = {
        attrs: {
          "aria-selected": String(value),
          id: `list-item-${this._uid}-${index}`,
          role: "option"
        },
        on: {
          mousedown: (e) => {
            e.preventDefault();
          },
          click: () => disabled || this.$emit("select", item)
        },
        props: {
          activeClass: this.tileActiveClass,
          disabled,
          ripple: true,
          inputValue: value
        }
      };
      if (!this.$scopedSlots.item) {
        return this.$createElement(VListItem, tile, [this.action && !this.hideSelected && this.items.length > 0 ? this.genAction(item, value) : null, this.genTileContent(item, index)]);
      }
      const parent = this;
      const scopedSlot = this.$scopedSlots.item({
        parent,
        item,
        attrs: __spreadValues(__spreadValues({}, tile.attrs), tile.props),
        on: tile.on
      });
      return this.needsTile(scopedSlot) ? this.$createElement(VListItem, tile, scopedSlot) : scopedSlot;
    },
    genTileContent(item, index = 0) {
      const innerHTML = this.genFilteredText(this.getText(item));
      return this.$createElement(VListItemContent, [this.$createElement(VListItemTitle, {
        domProps: {
          innerHTML
        }
      })]);
    },
    hasItem(item) {
      return this.parsedItems.indexOf(this.getValue(item)) > -1;
    },
    needsTile(slot) {
      return slot.length !== 1 || slot[0].componentOptions == null || slot[0].componentOptions.Ctor.options.name !== "v-list-item";
    },
    getDisabled(item) {
      return Boolean(getPropertyFromItem(item, this.itemDisabled, false));
    },
    getText(item) {
      return String(getPropertyFromItem(item, this.itemText, item));
    },
    getValue(item) {
      return getPropertyFromItem(item, this.itemValue, this.getText(item));
    }
  },
  render() {
    const children = [];
    const itemsLength = this.items.length;
    for (let index = 0; index < itemsLength; index++) {
      const item = this.items[index];
      if (this.hideSelected && this.hasItem(item))
        continue;
      if (item == null)
        children.push(this.genTile({
          item,
          index
        }));
      else if (item.header)
        children.push(this.genHeader(item));
      else if (item.divider)
        children.push(this.genDivider(item));
      else
        children.push(this.genTile({
          item,
          index
        }));
    }
    children.length || children.push(this.$slots["no-data"] || this.staticNoDataTile);
    this.$slots["prepend-item"] && children.unshift(this.$slots["prepend-item"]);
    this.$slots["append-item"] && children.push(this.$slots["append-item"]);
    return this.$createElement(VList, {
      staticClass: "v-select-list",
      class: this.themeClasses,
      attrs: {
        role: "listbox",
        tabindex: -1
      },
      props: {
        dense: this.dense
      }
    }, children);
  }
});
var VLabel = mixins(Themeable$1).extend({
  name: "v-label",
  functional: true,
  props: {
    absolute: Boolean,
    color: {
      type: String,
      default: "primary"
    },
    disabled: Boolean,
    focused: Boolean,
    for: String,
    left: {
      type: [Number, String],
      default: 0
    },
    right: {
      type: [Number, String],
      default: "auto"
    },
    value: Boolean
  },
  render(h, ctx) {
    const {
      children,
      listeners,
      props: props2
    } = ctx;
    const data = {
      staticClass: "v-label",
      class: __spreadValues({
        "v-label--active": props2.value,
        "v-label--is-disabled": props2.disabled
      }, functionalThemeClasses(ctx)),
      attrs: {
        for: props2.for,
        "aria-hidden": !props2.for
      },
      on: listeners,
      style: {
        left: convertToUnit(props2.left),
        right: convertToUnit(props2.right),
        position: props2.absolute ? "absolute" : "relative"
      },
      ref: "label"
    };
    return h("label", Colorable.options.methods.setTextColor(props2.focused && props2.color, data), children);
  }
});
var VMessages = mixins(Colorable, Themeable$1).extend({
  name: "v-messages",
  props: {
    value: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    genChildren() {
      return this.$createElement("transition-group", {
        staticClass: "v-messages__wrapper",
        attrs: {
          name: "message-transition",
          tag: "div"
        }
      }, this.value.map(this.genMessage));
    },
    genMessage(message, key) {
      return this.$createElement("div", {
        staticClass: "v-messages__message",
        key
      }, getSlot(this, "default", {
        message,
        key
      }) || [message]);
    }
  },
  render(h) {
    return h("div", this.setTextColor(this.color, {
      staticClass: "v-messages",
      class: this.themeClasses
    }), [this.genChildren()]);
  }
});
const baseMixins$m = mixins(Colorable, inject("form"), Themeable$1);
var Validatable = baseMixins$m.extend({
  name: "validatable",
  props: {
    disabled: Boolean,
    error: Boolean,
    errorCount: {
      type: [Number, String],
      default: 1
    },
    errorMessages: {
      type: [String, Array],
      default: () => []
    },
    messages: {
      type: [String, Array],
      default: () => []
    },
    readonly: Boolean,
    rules: {
      type: Array,
      default: () => []
    },
    success: Boolean,
    successMessages: {
      type: [String, Array],
      default: () => []
    },
    validateOnBlur: Boolean,
    value: {
      required: false
    }
  },
  data() {
    return {
      errorBucket: [],
      hasColor: false,
      hasFocused: false,
      hasInput: false,
      isFocused: false,
      isResetting: false,
      lazyValue: this.value,
      valid: false
    };
  },
  computed: {
    computedColor() {
      if (this.isDisabled)
        return void 0;
      if (this.color)
        return this.color;
      if (this.isDark && !this.appIsDark)
        return "white";
      else
        return "primary";
    },
    hasError() {
      return this.internalErrorMessages.length > 0 || this.errorBucket.length > 0 || this.error;
    },
    hasSuccess() {
      return this.internalSuccessMessages.length > 0 || this.success;
    },
    externalError() {
      return this.internalErrorMessages.length > 0 || this.error;
    },
    hasMessages() {
      return this.validationTarget.length > 0;
    },
    hasState() {
      if (this.isDisabled)
        return false;
      return this.hasSuccess || this.shouldValidate && this.hasError;
    },
    internalErrorMessages() {
      return this.genInternalMessages(this.errorMessages);
    },
    internalMessages() {
      return this.genInternalMessages(this.messages);
    },
    internalSuccessMessages() {
      return this.genInternalMessages(this.successMessages);
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.lazyValue = val;
        this.$emit("input", val);
      }
    },
    isDisabled() {
      return this.disabled || !!this.form && this.form.disabled;
    },
    isInteractive() {
      return !this.isDisabled && !this.isReadonly;
    },
    isReadonly() {
      return this.readonly || !!this.form && this.form.readonly;
    },
    shouldValidate() {
      if (this.externalError)
        return true;
      if (this.isResetting)
        return false;
      return this.validateOnBlur ? this.hasFocused && !this.isFocused : this.hasInput || this.hasFocused;
    },
    validations() {
      return this.validationTarget.slice(0, Number(this.errorCount));
    },
    validationState() {
      if (this.isDisabled)
        return void 0;
      if (this.hasError && this.shouldValidate)
        return "error";
      if (this.hasSuccess)
        return "success";
      if (this.hasColor)
        return this.computedColor;
      return void 0;
    },
    validationTarget() {
      if (this.internalErrorMessages.length > 0) {
        return this.internalErrorMessages;
      } else if (this.successMessages && this.successMessages.length > 0) {
        return this.internalSuccessMessages;
      } else if (this.messages && this.messages.length > 0) {
        return this.internalMessages;
      } else if (this.shouldValidate) {
        return this.errorBucket;
      } else
        return [];
    }
  },
  watch: {
    rules: {
      handler(newVal, oldVal) {
        if (deepEqual(newVal, oldVal))
          return;
        this.validate();
      },
      deep: true
    },
    internalValue() {
      this.hasInput = true;
      this.validateOnBlur || this.$nextTick(this.validate);
    },
    isFocused(val) {
      if (!val && !this.isDisabled) {
        this.hasFocused = true;
        this.validateOnBlur && this.$nextTick(this.validate);
      }
    },
    isResetting() {
      setTimeout(() => {
        this.hasInput = false;
        this.hasFocused = false;
        this.isResetting = false;
        this.validate();
      }, 0);
    },
    hasError(val) {
      if (this.shouldValidate) {
        this.$emit("update:error", val);
      }
    },
    value(val) {
      this.lazyValue = val;
    }
  },
  beforeMount() {
    this.validate();
  },
  created() {
    this.form && this.form.register(this);
  },
  beforeDestroy() {
    this.form && this.form.unregister(this);
  },
  methods: {
    genInternalMessages(messages) {
      if (!messages)
        return [];
      else if (Array.isArray(messages))
        return messages;
      else
        return [messages];
    },
    reset() {
      this.isResetting = true;
      this.internalValue = Array.isArray(this.internalValue) ? [] : null;
    },
    resetValidation() {
      this.isResetting = true;
    },
    validate(force = false, value) {
      const errorBucket = [];
      value = value || this.internalValue;
      if (force)
        this.hasInput = this.hasFocused = true;
      for (let index = 0; index < this.rules.length; index++) {
        const rule = this.rules[index];
        const valid = typeof rule === "function" ? rule(value) : rule;
        if (valid === false || typeof valid === "string") {
          errorBucket.push(valid || "");
        } else if (typeof valid !== "boolean") {
          consoleError(`Rules should return a string or boolean, received '${typeof valid}' instead`, this);
        }
      }
      this.errorBucket = errorBucket;
      this.valid = errorBucket.length === 0;
      return this.valid;
    }
  }
});
const baseMixins$l = mixins(BindsAttrs, Validatable);
var VInput = baseMixins$l.extend().extend({
  name: "v-input",
  inheritAttrs: false,
  props: {
    appendIcon: String,
    backgroundColor: {
      type: String,
      default: ""
    },
    dense: Boolean,
    height: [Number, String],
    hideDetails: [Boolean, String],
    hint: String,
    id: String,
    label: String,
    loading: Boolean,
    persistentHint: Boolean,
    prependIcon: String,
    value: null
  },
  data() {
    return {
      lazyValue: this.value,
      hasMouseDown: false
    };
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-input--has-state": this.hasState,
        "v-input--hide-details": !this.showDetails,
        "v-input--is-label-active": this.isLabelActive,
        "v-input--is-dirty": this.isDirty,
        "v-input--is-disabled": this.isDisabled,
        "v-input--is-focused": this.isFocused,
        "v-input--is-loading": this.loading !== false && this.loading != null,
        "v-input--is-readonly": this.isReadonly,
        "v-input--dense": this.dense
      }, this.themeClasses);
    },
    computedId() {
      return this.id || `input-${this._uid}`;
    },
    hasDetails() {
      return this.messagesToDisplay.length > 0;
    },
    hasHint() {
      return !this.hasMessages && !!this.hint && (this.persistentHint || this.isFocused);
    },
    hasLabel() {
      return !!(this.$slots.label || this.label);
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.lazyValue = val;
        this.$emit(this.$_modelEvent, val);
      }
    },
    isDirty() {
      return !!this.lazyValue;
    },
    isLabelActive() {
      return this.isDirty;
    },
    messagesToDisplay() {
      if (this.hasHint)
        return [this.hint];
      if (!this.hasMessages)
        return [];
      return this.validations.map((validation) => {
        if (typeof validation === "string")
          return validation;
        const validationResult = validation(this.internalValue);
        return typeof validationResult === "string" ? validationResult : "";
      }).filter((message) => message !== "");
    },
    showDetails() {
      return this.hideDetails === false || this.hideDetails === "auto" && this.hasDetails;
    }
  },
  watch: {
    value(val) {
      this.lazyValue = val;
    }
  },
  beforeCreate() {
    this.$_modelEvent = this.$options.model && this.$options.model.event || "input";
  },
  methods: {
    genContent() {
      return [this.genPrependSlot(), this.genControl(), this.genAppendSlot()];
    },
    genControl() {
      return this.$createElement("div", {
        staticClass: "v-input__control"
      }, [this.genInputSlot(), this.genMessages()]);
    },
    genDefaultSlot() {
      return [this.genLabel(), this.$slots.default];
    },
    genIcon(type2, cb, extraData = {}) {
      const icon = this[`${type2}Icon`];
      const eventName = `click:${kebabCase(type2)}`;
      const hasListener = !!(this.listeners$[eventName] || cb);
      const data = mergeData({
        attrs: {
          "aria-label": hasListener ? kebabCase(type2).split("-")[0] + " icon" : void 0,
          color: this.validationState,
          dark: this.dark,
          disabled: this.isDisabled,
          light: this.light
        },
        on: !hasListener ? void 0 : {
          click: (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.$emit(eventName, e);
            cb && cb(e);
          },
          mouseup: (e) => {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }, extraData);
      return this.$createElement("div", {
        staticClass: `v-input__icon`,
        class: type2 ? `v-input__icon--${kebabCase(type2)}` : void 0
      }, [this.$createElement(VIcon$1, data, icon)]);
    },
    genInputSlot() {
      return this.$createElement("div", this.setBackgroundColor(this.backgroundColor, {
        staticClass: "v-input__slot",
        style: {
          height: convertToUnit(this.height)
        },
        on: {
          click: this.onClick,
          mousedown: this.onMouseDown,
          mouseup: this.onMouseUp
        },
        ref: "input-slot"
      }), [this.genDefaultSlot()]);
    },
    genLabel() {
      if (!this.hasLabel)
        return null;
      return this.$createElement(VLabel, {
        props: {
          color: this.validationState,
          dark: this.dark,
          disabled: this.isDisabled,
          focused: this.hasState,
          for: this.computedId,
          light: this.light
        }
      }, this.$slots.label || this.label);
    },
    genMessages() {
      if (!this.showDetails)
        return null;
      return this.$createElement(VMessages, {
        props: {
          color: this.hasHint ? "" : this.validationState,
          dark: this.dark,
          light: this.light,
          value: this.messagesToDisplay
        },
        attrs: {
          role: this.hasMessages ? "alert" : null
        },
        scopedSlots: {
          default: (props2) => getSlot(this, "message", props2)
        }
      });
    },
    genSlot(type2, location, slot) {
      if (!slot.length)
        return null;
      const ref = `${type2}-${location}`;
      return this.$createElement("div", {
        staticClass: `v-input__${ref}`,
        ref
      }, slot);
    },
    genPrependSlot() {
      const slot = [];
      if (this.$slots.prepend) {
        slot.push(this.$slots.prepend);
      } else if (this.prependIcon) {
        slot.push(this.genIcon("prepend"));
      }
      return this.genSlot("prepend", "outer", slot);
    },
    genAppendSlot() {
      const slot = [];
      if (this.$slots.append) {
        slot.push(this.$slots.append);
      } else if (this.appendIcon) {
        slot.push(this.genIcon("append"));
      }
      return this.genSlot("append", "outer", slot);
    },
    onClick(e) {
      this.$emit("click", e);
    },
    onMouseDown(e) {
      this.hasMouseDown = true;
      this.$emit("mousedown", e);
    },
    onMouseUp(e) {
      this.hasMouseDown = false;
      this.$emit("mouseup", e);
    }
  },
  render(h) {
    return h("div", this.setTextColor(this.validationState, {
      staticClass: "v-input",
      class: this.classes
    }), this.genContent());
  }
});
var VCounter = mixins(Themeable$1).extend({
  name: "v-counter",
  functional: true,
  props: {
    value: {
      type: [Number, String],
      default: ""
    },
    max: [Number, String]
  },
  render(h, ctx) {
    const {
      props: props2
    } = ctx;
    const max = parseInt(props2.max, 10);
    const value = parseInt(props2.value, 10);
    const content = max ? `${value} / ${max}` : String(props2.value);
    const isGreater = max && value > max;
    return h("div", {
      staticClass: "v-counter",
      class: __spreadValues({
        "error--text": isGreater
      }, functionalThemeClasses(ctx))
    }, content);
  }
});
function intersectable(options2) {
  if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
    return Vue.extend({
      name: "intersectable"
    });
  }
  return Vue.extend({
    name: "intersectable",
    mounted() {
      intersect.inserted(this.$el, {
        name: "intersect",
        value: this.onObserve
      });
    },
    destroyed() {
      intersect.unbind(this.$el);
    },
    methods: {
      onObserve(entries, observer, isIntersecting) {
        if (!isIntersecting)
          return;
        for (let i = 0, length = options2.onVisible.length; i < length; i++) {
          const callback = this[options2.onVisible[i]];
          if (typeof callback === "function") {
            callback();
            continue;
          }
          consoleWarn(options2.onVisible[i] + " method is not available on the instance but referenced in intersectable mixin options");
        }
      }
    }
  });
}
const baseMixins$k = mixins(Colorable, factory$3(["absolute", "fixed", "top", "bottom"]), Proxyable$1, Themeable$1);
var VProgressLinear = baseMixins$k.extend({
  name: "v-progress-linear",
  props: {
    active: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: null
    },
    backgroundOpacity: {
      type: [Number, String],
      default: null
    },
    bufferValue: {
      type: [Number, String],
      default: 100
    },
    color: {
      type: String,
      default: "primary"
    },
    height: {
      type: [Number, String],
      default: 4
    },
    indeterminate: Boolean,
    query: Boolean,
    reverse: Boolean,
    rounded: Boolean,
    stream: Boolean,
    striped: Boolean,
    value: {
      type: [Number, String],
      default: 0
    }
  },
  data() {
    return {
      internalLazyValue: this.value || 0
    };
  },
  computed: {
    __cachedBackground() {
      return this.$createElement("div", this.setBackgroundColor(this.backgroundColor || this.color, {
        staticClass: "v-progress-linear__background",
        style: this.backgroundStyle
      }));
    },
    __cachedBar() {
      return this.$createElement(this.computedTransition, [this.__cachedBarType]);
    },
    __cachedBarType() {
      return this.indeterminate ? this.__cachedIndeterminate : this.__cachedDeterminate;
    },
    __cachedBuffer() {
      return this.$createElement("div", {
        staticClass: "v-progress-linear__buffer",
        style: this.styles
      });
    },
    __cachedDeterminate() {
      return this.$createElement("div", this.setBackgroundColor(this.color, {
        staticClass: `v-progress-linear__determinate`,
        style: {
          width: convertToUnit(this.normalizedValue, "%")
        }
      }));
    },
    __cachedIndeterminate() {
      return this.$createElement("div", {
        staticClass: "v-progress-linear__indeterminate",
        class: {
          "v-progress-linear__indeterminate--active": this.active
        }
      }, [this.genProgressBar("long"), this.genProgressBar("short")]);
    },
    __cachedStream() {
      if (!this.stream)
        return null;
      return this.$createElement("div", this.setTextColor(this.color, {
        staticClass: "v-progress-linear__stream",
        style: {
          width: convertToUnit(100 - this.normalizedBuffer, "%")
        }
      }));
    },
    backgroundStyle() {
      const backgroundOpacity = this.backgroundOpacity == null ? this.backgroundColor ? 1 : 0.3 : parseFloat(this.backgroundOpacity);
      return {
        opacity: backgroundOpacity,
        [this.isReversed ? "right" : "left"]: convertToUnit(this.normalizedValue, "%"),
        width: convertToUnit(this.normalizedBuffer - this.normalizedValue, "%")
      };
    },
    classes() {
      return __spreadValues({
        "v-progress-linear--absolute": this.absolute,
        "v-progress-linear--fixed": this.fixed,
        "v-progress-linear--query": this.query,
        "v-progress-linear--reactive": this.reactive,
        "v-progress-linear--reverse": this.isReversed,
        "v-progress-linear--rounded": this.rounded,
        "v-progress-linear--striped": this.striped
      }, this.themeClasses);
    },
    computedTransition() {
      return this.indeterminate ? VFadeTransition : VSlideXTransition;
    },
    isReversed() {
      return this.$vuetify.rtl !== this.reverse;
    },
    normalizedBuffer() {
      return this.normalize(this.bufferValue);
    },
    normalizedValue() {
      return this.normalize(this.internalLazyValue);
    },
    reactive() {
      return Boolean(this.$listeners.change);
    },
    styles() {
      const styles = {};
      if (!this.active) {
        styles.height = 0;
      }
      if (!this.indeterminate && parseFloat(this.normalizedBuffer) !== 100) {
        styles.width = convertToUnit(this.normalizedBuffer, "%");
      }
      return styles;
    }
  },
  methods: {
    genContent() {
      const slot = getSlot(this, "default", {
        value: this.internalLazyValue
      });
      if (!slot)
        return null;
      return this.$createElement("div", {
        staticClass: "v-progress-linear__content"
      }, slot);
    },
    genListeners() {
      const listeners = this.$listeners;
      if (this.reactive) {
        listeners.click = this.onClick;
      }
      return listeners;
    },
    genProgressBar(name) {
      return this.$createElement("div", this.setBackgroundColor(this.color, {
        staticClass: "v-progress-linear__indeterminate",
        class: {
          [name]: true
        }
      }));
    },
    onClick(e) {
      if (!this.reactive)
        return;
      const {
        width
      } = this.$el.getBoundingClientRect();
      this.internalValue = e.offsetX / width * 100;
    },
    normalize(value) {
      if (value < 0)
        return 0;
      if (value > 100)
        return 100;
      return parseFloat(value);
    }
  },
  render(h) {
    const data = {
      staticClass: "v-progress-linear",
      attrs: {
        role: "progressbar",
        "aria-valuemin": 0,
        "aria-valuemax": this.normalizedBuffer,
        "aria-valuenow": this.indeterminate ? void 0 : this.normalizedValue
      },
      class: this.classes,
      style: {
        bottom: this.bottom ? 0 : void 0,
        height: this.active ? convertToUnit(this.height) : 0,
        top: this.top ? 0 : void 0
      },
      on: this.genListeners()
    };
    return h("div", data, [this.__cachedStream, this.__cachedBackground, this.__cachedBuffer, this.__cachedBar, this.genContent()]);
  }
});
var Loadable = Vue.extend().extend({
  name: "loadable",
  props: {
    loading: {
      type: [Boolean, String],
      default: false
    },
    loaderHeight: {
      type: [Number, String],
      default: 2
    }
  },
  methods: {
    genProgress() {
      if (this.loading === false)
        return null;
      return this.$slots.progress || this.$createElement(VProgressLinear, {
        props: {
          absolute: true,
          color: this.loading === true || this.loading === "" ? this.color || "primary" : this.loading,
          height: this.loaderHeight,
          indeterminate: true
        }
      });
    }
  }
});
function attachedRoot(node) {
  if (typeof node.getRootNode !== "function") {
    while (node.parentNode)
      node = node.parentNode;
    if (node !== document)
      return null;
    return document;
  }
  const root = node.getRootNode();
  if (root !== document && root.getRootNode({
    composed: true
  }) !== document)
    return null;
  return root;
}
const baseMixins$j = mixins(VInput, intersectable({
  onVisible: ["onResize", "tryAutofocus"]
}), Loadable);
const dirtyTypes = ["color", "file", "time", "date", "datetime-local", "week", "month"];
var VTextField = baseMixins$j.extend().extend({
  name: "v-text-field",
  directives: {
    resize: Resize$1,
    ripple
  },
  inheritAttrs: false,
  props: {
    appendOuterIcon: String,
    autofocus: Boolean,
    clearable: Boolean,
    clearIcon: {
      type: String,
      default: "$clear"
    },
    counter: [Boolean, Number, String],
    counterValue: Function,
    filled: Boolean,
    flat: Boolean,
    fullWidth: Boolean,
    label: String,
    outlined: Boolean,
    placeholder: String,
    prefix: String,
    prependInnerIcon: String,
    reverse: Boolean,
    rounded: Boolean,
    shaped: Boolean,
    singleLine: Boolean,
    solo: Boolean,
    soloInverted: Boolean,
    suffix: String,
    type: {
      type: String,
      default: "text"
    }
  },
  data: () => ({
    badInput: false,
    labelWidth: 0,
    prefixWidth: 0,
    prependWidth: 0,
    initialValue: null,
    isBooted: false,
    isClearing: false
  }),
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VInput.options.computed.classes.call(this)), {
        "v-text-field": true,
        "v-text-field--full-width": this.fullWidth,
        "v-text-field--prefix": this.prefix,
        "v-text-field--single-line": this.isSingle,
        "v-text-field--solo": this.isSolo,
        "v-text-field--solo-inverted": this.soloInverted,
        "v-text-field--solo-flat": this.flat,
        "v-text-field--filled": this.filled,
        "v-text-field--is-booted": this.isBooted,
        "v-text-field--enclosed": this.isEnclosed,
        "v-text-field--reverse": this.reverse,
        "v-text-field--outlined": this.outlined,
        "v-text-field--placeholder": this.placeholder,
        "v-text-field--rounded": this.rounded,
        "v-text-field--shaped": this.shaped
      });
    },
    computedColor() {
      const computedColor = Validatable.options.computed.computedColor.call(this);
      if (!this.soloInverted || !this.isFocused)
        return computedColor;
      return this.color || "primary";
    },
    computedCounterValue() {
      if (typeof this.counterValue === "function") {
        return this.counterValue(this.internalValue);
      }
      return [...(this.internalValue || "").toString()].length;
    },
    hasCounter() {
      return this.counter !== false && this.counter != null;
    },
    hasDetails() {
      return VInput.options.computed.hasDetails.call(this) || this.hasCounter;
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.lazyValue = val;
        this.$emit("input", this.lazyValue);
      }
    },
    isDirty() {
      var _this$lazyValue;
      return ((_this$lazyValue = this.lazyValue) == null ? void 0 : _this$lazyValue.toString().length) > 0 || this.badInput;
    },
    isEnclosed() {
      return this.filled || this.isSolo || this.outlined;
    },
    isLabelActive() {
      return this.isDirty || dirtyTypes.includes(this.type);
    },
    isSingle() {
      return this.isSolo || this.singleLine || this.fullWidth || this.filled && !this.hasLabel;
    },
    isSolo() {
      return this.solo || this.soloInverted;
    },
    labelPosition() {
      let offset = this.prefix && !this.labelValue ? this.prefixWidth : 0;
      if (this.labelValue && this.prependWidth)
        offset -= this.prependWidth;
      return this.$vuetify.rtl === this.reverse ? {
        left: offset,
        right: "auto"
      } : {
        left: "auto",
        right: offset
      };
    },
    showLabel() {
      return this.hasLabel && !(this.isSingle && this.labelValue);
    },
    labelValue() {
      return this.isFocused || this.isLabelActive;
    }
  },
  watch: {
    outlined: "setLabelWidth",
    label() {
      this.$nextTick(this.setLabelWidth);
    },
    prefix() {
      this.$nextTick(this.setPrefixWidth);
    },
    isFocused: "updateValue",
    value(val) {
      this.lazyValue = val;
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("box")) {
      breaking("box", "filled", this);
    }
    if (this.$attrs.hasOwnProperty("browser-autocomplete")) {
      breaking("browser-autocomplete", "autocomplete", this);
    }
    if (this.shaped && !(this.filled || this.outlined || this.isSolo)) {
      consoleWarn("shaped should be used with either filled or outlined", this);
    }
  },
  mounted() {
    this.$watch(() => this.labelValue, this.setLabelWidth);
    this.autofocus && this.tryAutofocus();
    requestAnimationFrame(() => this.isBooted = true);
  },
  methods: {
    focus() {
      this.onFocus();
    },
    blur(e) {
      window.requestAnimationFrame(() => {
        this.$refs.input && this.$refs.input.blur();
      });
    },
    clearableCallback() {
      this.$refs.input && this.$refs.input.focus();
      this.$nextTick(() => this.internalValue = null);
    },
    genAppendSlot() {
      const slot = [];
      if (this.$slots["append-outer"]) {
        slot.push(this.$slots["append-outer"]);
      } else if (this.appendOuterIcon) {
        slot.push(this.genIcon("appendOuter"));
      }
      return this.genSlot("append", "outer", slot);
    },
    genPrependInnerSlot() {
      const slot = [];
      if (this.$slots["prepend-inner"]) {
        slot.push(this.$slots["prepend-inner"]);
      } else if (this.prependInnerIcon) {
        slot.push(this.genIcon("prependInner"));
      }
      return this.genSlot("prepend", "inner", slot);
    },
    genIconSlot() {
      const slot = [];
      if (this.$slots.append) {
        slot.push(this.$slots.append);
      } else if (this.appendIcon) {
        slot.push(this.genIcon("append"));
      }
      return this.genSlot("append", "inner", slot);
    },
    genInputSlot() {
      const input = VInput.options.methods.genInputSlot.call(this);
      const prepend = this.genPrependInnerSlot();
      if (prepend) {
        input.children = input.children || [];
        input.children.unshift(prepend);
      }
      return input;
    },
    genClearIcon() {
      if (!this.clearable)
        return null;
      const data = this.isDirty ? void 0 : {
        attrs: {
          disabled: true
        }
      };
      return this.genSlot("append", "inner", [this.genIcon("clear", this.clearableCallback, data)]);
    },
    genCounter() {
      var _ref;
      if (!this.hasCounter)
        return null;
      const max = this.counter === true ? this.attrs$.maxlength : this.counter;
      const props2 = {
        dark: this.dark,
        light: this.light,
        max,
        value: this.computedCounterValue
      };
      return (_ref = this.$scopedSlots.counter == null ? void 0 : this.$scopedSlots.counter({
        props: props2
      })) != null ? _ref : this.$createElement(VCounter, {
        props: props2
      });
    },
    genControl() {
      return VInput.options.methods.genControl.call(this);
    },
    genDefaultSlot() {
      return [this.genFieldset(), this.genTextFieldSlot(), this.genClearIcon(), this.genIconSlot(), this.genProgress()];
    },
    genFieldset() {
      if (!this.outlined)
        return null;
      return this.$createElement("fieldset", {
        attrs: {
          "aria-hidden": true
        }
      }, [this.genLegend()]);
    },
    genLabel() {
      if (!this.showLabel)
        return null;
      const data = {
        props: {
          absolute: true,
          color: this.validationState,
          dark: this.dark,
          disabled: this.isDisabled,
          focused: !this.isSingle && (this.isFocused || !!this.validationState),
          for: this.computedId,
          left: this.labelPosition.left,
          light: this.light,
          right: this.labelPosition.right,
          value: this.labelValue
        }
      };
      return this.$createElement(VLabel, data, this.$slots.label || this.label);
    },
    genLegend() {
      const width = !this.singleLine && (this.labelValue || this.isDirty) ? this.labelWidth : 0;
      const span = this.$createElement("span", {
        domProps: {
          innerHTML: "&#8203;"
        }
      });
      return this.$createElement("legend", {
        style: {
          width: !this.isSingle ? convertToUnit(width) : void 0
        }
      }, [span]);
    },
    genInput() {
      const listeners = Object.assign({}, this.listeners$);
      delete listeners.change;
      return this.$createElement("input", {
        style: {},
        domProps: {
          value: this.type === "number" && Object.is(this.lazyValue, -0) ? "-0" : this.lazyValue
        },
        attrs: __spreadProps(__spreadValues({}, this.attrs$), {
          autofocus: this.autofocus,
          disabled: this.isDisabled,
          id: this.computedId,
          placeholder: this.isFocused || !this.hasLabel ? this.placeholder : void 0,
          readonly: this.isReadonly,
          type: this.type
        }),
        on: Object.assign(listeners, {
          blur: this.onBlur,
          input: this.onInput,
          focus: this.onFocus,
          keydown: this.onKeyDown
        }),
        ref: "input",
        directives: [{
          name: "resize",
          modifiers: {
            quiet: true
          },
          value: this.onResize
        }]
      });
    },
    genMessages() {
      if (!this.showDetails)
        return null;
      const messagesNode = VInput.options.methods.genMessages.call(this);
      const counterNode = this.genCounter();
      return this.$createElement("div", {
        staticClass: "v-text-field__details"
      }, [messagesNode, counterNode]);
    },
    genTextFieldSlot() {
      return this.$createElement("div", {
        staticClass: "v-text-field__slot"
      }, [this.genLabel(), this.prefix ? this.genAffix("prefix") : null, this.genInput(), this.suffix ? this.genAffix("suffix") : null]);
    },
    genAffix(type2) {
      return this.$createElement("div", {
        class: `v-text-field__${type2}`,
        ref: type2
      }, this[type2]);
    },
    onBlur(e) {
      this.isFocused = false;
      e && this.$nextTick(() => this.$emit("blur", e));
    },
    onClick() {
      if (this.isFocused || this.isDisabled || !this.$refs.input)
        return;
      this.$refs.input.focus();
    },
    onFocus(e) {
      if (!this.$refs.input)
        return;
      const root = attachedRoot(this.$el);
      if (!root)
        return;
      if (root.activeElement !== this.$refs.input) {
        return this.$refs.input.focus();
      }
      if (!this.isFocused) {
        this.isFocused = true;
        e && this.$emit("focus", e);
      }
    },
    onInput(e) {
      const target = e.target;
      this.internalValue = target.value;
      this.badInput = target.validity && target.validity.badInput;
    },
    onKeyDown(e) {
      if (e.keyCode === keyCodes.enter)
        this.$emit("change", this.internalValue);
      this.$emit("keydown", e);
    },
    onMouseDown(e) {
      if (e.target !== this.$refs.input) {
        e.preventDefault();
        e.stopPropagation();
      }
      VInput.options.methods.onMouseDown.call(this, e);
    },
    onMouseUp(e) {
      if (this.hasMouseDown)
        this.focus();
      VInput.options.methods.onMouseUp.call(this, e);
    },
    setLabelWidth() {
      if (!this.outlined)
        return;
      this.labelWidth = this.$refs.label ? Math.min(this.$refs.label.scrollWidth * 0.75 + 6, this.$el.offsetWidth - 24) : 0;
    },
    setPrefixWidth() {
      if (!this.$refs.prefix)
        return;
      this.prefixWidth = this.$refs.prefix.offsetWidth;
    },
    setPrependWidth() {
      if (!this.outlined || !this.$refs["prepend-inner"])
        return;
      this.prependWidth = this.$refs["prepend-inner"].offsetWidth;
    },
    tryAutofocus() {
      if (!this.autofocus || typeof document === "undefined" || !this.$refs.input)
        return false;
      const root = attachedRoot(this.$el);
      if (!root || root.activeElement === this.$refs.input)
        return false;
      this.$refs.input.focus();
      return true;
    },
    updateValue(val) {
      this.hasColor = val;
      if (val) {
        this.initialValue = this.lazyValue;
      } else if (this.initialValue !== this.lazyValue) {
        this.$emit("change", this.lazyValue);
      }
    },
    onResize() {
      this.setLabelWidth();
      this.setPrefixWidth();
      this.setPrependWidth();
    }
  }
});
var Comparable = Vue.extend({
  name: "comparable",
  props: {
    valueComparator: {
      type: Function,
      default: deepEqual
    }
  }
});
var Filterable = Vue.extend({
  name: "filterable",
  props: {
    noDataText: {
      type: String,
      default: "$vuetify.noDataText"
    }
  }
});
const defaultMenuProps$1 = {
  closeOnClick: false,
  closeOnContentClick: false,
  disableKeys: true,
  openOnClick: false,
  maxHeight: 304
};
const baseMixins$i = mixins(VTextField, Comparable, Dependent, Filterable);
var VSelect = baseMixins$i.extend().extend({
  name: "v-select",
  directives: {
    ClickOutside: ClickOutside$1
  },
  props: {
    appendIcon: {
      type: String,
      default: "$dropdown"
    },
    attach: {
      type: null,
      default: false
    },
    cacheItems: Boolean,
    chips: Boolean,
    clearable: Boolean,
    deletableChips: Boolean,
    disableLookup: Boolean,
    eager: Boolean,
    hideSelected: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    itemColor: {
      type: String,
      default: "primary"
    },
    itemDisabled: {
      type: [String, Array, Function],
      default: "disabled"
    },
    itemText: {
      type: [String, Array, Function],
      default: "text"
    },
    itemValue: {
      type: [String, Array, Function],
      default: "value"
    },
    menuProps: {
      type: [String, Array, Object],
      default: () => defaultMenuProps$1
    },
    multiple: Boolean,
    openOnClear: Boolean,
    returnObject: Boolean,
    smallChips: Boolean
  },
  data() {
    return {
      cachedItems: this.cacheItems ? this.items : [],
      menuIsBooted: false,
      isMenuActive: false,
      lastItem: 20,
      lazyValue: this.value !== void 0 ? this.value : this.multiple ? [] : void 0,
      selectedIndex: -1,
      selectedItems: [],
      keyboardLookupPrefix: "",
      keyboardLookupLastTime: 0
    };
  },
  computed: {
    allItems() {
      return this.filterDuplicates(this.cachedItems.concat(this.items));
    },
    classes() {
      return __spreadProps(__spreadValues({}, VTextField.options.computed.classes.call(this)), {
        "v-select": true,
        "v-select--chips": this.hasChips,
        "v-select--chips--small": this.smallChips,
        "v-select--is-menu-active": this.isMenuActive,
        "v-select--is-multi": this.multiple
      });
    },
    computedItems() {
      return this.allItems;
    },
    computedOwns() {
      return `list-${this._uid}`;
    },
    computedCounterValue() {
      const value = this.multiple ? this.selectedItems : (this.getText(this.selectedItems[0]) || "").toString();
      if (typeof this.counterValue === "function") {
        return this.counterValue(value);
      }
      return value.length;
    },
    directives() {
      return this.isFocused ? [{
        name: "click-outside",
        value: {
          handler: this.blur,
          closeConditional: this.closeConditional,
          include: () => this.getOpenDependentElements()
        }
      }] : void 0;
    },
    dynamicHeight() {
      return "auto";
    },
    hasChips() {
      return this.chips || this.smallChips;
    },
    hasSlot() {
      return Boolean(this.hasChips || this.$scopedSlots.selection);
    },
    isDirty() {
      return this.selectedItems.length > 0;
    },
    listData() {
      const scopeId = this.$vnode && this.$vnode.context.$options._scopeId;
      const attrs = scopeId ? {
        [scopeId]: true
      } : {};
      return {
        attrs: __spreadProps(__spreadValues({}, attrs), {
          id: this.computedOwns
        }),
        props: {
          action: this.multiple,
          color: this.itemColor,
          dense: this.dense,
          hideSelected: this.hideSelected,
          items: this.virtualizedItems,
          itemDisabled: this.itemDisabled,
          itemText: this.itemText,
          itemValue: this.itemValue,
          noDataText: this.$vuetify.lang.t(this.noDataText),
          selectedItems: this.selectedItems
        },
        on: {
          select: this.selectItem
        },
        scopedSlots: {
          item: this.$scopedSlots.item
        }
      };
    },
    staticList() {
      if (this.$slots["no-data"] || this.$slots["prepend-item"] || this.$slots["append-item"]) {
        consoleError("assert: staticList should not be called if slots are used");
      }
      return this.$createElement(VSelectList, this.listData);
    },
    virtualizedItems() {
      return this.$_menuProps.auto ? this.computedItems : this.computedItems.slice(0, this.lastItem);
    },
    menuCanShow: () => true,
    $_menuProps() {
      let normalisedProps = typeof this.menuProps === "string" ? this.menuProps.split(",") : this.menuProps;
      if (Array.isArray(normalisedProps)) {
        normalisedProps = normalisedProps.reduce((acc, p) => {
          acc[p.trim()] = true;
          return acc;
        }, {});
      }
      return __spreadValues(__spreadProps(__spreadValues({}, defaultMenuProps$1), {
        eager: this.eager,
        value: this.menuCanShow && this.isMenuActive,
        nudgeBottom: normalisedProps.offsetY ? 1 : 0
      }), normalisedProps);
    }
  },
  watch: {
    internalValue(val) {
      this.initialValue = val;
      this.setSelectedItems();
    },
    isMenuActive(val) {
      window.setTimeout(() => this.onMenuActiveChange(val));
    },
    items: {
      immediate: true,
      handler(val) {
        if (this.cacheItems) {
          this.$nextTick(() => {
            this.cachedItems = this.filterDuplicates(this.cachedItems.concat(val));
          });
        }
        this.setSelectedItems();
      }
    }
  },
  methods: {
    blur(e) {
      VTextField.options.methods.blur.call(this, e);
      this.isMenuActive = false;
      this.isFocused = false;
      this.selectedIndex = -1;
      this.setMenuIndex(-1);
    },
    activateMenu() {
      if (!this.isInteractive || this.isMenuActive)
        return;
      this.isMenuActive = true;
    },
    clearableCallback() {
      this.setValue(this.multiple ? [] : null);
      this.setMenuIndex(-1);
      this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      if (this.openOnClear)
        this.isMenuActive = true;
    },
    closeConditional(e) {
      if (!this.isMenuActive)
        return true;
      return !this._isDestroyed && (!this.getContent() || !this.getContent().contains(e.target)) && this.$el && !this.$el.contains(e.target) && e.target !== this.$el;
    },
    filterDuplicates(arr) {
      const uniqueValues = new Map();
      for (let index = 0; index < arr.length; ++index) {
        const item = arr[index];
        if (item.header || item.divider) {
          uniqueValues.set(item, item);
          continue;
        }
        const val = this.getValue(item);
        !uniqueValues.has(val) && uniqueValues.set(val, item);
      }
      return Array.from(uniqueValues.values());
    },
    findExistingIndex(item) {
      const itemValue = this.getValue(item);
      return (this.internalValue || []).findIndex((i) => this.valueComparator(this.getValue(i), itemValue));
    },
    getContent() {
      return this.$refs.menu && this.$refs.menu.$refs.content;
    },
    genChipSelection(item, index) {
      const isDisabled = this.isDisabled || this.getDisabled(item);
      const isInteractive = !isDisabled && this.isInteractive;
      return this.$createElement(VChip, {
        staticClass: "v-chip--select",
        attrs: {
          tabindex: -1
        },
        props: {
          close: this.deletableChips && isInteractive,
          disabled: isDisabled,
          inputValue: index === this.selectedIndex,
          small: this.smallChips
        },
        on: {
          click: (e) => {
            if (!isInteractive)
              return;
            e.stopPropagation();
            this.selectedIndex = index;
          },
          "click:close": () => this.onChipInput(item)
        },
        key: JSON.stringify(this.getValue(item))
      }, this.getText(item));
    },
    genCommaSelection(item, index, last) {
      const color = index === this.selectedIndex && this.computedColor;
      const isDisabled = this.isDisabled || this.getDisabled(item);
      return this.$createElement("div", this.setTextColor(color, {
        staticClass: "v-select__selection v-select__selection--comma",
        class: {
          "v-select__selection--disabled": isDisabled
        },
        key: JSON.stringify(this.getValue(item))
      }), `${this.getText(item)}${last ? "" : ", "}`);
    },
    genDefaultSlot() {
      const selections = this.genSelections();
      const input = this.genInput();
      if (Array.isArray(selections)) {
        selections.push(input);
      } else {
        selections.children = selections.children || [];
        selections.children.push(input);
      }
      return [this.genFieldset(), this.$createElement("div", {
        staticClass: "v-select__slot",
        directives: this.directives
      }, [this.genLabel(), this.prefix ? this.genAffix("prefix") : null, selections, this.suffix ? this.genAffix("suffix") : null, this.genClearIcon(), this.genIconSlot(), this.genHiddenInput()]), this.genMenu(), this.genProgress()];
    },
    genIcon(type2, cb, extraData) {
      const icon = VInput.options.methods.genIcon.call(this, type2, cb, extraData);
      if (type2 === "append") {
        icon.children[0].data = mergeData(icon.children[0].data, {
          attrs: {
            tabindex: icon.children[0].componentOptions.listeners && "-1",
            "aria-hidden": "true",
            "aria-label": void 0
          }
        });
      }
      return icon;
    },
    genInput() {
      const input = VTextField.options.methods.genInput.call(this);
      delete input.data.attrs.name;
      input.data = mergeData(input.data, {
        domProps: {
          value: null
        },
        attrs: {
          readonly: true,
          type: "text",
          "aria-readonly": String(this.isReadonly),
          "aria-activedescendant": getObjectValueByPath(this.$refs.menu, "activeTile.id"),
          autocomplete: getObjectValueByPath(input.data, "attrs.autocomplete", "off"),
          placeholder: !this.isDirty && (this.isFocused || !this.hasLabel) ? this.placeholder : void 0
        },
        on: {
          keypress: this.onKeyPress
        }
      });
      return input;
    },
    genHiddenInput() {
      return this.$createElement("input", {
        domProps: {
          value: this.lazyValue
        },
        attrs: {
          type: "hidden",
          name: this.attrs$.name
        }
      });
    },
    genInputSlot() {
      const render = VTextField.options.methods.genInputSlot.call(this);
      render.data.attrs = __spreadProps(__spreadValues({}, render.data.attrs), {
        role: "button",
        "aria-haspopup": "listbox",
        "aria-expanded": String(this.isMenuActive),
        "aria-owns": this.computedOwns
      });
      return render;
    },
    genList() {
      if (this.$slots["no-data"] || this.$slots["prepend-item"] || this.$slots["append-item"]) {
        return this.genListWithSlot();
      } else {
        return this.staticList;
      }
    },
    genListWithSlot() {
      const slots = ["prepend-item", "no-data", "append-item"].filter((slotName) => this.$slots[slotName]).map((slotName) => this.$createElement("template", {
        slot: slotName
      }, this.$slots[slotName]));
      return this.$createElement(VSelectList, __spreadValues({}, this.listData), slots);
    },
    genMenu() {
      const props2 = this.$_menuProps;
      props2.activator = this.$refs["input-slot"];
      if (this.attach === "" || this.attach === true || this.attach === "attach") {
        props2.attach = this.$el;
      } else {
        props2.attach = this.attach;
      }
      return this.$createElement(VMenu, {
        attrs: {
          role: void 0
        },
        props: props2,
        on: {
          input: (val) => {
            this.isMenuActive = val;
            this.isFocused = val;
          },
          scroll: this.onScroll
        },
        ref: "menu"
      }, [this.genList()]);
    },
    genSelections() {
      let length = this.selectedItems.length;
      const children = new Array(length);
      let genSelection;
      if (this.$scopedSlots.selection) {
        genSelection = this.genSlotSelection;
      } else if (this.hasChips) {
        genSelection = this.genChipSelection;
      } else {
        genSelection = this.genCommaSelection;
      }
      while (length--) {
        children[length] = genSelection(this.selectedItems[length], length, length === children.length - 1);
      }
      return this.$createElement("div", {
        staticClass: "v-select__selections"
      }, children);
    },
    genSlotSelection(item, index) {
      return this.$scopedSlots.selection({
        attrs: {
          class: "v-chip--select"
        },
        parent: this,
        item,
        index,
        select: (e) => {
          e.stopPropagation();
          this.selectedIndex = index;
        },
        selected: index === this.selectedIndex,
        disabled: !this.isInteractive
      });
    },
    getMenuIndex() {
      return this.$refs.menu ? this.$refs.menu.listIndex : -1;
    },
    getDisabled(item) {
      return getPropertyFromItem(item, this.itemDisabled, false);
    },
    getText(item) {
      return getPropertyFromItem(item, this.itemText, item);
    },
    getValue(item) {
      return getPropertyFromItem(item, this.itemValue, this.getText(item));
    },
    onBlur(e) {
      e && this.$emit("blur", e);
    },
    onChipInput(item) {
      if (this.multiple)
        this.selectItem(item);
      else
        this.setValue(null);
      if (this.selectedItems.length === 0) {
        this.isMenuActive = true;
      } else {
        this.isMenuActive = false;
      }
      this.selectedIndex = -1;
    },
    onClick(e) {
      if (!this.isInteractive)
        return;
      if (!this.isAppendInner(e.target)) {
        this.isMenuActive = true;
      }
      if (!this.isFocused) {
        this.isFocused = true;
        this.$emit("focus");
      }
      this.$emit("click", e);
    },
    onEscDown(e) {
      e.preventDefault();
      if (this.isMenuActive) {
        e.stopPropagation();
        this.isMenuActive = false;
      }
    },
    onKeyPress(e) {
      if (this.multiple || !this.isInteractive || this.disableLookup)
        return;
      const KEYBOARD_LOOKUP_THRESHOLD = 1e3;
      const now = performance.now();
      if (now - this.keyboardLookupLastTime > KEYBOARD_LOOKUP_THRESHOLD) {
        this.keyboardLookupPrefix = "";
      }
      this.keyboardLookupPrefix += e.key.toLowerCase();
      this.keyboardLookupLastTime = now;
      const index = this.allItems.findIndex((item2) => {
        const text = (this.getText(item2) || "").toString();
        return text.toLowerCase().startsWith(this.keyboardLookupPrefix);
      });
      const item = this.allItems[index];
      if (index !== -1) {
        this.lastItem = Math.max(this.lastItem, index + 5);
        this.setValue(this.returnObject ? item : this.getValue(item));
        this.$nextTick(() => this.$refs.menu.getTiles());
        setTimeout(() => this.setMenuIndex(index));
      }
    },
    onKeyDown(e) {
      if (this.isReadonly && e.keyCode !== keyCodes.tab)
        return;
      const keyCode = e.keyCode;
      const menu = this.$refs.menu;
      if ([keyCodes.enter, keyCodes.space].includes(keyCode))
        this.activateMenu();
      this.$emit("keydown", e);
      if (!menu)
        return;
      if (this.isMenuActive && keyCode !== keyCodes.tab) {
        this.$nextTick(() => {
          menu.changeListIndex(e);
          this.$emit("update:list-index", menu.listIndex);
        });
      }
      if (!this.isMenuActive && [keyCodes.up, keyCodes.down, keyCodes.home, keyCodes.end].includes(keyCode))
        return this.onUpDown(e);
      if (keyCode === keyCodes.esc)
        return this.onEscDown(e);
      if (keyCode === keyCodes.tab)
        return this.onTabDown(e);
      if (keyCode === keyCodes.space)
        return this.onSpaceDown(e);
    },
    onMenuActiveChange(val) {
      if (this.multiple && !val || this.getMenuIndex() > -1)
        return;
      const menu = this.$refs.menu;
      if (!menu || !this.isDirty)
        return;
      for (let i = 0; i < menu.tiles.length; i++) {
        if (menu.tiles[i].getAttribute("aria-selected") === "true") {
          this.setMenuIndex(i);
          break;
        }
      }
    },
    onMouseUp(e) {
      if (this.hasMouseDown && e.which !== 3 && this.isInteractive) {
        if (this.isAppendInner(e.target)) {
          this.$nextTick(() => this.isMenuActive = !this.isMenuActive);
        }
      }
      VTextField.options.methods.onMouseUp.call(this, e);
    },
    onScroll() {
      if (!this.isMenuActive) {
        requestAnimationFrame(() => this.getContent().scrollTop = 0);
      } else {
        if (this.lastItem > this.computedItems.length)
          return;
        const showMoreItems = this.getContent().scrollHeight - (this.getContent().scrollTop + this.getContent().clientHeight) < 200;
        if (showMoreItems) {
          this.lastItem += 20;
        }
      }
    },
    onSpaceDown(e) {
      e.preventDefault();
    },
    onTabDown(e) {
      const menu = this.$refs.menu;
      if (!menu)
        return;
      const activeTile = menu.activeTile;
      if (!this.multiple && activeTile && this.isMenuActive) {
        e.preventDefault();
        e.stopPropagation();
        activeTile.click();
      } else {
        this.blur(e);
      }
    },
    onUpDown(e) {
      const menu = this.$refs.menu;
      if (!menu)
        return;
      e.preventDefault();
      if (this.multiple)
        return this.activateMenu();
      const keyCode = e.keyCode;
      menu.isBooted = true;
      window.requestAnimationFrame(() => {
        menu.getTiles();
        if (!menu.hasClickableTiles)
          return this.activateMenu();
        switch (keyCode) {
          case keyCodes.up:
            menu.prevTile();
            break;
          case keyCodes.down:
            menu.nextTile();
            break;
          case keyCodes.home:
            menu.firstTile();
            break;
          case keyCodes.end:
            menu.lastTile();
            break;
        }
        this.selectItem(this.allItems[this.getMenuIndex()]);
      });
    },
    selectItem(item) {
      if (!this.multiple) {
        this.setValue(this.returnObject ? item : this.getValue(item));
        this.isMenuActive = false;
      } else {
        const internalValue = (this.internalValue || []).slice();
        const i = this.findExistingIndex(item);
        i !== -1 ? internalValue.splice(i, 1) : internalValue.push(item);
        this.setValue(internalValue.map((i2) => {
          return this.returnObject ? i2 : this.getValue(i2);
        }));
        this.$nextTick(() => {
          this.$refs.menu && this.$refs.menu.updateDimensions();
        });
        if (!this.multiple)
          return;
        const listIndex = this.getMenuIndex();
        this.setMenuIndex(-1);
        if (this.hideSelected)
          return;
        this.$nextTick(() => this.setMenuIndex(listIndex));
      }
    },
    setMenuIndex(index) {
      this.$refs.menu && (this.$refs.menu.listIndex = index);
    },
    setSelectedItems() {
      const selectedItems = [];
      const values = !this.multiple || !Array.isArray(this.internalValue) ? [this.internalValue] : this.internalValue;
      for (const value of values) {
        const index = this.allItems.findIndex((v) => this.valueComparator(this.getValue(v), this.getValue(value)));
        if (index > -1) {
          selectedItems.push(this.allItems[index]);
        }
      }
      this.selectedItems = selectedItems;
    },
    setValue(value) {
      const oldValue = this.internalValue;
      this.internalValue = value;
      value !== oldValue && this.$emit("change", value);
    },
    isAppendInner(target) {
      const appendInner = this.$refs["append-inner"];
      return appendInner && (appendInner === target || appendInner.contains(target));
    }
  }
});
const defaultMenuProps = __spreadProps(__spreadValues({}, defaultMenuProps$1), {
  offsetY: true,
  offsetOverflow: true,
  transition: false
});
var VAutocomplete = VSelect.extend({
  name: "v-autocomplete",
  props: {
    allowOverflow: {
      type: Boolean,
      default: true
    },
    autoSelectFirst: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Function,
      default: (item, queryText, itemText) => {
        return itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1;
      }
    },
    hideNoData: Boolean,
    menuProps: {
      type: VSelect.options.props.menuProps.type,
      default: () => defaultMenuProps
    },
    noFilter: Boolean,
    searchInput: {
      type: String
    }
  },
  data() {
    return {
      lazySearch: this.searchInput
    };
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VSelect.options.computed.classes.call(this)), {
        "v-autocomplete": true,
        "v-autocomplete--is-selecting-index": this.selectedIndex > -1
      });
    },
    computedItems() {
      return this.filteredItems;
    },
    selectedValues() {
      return this.selectedItems.map((item) => this.getValue(item));
    },
    hasDisplayedItems() {
      return this.hideSelected ? this.filteredItems.some((item) => !this.hasItem(item)) : this.filteredItems.length > 0;
    },
    currentRange() {
      if (this.selectedItem == null)
        return 0;
      return String(this.getText(this.selectedItem)).length;
    },
    filteredItems() {
      if (!this.isSearching || this.noFilter || this.internalSearch == null)
        return this.allItems;
      return this.allItems.filter((item) => {
        const value = getPropertyFromItem(item, this.itemText);
        const text = value != null ? String(value) : "";
        return this.filter(item, String(this.internalSearch), text);
      });
    },
    internalSearch: {
      get() {
        return this.lazySearch;
      },
      set(val) {
        if (this.lazySearch !== val) {
          this.lazySearch = val;
          this.$emit("update:search-input", val);
        }
      }
    },
    isAnyValueAllowed() {
      return false;
    },
    isDirty() {
      return this.searchIsDirty || this.selectedItems.length > 0;
    },
    isSearching() {
      return this.multiple && this.searchIsDirty || this.searchIsDirty && this.internalSearch !== this.getText(this.selectedItem);
    },
    menuCanShow() {
      if (!this.isFocused)
        return false;
      return this.hasDisplayedItems || !this.hideNoData;
    },
    $_menuProps() {
      const props2 = VSelect.options.computed.$_menuProps.call(this);
      props2.contentClass = `v-autocomplete__content ${props2.contentClass || ""}`.trim();
      return __spreadValues(__spreadValues({}, defaultMenuProps), props2);
    },
    searchIsDirty() {
      return this.internalSearch != null && this.internalSearch !== "";
    },
    selectedItem() {
      if (this.multiple)
        return null;
      return this.selectedItems.find((i) => {
        return this.valueComparator(this.getValue(i), this.getValue(this.internalValue));
      });
    },
    listData() {
      const data = VSelect.options.computed.listData.call(this);
      data.props = __spreadProps(__spreadValues({}, data.props), {
        items: this.virtualizedItems,
        noFilter: this.noFilter || !this.isSearching || !this.filteredItems.length,
        searchInput: this.internalSearch
      });
      return data;
    }
  },
  watch: {
    filteredItems: "onFilteredItemsChanged",
    internalValue: "setSearch",
    isFocused(val) {
      if (val) {
        document.addEventListener("copy", this.onCopy);
        this.$refs.input && this.$refs.input.select();
      } else {
        document.removeEventListener("copy", this.onCopy);
        this.$refs.input && this.$refs.input.blur();
        this.updateSelf();
      }
    },
    isMenuActive(val) {
      if (val || !this.hasSlot)
        return;
      this.lazySearch = null;
    },
    items(val, oldVal) {
      if (!(oldVal && oldVal.length) && this.hideNoData && this.isFocused && !this.isMenuActive && val.length)
        this.activateMenu();
    },
    searchInput(val) {
      this.lazySearch = val;
    },
    internalSearch: "onInternalSearchChanged",
    itemText: "updateSelf"
  },
  created() {
    this.setSearch();
  },
  destroyed() {
    document.removeEventListener("copy", this.onCopy);
  },
  methods: {
    onFilteredItemsChanged(val, oldVal) {
      if (val === oldVal)
        return;
      this.setMenuIndex(-1);
      this.$nextTick(() => {
        if (!this.internalSearch || val.length !== 1 && !this.autoSelectFirst)
          return;
        this.$refs.menu.getTiles();
        this.setMenuIndex(0);
      });
    },
    onInternalSearchChanged() {
      this.updateMenuDimensions();
    },
    updateMenuDimensions() {
      this.isMenuActive && this.$refs.menu && this.$refs.menu.updateDimensions();
    },
    changeSelectedIndex(keyCode) {
      if (this.searchIsDirty)
        return;
      if (this.multiple && keyCode === keyCodes.left) {
        if (this.selectedIndex === -1) {
          this.selectedIndex = this.selectedItems.length - 1;
        } else {
          this.selectedIndex--;
        }
      } else if (this.multiple && keyCode === keyCodes.right) {
        if (this.selectedIndex >= this.selectedItems.length - 1) {
          this.selectedIndex = -1;
        } else {
          this.selectedIndex++;
        }
      } else if (keyCode === keyCodes.backspace || keyCode === keyCodes.delete) {
        this.deleteCurrentItem();
      }
    },
    deleteCurrentItem() {
      const curIndex = this.selectedIndex;
      const curItem = this.selectedItems[curIndex];
      if (!this.isInteractive || this.getDisabled(curItem))
        return;
      const lastIndex = this.selectedItems.length - 1;
      if (this.selectedIndex === -1 && lastIndex !== 0) {
        this.selectedIndex = lastIndex;
        return;
      }
      const length = this.selectedItems.length;
      const nextIndex = curIndex !== length - 1 ? curIndex : curIndex - 1;
      const nextItem = this.selectedItems[nextIndex];
      if (!nextItem) {
        this.setValue(this.multiple ? [] : null);
      } else {
        this.selectItem(curItem);
      }
      this.selectedIndex = nextIndex;
    },
    clearableCallback() {
      this.internalSearch = null;
      VSelect.options.methods.clearableCallback.call(this);
    },
    genInput() {
      const input = VTextField.options.methods.genInput.call(this);
      input.data = mergeData(input.data, {
        attrs: {
          "aria-activedescendant": getObjectValueByPath(this.$refs.menu, "activeTile.id"),
          autocomplete: getObjectValueByPath(input.data, "attrs.autocomplete", "off")
        },
        domProps: {
          value: this.internalSearch
        }
      });
      return input;
    },
    genInputSlot() {
      const slot = VSelect.options.methods.genInputSlot.call(this);
      slot.data.attrs.role = "combobox";
      return slot;
    },
    genSelections() {
      return this.hasSlot || this.multiple ? VSelect.options.methods.genSelections.call(this) : [];
    },
    onClick(e) {
      if (!this.isInteractive)
        return;
      this.selectedIndex > -1 ? this.selectedIndex = -1 : this.onFocus();
      if (!this.isAppendInner(e.target))
        this.activateMenu();
    },
    onInput(e) {
      if (this.selectedIndex > -1 || !e.target)
        return;
      const target = e.target;
      const value = target.value;
      if (target.value)
        this.activateMenu();
      this.internalSearch = value;
      this.badInput = target.validity && target.validity.badInput;
    },
    onKeyDown(e) {
      const keyCode = e.keyCode;
      if (e.ctrlKey || ![keyCodes.home, keyCodes.end].includes(keyCode)) {
        VSelect.options.methods.onKeyDown.call(this, e);
      }
      this.changeSelectedIndex(keyCode);
    },
    onSpaceDown(e) {
    },
    onTabDown(e) {
      VSelect.options.methods.onTabDown.call(this, e);
      this.updateSelf();
    },
    onUpDown(e) {
      e.preventDefault();
      this.activateMenu();
    },
    selectItem(item) {
      VSelect.options.methods.selectItem.call(this, item);
      this.setSearch();
    },
    setSelectedItems() {
      VSelect.options.methods.setSelectedItems.call(this);
      if (!this.isFocused)
        this.setSearch();
    },
    setSearch() {
      this.$nextTick(() => {
        if (!this.multiple || !this.internalSearch || !this.isMenuActive) {
          this.internalSearch = !this.selectedItems.length || this.multiple || this.hasSlot ? null : this.getText(this.selectedItem);
        }
      });
    },
    updateSelf() {
      if (!this.searchIsDirty && !this.internalValue)
        return;
      if (!this.valueComparator(this.internalSearch, this.getValue(this.internalValue))) {
        this.setSearch();
      }
    },
    hasItem(item) {
      return this.selectedValues.indexOf(this.getValue(item)) > -1;
    },
    onCopy(event) {
      var _event$clipboardData, _event$clipboardData2;
      if (this.selectedIndex === -1)
        return;
      const currentItem = this.selectedItems[this.selectedIndex];
      const currentItemText = this.getText(currentItem);
      (_event$clipboardData = event.clipboardData) == null ? void 0 : _event$clipboardData.setData("text/plain", currentItemText);
      (_event$clipboardData2 = event.clipboardData) == null ? void 0 : _event$clipboardData2.setData("text/vnd.vuetify.autocomplete.item+plain", currentItemText);
      event.preventDefault();
    }
  }
});
var VBadge = mixins(Colorable, factory$3(["left", "bottom"]), Themeable$1, Toggleable$1, Transitionable).extend({
  name: "v-badge",
  props: {
    avatar: Boolean,
    bordered: Boolean,
    color: {
      type: String,
      default: "primary"
    },
    content: {
      required: false
    },
    dot: Boolean,
    label: {
      type: String,
      default: "$vuetify.badge"
    },
    icon: String,
    inline: Boolean,
    offsetX: [Number, String],
    offsetY: [Number, String],
    overlap: Boolean,
    tile: Boolean,
    transition: {
      type: String,
      default: "scale-rotate-transition"
    },
    value: {
      default: true
    }
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-badge--avatar": this.avatar,
        "v-badge--bordered": this.bordered,
        "v-badge--bottom": this.bottom,
        "v-badge--dot": this.dot,
        "v-badge--icon": this.icon != null,
        "v-badge--inline": this.inline,
        "v-badge--left": this.left,
        "v-badge--overlap": this.overlap,
        "v-badge--tile": this.tile
      }, this.themeClasses);
    },
    computedBottom() {
      return this.bottom ? "auto" : this.computedYOffset;
    },
    computedLeft() {
      if (this.isRtl) {
        return this.left ? this.computedXOffset : "auto";
      }
      return this.left ? "auto" : this.computedXOffset;
    },
    computedRight() {
      if (this.isRtl) {
        return this.left ? "auto" : this.computedXOffset;
      }
      return !this.left ? "auto" : this.computedXOffset;
    },
    computedTop() {
      return this.bottom ? this.computedYOffset : "auto";
    },
    computedXOffset() {
      return this.calcPosition(this.offsetX);
    },
    computedYOffset() {
      return this.calcPosition(this.offsetY);
    },
    isRtl() {
      return this.$vuetify.rtl;
    },
    offset() {
      if (this.overlap)
        return this.dot ? 8 : 12;
      return this.dot ? 2 : 4;
    },
    styles() {
      if (this.inline)
        return {};
      return {
        bottom: this.computedBottom,
        left: this.computedLeft,
        right: this.computedRight,
        top: this.computedTop
      };
    }
  },
  methods: {
    calcPosition(offset) {
      return `calc(100% - ${convertToUnit(offset || this.offset)})`;
    },
    genBadge() {
      const lang = this.$vuetify.lang;
      const label = this.$attrs["aria-label"] || lang.t(this.label);
      const data = this.setBackgroundColor(this.color, {
        staticClass: "v-badge__badge",
        style: this.styles,
        attrs: {
          "aria-atomic": this.$attrs["aria-atomic"] || "true",
          "aria-label": label,
          "aria-live": this.$attrs["aria-live"] || "polite",
          title: this.$attrs.title,
          role: this.$attrs.role || "status"
        },
        directives: [{
          name: "show",
          value: this.isActive
        }]
      });
      const badge = this.$createElement("span", data, [this.genBadgeContent()]);
      if (!this.transition)
        return badge;
      return this.$createElement("transition", {
        props: {
          name: this.transition,
          origin: this.origin,
          mode: this.mode
        }
      }, [badge]);
    },
    genBadgeContent() {
      if (this.dot)
        return void 0;
      const slot = getSlot(this, "badge");
      if (slot)
        return slot;
      if (this.content)
        return String(this.content);
      if (this.icon)
        return this.$createElement(VIcon$1, this.icon);
      return void 0;
    },
    genBadgeWrapper() {
      return this.$createElement("span", {
        staticClass: "v-badge__wrapper"
      }, [this.genBadge()]);
    }
  },
  render(h) {
    const badge = [this.genBadgeWrapper()];
    const children = [getSlot(this)];
    const _a = this.$attrs, attrs = __objRest(_a, [
      "aria-atomic",
      "aria-label",
      "aria-live",
      "role",
      "title"
    ]);
    if (this.inline && this.left)
      children.unshift(badge);
    else
      children.push(badge);
    return h("span", {
      staticClass: "v-badge",
      attrs,
      class: this.classes
    }, children);
  }
});
var Mobile = Vue.extend({
  name: "mobile",
  props: {
    mobileBreakpoint: {
      type: [Number, String],
      default() {
        return this.$vuetify ? this.$vuetify.breakpoint.mobileBreakpoint : void 0;
      },
      validator: (v) => !isNaN(Number(v)) || ["xs", "sm", "md", "lg", "xl"].includes(String(v))
    }
  },
  computed: {
    isMobile() {
      const {
        mobile,
        width,
        name,
        mobileBreakpoint
      } = this.$vuetify.breakpoint;
      if (mobileBreakpoint === this.mobileBreakpoint)
        return mobile;
      const mobileWidth = parseInt(this.mobileBreakpoint, 10);
      const isNumber = !isNaN(mobileWidth);
      return isNumber ? width < mobileWidth : name === this.mobileBreakpoint;
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("mobile-break-point")) {
      deprecate("mobile-break-point", "mobile-breakpoint", this);
    }
  }
});
var VBanner = mixins(VSheet, Mobile, Toggleable$1).extend({
  name: "v-banner",
  inheritAttrs: false,
  props: {
    app: Boolean,
    icon: String,
    iconColor: String,
    singleLine: Boolean,
    sticky: Boolean,
    value: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VSheet.options.computed.classes.call(this)), {
        "v-banner--has-icon": this.hasIcon,
        "v-banner--is-mobile": this.isMobile,
        "v-banner--single-line": this.singleLine,
        "v-banner--sticky": this.isSticky
      });
    },
    hasIcon() {
      return Boolean(this.icon || this.$slots.icon);
    },
    isSticky() {
      return this.sticky || this.app;
    },
    styles() {
      const styles = __spreadValues({}, VSheet.options.computed.styles.call(this));
      if (this.isSticky) {
        const top = !this.app ? 0 : this.$vuetify.application.bar + this.$vuetify.application.top;
        styles.top = convertToUnit(top);
        styles.position = "sticky";
        styles.zIndex = 1;
      }
      return styles;
    }
  },
  methods: {
    toggle() {
      this.isActive = !this.isActive;
    },
    iconClick(e) {
      this.$emit("click:icon", e);
    },
    genIcon() {
      if (!this.hasIcon)
        return void 0;
      let content;
      if (this.icon) {
        content = this.$createElement(VIcon$1, {
          props: {
            color: this.iconColor,
            size: 28
          }
        }, [this.icon]);
      } else {
        content = this.$slots.icon;
      }
      return this.$createElement(VAvatar, {
        staticClass: "v-banner__icon",
        props: {
          color: this.color,
          size: 40
        },
        on: {
          click: this.iconClick
        }
      }, [content]);
    },
    genText() {
      return this.$createElement("div", {
        staticClass: "v-banner__text"
      }, this.$slots.default);
    },
    genActions() {
      const children = getSlot(this, "actions", {
        dismiss: () => this.isActive = false
      });
      if (!children)
        return void 0;
      return this.$createElement("div", {
        staticClass: "v-banner__actions"
      }, children);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-banner__content"
      }, [this.genIcon(), this.genText()]);
    },
    genWrapper() {
      return this.$createElement("div", {
        staticClass: "v-banner__wrapper"
      }, [this.genContent(), this.genActions()]);
    }
  },
  render(h) {
    return h(VExpandTransition, [h("div", this.setBackgroundColor(this.color, {
      staticClass: "v-banner",
      attrs: this.attrs$,
      class: this.classes,
      style: this.styles,
      directives: [{
        name: "show",
        value: this.isActive
      }]
    }), [this.genWrapper()])]);
  }
});
var ButtonGroup = BaseItemGroup.extend({
  name: "button-group",
  provide() {
    return {
      btnToggle: this
    };
  },
  computed: {
    classes() {
      return BaseItemGroup.options.computed.classes.call(this);
    }
  },
  methods: {
    genData: BaseItemGroup.options.methods.genData
  }
});
var VBottomNavigation = mixins(applicationable("bottom", ["height", "inputValue"]), Colorable, Measurable, factory$2("inputValue"), Proxyable$1, Scrollable, Themeable$1).extend({
  name: "v-bottom-navigation",
  props: {
    activeClass: {
      type: String,
      default: "v-btn--active"
    },
    backgroundColor: String,
    grow: Boolean,
    height: {
      type: [Number, String],
      default: 56
    },
    hideOnScroll: Boolean,
    horizontal: Boolean,
    inputValue: {
      type: Boolean,
      default: true
    },
    mandatory: Boolean,
    shift: Boolean,
    tag: {
      type: String,
      default: "div"
    }
  },
  data() {
    return {
      isActive: this.inputValue
    };
  },
  computed: {
    canScroll() {
      return Scrollable.options.computed.canScroll.call(this) && (this.hideOnScroll || !this.inputValue);
    },
    classes() {
      return {
        "v-bottom-navigation--absolute": this.absolute,
        "v-bottom-navigation--grow": this.grow,
        "v-bottom-navigation--fixed": !this.absolute && (this.app || this.fixed),
        "v-bottom-navigation--horizontal": this.horizontal,
        "v-bottom-navigation--shift": this.shift
      };
    },
    styles() {
      return __spreadProps(__spreadValues({}, this.measurableStyles), {
        transform: this.isActive ? "none" : "translateY(100%)"
      });
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("active")) {
      breaking("active.sync", "value or v-model", this);
    }
  },
  methods: {
    thresholdMet() {
      this.isActive = !this.isScrollingUp;
      this.$emit("update:input-value", this.isActive);
    },
    updateApplication() {
      return this.$el ? this.$el.clientHeight : 0;
    },
    updateValue(val) {
      this.$emit("change", val);
    }
  },
  render(h) {
    const data = this.setBackgroundColor(this.backgroundColor, {
      staticClass: "v-bottom-navigation",
      class: this.classes,
      style: this.styles,
      props: {
        activeClass: this.activeClass,
        mandatory: Boolean(this.mandatory || this.value !== void 0),
        tag: this.tag,
        value: this.internalValue
      },
      on: {
        change: this.updateValue
      }
    });
    if (this.canScroll) {
      data.directives = data.directives || [];
      data.directives.push({
        arg: this.scrollTarget,
        name: "scroll",
        value: this.onScroll
      });
    }
    return h(ButtonGroup, this.setTextColor(this.color, data), this.$slots.default);
  }
});
var VOverlay = mixins(Colorable, Themeable$1, Toggleable$1).extend({
  name: "v-overlay",
  props: {
    absolute: Boolean,
    color: {
      type: String,
      default: "#212121"
    },
    dark: {
      type: Boolean,
      default: true
    },
    opacity: {
      type: [Number, String],
      default: 0.46
    },
    value: {
      default: true
    },
    zIndex: {
      type: [Number, String],
      default: 5
    }
  },
  computed: {
    __scrim() {
      const data = this.setBackgroundColor(this.color, {
        staticClass: "v-overlay__scrim",
        style: {
          opacity: this.computedOpacity
        }
      });
      return this.$createElement("div", data);
    },
    classes() {
      return __spreadValues({
        "v-overlay--absolute": this.absolute,
        "v-overlay--active": this.isActive
      }, this.themeClasses);
    },
    computedOpacity() {
      return Number(this.isActive ? this.opacity : 0);
    },
    styles() {
      return {
        zIndex: this.zIndex
      };
    }
  },
  methods: {
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-overlay__content"
      }, this.$slots.default);
    }
  },
  render(h) {
    const children = [this.__scrim];
    if (this.isActive)
      children.push(this.genContent());
    return h("div", {
      staticClass: "v-overlay",
      class: this.classes,
      style: this.styles
    }, children);
  }
});
var Overlayable = Vue.extend().extend({
  name: "overlayable",
  props: {
    hideOverlay: Boolean,
    overlayColor: String,
    overlayOpacity: [Number, String]
  },
  data() {
    return {
      animationFrame: 0,
      overlay: null
    };
  },
  watch: {
    hideOverlay(value) {
      if (!this.isActive)
        return;
      if (value)
        this.removeOverlay();
      else
        this.genOverlay();
    }
  },
  beforeDestroy() {
    this.removeOverlay();
  },
  methods: {
    createOverlay() {
      const overlay = new VOverlay({
        propsData: {
          absolute: this.absolute,
          value: false,
          color: this.overlayColor,
          opacity: this.overlayOpacity
        }
      });
      overlay.$mount();
      const parent = this.absolute ? this.$el.parentNode : document.querySelector("[data-app]");
      parent && parent.insertBefore(overlay.$el, parent.firstChild);
      this.overlay = overlay;
    },
    genOverlay() {
      this.hideScroll();
      if (this.hideOverlay)
        return;
      if (!this.overlay)
        this.createOverlay();
      this.animationFrame = requestAnimationFrame(() => {
        if (!this.overlay)
          return;
        if (this.activeZIndex !== void 0) {
          this.overlay.zIndex = String(this.activeZIndex - 1);
        } else if (this.$el) {
          this.overlay.zIndex = getZIndex(this.$el);
        }
        this.overlay.value = true;
      });
      return true;
    },
    removeOverlay(showScroll = true) {
      if (this.overlay) {
        addOnceEventListener(this.overlay.$el, "transitionend", () => {
          if (!this.overlay || !this.overlay.$el || !this.overlay.$el.parentNode || this.overlay.value)
            return;
          this.overlay.$el.parentNode.removeChild(this.overlay.$el);
          this.overlay.$destroy();
          this.overlay = null;
        });
        cancelAnimationFrame(this.animationFrame);
        this.overlay.value = false;
      }
      showScroll && this.showScroll();
    },
    scrollListener(e) {
      if (e.type === "keydown") {
        if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.target.isContentEditable)
          return;
        const up = [keyCodes.up, keyCodes.pageup];
        const down = [keyCodes.down, keyCodes.pagedown];
        if (up.includes(e.keyCode)) {
          e.deltaY = -1;
        } else if (down.includes(e.keyCode)) {
          e.deltaY = 1;
        } else {
          return;
        }
      }
      if (e.target === this.overlay || e.type !== "keydown" && e.target === document.body || this.checkPath(e))
        e.preventDefault();
    },
    hasScrollbar(el) {
      if (!el || el.nodeType !== Node.ELEMENT_NODE)
        return false;
      const style = window.getComputedStyle(el);
      return ["auto", "scroll"].includes(style.overflowY) && el.scrollHeight > el.clientHeight;
    },
    shouldScroll(el, delta2) {
      if (el.scrollTop === 0 && delta2 < 0)
        return true;
      return el.scrollTop + el.clientHeight === el.scrollHeight && delta2 > 0;
    },
    isInside(el, parent) {
      if (el === parent) {
        return true;
      } else if (el === null || el === document.body) {
        return false;
      } else {
        return this.isInside(el.parentNode, parent);
      }
    },
    checkPath(e) {
      const path = e.path || this.composedPath(e);
      const delta2 = e.deltaY;
      if (e.type === "keydown" && path[0] === document.body) {
        const dialog = this.$refs.dialog;
        const selected = window.getSelection().anchorNode;
        if (dialog && this.hasScrollbar(dialog) && this.isInside(selected, dialog)) {
          return this.shouldScroll(dialog, delta2);
        }
        return true;
      }
      for (let index = 0; index < path.length; index++) {
        const el = path[index];
        if (el === document)
          return true;
        if (el === document.documentElement)
          return true;
        if (el === this.$refs.content)
          return true;
        if (this.hasScrollbar(el))
          return this.shouldScroll(el, delta2);
      }
      return true;
    },
    composedPath(e) {
      if (e.composedPath)
        return e.composedPath();
      const path = [];
      let el = e.target;
      while (el) {
        path.push(el);
        if (el.tagName === "HTML") {
          path.push(document);
          path.push(window);
          return path;
        }
        el = el.parentElement;
      }
      return path;
    },
    hideScroll() {
      if (this.$vuetify.breakpoint.smAndDown) {
        document.documentElement.classList.add("overflow-y-hidden");
      } else {
        addPassiveEventListener(window, "wheel", this.scrollListener, {
          passive: false
        });
        window.addEventListener("keydown", this.scrollListener);
      }
    },
    showScroll() {
      document.documentElement.classList.remove("overflow-y-hidden");
      window.removeEventListener("wheel", this.scrollListener);
      window.removeEventListener("keydown", this.scrollListener);
    }
  }
});
const baseMixins$h = mixins(Activatable, Dependent, Detachable, Overlayable, Returnable, Stackable, Toggleable$1);
var VDialog = baseMixins$h.extend({
  name: "v-dialog",
  directives: {
    ClickOutside: ClickOutside$1
  },
  props: {
    dark: Boolean,
    disabled: Boolean,
    fullscreen: Boolean,
    light: Boolean,
    maxWidth: {
      type: [String, Number],
      default: "none"
    },
    noClickAnimation: Boolean,
    origin: {
      type: String,
      default: "center center"
    },
    persistent: Boolean,
    retainFocus: {
      type: Boolean,
      default: true
    },
    scrollable: Boolean,
    transition: {
      type: [String, Boolean],
      default: "dialog-transition"
    },
    width: {
      type: [String, Number],
      default: "auto"
    }
  },
  data() {
    return {
      activatedBy: null,
      animate: false,
      animateTimeout: -1,
      isActive: !!this.value,
      stackMinZIndex: 200,
      previousActiveElement: null
    };
  },
  computed: {
    classes() {
      return {
        [`v-dialog ${this.contentClass}`.trim()]: true,
        "v-dialog--active": this.isActive,
        "v-dialog--persistent": this.persistent,
        "v-dialog--fullscreen": this.fullscreen,
        "v-dialog--scrollable": this.scrollable,
        "v-dialog--animated": this.animate
      };
    },
    contentClasses() {
      return {
        "v-dialog__content": true,
        "v-dialog__content--active": this.isActive
      };
    },
    hasActivator() {
      return Boolean(!!this.$slots.activator || !!this.$scopedSlots.activator);
    }
  },
  watch: {
    isActive(val) {
      if (val) {
        this.show();
        this.hideScroll();
      } else {
        var _this$previousActiveE;
        this.removeOverlay();
        this.unbind();
        (_this$previousActiveE = this.previousActiveElement) == null ? void 0 : _this$previousActiveE.focus();
      }
    },
    fullscreen(val) {
      if (!this.isActive)
        return;
      if (val) {
        this.hideScroll();
        this.removeOverlay(false);
      } else {
        this.showScroll();
        this.genOverlay();
      }
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("full-width")) {
      removed("full-width", this);
    }
  },
  beforeMount() {
    this.$nextTick(() => {
      this.isBooted = this.isActive;
      this.isActive && this.show();
    });
  },
  beforeDestroy() {
    if (typeof window !== "undefined")
      this.unbind();
  },
  methods: {
    animateClick() {
      this.animate = false;
      this.$nextTick(() => {
        this.animate = true;
        window.clearTimeout(this.animateTimeout);
        this.animateTimeout = window.setTimeout(() => this.animate = false, 150);
      });
    },
    closeConditional(e) {
      const target = e.target;
      return !(this._isDestroyed || !this.isActive || this.$refs.content.contains(target) || this.overlay && target && !this.overlay.$el.contains(target)) && this.activeZIndex >= this.getMaxZIndex();
    },
    hideScroll() {
      if (this.fullscreen) {
        document.documentElement.classList.add("overflow-y-hidden");
      } else {
        Overlayable.options.methods.hideScroll.call(this);
      }
    },
    show() {
      !this.fullscreen && !this.hideOverlay && this.genOverlay();
      this.$nextTick(() => {
        this.$nextTick(() => {
          this.previousActiveElement = document.activeElement;
          this.$refs.content.focus();
          this.bind();
        });
      });
    },
    bind() {
      window.addEventListener("focusin", this.onFocusin);
    },
    unbind() {
      window.removeEventListener("focusin", this.onFocusin);
    },
    onClickOutside(e) {
      this.$emit("click:outside", e);
      if (this.persistent) {
        this.noClickAnimation || this.animateClick();
      } else {
        this.isActive = false;
      }
    },
    onKeydown(e) {
      if (e.keyCode === keyCodes.esc && !this.getOpenDependents().length) {
        if (!this.persistent) {
          this.isActive = false;
          const activator = this.getActivator();
          this.$nextTick(() => activator && activator.focus());
        } else if (!this.noClickAnimation) {
          this.animateClick();
        }
      }
      this.$emit("keydown", e);
    },
    onFocusin(e) {
      if (!e || !this.retainFocus)
        return;
      const target = e.target;
      if (!!target && ![document, this.$refs.content].includes(target) && !this.$refs.content.contains(target) && this.activeZIndex >= this.getMaxZIndex() && !this.getOpenDependentElements().some((el) => el.contains(target))) {
        const focusable = this.$refs.content.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        const el = [...focusable].find((el2) => !el2.hasAttribute("disabled"));
        el && el.focus();
      }
    },
    genContent() {
      return this.showLazyContent(() => [this.$createElement(VThemeProvider, {
        props: {
          root: true,
          light: this.light,
          dark: this.dark
        }
      }, [this.$createElement("div", {
        class: this.contentClasses,
        attrs: __spreadValues({
          role: "document",
          tabindex: this.isActive ? 0 : void 0
        }, this.getScopeIdAttrs()),
        on: {
          keydown: this.onKeydown
        },
        style: {
          zIndex: this.activeZIndex
        },
        ref: "content"
      }, [this.genTransition()])])]);
    },
    genTransition() {
      const content = this.genInnerContent();
      if (!this.transition)
        return content;
      return this.$createElement("transition", {
        props: {
          name: this.transition,
          origin: this.origin,
          appear: true
        }
      }, [content]);
    },
    genInnerContent() {
      const data = {
        class: this.classes,
        ref: "dialog",
        directives: [{
          name: "click-outside",
          value: {
            handler: this.onClickOutside,
            closeConditional: this.closeConditional,
            include: this.getOpenDependentElements
          }
        }, {
          name: "show",
          value: this.isActive
        }],
        style: {
          transformOrigin: this.origin
        }
      };
      if (!this.fullscreen) {
        data.style = __spreadProps(__spreadValues({}, data.style), {
          maxWidth: this.maxWidth === "none" ? void 0 : convertToUnit(this.maxWidth),
          width: this.width === "auto" ? void 0 : convertToUnit(this.width)
        });
      }
      return this.$createElement("div", data, this.getContentSlot());
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-dialog__container",
      class: {
        "v-dialog__container--attached": this.attach === "" || this.attach === true || this.attach === "attach"
      },
      attrs: {
        role: "dialog"
      }
    }, [this.genActivator(), this.genContent()]);
  }
});
var VBottomSheet = VDialog.extend({
  name: "v-bottom-sheet",
  props: {
    inset: Boolean,
    maxWidth: {
      type: [String, Number],
      default: "auto"
    },
    transition: {
      type: String,
      default: "bottom-sheet-transition"
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VDialog.options.computed.classes.call(this)), {
        "v-bottom-sheet": true,
        "v-bottom-sheet--inset": this.inset
      });
    }
  }
});
var VBreadcrumbsItem = mixins(Routable).extend({
  name: "v-breadcrumbs-item",
  props: {
    activeClass: {
      type: String,
      default: "v-breadcrumbs__item--disabled"
    },
    ripple: {
      type: [Boolean, Object],
      default: false
    }
  },
  computed: {
    classes() {
      return {
        "v-breadcrumbs__item": true,
        [this.activeClass]: this.disabled
      };
    }
  },
  render(h) {
    const {
      tag,
      data
    } = this.generateRouteLink();
    return h("li", [h(tag, __spreadProps(__spreadValues({}, data), {
      attrs: __spreadProps(__spreadValues({}, data.attrs), {
        "aria-current": this.isActive && this.isLink ? "page" : void 0
      })
    }), this.$slots.default)]);
  }
});
var VBreadcrumbsDivider = createSimpleFunctional("v-breadcrumbs__divider", "li");
var VBreadcrumbs = mixins(Themeable$1).extend({
  name: "v-breadcrumbs",
  props: {
    divider: {
      type: String,
      default: "/"
    },
    items: {
      type: Array,
      default: () => []
    },
    large: Boolean
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-breadcrumbs--large": this.large
      }, this.themeClasses);
    }
  },
  methods: {
    genDivider() {
      return this.$createElement(VBreadcrumbsDivider, this.$slots.divider ? this.$slots.divider : this.divider);
    },
    genItems() {
      const items = [];
      const hasSlot = !!this.$scopedSlots.item;
      const keys2 = [];
      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        keys2.push(item.text);
        if (hasSlot)
          items.push(this.$scopedSlots.item({
            item
          }));
        else
          items.push(this.$createElement(VBreadcrumbsItem, {
            key: keys2.join("."),
            props: item
          }, [item.text]));
        if (i < this.items.length - 1)
          items.push(this.genDivider());
      }
      return items;
    }
  },
  render(h) {
    const children = this.$slots.default || this.genItems();
    return h("ul", {
      staticClass: "v-breadcrumbs",
      class: this.classes
    }, children);
  }
});
var VBtnToggle = mixins(ButtonGroup, Colorable).extend({
  name: "v-btn-toggle",
  props: {
    backgroundColor: String,
    borderless: Boolean,
    dense: Boolean,
    group: Boolean,
    rounded: Boolean,
    shaped: Boolean,
    tile: Boolean
  },
  computed: {
    classes() {
      return __spreadValues(__spreadProps(__spreadValues({}, ButtonGroup.options.computed.classes.call(this)), {
        "v-btn-toggle": true,
        "v-btn-toggle--borderless": this.borderless,
        "v-btn-toggle--dense": this.dense,
        "v-btn-toggle--group": this.group,
        "v-btn-toggle--rounded": this.rounded,
        "v-btn-toggle--shaped": this.shaped,
        "v-btn-toggle--tile": this.tile
      }), this.themeClasses);
    }
  },
  methods: {
    genData() {
      const data = this.setTextColor(this.color, __spreadValues({}, ButtonGroup.options.methods.genData.call(this)));
      if (this.group)
        return data;
      return this.setBackgroundColor(this.backgroundColor, data);
    }
  }
});
var Localable = Vue.extend({
  name: "localable",
  props: {
    locale: String
  },
  computed: {
    currentLocale() {
      return this.locale || this.$vuetify.lang.current;
    }
  }
});
var Mouse = Vue.extend({
  name: "mouse",
  methods: {
    getDefaultMouseEventHandlers(suffix, getEvent) {
      return this.getMouseEventHandlers({
        ["click" + suffix]: {
          event: "click"
        },
        ["contextmenu" + suffix]: {
          event: "contextmenu",
          prevent: true,
          result: false
        },
        ["mousedown" + suffix]: {
          event: "mousedown"
        },
        ["mousemove" + suffix]: {
          event: "mousemove"
        },
        ["mouseup" + suffix]: {
          event: "mouseup"
        },
        ["mouseenter" + suffix]: {
          event: "mouseenter"
        },
        ["mouseleave" + suffix]: {
          event: "mouseleave"
        },
        ["touchstart" + suffix]: {
          event: "touchstart"
        },
        ["touchmove" + suffix]: {
          event: "touchmove"
        },
        ["touchend" + suffix]: {
          event: "touchend"
        }
      }, getEvent);
    },
    getMouseEventHandlers(events, getEvent) {
      const on = {};
      for (const event in events) {
        const eventOptions = events[event];
        if (!this.$listeners[event])
          continue;
        const prefix = eventOptions.passive ? "&" : (eventOptions.once ? "~" : "") + (eventOptions.capture ? "!" : "");
        const key = prefix + eventOptions.event;
        const handler = (e) => {
          const mouseEvent = e;
          if (eventOptions.button === void 0 || mouseEvent.buttons > 0 && mouseEvent.button === eventOptions.button) {
            if (eventOptions.prevent) {
              e.preventDefault();
            }
            if (eventOptions.stop) {
              e.stopPropagation();
            }
            this.$emit(event, getEvent(e));
          }
          return eventOptions.result;
        };
        if (key in on) {
          if (Array.isArray(on[key])) {
            on[key].push(handler);
          } else {
            on[key] = [on[key], handler];
          }
        } else {
          on[key] = handler;
        }
      }
      return on;
    }
  }
});
function createUTCDate(year, month = 0, day = 1) {
  let date;
  if (year < 100 && year >= 0) {
    date = new Date(Date.UTC(year, month, day));
    if (isFinite(date.getUTCFullYear())) {
      date.setUTCFullYear(year);
    }
  } else {
    date = new Date(Date.UTC(year, month, day));
  }
  return date;
}
function firstWeekOffset(year, firstDayOfWeek, firstDayOfYear) {
  const firstWeekDayInFirstWeek = 7 + firstDayOfWeek - firstDayOfYear;
  const firstWeekDayOfYear = (7 + createUTCDate(year, 0, firstWeekDayInFirstWeek).getUTCDay() - firstDayOfWeek) % 7;
  return -firstWeekDayOfYear + firstWeekDayInFirstWeek - 1;
}
function dayOfYear(year, month, day, firstDayOfWeek) {
  let dayOfYear2 = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334][month];
  if (month > 1 && isLeapYear(year)) {
    dayOfYear2++;
  }
  return dayOfYear2 + day;
}
function weeksInYear(year, firstDayOfWeek, firstDayOfYear) {
  const weekOffset = firstWeekOffset(year, firstDayOfWeek, firstDayOfYear);
  const weekOffsetNext = firstWeekOffset(year + 1, firstDayOfWeek, firstDayOfYear);
  const daysInYear = isLeapYear(year) ? 366 : 365;
  return (daysInYear - weekOffset + weekOffsetNext) / 7;
}
function weekNumber(year, month, day, firstDayOfWeek, localeFirstDayOfYear) {
  const weekOffset = firstWeekOffset(year, firstDayOfWeek, localeFirstDayOfYear);
  const week = Math.ceil((dayOfYear(year, month, day) - weekOffset) / 7);
  if (week < 1) {
    return week + weeksInYear(year - 1, firstDayOfWeek, localeFirstDayOfYear);
  } else if (week > weeksInYear(year, firstDayOfWeek, localeFirstDayOfYear)) {
    return week - weeksInYear(year, firstDayOfWeek, localeFirstDayOfYear);
  } else {
    return week;
  }
}
function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
}
const PARSE_REGEX = /^(\d{4})-(\d{1,2})(-(\d{1,2}))?([^\d]+(\d{1,2}))?(:(\d{1,2}))?(:(\d{1,2}))?$/;
const PARSE_TIME = /(\d\d?)(:(\d\d?)|)(:(\d\d?)|)/;
const DAYS_IN_MONTH = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DAYS_IN_MONTH_LEAP = [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DAYS_IN_MONTH_MIN = 28;
const DAYS_IN_MONTH_MAX = 31;
const MONTH_MAX = 12;
const MONTH_MIN = 1;
const DAY_MIN = 1;
const DAYS_IN_WEEK = 7;
const MINUTES_IN_HOUR = 60;
const MINUTE_MAX = 59;
const MINUTES_IN_DAY$1 = 24 * 60;
const HOUR_MAX = 23;
const OFFSET_YEAR = 1e4;
const OFFSET_MONTH = 100;
const OFFSET_HOUR = 100;
const OFFSET_TIME = 1e4;
function getStartOfWeek(timestamp, weekdays, today) {
  const start = copyTimestamp(timestamp);
  findWeekday(start, weekdays[0], prevDay);
  updateFormatted(start);
  if (today) {
    updateRelative(start, today, start.hasTime);
  }
  return start;
}
function getEndOfWeek(timestamp, weekdays, today) {
  const end = copyTimestamp(timestamp);
  findWeekday(end, weekdays[weekdays.length - 1]);
  updateFormatted(end);
  if (today) {
    updateRelative(end, today, end.hasTime);
  }
  return end;
}
function getStartOfMonth(timestamp) {
  const start = copyTimestamp(timestamp);
  start.day = DAY_MIN;
  updateWeekday(start);
  updateFormatted(start);
  return start;
}
function getEndOfMonth(timestamp) {
  const end = copyTimestamp(timestamp);
  end.day = daysInMonth(end.year, end.month);
  updateWeekday(end);
  updateFormatted(end);
  return end;
}
function validateTime$1(input) {
  return typeof input === "number" && isFinite(input) || !!PARSE_TIME.exec(input) || typeof input === "object" && isFinite(input.hour) && isFinite(input.minute);
}
function parseTime$1(input) {
  if (typeof input === "number") {
    return input;
  } else if (typeof input === "string") {
    const parts = PARSE_TIME.exec(input);
    if (!parts) {
      return false;
    }
    return parseInt(parts[1]) * 60 + parseInt(parts[3] || 0);
  } else if (typeof input === "object") {
    if (typeof input.hour !== "number" || typeof input.minute !== "number") {
      return false;
    }
    return input.hour * 60 + input.minute;
  } else {
    return false;
  }
}
function validateTimestamp(input) {
  return typeof input === "number" && isFinite(input) || typeof input === "string" && !!PARSE_REGEX.exec(input) || input instanceof Date;
}
function parseTimestamp(input, required = false, now) {
  if (typeof input === "number" && isFinite(input)) {
    input = new Date(input);
  }
  if (input instanceof Date) {
    const date = parseDate$1(input);
    if (now) {
      updateRelative(date, now, date.hasTime);
    }
    return date;
  }
  if (typeof input !== "string") {
    if (required) {
      throw new Error(`${input} is not a valid timestamp. It must be a Date, number of seconds since Epoch, or a string in the format of YYYY-MM-DD or YYYY-MM-DD hh:mm. Zero-padding is optional and seconds are ignored.`);
    }
    return null;
  }
  const parts = PARSE_REGEX.exec(input);
  if (!parts) {
    if (required) {
      throw new Error(`${input} is not a valid timestamp. It must be a Date, number of seconds since Epoch, or a string in the format of YYYY-MM-DD or YYYY-MM-DD hh:mm. Zero-padding is optional and seconds are ignored.`);
    }
    return null;
  }
  const timestamp = {
    date: input,
    time: "",
    year: parseInt(parts[1]),
    month: parseInt(parts[2]),
    day: parseInt(parts[4]) || 1,
    hour: parseInt(parts[6]) || 0,
    minute: parseInt(parts[8]) || 0,
    weekday: 0,
    hasDay: !!parts[4],
    hasTime: !!(parts[6] && parts[8]),
    past: false,
    present: false,
    future: false
  };
  updateWeekday(timestamp);
  updateFormatted(timestamp);
  if (now) {
    updateRelative(timestamp, now, timestamp.hasTime);
  }
  return timestamp;
}
function parseDate$1(date) {
  return updateFormatted({
    date: "",
    time: "",
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    weekday: date.getDay(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    hasDay: true,
    hasTime: true,
    past: false,
    present: true,
    future: false
  });
}
function getDayIdentifier(timestamp) {
  return timestamp.year * OFFSET_YEAR + timestamp.month * OFFSET_MONTH + timestamp.day;
}
function getTimeIdentifier(timestamp) {
  return timestamp.hour * OFFSET_HOUR + timestamp.minute;
}
function getTimestampIdentifier(timestamp) {
  return getDayIdentifier(timestamp) * OFFSET_TIME + getTimeIdentifier(timestamp);
}
function updateRelative(timestamp, now, time = false) {
  let a = getDayIdentifier(now);
  let b = getDayIdentifier(timestamp);
  let present = a === b;
  if (timestamp.hasTime && time && present) {
    a = getTimeIdentifier(now);
    b = getTimeIdentifier(timestamp);
    present = a === b;
  }
  timestamp.past = b < a;
  timestamp.present = present;
  timestamp.future = b > a;
  return timestamp;
}
function isTimedless(input) {
  return input instanceof Date || typeof input === "number" && isFinite(input);
}
function updateHasTime(timestamp, hasTime, now) {
  if (timestamp.hasTime !== hasTime) {
    timestamp.hasTime = hasTime;
    if (!hasTime) {
      timestamp.hour = HOUR_MAX;
      timestamp.minute = MINUTE_MAX;
      timestamp.time = getTime(timestamp);
    }
    if (now) {
      updateRelative(timestamp, now, timestamp.hasTime);
    }
  }
  return timestamp;
}
function updateMinutes(timestamp, minutes, now) {
  timestamp.hasTime = true;
  timestamp.hour = Math.floor(minutes / MINUTES_IN_HOUR);
  timestamp.minute = minutes % MINUTES_IN_HOUR;
  timestamp.time = getTime(timestamp);
  if (now) {
    updateRelative(timestamp, now, true);
  }
  return timestamp;
}
function updateWeekday(timestamp) {
  timestamp.weekday = getWeekday(timestamp);
  return timestamp;
}
function updateFormatted(timestamp) {
  timestamp.time = getTime(timestamp);
  timestamp.date = getDate(timestamp);
  return timestamp;
}
function getWeekday(timestamp) {
  if (timestamp.hasDay) {
    const _ = Math.floor;
    const k = timestamp.day;
    const m = (timestamp.month + 9) % MONTH_MAX + 1;
    const C = _(timestamp.year / 100);
    const Y = timestamp.year % 100 - (timestamp.month <= 2 ? 1 : 0);
    return ((k + _(2.6 * m - 0.2) - 2 * C + Y + _(Y / 4) + _(C / 4)) % 7 + 7) % 7;
  }
  return timestamp.weekday;
}
function daysInMonth(year, month) {
  return isLeapYear(year) ? DAYS_IN_MONTH_LEAP[month] : DAYS_IN_MONTH[month];
}
function copyTimestamp(timestamp) {
  const {
    date,
    time,
    year,
    month,
    day,
    weekday,
    hour,
    minute,
    hasDay,
    hasTime,
    past,
    present,
    future
  } = timestamp;
  return {
    date,
    time,
    year,
    month,
    day,
    weekday,
    hour,
    minute,
    hasDay,
    hasTime,
    past,
    present,
    future
  };
}
function padNumber(x, length) {
  let padded = String(x);
  while (padded.length < length) {
    padded = "0" + padded;
  }
  return padded;
}
function getDate(timestamp) {
  let str = `${padNumber(timestamp.year, 4)}-${padNumber(timestamp.month, 2)}`;
  if (timestamp.hasDay)
    str += `-${padNumber(timestamp.day, 2)}`;
  return str;
}
function getTime(timestamp) {
  if (!timestamp.hasTime) {
    return "";
  }
  return `${padNumber(timestamp.hour, 2)}:${padNumber(timestamp.minute, 2)}`;
}
function nextDay(timestamp) {
  timestamp.day++;
  timestamp.weekday = (timestamp.weekday + 1) % DAYS_IN_WEEK;
  if (timestamp.day > DAYS_IN_MONTH_MIN && timestamp.day > daysInMonth(timestamp.year, timestamp.month)) {
    timestamp.day = DAY_MIN;
    timestamp.month++;
    if (timestamp.month > MONTH_MAX) {
      timestamp.month = MONTH_MIN;
      timestamp.year++;
    }
  }
  return timestamp;
}
function prevDay(timestamp) {
  timestamp.day--;
  timestamp.weekday = (timestamp.weekday + 6) % DAYS_IN_WEEK;
  if (timestamp.day < DAY_MIN) {
    timestamp.month--;
    if (timestamp.month < MONTH_MIN) {
      timestamp.year--;
      timestamp.month = MONTH_MAX;
    }
    timestamp.day = daysInMonth(timestamp.year, timestamp.month);
  }
  return timestamp;
}
function relativeDays(timestamp, mover = nextDay, days = 1) {
  while (--days >= 0)
    mover(timestamp);
  return timestamp;
}
function diffMinutes(min, max) {
  const Y = (max.year - min.year) * 525600;
  const M = (max.month - min.month) * 43800;
  const D = (max.day - min.day) * 1440;
  const h = (max.hour - min.hour) * 60;
  const m = max.minute - min.minute;
  return Y + M + D + h + m;
}
function findWeekday(timestamp, weekday, mover = nextDay, maxDays = 6) {
  while (timestamp.weekday !== weekday && --maxDays >= 0)
    mover(timestamp);
  return timestamp;
}
function getWeekdaySkips(weekdays) {
  const skips = [1, 1, 1, 1, 1, 1, 1];
  const filled = [0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < weekdays.length; i++) {
    filled[weekdays[i]] = 1;
  }
  for (let k = 0; k < DAYS_IN_WEEK; k++) {
    let skip = 1;
    for (let j = 1; j < DAYS_IN_WEEK; j++) {
      const next = (k + j) % DAYS_IN_WEEK;
      if (filled[next]) {
        break;
      }
      skip++;
    }
    skips[k] = filled[k] * skip;
  }
  return skips;
}
function timestampToDate(timestamp) {
  const time = `${padNumber(timestamp.hour, 2)}:${padNumber(timestamp.minute, 2)}`;
  const date = timestamp.date;
  return new Date(`${date}T${time}:00+00:00`);
}
function createDayList(start, end, now, weekdaySkips, max = 42, min = 0) {
  const stop = getDayIdentifier(end);
  const days = [];
  let current = copyTimestamp(start);
  let currentIdentifier = 0;
  let stopped = currentIdentifier === stop;
  if (stop < getDayIdentifier(start)) {
    throw new Error("End date is earlier than start date.");
  }
  while ((!stopped || days.length < min) && days.length < max) {
    currentIdentifier = getDayIdentifier(current);
    stopped = stopped || currentIdentifier === stop;
    if (weekdaySkips[current.weekday] === 0) {
      current = nextDay(current);
      continue;
    }
    const day = copyTimestamp(current);
    updateFormatted(day);
    updateRelative(day, now);
    days.push(day);
    current = relativeDays(current, nextDay, weekdaySkips[current.weekday]);
  }
  if (!days.length)
    throw new Error("No dates found using specified start date, end date, and weekdays.");
  return days;
}
function createIntervalList(timestamp, first, minutes, count, now) {
  const intervals = [];
  for (let i = 0; i < count; i++) {
    const mins = first + i * minutes;
    const int2 = copyTimestamp(timestamp);
    intervals.push(updateMinutes(int2, mins, now));
  }
  return intervals;
}
function createNativeLocaleFormatter$1(locale, getOptions) {
  const emptyFormatter = (_t, _s) => "";
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat === "undefined") {
    return emptyFormatter;
  }
  return (timestamp, short) => {
    try {
      const intlFormatter = new Intl.DateTimeFormat(locale || void 0, getOptions(timestamp, short));
      return intlFormatter.format(timestampToDate(timestamp));
    } catch (e) {
      return "";
    }
  };
}
var Times = Vue.extend({
  name: "times",
  props: {
    now: {
      type: String,
      validator: validateTimestamp
    }
  },
  data: () => ({
    times: {
      now: parseTimestamp("0000-00-00 00:00", true),
      today: parseTimestamp("0000-00-00", true)
    }
  }),
  computed: {
    parsedNow() {
      return this.now ? parseTimestamp(this.now, true) : null;
    }
  },
  watch: {
    parsedNow: "updateTimes"
  },
  created() {
    this.updateTimes();
    this.setPresent();
  },
  methods: {
    setPresent() {
      this.times.now.present = this.times.today.present = true;
      this.times.now.past = this.times.today.past = false;
      this.times.now.future = this.times.today.future = false;
    },
    updateTimes() {
      const now = this.parsedNow || this.getNow();
      this.updateDay(now, this.times.now);
      this.updateTime(now, this.times.now);
      this.updateDay(now, this.times.today);
    },
    getNow() {
      return parseDate$1(new Date());
    },
    updateDay(now, target) {
      if (now.date !== target.date) {
        target.year = now.year;
        target.month = now.month;
        target.day = now.day;
        target.weekday = now.weekday;
        target.date = now.date;
      }
    },
    updateTime(now, target) {
      if (now.time !== target.time) {
        target.hour = now.hour;
        target.minute = now.minute;
        target.time = now.time;
      }
    }
  }
});
const MILLIS_IN_DAY = 864e5;
function getVisuals(events, minStart = 0) {
  const visuals = events.map((event) => ({
    event,
    columnCount: 0,
    column: 0,
    left: 0,
    width: 100
  }));
  visuals.sort((a, b) => {
    return Math.max(minStart, a.event.startTimestampIdentifier) - Math.max(minStart, b.event.startTimestampIdentifier) || b.event.endTimestampIdentifier - a.event.endTimestampIdentifier;
  });
  return visuals;
}
function hasOverlap(s0, e0, s1, e1, exclude = true) {
  return exclude ? !(s0 >= e1 || e0 <= s1) : !(s0 > e1 || e0 < s1);
}
function setColumnCount(groups) {
  groups.forEach((group) => {
    group.visuals.forEach((groupVisual) => {
      groupVisual.columnCount = groups.length;
    });
  });
}
function getRange(event) {
  return [event.startTimestampIdentifier, event.endTimestampIdentifier];
}
function getDayRange(event) {
  return [event.startIdentifier, event.endIdentifier];
}
function getNormalizedRange(event, dayStart) {
  return [Math.max(dayStart, event.startTimestampIdentifier), Math.min(dayStart + MILLIS_IN_DAY, event.endTimestampIdentifier)];
}
function getOpenGroup(groups, start, end, timed) {
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    let intersected = false;
    if (hasOverlap(start, end, group.start, group.end, timed)) {
      for (let k = 0; k < group.visuals.length; k++) {
        const groupVisual = group.visuals[k];
        const [groupStart, groupEnd] = timed ? getRange(groupVisual.event) : getDayRange(groupVisual.event);
        if (hasOverlap(start, end, groupStart, groupEnd, timed)) {
          intersected = true;
          break;
        }
      }
    }
    if (!intersected) {
      return i;
    }
  }
  return -1;
}
function getOverlapGroupHandler(firstWeekday) {
  const handler = {
    groups: [],
    min: -1,
    max: -1,
    reset: () => {
      handler.groups = [];
      handler.min = handler.max = -1;
    },
    getVisuals: (day, dayEvents, timed, reset = false) => {
      if (day.weekday === firstWeekday || reset) {
        handler.reset();
      }
      const dayStart = getTimestampIdentifier(day);
      const visuals = getVisuals(dayEvents, dayStart);
      visuals.forEach((visual) => {
        const [start, end] = timed ? getRange(visual.event) : getDayRange(visual.event);
        if (handler.groups.length > 0 && !hasOverlap(start, end, handler.min, handler.max, timed)) {
          setColumnCount(handler.groups);
          handler.reset();
        }
        let targetGroup = getOpenGroup(handler.groups, start, end, timed);
        if (targetGroup === -1) {
          targetGroup = handler.groups.length;
          handler.groups.push({
            start,
            end,
            visuals: []
          });
        }
        const target = handler.groups[targetGroup];
        target.visuals.push(visual);
        target.start = Math.min(target.start, start);
        target.end = Math.max(target.end, end);
        visual.column = targetGroup;
        if (handler.min === -1) {
          handler.min = start;
          handler.max = end;
        } else {
          handler.min = Math.min(handler.min, start);
          handler.max = Math.max(handler.max, end);
        }
      });
      setColumnCount(handler.groups);
      if (timed) {
        handler.reset();
      }
      return visuals;
    }
  };
  return handler;
}
const FULL_WIDTH$1 = 100;
const DEFAULT_OFFSET = 5;
const WIDTH_MULTIPLIER = 1.7;
const stack = (events, firstWeekday, overlapThreshold) => {
  const handler = getOverlapGroupHandler(firstWeekday);
  return (day, dayEvents, timed, reset) => {
    if (!timed) {
      return handler.getVisuals(day, dayEvents, timed, reset);
    }
    const dayStart = getTimestampIdentifier(day);
    const visuals = getVisuals(dayEvents, dayStart);
    const groups = getGroups(visuals, dayStart);
    for (const group of groups) {
      const nodes = [];
      for (const visual of group.visuals) {
        const child = getNode(visual, dayStart);
        const index = getNextIndex(child, nodes);
        if (index === false) {
          const parent = getParent(child, nodes);
          if (parent) {
            child.parent = parent;
            child.sibling = hasOverlap(child.start, child.end, parent.start, addTime(parent.start, overlapThreshold));
            child.index = parent.index + 1;
            parent.children.push(child);
          }
        } else {
          const [parent] = getOverlappingRange(child, nodes, index - 1, index - 1);
          const children = getOverlappingRange(child, nodes, index + 1, index + nodes.length, true);
          child.children = children;
          child.index = index;
          if (parent) {
            child.parent = parent;
            child.sibling = hasOverlap(child.start, child.end, parent.start, addTime(parent.start, overlapThreshold));
            parent.children.push(child);
          }
          for (const grand of children) {
            if (grand.parent === parent) {
              grand.parent = child;
            }
            const grandNext = grand.index - child.index <= 1;
            if (grandNext && child.sibling && hasOverlap(child.start, addTime(child.start, overlapThreshold), grand.start, grand.end)) {
              grand.sibling = true;
            }
          }
        }
        nodes.push(child);
      }
      calculateBounds(nodes, overlapThreshold);
    }
    visuals.sort((a, b) => a.left - b.left || a.event.startTimestampIdentifier - b.event.startTimestampIdentifier);
    return visuals;
  };
};
function calculateBounds(nodes, overlapThreshold) {
  for (const node of nodes) {
    const {
      visual,
      parent
    } = node;
    const columns = getMaxChildIndex(node) + 1;
    const spaceLeft = parent ? parent.visual.left : 0;
    const spaceWidth = FULL_WIDTH$1 - spaceLeft;
    const offset = Math.min(DEFAULT_OFFSET, FULL_WIDTH$1 / columns);
    const columnWidthMultiplier = getColumnWidthMultiplier(node, nodes);
    const columnOffset = spaceWidth / (columns - node.index + 1);
    const columnWidth = spaceWidth / (columns - node.index + (node.sibling ? 1 : 0)) * columnWidthMultiplier;
    if (parent) {
      visual.left = node.sibling ? spaceLeft + columnOffset : spaceLeft + offset;
    }
    visual.width = hasFullWidth(node, nodes, overlapThreshold) ? FULL_WIDTH$1 - visual.left : Math.min(FULL_WIDTH$1 - visual.left, columnWidth * WIDTH_MULTIPLIER);
  }
}
function getColumnWidthMultiplier(node, nodes) {
  if (!node.children.length) {
    return 1;
  }
  const maxColumn = node.index + nodes.length;
  const minColumn = node.children.reduce((min, c) => Math.min(min, c.index), maxColumn);
  return minColumn - node.index;
}
function getOverlappingIndices(node, nodes) {
  const indices = [];
  for (const other of nodes) {
    if (hasOverlap(node.start, node.end, other.start, other.end)) {
      indices.push(other.index);
    }
  }
  return indices;
}
function getNextIndex(node, nodes) {
  const indices = getOverlappingIndices(node, nodes);
  indices.sort();
  for (let i = 0; i < indices.length; i++) {
    if (i < indices[i]) {
      return i;
    }
  }
  return false;
}
function getOverlappingRange(node, nodes, indexMin, indexMax, returnFirstColumn = false) {
  const overlapping = [];
  for (const other of nodes) {
    if (other.index >= indexMin && other.index <= indexMax && hasOverlap(node.start, node.end, other.start, other.end)) {
      overlapping.push(other);
    }
  }
  if (returnFirstColumn && overlapping.length > 0) {
    const first = overlapping.reduce((min, n) => Math.min(min, n.index), overlapping[0].index);
    return overlapping.filter((n) => n.index === first);
  }
  return overlapping;
}
function getParent(node, nodes) {
  let parent = null;
  for (const other of nodes) {
    if (hasOverlap(node.start, node.end, other.start, other.end) && (parent === null || other.index > parent.index)) {
      parent = other;
    }
  }
  return parent;
}
function hasFullWidth(node, nodes, overlapThreshold) {
  for (const other of nodes) {
    if (other !== node && other.index > node.index && hasOverlap(node.start, addTime(node.start, overlapThreshold), other.start, other.end)) {
      return false;
    }
  }
  return true;
}
function getGroups(visuals, dayStart) {
  const groups = [];
  for (const visual of visuals) {
    const [start, end] = getNormalizedRange(visual.event, dayStart);
    let added = false;
    for (const group of groups) {
      if (hasOverlap(start, end, group.start, group.end)) {
        group.visuals.push(visual);
        group.end = Math.max(group.end, end);
        added = true;
        break;
      }
    }
    if (!added) {
      groups.push({
        start,
        end,
        visuals: [visual]
      });
    }
  }
  return groups;
}
function getNode(visual, dayStart) {
  const [start, end] = getNormalizedRange(visual.event, dayStart);
  return {
    parent: null,
    sibling: true,
    index: 0,
    visual,
    start,
    end,
    children: []
  };
}
function getMaxChildIndex(node) {
  let max = node.index;
  for (const child of node.children) {
    const childMax = getMaxChildIndex(child);
    if (childMax > max) {
      max = childMax;
    }
  }
  return max;
}
function addTime(identifier, minutes) {
  const removeMinutes = identifier % 100;
  const totalMinutes = removeMinutes + minutes;
  const addHours = Math.floor(totalMinutes / 60);
  const addMinutes = totalMinutes % 60;
  return identifier - removeMinutes + addHours * 100 + addMinutes;
}
const FULL_WIDTH = 100;
const column = (events, firstWeekday, overlapThreshold) => {
  const handler = getOverlapGroupHandler(firstWeekday);
  return (day, dayEvents, timed, reset) => {
    const visuals = handler.getVisuals(day, dayEvents, timed, reset);
    if (timed) {
      visuals.forEach((visual) => {
        visual.left = visual.column * FULL_WIDTH / visual.columnCount;
        visual.width = FULL_WIDTH / visual.columnCount;
      });
    }
    return visuals;
  };
};
const CalendarEventOverlapModes = {
  stack,
  column
};
var props = {
  base: {
    start: {
      type: [String, Number, Date],
      validate: validateTimestamp,
      default: () => parseDate$1(new Date()).date
    },
    end: {
      type: [String, Number, Date],
      validate: validateTimestamp
    },
    weekdays: {
      type: [Array, String],
      default: () => [0, 1, 2, 3, 4, 5, 6],
      validate: validateWeekdays
    },
    hideHeader: {
      type: Boolean
    },
    shortWeekdays: {
      type: Boolean,
      default: true
    },
    weekdayFormat: {
      type: Function,
      default: null
    },
    dayFormat: {
      type: Function,
      default: null
    }
  },
  intervals: {
    maxDays: {
      type: Number,
      default: 7
    },
    shortIntervals: {
      type: Boolean,
      default: true
    },
    intervalHeight: {
      type: [Number, String],
      default: 48,
      validate: validateNumber
    },
    intervalWidth: {
      type: [Number, String],
      default: 60,
      validate: validateNumber
    },
    intervalMinutes: {
      type: [Number, String],
      default: 60,
      validate: validateNumber
    },
    firstInterval: {
      type: [Number, String],
      default: 0,
      validate: validateNumber
    },
    firstTime: {
      type: [Number, String, Object],
      validate: validateTime$1
    },
    intervalCount: {
      type: [Number, String],
      default: 24,
      validate: validateNumber
    },
    intervalFormat: {
      type: Function,
      default: null
    },
    intervalStyle: {
      type: Function,
      default: null
    },
    showIntervalLabel: {
      type: Function,
      default: null
    }
  },
  weeks: {
    localeFirstDayOfYear: {
      type: [String, Number],
      default: 0
    },
    minWeeks: {
      validate: validateNumber,
      default: 1
    },
    shortMonths: {
      type: Boolean,
      default: true
    },
    showMonthOnFirst: {
      type: Boolean,
      default: true
    },
    showWeek: Boolean,
    monthFormat: {
      type: Function,
      default: null
    }
  },
  calendar: {
    type: {
      type: String,
      default: "month"
    },
    value: {
      type: [String, Number, Date],
      validate: validateTimestamp
    }
  },
  category: {
    categories: {
      type: [Array, String],
      default: ""
    },
    categoryText: {
      type: [String, Function]
    },
    categoryHideDynamic: {
      type: Boolean
    },
    categoryShowAll: {
      type: Boolean
    },
    categoryForInvalid: {
      type: String,
      default: ""
    },
    categoryDays: {
      type: [Number, String],
      default: 1,
      validate: (x) => isFinite(parseInt(x)) && parseInt(x) > 0
    }
  },
  events: {
    events: {
      type: Array,
      default: () => []
    },
    eventStart: {
      type: String,
      default: "start"
    },
    eventEnd: {
      type: String,
      default: "end"
    },
    eventTimed: {
      type: [String, Function],
      default: "timed"
    },
    eventCategory: {
      type: [String, Function],
      default: "category"
    },
    eventHeight: {
      type: Number,
      default: 20
    },
    eventColor: {
      type: [String, Function],
      default: "primary"
    },
    eventTextColor: {
      type: [String, Function],
      default: "white"
    },
    eventName: {
      type: [String, Function],
      default: "name"
    },
    eventOverlapThreshold: {
      type: [String, Number],
      default: 60
    },
    eventOverlapMode: {
      type: [String, Function],
      default: "stack",
      validate: (mode) => mode in CalendarEventOverlapModes || typeof mode === "function"
    },
    eventMore: {
      type: Boolean,
      default: true
    },
    eventMoreText: {
      type: String,
      default: "$vuetify.calendar.moreEvents"
    },
    eventRipple: {
      type: [Boolean, Object],
      default: null
    },
    eventMarginBottom: {
      type: Number,
      default: 1
    }
  }
};
function validateNumber(input) {
  return isFinite(parseInt(input));
}
function validateWeekdays(input) {
  if (typeof input === "string") {
    input = input.split(",");
  }
  if (Array.isArray(input)) {
    const ints = input.map((x) => parseInt(x));
    if (ints.length > DAYS_IN_WEEK || ints.length === 0) {
      return false;
    }
    const visited = {};
    let wrapped = false;
    for (let i = 0; i < ints.length; i++) {
      const x = ints[i];
      if (!isFinite(x) || x < 0 || x >= DAYS_IN_WEEK) {
        return false;
      }
      if (i > 0) {
        const d = x - ints[i - 1];
        if (d < 0) {
          if (wrapped) {
            return false;
          }
          wrapped = true;
        } else if (d === 0) {
          return false;
        }
      }
      if (visited[x]) {
        return false;
      }
      visited[x] = true;
    }
    return true;
  }
  return false;
}
var CalendarBase = mixins(Colorable, Localable, Mouse, Themeable$1, Times).extend({
  name: "calendar-base",
  directives: {
    Resize: Resize$1
  },
  props: props.base,
  computed: {
    parsedWeekdays() {
      return Array.isArray(this.weekdays) ? this.weekdays : (this.weekdays || "").split(",").map((x) => parseInt(x, 10));
    },
    weekdaySkips() {
      return getWeekdaySkips(this.parsedWeekdays);
    },
    weekdaySkipsReverse() {
      const reversed = this.weekdaySkips.slice();
      reversed.reverse();
      return reversed;
    },
    parsedStart() {
      return parseTimestamp(this.start, true);
    },
    parsedEnd() {
      const start = this.parsedStart;
      const end = this.end ? parseTimestamp(this.end) || start : start;
      return getTimestampIdentifier(end) < getTimestampIdentifier(start) ? start : end;
    },
    days() {
      return createDayList(this.parsedStart, this.parsedEnd, this.times.today, this.weekdaySkips);
    },
    dayFormatter() {
      if (this.dayFormat) {
        return this.dayFormat;
      }
      const options2 = {
        timeZone: "UTC",
        day: "numeric"
      };
      return createNativeLocaleFormatter$1(this.currentLocale, (_tms, _short) => options2);
    },
    weekdayFormatter() {
      if (this.weekdayFormat) {
        return this.weekdayFormat;
      }
      const longOptions = {
        timeZone: "UTC",
        weekday: "long"
      };
      const shortOptions = {
        timeZone: "UTC",
        weekday: "short"
      };
      return createNativeLocaleFormatter$1(this.currentLocale, (_tms, short) => short ? shortOptions : longOptions);
    }
  },
  methods: {
    getRelativeClasses(timestamp, outside = false) {
      return {
        "v-present": timestamp.present,
        "v-past": timestamp.past,
        "v-future": timestamp.future,
        "v-outside": outside
      };
    },
    getStartOfWeek(timestamp) {
      return getStartOfWeek(timestamp, this.parsedWeekdays, this.times.today);
    },
    getEndOfWeek(timestamp) {
      return getEndOfWeek(timestamp, this.parsedWeekdays, this.times.today);
    },
    getFormatter(options2) {
      return createNativeLocaleFormatter$1(this.locale, (_tms, _short) => options2);
    }
  }
});
function parseEvent(input, index, startProperty, endProperty, timed = false, category = false) {
  const startInput = input[startProperty];
  const endInput = input[endProperty];
  const startParsed = parseTimestamp(startInput, true);
  const endParsed = endInput ? parseTimestamp(endInput, true) : startParsed;
  const start = isTimedless(startInput) ? updateHasTime(startParsed, timed) : startParsed;
  const end = isTimedless(endInput) ? updateHasTime(endParsed, timed) : endParsed;
  const startIdentifier = getDayIdentifier(start);
  const startTimestampIdentifier = getTimestampIdentifier(start);
  const endIdentifier = getDayIdentifier(end);
  const endOffset = start.hasTime ? 0 : 2359;
  const endTimestampIdentifier = getTimestampIdentifier(end) + endOffset;
  const allDay = !start.hasTime;
  return {
    input,
    start,
    startIdentifier,
    startTimestampIdentifier,
    end,
    endIdentifier,
    endTimestampIdentifier,
    allDay,
    index,
    category
  };
}
function isEventOn(event, dayIdentifier) {
  return dayIdentifier >= event.startIdentifier && dayIdentifier <= event.endIdentifier && dayIdentifier * OFFSET_TIME !== event.endTimestampIdentifier;
}
function isEventStart(event, day, dayIdentifier, firstWeekday) {
  return dayIdentifier === event.startIdentifier || firstWeekday === day.weekday && isEventOn(event, dayIdentifier);
}
function isEventOverlapping(event, startIdentifier, endIdentifier) {
  return startIdentifier <= event.endIdentifier && endIdentifier >= event.startIdentifier;
}
const WIDTH_FULL = 100;
const WIDTH_START = 95;
const MINUTES_IN_DAY = 1440;
var CalendarWithEvents = CalendarBase.extend({
  name: "calendar-with-events",
  directives: {
    ripple
  },
  props: __spreadValues(__spreadValues(__spreadValues({}, props.events), props.calendar), props.category),
  computed: {
    noEvents() {
      return this.events.length === 0;
    },
    parsedEvents() {
      return this.events.map(this.parseEvent);
    },
    parsedEventOverlapThreshold() {
      return parseInt(this.eventOverlapThreshold);
    },
    eventTimedFunction() {
      return typeof this.eventTimed === "function" ? this.eventTimed : (event) => !!event[this.eventTimed];
    },
    eventCategoryFunction() {
      return typeof this.eventCategory === "function" ? this.eventCategory : (event) => event[this.eventCategory];
    },
    eventTextColorFunction() {
      return typeof this.eventTextColor === "function" ? this.eventTextColor : () => this.eventTextColor;
    },
    eventNameFunction() {
      return typeof this.eventName === "function" ? this.eventName : (event, timedEvent) => escapeHTML(event.input[this.eventName] || "");
    },
    eventModeFunction() {
      return typeof this.eventOverlapMode === "function" ? this.eventOverlapMode : CalendarEventOverlapModes[this.eventOverlapMode];
    },
    eventWeekdays() {
      return this.parsedWeekdays;
    },
    categoryMode() {
      return this.type === "category";
    }
  },
  methods: {
    eventColorFunction(e) {
      return typeof this.eventColor === "function" ? this.eventColor(e) : e.color || this.eventColor;
    },
    parseEvent(input, index = 0) {
      return parseEvent(input, index, this.eventStart, this.eventEnd, this.eventTimedFunction(input), this.categoryMode ? this.eventCategoryFunction(input) : false);
    },
    formatTime(withTime, ampm) {
      const formatter = this.getFormatter({
        timeZone: "UTC",
        hour: "numeric",
        minute: withTime.minute > 0 ? "numeric" : void 0
      });
      return formatter(withTime, true);
    },
    updateEventVisibility() {
      if (this.noEvents || !this.eventMore) {
        return;
      }
      const eventHeight = this.eventHeight;
      const eventsMap = this.getEventsMap();
      for (const date in eventsMap) {
        const {
          parent,
          events,
          more
        } = eventsMap[date];
        if (!more) {
          break;
        }
        const parentBounds = parent.getBoundingClientRect();
        const last = events.length - 1;
        let hide = false;
        let hidden = 0;
        for (let i = 0; i <= last; i++) {
          if (!hide) {
            const eventBounds = events[i].getBoundingClientRect();
            hide = i === last ? eventBounds.bottom > parentBounds.bottom : eventBounds.bottom + eventHeight > parentBounds.bottom;
          }
          if (hide) {
            events[i].style.display = "none";
            hidden++;
          }
        }
        if (hide) {
          more.style.display = "";
          more.innerHTML = this.$vuetify.lang.t(this.eventMoreText, hidden);
        } else {
          more.style.display = "none";
        }
      }
    },
    getEventsMap() {
      const eventsMap = {};
      const elements = this.$refs.events;
      if (!elements || !elements.forEach) {
        return eventsMap;
      }
      elements.forEach((el) => {
        const date = el.getAttribute("data-date");
        if (el.parentElement && date) {
          if (!(date in eventsMap)) {
            eventsMap[date] = {
              parent: el.parentElement,
              more: null,
              events: []
            };
          }
          if (el.getAttribute("data-more")) {
            eventsMap[date].more = el;
          } else {
            eventsMap[date].events.push(el);
            el.style.display = "";
          }
        }
      });
      return eventsMap;
    },
    genDayEvent({
      event
    }, day) {
      const eventHeight = this.eventHeight;
      const eventMarginBottom = this.eventMarginBottom;
      const dayIdentifier = getDayIdentifier(day);
      const week = day.week;
      const start = dayIdentifier === event.startIdentifier;
      let end = dayIdentifier === event.endIdentifier;
      let width = WIDTH_START;
      if (!this.categoryMode) {
        for (let i = day.index + 1; i < week.length; i++) {
          const weekdayIdentifier = getDayIdentifier(week[i]);
          if (event.endIdentifier >= weekdayIdentifier) {
            width += WIDTH_FULL;
            end = end || weekdayIdentifier === event.endIdentifier;
          } else {
            end = true;
            break;
          }
        }
      }
      const scope = {
        eventParsed: event,
        day,
        start,
        end,
        timed: false
      };
      return this.genEvent(event, scope, false, {
        staticClass: "v-event",
        class: {
          "v-event-start": start,
          "v-event-end": end
        },
        style: {
          height: `${eventHeight}px`,
          width: `${width}%`,
          "margin-bottom": `${eventMarginBottom}px`
        },
        attrs: {
          "data-date": day.date
        },
        key: event.index,
        ref: "events",
        refInFor: true
      });
    },
    genTimedEvent({
      event,
      left,
      width
    }, day) {
      if (day.timeDelta(event.end) <= 0 || day.timeDelta(event.start) >= 1) {
        return false;
      }
      const dayIdentifier = getDayIdentifier(day);
      const start = event.startIdentifier >= dayIdentifier;
      const end = event.endIdentifier > dayIdentifier;
      const top = start ? day.timeToY(event.start) : 0;
      const bottom = end ? day.timeToY(MINUTES_IN_DAY) : day.timeToY(event.end);
      const height = Math.max(this.eventHeight, bottom - top);
      const scope = {
        eventParsed: event,
        day,
        start,
        end,
        timed: true
      };
      return this.genEvent(event, scope, true, {
        staticClass: "v-event-timed",
        style: {
          top: `${top}px`,
          height: `${height}px`,
          left: `${left}%`,
          width: `${width}%`
        }
      });
    },
    genEvent(event, scopeInput, timedEvent, data) {
      var _this$eventRipple;
      const slot = this.$scopedSlots.event;
      const text = this.eventTextColorFunction(event.input);
      const background = this.eventColorFunction(event.input);
      const overlapsNoon = event.start.hour < 12 && event.end.hour >= 12;
      const singline = diffMinutes(event.start, event.end) <= this.parsedEventOverlapThreshold;
      const formatTime = this.formatTime;
      const timeSummary = () => formatTime(event.start, overlapsNoon) + " - " + formatTime(event.end, true);
      const eventSummary = () => {
        const name = this.eventNameFunction(event, timedEvent);
        if (event.start.hasTime) {
          if (timedEvent) {
            const time = timeSummary();
            const delimiter = singline ? ", " : "<br>";
            return `<strong>${name}</strong>${delimiter}${time}`;
          } else {
            const time = formatTime(event.start, true);
            return `<strong>${time}</strong> ${name}`;
          }
        }
        return name;
      };
      const scope = __spreadProps(__spreadValues({}, scopeInput), {
        event: event.input,
        outside: scopeInput.day.outside,
        singline,
        overlapsNoon,
        formatTime,
        timeSummary,
        eventSummary
      });
      return this.$createElement("div", this.setTextColor(text, this.setBackgroundColor(background, __spreadValues({
        on: this.getDefaultMouseEventHandlers(":event", (nativeEvent) => __spreadProps(__spreadValues({}, scope), {
          nativeEvent
        })),
        directives: [{
          name: "ripple",
          value: (_this$eventRipple = this.eventRipple) != null ? _this$eventRipple : true
        }]
      }, data))), slot ? slot(scope) : [this.genName(eventSummary)]);
    },
    genName(eventSummary) {
      return this.$createElement("div", {
        staticClass: "pl-1",
        domProps: {
          innerHTML: eventSummary()
        }
      });
    },
    genPlaceholder(day) {
      const height = this.eventHeight + this.eventMarginBottom;
      return this.$createElement("div", {
        style: {
          height: `${height}px`
        },
        attrs: {
          "data-date": day.date
        },
        ref: "events",
        refInFor: true
      });
    },
    genMore(day) {
      var _this$eventRipple2;
      const eventHeight = this.eventHeight;
      const eventMarginBottom = this.eventMarginBottom;
      return this.$createElement("div", {
        staticClass: "v-event-more pl-1",
        class: {
          "v-outside": day.outside
        },
        attrs: {
          "data-date": day.date,
          "data-more": 1
        },
        directives: [{
          name: "ripple",
          value: (_this$eventRipple2 = this.eventRipple) != null ? _this$eventRipple2 : true
        }],
        on: {
          click: () => this.$emit("click:more", day)
        },
        style: {
          display: "none",
          height: `${eventHeight}px`,
          "margin-bottom": `${eventMarginBottom}px`
        },
        ref: "events",
        refInFor: true
      });
    },
    getVisibleEvents() {
      const start = getDayIdentifier(this.days[0]);
      const end = getDayIdentifier(this.days[this.days.length - 1]);
      return this.parsedEvents.filter((event) => isEventOverlapping(event, start, end));
    },
    isEventForCategory(event, category) {
      return !this.categoryMode || typeof category === "object" && category.categoryName && category.categoryName === event.category || typeof event.category !== "string" && category === null;
    },
    getEventsForDay(day) {
      const identifier = getDayIdentifier(day);
      const firstWeekday = this.eventWeekdays[0];
      return this.parsedEvents.filter((event) => isEventStart(event, day, identifier, firstWeekday));
    },
    getEventsForDayAll(day) {
      const identifier = getDayIdentifier(day);
      const firstWeekday = this.eventWeekdays[0];
      return this.parsedEvents.filter((event) => event.allDay && (this.categoryMode ? isEventOn(event, identifier) : isEventStart(event, day, identifier, firstWeekday)) && this.isEventForCategory(event, day.category));
    },
    getEventsForDayTimed(day) {
      const identifier = getDayIdentifier(day);
      return this.parsedEvents.filter((event) => !event.allDay && isEventOn(event, identifier) && this.isEventForCategory(event, day.category));
    },
    getScopedSlots() {
      if (this.noEvents) {
        return __spreadValues({}, this.$scopedSlots);
      }
      const mode = this.eventModeFunction(this.parsedEvents, this.eventWeekdays[0], this.parsedEventOverlapThreshold);
      const isNode = (input) => !!input;
      const getSlotChildren = (day, getter, mapper, timed) => {
        const events = getter(day);
        const visuals = mode(day, events, timed, this.categoryMode);
        if (timed) {
          return visuals.map((visual) => mapper(visual, day)).filter(isNode);
        }
        const children = [];
        visuals.forEach((visual, index) => {
          while (children.length < visual.column) {
            children.push(this.genPlaceholder(day));
          }
          const mapped = mapper(visual, day);
          if (mapped) {
            children.push(mapped);
          }
        });
        return children;
      };
      const slots = this.$scopedSlots;
      const slotDay = slots.day;
      const slotDayHeader = slots["day-header"];
      const slotDayBody = slots["day-body"];
      return __spreadProps(__spreadValues({}, slots), {
        day: (day) => {
          let children = getSlotChildren(day, this.getEventsForDay, this.genDayEvent, false);
          if (children && children.length > 0 && this.eventMore) {
            children.push(this.genMore(day));
          }
          if (slotDay) {
            const slot = slotDay(day);
            if (slot) {
              children = children ? children.concat(slot) : slot;
            }
          }
          return children;
        },
        "day-header": (day) => {
          let children = getSlotChildren(day, this.getEventsForDayAll, this.genDayEvent, false);
          if (slotDayHeader) {
            const slot = slotDayHeader(day);
            if (slot) {
              children = children ? children.concat(slot) : slot;
            }
          }
          return children;
        },
        "day-body": (day) => {
          const events = getSlotChildren(day, this.getEventsForDayTimed, this.genTimedEvent, true);
          let children = [this.$createElement("div", {
            staticClass: "v-event-timed-container"
          }, events)];
          if (slotDayBody) {
            const slot = slotDayBody(day);
            if (slot) {
              children = children.concat(slot);
            }
          }
          return children;
        }
      });
    }
  }
});
var VCalendarWeekly = CalendarBase.extend({
  name: "v-calendar-weekly",
  props: props.weeks,
  computed: {
    staticClass() {
      return "v-calendar-weekly";
    },
    classes() {
      return this.themeClasses;
    },
    parsedMinWeeks() {
      return parseInt(this.minWeeks);
    },
    days() {
      const minDays = this.parsedMinWeeks * this.parsedWeekdays.length;
      const start = this.getStartOfWeek(this.parsedStart);
      const end = this.getEndOfWeek(this.parsedEnd);
      return createDayList(start, end, this.times.today, this.weekdaySkips, Number.MAX_SAFE_INTEGER, minDays);
    },
    todayWeek() {
      const today = this.times.today;
      const start = this.getStartOfWeek(today);
      const end = this.getEndOfWeek(today);
      return createDayList(start, end, today, this.weekdaySkips, this.parsedWeekdays.length, this.parsedWeekdays.length);
    },
    monthFormatter() {
      if (this.monthFormat) {
        return this.monthFormat;
      }
      const longOptions = {
        timeZone: "UTC",
        month: "long"
      };
      const shortOptions = {
        timeZone: "UTC",
        month: "short"
      };
      return createNativeLocaleFormatter$1(this.currentLocale, (_tms, short) => short ? shortOptions : longOptions);
    }
  },
  methods: {
    isOutside(day) {
      const dayIdentifier = getDayIdentifier(day);
      return dayIdentifier < getDayIdentifier(this.parsedStart) || dayIdentifier > getDayIdentifier(this.parsedEnd);
    },
    genHead() {
      return this.$createElement("div", {
        staticClass: "v-calendar-weekly__head"
      }, this.genHeadDays());
    },
    genHeadDays() {
      const header2 = this.todayWeek.map(this.genHeadDay);
      if (this.showWeek) {
        header2.unshift(this.$createElement("div", {
          staticClass: "v-calendar-weekly__head-weeknumber"
        }));
      }
      return header2;
    },
    genHeadDay(day, index) {
      const outside = this.isOutside(this.days[index]);
      const color = day.present ? this.color : void 0;
      return this.$createElement("div", this.setTextColor(color, {
        key: day.date,
        staticClass: "v-calendar-weekly__head-weekday",
        class: this.getRelativeClasses(day, outside)
      }), this.weekdayFormatter(day, this.shortWeekdays));
    },
    genWeeks() {
      const days = this.days;
      const weekDays = this.parsedWeekdays.length;
      const weeks = [];
      for (let i = 0; i < days.length; i += weekDays) {
        weeks.push(this.genWeek(days.slice(i, i + weekDays), this.getWeekNumber(days[i])));
      }
      return weeks;
    },
    genWeek(week, weekNumber2) {
      const weekNodes = week.map((day, index) => this.genDay(day, index, week));
      if (this.showWeek) {
        weekNodes.unshift(this.genWeekNumber(weekNumber2));
      }
      return this.$createElement("div", {
        key: week[0].date,
        staticClass: "v-calendar-weekly__week"
      }, weekNodes);
    },
    getWeekNumber(determineDay) {
      return weekNumber(determineDay.year, determineDay.month - 1, determineDay.day, this.parsedWeekdays[0], parseInt(this.localeFirstDayOfYear));
    },
    genWeekNumber(weekNumber2) {
      return this.$createElement("div", {
        staticClass: "v-calendar-weekly__weeknumber"
      }, [this.$createElement("small", String(weekNumber2))]);
    },
    genDay(day, index, week) {
      const outside = this.isOutside(day);
      return this.$createElement("div", {
        key: day.date,
        staticClass: "v-calendar-weekly__day",
        class: this.getRelativeClasses(day, outside),
        on: this.getDefaultMouseEventHandlers(":day", (_e) => day)
      }, [this.genDayLabel(day), ...getSlot(this, "day", () => __spreadValues({
        outside,
        index,
        week
      }, day)) || []]);
    },
    genDayLabel(day) {
      return this.$createElement("div", {
        staticClass: "v-calendar-weekly__day-label"
      }, getSlot(this, "day-label", day) || [this.genDayLabelButton(day)]);
    },
    genDayLabelButton(day) {
      const color = day.present ? this.color : "transparent";
      const hasMonth = day.day === 1 && this.showMonthOnFirst;
      return this.$createElement(VBtn, {
        props: {
          color,
          fab: true,
          depressed: true,
          small: true
        },
        on: this.getMouseEventHandlers({
          "click:date": {
            event: "click",
            stop: true
          },
          "contextmenu:date": {
            event: "contextmenu",
            stop: true,
            prevent: true,
            result: false
          }
        }, (_e) => day)
      }, hasMonth ? this.monthFormatter(day, this.shortMonths) + " " + this.dayFormatter(day, false) : this.dayFormatter(day, false));
    },
    genDayMonth(day) {
      const color = day.present ? this.color : void 0;
      return this.$createElement("div", this.setTextColor(color, {
        staticClass: "v-calendar-weekly__day-month"
      }), getSlot(this, "day-month", day) || this.monthFormatter(day, this.shortMonths));
    }
  },
  render(h) {
    return h("div", {
      staticClass: this.staticClass,
      class: this.classes,
      on: {
        dragstart: (e) => {
          e.preventDefault();
        }
      }
    }, [!this.hideHeader ? this.genHead() : "", ...this.genWeeks()]);
  }
});
var VCalendarMonthly = VCalendarWeekly.extend({
  name: "v-calendar-monthly",
  computed: {
    staticClass() {
      return "v-calendar-monthly v-calendar-weekly";
    },
    parsedStart() {
      return getStartOfMonth(parseTimestamp(this.start, true));
    },
    parsedEnd() {
      return getEndOfMonth(parseTimestamp(this.end, true));
    }
  }
});
var CalendarWithIntervals = CalendarBase.extend({
  name: "calendar-with-intervals",
  props: props.intervals,
  computed: {
    parsedFirstInterval() {
      return parseInt(this.firstInterval);
    },
    parsedIntervalMinutes() {
      return parseInt(this.intervalMinutes);
    },
    parsedIntervalCount() {
      return parseInt(this.intervalCount);
    },
    parsedIntervalHeight() {
      return parseFloat(this.intervalHeight);
    },
    parsedFirstTime() {
      return parseTime$1(this.firstTime);
    },
    firstMinute() {
      const time = this.parsedFirstTime;
      return time !== false && time >= 0 && time <= MINUTES_IN_DAY$1 ? time : this.parsedFirstInterval * this.parsedIntervalMinutes;
    },
    bodyHeight() {
      return this.parsedIntervalCount * this.parsedIntervalHeight;
    },
    days() {
      return createDayList(this.parsedStart, this.parsedEnd, this.times.today, this.weekdaySkips, this.maxDays);
    },
    intervals() {
      const days = this.days;
      const first = this.firstMinute;
      const minutes = this.parsedIntervalMinutes;
      const count = this.parsedIntervalCount;
      const now = this.times.now;
      return days.map((d) => createIntervalList(d, first, minutes, count, now));
    },
    intervalFormatter() {
      if (this.intervalFormat) {
        return this.intervalFormat;
      }
      const longOptions = {
        timeZone: "UTC",
        hour: "2-digit",
        minute: "2-digit"
      };
      const shortOptions = {
        timeZone: "UTC",
        hour: "numeric",
        minute: "2-digit"
      };
      const shortHourOptions = {
        timeZone: "UTC",
        hour: "numeric"
      };
      return createNativeLocaleFormatter$1(this.currentLocale, (tms, short) => short ? tms.minute === 0 ? shortHourOptions : shortOptions : longOptions);
    }
  },
  methods: {
    showIntervalLabelDefault(interval) {
      const first = this.intervals[0][0];
      const isFirst = first.hour === interval.hour && first.minute === interval.minute;
      return !isFirst;
    },
    intervalStyleDefault(_interval) {
      return void 0;
    },
    getTimestampAtEvent(e, day) {
      const timestamp = copyTimestamp(day);
      const bounds = e.currentTarget.getBoundingClientRect();
      const baseMinutes = this.firstMinute;
      const touchEvent = e;
      const mouseEvent = e;
      const touches = touchEvent.changedTouches || touchEvent.touches;
      const clientY = touches && touches[0] ? touches[0].clientY : mouseEvent.clientY;
      const addIntervals = (clientY - bounds.top) / this.parsedIntervalHeight;
      const addMinutes = Math.floor(addIntervals * this.parsedIntervalMinutes);
      const minutes = baseMinutes + addMinutes;
      return updateMinutes(timestamp, minutes, this.times.now);
    },
    getSlotScope(timestamp) {
      const scope = copyTimestamp(timestamp);
      scope.timeToY = this.timeToY;
      scope.timeDelta = this.timeDelta;
      scope.minutesToPixels = this.minutesToPixels;
      scope.week = this.days;
      return scope;
    },
    scrollToTime(time) {
      const y = this.timeToY(time);
      const pane = this.$refs.scrollArea;
      if (y === false || !pane) {
        return false;
      }
      pane.scrollTop = y;
      return true;
    },
    minutesToPixels(minutes) {
      return minutes / this.parsedIntervalMinutes * this.parsedIntervalHeight;
    },
    timeToY(time, clamp2 = true) {
      let y = this.timeDelta(time);
      if (y !== false) {
        y *= this.bodyHeight;
        if (clamp2) {
          if (y < 0) {
            y = 0;
          }
          if (y > this.bodyHeight) {
            y = this.bodyHeight;
          }
        }
      }
      return y;
    },
    timeDelta(time) {
      const minutes = parseTime$1(time);
      if (minutes === false) {
        return false;
      }
      const min = this.firstMinute;
      const gap = this.parsedIntervalCount * this.parsedIntervalMinutes;
      return (minutes - min) / gap;
    }
  }
});
var VCalendarDaily = CalendarWithIntervals.extend({
  name: "v-calendar-daily",
  directives: {
    Resize: Resize$1
  },
  data: () => ({
    scrollPush: 0
  }),
  computed: {
    classes() {
      return __spreadValues({
        "v-calendar-daily": true
      }, this.themeClasses);
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.$nextTick(this.onResize);
    },
    onResize() {
      this.scrollPush = this.getScrollPush();
    },
    getScrollPush() {
      const area = this.$refs.scrollArea;
      const pane = this.$refs.pane;
      return area && pane ? area.offsetWidth - pane.offsetWidth : 0;
    },
    genHead() {
      return this.$createElement("div", {
        staticClass: "v-calendar-daily__head",
        style: {
          marginRight: this.scrollPush + "px"
        }
      }, [this.genHeadIntervals(), ...this.genHeadDays()]);
    },
    genHeadIntervals() {
      const width = convertToUnit(this.intervalWidth);
      return this.$createElement("div", {
        staticClass: "v-calendar-daily__intervals-head",
        style: {
          width
        }
      }, getSlot(this, "interval-header"));
    },
    genHeadDays() {
      return this.days.map(this.genHeadDay);
    },
    genHeadDay(day, index) {
      return this.$createElement("div", {
        key: day.date,
        staticClass: "v-calendar-daily_head-day",
        class: this.getRelativeClasses(day),
        on: this.getDefaultMouseEventHandlers(":day", (_e) => {
          return this.getSlotScope(day);
        })
      }, [this.genHeadWeekday(day), this.genHeadDayLabel(day), ...this.genDayHeader(day, index)]);
    },
    genDayHeader(day, index) {
      return getSlot(this, "day-header", () => __spreadProps(__spreadValues({
        week: this.days
      }, day), {
        index
      })) || [];
    },
    genHeadWeekday(day) {
      const color = day.present ? this.color : void 0;
      return this.$createElement("div", this.setTextColor(color, {
        staticClass: "v-calendar-daily_head-weekday"
      }), this.weekdayFormatter(day, this.shortWeekdays));
    },
    genHeadDayLabel(day) {
      return this.$createElement("div", {
        staticClass: "v-calendar-daily_head-day-label"
      }, getSlot(this, "day-label-header", day) || [this.genHeadDayButton(day)]);
    },
    genHeadDayButton(day) {
      const color = day.present ? this.color : "transparent";
      return this.$createElement(VBtn, {
        props: {
          color,
          fab: true,
          depressed: true
        },
        on: this.getMouseEventHandlers({
          "click:date": {
            event: "click",
            stop: true
          },
          "contextmenu:date": {
            event: "contextmenu",
            stop: true,
            prevent: true,
            result: false
          }
        }, (_e) => {
          return day;
        })
      }, this.dayFormatter(day, false));
    },
    genBody() {
      return this.$createElement("div", {
        staticClass: "v-calendar-daily__body"
      }, [this.genScrollArea()]);
    },
    genScrollArea() {
      return this.$createElement("div", {
        ref: "scrollArea",
        staticClass: "v-calendar-daily__scroll-area"
      }, [this.genPane()]);
    },
    genPane() {
      return this.$createElement("div", {
        ref: "pane",
        staticClass: "v-calendar-daily__pane",
        style: {
          height: convertToUnit(this.bodyHeight)
        }
      }, [this.genDayContainer()]);
    },
    genDayContainer() {
      return this.$createElement("div", {
        staticClass: "v-calendar-daily__day-container"
      }, [this.genBodyIntervals(), ...this.genDays()]);
    },
    genDays() {
      return this.days.map(this.genDay);
    },
    genDay(day, index) {
      return this.$createElement("div", {
        key: day.date,
        staticClass: "v-calendar-daily__day",
        class: this.getRelativeClasses(day),
        on: this.getDefaultMouseEventHandlers(":time", (e) => {
          return this.getSlotScope(this.getTimestampAtEvent(e, day));
        })
      }, [...this.genDayIntervals(index), ...this.genDayBody(day)]);
    },
    genDayBody(day) {
      return getSlot(this, "day-body", () => this.getSlotScope(day)) || [];
    },
    genDayIntervals(index) {
      return this.intervals[index].map(this.genDayInterval);
    },
    genDayInterval(interval) {
      const height = convertToUnit(this.intervalHeight);
      const styler = this.intervalStyle || this.intervalStyleDefault;
      const data = {
        key: interval.time,
        staticClass: "v-calendar-daily__day-interval",
        style: __spreadValues({
          height
        }, styler(interval))
      };
      const children = getSlot(this, "interval", () => this.getSlotScope(interval));
      return this.$createElement("div", data, children);
    },
    genBodyIntervals() {
      const width = convertToUnit(this.intervalWidth);
      const data = {
        staticClass: "v-calendar-daily__intervals-body",
        style: {
          width
        },
        on: this.getDefaultMouseEventHandlers(":interval", (e) => {
          return this.getTimestampAtEvent(e, this.parsedStart);
        })
      };
      return this.$createElement("div", data, this.genIntervalLabels());
    },
    genIntervalLabels() {
      if (!this.intervals.length)
        return null;
      return this.intervals[0].map(this.genIntervalLabel);
    },
    genIntervalLabel(interval) {
      const height = convertToUnit(this.intervalHeight);
      const short = this.shortIntervals;
      const shower = this.showIntervalLabel || this.showIntervalLabelDefault;
      const show = shower(interval);
      const label = show ? this.intervalFormatter(interval, short) : void 0;
      return this.$createElement("div", {
        key: interval.time,
        staticClass: "v-calendar-daily__interval",
        style: {
          height
        }
      }, [this.$createElement("div", {
        staticClass: "v-calendar-daily__interval-text"
      }, label)]);
    }
  },
  render(h) {
    return h("div", {
      class: this.classes,
      on: {
        dragstart: (e) => {
          e.preventDefault();
        }
      },
      directives: [{
        modifiers: {
          quiet: true
        },
        name: "resize",
        value: this.onResize
      }]
    }, [!this.hideHeader ? this.genHead() : "", this.genBody()]);
  }
});
function parsedCategoryText(category, categoryText) {
  return typeof categoryText === "string" && typeof category === "object" && category ? category[categoryText] : typeof categoryText === "function" ? categoryText(category) : category;
}
function getParsedCategories(categories, categoryText) {
  if (typeof categories === "string")
    return categories.split(/\s*,\s/);
  if (Array.isArray(categories)) {
    return categories.map((category) => {
      if (typeof category === "string")
        return {
          categoryName: category
        };
      const categoryName = typeof category.categoryName === "string" ? category.categoryName : parsedCategoryText(category, categoryText);
      return __spreadProps(__spreadValues({}, category), {
        categoryName
      });
    });
  }
  return [];
}
var VCalendarCategory = VCalendarDaily.extend({
  name: "v-calendar-category",
  props: props.category,
  computed: {
    classes() {
      return __spreadValues({
        "v-calendar-daily": true,
        "v-calendar-category": true
      }, this.themeClasses);
    },
    parsedCategories() {
      return getParsedCategories(this.categories, this.categoryText);
    }
  },
  methods: {
    genDayHeader(day, index) {
      const data = {
        staticClass: "v-calendar-category__columns"
      };
      const scope = __spreadProps(__spreadValues({
        week: this.days
      }, day), {
        index
      });
      const children = this.parsedCategories.map((category) => {
        return this.genDayHeaderCategory(day, this.getCategoryScope(scope, category));
      });
      return [this.$createElement("div", data, children)];
    },
    getCategoryScope(scope, category) {
      const cat = typeof category === "object" && category && category.categoryName === this.categoryForInvalid ? null : category;
      return __spreadProps(__spreadValues({}, scope), {
        category: cat
      });
    },
    genDayHeaderCategory(day, scope) {
      return this.$createElement("div", {
        staticClass: "v-calendar-category__column-header",
        on: this.getDefaultMouseEventHandlers(":day-category", (e) => {
          return this.getCategoryScope(this.getSlotScope(day), scope.category);
        })
      }, [getSlot(this, "category", scope) || this.genDayHeaderCategoryTitle(scope.category && scope.category.categoryName), getSlot(this, "day-header", scope)]);
    },
    genDayHeaderCategoryTitle(categoryName) {
      return this.$createElement("div", {
        staticClass: "v-calendar-category__category"
      }, categoryName === null ? this.categoryForInvalid : categoryName);
    },
    genDays() {
      const days = [];
      this.days.forEach((d) => {
        const day = new Array(this.parsedCategories.length || 1);
        day.fill(d);
        days.push(...day.map((v, i) => this.genDay(v, 0, i)));
      });
      return days;
    },
    genDay(day, index, categoryIndex) {
      const category = this.parsedCategories[categoryIndex];
      return this.$createElement("div", {
        key: day.date + "-" + categoryIndex,
        staticClass: "v-calendar-daily__day",
        class: this.getRelativeClasses(day),
        on: this.getDefaultMouseEventHandlers(":time", (e) => {
          return this.getSlotScope(this.getTimestampAtEvent(e, day));
        })
      }, [...this.genDayIntervals(index, category), ...this.genDayBody(day, category)]);
    },
    genDayIntervals(index, category) {
      return this.intervals[index].map((v) => this.genDayInterval(v, category));
    },
    genDayInterval(interval, category) {
      const height = convertToUnit(this.intervalHeight);
      const styler = this.intervalStyle || this.intervalStyleDefault;
      const data = {
        key: interval.time,
        staticClass: "v-calendar-daily__day-interval",
        style: __spreadValues({
          height
        }, styler(__spreadProps(__spreadValues({}, interval), {
          category
        })))
      };
      const children = getSlot(this, "interval", () => this.getCategoryScope(this.getSlotScope(interval), category));
      return this.$createElement("div", data, children);
    },
    genDayBody(day, category) {
      const data = {
        staticClass: "v-calendar-category__columns"
      };
      const children = [this.genDayBodyCategory(day, category)];
      return [this.$createElement("div", data, children)];
    },
    genDayBodyCategory(day, category) {
      const data = {
        staticClass: "v-calendar-category__column",
        on: this.getDefaultMouseEventHandlers(":time-category", (e) => {
          return this.getCategoryScope(this.getSlotScope(this.getTimestampAtEvent(e, day)), category);
        })
      };
      const children = getSlot(this, "day-body", () => this.getCategoryScope(this.getSlotScope(day), category));
      return this.$createElement("div", data, children);
    }
  }
});
var VCalendar = CalendarWithEvents.extend({
  name: "v-calendar",
  props: __spreadValues(__spreadValues(__spreadValues(__spreadValues({}, props.calendar), props.weeks), props.intervals), props.category),
  data: () => ({
    lastStart: null,
    lastEnd: null
  }),
  computed: {
    parsedValue() {
      return validateTimestamp(this.value) ? parseTimestamp(this.value, true) : this.parsedStart || this.times.today;
    },
    parsedCategoryDays() {
      return parseInt(this.categoryDays) || 1;
    },
    renderProps() {
      const around = this.parsedValue;
      let component = null;
      let maxDays = this.maxDays;
      let weekdays = this.parsedWeekdays;
      let categories = this.parsedCategories;
      let start = around;
      let end = around;
      switch (this.type) {
        case "month":
          component = VCalendarMonthly;
          start = getStartOfMonth(around);
          end = getEndOfMonth(around);
          break;
        case "week":
          component = VCalendarDaily;
          start = this.getStartOfWeek(around);
          end = this.getEndOfWeek(around);
          maxDays = 7;
          break;
        case "day":
          component = VCalendarDaily;
          maxDays = 1;
          weekdays = [start.weekday];
          break;
        case "4day":
          component = VCalendarDaily;
          end = relativeDays(copyTimestamp(end), nextDay, 3);
          updateFormatted(end);
          maxDays = 4;
          weekdays = [start.weekday, (start.weekday + 1) % 7, (start.weekday + 2) % 7, (start.weekday + 3) % 7];
          break;
        case "custom-weekly":
          component = VCalendarWeekly;
          start = this.parsedStart || around;
          end = this.parsedEnd;
          break;
        case "custom-daily":
          component = VCalendarDaily;
          start = this.parsedStart || around;
          end = this.parsedEnd;
          break;
        case "category":
          const days = this.parsedCategoryDays;
          component = VCalendarCategory;
          end = relativeDays(copyTimestamp(end), nextDay, days);
          updateFormatted(end);
          maxDays = days;
          weekdays = [];
          for (let i = 0; i < days; i++) {
            weekdays.push((start.weekday + i) % 7);
          }
          categories = this.getCategoryList(categories);
          break;
        default:
          throw new Error(this.type + " is not a valid Calendar type");
      }
      return {
        component,
        start,
        end,
        maxDays,
        weekdays,
        categories
      };
    },
    eventWeekdays() {
      return this.renderProps.weekdays;
    },
    categoryMode() {
      return this.type === "category";
    },
    title() {
      const {
        start,
        end
      } = this.renderProps;
      const spanYears = start.year !== end.year;
      const spanMonths = spanYears || start.month !== end.month;
      if (spanYears) {
        return this.monthShortFormatter(start, true) + " " + start.year + " - " + this.monthShortFormatter(end, true) + " " + end.year;
      }
      if (spanMonths) {
        return this.monthShortFormatter(start, true) + " - " + this.monthShortFormatter(end, true) + " " + end.year;
      } else {
        return this.monthLongFormatter(start, false) + " " + start.year;
      }
    },
    monthLongFormatter() {
      return this.getFormatter({
        timeZone: "UTC",
        month: "long"
      });
    },
    monthShortFormatter() {
      return this.getFormatter({
        timeZone: "UTC",
        month: "short"
      });
    },
    parsedCategories() {
      return getParsedCategories(this.categories, this.categoryText);
    }
  },
  watch: {
    renderProps: "checkChange"
  },
  mounted() {
    this.updateEventVisibility();
    this.checkChange();
  },
  updated() {
    window.requestAnimationFrame(this.updateEventVisibility);
  },
  methods: {
    checkChange() {
      const {
        lastStart,
        lastEnd
      } = this;
      const {
        start,
        end
      } = this.renderProps;
      if (!lastStart || !lastEnd || start.date !== lastStart.date || end.date !== lastEnd.date) {
        this.lastStart = start;
        this.lastEnd = end;
        this.$emit("change", {
          start,
          end
        });
      }
    },
    move(amount = 1) {
      const moved = copyTimestamp(this.parsedValue);
      const forward = amount > 0;
      const mover = forward ? nextDay : prevDay;
      const limit = forward ? DAYS_IN_MONTH_MAX : DAY_MIN;
      let times = forward ? amount : -amount;
      while (--times >= 0) {
        switch (this.type) {
          case "month":
            moved.day = limit;
            mover(moved);
            break;
          case "week":
            relativeDays(moved, mover, DAYS_IN_WEEK);
            break;
          case "day":
            relativeDays(moved, mover, 1);
            break;
          case "4day":
            relativeDays(moved, mover, 4);
            break;
          case "category":
            relativeDays(moved, mover, this.parsedCategoryDays);
            break;
        }
      }
      updateWeekday(moved);
      updateFormatted(moved);
      updateRelative(moved, this.times.now);
      if (this.value instanceof Date) {
        this.$emit("input", timestampToDate(moved));
      } else if (typeof this.value === "number") {
        this.$emit("input", timestampToDate(moved).getTime());
      } else {
        this.$emit("input", moved.date);
      }
      this.$emit("moved", moved);
    },
    next(amount = 1) {
      this.move(amount);
    },
    prev(amount = 1) {
      this.move(-amount);
    },
    timeToY(time, clamp2 = true) {
      const c = this.$children[0];
      if (c && c.timeToY) {
        return c.timeToY(time, clamp2);
      } else {
        return false;
      }
    },
    timeDelta(time) {
      const c = this.$children[0];
      if (c && c.timeDelta) {
        return c.timeDelta(time);
      } else {
        return false;
      }
    },
    minutesToPixels(minutes) {
      const c = this.$children[0];
      if (c && c.minutesToPixels) {
        return c.minutesToPixels(minutes);
      } else {
        return -1;
      }
    },
    scrollToTime(time) {
      const c = this.$children[0];
      if (c && c.scrollToTime) {
        return c.scrollToTime(time);
      } else {
        return false;
      }
    },
    parseTimestamp(input, required) {
      return parseTimestamp(input, required, this.times.now);
    },
    timestampToDate(timestamp) {
      return timestampToDate(timestamp);
    },
    getCategoryList(categories) {
      if (!this.noEvents) {
        const categoryMap = categories.reduce((map, category, index) => {
          if (typeof category === "object" && category.categoryName)
            map[category.categoryName] = {
              index,
              count: 0
            };
          return map;
        }, {});
        if (!this.categoryHideDynamic || !this.categoryShowAll) {
          let categoryLength = categories.length;
          this.parsedEvents.forEach((ev) => {
            let category = ev.category;
            if (typeof category !== "string") {
              category = this.categoryForInvalid;
            }
            if (!category) {
              return;
            }
            if (category in categoryMap) {
              categoryMap[category].count++;
            } else if (!this.categoryHideDynamic) {
              categoryMap[category] = {
                index: categoryLength++,
                count: 1
              };
            }
          });
        }
        if (!this.categoryShowAll) {
          for (const category in categoryMap) {
            if (categoryMap[category].count === 0) {
              delete categoryMap[category];
            }
          }
        }
        categories = categories.filter((category) => {
          if (typeof category === "object" && category.categoryName) {
            return categoryMap.hasOwnProperty(category.categoryName);
          }
          return false;
        });
      }
      return categories;
    }
  },
  render(h) {
    const {
      start,
      end,
      maxDays,
      component,
      weekdays,
      categories
    } = this.renderProps;
    return h(component, {
      staticClass: "v-calendar",
      class: {
        "v-calendar-events": !this.noEvents
      },
      props: __spreadProps(__spreadValues({}, this.$props), {
        start: start.date,
        end: end.date,
        maxDays,
        weekdays,
        categories
      }),
      directives: [{
        modifiers: {
          quiet: true
        },
        name: "resize",
        value: this.updateEventVisibility
      }],
      on: __spreadProps(__spreadValues({}, this.$listeners), {
        "click:date": (day) => {
          if (this.$listeners.input) {
            this.$emit("input", day.date);
          }
          if (this.$listeners["click:date"]) {
            this.$emit("click:date", day);
          }
        }
      }),
      scopedSlots: this.getScopedSlots()
    });
  }
});
var VCard = mixins(Loadable, Routable, VSheet).extend({
  name: "v-card",
  props: {
    flat: Boolean,
    hover: Boolean,
    img: String,
    link: Boolean,
    loaderHeight: {
      type: [Number, String],
      default: 4
    },
    raised: Boolean
  },
  computed: {
    classes() {
      return __spreadValues(__spreadProps(__spreadValues({
        "v-card": true
      }, Routable.options.computed.classes.call(this)), {
        "v-card--flat": this.flat,
        "v-card--hover": this.hover,
        "v-card--link": this.isClickable,
        "v-card--loading": this.loading,
        "v-card--disabled": this.disabled,
        "v-card--raised": this.raised
      }), VSheet.options.computed.classes.call(this));
    },
    styles() {
      const style = __spreadValues({}, VSheet.options.computed.styles.call(this));
      if (this.img) {
        style.background = `url("${this.img}") center center / cover no-repeat`;
      }
      return style;
    }
  },
  methods: {
    genProgress() {
      const render = Loadable.options.methods.genProgress.call(this);
      if (!render)
        return null;
      return this.$createElement("div", {
        staticClass: "v-card__progress",
        key: "progress"
      }, [render]);
    }
  },
  render(h) {
    const {
      tag,
      data
    } = this.generateRouteLink();
    data.style = this.styles;
    if (this.isClickable) {
      data.attrs = data.attrs || {};
      data.attrs.tabindex = 0;
    }
    return h(tag, this.setBackgroundColor(this.color, data), [this.genProgress(), this.$slots.default]);
  }
});
const VCardActions = createSimpleFunctional("v-card__actions");
const VCardSubtitle = createSimpleFunctional("v-card__subtitle");
const VCardText = createSimpleFunctional("v-card__text");
const VCardTitle = createSimpleFunctional("v-card__title");
var VWindow = BaseItemGroup.extend({
  name: "v-window",
  directives: {
    Touch: Touch$1
  },
  provide() {
    return {
      windowGroup: this
    };
  },
  props: {
    activeClass: {
      type: String,
      default: "v-window-item--active"
    },
    continuous: Boolean,
    mandatory: {
      type: Boolean,
      default: true
    },
    nextIcon: {
      type: [Boolean, String],
      default: "$next"
    },
    prevIcon: {
      type: [Boolean, String],
      default: "$prev"
    },
    reverse: Boolean,
    showArrows: Boolean,
    showArrowsOnHover: Boolean,
    touch: Object,
    touchless: Boolean,
    value: {
      required: false
    },
    vertical: Boolean
  },
  data() {
    return {
      changedByDelimiters: false,
      internalHeight: void 0,
      transitionHeight: void 0,
      transitionCount: 0,
      isBooted: false,
      isReverse: false
    };
  },
  computed: {
    isActive() {
      return this.transitionCount > 0;
    },
    classes() {
      return __spreadProps(__spreadValues({}, BaseItemGroup.options.computed.classes.call(this)), {
        "v-window--show-arrows-on-hover": this.showArrowsOnHover
      });
    },
    computedTransition() {
      if (!this.isBooted)
        return "";
      const axis = this.vertical ? "y" : "x";
      const reverse = this.internalReverse ? !this.isReverse : this.isReverse;
      const direction = reverse ? "-reverse" : "";
      return `v-window-${axis}${direction}-transition`;
    },
    hasActiveItems() {
      return Boolean(this.items.find((item) => !item.disabled));
    },
    hasNext() {
      return this.continuous || this.internalIndex < this.items.length - 1;
    },
    hasPrev() {
      return this.continuous || this.internalIndex > 0;
    },
    internalIndex() {
      return this.items.findIndex((item, i) => {
        return this.internalValue === this.getValue(item, i);
      });
    },
    internalReverse() {
      return this.$vuetify.rtl ? !this.reverse : this.reverse;
    }
  },
  watch: {
    internalIndex(val, oldVal) {
      this.isReverse = this.updateReverse(val, oldVal);
    }
  },
  mounted() {
    window.requestAnimationFrame(() => this.isBooted = true);
  },
  methods: {
    genContainer() {
      const children = [this.$slots.default];
      if (this.showArrows) {
        children.push(this.genControlIcons());
      }
      return this.$createElement("div", {
        staticClass: "v-window__container",
        class: {
          "v-window__container--is-active": this.isActive
        },
        style: {
          height: this.internalHeight || this.transitionHeight
        }
      }, children);
    },
    genIcon(direction, icon, click) {
      var _ref;
      const on = {
        click: (e) => {
          e.stopPropagation();
          this.changedByDelimiters = true;
          click();
        }
      };
      const attrs = {
        "aria-label": this.$vuetify.lang.t(`$vuetify.carousel.${direction}`)
      };
      const children = (_ref = this.$scopedSlots[direction] == null ? void 0 : this.$scopedSlots[direction]({
        on,
        attrs
      })) != null ? _ref : [this.$createElement(VBtn, {
        props: {
          icon: true
        },
        attrs,
        on
      }, [this.$createElement(VIcon$1, {
        props: {
          large: true
        }
      }, icon)])];
      return this.$createElement("div", {
        staticClass: `v-window__${direction}`
      }, children);
    },
    genControlIcons() {
      const icons2 = [];
      const prevIcon = this.$vuetify.rtl ? this.nextIcon : this.prevIcon;
      if (this.hasPrev && prevIcon && typeof prevIcon === "string") {
        const icon = this.genIcon("prev", prevIcon, this.prev);
        icon && icons2.push(icon);
      }
      const nextIcon = this.$vuetify.rtl ? this.prevIcon : this.nextIcon;
      if (this.hasNext && nextIcon && typeof nextIcon === "string") {
        const icon = this.genIcon("next", nextIcon, this.next);
        icon && icons2.push(icon);
      }
      return icons2;
    },
    getNextIndex(index) {
      const nextIndex = (index + 1) % this.items.length;
      const item = this.items[nextIndex];
      if (item.disabled)
        return this.getNextIndex(nextIndex);
      return nextIndex;
    },
    getPrevIndex(index) {
      const prevIndex = (index + this.items.length - 1) % this.items.length;
      const item = this.items[prevIndex];
      if (item.disabled)
        return this.getPrevIndex(prevIndex);
      return prevIndex;
    },
    next() {
      if (!this.hasActiveItems || !this.hasNext)
        return;
      const nextIndex = this.getNextIndex(this.internalIndex);
      const item = this.items[nextIndex];
      this.internalValue = this.getValue(item, nextIndex);
    },
    prev() {
      if (!this.hasActiveItems || !this.hasPrev)
        return;
      const lastIndex = this.getPrevIndex(this.internalIndex);
      const item = this.items[lastIndex];
      this.internalValue = this.getValue(item, lastIndex);
    },
    updateReverse(val, oldVal) {
      const itemsLength = this.items.length;
      const lastIndex = itemsLength - 1;
      if (itemsLength <= 2)
        return val < oldVal;
      if (val === lastIndex && oldVal === 0) {
        return true;
      } else if (val === 0 && oldVal === lastIndex) {
        return false;
      } else {
        return val < oldVal;
      }
    }
  },
  render(h) {
    const data = {
      staticClass: "v-window",
      class: this.classes,
      directives: []
    };
    if (!this.touchless) {
      const value = this.touch || {
        left: () => {
          this.$vuetify.rtl ? this.prev() : this.next();
        },
        right: () => {
          this.$vuetify.rtl ? this.next() : this.prev();
        },
        end: (e) => {
          e.stopPropagation();
        },
        start: (e) => {
          e.stopPropagation();
        }
      };
      data.directives.push({
        name: "touch",
        value
      });
    }
    return h("div", data, [this.genContainer()]);
  }
});
var VCarousel = VWindow.extend({
  name: "v-carousel",
  props: {
    continuous: {
      type: Boolean,
      default: true
    },
    cycle: Boolean,
    delimiterIcon: {
      type: String,
      default: "$delimiter"
    },
    height: {
      type: [Number, String],
      default: 500
    },
    hideDelimiters: Boolean,
    hideDelimiterBackground: Boolean,
    interval: {
      type: [Number, String],
      default: 6e3,
      validator: (value) => value > 0
    },
    mandatory: {
      type: Boolean,
      default: true
    },
    progress: Boolean,
    progressColor: String,
    showArrows: {
      type: Boolean,
      default: true
    },
    verticalDelimiters: {
      type: String,
      default: void 0
    }
  },
  data() {
    return {
      internalHeight: this.height,
      slideTimeout: void 0
    };
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VWindow.options.computed.classes.call(this)), {
        "v-carousel": true,
        "v-carousel--hide-delimiter-background": this.hideDelimiterBackground,
        "v-carousel--vertical-delimiters": this.isVertical
      });
    },
    isDark() {
      return this.dark || !this.light;
    },
    isVertical() {
      return this.verticalDelimiters != null;
    }
  },
  watch: {
    internalValue: "restartTimeout",
    interval: "restartTimeout",
    height(val, oldVal) {
      if (val === oldVal || !val)
        return;
      this.internalHeight = val;
    },
    cycle(val) {
      if (val) {
        this.restartTimeout();
      } else {
        clearTimeout(this.slideTimeout);
        this.slideTimeout = void 0;
      }
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("hide-controls")) {
      breaking("hide-controls", ':show-arrows="false"', this);
    }
  },
  mounted() {
    this.startTimeout();
  },
  methods: {
    genControlIcons() {
      if (this.isVertical)
        return null;
      return VWindow.options.methods.genControlIcons.call(this);
    },
    genDelimiters() {
      return this.$createElement("div", {
        staticClass: "v-carousel__controls",
        style: {
          left: this.verticalDelimiters === "left" && this.isVertical ? 0 : "auto",
          right: this.verticalDelimiters === "right" ? 0 : "auto"
        }
      }, [this.genItems()]);
    },
    genItems() {
      const length = this.items.length;
      const children = [];
      for (let i = 0; i < length; i++) {
        const child = this.$createElement(VBtn, {
          staticClass: "v-carousel__controls__item",
          attrs: {
            "aria-label": this.$vuetify.lang.t("$vuetify.carousel.ariaLabel.delimiter", i + 1, length)
          },
          props: {
            icon: true,
            small: true,
            value: this.getValue(this.items[i], i)
          }
        }, [this.$createElement(VIcon$1, {
          props: {
            size: 18
          }
        }, this.delimiterIcon)]);
        children.push(child);
      }
      return this.$createElement(ButtonGroup, {
        props: {
          value: this.internalValue,
          mandatory: this.mandatory
        },
        on: {
          change: (val) => {
            this.internalValue = val;
          }
        }
      }, children);
    },
    genProgress() {
      return this.$createElement(VProgressLinear, {
        staticClass: "v-carousel__progress",
        props: {
          color: this.progressColor,
          value: (this.internalIndex + 1) / this.items.length * 100
        }
      });
    },
    restartTimeout() {
      this.slideTimeout && clearTimeout(this.slideTimeout);
      this.slideTimeout = void 0;
      window.requestAnimationFrame(this.startTimeout);
    },
    startTimeout() {
      if (!this.cycle)
        return;
      this.slideTimeout = window.setTimeout(this.next, +this.interval > 0 ? +this.interval : 6e3);
    }
  },
  render(h) {
    const render = VWindow.options.render.call(this, h);
    render.data.style = `height: ${convertToUnit(this.height)};`;
    if (!this.hideDelimiters) {
      render.children.push(this.genDelimiters());
    }
    if (this.progress || this.progressColor) {
      render.children.push(this.genProgress());
    }
    return render;
  }
});
const baseMixins$g = mixins(Bootable, factory$1("windowGroup", "v-window-item", "v-window"));
var VWindowItem = baseMixins$g.extend().extend().extend({
  name: "v-window-item",
  directives: {
    Touch: Touch$1
  },
  props: {
    disabled: Boolean,
    reverseTransition: {
      type: [Boolean, String],
      default: void 0
    },
    transition: {
      type: [Boolean, String],
      default: void 0
    },
    value: {
      required: false
    }
  },
  data() {
    return {
      isActive: false,
      inTransition: false
    };
  },
  computed: {
    classes() {
      return this.groupClasses;
    },
    computedTransition() {
      if (!this.windowGroup.internalReverse) {
        return typeof this.transition !== "undefined" ? this.transition || "" : this.windowGroup.computedTransition;
      }
      return typeof this.reverseTransition !== "undefined" ? this.reverseTransition || "" : this.windowGroup.computedTransition;
    }
  },
  methods: {
    genDefaultSlot() {
      return this.$slots.default;
    },
    genWindowItem() {
      return this.$createElement("div", {
        staticClass: "v-window-item",
        class: this.classes,
        directives: [{
          name: "show",
          value: this.isActive
        }],
        on: this.$listeners
      }, this.genDefaultSlot());
    },
    onAfterTransition() {
      if (!this.inTransition) {
        return;
      }
      this.inTransition = false;
      if (this.windowGroup.transitionCount > 0) {
        this.windowGroup.transitionCount--;
        if (this.windowGroup.transitionCount === 0) {
          this.windowGroup.transitionHeight = void 0;
        }
      }
    },
    onBeforeTransition() {
      if (this.inTransition) {
        return;
      }
      this.inTransition = true;
      if (this.windowGroup.transitionCount === 0) {
        this.windowGroup.transitionHeight = convertToUnit(this.windowGroup.$el.clientHeight);
      }
      this.windowGroup.transitionCount++;
    },
    onTransitionCancelled() {
      this.onAfterTransition();
    },
    onEnter(el) {
      if (!this.inTransition) {
        return;
      }
      this.$nextTick(() => {
        if (!this.computedTransition || !this.inTransition) {
          return;
        }
        this.windowGroup.transitionHeight = convertToUnit(el.clientHeight);
      });
    }
  },
  render(h) {
    return h("transition", {
      props: {
        name: this.computedTransition
      },
      on: {
        beforeEnter: this.onBeforeTransition,
        afterEnter: this.onAfterTransition,
        enterCancelled: this.onTransitionCancelled,
        beforeLeave: this.onBeforeTransition,
        afterLeave: this.onAfterTransition,
        leaveCancelled: this.onTransitionCancelled,
        enter: this.onEnter
      }
    }, this.showLazyContent(() => [this.genWindowItem()]));
  }
});
const baseMixins$f = mixins(VWindowItem, Routable);
var VCarouselItem = baseMixins$f.extend({
  name: "v-carousel-item",
  inheritAttrs: false,
  methods: {
    genDefaultSlot() {
      return [this.$createElement(VImg, {
        staticClass: "v-carousel__item",
        props: __spreadProps(__spreadValues({}, this.$attrs), {
          height: this.windowGroup.internalHeight
        }),
        on: this.$listeners,
        scopedSlots: {
          placeholder: this.$scopedSlots.placeholder
        }
      }, getSlot(this))];
    },
    genWindowItem() {
      const {
        tag,
        data
      } = this.generateRouteLink();
      data.staticClass = "v-window-item";
      data.directives.push({
        name: "show",
        value: this.isActive
      });
      return this.$createElement(tag, data, this.genDefaultSlot());
    }
  }
});
var Rippleable = Vue.extend({
  name: "rippleable",
  directives: {
    ripple
  },
  props: {
    ripple: {
      type: [Boolean, Object],
      default: true
    }
  },
  methods: {
    genRipple(data = {}) {
      if (!this.ripple)
        return null;
      data.staticClass = "v-input--selection-controls__ripple";
      data.directives = data.directives || [];
      data.directives.push({
        name: "ripple",
        value: {
          center: true
        }
      });
      return this.$createElement("div", data);
    }
  }
});
function prevent(e) {
  e.preventDefault();
}
var Selectable = mixins(VInput, Rippleable, Comparable).extend({
  name: "selectable",
  model: {
    prop: "inputValue",
    event: "change"
  },
  props: {
    id: String,
    inputValue: null,
    falseValue: null,
    trueValue: null,
    multiple: {
      type: Boolean,
      default: null
    },
    label: String
  },
  data() {
    return {
      hasColor: this.inputValue,
      lazyValue: this.inputValue
    };
  },
  computed: {
    computedColor() {
      if (!this.isActive)
        return void 0;
      if (this.color)
        return this.color;
      if (this.isDark && !this.appIsDark)
        return "white";
      return "primary";
    },
    isMultiple() {
      return this.multiple === true || this.multiple === null && Array.isArray(this.internalValue);
    },
    isActive() {
      const value = this.value;
      const input = this.internalValue;
      if (this.isMultiple) {
        if (!Array.isArray(input))
          return false;
        return input.some((item) => this.valueComparator(item, value));
      }
      if (this.trueValue === void 0 || this.falseValue === void 0) {
        return value ? this.valueComparator(value, input) : Boolean(input);
      }
      return this.valueComparator(input, this.trueValue);
    },
    isDirty() {
      return this.isActive;
    },
    rippleState() {
      return !this.isDisabled && !this.validationState ? void 0 : this.validationState;
    }
  },
  watch: {
    inputValue(val) {
      this.lazyValue = val;
      this.hasColor = val;
    }
  },
  methods: {
    genLabel() {
      const label = VInput.options.methods.genLabel.call(this);
      if (!label)
        return label;
      label.data.on = {
        click: prevent
      };
      return label;
    },
    genInput(type2, attrs) {
      return this.$createElement("input", {
        attrs: Object.assign({
          "aria-checked": this.isActive.toString(),
          disabled: this.isDisabled,
          id: this.computedId,
          role: type2,
          type: type2
        }, attrs),
        domProps: {
          value: this.value,
          checked: this.isActive
        },
        on: {
          blur: this.onBlur,
          change: this.onChange,
          focus: this.onFocus,
          keydown: this.onKeydown,
          click: prevent
        },
        ref: "input"
      });
    },
    onBlur() {
      this.isFocused = false;
    },
    onClick(e) {
      this.onChange();
      this.$emit("click", e);
    },
    onChange() {
      if (!this.isInteractive)
        return;
      const value = this.value;
      let input = this.internalValue;
      if (this.isMultiple) {
        if (!Array.isArray(input)) {
          input = [];
        }
        const length = input.length;
        input = input.filter((item) => !this.valueComparator(item, value));
        if (input.length === length) {
          input.push(value);
        }
      } else if (this.trueValue !== void 0 && this.falseValue !== void 0) {
        input = this.valueComparator(input, this.trueValue) ? this.falseValue : this.trueValue;
      } else if (value) {
        input = this.valueComparator(input, value) ? null : value;
      } else {
        input = !input;
      }
      this.validate(true, input);
      this.internalValue = input;
      this.hasColor = input;
    },
    onFocus() {
      this.isFocused = true;
    },
    onKeydown(e) {
    }
  }
});
var VCheckbox = Selectable.extend({
  name: "v-checkbox",
  props: {
    indeterminate: Boolean,
    indeterminateIcon: {
      type: String,
      default: "$checkboxIndeterminate"
    },
    offIcon: {
      type: String,
      default: "$checkboxOff"
    },
    onIcon: {
      type: String,
      default: "$checkboxOn"
    }
  },
  data() {
    return {
      inputIndeterminate: this.indeterminate
    };
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VInput.options.computed.classes.call(this)), {
        "v-input--selection-controls": true,
        "v-input--checkbox": true,
        "v-input--indeterminate": this.inputIndeterminate
      });
    },
    computedIcon() {
      if (this.inputIndeterminate) {
        return this.indeterminateIcon;
      } else if (this.isActive) {
        return this.onIcon;
      } else {
        return this.offIcon;
      }
    },
    validationState() {
      if (this.isDisabled && !this.inputIndeterminate)
        return void 0;
      if (this.hasError && this.shouldValidate)
        return "error";
      if (this.hasSuccess)
        return "success";
      if (this.hasColor !== null)
        return this.computedColor;
      return void 0;
    }
  },
  watch: {
    indeterminate(val) {
      this.$nextTick(() => this.inputIndeterminate = val);
    },
    inputIndeterminate(val) {
      this.$emit("update:indeterminate", val);
    },
    isActive() {
      if (!this.indeterminate)
        return;
      this.inputIndeterminate = false;
    }
  },
  methods: {
    genCheckbox() {
      return this.$createElement("div", {
        staticClass: "v-input--selection-controls__input"
      }, [this.$createElement(VIcon$1, this.setTextColor(this.validationState, {
        props: {
          dense: this.dense,
          dark: this.dark,
          light: this.light
        }
      }), this.computedIcon), this.genInput("checkbox", __spreadProps(__spreadValues({}, this.attrs$), {
        "aria-checked": this.inputIndeterminate ? "mixed" : this.isActive.toString()
      })), this.genRipple(this.setTextColor(this.rippleState))]);
    },
    genDefaultSlot() {
      return [this.genCheckbox(), this.genLabel()];
    }
  }
});
const BaseSlideGroup = mixins(BaseItemGroup, Mobile).extend({
  name: "base-slide-group",
  directives: {
    Resize: Resize$1,
    Touch: Touch$1
  },
  props: {
    activeClass: {
      type: String,
      default: "v-slide-item--active"
    },
    centerActive: Boolean,
    nextIcon: {
      type: String,
      default: "$next"
    },
    prevIcon: {
      type: String,
      default: "$prev"
    },
    showArrows: {
      type: [Boolean, String],
      validator: (v) => typeof v === "boolean" || ["always", "desktop", "mobile"].includes(v)
    }
  },
  data: () => ({
    internalItemsLength: 0,
    isOverflowing: false,
    resizeTimeout: 0,
    startX: 0,
    scrollOffset: 0,
    widths: {
      content: 0,
      wrapper: 0
    }
  }),
  computed: {
    __cachedNext() {
      return this.genTransition("next");
    },
    __cachedPrev() {
      return this.genTransition("prev");
    },
    classes() {
      return __spreadProps(__spreadValues({}, BaseItemGroup.options.computed.classes.call(this)), {
        "v-slide-group": true,
        "v-slide-group--has-affixes": this.hasAffixes,
        "v-slide-group--is-overflowing": this.isOverflowing
      });
    },
    hasAffixes() {
      switch (this.showArrows) {
        case "always":
          return true;
        case "desktop":
          return !this.isMobile;
        case true:
          return this.isOverflowing || Math.abs(this.scrollOffset) > 0;
        case "mobile":
          return this.isMobile || this.isOverflowing || Math.abs(this.scrollOffset) > 0;
        default:
          return !this.isMobile && (this.isOverflowing || Math.abs(this.scrollOffset) > 0);
      }
    },
    hasNext() {
      if (!this.hasAffixes)
        return false;
      const {
        content,
        wrapper
      } = this.widths;
      return content > Math.abs(this.scrollOffset) + wrapper;
    },
    hasPrev() {
      return this.hasAffixes && this.scrollOffset !== 0;
    }
  },
  watch: {
    internalValue: "setWidths",
    isOverflowing: "setWidths",
    scrollOffset(val) {
      this.$refs.content.style.transform = `translateX(${-val}px)`;
    }
  },
  beforeUpdate() {
    this.internalItemsLength = (this.$children || []).length;
  },
  updated() {
    if (this.internalItemsLength === (this.$children || []).length)
      return;
    this.setWidths();
  },
  methods: {
    genNext() {
      const slot = this.$scopedSlots.next ? this.$scopedSlots.next({}) : this.$slots.next || this.__cachedNext;
      return this.$createElement("div", {
        staticClass: "v-slide-group__next",
        class: {
          "v-slide-group__next--disabled": !this.hasNext
        },
        on: {
          click: () => this.onAffixClick("next")
        },
        key: "next"
      }, [slot]);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-slide-group__content",
        ref: "content"
      }, this.$slots.default);
    },
    genData() {
      return {
        class: this.classes,
        directives: [{
          name: "resize",
          value: this.onResize
        }]
      };
    },
    genIcon(location) {
      let icon = location;
      if (this.$vuetify.rtl && location === "prev") {
        icon = "next";
      } else if (this.$vuetify.rtl && location === "next") {
        icon = "prev";
      }
      const upperLocation = `${location[0].toUpperCase()}${location.slice(1)}`;
      const hasAffix = this[`has${upperLocation}`];
      if (!this.showArrows && !hasAffix)
        return null;
      return this.$createElement(VIcon$1, {
        props: {
          disabled: !hasAffix
        }
      }, this[`${icon}Icon`]);
    },
    genPrev() {
      const slot = this.$scopedSlots.prev ? this.$scopedSlots.prev({}) : this.$slots.prev || this.__cachedPrev;
      return this.$createElement("div", {
        staticClass: "v-slide-group__prev",
        class: {
          "v-slide-group__prev--disabled": !this.hasPrev
        },
        on: {
          click: () => this.onAffixClick("prev")
        },
        key: "prev"
      }, [slot]);
    },
    genTransition(location) {
      return this.$createElement(VFadeTransition, [this.genIcon(location)]);
    },
    genWrapper() {
      return this.$createElement("div", {
        staticClass: "v-slide-group__wrapper",
        directives: [{
          name: "touch",
          value: {
            start: (e) => this.overflowCheck(e, this.onTouchStart),
            move: (e) => this.overflowCheck(e, this.onTouchMove),
            end: (e) => this.overflowCheck(e, this.onTouchEnd)
          }
        }],
        ref: "wrapper"
      }, [this.genContent()]);
    },
    calculateNewOffset(direction, widths, rtl, currentScrollOffset) {
      const sign = rtl ? -1 : 1;
      const newAbosluteOffset = sign * currentScrollOffset + (direction === "prev" ? -1 : 1) * widths.wrapper;
      return sign * Math.max(Math.min(newAbosluteOffset, widths.content - widths.wrapper), 0);
    },
    onAffixClick(location) {
      this.$emit(`click:${location}`);
      this.scrollTo(location);
    },
    onResize() {
      if (this._isDestroyed)
        return;
      this.setWidths();
    },
    onTouchStart(e) {
      const {
        content
      } = this.$refs;
      this.startX = this.scrollOffset + e.touchstartX;
      content.style.setProperty("transition", "none");
      content.style.setProperty("willChange", "transform");
    },
    onTouchMove(e) {
      this.scrollOffset = this.startX - e.touchmoveX;
    },
    onTouchEnd() {
      const {
        content,
        wrapper
      } = this.$refs;
      const maxScrollOffset = content.clientWidth - wrapper.clientWidth;
      content.style.setProperty("transition", null);
      content.style.setProperty("willChange", null);
      if (this.$vuetify.rtl) {
        if (this.scrollOffset > 0 || !this.isOverflowing) {
          this.scrollOffset = 0;
        } else if (this.scrollOffset <= -maxScrollOffset) {
          this.scrollOffset = -maxScrollOffset;
        }
      } else {
        if (this.scrollOffset < 0 || !this.isOverflowing) {
          this.scrollOffset = 0;
        } else if (this.scrollOffset >= maxScrollOffset) {
          this.scrollOffset = maxScrollOffset;
        }
      }
    },
    overflowCheck(e, fn) {
      e.stopPropagation();
      this.isOverflowing && fn(e);
    },
    scrollIntoView() {
      if (!this.selectedItem && this.items.length) {
        const lastItemPosition = this.items[this.items.length - 1].$el.getBoundingClientRect();
        const wrapperPosition = this.$refs.wrapper.getBoundingClientRect();
        if (this.$vuetify.rtl && wrapperPosition.right < lastItemPosition.right || !this.$vuetify.rtl && wrapperPosition.left > lastItemPosition.left) {
          this.scrollTo("prev");
        }
      }
      if (!this.selectedItem) {
        return;
      }
      if (this.selectedIndex === 0 || !this.centerActive && !this.isOverflowing) {
        this.scrollOffset = 0;
      } else if (this.centerActive) {
        this.scrollOffset = this.calculateCenteredOffset(this.selectedItem.$el, this.widths, this.$vuetify.rtl);
      } else if (this.isOverflowing) {
        this.scrollOffset = this.calculateUpdatedOffset(this.selectedItem.$el, this.widths, this.$vuetify.rtl, this.scrollOffset);
      }
    },
    calculateUpdatedOffset(selectedElement, widths, rtl, currentScrollOffset) {
      const clientWidth = selectedElement.clientWidth;
      const offsetLeft = rtl ? widths.content - selectedElement.offsetLeft - clientWidth : selectedElement.offsetLeft;
      if (rtl) {
        currentScrollOffset = -currentScrollOffset;
      }
      const totalWidth = widths.wrapper + currentScrollOffset;
      const itemOffset = clientWidth + offsetLeft;
      const additionalOffset = clientWidth * 0.4;
      if (offsetLeft <= currentScrollOffset) {
        currentScrollOffset = Math.max(offsetLeft - additionalOffset, 0);
      } else if (totalWidth <= itemOffset) {
        currentScrollOffset = Math.min(currentScrollOffset - (totalWidth - itemOffset - additionalOffset), widths.content - widths.wrapper);
      }
      return rtl ? -currentScrollOffset : currentScrollOffset;
    },
    calculateCenteredOffset(selectedElement, widths, rtl) {
      const {
        offsetLeft,
        clientWidth
      } = selectedElement;
      if (rtl) {
        const offsetCentered = widths.content - offsetLeft - clientWidth / 2 - widths.wrapper / 2;
        return -Math.min(widths.content - widths.wrapper, Math.max(0, offsetCentered));
      } else {
        const offsetCentered = offsetLeft + clientWidth / 2 - widths.wrapper / 2;
        return Math.min(widths.content - widths.wrapper, Math.max(0, offsetCentered));
      }
    },
    scrollTo(location) {
      this.scrollOffset = this.calculateNewOffset(location, {
        content: this.$refs.content ? this.$refs.content.clientWidth : 0,
        wrapper: this.$refs.wrapper ? this.$refs.wrapper.clientWidth : 0
      }, this.$vuetify.rtl, this.scrollOffset);
    },
    setWidths() {
      window.requestAnimationFrame(() => {
        const {
          content,
          wrapper
        } = this.$refs;
        this.widths = {
          content: content ? content.clientWidth : 0,
          wrapper: wrapper ? wrapper.clientWidth : 0
        };
        this.isOverflowing = this.widths.wrapper + 1 < this.widths.content;
        this.scrollIntoView();
      });
    }
  },
  render(h) {
    return h("div", this.genData(), [this.genPrev(), this.genWrapper(), this.genNext()]);
  }
});
var VSlideGroup = BaseSlideGroup.extend({
  name: "v-slide-group",
  provide() {
    return {
      slideGroup: this
    };
  }
});
var VChipGroup = mixins(BaseSlideGroup, Colorable).extend({
  name: "v-chip-group",
  provide() {
    return {
      chipGroup: this
    };
  },
  props: {
    column: Boolean
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, BaseSlideGroup.options.computed.classes.call(this)), {
        "v-chip-group": true,
        "v-chip-group--column": this.column
      });
    }
  },
  watch: {
    column(val) {
      if (val)
        this.scrollOffset = 0;
      this.$nextTick(this.onResize);
    }
  },
  methods: {
    genData() {
      return this.setTextColor(this.color, __spreadValues({}, BaseSlideGroup.options.methods.genData.call(this)));
    }
  }
});
var VSlider = mixins(VInput, Loadable).extend({
  name: "v-slider",
  directives: {
    ClickOutside: ClickOutside$1
  },
  mixins: [Loadable],
  props: {
    disabled: Boolean,
    inverseLabel: Boolean,
    max: {
      type: [Number, String],
      default: 100
    },
    min: {
      type: [Number, String],
      default: 0
    },
    step: {
      type: [Number, String],
      default: 1
    },
    thumbColor: String,
    thumbLabel: {
      type: [Boolean, String],
      default: void 0,
      validator: (v) => typeof v === "boolean" || v === "always"
    },
    thumbSize: {
      type: [Number, String],
      default: 32
    },
    tickLabels: {
      type: Array,
      default: () => []
    },
    ticks: {
      type: [Boolean, String],
      default: false,
      validator: (v) => typeof v === "boolean" || v === "always"
    },
    tickSize: {
      type: [Number, String],
      default: 2
    },
    trackColor: String,
    trackFillColor: String,
    value: [Number, String],
    vertical: Boolean
  },
  data: () => ({
    app: null,
    oldValue: null,
    thumbPressed: false,
    mouseTimeout: -1,
    isFocused: false,
    isActive: false,
    noClick: false
  }),
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VInput.options.computed.classes.call(this)), {
        "v-input__slider": true,
        "v-input__slider--vertical": this.vertical,
        "v-input__slider--inverse-label": this.inverseLabel
      });
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        val = isNaN(val) ? this.minValue : val;
        const value = this.roundValue(Math.min(Math.max(val, this.minValue), this.maxValue));
        if (value === this.lazyValue)
          return;
        this.lazyValue = value;
        this.$emit("input", value);
      }
    },
    trackTransition() {
      return this.thumbPressed ? this.showTicks || this.stepNumeric ? "0.1s cubic-bezier(0.25, 0.8, 0.5, 1)" : "none" : "";
    },
    minValue() {
      return parseFloat(this.min);
    },
    maxValue() {
      return parseFloat(this.max);
    },
    stepNumeric() {
      return this.step > 0 ? parseFloat(this.step) : 0;
    },
    inputWidth() {
      return (this.roundValue(this.internalValue) - this.minValue) / (this.maxValue - this.minValue) * 100;
    },
    trackFillStyles() {
      const startDir = this.vertical ? "bottom" : "left";
      const endDir = this.vertical ? "top" : "right";
      const valueDir = this.vertical ? "height" : "width";
      const start = this.$vuetify.rtl ? "auto" : "0";
      const end = this.$vuetify.rtl ? "0" : "auto";
      const value = this.isDisabled ? `calc(${this.inputWidth}% - 10px)` : `${this.inputWidth}%`;
      return {
        transition: this.trackTransition,
        [startDir]: start,
        [endDir]: end,
        [valueDir]: value
      };
    },
    trackStyles() {
      const startDir = this.vertical ? this.$vuetify.rtl ? "bottom" : "top" : this.$vuetify.rtl ? "left" : "right";
      const endDir = this.vertical ? "height" : "width";
      const start = "0px";
      const end = this.isDisabled ? `calc(${100 - this.inputWidth}% - 10px)` : `calc(${100 - this.inputWidth}%)`;
      return {
        transition: this.trackTransition,
        [startDir]: start,
        [endDir]: end
      };
    },
    showTicks() {
      return this.tickLabels.length > 0 || !!(!this.isDisabled && this.stepNumeric && this.ticks);
    },
    numTicks() {
      return Math.ceil((this.maxValue - this.minValue) / this.stepNumeric);
    },
    showThumbLabel() {
      return !this.isDisabled && !!(this.thumbLabel || this.$scopedSlots["thumb-label"]);
    },
    computedTrackColor() {
      if (this.isDisabled)
        return void 0;
      if (this.trackColor)
        return this.trackColor;
      if (this.isDark)
        return this.validationState;
      return this.validationState || "primary lighten-3";
    },
    computedTrackFillColor() {
      if (this.isDisabled)
        return void 0;
      if (this.trackFillColor)
        return this.trackFillColor;
      return this.validationState || this.computedColor;
    },
    computedThumbColor() {
      if (this.thumbColor)
        return this.thumbColor;
      return this.validationState || this.computedColor;
    }
  },
  watch: {
    min(val) {
      const parsed = parseFloat(val);
      parsed > this.internalValue && this.$emit("input", parsed);
    },
    max(val) {
      const parsed = parseFloat(val);
      parsed < this.internalValue && this.$emit("input", parsed);
    },
    value: {
      handler(v) {
        this.internalValue = v;
      }
    }
  },
  beforeMount() {
    this.internalValue = this.value;
  },
  mounted() {
    this.app = document.querySelector("[data-app]") || consoleWarn("Missing v-app or a non-body wrapping element with the [data-app] attribute", this);
  },
  methods: {
    genDefaultSlot() {
      const children = [this.genLabel()];
      const slider = this.genSlider();
      this.inverseLabel ? children.unshift(slider) : children.push(slider);
      children.push(this.genProgress());
      return children;
    },
    genSlider() {
      return this.$createElement("div", {
        class: __spreadValues({
          "v-slider": true,
          "v-slider--horizontal": !this.vertical,
          "v-slider--vertical": this.vertical,
          "v-slider--focused": this.isFocused,
          "v-slider--active": this.isActive,
          "v-slider--disabled": this.isDisabled,
          "v-slider--readonly": this.isReadonly
        }, this.themeClasses),
        directives: [{
          name: "click-outside",
          value: this.onBlur
        }],
        on: {
          click: this.onSliderClick,
          mousedown: this.onSliderMouseDown,
          touchstart: this.onSliderMouseDown
        }
      }, this.genChildren());
    },
    genChildren() {
      return [this.genInput(), this.genTrackContainer(), this.genSteps(), this.genThumbContainer(this.internalValue, this.inputWidth, this.isActive, this.isFocused, this.onFocus, this.onBlur)];
    },
    genInput() {
      return this.$createElement("input", {
        attrs: __spreadValues({
          value: this.internalValue,
          id: this.computedId,
          disabled: true,
          readonly: true,
          tabindex: -1
        }, this.$attrs)
      });
    },
    genTrackContainer() {
      const children = [this.$createElement("div", this.setBackgroundColor(this.computedTrackColor, {
        staticClass: "v-slider__track-background",
        style: this.trackStyles
      })), this.$createElement("div", this.setBackgroundColor(this.computedTrackFillColor, {
        staticClass: "v-slider__track-fill",
        style: this.trackFillStyles
      }))];
      return this.$createElement("div", {
        staticClass: "v-slider__track-container",
        ref: "track"
      }, children);
    },
    genSteps() {
      if (!this.step || !this.showTicks)
        return null;
      const tickSize = parseFloat(this.tickSize);
      const range = createRange(this.numTicks + 1);
      const direction = this.vertical ? "bottom" : this.$vuetify.rtl ? "right" : "left";
      const offsetDirection = this.vertical ? this.$vuetify.rtl ? "left" : "right" : "top";
      if (this.vertical)
        range.reverse();
      const ticks = range.map((index) => {
        const children = [];
        if (this.tickLabels[index]) {
          children.push(this.$createElement("div", {
            staticClass: "v-slider__tick-label"
          }, this.tickLabels[index]));
        }
        const width = index * (100 / this.numTicks);
        const filled = this.$vuetify.rtl ? 100 - this.inputWidth < width : width < this.inputWidth;
        return this.$createElement("span", {
          key: index,
          staticClass: "v-slider__tick",
          class: {
            "v-slider__tick--filled": filled
          },
          style: {
            width: `${tickSize}px`,
            height: `${tickSize}px`,
            [direction]: `calc(${width}% - ${tickSize / 2}px)`,
            [offsetDirection]: `calc(50% - ${tickSize / 2}px)`
          }
        }, children);
      });
      return this.$createElement("div", {
        staticClass: "v-slider__ticks-container",
        class: {
          "v-slider__ticks-container--always-show": this.ticks === "always" || this.tickLabels.length > 0
        }
      }, ticks);
    },
    genThumbContainer(value, valueWidth, isActive, isFocused, onFocus, onBlur, ref = "thumb") {
      const children = [this.genThumb()];
      const thumbLabelContent = this.genThumbLabelContent(value);
      this.showThumbLabel && children.push(this.genThumbLabel(thumbLabelContent));
      return this.$createElement("div", this.setTextColor(this.computedThumbColor, {
        ref,
        key: ref,
        staticClass: "v-slider__thumb-container",
        class: {
          "v-slider__thumb-container--active": isActive,
          "v-slider__thumb-container--focused": isFocused,
          "v-slider__thumb-container--show-label": this.showThumbLabel
        },
        style: this.getThumbContainerStyles(valueWidth),
        attrs: __spreadValues({
          role: "slider",
          tabindex: this.isDisabled ? -1 : this.$attrs.tabindex ? this.$attrs.tabindex : 0,
          "aria-label": this.label,
          "aria-valuemin": this.min,
          "aria-valuemax": this.max,
          "aria-valuenow": this.internalValue,
          "aria-readonly": String(this.isReadonly),
          "aria-orientation": this.vertical ? "vertical" : "horizontal"
        }, this.$attrs),
        on: {
          focus: onFocus,
          blur: onBlur,
          keydown: this.onKeyDown
        }
      }), children);
    },
    genThumbLabelContent(value) {
      return this.$scopedSlots["thumb-label"] ? this.$scopedSlots["thumb-label"]({
        value
      }) : [this.$createElement("span", [String(value)])];
    },
    genThumbLabel(content) {
      const size = convertToUnit(this.thumbSize);
      const transform2 = this.vertical ? `translateY(20%) translateY(${Number(this.thumbSize) / 3 - 1}px) translateX(55%) rotate(135deg)` : `translateY(-20%) translateY(-12px) translateX(-50%) rotate(45deg)`;
      return this.$createElement(VScaleTransition, {
        props: {
          origin: "bottom center"
        }
      }, [this.$createElement("div", {
        staticClass: "v-slider__thumb-label-container",
        directives: [{
          name: "show",
          value: this.isFocused || this.isActive || this.thumbLabel === "always"
        }]
      }, [this.$createElement("div", this.setBackgroundColor(this.computedThumbColor, {
        staticClass: "v-slider__thumb-label",
        style: {
          height: size,
          width: size,
          transform: transform2
        }
      }), [this.$createElement("div", content)])])]);
    },
    genThumb() {
      return this.$createElement("div", this.setBackgroundColor(this.computedThumbColor, {
        staticClass: "v-slider__thumb"
      }));
    },
    getThumbContainerStyles(width) {
      const direction = this.vertical ? "top" : "left";
      let value = this.$vuetify.rtl ? 100 - width : width;
      value = this.vertical ? 100 - value : value;
      return {
        transition: this.trackTransition,
        [direction]: `${value}%`
      };
    },
    onSliderMouseDown(e) {
      var _e$target;
      e.preventDefault();
      this.oldValue = this.internalValue;
      this.isActive = true;
      const mouseUpOptions = passiveSupported ? {
        passive: true,
        capture: true
      } : true;
      const mouseMoveOptions = passiveSupported ? {
        passive: true
      } : false;
      if ((_e$target = e.target) == null ? void 0 : _e$target.matches(".v-slider__thumb-container, .v-slider__thumb-container *")) {
        this.thumbPressed = true;
      } else {
        window.clearTimeout(this.mouseTimeout);
        this.mouseTimeout = window.setTimeout(() => {
          this.thumbPressed = true;
        }, 300);
      }
      const isTouchEvent2 = "touches" in e;
      this.onMouseMove(e);
      this.app.addEventListener(isTouchEvent2 ? "touchmove" : "mousemove", this.onMouseMove, mouseMoveOptions);
      addOnceEventListener(this.app, isTouchEvent2 ? "touchend" : "mouseup", this.onSliderMouseUp, mouseUpOptions);
      this.$emit("start", this.internalValue);
    },
    onSliderMouseUp(e) {
      e.stopPropagation();
      window.clearTimeout(this.mouseTimeout);
      this.thumbPressed = false;
      const mouseMoveOptions = passiveSupported ? {
        passive: true
      } : false;
      this.app.removeEventListener("touchmove", this.onMouseMove, mouseMoveOptions);
      this.app.removeEventListener("mousemove", this.onMouseMove, mouseMoveOptions);
      this.$emit("mouseup", e);
      this.$emit("end", this.internalValue);
      if (!deepEqual(this.oldValue, this.internalValue)) {
        this.$emit("change", this.internalValue);
        this.noClick = true;
      }
      this.isActive = false;
    },
    onMouseMove(e) {
      if (e.type === "mousemove") {
        this.thumbPressed = true;
      }
      this.internalValue = this.parseMouseMove(e);
    },
    onKeyDown(e) {
      if (!this.isInteractive)
        return;
      const value = this.parseKeyDown(e, this.internalValue);
      if (value == null || value < this.minValue || value > this.maxValue)
        return;
      this.internalValue = value;
      this.$emit("change", value);
    },
    onSliderClick(e) {
      if (this.noClick) {
        this.noClick = false;
        return;
      }
      const thumb = this.$refs.thumb;
      thumb.focus();
      this.onMouseMove(e);
      this.$emit("change", this.internalValue);
    },
    onBlur(e) {
      this.isFocused = false;
      this.$emit("blur", e);
    },
    onFocus(e) {
      this.isFocused = true;
      this.$emit("focus", e);
    },
    parseMouseMove(e) {
      const start = this.vertical ? "top" : "left";
      const length = this.vertical ? "height" : "width";
      const click = this.vertical ? "clientY" : "clientX";
      const {
        [start]: trackStart,
        [length]: trackLength
      } = this.$refs.track.getBoundingClientRect();
      const clickOffset = "touches" in e ? e.touches[0][click] : e[click];
      let clickPos = Math.min(Math.max((clickOffset - trackStart) / trackLength, 0), 1) || 0;
      if (this.vertical)
        clickPos = 1 - clickPos;
      if (this.$vuetify.rtl)
        clickPos = 1 - clickPos;
      return parseFloat(this.min) + clickPos * (this.maxValue - this.minValue);
    },
    parseKeyDown(e, value) {
      if (!this.isInteractive)
        return;
      const {
        pageup,
        pagedown,
        end,
        home,
        left,
        right,
        down,
        up
      } = keyCodes;
      if (![pageup, pagedown, end, home, left, right, down, up].includes(e.keyCode))
        return;
      e.preventDefault();
      const step = this.stepNumeric || 1;
      const steps = (this.maxValue - this.minValue) / step;
      if ([left, right, down, up].includes(e.keyCode)) {
        const increase = this.$vuetify.rtl ? [left, up] : [right, up];
        const direction = increase.includes(e.keyCode) ? 1 : -1;
        const multiplier = e.shiftKey ? 3 : e.ctrlKey ? 2 : 1;
        value = value + direction * step * multiplier;
      } else if (e.keyCode === home) {
        value = this.minValue;
      } else if (e.keyCode === end) {
        value = this.maxValue;
      } else {
        const direction = e.keyCode === pagedown ? 1 : -1;
        value = value - direction * step * (steps > 100 ? steps / 10 : 10);
      }
      return value;
    },
    roundValue(value) {
      if (!this.stepNumeric)
        return value;
      const trimmedStep = this.step.toString().trim();
      const decimals = trimmedStep.indexOf(".") > -1 ? trimmedStep.length - trimmedStep.indexOf(".") - 1 : 0;
      const offset = this.minValue % this.stepNumeric;
      const newValue = Math.round((value - offset) / this.stepNumeric) * this.stepNumeric + offset;
      return parseFloat(Math.min(newValue, this.maxValue).toFixed(decimals));
    }
  }
});
function fromHSVA(hsva) {
  hsva = __spreadValues({}, hsva);
  const hexa = HSVAtoHex(hsva);
  const hsla = HSVAtoHSLA(hsva);
  const rgba = HSVAtoRGBA(hsva);
  return {
    alpha: hsva.a,
    hex: hexa.substr(0, 7),
    hexa,
    hsla,
    hsva,
    hue: hsva.h,
    rgba
  };
}
function fromHSLA(hsla) {
  const hsva = HSLAtoHSVA(hsla);
  const hexa = HSVAtoHex(hsva);
  const rgba = HSVAtoRGBA(hsva);
  return {
    alpha: hsva.a,
    hex: hexa.substr(0, 7),
    hexa,
    hsla,
    hsva,
    hue: hsva.h,
    rgba
  };
}
function fromRGBA(rgba) {
  const hsva = RGBAtoHSVA(rgba);
  const hexa = RGBAtoHex(rgba);
  const hsla = HSVAtoHSLA(hsva);
  return {
    alpha: hsva.a,
    hex: hexa.substr(0, 7),
    hexa,
    hsla,
    hsva,
    hue: hsva.h,
    rgba
  };
}
function fromHexa(hexa) {
  const hsva = HexToHSVA(hexa);
  const hsla = HSVAtoHSLA(hsva);
  const rgba = HSVAtoRGBA(hsva);
  return {
    alpha: hsva.a,
    hex: hexa.substr(0, 7),
    hexa,
    hsla,
    hsva,
    hue: hsva.h,
    rgba
  };
}
function fromHex(hex) {
  return fromHexa(parseHex(hex));
}
function has(obj, key) {
  return key.every((k) => obj.hasOwnProperty(k));
}
function parseColor(color, oldColor) {
  if (!color)
    return fromRGBA({
      r: 255,
      g: 0,
      b: 0,
      a: 1
    });
  if (typeof color === "string") {
    if (color === "transparent")
      return fromHexa("#00000000");
    const hex = parseHex(color);
    if (oldColor && hex === oldColor.hexa)
      return oldColor;
    else
      return fromHexa(hex);
  }
  if (typeof color === "object") {
    if (color.hasOwnProperty("alpha"))
      return color;
    const a = color.hasOwnProperty("a") ? parseFloat(color.a) : 1;
    if (has(color, ["r", "g", "b"])) {
      if (oldColor && color === oldColor.rgba)
        return oldColor;
      else
        return fromRGBA(__spreadProps(__spreadValues({}, color), {
          a
        }));
    } else if (has(color, ["h", "s", "l"])) {
      if (oldColor && color === oldColor.hsla)
        return oldColor;
      else
        return fromHSLA(__spreadProps(__spreadValues({}, color), {
          a
        }));
    } else if (has(color, ["h", "s", "v"])) {
      if (oldColor && color === oldColor.hsva)
        return oldColor;
      else
        return fromHSVA(__spreadProps(__spreadValues({}, color), {
          a
        }));
    }
  }
  return fromRGBA({
    r: 255,
    g: 0,
    b: 0,
    a: 1
  });
}
function stripAlpha(color, stripAlpha2) {
  if (stripAlpha2) {
    const _a = color, rest = __objRest(_a, [
      "a"
    ]);
    return rest;
  }
  return color;
}
function extractColor(color, input) {
  if (input == null)
    return color;
  if (typeof input === "string") {
    return input.length === 7 ? color.hex : color.hexa;
  }
  if (typeof input === "object") {
    const shouldStrip = typeof input.a === "number" && input.a === 0 ? !!input.a : !input.a;
    if (has(input, ["r", "g", "b"]))
      return stripAlpha(color.rgba, shouldStrip);
    else if (has(input, ["h", "s", "l"]))
      return stripAlpha(color.hsla, shouldStrip);
    else if (has(input, ["h", "s", "v"]))
      return stripAlpha(color.hsva, shouldStrip);
  }
  return color;
}
function hasAlpha(color) {
  if (!color)
    return false;
  if (typeof color === "string") {
    return color.length > 7;
  }
  if (typeof color === "object") {
    return has(color, ["a"]) || has(color, ["alpha"]);
  }
  return false;
}
var VColorPickerPreview = Vue.extend({
  name: "v-color-picker-preview",
  props: {
    color: Object,
    disabled: Boolean,
    hideAlpha: Boolean
  },
  methods: {
    genAlpha() {
      return this.genTrack({
        staticClass: "v-color-picker__alpha",
        props: {
          thumbColor: "grey lighten-2",
          hideDetails: true,
          value: this.color.alpha,
          step: 0,
          min: 0,
          max: 1
        },
        style: {
          backgroundImage: this.disabled ? void 0 : `linear-gradient(to ${this.$vuetify.rtl ? "left" : "right"}, transparent, ${RGBtoCSS(this.color.rgba)})`
        },
        on: {
          input: (val) => this.color.alpha !== val && this.$emit("update:color", fromHSVA(__spreadProps(__spreadValues({}, this.color.hsva), {
            a: val
          })))
        }
      });
    },
    genSliders() {
      return this.$createElement("div", {
        staticClass: "v-color-picker__sliders"
      }, [this.genHue(), !this.hideAlpha && this.genAlpha()]);
    },
    genDot() {
      return this.$createElement("div", {
        staticClass: "v-color-picker__dot"
      }, [this.$createElement("div", {
        style: {
          background: RGBAtoCSS(this.color.rgba)
        }
      })]);
    },
    genHue() {
      return this.genTrack({
        staticClass: "v-color-picker__hue",
        props: {
          thumbColor: "grey lighten-2",
          hideDetails: true,
          value: this.color.hue,
          step: 0,
          min: 0,
          max: 360
        },
        on: {
          input: (val) => this.color.hue !== val && this.$emit("update:color", fromHSVA(__spreadProps(__spreadValues({}, this.color.hsva), {
            h: val
          })))
        }
      });
    },
    genTrack(options2) {
      return this.$createElement(VSlider, __spreadProps(__spreadValues({
        class: "v-color-picker__track"
      }, options2), {
        props: __spreadValues({
          disabled: this.disabled
        }, options2.props)
      }));
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-color-picker__preview",
      class: {
        "v-color-picker__preview--hide-alpha": this.hideAlpha
      }
    }, [this.genDot(), this.genSliders()]);
  }
});
var VColorPickerCanvas = Vue.extend({
  name: "v-color-picker-canvas",
  props: {
    color: {
      type: Object,
      default: () => fromRGBA({
        r: 255,
        g: 0,
        b: 0,
        a: 1
      })
    },
    disabled: Boolean,
    dotSize: {
      type: [Number, String],
      default: 10
    },
    height: {
      type: [Number, String],
      default: 150
    },
    width: {
      type: [Number, String],
      default: 300
    }
  },
  data() {
    return {
      boundingRect: {
        width: 0,
        height: 0,
        left: 0,
        top: 0
      }
    };
  },
  computed: {
    dot() {
      if (!this.color)
        return {
          x: 0,
          y: 0
        };
      return {
        x: this.color.hsva.s * parseInt(this.width, 10),
        y: (1 - this.color.hsva.v) * parseInt(this.height, 10)
      };
    }
  },
  watch: {
    "color.hue": "updateCanvas"
  },
  mounted() {
    this.updateCanvas();
  },
  methods: {
    emitColor(x, y) {
      const {
        left,
        top,
        width,
        height
      } = this.boundingRect;
      this.$emit("update:color", fromHSVA({
        h: this.color.hue,
        s: clamp(x - left, 0, width) / width,
        v: 1 - clamp(y - top, 0, height) / height,
        a: this.color.alpha
      }));
    },
    updateCanvas() {
      if (!this.color)
        return;
      const canvas = this.$refs.canvas;
      const ctx = canvas.getContext("2d");
      if (!ctx)
        return;
      const saturationGradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
      saturationGradient.addColorStop(0, "hsla(0, 0%, 100%, 1)");
      saturationGradient.addColorStop(1, `hsla(${this.color.hue}, 100%, 50%, 1)`);
      ctx.fillStyle = saturationGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const valueGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      valueGradient.addColorStop(0, "hsla(0, 0%, 100%, 0)");
      valueGradient.addColorStop(1, "hsla(0, 0%, 0%, 1)");
      ctx.fillStyle = valueGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    },
    handleClick(e) {
      if (this.disabled)
        return;
      this.boundingRect = this.$el.getBoundingClientRect();
      this.emitColor(e.clientX, e.clientY);
    },
    handleMouseDown(e) {
      e.preventDefault();
      if (this.disabled)
        return;
      this.boundingRect = this.$el.getBoundingClientRect();
      window.addEventListener("mousemove", this.handleMouseMove);
      window.addEventListener("mouseup", this.handleMouseUp);
    },
    handleMouseMove(e) {
      if (this.disabled)
        return;
      this.emitColor(e.clientX, e.clientY);
    },
    handleMouseUp() {
      window.removeEventListener("mousemove", this.handleMouseMove);
      window.removeEventListener("mouseup", this.handleMouseUp);
    },
    genCanvas() {
      return this.$createElement("canvas", {
        ref: "canvas",
        attrs: {
          width: this.width,
          height: this.height
        }
      });
    },
    genDot() {
      const radius = parseInt(this.dotSize, 10) / 2;
      const x = convertToUnit(this.dot.x - radius);
      const y = convertToUnit(this.dot.y - radius);
      return this.$createElement("div", {
        staticClass: "v-color-picker__canvas-dot",
        class: {
          "v-color-picker__canvas-dot--disabled": this.disabled
        },
        style: {
          width: convertToUnit(this.dotSize),
          height: convertToUnit(this.dotSize),
          transform: `translate(${x}, ${y})`
        }
      });
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-color-picker__canvas",
      style: {
        width: convertToUnit(this.width),
        height: convertToUnit(this.height)
      },
      on: {
        click: this.handleClick,
        mousedown: this.handleMouseDown
      }
    }, [this.genCanvas(), this.genDot()]);
  }
});
const modes = {
  rgba: {
    inputs: [["r", 255, "int"], ["g", 255, "int"], ["b", 255, "int"], ["a", 1, "float"]],
    from: fromRGBA
  },
  hsla: {
    inputs: [["h", 360, "int"], ["s", 1, "float"], ["l", 1, "float"], ["a", 1, "float"]],
    from: fromHSLA
  },
  hexa: {
    from: fromHexa
  }
};
var VColorPickerEdit = Vue.extend({
  name: "v-color-picker-edit",
  props: {
    color: Object,
    disabled: Boolean,
    hideAlpha: Boolean,
    hideModeSwitch: Boolean,
    mode: {
      type: String,
      default: "rgba",
      validator: (v) => Object.keys(modes).includes(v)
    }
  },
  data() {
    return {
      modes,
      internalMode: this.mode
    };
  },
  computed: {
    currentMode() {
      return this.modes[this.internalMode];
    }
  },
  watch: {
    mode(mode) {
      this.internalMode = mode;
    }
  },
  created() {
    this.internalMode = this.mode;
  },
  methods: {
    getValue(v, type2) {
      if (type2 === "float")
        return Math.round(v * 100) / 100;
      else if (type2 === "int")
        return Math.round(v);
      else
        return 0;
    },
    parseValue(v, type2) {
      if (type2 === "float")
        return parseFloat(v);
      else if (type2 === "int")
        return parseInt(v, 10) || 0;
      else
        return 0;
    },
    changeMode() {
      const modes2 = Object.keys(this.modes);
      const index = modes2.indexOf(this.internalMode);
      const newMode = modes2[(index + 1) % modes2.length];
      this.internalMode = newMode;
      this.$emit("update:mode", newMode);
    },
    genInput(target, attrs, value, on) {
      return this.$createElement("div", {
        staticClass: "v-color-picker__input"
      }, [this.$createElement("input", {
        key: target,
        attrs,
        domProps: {
          value
        },
        on
      }), this.$createElement("span", target.toUpperCase())]);
    },
    genInputs() {
      if (this.internalMode === "hexa") {
        const hex = this.color.hexa;
        const value = this.hideAlpha && hex.endsWith("FF") ? hex.substr(0, 7) : hex;
        return this.genInput("hex", {
          maxlength: this.hideAlpha ? 7 : 9,
          disabled: this.disabled
        }, value, {
          change: (e) => {
            const el = e.target;
            this.$emit("update:color", this.currentMode.from(parseHex(el.value)));
          }
        });
      } else {
        const inputs = this.hideAlpha ? this.currentMode.inputs.slice(0, -1) : this.currentMode.inputs;
        return inputs.map(([target, max, type2]) => {
          const value = this.color[this.internalMode];
          return this.genInput(target, {
            type: "number",
            min: 0,
            max,
            step: type2 === "float" ? "0.01" : type2 === "int" ? "1" : void 0,
            disabled: this.disabled
          }, this.getValue(value[target], type2), {
            input: (e) => {
              const el = e.target;
              const newVal = this.parseValue(el.value || "0", type2);
              this.$emit("update:color", this.currentMode.from(Object.assign({}, value, {
                [target]: newVal
              }), this.color.alpha));
            }
          });
        });
      }
    },
    genSwitch() {
      return this.$createElement(VBtn, {
        props: {
          small: true,
          icon: true,
          disabled: this.disabled
        },
        on: {
          click: this.changeMode
        }
      }, [this.$createElement(VIcon$1, "$unfold")]);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-color-picker__edit"
    }, [this.genInputs(), !this.hideModeSwitch && this.genSwitch()]);
  }
});
const red = Object.freeze({
  base: "#f44336",
  lighten5: "#ffebee",
  lighten4: "#ffcdd2",
  lighten3: "#ef9a9a",
  lighten2: "#e57373",
  lighten1: "#ef5350",
  darken1: "#e53935",
  darken2: "#d32f2f",
  darken3: "#c62828",
  darken4: "#b71c1c",
  accent1: "#ff8a80",
  accent2: "#ff5252",
  accent3: "#ff1744",
  accent4: "#d50000"
});
const pink = Object.freeze({
  base: "#e91e63",
  lighten5: "#fce4ec",
  lighten4: "#f8bbd0",
  lighten3: "#f48fb1",
  lighten2: "#f06292",
  lighten1: "#ec407a",
  darken1: "#d81b60",
  darken2: "#c2185b",
  darken3: "#ad1457",
  darken4: "#880e4f",
  accent1: "#ff80ab",
  accent2: "#ff4081",
  accent3: "#f50057",
  accent4: "#c51162"
});
const purple = Object.freeze({
  base: "#9c27b0",
  lighten5: "#f3e5f5",
  lighten4: "#e1bee7",
  lighten3: "#ce93d8",
  lighten2: "#ba68c8",
  lighten1: "#ab47bc",
  darken1: "#8e24aa",
  darken2: "#7b1fa2",
  darken3: "#6a1b9a",
  darken4: "#4a148c",
  accent1: "#ea80fc",
  accent2: "#e040fb",
  accent3: "#d500f9",
  accent4: "#aa00ff"
});
const deepPurple = Object.freeze({
  base: "#673ab7",
  lighten5: "#ede7f6",
  lighten4: "#d1c4e9",
  lighten3: "#b39ddb",
  lighten2: "#9575cd",
  lighten1: "#7e57c2",
  darken1: "#5e35b1",
  darken2: "#512da8",
  darken3: "#4527a0",
  darken4: "#311b92",
  accent1: "#b388ff",
  accent2: "#7c4dff",
  accent3: "#651fff",
  accent4: "#6200ea"
});
const indigo = Object.freeze({
  base: "#3f51b5",
  lighten5: "#e8eaf6",
  lighten4: "#c5cae9",
  lighten3: "#9fa8da",
  lighten2: "#7986cb",
  lighten1: "#5c6bc0",
  darken1: "#3949ab",
  darken2: "#303f9f",
  darken3: "#283593",
  darken4: "#1a237e",
  accent1: "#8c9eff",
  accent2: "#536dfe",
  accent3: "#3d5afe",
  accent4: "#304ffe"
});
const blue = Object.freeze({
  base: "#2196f3",
  lighten5: "#e3f2fd",
  lighten4: "#bbdefb",
  lighten3: "#90caf9",
  lighten2: "#64b5f6",
  lighten1: "#42a5f5",
  darken1: "#1e88e5",
  darken2: "#1976d2",
  darken3: "#1565c0",
  darken4: "#0d47a1",
  accent1: "#82b1ff",
  accent2: "#448aff",
  accent3: "#2979ff",
  accent4: "#2962ff"
});
const lightBlue = Object.freeze({
  base: "#03a9f4",
  lighten5: "#e1f5fe",
  lighten4: "#b3e5fc",
  lighten3: "#81d4fa",
  lighten2: "#4fc3f7",
  lighten1: "#29b6f6",
  darken1: "#039be5",
  darken2: "#0288d1",
  darken3: "#0277bd",
  darken4: "#01579b",
  accent1: "#80d8ff",
  accent2: "#40c4ff",
  accent3: "#00b0ff",
  accent4: "#0091ea"
});
const cyan = Object.freeze({
  base: "#00bcd4",
  lighten5: "#e0f7fa",
  lighten4: "#b2ebf2",
  lighten3: "#80deea",
  lighten2: "#4dd0e1",
  lighten1: "#26c6da",
  darken1: "#00acc1",
  darken2: "#0097a7",
  darken3: "#00838f",
  darken4: "#006064",
  accent1: "#84ffff",
  accent2: "#18ffff",
  accent3: "#00e5ff",
  accent4: "#00b8d4"
});
const teal = Object.freeze({
  base: "#009688",
  lighten5: "#e0f2f1",
  lighten4: "#b2dfdb",
  lighten3: "#80cbc4",
  lighten2: "#4db6ac",
  lighten1: "#26a69a",
  darken1: "#00897b",
  darken2: "#00796b",
  darken3: "#00695c",
  darken4: "#004d40",
  accent1: "#a7ffeb",
  accent2: "#64ffda",
  accent3: "#1de9b6",
  accent4: "#00bfa5"
});
const green = Object.freeze({
  base: "#4caf50",
  lighten5: "#e8f5e9",
  lighten4: "#c8e6c9",
  lighten3: "#a5d6a7",
  lighten2: "#81c784",
  lighten1: "#66bb6a",
  darken1: "#43a047",
  darken2: "#388e3c",
  darken3: "#2e7d32",
  darken4: "#1b5e20",
  accent1: "#b9f6ca",
  accent2: "#69f0ae",
  accent3: "#00e676",
  accent4: "#00c853"
});
const lightGreen = Object.freeze({
  base: "#8bc34a",
  lighten5: "#f1f8e9",
  lighten4: "#dcedc8",
  lighten3: "#c5e1a5",
  lighten2: "#aed581",
  lighten1: "#9ccc65",
  darken1: "#7cb342",
  darken2: "#689f38",
  darken3: "#558b2f",
  darken4: "#33691e",
  accent1: "#ccff90",
  accent2: "#b2ff59",
  accent3: "#76ff03",
  accent4: "#64dd17"
});
const lime = Object.freeze({
  base: "#cddc39",
  lighten5: "#f9fbe7",
  lighten4: "#f0f4c3",
  lighten3: "#e6ee9c",
  lighten2: "#dce775",
  lighten1: "#d4e157",
  darken1: "#c0ca33",
  darken2: "#afb42b",
  darken3: "#9e9d24",
  darken4: "#827717",
  accent1: "#f4ff81",
  accent2: "#eeff41",
  accent3: "#c6ff00",
  accent4: "#aeea00"
});
const yellow = Object.freeze({
  base: "#ffeb3b",
  lighten5: "#fffde7",
  lighten4: "#fff9c4",
  lighten3: "#fff59d",
  lighten2: "#fff176",
  lighten1: "#ffee58",
  darken1: "#fdd835",
  darken2: "#fbc02d",
  darken3: "#f9a825",
  darken4: "#f57f17",
  accent1: "#ffff8d",
  accent2: "#ffff00",
  accent3: "#ffea00",
  accent4: "#ffd600"
});
const amber = Object.freeze({
  base: "#ffc107",
  lighten5: "#fff8e1",
  lighten4: "#ffecb3",
  lighten3: "#ffe082",
  lighten2: "#ffd54f",
  lighten1: "#ffca28",
  darken1: "#ffb300",
  darken2: "#ffa000",
  darken3: "#ff8f00",
  darken4: "#ff6f00",
  accent1: "#ffe57f",
  accent2: "#ffd740",
  accent3: "#ffc400",
  accent4: "#ffab00"
});
const orange = Object.freeze({
  base: "#ff9800",
  lighten5: "#fff3e0",
  lighten4: "#ffe0b2",
  lighten3: "#ffcc80",
  lighten2: "#ffb74d",
  lighten1: "#ffa726",
  darken1: "#fb8c00",
  darken2: "#f57c00",
  darken3: "#ef6c00",
  darken4: "#e65100",
  accent1: "#ffd180",
  accent2: "#ffab40",
  accent3: "#ff9100",
  accent4: "#ff6d00"
});
const deepOrange = Object.freeze({
  base: "#ff5722",
  lighten5: "#fbe9e7",
  lighten4: "#ffccbc",
  lighten3: "#ffab91",
  lighten2: "#ff8a65",
  lighten1: "#ff7043",
  darken1: "#f4511e",
  darken2: "#e64a19",
  darken3: "#d84315",
  darken4: "#bf360c",
  accent1: "#ff9e80",
  accent2: "#ff6e40",
  accent3: "#ff3d00",
  accent4: "#dd2c00"
});
const brown = Object.freeze({
  base: "#795548",
  lighten5: "#efebe9",
  lighten4: "#d7ccc8",
  lighten3: "#bcaaa4",
  lighten2: "#a1887f",
  lighten1: "#8d6e63",
  darken1: "#6d4c41",
  darken2: "#5d4037",
  darken3: "#4e342e",
  darken4: "#3e2723"
});
const blueGrey = Object.freeze({
  base: "#607d8b",
  lighten5: "#eceff1",
  lighten4: "#cfd8dc",
  lighten3: "#b0bec5",
  lighten2: "#90a4ae",
  lighten1: "#78909c",
  darken1: "#546e7a",
  darken2: "#455a64",
  darken3: "#37474f",
  darken4: "#263238"
});
const grey = Object.freeze({
  base: "#9e9e9e",
  lighten5: "#fafafa",
  lighten4: "#f5f5f5",
  lighten3: "#eeeeee",
  lighten2: "#e0e0e0",
  lighten1: "#bdbdbd",
  darken1: "#757575",
  darken2: "#616161",
  darken3: "#424242",
  darken4: "#212121"
});
const shades = Object.freeze({
  black: "#000000",
  white: "#ffffff",
  transparent: "transparent"
});
var colors = Object.freeze({
  red,
  pink,
  purple,
  deepPurple,
  indigo,
  blue,
  lightBlue,
  cyan,
  teal,
  green,
  lightGreen,
  lime,
  yellow,
  amber,
  orange,
  deepOrange,
  brown,
  blueGrey,
  grey,
  shades
});
function parseDefaultColors(colors2) {
  return Object.keys(colors2).map((key) => {
    const color = colors2[key];
    return color.base ? [color.base, color.darken4, color.darken3, color.darken2, color.darken1, color.lighten1, color.lighten2, color.lighten3, color.lighten4, color.lighten5] : [color.black, color.white, color.transparent];
  });
}
const white = fromHex("#FFFFFF").rgba;
const black = fromHex("#000000").rgba;
var VColorPickerSwatches = mixins(Themeable$1).extend({
  name: "v-color-picker-swatches",
  props: {
    swatches: {
      type: Array,
      default: () => parseDefaultColors(colors)
    },
    disabled: Boolean,
    color: Object,
    maxWidth: [Number, String],
    maxHeight: [Number, String]
  },
  methods: {
    genColor(color) {
      const content = this.$createElement("div", {
        style: {
          background: color
        }
      }, [deepEqual(this.color, parseColor(color, null)) && this.$createElement(VIcon$1, {
        props: {
          small: true,
          dark: contrastRatio(this.color.rgba, white) > 2 && this.color.alpha > 0.5,
          light: contrastRatio(this.color.rgba, black) > 2 && this.color.alpha > 0.5
        }
      }, "$success")]);
      return this.$createElement("div", {
        staticClass: "v-color-picker__color",
        on: {
          click: () => this.disabled || this.$emit("update:color", fromHex(color === "transparent" ? "#00000000" : color))
        }
      }, [content]);
    },
    genSwatches() {
      return this.swatches.map((swatch) => {
        const colors2 = swatch.map(this.genColor);
        return this.$createElement("div", {
          staticClass: "v-color-picker__swatch"
        }, colors2);
      });
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-color-picker__swatches",
      style: {
        maxWidth: convertToUnit(this.maxWidth),
        maxHeight: convertToUnit(this.maxHeight)
      }
    }, [this.$createElement("div", this.genSwatches())]);
  }
});
var VColorPicker = mixins(Elevatable, Themeable$1).extend({
  name: "v-color-picker",
  props: {
    canvasHeight: {
      type: [String, Number],
      default: 150
    },
    disabled: Boolean,
    dotSize: {
      type: [Number, String],
      default: 10
    },
    flat: Boolean,
    hideCanvas: Boolean,
    hideSliders: Boolean,
    hideInputs: Boolean,
    hideModeSwitch: Boolean,
    mode: {
      type: String,
      default: "rgba",
      validator: (v) => Object.keys(modes).includes(v)
    },
    showSwatches: Boolean,
    swatches: Array,
    swatchesMaxHeight: {
      type: [Number, String],
      default: 150
    },
    value: {
      type: [Object, String]
    },
    width: {
      type: [Number, String],
      default: 300
    }
  },
  data: () => ({
    internalValue: fromRGBA({
      r: 255,
      g: 0,
      b: 0,
      a: 1
    })
  }),
  computed: {
    hideAlpha() {
      if (!this.value)
        return false;
      return !hasAlpha(this.value);
    }
  },
  watch: {
    value: {
      handler(color) {
        this.updateColor(parseColor(color, this.internalValue));
      },
      immediate: true
    }
  },
  methods: {
    updateColor(color) {
      this.internalValue = color;
      const value = extractColor(this.internalValue, this.value);
      if (!deepEqual(value, this.value)) {
        this.$emit("input", value);
        this.$emit("update:color", this.internalValue);
      }
    },
    genCanvas() {
      return this.$createElement(VColorPickerCanvas, {
        props: {
          color: this.internalValue,
          disabled: this.disabled,
          dotSize: this.dotSize,
          width: this.width,
          height: this.canvasHeight
        },
        on: {
          "update:color": this.updateColor
        }
      });
    },
    genControls() {
      return this.$createElement("div", {
        staticClass: "v-color-picker__controls"
      }, [!this.hideSliders && this.genPreview(), !this.hideInputs && this.genEdit()]);
    },
    genEdit() {
      return this.$createElement(VColorPickerEdit, {
        props: {
          color: this.internalValue,
          disabled: this.disabled,
          hideAlpha: this.hideAlpha,
          hideModeSwitch: this.hideModeSwitch,
          mode: this.mode
        },
        on: {
          "update:color": this.updateColor,
          "update:mode": (v) => this.$emit("update:mode", v)
        }
      });
    },
    genPreview() {
      return this.$createElement(VColorPickerPreview, {
        props: {
          color: this.internalValue,
          disabled: this.disabled,
          hideAlpha: this.hideAlpha
        },
        on: {
          "update:color": this.updateColor
        }
      });
    },
    genSwatches() {
      return this.$createElement(VColorPickerSwatches, {
        props: {
          dark: this.dark,
          light: this.light,
          disabled: this.disabled,
          swatches: this.swatches,
          color: this.internalValue,
          maxHeight: this.swatchesMaxHeight
        },
        on: {
          "update:color": this.updateColor
        }
      });
    }
  },
  render(h) {
    return h(VSheet, {
      staticClass: "v-color-picker",
      class: __spreadValues(__spreadValues({
        "v-color-picker--flat": this.flat
      }, this.themeClasses), this.elevationClasses),
      props: {
        maxWidth: this.width
      }
    }, [!this.hideCanvas && this.genCanvas(), (!this.hideSliders || !this.hideInputs) && this.genControls(), this.showSwatches && this.genSwatches()]);
  }
});
var VMain = SSRBootable.extend({
  name: "v-main",
  props: {
    tag: {
      type: String,
      default: "main"
    }
  },
  computed: {
    styles() {
      const {
        bar,
        top,
        right,
        footer,
        insetFooter,
        bottom,
        left
      } = this.$vuetify.application;
      return {
        paddingTop: `${top + bar}px`,
        paddingRight: `${right}px`,
        paddingBottom: `${footer + insetFooter + bottom}px`,
        paddingLeft: `${left}px`
      };
    }
  },
  render(h) {
    const data = {
      staticClass: "v-main",
      style: this.styles,
      ref: "main"
    };
    return h(this.tag, data, [h("div", {
      staticClass: "v-main__wrap"
    }, this.$slots.default)]);
  }
});
var VContent = VMain.extend({
  name: "v-main",
  created() {
    deprecate("v-content", "v-main", this);
  },
  render(h) {
    const node = VMain.options.render.call(this, h);
    node.data.staticClass += " v-content";
    node.children[0].data.staticClass += " v-content__wrap";
    return h(node.tag, node.data, node.children);
  }
});
var VCombobox = VAutocomplete.extend({
  name: "v-combobox",
  props: {
    delimiters: {
      type: Array,
      default: () => []
    },
    returnObject: {
      type: Boolean,
      default: true
    }
  },
  data: () => ({
    editingIndex: -1
  }),
  computed: {
    computedCounterValue() {
      return this.multiple ? this.selectedItems.length : (this.internalSearch || "").toString().length;
    },
    hasSlot() {
      return VSelect.options.computed.hasSlot.call(this) || this.multiple;
    },
    isAnyValueAllowed() {
      return true;
    },
    menuCanShow() {
      if (!this.isFocused)
        return false;
      return this.hasDisplayedItems || !!this.$slots["no-data"] && !this.hideNoData;
    },
    searchIsDirty() {
      return this.internalSearch != null;
    }
  },
  methods: {
    onInternalSearchChanged(val) {
      if (val && this.multiple && this.delimiters.length) {
        const delimiter = this.delimiters.find((d) => val.endsWith(d));
        if (delimiter != null) {
          this.internalSearch = val.slice(0, val.length - delimiter.length);
          this.updateTags();
        }
      }
      this.updateMenuDimensions();
    },
    genInput() {
      const input = VAutocomplete.options.methods.genInput.call(this);
      delete input.data.attrs.name;
      input.data.on.paste = this.onPaste;
      return input;
    },
    genChipSelection(item, index) {
      const chip = VSelect.options.methods.genChipSelection.call(this, item, index);
      if (this.multiple) {
        chip.componentOptions.listeners = __spreadProps(__spreadValues({}, chip.componentOptions.listeners), {
          dblclick: () => {
            this.editingIndex = index;
            this.internalSearch = this.getText(item);
            this.selectedIndex = -1;
          }
        });
      }
      return chip;
    },
    onChipInput(item) {
      VSelect.options.methods.onChipInput.call(this, item);
      this.editingIndex = -1;
    },
    onEnterDown(e) {
      e.preventDefault();
      if (this.getMenuIndex() > -1)
        return;
      this.$nextTick(this.updateSelf);
    },
    onFilteredItemsChanged(val, oldVal) {
      if (!this.autoSelectFirst)
        return;
      VAutocomplete.options.methods.onFilteredItemsChanged.call(this, val, oldVal);
    },
    onKeyDown(e) {
      const keyCode = e.keyCode;
      if (e.ctrlKey || ![keyCodes.home, keyCodes.end].includes(keyCode)) {
        VSelect.options.methods.onKeyDown.call(this, e);
      }
      if (this.multiple && keyCode === keyCodes.left && this.$refs.input.selectionStart === 0) {
        this.updateSelf();
      } else if (keyCode === keyCodes.enter) {
        this.onEnterDown(e);
      }
      this.changeSelectedIndex(keyCode);
    },
    onTabDown(e) {
      if (this.multiple && this.internalSearch && this.getMenuIndex() === -1) {
        e.preventDefault();
        e.stopPropagation();
        return this.updateTags();
      }
      VAutocomplete.options.methods.onTabDown.call(this, e);
    },
    selectItem(item) {
      if (this.editingIndex > -1) {
        this.updateEditing();
      } else {
        VAutocomplete.options.methods.selectItem.call(this, item);
        if (this.internalSearch && this.multiple && this.getText(item).toLocaleLowerCase().includes(this.internalSearch.toLocaleLowerCase())) {
          this.internalSearch = null;
        }
      }
    },
    setSelectedItems() {
      if (this.internalValue == null || this.internalValue === "") {
        this.selectedItems = [];
      } else {
        this.selectedItems = this.multiple ? this.internalValue : [this.internalValue];
      }
    },
    setValue(value) {
      var _value;
      VSelect.options.methods.setValue.call(this, (_value = value) != null ? _value : this.internalSearch);
    },
    updateEditing() {
      const value = this.internalValue.slice();
      value[this.editingIndex] = this.internalSearch;
      this.setValue(value);
      this.editingIndex = -1;
    },
    updateCombobox() {
      if (!this.searchIsDirty)
        return;
      if (this.internalSearch !== this.getText(this.internalValue))
        this.setValue();
      const isUsingSlot = Boolean(this.$scopedSlots.selection) || this.hasChips;
      if (isUsingSlot)
        this.internalSearch = null;
    },
    updateSelf() {
      this.multiple ? this.updateTags() : this.updateCombobox();
    },
    updateTags() {
      const menuIndex = this.getMenuIndex();
      if (menuIndex < 0 && !this.searchIsDirty || !this.internalSearch)
        return;
      if (this.editingIndex > -1) {
        return this.updateEditing();
      }
      const index = this.selectedItems.indexOf(this.internalSearch);
      if (index > -1) {
        const internalValue = this.internalValue.slice();
        internalValue.splice(index, 1);
        this.setValue(internalValue);
      }
      if (menuIndex > -1)
        return this.internalSearch = null;
      this.selectItem(this.internalSearch);
      this.internalSearch = null;
    },
    onPaste(event) {
      var _event$clipboardData;
      if (!this.multiple || this.searchIsDirty)
        return;
      const pastedItemText = (_event$clipboardData = event.clipboardData) == null ? void 0 : _event$clipboardData.getData("text/vnd.vuetify.autocomplete.item+plain");
      if (pastedItemText && this.findExistingIndex(pastedItemText) === -1) {
        event.preventDefault();
        VSelect.options.methods.selectItem.call(this, pastedItemText);
      }
    }
  }
});
var VData = Vue.extend({
  name: "v-data",
  inheritAttrs: false,
  props: {
    items: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => ({})
    },
    sortBy: {
      type: [String, Array],
      default: () => []
    },
    sortDesc: {
      type: [Boolean, Array],
      default: () => []
    },
    customSort: {
      type: Function,
      default: sortItems
    },
    mustSort: Boolean,
    multiSort: Boolean,
    page: {
      type: Number,
      default: 1
    },
    itemsPerPage: {
      type: Number,
      default: 10
    },
    groupBy: {
      type: [String, Array],
      default: () => []
    },
    groupDesc: {
      type: [Boolean, Array],
      default: () => []
    },
    customGroup: {
      type: Function,
      default: groupItems
    },
    locale: {
      type: String,
      default: "en-US"
    },
    disableSort: Boolean,
    disablePagination: Boolean,
    disableFiltering: Boolean,
    search: String,
    customFilter: {
      type: Function,
      default: searchItems
    },
    serverItemsLength: {
      type: Number,
      default: -1
    }
  },
  data() {
    let internalOptions = {
      page: this.page,
      itemsPerPage: this.itemsPerPage,
      sortBy: wrapInArray(this.sortBy),
      sortDesc: wrapInArray(this.sortDesc),
      groupBy: wrapInArray(this.groupBy),
      groupDesc: wrapInArray(this.groupDesc),
      mustSort: this.mustSort,
      multiSort: this.multiSort
    };
    if (this.options) {
      internalOptions = Object.assign(internalOptions, this.options);
    }
    const {
      sortBy,
      sortDesc,
      groupBy,
      groupDesc
    } = internalOptions;
    const sortDiff = sortBy.length - sortDesc.length;
    const groupDiff = groupBy.length - groupDesc.length;
    if (sortDiff > 0) {
      internalOptions.sortDesc.push(...fillArray(sortDiff, false));
    }
    if (groupDiff > 0) {
      internalOptions.groupDesc.push(...fillArray(groupDiff, false));
    }
    return {
      internalOptions
    };
  },
  computed: {
    itemsLength() {
      return this.serverItemsLength >= 0 ? this.serverItemsLength : this.filteredItems.length;
    },
    pageCount() {
      return this.internalOptions.itemsPerPage <= 0 ? 1 : Math.ceil(this.itemsLength / this.internalOptions.itemsPerPage);
    },
    pageStart() {
      if (this.internalOptions.itemsPerPage === -1 || !this.items.length)
        return 0;
      return (this.internalOptions.page - 1) * this.internalOptions.itemsPerPage;
    },
    pageStop() {
      if (this.internalOptions.itemsPerPage === -1)
        return this.itemsLength;
      if (!this.items.length)
        return 0;
      return Math.min(this.itemsLength, this.internalOptions.page * this.internalOptions.itemsPerPage);
    },
    isGrouped() {
      return !!this.internalOptions.groupBy.length;
    },
    pagination() {
      return {
        page: this.internalOptions.page,
        itemsPerPage: this.internalOptions.itemsPerPage,
        pageStart: this.pageStart,
        pageStop: this.pageStop,
        pageCount: this.pageCount,
        itemsLength: this.itemsLength
      };
    },
    filteredItems() {
      let items = this.items.slice();
      if (!this.disableFiltering && this.serverItemsLength <= 0) {
        items = this.customFilter(items, this.search);
      }
      return items;
    },
    computedItems() {
      let items = this.filteredItems.slice();
      if (!this.disableSort && this.serverItemsLength <= 0) {
        items = this.sortItems(items);
      }
      if (!this.disablePagination && this.serverItemsLength <= 0) {
        items = this.paginateItems(items);
      }
      return items;
    },
    groupedItems() {
      return this.isGrouped ? this.groupItems(this.computedItems) : null;
    },
    scopedProps() {
      return {
        sort: this.sort,
        sortArray: this.sortArray,
        group: this.group,
        items: this.computedItems,
        options: this.internalOptions,
        updateOptions: this.updateOptions,
        pagination: this.pagination,
        groupedItems: this.groupedItems,
        originalItemsLength: this.items.length
      };
    },
    computedOptions() {
      return __spreadValues({}, this.options);
    }
  },
  watch: {
    computedOptions: {
      handler(options2, old) {
        if (deepEqual(options2, old))
          return;
        this.updateOptions(options2);
      },
      deep: true,
      immediate: true
    },
    internalOptions: {
      handler(options2, old) {
        if (deepEqual(options2, old))
          return;
        this.$emit("update:options", options2);
      },
      deep: true,
      immediate: true
    },
    page(page) {
      this.updateOptions({
        page
      });
    },
    "internalOptions.page"(page) {
      this.$emit("update:page", page);
    },
    itemsPerPage(itemsPerPage) {
      this.updateOptions({
        itemsPerPage
      });
    },
    "internalOptions.itemsPerPage"(itemsPerPage) {
      this.$emit("update:items-per-page", itemsPerPage);
    },
    sortBy(sortBy) {
      this.updateOptions({
        sortBy: wrapInArray(sortBy)
      });
    },
    "internalOptions.sortBy"(sortBy, old) {
      !deepEqual(sortBy, old) && this.$emit("update:sort-by", Array.isArray(this.sortBy) ? sortBy : sortBy[0]);
    },
    sortDesc(sortDesc) {
      this.updateOptions({
        sortDesc: wrapInArray(sortDesc)
      });
    },
    "internalOptions.sortDesc"(sortDesc, old) {
      !deepEqual(sortDesc, old) && this.$emit("update:sort-desc", Array.isArray(this.sortDesc) ? sortDesc : sortDesc[0]);
    },
    groupBy(groupBy) {
      this.updateOptions({
        groupBy: wrapInArray(groupBy)
      });
    },
    "internalOptions.groupBy"(groupBy, old) {
      !deepEqual(groupBy, old) && this.$emit("update:group-by", Array.isArray(this.groupBy) ? groupBy : groupBy[0]);
    },
    groupDesc(groupDesc) {
      this.updateOptions({
        groupDesc: wrapInArray(groupDesc)
      });
    },
    "internalOptions.groupDesc"(groupDesc, old) {
      !deepEqual(groupDesc, old) && this.$emit("update:group-desc", Array.isArray(this.groupDesc) ? groupDesc : groupDesc[0]);
    },
    multiSort(multiSort) {
      this.updateOptions({
        multiSort
      });
    },
    "internalOptions.multiSort"(multiSort) {
      this.$emit("update:multi-sort", multiSort);
    },
    mustSort(mustSort) {
      this.updateOptions({
        mustSort
      });
    },
    "internalOptions.mustSort"(mustSort) {
      this.$emit("update:must-sort", mustSort);
    },
    pageCount: {
      handler(pageCount) {
        this.$emit("page-count", pageCount);
      },
      immediate: true
    },
    computedItems: {
      handler(computedItems) {
        this.$emit("current-items", computedItems);
      },
      immediate: true
    },
    pagination: {
      handler(pagination, old) {
        if (deepEqual(pagination, old))
          return;
        this.$emit("pagination", this.pagination);
      },
      immediate: true
    }
  },
  methods: {
    toggle(key, oldBy, oldDesc, page, mustSort, multiSort) {
      let by = oldBy.slice();
      let desc = oldDesc.slice();
      const byIndex = by.findIndex((k) => k === key);
      if (byIndex < 0) {
        if (!multiSort) {
          by = [];
          desc = [];
        }
        by.push(key);
        desc.push(false);
      } else if (byIndex >= 0 && !desc[byIndex]) {
        desc[byIndex] = true;
      } else if (!mustSort) {
        by.splice(byIndex, 1);
        desc.splice(byIndex, 1);
      } else {
        desc[byIndex] = false;
      }
      if (!deepEqual(by, oldBy) || !deepEqual(desc, oldDesc)) {
        page = 1;
      }
      return {
        by,
        desc,
        page
      };
    },
    group(key) {
      const {
        by: groupBy,
        desc: groupDesc,
        page
      } = this.toggle(key, this.internalOptions.groupBy, this.internalOptions.groupDesc, this.internalOptions.page, true, false);
      this.updateOptions({
        groupBy,
        groupDesc,
        page
      });
    },
    sort(key) {
      if (Array.isArray(key))
        return this.sortArray(key);
      const {
        by: sortBy,
        desc: sortDesc,
        page
      } = this.toggle(key, this.internalOptions.sortBy, this.internalOptions.sortDesc, this.internalOptions.page, this.internalOptions.mustSort, this.internalOptions.multiSort);
      this.updateOptions({
        sortBy,
        sortDesc,
        page
      });
    },
    sortArray(sortBy) {
      const sortDesc = sortBy.map((s) => {
        const i = this.internalOptions.sortBy.findIndex((k) => k === s);
        return i > -1 ? this.internalOptions.sortDesc[i] : false;
      });
      this.updateOptions({
        sortBy,
        sortDesc
      });
    },
    updateOptions(options2) {
      this.internalOptions = __spreadProps(__spreadValues(__spreadValues({}, this.internalOptions), options2), {
        page: this.serverItemsLength < 0 ? Math.max(1, Math.min(options2.page || this.internalOptions.page, this.pageCount)) : options2.page || this.internalOptions.page
      });
    },
    sortItems(items) {
      let sortBy = this.internalOptions.sortBy;
      let sortDesc = this.internalOptions.sortDesc;
      if (this.internalOptions.groupBy.length) {
        sortBy = [...this.internalOptions.groupBy, ...sortBy];
        sortDesc = [...this.internalOptions.groupDesc, ...sortDesc];
      }
      return this.customSort(items, sortBy, sortDesc, this.locale);
    },
    groupItems(items) {
      return this.customGroup(items, this.internalOptions.groupBy, this.internalOptions.groupDesc);
    },
    paginateItems(items) {
      if (this.serverItemsLength === -1 && items.length <= this.pageStart) {
        this.internalOptions.page = Math.max(1, Math.ceil(items.length / this.internalOptions.itemsPerPage)) || 1;
      }
      return items.slice(this.pageStart, this.pageStop);
    }
  },
  render() {
    return this.$scopedSlots.default && this.$scopedSlots.default(this.scopedProps);
  }
});
var VDataFooter = Vue.extend({
  name: "v-data-footer",
  props: {
    options: {
      type: Object,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15, -1]
    },
    prevIcon: {
      type: String,
      default: "$prev"
    },
    nextIcon: {
      type: String,
      default: "$next"
    },
    firstIcon: {
      type: String,
      default: "$first"
    },
    lastIcon: {
      type: String,
      default: "$last"
    },
    itemsPerPageText: {
      type: String,
      default: "$vuetify.dataFooter.itemsPerPageText"
    },
    itemsPerPageAllText: {
      type: String,
      default: "$vuetify.dataFooter.itemsPerPageAll"
    },
    showFirstLastPage: Boolean,
    showCurrentPage: Boolean,
    disablePagination: Boolean,
    disableItemsPerPage: Boolean,
    pageText: {
      type: String,
      default: "$vuetify.dataFooter.pageText"
    }
  },
  computed: {
    disableNextPageIcon() {
      return this.options.itemsPerPage <= 0 || this.options.page * this.options.itemsPerPage >= this.pagination.itemsLength || this.pagination.pageStop < 0;
    },
    computedDataItemsPerPageOptions() {
      return this.itemsPerPageOptions.map((option) => {
        if (typeof option === "object")
          return option;
        else
          return this.genDataItemsPerPageOption(option);
      });
    }
  },
  methods: {
    updateOptions(obj) {
      this.$emit("update:options", Object.assign({}, this.options, obj));
    },
    onFirstPage() {
      this.updateOptions({
        page: 1
      });
    },
    onPreviousPage() {
      this.updateOptions({
        page: this.options.page - 1
      });
    },
    onNextPage() {
      this.updateOptions({
        page: this.options.page + 1
      });
    },
    onLastPage() {
      this.updateOptions({
        page: this.pagination.pageCount
      });
    },
    onChangeItemsPerPage(itemsPerPage) {
      this.updateOptions({
        itemsPerPage,
        page: 1
      });
    },
    genDataItemsPerPageOption(option) {
      return {
        text: option === -1 ? this.$vuetify.lang.t(this.itemsPerPageAllText) : String(option),
        value: option
      };
    },
    genItemsPerPageSelect() {
      let value = this.options.itemsPerPage;
      const computedIPPO = this.computedDataItemsPerPageOptions;
      if (computedIPPO.length <= 1)
        return null;
      if (!computedIPPO.find((ippo) => ippo.value === value))
        value = computedIPPO[0];
      return this.$createElement("div", {
        staticClass: "v-data-footer__select"
      }, [this.$vuetify.lang.t(this.itemsPerPageText), this.$createElement(VSelect, {
        attrs: {
          "aria-label": this.$vuetify.lang.t(this.itemsPerPageText)
        },
        props: {
          disabled: this.disableItemsPerPage,
          items: computedIPPO,
          value,
          hideDetails: true,
          auto: true,
          minWidth: "75px"
        },
        on: {
          input: this.onChangeItemsPerPage
        }
      })]);
    },
    genPaginationInfo() {
      let children = ["\u2013"];
      const itemsLength = this.pagination.itemsLength;
      let pageStart = this.pagination.pageStart;
      let pageStop = this.pagination.pageStop;
      if (this.pagination.itemsLength && this.pagination.itemsPerPage) {
        pageStart = this.pagination.pageStart + 1;
        pageStop = itemsLength < this.pagination.pageStop || this.pagination.pageStop < 0 ? itemsLength : this.pagination.pageStop;
        children = this.$scopedSlots["page-text"] ? [this.$scopedSlots["page-text"]({
          pageStart,
          pageStop,
          itemsLength
        })] : [this.$vuetify.lang.t(this.pageText, pageStart, pageStop, itemsLength)];
      } else if (this.$scopedSlots["page-text"]) {
        children = [this.$scopedSlots["page-text"]({
          pageStart,
          pageStop,
          itemsLength
        })];
      }
      return this.$createElement("div", {
        class: "v-data-footer__pagination"
      }, children);
    },
    genIcon(click, disabled, label, icon) {
      return this.$createElement(VBtn, {
        props: {
          disabled: disabled || this.disablePagination,
          icon: true,
          text: true
        },
        on: {
          click
        },
        attrs: {
          "aria-label": label
        }
      }, [this.$createElement(VIcon$1, icon)]);
    },
    genIcons() {
      const before = [];
      const after = [];
      before.push(this.genIcon(this.onPreviousPage, this.options.page === 1, this.$vuetify.lang.t("$vuetify.dataFooter.prevPage"), this.$vuetify.rtl ? this.nextIcon : this.prevIcon));
      after.push(this.genIcon(this.onNextPage, this.disableNextPageIcon, this.$vuetify.lang.t("$vuetify.dataFooter.nextPage"), this.$vuetify.rtl ? this.prevIcon : this.nextIcon));
      if (this.showFirstLastPage) {
        before.unshift(this.genIcon(this.onFirstPage, this.options.page === 1, this.$vuetify.lang.t("$vuetify.dataFooter.firstPage"), this.$vuetify.rtl ? this.lastIcon : this.firstIcon));
        after.push(this.genIcon(this.onLastPage, this.options.page >= this.pagination.pageCount || this.options.itemsPerPage === -1, this.$vuetify.lang.t("$vuetify.dataFooter.lastPage"), this.$vuetify.rtl ? this.firstIcon : this.lastIcon));
      }
      return [this.$createElement("div", {
        staticClass: "v-data-footer__icons-before"
      }, before), this.showCurrentPage && this.$createElement("span", [this.options.page.toString()]), this.$createElement("div", {
        staticClass: "v-data-footer__icons-after"
      }, after)];
    }
  },
  render() {
    return this.$createElement("div", {
      staticClass: "v-data-footer"
    }, [this.genItemsPerPageSelect(), this.genPaginationInfo(), this.genIcons()]);
  }
});
var VDataIterator = mixins(Mobile, Themeable$1).extend({
  name: "v-data-iterator",
  props: __spreadProps(__spreadValues({}, VData.options.props), {
    itemKey: {
      type: String,
      default: "id"
    },
    value: {
      type: Array,
      default: () => []
    },
    singleSelect: Boolean,
    expanded: {
      type: Array,
      default: () => []
    },
    mobileBreakpoint: __spreadProps(__spreadValues({}, Mobile.options.props.mobileBreakpoint), {
      default: 600
    }),
    singleExpand: Boolean,
    loading: [Boolean, String],
    noResultsText: {
      type: String,
      default: "$vuetify.dataIterator.noResultsText"
    },
    noDataText: {
      type: String,
      default: "$vuetify.noDataText"
    },
    loadingText: {
      type: String,
      default: "$vuetify.dataIterator.loadingText"
    },
    hideDefaultFooter: Boolean,
    footerProps: Object,
    selectableKey: {
      type: String,
      default: "isSelectable"
    }
  }),
  data: () => ({
    selection: {},
    expansion: {},
    internalCurrentItems: []
  }),
  computed: {
    everyItem() {
      return !!this.selectableItems.length && this.selectableItems.every((i) => this.isSelected(i));
    },
    someItems() {
      return this.selectableItems.some((i) => this.isSelected(i));
    },
    sanitizedFooterProps() {
      return camelizeObjectKeys(this.footerProps);
    },
    selectableItems() {
      return this.internalCurrentItems.filter((item) => this.isSelectable(item));
    }
  },
  watch: {
    value: {
      handler(value) {
        this.selection = value.reduce((selection, item) => {
          selection[getObjectValueByPath(item, this.itemKey)] = item;
          return selection;
        }, {});
      },
      immediate: true
    },
    selection(value, old) {
      if (deepEqual(Object.keys(value), Object.keys(old)))
        return;
      this.$emit("input", Object.values(value));
    },
    expanded: {
      handler(value) {
        this.expansion = value.reduce((expansion, item) => {
          expansion[getObjectValueByPath(item, this.itemKey)] = true;
          return expansion;
        }, {});
      },
      immediate: true
    },
    expansion(value, old) {
      if (deepEqual(value, old))
        return;
      const keys2 = Object.keys(value).filter((k) => value[k]);
      const expanded = !keys2.length ? [] : this.items.filter((i) => keys2.includes(String(getObjectValueByPath(i, this.itemKey))));
      this.$emit("update:expanded", expanded);
    }
  },
  created() {
    const breakingProps = [["disable-initial-sort", "sort-by"], ["filter", "custom-filter"], ["pagination", "options"], ["total-items", "server-items-length"], ["hide-actions", "hide-default-footer"], ["rows-per-page-items", "footer-props.items-per-page-options"], ["rows-per-page-text", "footer-props.items-per-page-text"], ["prev-icon", "footer-props.prev-icon"], ["next-icon", "footer-props.next-icon"]];
    breakingProps.forEach(([original, replacement]) => {
      if (this.$attrs.hasOwnProperty(original))
        breaking(original, replacement, this);
    });
    const removedProps = ["expand", "content-class", "content-props", "content-tag"];
    removedProps.forEach((prop) => {
      if (this.$attrs.hasOwnProperty(prop))
        removed(prop);
    });
  },
  methods: {
    toggleSelectAll(value) {
      const selection = Object.assign({}, this.selection);
      for (let i = 0; i < this.selectableItems.length; i++) {
        const item = this.selectableItems[i];
        if (!this.isSelectable(item))
          continue;
        const key = getObjectValueByPath(item, this.itemKey);
        if (value)
          selection[key] = item;
        else
          delete selection[key];
      }
      this.selection = selection;
      this.$emit("toggle-select-all", {
        items: this.internalCurrentItems,
        value
      });
    },
    isSelectable(item) {
      return getObjectValueByPath(item, this.selectableKey) !== false;
    },
    isSelected(item) {
      return !!this.selection[getObjectValueByPath(item, this.itemKey)] || false;
    },
    select(item, value = true, emit = true) {
      if (!this.isSelectable(item))
        return;
      const selection = this.singleSelect ? {} : Object.assign({}, this.selection);
      const key = getObjectValueByPath(item, this.itemKey);
      if (value)
        selection[key] = item;
      else
        delete selection[key];
      if (this.singleSelect && emit) {
        const keys2 = Object.keys(this.selection);
        const old = keys2.length && getObjectValueByPath(this.selection[keys2[0]], this.itemKey);
        old && old !== key && this.$emit("item-selected", {
          item: this.selection[old],
          value: false
        });
      }
      this.selection = selection;
      emit && this.$emit("item-selected", {
        item,
        value
      });
    },
    isExpanded(item) {
      return this.expansion[getObjectValueByPath(item, this.itemKey)] || false;
    },
    expand(item, value = true) {
      const expansion = this.singleExpand ? {} : Object.assign({}, this.expansion);
      const key = getObjectValueByPath(item, this.itemKey);
      if (value)
        expansion[key] = true;
      else
        delete expansion[key];
      this.expansion = expansion;
      this.$emit("item-expanded", {
        item,
        value
      });
    },
    createItemProps(item, index) {
      return {
        item,
        index,
        select: (v) => this.select(item, v),
        isSelected: this.isSelected(item),
        expand: (v) => this.expand(item, v),
        isExpanded: this.isExpanded(item),
        isMobile: this.isMobile
      };
    },
    genEmptyWrapper(content) {
      return this.$createElement("div", content);
    },
    genEmpty(originalItemsLength, filteredItemsLength) {
      if (originalItemsLength === 0 && this.loading) {
        const loading = this.$slots.loading || this.$vuetify.lang.t(this.loadingText);
        return this.genEmptyWrapper(loading);
      } else if (originalItemsLength === 0) {
        const noData = this.$slots["no-data"] || this.$vuetify.lang.t(this.noDataText);
        return this.genEmptyWrapper(noData);
      } else if (filteredItemsLength === 0) {
        const noResults = this.$slots["no-results"] || this.$vuetify.lang.t(this.noResultsText);
        return this.genEmptyWrapper(noResults);
      }
      return null;
    },
    genItems(props2) {
      const empty = this.genEmpty(props2.originalItemsLength, props2.pagination.itemsLength);
      if (empty)
        return [empty];
      if (this.$scopedSlots.default) {
        return this.$scopedSlots.default(__spreadProps(__spreadValues({}, props2), {
          isSelected: this.isSelected,
          select: this.select,
          isExpanded: this.isExpanded,
          isMobile: this.isMobile,
          expand: this.expand
        }));
      }
      if (this.$scopedSlots.item) {
        return props2.items.map((item, index) => this.$scopedSlots.item(this.createItemProps(item, index)));
      }
      return [];
    },
    genFooter(props2) {
      if (this.hideDefaultFooter)
        return null;
      const data = {
        props: __spreadProps(__spreadValues({}, this.sanitizedFooterProps), {
          options: props2.options,
          pagination: props2.pagination
        }),
        on: {
          "update:options": (value) => props2.updateOptions(value)
        }
      };
      const scopedSlots = getPrefixedScopedSlots("footer.", this.$scopedSlots);
      return this.$createElement(VDataFooter, __spreadValues({
        scopedSlots
      }, data));
    },
    genDefaultScopedSlot(props2) {
      const outerProps = __spreadProps(__spreadValues({}, props2), {
        someItems: this.someItems,
        everyItem: this.everyItem,
        toggleSelectAll: this.toggleSelectAll
      });
      return this.$createElement("div", {
        staticClass: "v-data-iterator"
      }, [getSlot(this, "header", outerProps, true), this.genItems(props2), this.genFooter(props2), getSlot(this, "footer", outerProps, true)]);
    }
  },
  render() {
    return this.$createElement(VData, {
      props: this.$props,
      on: {
        "update:options": (v, old) => !deepEqual(v, old) && this.$emit("update:options", v),
        "update:page": (v) => this.$emit("update:page", v),
        "update:items-per-page": (v) => this.$emit("update:items-per-page", v),
        "update:sort-by": (v) => this.$emit("update:sort-by", v),
        "update:sort-desc": (v) => this.$emit("update:sort-desc", v),
        "update:group-by": (v) => this.$emit("update:group-by", v),
        "update:group-desc": (v) => this.$emit("update:group-desc", v),
        pagination: (v, old) => !deepEqual(v, old) && this.$emit("pagination", v),
        "current-items": (v) => {
          this.internalCurrentItems = v;
          this.$emit("current-items", v);
        },
        "page-count": (v) => this.$emit("page-count", v)
      },
      scopedSlots: {
        default: this.genDefaultScopedSlot
      }
    });
  }
});
var header = mixins().extend({
  directives: {
    ripple
  },
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    options: {
      type: Object,
      default: () => ({
        page: 1,
        itemsPerPage: 10,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        multiSort: false,
        mustSort: false
      })
    },
    sortIcon: {
      type: String,
      default: "$sort"
    },
    everyItem: Boolean,
    someItems: Boolean,
    showGroupBy: Boolean,
    singleSelect: Boolean,
    disableSort: Boolean
  },
  methods: {
    genSelectAll() {
      const data = {
        props: {
          value: this.everyItem,
          indeterminate: !this.everyItem && this.someItems
        },
        on: {
          input: (v) => this.$emit("toggle-select-all", v)
        }
      };
      if (this.$scopedSlots["data-table-select"]) {
        return this.$scopedSlots["data-table-select"](data);
      }
      return this.$createElement(VSimpleCheckbox, __spreadValues({
        staticClass: "v-data-table__checkbox"
      }, data));
    },
    genSortIcon() {
      return this.$createElement(VIcon$1, {
        staticClass: "v-data-table-header__icon",
        props: {
          size: 18
        }
      }, [this.sortIcon]);
    }
  }
});
var VDataTableHeaderMobile = mixins(header).extend({
  name: "v-data-table-header-mobile",
  props: {
    sortByText: {
      type: String,
      default: "$vuetify.dataTable.sortBy"
    }
  },
  methods: {
    genSortChip(props2) {
      const children = [props2.item.text];
      const sortIndex = this.options.sortBy.findIndex((k) => k === props2.item.value);
      const beingSorted = sortIndex >= 0;
      const isDesc = this.options.sortDesc[sortIndex];
      children.push(this.$createElement("div", {
        staticClass: "v-chip__close",
        class: {
          sortable: true,
          active: beingSorted,
          asc: beingSorted && !isDesc,
          desc: beingSorted && isDesc
        }
      }, [this.genSortIcon()]));
      return this.$createElement(VChip, {
        staticClass: "sortable",
        on: {
          click: (e) => {
            e.stopPropagation();
            this.$emit("sort", props2.item.value);
          }
        }
      }, children);
    },
    genSortSelect(items) {
      return this.$createElement(VSelect, {
        props: {
          label: this.$vuetify.lang.t(this.sortByText),
          items,
          hideDetails: true,
          multiple: this.options.multiSort,
          value: this.options.multiSort ? this.options.sortBy : this.options.sortBy[0],
          menuProps: {
            closeOnContentClick: true
          }
        },
        on: {
          change: (v) => this.$emit("sort", v)
        },
        scopedSlots: {
          selection: (props2) => this.genSortChip(props2)
        }
      });
    }
  },
  render(h) {
    const children = [];
    const header2 = this.headers.find((h2) => h2.value === "data-table-select");
    if (header2 && !this.singleSelect) {
      children.push(this.$createElement("div", {
        class: ["v-data-table-header-mobile__select", ...wrapInArray(header2.class)],
        attrs: {
          width: header2.width
        }
      }, [this.genSelectAll()]));
    }
    const sortHeaders = this.headers.filter((h2) => h2.sortable !== false && h2.value !== "data-table-select").map((h2) => ({
      text: h2.text,
      value: h2.value
    }));
    if (!this.disableSort && sortHeaders.length) {
      children.push(this.genSortSelect(sortHeaders));
    }
    const th = h("th", [h("div", {
      staticClass: "v-data-table-header-mobile__wrapper"
    }, children)]);
    const tr = h("tr", [th]);
    return h("thead", {
      staticClass: "v-data-table-header v-data-table-header-mobile"
    }, [tr]);
  }
});
var VDataTableHeaderDesktop = mixins(header).extend({
  name: "v-data-table-header-desktop",
  methods: {
    genGroupByToggle(header2) {
      return this.$createElement("span", {
        on: {
          click: (e) => {
            e.stopPropagation();
            this.$emit("group", header2.value);
          }
        }
      }, ["group"]);
    },
    getAria(beingSorted, isDesc) {
      const $t = (key) => this.$vuetify.lang.t(`$vuetify.dataTable.ariaLabel.${key}`);
      let ariaSort = "none";
      let ariaLabel = [$t("sortNone"), $t("activateAscending")];
      if (!beingSorted) {
        return {
          ariaSort,
          ariaLabel: ariaLabel.join(" ")
        };
      }
      if (isDesc) {
        ariaSort = "descending";
        ariaLabel = [$t("sortDescending"), $t(this.options.mustSort ? "activateAscending" : "activateNone")];
      } else {
        ariaSort = "ascending";
        ariaLabel = [$t("sortAscending"), $t("activateDescending")];
      }
      return {
        ariaSort,
        ariaLabel: ariaLabel.join(" ")
      };
    },
    genHeader(header2) {
      const data = {
        attrs: {
          role: "columnheader",
          scope: "col",
          "aria-label": header2.text || ""
        },
        style: {
          width: convertToUnit(header2.width),
          minWidth: convertToUnit(header2.width)
        },
        class: [`text-${header2.align || "start"}`, ...wrapInArray(header2.class), header2.divider && "v-data-table__divider"],
        on: {}
      };
      const children = [];
      if (header2.value === "data-table-select" && !this.singleSelect) {
        return this.$createElement("th", data, [this.genSelectAll()]);
      }
      children.push(this.$scopedSlots[header2.value] ? this.$scopedSlots[header2.value]({
        header: header2
      }) : this.$createElement("span", [header2.text]));
      if (!this.disableSort && (header2.sortable || !header2.hasOwnProperty("sortable"))) {
        data.on.click = () => this.$emit("sort", header2.value);
        const sortIndex = this.options.sortBy.findIndex((k) => k === header2.value);
        const beingSorted = sortIndex >= 0;
        const isDesc = this.options.sortDesc[sortIndex];
        data.class.push("sortable");
        const {
          ariaLabel,
          ariaSort
        } = this.getAria(beingSorted, isDesc);
        data.attrs["aria-label"] += `${header2.text ? ": " : ""}${ariaLabel}`;
        data.attrs["aria-sort"] = ariaSort;
        if (beingSorted) {
          data.class.push("active");
          data.class.push(isDesc ? "desc" : "asc");
        }
        if (header2.align === "end")
          children.unshift(this.genSortIcon());
        else
          children.push(this.genSortIcon());
        if (this.options.multiSort && beingSorted) {
          children.push(this.$createElement("span", {
            class: "v-data-table-header__sort-badge"
          }, [String(sortIndex + 1)]));
        }
      }
      if (this.showGroupBy && header2.groupable !== false)
        children.push(this.genGroupByToggle(header2));
      return this.$createElement("th", data, children);
    }
  },
  render() {
    return this.$createElement("thead", {
      staticClass: "v-data-table-header"
    }, [this.$createElement("tr", this.headers.map((header2) => this.genHeader(header2)))]);
  }
});
function dedupeModelListeners(data) {
  if (data.model && data.on && data.on.input) {
    if (Array.isArray(data.on.input)) {
      const i = data.on.input.indexOf(data.model.callback);
      if (i > -1)
        data.on.input.splice(i, 1);
    } else {
      delete data.on.input;
    }
  }
}
function rebuildFunctionalSlots(slots, h) {
  const children = [];
  for (const slot in slots) {
    if (slots.hasOwnProperty(slot)) {
      children.push(h("template", {
        slot
      }, slots[slot]));
    }
  }
  return children;
}
var VDataTableHeader = Vue.extend({
  name: "v-data-table-header",
  functional: true,
  props: __spreadProps(__spreadValues({}, header.options.props), {
    mobile: Boolean
  }),
  render(h, {
    props: props2,
    data,
    slots
  }) {
    dedupeModelListeners(data);
    const children = rebuildFunctionalSlots(slots(), h);
    data = mergeData(data, {
      props: props2
    });
    if (props2.mobile) {
      return h(VDataTableHeaderMobile, data, children);
    } else {
      return h(VDataTableHeaderDesktop, data, children);
    }
  }
});
var Row = Vue.extend({
  name: "row",
  functional: true,
  props: {
    headers: Array,
    index: Number,
    item: Object,
    rtl: Boolean
  },
  render(h, {
    props: props2,
    slots,
    data
  }) {
    const computedSlots = slots();
    const columns = props2.headers.map((header2) => {
      const children = [];
      const value = getObjectValueByPath(props2.item, header2.value);
      const slotName = header2.value;
      const scopedSlot = data.scopedSlots && data.scopedSlots[slotName];
      const regularSlot = computedSlots[slotName];
      if (scopedSlot) {
        children.push(scopedSlot({
          item: props2.item,
          isMobile: false,
          header: header2,
          index: props2.index,
          value
        }));
      } else if (regularSlot) {
        children.push(regularSlot);
      } else {
        children.push(value == null ? value : String(value));
      }
      const textAlign = `text-${header2.align || "start"}`;
      return h("td", {
        class: [textAlign, header2.cellClass, {
          "v-data-table__divider": header2.divider
        }]
      }, children);
    });
    return h("tr", data, columns);
  }
});
var RowGroup = Vue.extend({
  name: "row-group",
  functional: true,
  props: {
    value: {
      type: Boolean,
      default: true
    },
    headerClass: {
      type: String,
      default: "v-row-group__header"
    },
    contentClass: String,
    summaryClass: {
      type: String,
      default: "v-row-group__summary"
    }
  },
  render(h, {
    slots,
    props: props2
  }) {
    const computedSlots = slots();
    const children = [];
    if (computedSlots["column.header"]) {
      children.push(h("tr", {
        staticClass: props2.headerClass
      }, computedSlots["column.header"]));
    } else if (computedSlots["row.header"]) {
      children.push(...computedSlots["row.header"]);
    }
    if (computedSlots["row.content"] && props2.value)
      children.push(...computedSlots["row.content"]);
    if (computedSlots["column.summary"]) {
      children.push(h("tr", {
        staticClass: props2.summaryClass
      }, computedSlots["column.summary"]));
    } else if (computedSlots["row.summary"]) {
      children.push(...computedSlots["row.summary"]);
    }
    return children;
  }
});
var VSimpleTable = mixins(Themeable$1).extend({
  name: "v-simple-table",
  props: {
    dense: Boolean,
    fixedHeader: Boolean,
    height: [Number, String]
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-data-table--dense": this.dense,
        "v-data-table--fixed-height": !!this.height && !this.fixedHeader,
        "v-data-table--fixed-header": this.fixedHeader,
        "v-data-table--has-top": !!this.$slots.top,
        "v-data-table--has-bottom": !!this.$slots.bottom
      }, this.themeClasses);
    }
  },
  methods: {
    genWrapper() {
      return this.$slots.wrapper || this.$createElement("div", {
        staticClass: "v-data-table__wrapper",
        style: {
          height: convertToUnit(this.height)
        }
      }, [this.$createElement("table", this.$slots.default)]);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-data-table",
      class: this.classes
    }, [this.$slots.top, this.genWrapper(), this.$slots.bottom]);
  }
});
var MobileRow = Vue.extend({
  name: "row",
  functional: true,
  props: {
    headers: Array,
    hideDefaultHeader: Boolean,
    index: Number,
    item: Object,
    rtl: Boolean
  },
  render(h, {
    props: props2,
    slots,
    data
  }) {
    const computedSlots = slots();
    const columns = props2.headers.map((header2) => {
      const classes = {
        "v-data-table__mobile-row": true
      };
      const children = [];
      const value = getObjectValueByPath(props2.item, header2.value);
      const slotName = header2.value;
      const scopedSlot = data.scopedSlots && data.scopedSlots[slotName];
      const regularSlot = computedSlots[slotName];
      if (scopedSlot) {
        children.push(scopedSlot({
          item: props2.item,
          isMobile: true,
          header: header2,
          index: props2.index,
          value
        }));
      } else if (regularSlot) {
        children.push(regularSlot);
      } else {
        children.push(value == null ? value : String(value));
      }
      const mobileRowChildren = [h("div", {
        staticClass: "v-data-table__mobile-row__cell"
      }, children)];
      if (header2.value !== "dataTableSelect" && !props2.hideDefaultHeader) {
        mobileRowChildren.unshift(h("div", {
          staticClass: "v-data-table__mobile-row__header"
        }, [header2.text]));
      }
      return h("td", {
        class: classes
      }, mobileRowChildren);
    });
    return h("tr", __spreadProps(__spreadValues({}, data), {
      staticClass: "v-data-table__mobile-table-row"
    }), columns);
  }
});
function filterFn(item, search, filter) {
  return (header2) => {
    const value = getObjectValueByPath(item, header2.value);
    return header2.filter ? header2.filter(value, search, item) : filter(value, search, item);
  };
}
function searchTableItems(items, search, headersWithCustomFilters, headersWithoutCustomFilters, customFilter) {
  search = typeof search === "string" ? search.trim() : null;
  return items.filter((item) => {
    const matchesColumnFilters = headersWithCustomFilters.every(filterFn(item, search, defaultFilter));
    const matchesSearchTerm = !search || headersWithoutCustomFilters.some(filterFn(item, search, customFilter));
    return matchesColumnFilters && matchesSearchTerm;
  });
}
mixins(VDataIterator, Loadable).extend({
  name: "v-data-table",
  directives: {
    ripple
  },
  props: {
    headers: {
      type: Array,
      default: () => []
    },
    showSelect: Boolean,
    showExpand: Boolean,
    showGroupBy: Boolean,
    height: [Number, String],
    hideDefaultHeader: Boolean,
    caption: String,
    dense: Boolean,
    headerProps: Object,
    calculateWidths: Boolean,
    fixedHeader: Boolean,
    headersLength: Number,
    expandIcon: {
      type: String,
      default: "$expand"
    },
    customFilter: {
      type: Function,
      default: defaultFilter
    },
    itemClass: {
      type: [String, Function],
      default: () => ""
    },
    loaderHeight: {
      type: [Number, String],
      default: 4
    }
  },
  data() {
    return {
      internalGroupBy: [],
      openCache: {},
      widths: []
    };
  },
  computed: {
    computedHeaders() {
      if (!this.headers)
        return [];
      const headers = this.headers.filter((h) => h.value === void 0 || !this.internalGroupBy.find((v) => v === h.value));
      const defaultHeader = {
        text: "",
        sortable: false,
        width: "1px"
      };
      if (this.showSelect) {
        const index = headers.findIndex((h) => h.value === "data-table-select");
        if (index < 0)
          headers.unshift(__spreadProps(__spreadValues({}, defaultHeader), {
            value: "data-table-select"
          }));
        else
          headers.splice(index, 1, __spreadValues(__spreadValues({}, defaultHeader), headers[index]));
      }
      if (this.showExpand) {
        const index = headers.findIndex((h) => h.value === "data-table-expand");
        if (index < 0)
          headers.unshift(__spreadProps(__spreadValues({}, defaultHeader), {
            value: "data-table-expand"
          }));
        else
          headers.splice(index, 1, __spreadValues(__spreadValues({}, defaultHeader), headers[index]));
      }
      return headers;
    },
    colspanAttrs() {
      return this.isMobile ? void 0 : {
        colspan: this.headersLength || this.computedHeaders.length
      };
    },
    columnSorters() {
      return this.computedHeaders.reduce((acc, header2) => {
        if (header2.sort)
          acc[header2.value] = header2.sort;
        return acc;
      }, {});
    },
    headersWithCustomFilters() {
      return this.headers.filter((header2) => header2.filter && (!header2.hasOwnProperty("filterable") || header2.filterable === true));
    },
    headersWithoutCustomFilters() {
      return this.headers.filter((header2) => !header2.filter && (!header2.hasOwnProperty("filterable") || header2.filterable === true));
    },
    sanitizedHeaderProps() {
      return camelizeObjectKeys(this.headerProps);
    },
    computedItemsPerPage() {
      const itemsPerPage = this.options && this.options.itemsPerPage ? this.options.itemsPerPage : this.itemsPerPage;
      const itemsPerPageOptions = this.sanitizedFooterProps.itemsPerPageOptions;
      if (itemsPerPageOptions && !itemsPerPageOptions.find((item) => typeof item === "number" ? item === itemsPerPage : item.value === itemsPerPage)) {
        const firstOption = itemsPerPageOptions[0];
        return typeof firstOption === "object" ? firstOption.value : firstOption;
      }
      return itemsPerPage;
    }
  },
  created() {
    const breakingProps = [["sort-icon", "header-props.sort-icon"], ["hide-headers", "hide-default-header"], ["select-all", "show-select"]];
    breakingProps.forEach(([original, replacement]) => {
      if (this.$attrs.hasOwnProperty(original))
        breaking(original, replacement, this);
    });
  },
  mounted() {
    if (this.calculateWidths) {
      window.addEventListener("resize", this.calcWidths);
      this.calcWidths();
    }
  },
  beforeDestroy() {
    if (this.calculateWidths) {
      window.removeEventListener("resize", this.calcWidths);
    }
  },
  methods: {
    calcWidths() {
      this.widths = Array.from(this.$el.querySelectorAll("th")).map((e) => e.clientWidth);
    },
    customFilterWithColumns(items, search) {
      return searchTableItems(items, search, this.headersWithCustomFilters, this.headersWithoutCustomFilters, this.customFilter);
    },
    customSortWithHeaders(items, sortBy, sortDesc, locale) {
      return this.customSort(items, sortBy, sortDesc, locale, this.columnSorters);
    },
    createItemProps(item, index) {
      const props2 = VDataIterator.options.methods.createItemProps.call(this, item, index);
      return Object.assign(props2, {
        headers: this.computedHeaders
      });
    },
    genCaption(props2) {
      if (this.caption)
        return [this.$createElement("caption", [this.caption])];
      return getSlot(this, "caption", props2, true);
    },
    genColgroup(props2) {
      return this.$createElement("colgroup", this.computedHeaders.map((header2) => {
        return this.$createElement("col", {
          class: {
            divider: header2.divider
          }
        });
      }));
    },
    genLoading() {
      const th = this.$createElement("th", {
        staticClass: "column",
        attrs: this.colspanAttrs
      }, [this.genProgress()]);
      const tr = this.$createElement("tr", {
        staticClass: "v-data-table__progress"
      }, [th]);
      return this.$createElement("thead", [tr]);
    },
    genHeaders(props2) {
      const data = {
        props: __spreadProps(__spreadValues({}, this.sanitizedHeaderProps), {
          headers: this.computedHeaders,
          options: props2.options,
          mobile: this.isMobile,
          showGroupBy: this.showGroupBy,
          someItems: this.someItems,
          everyItem: this.everyItem,
          singleSelect: this.singleSelect,
          disableSort: this.disableSort
        }),
        on: {
          sort: props2.sort,
          group: props2.group,
          "toggle-select-all": this.toggleSelectAll
        }
      };
      const children = [getSlot(this, "header", __spreadProps(__spreadValues({}, data), {
        isMobile: this.isMobile
      }))];
      if (!this.hideDefaultHeader) {
        const scopedSlots = getPrefixedScopedSlots("header.", this.$scopedSlots);
        children.push(this.$createElement(VDataTableHeader, __spreadProps(__spreadValues({}, data), {
          scopedSlots
        })));
      }
      if (this.loading)
        children.push(this.genLoading());
      return children;
    },
    genEmptyWrapper(content) {
      return this.$createElement("tr", {
        staticClass: "v-data-table__empty-wrapper"
      }, [this.$createElement("td", {
        attrs: this.colspanAttrs
      }, content)]);
    },
    genItems(items, props2) {
      const empty = this.genEmpty(props2.originalItemsLength, props2.pagination.itemsLength);
      if (empty)
        return [empty];
      return props2.groupedItems ? this.genGroupedRows(props2.groupedItems, props2) : this.genRows(items, props2);
    },
    genGroupedRows(groupedItems, props2) {
      return groupedItems.map((group) => {
        if (!this.openCache.hasOwnProperty(group.name))
          this.$set(this.openCache, group.name, true);
        if (this.$scopedSlots.group) {
          return this.$scopedSlots.group({
            group: group.name,
            options: props2.options,
            isMobile: this.isMobile,
            items: group.items,
            headers: this.computedHeaders
          });
        } else {
          return this.genDefaultGroupedRow(group.name, group.items, props2);
        }
      });
    },
    genDefaultGroupedRow(group, items, props2) {
      const isOpen = !!this.openCache[group];
      const children = [this.$createElement("template", {
        slot: "row.content"
      }, this.genRows(items, props2))];
      const toggleFn = () => this.$set(this.openCache, group, !this.openCache[group]);
      const removeFn = () => props2.updateOptions({
        groupBy: [],
        groupDesc: []
      });
      if (this.$scopedSlots["group.header"]) {
        children.unshift(this.$createElement("template", {
          slot: "column.header"
        }, [this.$scopedSlots["group.header"]({
          group,
          groupBy: props2.options.groupBy,
          isMobile: this.isMobile,
          items,
          headers: this.computedHeaders,
          isOpen,
          toggle: toggleFn,
          remove: removeFn
        })]));
      } else {
        const toggle = this.$createElement(VBtn, {
          staticClass: "ma-0",
          props: {
            icon: true,
            small: true
          },
          on: {
            click: toggleFn
          }
        }, [this.$createElement(VIcon$1, [isOpen ? "$minus" : "$plus"])]);
        const remove = this.$createElement(VBtn, {
          staticClass: "ma-0",
          props: {
            icon: true,
            small: true
          },
          on: {
            click: removeFn
          }
        }, [this.$createElement(VIcon$1, ["$close"])]);
        const column2 = this.$createElement("td", {
          staticClass: "text-start",
          attrs: this.colspanAttrs
        }, [toggle, `${props2.options.groupBy[0]}: ${group}`, remove]);
        children.unshift(this.$createElement("template", {
          slot: "column.header"
        }, [column2]));
      }
      if (this.$scopedSlots["group.summary"]) {
        children.push(this.$createElement("template", {
          slot: "column.summary"
        }, [this.$scopedSlots["group.summary"]({
          group,
          groupBy: props2.options.groupBy,
          isMobile: this.isMobile,
          items,
          headers: this.computedHeaders,
          isOpen,
          toggle: toggleFn
        })]));
      }
      return this.$createElement(RowGroup, {
        key: group,
        props: {
          value: isOpen
        }
      }, children);
    },
    genRows(items, props2) {
      return this.$scopedSlots.item ? this.genScopedRows(items, props2) : this.genDefaultRows(items, props2);
    },
    genScopedRows(items, props2) {
      const rows = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        rows.push(this.$scopedSlots.item(__spreadProps(__spreadValues({}, this.createItemProps(item, i)), {
          isMobile: this.isMobile
        })));
        if (this.isExpanded(item)) {
          rows.push(this.$scopedSlots["expanded-item"]({
            headers: this.computedHeaders,
            isMobile: this.isMobile,
            index: i,
            item
          }));
        }
      }
      return rows;
    },
    genDefaultRows(items, props2) {
      return this.$scopedSlots["expanded-item"] ? items.map((item, index) => this.genDefaultExpandedRow(item, index)) : items.map((item, index) => this.genDefaultSimpleRow(item, index));
    },
    genDefaultExpandedRow(item, index) {
      const isExpanded = this.isExpanded(item);
      const classes = {
        "v-data-table__expanded v-data-table__expanded__row": isExpanded
      };
      const headerRow = this.genDefaultSimpleRow(item, index, classes);
      const expandedRow = this.$createElement("tr", {
        staticClass: "v-data-table__expanded v-data-table__expanded__content"
      }, [this.$scopedSlots["expanded-item"]({
        headers: this.computedHeaders,
        isMobile: this.isMobile,
        item
      })]);
      return this.$createElement(RowGroup, {
        props: {
          value: isExpanded
        }
      }, [this.$createElement("template", {
        slot: "row.header"
      }, [headerRow]), this.$createElement("template", {
        slot: "row.content"
      }, [expandedRow])]);
    },
    genDefaultSimpleRow(item, index, classes = {}) {
      const scopedSlots = getPrefixedScopedSlots("item.", this.$scopedSlots);
      const data = this.createItemProps(item, index);
      if (this.showSelect) {
        const slot = scopedSlots["data-table-select"];
        scopedSlots["data-table-select"] = slot ? () => slot(__spreadProps(__spreadValues({}, data), {
          isMobile: this.isMobile
        })) : () => this.$createElement(VSimpleCheckbox, {
          staticClass: "v-data-table__checkbox",
          props: {
            value: data.isSelected,
            disabled: !this.isSelectable(item)
          },
          on: {
            input: (val) => data.select(val)
          }
        });
      }
      if (this.showExpand) {
        const slot = scopedSlots["data-table-expand"];
        scopedSlots["data-table-expand"] = slot ? () => slot(data) : () => this.$createElement(VIcon$1, {
          staticClass: "v-data-table__expand-icon",
          class: {
            "v-data-table__expand-icon--active": data.isExpanded
          },
          on: {
            click: (e) => {
              e.stopPropagation();
              data.expand(!data.isExpanded);
            }
          }
        }, [this.expandIcon]);
      }
      return this.$createElement(this.isMobile ? MobileRow : Row, {
        key: getObjectValueByPath(item, this.itemKey),
        class: mergeClasses(__spreadProps(__spreadValues({}, classes), {
          "v-data-table__selected": data.isSelected
        }), getPropertyFromItem(item, this.itemClass)),
        props: {
          headers: this.computedHeaders,
          hideDefaultHeader: this.hideDefaultHeader,
          index,
          item,
          rtl: this.$vuetify.rtl
        },
        scopedSlots,
        on: {
          click: () => this.$emit("click:row", item, data),
          contextmenu: (event) => this.$emit("contextmenu:row", event, data),
          dblclick: (event) => this.$emit("dblclick:row", event, data)
        }
      });
    },
    genBody(props2) {
      const data = __spreadProps(__spreadValues({}, props2), {
        expand: this.expand,
        headers: this.computedHeaders,
        isExpanded: this.isExpanded,
        isMobile: this.isMobile,
        isSelected: this.isSelected,
        select: this.select
      });
      if (this.$scopedSlots.body) {
        return this.$scopedSlots.body(data);
      }
      return this.$createElement("tbody", [getSlot(this, "body.prepend", data, true), this.genItems(props2.items, props2), getSlot(this, "body.append", data, true)]);
    },
    genFooters(props2) {
      const data = {
        props: __spreadValues({
          options: props2.options,
          pagination: props2.pagination,
          itemsPerPageText: "$vuetify.dataTable.itemsPerPageText"
        }, this.sanitizedFooterProps),
        on: {
          "update:options": (value) => props2.updateOptions(value)
        },
        widths: this.widths,
        headers: this.computedHeaders
      };
      const children = [getSlot(this, "footer", data, true)];
      if (!this.hideDefaultFooter) {
        children.push(this.$createElement(VDataFooter, __spreadProps(__spreadValues({}, data), {
          scopedSlots: getPrefixedScopedSlots("footer.", this.$scopedSlots)
        })));
      }
      return children;
    },
    genDefaultScopedSlot(props2) {
      const simpleProps = {
        height: this.height,
        fixedHeader: this.fixedHeader,
        dense: this.dense
      };
      return this.$createElement(VSimpleTable, {
        props: simpleProps
      }, [this.proxySlot("top", getSlot(this, "top", __spreadProps(__spreadValues({}, props2), {
        isMobile: this.isMobile
      }), true)), this.genCaption(props2), this.genColgroup(props2), this.genHeaders(props2), this.genBody(props2), this.proxySlot("bottom", this.genFooters(props2))]);
    },
    proxySlot(slot, content) {
      return this.$createElement("template", {
        slot
      }, content);
    }
  },
  render() {
    return this.$createElement(VData, {
      props: __spreadProps(__spreadValues({}, this.$props), {
        customFilter: this.customFilterWithColumns,
        customSort: this.customSortWithHeaders,
        itemsPerPage: this.computedItemsPerPage
      }),
      on: {
        "update:options": (v, old) => {
          this.internalGroupBy = v.groupBy || [];
          !deepEqual(v, old) && this.$emit("update:options", v);
        },
        "update:page": (v) => this.$emit("update:page", v),
        "update:items-per-page": (v) => this.$emit("update:items-per-page", v),
        "update:sort-by": (v) => this.$emit("update:sort-by", v),
        "update:sort-desc": (v) => this.$emit("update:sort-desc", v),
        "update:group-by": (v) => this.$emit("update:group-by", v),
        "update:group-desc": (v) => this.$emit("update:group-desc", v),
        pagination: (v, old) => !deepEqual(v, old) && this.$emit("pagination", v),
        "current-items": (v) => {
          this.internalCurrentItems = v;
          this.$emit("current-items", v);
        },
        "page-count": (v) => this.$emit("page-count", v)
      },
      scopedSlots: {
        default: this.genDefaultScopedSlot
      }
    });
  }
});
var VEditDialog = mixins(Returnable, Themeable$1).extend({
  name: "v-edit-dialog",
  props: {
    cancelText: {
      default: "Cancel"
    },
    large: Boolean,
    eager: Boolean,
    persistent: Boolean,
    saveText: {
      default: "Save"
    },
    transition: {
      type: String,
      default: "slide-x-reverse-transition"
    }
  },
  data() {
    return {
      isActive: false
    };
  },
  watch: {
    isActive(val) {
      if (val) {
        this.$emit("open");
        setTimeout(this.focus, 50);
      } else {
        this.$emit("close");
      }
    }
  },
  methods: {
    cancel() {
      this.isActive = false;
      this.$emit("cancel");
    },
    focus() {
      const input = this.$refs.content.querySelector("input");
      input && input.focus();
    },
    genButton(fn, text) {
      return this.$createElement(VBtn, {
        props: {
          text: true,
          color: "primary",
          light: true
        },
        on: {
          click: fn
        }
      }, text);
    },
    genActions() {
      return this.$createElement("div", {
        class: "v-small-dialog__actions"
      }, [this.genButton(this.cancel, this.cancelText), this.genButton(() => {
        this.save(this.returnValue);
        this.$emit("save");
      }, this.saveText)]);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-small-dialog__content",
        on: {
          keydown: (e) => {
            e.keyCode === keyCodes.esc && this.cancel();
            if (e.keyCode === keyCodes.enter) {
              this.save(this.returnValue);
              this.$emit("save");
            }
          }
        },
        ref: "content"
      }, [this.$slots.input]);
    }
  },
  render(h) {
    return h(VMenu, {
      staticClass: "v-small-dialog",
      class: this.themeClasses,
      props: {
        contentClass: "v-small-dialog__menu-content",
        transition: this.transition,
        origin: "top right",
        right: true,
        value: this.isActive,
        closeOnClick: !this.persistent,
        closeOnContentClick: false,
        eager: this.eager,
        light: this.light,
        dark: this.dark
      },
      on: {
        input: (val) => this.isActive = val
      },
      scopedSlots: {
        activator: ({
          on
        }) => {
          return h("div", {
            staticClass: "v-small-dialog__activator",
            on
          }, [h("span", {
            staticClass: "v-small-dialog__activator__content"
          }, this.$slots.default)]);
        }
      }
    }, [this.genContent(), this.large ? this.genActions() : null]);
  }
});
const baseMixins$e = mixins(VSimpleTable);
var VVirtualTable = baseMixins$e.extend().extend({
  name: "v-virtual-table",
  props: {
    chunkSize: {
      type: Number,
      default: 25
    },
    headerHeight: {
      type: Number,
      default: 48
    },
    items: {
      type: Array,
      default: () => []
    },
    rowHeight: {
      type: Number,
      default: 48
    }
  },
  data: () => ({
    scrollTop: 0,
    oldChunk: 0,
    scrollDebounce: null,
    invalidateCache: false
  }),
  computed: {
    itemsLength() {
      return this.items.length;
    },
    totalHeight() {
      return this.itemsLength * this.rowHeight + this.headerHeight;
    },
    topIndex() {
      return Math.floor(this.scrollTop / this.rowHeight);
    },
    chunkIndex() {
      return Math.floor(this.topIndex / this.chunkSize);
    },
    startIndex() {
      return Math.max(0, this.chunkIndex * this.chunkSize - this.chunkSize);
    },
    offsetTop() {
      return Math.max(0, this.startIndex * this.rowHeight);
    },
    stopIndex() {
      return Math.min(this.startIndex + this.chunkSize * 3, this.itemsLength);
    },
    offsetBottom() {
      return Math.max(0, (this.itemsLength - this.stopIndex - this.startIndex) * this.rowHeight);
    }
  },
  watch: {
    chunkIndex(newValue, oldValue) {
      this.oldChunk = oldValue;
    },
    items() {
      this.cachedItems = null;
      this.$refs.table.scrollTop = 0;
    }
  },
  created() {
    this.cachedItems = null;
  },
  mounted() {
    this.scrollDebounce = debounce(this.onScroll, 50);
    this.$refs.table.addEventListener("scroll", this.scrollDebounce, {
      passive: true
    });
  },
  beforeDestroy() {
    this.$refs.table.removeEventListener("scroll", this.scrollDebounce);
  },
  methods: {
    createStyleHeight(height) {
      return {
        height: `${height}px`
      };
    },
    genBody() {
      if (this.cachedItems === null || this.chunkIndex !== this.oldChunk) {
        this.cachedItems = this.genItems();
        this.oldChunk = this.chunkIndex;
      }
      return this.$createElement("tbody", [this.$createElement("tr", {
        style: this.createStyleHeight(this.offsetTop)
      }), this.cachedItems, this.$createElement("tr", {
        style: this.createStyleHeight(this.offsetBottom)
      })]);
    },
    genItems() {
      return this.$scopedSlots.items({
        items: this.items.slice(this.startIndex, this.stopIndex)
      });
    },
    onScroll(e) {
      const target = e.target;
      this.scrollTop = target.scrollTop;
    },
    genTable() {
      return this.$createElement("div", {
        ref: "table",
        staticClass: "v-virtual-table__table"
      }, [this.$createElement("table", [this.$slots["body.before"], this.genBody(), this.$slots["body.after"]])]);
    },
    genWrapper() {
      return this.$createElement("div", {
        staticClass: "v-virtual-table__wrapper",
        style: {
          height: convertToUnit(this.height)
        }
      }, [this.genTable()]);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-data-table v-virtual-table",
      class: this.classes
    }, [this.$slots.top, this.genWrapper(), this.$slots.bottom]);
  }
});
const VTableOverflow = createSimpleFunctional("v-table__overflow");
var PickerButton = mixins(Colorable).extend({
  methods: {
    genPickerButton(prop, value, content, readonly = false, staticClass = "") {
      const active = this[prop] === value;
      const click = (event) => {
        event.stopPropagation();
        this.$emit(`update:${kebabCase(prop)}`, value);
      };
      return this.$createElement("div", {
        staticClass: `v-picker__title__btn ${staticClass}`.trim(),
        class: {
          "v-picker__title__btn--active": active,
          "v-picker__title__btn--readonly": readonly
        },
        on: active || readonly ? void 0 : {
          click
        }
      }, Array.isArray(content) ? content : [content]);
    }
  }
});
var VDatePickerTitle = mixins(PickerButton).extend({
  name: "v-date-picker-title",
  props: {
    date: {
      type: String,
      default: ""
    },
    disabled: Boolean,
    readonly: Boolean,
    selectingYear: Boolean,
    value: {
      type: String
    },
    year: {
      type: [Number, String],
      default: ""
    },
    yearIcon: {
      type: String
    }
  },
  data: () => ({
    isReversing: false
  }),
  computed: {
    computedTransition() {
      return this.isReversing ? "picker-reverse-transition" : "picker-transition";
    }
  },
  watch: {
    value(val, prev) {
      this.isReversing = val < prev;
    }
  },
  methods: {
    genYearIcon() {
      return this.$createElement(VIcon$1, {
        props: {
          dark: true
        }
      }, this.yearIcon);
    },
    getYearBtn() {
      return this.genPickerButton("selectingYear", true, [String(this.year), this.yearIcon ? this.genYearIcon() : null], false, "v-date-picker-title__year");
    },
    genTitleText() {
      return this.$createElement("transition", {
        props: {
          name: this.computedTransition
        }
      }, [this.$createElement("div", {
        domProps: {
          innerHTML: this.date || "&nbsp;"
        },
        key: this.value
      })]);
    },
    genTitleDate() {
      return this.genPickerButton("selectingYear", false, [this.genTitleText()], false, "v-date-picker-title__date");
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-date-picker-title",
      class: {
        "v-date-picker-title--disabled": this.disabled
      }
    }, [this.getYearBtn(), this.genTitleDate()]);
  }
});
function createItemTypeNativeListeners(instance, itemTypeSuffix, value) {
  return Object.keys(instance.$listeners).reduce((on, eventName) => {
    if (eventName.endsWith(itemTypeSuffix)) {
      on[eventName.slice(0, -itemTypeSuffix.length)] = (event) => instance.$emit(eventName, value, event);
    }
    return on;
  }, {});
}
function createItemTypeListeners(instance, itemTypeSuffix) {
  return Object.keys(instance.$listeners).reduce((on, eventName) => {
    if (eventName.endsWith(itemTypeSuffix)) {
      on[eventName] = instance.$listeners[eventName];
    }
    return on;
  }, {});
}
const padStart = (string, targetLength, padString) => {
  targetLength = targetLength >> 0;
  string = String(string);
  padString = String(padString);
  if (string.length > targetLength) {
    return String(string);
  }
  targetLength = targetLength - string.length;
  if (targetLength > padString.length) {
    padString += padString.repeat(targetLength / padString.length);
  }
  return padString.slice(0, targetLength) + String(string);
};
var pad = (n, length = 2) => padStart(n, length, "0");
function createNativeLocaleFormatter(locale, options2, substrOptions = {
  start: 0,
  length: 0
}) {
  const makeIsoString = (dateString) => {
    const [year, month, date] = dateString.trim().split(" ")[0].split("-");
    return [pad(year, 4), pad(month || 1), pad(date || 1)].join("-");
  };
  try {
    const intlFormatter = new Intl.DateTimeFormat(locale || void 0, options2);
    return (dateString) => intlFormatter.format(new Date(`${makeIsoString(dateString)}T00:00:00+00:00`));
  } catch (e) {
    return substrOptions.start || substrOptions.length ? (dateString) => makeIsoString(dateString).substr(substrOptions.start || 0, substrOptions.length) : void 0;
  }
}
var monthChange = (value, sign) => {
  const [year, month] = value.split("-").map(Number);
  if (month + sign === 0) {
    return `${year - 1}-12`;
  } else if (month + sign === 13) {
    return `${year + 1}-01`;
  } else {
    return `${year}-${pad(month + sign)}`;
  }
};
var sanitizeDateString = (dateString, type2) => {
  const [year, month = 1, date = 1] = dateString.split("-");
  return `${year}-${pad(month)}-${pad(date)}`.substr(0, {
    date: 10,
    month: 7,
    year: 4
  }[type2]);
};
var VDatePickerHeader = mixins(Colorable, Localable, Themeable$1).extend({
  name: "v-date-picker-header",
  props: {
    disabled: Boolean,
    format: Function,
    min: String,
    max: String,
    nextAriaLabel: String,
    nextIcon: {
      type: String,
      default: "$next"
    },
    prevAriaLabel: String,
    prevIcon: {
      type: String,
      default: "$prev"
    },
    readonly: Boolean,
    value: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      isReversing: false
    };
  },
  computed: {
    formatter() {
      if (this.format) {
        return this.format;
      } else if (String(this.value).split("-")[1]) {
        return createNativeLocaleFormatter(this.currentLocale, {
          month: "long",
          year: "numeric",
          timeZone: "UTC"
        }, {
          length: 7
        });
      } else {
        return createNativeLocaleFormatter(this.currentLocale, {
          year: "numeric",
          timeZone: "UTC"
        }, {
          length: 4
        });
      }
    }
  },
  watch: {
    value(newVal, oldVal) {
      this.isReversing = newVal < oldVal;
    }
  },
  methods: {
    genBtn(change) {
      const ariaLabelId = change > 0 ? this.nextAriaLabel : this.prevAriaLabel;
      const ariaLabel = ariaLabelId ? this.$vuetify.lang.t(ariaLabelId) : void 0;
      const disabled = this.disabled || change < 0 && this.min && this.calculateChange(change) < this.min || change > 0 && this.max && this.calculateChange(change) > this.max;
      return this.$createElement(VBtn, {
        attrs: {
          "aria-label": ariaLabel
        },
        props: {
          dark: this.dark,
          disabled,
          icon: true,
          light: this.light
        },
        on: {
          click: (e) => {
            e.stopPropagation();
            this.$emit("input", this.calculateChange(change));
          }
        }
      }, [this.$createElement(VIcon$1, change < 0 === !this.$vuetify.rtl ? this.prevIcon : this.nextIcon)]);
    },
    calculateChange(sign) {
      const [year, month] = String(this.value).split("-").map(Number);
      if (month == null) {
        return `${year + sign}`;
      } else {
        return monthChange(String(this.value), sign);
      }
    },
    genHeader() {
      const color = !this.disabled && (this.color || "accent");
      const header2 = this.$createElement("div", this.setTextColor(color, {
        key: String(this.value)
      }), [this.$createElement("button", {
        attrs: {
          type: "button"
        },
        on: {
          click: () => this.$emit("toggle")
        }
      }, [this.$slots.default || this.formatter(String(this.value))])]);
      const transition = this.$createElement("transition", {
        props: {
          name: this.isReversing === !this.$vuetify.rtl ? "tab-reverse-transition" : "tab-transition"
        }
      }, [header2]);
      return this.$createElement("div", {
        staticClass: "v-date-picker-header__value",
        class: {
          "v-date-picker-header__value--disabled": this.disabled
        }
      }, [transition]);
    }
  },
  render() {
    return this.$createElement("div", {
      staticClass: "v-date-picker-header",
      class: __spreadValues({
        "v-date-picker-header--disabled": this.disabled
      }, this.themeClasses)
    }, [this.genBtn(-1), this.genHeader(), this.genBtn(1)]);
  }
});
function isDateAllowed(date, min, max, allowedFn) {
  return (!allowedFn || allowedFn(date)) && (!min || date >= min.substr(0, 10)) && (!max || date <= max);
}
var DatePickerTable = mixins(Colorable, Localable, Themeable$1).extend({
  directives: {
    Touch: Touch$1
  },
  props: {
    allowedDates: Function,
    current: String,
    disabled: Boolean,
    format: Function,
    events: {
      type: [Array, Function, Object],
      default: () => null
    },
    eventColor: {
      type: [Array, Function, Object, String],
      default: () => "warning"
    },
    min: String,
    max: String,
    range: Boolean,
    readonly: Boolean,
    scrollable: Boolean,
    tableDate: {
      type: String,
      required: true
    },
    value: [String, Array]
  },
  data: () => ({
    isReversing: false,
    wheelThrottle: null
  }),
  computed: {
    computedTransition() {
      return this.isReversing === !this.$vuetify.rtl ? "tab-reverse-transition" : "tab-transition";
    },
    displayedMonth() {
      return Number(this.tableDate.split("-")[1]) - 1;
    },
    displayedYear() {
      return Number(this.tableDate.split("-")[0]);
    }
  },
  watch: {
    tableDate(newVal, oldVal) {
      this.isReversing = newVal < oldVal;
    }
  },
  mounted() {
    this.wheelThrottle = throttle(this.wheel, 250);
  },
  methods: {
    genButtonClasses(isAllowed, isFloating, isSelected, isCurrent) {
      return __spreadValues({
        "v-size--default": !isFloating,
        "v-date-picker-table__current": isCurrent,
        "v-btn--active": isSelected,
        "v-btn--flat": !isAllowed || this.disabled,
        "v-btn--text": isSelected === isCurrent,
        "v-btn--rounded": isFloating,
        "v-btn--disabled": !isAllowed || this.disabled,
        "v-btn--outlined": isCurrent && !isSelected
      }, this.themeClasses);
    },
    genButtonEvents(value, isAllowed, mouseEventType) {
      if (this.disabled)
        return void 0;
      return mergeListeners({
        click: () => {
          if (isAllowed && !this.readonly)
            this.$emit("input", value);
        }
      }, createItemTypeNativeListeners(this, `:${mouseEventType}`, value));
    },
    genButton(value, isFloating, mouseEventType, formatter, isOtherMonth = false) {
      const isAllowed = isDateAllowed(value, this.min, this.max, this.allowedDates);
      const isSelected = this.isSelected(value) && isAllowed;
      const isCurrent = value === this.current;
      const setColor = isSelected ? this.setBackgroundColor : this.setTextColor;
      const color = (isSelected || isCurrent) && (this.color || "accent");
      return this.$createElement("button", setColor(color, {
        staticClass: "v-btn",
        class: this.genButtonClasses(isAllowed && !isOtherMonth, isFloating, isSelected, isCurrent),
        attrs: {
          type: "button"
        },
        domProps: {
          disabled: this.disabled || !isAllowed || isOtherMonth
        },
        on: this.genButtonEvents(value, isAllowed, mouseEventType)
      }), [this.$createElement("div", {
        staticClass: "v-btn__content"
      }, [formatter(value)]), this.genEvents(value)]);
    },
    getEventColors(date) {
      const arrayize = (v) => Array.isArray(v) ? v : [v];
      let eventData;
      let eventColors = [];
      if (Array.isArray(this.events)) {
        eventData = this.events.includes(date);
      } else if (this.events instanceof Function) {
        eventData = this.events(date) || false;
      } else if (this.events) {
        eventData = this.events[date] || false;
      } else {
        eventData = false;
      }
      if (!eventData) {
        return [];
      } else if (eventData !== true) {
        eventColors = arrayize(eventData);
      } else if (typeof this.eventColor === "string") {
        eventColors = [this.eventColor];
      } else if (typeof this.eventColor === "function") {
        eventColors = arrayize(this.eventColor(date));
      } else if (Array.isArray(this.eventColor)) {
        eventColors = this.eventColor;
      } else {
        eventColors = arrayize(this.eventColor[date]);
      }
      return eventColors.filter((v) => v);
    },
    genEvents(date) {
      const eventColors = this.getEventColors(date);
      return eventColors.length ? this.$createElement("div", {
        staticClass: "v-date-picker-table__events"
      }, eventColors.map((color) => this.$createElement("div", this.setBackgroundColor(color)))) : null;
    },
    isValidScroll(e, calculateTableDate) {
      const tableDate = calculateTableDate(e.deltaY);
      const sanitizeType = tableDate.split("-").length === 1 ? "year" : "month";
      return e.deltaY === 0 || e.deltaY < 0 && (this.min ? tableDate >= sanitizeDateString(this.min, sanitizeType) : true) || e.deltaY > 0 && (this.max ? tableDate <= sanitizeDateString(this.max, sanitizeType) : true);
    },
    wheel(e, calculateTableDate) {
      this.$emit("update:table-date", calculateTableDate(e.deltaY));
    },
    touch(value, calculateTableDate) {
      this.$emit("update:table-date", calculateTableDate(value));
    },
    genTable(staticClass, children, calculateTableDate) {
      const transition = this.$createElement("transition", {
        props: {
          name: this.computedTransition
        }
      }, [this.$createElement("table", {
        key: this.tableDate
      }, children)]);
      const touchDirective = {
        name: "touch",
        value: {
          left: (e) => e.offsetX < -15 && this.touch(1, calculateTableDate),
          right: (e) => e.offsetX > 15 && this.touch(-1, calculateTableDate)
        }
      };
      return this.$createElement("div", {
        staticClass,
        class: __spreadValues({
          "v-date-picker-table--disabled": this.disabled
        }, this.themeClasses),
        on: !this.disabled && this.scrollable ? {
          wheel: (e) => {
            e.preventDefault();
            if (this.isValidScroll(e, calculateTableDate)) {
              this.wheelThrottle(e, calculateTableDate);
            }
          }
        } : void 0,
        directives: [touchDirective]
      }, [transition]);
    },
    isSelected(value) {
      if (Array.isArray(this.value)) {
        if (this.range && this.value.length === 2) {
          const [from, to] = [...this.value].sort();
          return from <= value && value <= to;
        } else {
          return this.value.indexOf(value) !== -1;
        }
      }
      return value === this.value;
    }
  }
});
var VDatePickerDateTable = mixins(DatePickerTable).extend({
  name: "v-date-picker-date-table",
  props: {
    firstDayOfWeek: {
      type: [String, Number],
      default: 0
    },
    localeFirstDayOfYear: {
      type: [String, Number],
      default: 0
    },
    showAdjacentMonths: Boolean,
    showWeek: Boolean,
    weekdayFormat: Function
  },
  computed: {
    formatter() {
      return this.format || createNativeLocaleFormatter(this.currentLocale, {
        day: "numeric",
        timeZone: "UTC"
      }, {
        start: 8,
        length: 2
      });
    },
    weekdayFormatter() {
      return this.weekdayFormat || createNativeLocaleFormatter(this.currentLocale, {
        weekday: "narrow",
        timeZone: "UTC"
      });
    },
    weekDays() {
      const first = parseInt(this.firstDayOfWeek, 10);
      return this.weekdayFormatter ? createRange(7).map((i) => this.weekdayFormatter(`2017-01-${first + i + 15}`)) : createRange(7).map((i) => ["S", "M", "T", "W", "T", "F", "S"][(i + first) % 7]);
    }
  },
  methods: {
    calculateTableDate(delta2) {
      return monthChange(this.tableDate, Math.sign(delta2 || 1));
    },
    genTHead() {
      const days = this.weekDays.map((day) => this.$createElement("th", day));
      if (this.showWeek) {
        days.unshift(this.$createElement("th"));
      }
      return this.$createElement("thead", this.genTR(days));
    },
    weekDaysBeforeFirstDayOfTheMonth() {
      const firstDayOfTheMonth = new Date(`${this.displayedYear}-${pad(this.displayedMonth + 1)}-01T00:00:00+00:00`);
      const weekDay = firstDayOfTheMonth.getUTCDay();
      return (weekDay - parseInt(this.firstDayOfWeek) + 7) % 7;
    },
    getWeekNumber(dayInMonth) {
      return weekNumber(this.displayedYear, this.displayedMonth, dayInMonth, parseInt(this.firstDayOfWeek), parseInt(this.localeFirstDayOfYear));
    },
    genWeekNumber(weekNumber2) {
      return this.$createElement("td", [this.$createElement("small", {
        staticClass: "v-date-picker-table--date__week"
      }, String(weekNumber2).padStart(2, "0"))]);
    },
    genTBody() {
      const children = [];
      const daysInMonth2 = new Date(this.displayedYear, this.displayedMonth + 1, 0).getDate();
      let rows = [];
      let day = this.weekDaysBeforeFirstDayOfTheMonth();
      if (this.showWeek) {
        rows.push(this.genWeekNumber(this.getWeekNumber(1)));
      }
      const prevMonthYear = this.displayedMonth ? this.displayedYear : this.displayedYear - 1;
      const prevMonth = (this.displayedMonth + 11) % 12;
      const firstDayFromPreviousMonth = new Date(this.displayedYear, this.displayedMonth, 0).getDate();
      const cellsInRow = this.showWeek ? 8 : 7;
      while (day--) {
        const date = `${prevMonthYear}-${pad(prevMonth + 1)}-${pad(firstDayFromPreviousMonth - day)}`;
        rows.push(this.$createElement("td", this.showAdjacentMonths ? [this.genButton(date, true, "date", this.formatter, true)] : []));
      }
      for (day = 1; day <= daysInMonth2; day++) {
        const date = `${this.displayedYear}-${pad(this.displayedMonth + 1)}-${pad(day)}`;
        rows.push(this.$createElement("td", [this.genButton(date, true, "date", this.formatter)]));
        if (rows.length % cellsInRow === 0) {
          children.push(this.genTR(rows));
          rows = [];
          if (this.showWeek && (day < daysInMonth2 || this.showAdjacentMonths)) {
            rows.push(this.genWeekNumber(this.getWeekNumber(day + 7)));
          }
        }
      }
      const nextMonthYear = this.displayedMonth === 11 ? this.displayedYear + 1 : this.displayedYear;
      const nextMonth = (this.displayedMonth + 1) % 12;
      let nextMonthDay = 1;
      while (rows.length < cellsInRow) {
        const date = `${nextMonthYear}-${pad(nextMonth + 1)}-${pad(nextMonthDay++)}`;
        rows.push(this.$createElement("td", this.showAdjacentMonths ? [this.genButton(date, true, "date", this.formatter, true)] : []));
      }
      if (rows.length) {
        children.push(this.genTR(rows));
      }
      return this.$createElement("tbody", children);
    },
    genTR(children) {
      return [this.$createElement("tr", children)];
    }
  },
  render() {
    return this.genTable("v-date-picker-table v-date-picker-table--date", [this.genTHead(), this.genTBody()], this.calculateTableDate);
  }
});
var VDatePickerMonthTable = mixins(DatePickerTable).extend({
  name: "v-date-picker-month-table",
  computed: {
    formatter() {
      return this.format || createNativeLocaleFormatter(this.currentLocale, {
        month: "short",
        timeZone: "UTC"
      }, {
        start: 5,
        length: 2
      });
    }
  },
  methods: {
    calculateTableDate(delta2) {
      return `${parseInt(this.tableDate, 10) + Math.sign(delta2 || 1)}`;
    },
    genTBody() {
      const children = [];
      const cols = Array(3).fill(null);
      const rows = 12 / cols.length;
      for (let row = 0; row < rows; row++) {
        const tds = cols.map((_, col) => {
          const month = row * cols.length + col;
          const date = `${this.displayedYear}-${pad(month + 1)}`;
          return this.$createElement("td", {
            key: month
          }, [this.genButton(date, false, "month", this.formatter)]);
        });
        children.push(this.$createElement("tr", {
          key: row
        }, tds));
      }
      return this.$createElement("tbody", children);
    }
  },
  render() {
    return this.genTable("v-date-picker-table v-date-picker-table--month", [this.genTBody()], this.calculateTableDate);
  }
});
var VDatePickerYears = mixins(Colorable, Localable).extend({
  name: "v-date-picker-years",
  props: {
    format: Function,
    min: [Number, String],
    max: [Number, String],
    readonly: Boolean,
    value: [Number, String]
  },
  data() {
    return {
      defaultColor: "primary"
    };
  },
  computed: {
    formatter() {
      return this.format || createNativeLocaleFormatter(this.currentLocale, {
        year: "numeric",
        timeZone: "UTC"
      }, {
        length: 4
      });
    }
  },
  mounted() {
    setTimeout(() => {
      const activeItem = this.$el.getElementsByClassName("active")[0];
      if (activeItem) {
        this.$el.scrollTop = activeItem.offsetTop - this.$el.offsetHeight / 2 + activeItem.offsetHeight / 2;
      } else if (this.min && !this.max) {
        this.$el.scrollTop = this.$el.scrollHeight;
      } else if (!this.min && this.max) {
        this.$el.scrollTop = 0;
      } else {
        this.$el.scrollTop = this.$el.scrollHeight / 2 - this.$el.offsetHeight / 2;
      }
    });
  },
  methods: {
    genYearItem(year) {
      const formatted = this.formatter(`${year}`);
      const active = parseInt(this.value, 10) === year;
      const color = active && (this.color || "primary");
      return this.$createElement("li", this.setTextColor(color, {
        key: year,
        class: {
          active
        },
        on: mergeListeners({
          click: () => this.$emit("input", year)
        }, createItemTypeNativeListeners(this, ":year", year))
      }), formatted);
    },
    genYearItems() {
      const children = [];
      const selectedYear = this.value ? parseInt(this.value, 10) : new Date().getFullYear();
      const maxYear = this.max ? parseInt(this.max, 10) : selectedYear + 100;
      const minYear = Math.min(maxYear, this.min ? parseInt(this.min, 10) : selectedYear - 100);
      for (let year = maxYear; year >= minYear; year--) {
        children.push(this.genYearItem(year));
      }
      return children;
    }
  },
  render() {
    return this.$createElement("ul", {
      staticClass: "v-date-picker-years",
      ref: "years"
    }, this.genYearItems());
  }
});
var VPicker = mixins(Colorable, Elevatable, Themeable$1).extend({
  name: "v-picker",
  props: {
    flat: Boolean,
    fullWidth: Boolean,
    landscape: Boolean,
    noTitle: Boolean,
    transition: {
      type: String,
      default: "fade-transition"
    },
    width: {
      type: [Number, String],
      default: 290
    }
  },
  computed: {
    computedTitleColor() {
      const defaultTitleColor = this.isDark ? false : this.color || "primary";
      return this.color || defaultTitleColor;
    }
  },
  methods: {
    genTitle() {
      return this.$createElement("div", this.setBackgroundColor(this.computedTitleColor, {
        staticClass: "v-picker__title",
        class: {
          "v-picker__title--landscape": this.landscape
        }
      }), this.$slots.title);
    },
    genBodyTransition() {
      return this.$createElement("transition", {
        props: {
          name: this.transition
        }
      }, this.$slots.default);
    },
    genBody() {
      return this.$createElement("div", {
        staticClass: "v-picker__body",
        class: __spreadValues({
          "v-picker__body--no-title": this.noTitle
        }, this.themeClasses),
        style: this.fullWidth ? void 0 : {
          width: convertToUnit(this.width)
        }
      }, [this.genBodyTransition()]);
    },
    genActions() {
      return this.$createElement("div", {
        staticClass: "v-picker__actions v-card__actions",
        class: {
          "v-picker__actions--no-title": this.noTitle
        }
      }, this.$slots.actions);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-picker v-card",
      class: __spreadValues(__spreadValues({
        "v-picker--flat": this.flat,
        "v-picker--landscape": this.landscape,
        "v-picker--full-width": this.fullWidth
      }, this.themeClasses), this.elevationClasses)
    }, [this.$slots.title ? this.genTitle() : null, this.genBody(), this.$slots.actions ? this.genActions() : null]);
  }
});
var Picker = mixins(Colorable, Elevatable, Themeable$1).extend({
  name: "picker",
  props: {
    flat: Boolean,
    fullWidth: Boolean,
    headerColor: String,
    landscape: Boolean,
    noTitle: Boolean,
    width: {
      type: [Number, String],
      default: 290
    }
  },
  methods: {
    genPickerTitle() {
      return null;
    },
    genPickerBody() {
      return null;
    },
    genPickerActionsSlot() {
      return this.$scopedSlots.default ? this.$scopedSlots.default({
        save: this.save,
        cancel: this.cancel
      }) : this.$slots.default;
    },
    genPicker(staticClass) {
      const children = [];
      if (!this.noTitle) {
        const title = this.genPickerTitle();
        title && children.push(title);
      }
      const body = this.genPickerBody();
      body && children.push(body);
      children.push(this.$createElement("template", {
        slot: "actions"
      }, [this.genPickerActionsSlot()]));
      return this.$createElement(VPicker, {
        staticClass,
        props: {
          color: this.headerColor || this.color,
          dark: this.dark,
          elevation: this.elevation,
          flat: this.flat,
          fullWidth: this.fullWidth,
          landscape: this.landscape,
          light: this.light,
          width: this.width,
          noTitle: this.noTitle
        }
      }, children);
    }
  }
});
var VDatePicker = mixins(Localable, Picker).extend({
  name: "v-date-picker",
  props: {
    allowedDates: Function,
    dayFormat: Function,
    disabled: Boolean,
    events: {
      type: [Array, Function, Object],
      default: () => null
    },
    eventColor: {
      type: [Array, Function, Object, String],
      default: () => "warning"
    },
    firstDayOfWeek: {
      type: [String, Number],
      default: 0
    },
    headerDateFormat: Function,
    localeFirstDayOfYear: {
      type: [String, Number],
      default: 0
    },
    max: String,
    min: String,
    monthFormat: Function,
    multiple: Boolean,
    nextIcon: {
      type: String,
      default: "$next"
    },
    nextMonthAriaLabel: {
      type: String,
      default: "$vuetify.datePicker.nextMonthAriaLabel"
    },
    nextYearAriaLabel: {
      type: String,
      default: "$vuetify.datePicker.nextYearAriaLabel"
    },
    pickerDate: String,
    prevIcon: {
      type: String,
      default: "$prev"
    },
    prevMonthAriaLabel: {
      type: String,
      default: "$vuetify.datePicker.prevMonthAriaLabel"
    },
    prevYearAriaLabel: {
      type: String,
      default: "$vuetify.datePicker.prevYearAriaLabel"
    },
    range: Boolean,
    reactive: Boolean,
    readonly: Boolean,
    scrollable: Boolean,
    showCurrent: {
      type: [Boolean, String],
      default: true
    },
    selectedItemsText: {
      type: String,
      default: "$vuetify.datePicker.itemsSelected"
    },
    showAdjacentMonths: Boolean,
    showWeek: Boolean,
    titleDateFormat: Function,
    type: {
      type: String,
      default: "date",
      validator: (type2) => ["date", "month"].includes(type2)
    },
    value: [Array, String],
    weekdayFormat: Function,
    yearFormat: Function,
    yearIcon: String
  },
  data() {
    const now = new Date();
    return {
      activePicker: this.type.toUpperCase(),
      inputDay: null,
      inputMonth: null,
      inputYear: null,
      isReversing: false,
      now,
      tableDate: (() => {
        if (this.pickerDate) {
          return this.pickerDate;
        }
        const multipleValue = wrapInArray(this.value);
        const date = multipleValue[multipleValue.length - 1] || (typeof this.showCurrent === "string" ? this.showCurrent : `${now.getFullYear()}-${now.getMonth() + 1}`);
        return sanitizeDateString(date, this.type === "date" ? "month" : "year");
      })()
    };
  },
  computed: {
    multipleValue() {
      return wrapInArray(this.value);
    },
    isMultiple() {
      return this.multiple || this.range;
    },
    lastValue() {
      return this.isMultiple ? this.multipleValue[this.multipleValue.length - 1] : this.value;
    },
    selectedMonths() {
      if (!this.value || this.type === "month") {
        return this.value;
      } else if (this.isMultiple) {
        return this.multipleValue.map((val) => val.substr(0, 7));
      } else {
        return this.value.substr(0, 7);
      }
    },
    current() {
      if (this.showCurrent === true) {
        return sanitizeDateString(`${this.now.getFullYear()}-${this.now.getMonth() + 1}-${this.now.getDate()}`, this.type);
      }
      return this.showCurrent || null;
    },
    inputDate() {
      return this.type === "date" ? `${this.inputYear}-${pad(this.inputMonth + 1)}-${pad(this.inputDay)}` : `${this.inputYear}-${pad(this.inputMonth + 1)}`;
    },
    tableMonth() {
      return Number((this.pickerDate || this.tableDate).split("-")[1]) - 1;
    },
    tableYear() {
      return Number((this.pickerDate || this.tableDate).split("-")[0]);
    },
    minMonth() {
      return this.min ? sanitizeDateString(this.min, "month") : null;
    },
    maxMonth() {
      return this.max ? sanitizeDateString(this.max, "month") : null;
    },
    minYear() {
      return this.min ? sanitizeDateString(this.min, "year") : null;
    },
    maxYear() {
      return this.max ? sanitizeDateString(this.max, "year") : null;
    },
    formatters() {
      return {
        year: this.yearFormat || createNativeLocaleFormatter(this.currentLocale, {
          year: "numeric",
          timeZone: "UTC"
        }, {
          length: 4
        }),
        titleDate: this.titleDateFormat || (this.isMultiple ? this.defaultTitleMultipleDateFormatter : this.defaultTitleDateFormatter)
      };
    },
    defaultTitleMultipleDateFormatter() {
      return (dates) => {
        if (!dates.length) {
          return "-";
        }
        if (dates.length === 1) {
          return this.defaultTitleDateFormatter(dates[0]);
        }
        return this.$vuetify.lang.t(this.selectedItemsText, dates.length);
      };
    },
    defaultTitleDateFormatter() {
      const titleFormats = {
        year: {
          year: "numeric",
          timeZone: "UTC"
        },
        month: {
          month: "long",
          timeZone: "UTC"
        },
        date: {
          weekday: "short",
          month: "short",
          day: "numeric",
          timeZone: "UTC"
        }
      };
      const titleDateFormatter = createNativeLocaleFormatter(this.currentLocale, titleFormats[this.type], {
        start: 0,
        length: {
          date: 10,
          month: 7,
          year: 4
        }[this.type]
      });
      const landscapeFormatter = (date) => titleDateFormatter(date).replace(/([^\d\s])([\d])/g, (match, nonDigit, digit) => `${nonDigit} ${digit}`).replace(", ", ",<br>");
      return this.landscape ? landscapeFormatter : titleDateFormatter;
    }
  },
  watch: {
    tableDate(val, prev) {
      const sanitizeType = this.type === "month" ? "year" : "month";
      this.isReversing = sanitizeDateString(val, sanitizeType) < sanitizeDateString(prev, sanitizeType);
      this.$emit("update:picker-date", val);
    },
    pickerDate(val) {
      if (val) {
        this.tableDate = val;
      } else if (this.lastValue && this.type === "date") {
        this.tableDate = sanitizeDateString(this.lastValue, "month");
      } else if (this.lastValue && this.type === "month") {
        this.tableDate = sanitizeDateString(this.lastValue, "year");
      }
    },
    value(newValue, oldValue) {
      this.checkMultipleProp();
      this.setInputDate();
      if (!this.isMultiple && this.value && !this.pickerDate || this.isMultiple && this.multipleValue.length && (!oldValue || !oldValue.length) && !this.pickerDate) {
        this.tableDate = sanitizeDateString(this.inputDate, this.type === "month" ? "year" : "month");
      }
    },
    type(type2) {
      this.activePicker = type2.toUpperCase();
      if (this.value && this.value.length) {
        const output = this.multipleValue.map((val) => sanitizeDateString(val, type2)).filter(this.isDateAllowed);
        this.$emit("input", this.isMultiple ? output : output[0]);
      }
    }
  },
  created() {
    this.checkMultipleProp();
    if (this.pickerDate !== this.tableDate) {
      this.$emit("update:picker-date", this.tableDate);
    }
    this.setInputDate();
  },
  methods: {
    emitInput(newInput) {
      if (this.range) {
        if (this.multipleValue.length !== 1) {
          this.$emit("input", [newInput]);
        } else {
          const output2 = [this.multipleValue[0], newInput];
          this.$emit("input", output2);
          this.$emit("change", output2);
        }
        return;
      }
      const output = this.multiple ? this.multipleValue.indexOf(newInput) === -1 ? this.multipleValue.concat([newInput]) : this.multipleValue.filter((x) => x !== newInput) : newInput;
      this.$emit("input", output);
      this.multiple || this.$emit("change", newInput);
    },
    checkMultipleProp() {
      if (this.value == null)
        return;
      const valueType = this.value.constructor.name;
      const expected = this.isMultiple ? "Array" : "String";
      if (valueType !== expected) {
        consoleWarn(`Value must be ${this.isMultiple ? "an" : "a"} ${expected}, got ${valueType}`, this);
      }
    },
    isDateAllowed(value) {
      return isDateAllowed(value, this.min, this.max, this.allowedDates);
    },
    yearClick(value) {
      this.inputYear = value;
      if (this.type === "month") {
        this.tableDate = `${value}`;
      } else {
        this.tableDate = `${value}-${pad((this.tableMonth || 0) + 1)}`;
      }
      this.activePicker = "MONTH";
      if (this.reactive && !this.readonly && !this.isMultiple && this.isDateAllowed(this.inputDate)) {
        this.$emit("input", this.inputDate);
      }
    },
    monthClick(value) {
      this.inputYear = parseInt(value.split("-")[0], 10);
      this.inputMonth = parseInt(value.split("-")[1], 10) - 1;
      if (this.type === "date") {
        if (this.inputDay) {
          this.inputDay = Math.min(this.inputDay, daysInMonth(this.inputYear, this.inputMonth + 1));
        }
        this.tableDate = value;
        this.activePicker = "DATE";
        if (this.reactive && !this.readonly && !this.isMultiple && this.isDateAllowed(this.inputDate)) {
          this.$emit("input", this.inputDate);
        }
      } else {
        this.emitInput(this.inputDate);
      }
    },
    dateClick(value) {
      this.inputYear = parseInt(value.split("-")[0], 10);
      this.inputMonth = parseInt(value.split("-")[1], 10) - 1;
      this.inputDay = parseInt(value.split("-")[2], 10);
      this.emitInput(this.inputDate);
    },
    genPickerTitle() {
      return this.$createElement(VDatePickerTitle, {
        props: {
          date: this.value ? this.formatters.titleDate(this.isMultiple ? this.multipleValue : this.value) : "",
          disabled: this.disabled,
          readonly: this.readonly,
          selectingYear: this.activePicker === "YEAR",
          year: this.formatters.year(this.multipleValue.length ? `${this.inputYear}` : this.tableDate),
          yearIcon: this.yearIcon,
          value: this.multipleValue[0]
        },
        slot: "title",
        on: {
          "update:selecting-year": (value) => this.activePicker = value ? "YEAR" : this.type.toUpperCase()
        }
      });
    },
    genTableHeader() {
      return this.$createElement(VDatePickerHeader, {
        props: {
          nextIcon: this.nextIcon,
          color: this.color,
          dark: this.dark,
          disabled: this.disabled,
          format: this.headerDateFormat,
          light: this.light,
          locale: this.locale,
          min: this.activePicker === "DATE" ? this.minMonth : this.minYear,
          max: this.activePicker === "DATE" ? this.maxMonth : this.maxYear,
          nextAriaLabel: this.activePicker === "DATE" ? this.nextMonthAriaLabel : this.nextYearAriaLabel,
          prevAriaLabel: this.activePicker === "DATE" ? this.prevMonthAriaLabel : this.prevYearAriaLabel,
          prevIcon: this.prevIcon,
          readonly: this.readonly,
          value: this.activePicker === "DATE" ? `${pad(this.tableYear, 4)}-${pad(this.tableMonth + 1)}` : `${pad(this.tableYear, 4)}`
        },
        on: {
          toggle: () => this.activePicker = this.activePicker === "DATE" ? "MONTH" : "YEAR",
          input: (value) => this.tableDate = value
        }
      });
    },
    genDateTable() {
      return this.$createElement(VDatePickerDateTable, {
        props: {
          allowedDates: this.allowedDates,
          color: this.color,
          current: this.current,
          dark: this.dark,
          disabled: this.disabled,
          events: this.events,
          eventColor: this.eventColor,
          firstDayOfWeek: this.firstDayOfWeek,
          format: this.dayFormat,
          light: this.light,
          locale: this.locale,
          localeFirstDayOfYear: this.localeFirstDayOfYear,
          min: this.min,
          max: this.max,
          range: this.range,
          readonly: this.readonly,
          scrollable: this.scrollable,
          showAdjacentMonths: this.showAdjacentMonths,
          showWeek: this.showWeek,
          tableDate: `${pad(this.tableYear, 4)}-${pad(this.tableMonth + 1)}`,
          value: this.value,
          weekdayFormat: this.weekdayFormat
        },
        ref: "table",
        on: __spreadValues({
          input: this.dateClick,
          "update:table-date": (value) => this.tableDate = value
        }, createItemTypeListeners(this, ":date"))
      });
    },
    genMonthTable() {
      return this.$createElement(VDatePickerMonthTable, {
        props: {
          allowedDates: this.type === "month" ? this.allowedDates : null,
          color: this.color,
          current: this.current ? sanitizeDateString(this.current, "month") : null,
          dark: this.dark,
          disabled: this.disabled,
          events: this.type === "month" ? this.events : null,
          eventColor: this.type === "month" ? this.eventColor : null,
          format: this.monthFormat,
          light: this.light,
          locale: this.locale,
          min: this.minMonth,
          max: this.maxMonth,
          range: this.range,
          readonly: this.readonly && this.type === "month",
          scrollable: this.scrollable,
          value: this.selectedMonths,
          tableDate: `${pad(this.tableYear, 4)}`
        },
        ref: "table",
        on: __spreadValues({
          input: this.monthClick,
          "update:table-date": (value) => this.tableDate = value
        }, createItemTypeListeners(this, ":month"))
      });
    },
    genYears() {
      return this.$createElement(VDatePickerYears, {
        props: {
          color: this.color,
          format: this.yearFormat,
          locale: this.locale,
          min: this.minYear,
          max: this.maxYear,
          value: this.tableYear
        },
        on: __spreadValues({
          input: this.yearClick
        }, createItemTypeListeners(this, ":year"))
      });
    },
    genPickerBody() {
      const children = this.activePicker === "YEAR" ? [this.genYears()] : [this.genTableHeader(), this.activePicker === "DATE" ? this.genDateTable() : this.genMonthTable()];
      return this.$createElement("div", {
        key: this.activePicker
      }, children);
    },
    setInputDate() {
      if (this.lastValue) {
        const array = this.lastValue.split("-");
        this.inputYear = parseInt(array[0], 10);
        this.inputMonth = parseInt(array[1], 10) - 1;
        if (this.type === "date") {
          this.inputDay = parseInt(array[2], 10);
        }
      } else {
        this.inputYear = this.inputYear || this.now.getFullYear();
        this.inputMonth = this.inputMonth == null ? this.inputMonth : this.now.getMonth();
        this.inputDay = this.inputDay || this.now.getDate();
      }
    }
  },
  render() {
    return this.genPicker("v-picker--date");
  }
});
var VExpansionPanels = BaseItemGroup.extend({
  name: "v-expansion-panels",
  provide() {
    return {
      expansionPanels: this
    };
  },
  props: {
    accordion: Boolean,
    disabled: Boolean,
    flat: Boolean,
    hover: Boolean,
    focusable: Boolean,
    inset: Boolean,
    popout: Boolean,
    readonly: Boolean,
    tile: Boolean
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, BaseItemGroup.options.computed.classes.call(this)), {
        "v-expansion-panels": true,
        "v-expansion-panels--accordion": this.accordion,
        "v-expansion-panels--flat": this.flat,
        "v-expansion-panels--hover": this.hover,
        "v-expansion-panels--focusable": this.focusable,
        "v-expansion-panels--inset": this.inset,
        "v-expansion-panels--popout": this.popout,
        "v-expansion-panels--tile": this.tile
      });
    }
  },
  created() {
    if (this.$attrs.hasOwnProperty("expand")) {
      breaking("expand", "multiple", this);
    }
    if (Array.isArray(this.value) && this.value.length > 0 && typeof this.value[0] === "boolean") {
      breaking(':value="[true, false, true]"', ':value="[0, 2]"', this);
    }
  },
  methods: {
    updateItem(item, index) {
      const value = this.getValue(item, index);
      const nextValue = this.getValue(item, index + 1);
      item.isActive = this.toggleMethod(value);
      item.nextIsActive = this.toggleMethod(nextValue);
    }
  }
});
var VExpansionPanel = mixins(factory$1("expansionPanels", "v-expansion-panel", "v-expansion-panels"), provide("expansionPanel", true)).extend({
  name: "v-expansion-panel",
  props: {
    disabled: Boolean,
    readonly: Boolean
  },
  data() {
    return {
      content: null,
      header: null,
      nextIsActive: false
    };
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-expansion-panel--active": this.isActive,
        "v-expansion-panel--next-active": this.nextIsActive,
        "v-expansion-panel--disabled": this.isDisabled
      }, this.groupClasses);
    },
    isDisabled() {
      return this.expansionPanels.disabled || this.disabled;
    },
    isReadonly() {
      return this.expansionPanels.readonly || this.readonly;
    }
  },
  methods: {
    registerContent(vm) {
      this.content = vm;
    },
    unregisterContent() {
      this.content = null;
    },
    registerHeader(vm) {
      this.header = vm;
      vm.$on("click", this.onClick);
    },
    unregisterHeader() {
      this.header = null;
    },
    onClick(e) {
      if (e.detail)
        this.header.$el.blur();
      this.$emit("click", e);
      this.isReadonly || this.isDisabled || this.toggle();
    },
    toggle() {
      if (this.content)
        this.content.isBooted = true;
      this.$nextTick(() => this.$emit("change"));
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-expansion-panel",
      class: this.classes,
      attrs: {
        "aria-expanded": String(this.isActive)
      }
    }, getSlot(this));
  }
});
const baseMixins$d = mixins(Bootable, Colorable, inject("expansionPanel", "v-expansion-panel-content", "v-expansion-panel"));
var VExpansionPanelContent = baseMixins$d.extend().extend({
  name: "v-expansion-panel-content",
  computed: {
    isActive() {
      return this.expansionPanel.isActive;
    }
  },
  created() {
    this.expansionPanel.registerContent(this);
  },
  beforeDestroy() {
    this.expansionPanel.unregisterContent();
  },
  render(h) {
    return h(VExpandTransition, this.showLazyContent(() => [h("div", this.setBackgroundColor(this.color, {
      staticClass: "v-expansion-panel-content",
      directives: [{
        name: "show",
        value: this.isActive
      }]
    }), [h("div", {
      class: "v-expansion-panel-content__wrap"
    }, getSlot(this))])]));
  }
});
const baseMixins$c = mixins(Colorable, inject("expansionPanel", "v-expansion-panel-header", "v-expansion-panel"));
var VExpansionPanelHeader = baseMixins$c.extend().extend({
  name: "v-expansion-panel-header",
  directives: {
    ripple
  },
  props: {
    disableIconRotate: Boolean,
    expandIcon: {
      type: String,
      default: "$expand"
    },
    hideActions: Boolean,
    ripple: {
      type: [Boolean, Object],
      default: false
    }
  },
  data: () => ({
    hasMousedown: false
  }),
  computed: {
    classes() {
      return {
        "v-expansion-panel-header--active": this.isActive,
        "v-expansion-panel-header--mousedown": this.hasMousedown
      };
    },
    isActive() {
      return this.expansionPanel.isActive;
    },
    isDisabled() {
      return this.expansionPanel.isDisabled;
    },
    isReadonly() {
      return this.expansionPanel.isReadonly;
    }
  },
  created() {
    this.expansionPanel.registerHeader(this);
  },
  beforeDestroy() {
    this.expansionPanel.unregisterHeader();
  },
  methods: {
    onClick(e) {
      this.$emit("click", e);
    },
    genIcon() {
      const icon = getSlot(this, "actions") || [this.$createElement(VIcon$1, this.expandIcon)];
      return this.$createElement(VFadeTransition, [this.$createElement("div", {
        staticClass: "v-expansion-panel-header__icon",
        class: {
          "v-expansion-panel-header__icon--disable-rotate": this.disableIconRotate
        },
        directives: [{
          name: "show",
          value: !this.isDisabled
        }]
      }, icon)]);
    }
  },
  render(h) {
    return h("button", this.setBackgroundColor(this.color, {
      staticClass: "v-expansion-panel-header",
      class: this.classes,
      attrs: {
        tabindex: this.isDisabled ? -1 : null,
        type: "button",
        "aria-expanded": this.hasMousedown
      },
      directives: [{
        name: "ripple",
        value: this.ripple
      }],
      on: __spreadProps(__spreadValues({}, this.$listeners), {
        click: this.onClick,
        mousedown: () => this.hasMousedown = true,
        mouseup: () => this.hasMousedown = false
      })
    }), [getSlot(this, "default", {
      open: this.isActive
    }, true), this.hideActions || this.genIcon()]);
  }
});
var VFileInput = VTextField.extend({
  name: "v-file-input",
  model: {
    prop: "value",
    event: "change"
  },
  props: {
    chips: Boolean,
    clearable: {
      type: Boolean,
      default: true
    },
    counterSizeString: {
      type: String,
      default: "$vuetify.fileInput.counterSize"
    },
    counterString: {
      type: String,
      default: "$vuetify.fileInput.counter"
    },
    hideInput: Boolean,
    placeholder: String,
    prependIcon: {
      type: String,
      default: "$file"
    },
    readonly: {
      type: Boolean,
      default: false
    },
    showSize: {
      type: [Boolean, Number],
      default: false,
      validator: (v) => {
        return typeof v === "boolean" || [1e3, 1024].includes(v);
      }
    },
    smallChips: Boolean,
    truncateLength: {
      type: [Number, String],
      default: 22
    },
    type: {
      type: String,
      default: "file"
    },
    value: {
      default: void 0,
      validator: (val) => {
        return wrapInArray(val).every((v) => v != null && typeof v === "object");
      }
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VTextField.options.computed.classes.call(this)), {
        "v-file-input": true
      });
    },
    computedCounterValue() {
      const fileCount = this.isMultiple && this.lazyValue ? this.lazyValue.length : this.lazyValue instanceof File ? 1 : 0;
      if (!this.showSize)
        return this.$vuetify.lang.t(this.counterString, fileCount);
      const bytes = this.internalArrayValue.reduce((bytes2, {
        size = 0
      }) => {
        return bytes2 + size;
      }, 0);
      return this.$vuetify.lang.t(this.counterSizeString, fileCount, humanReadableFileSize(bytes, this.base === 1024));
    },
    internalArrayValue() {
      return wrapInArray(this.internalValue);
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        this.lazyValue = val;
        this.$emit("change", this.lazyValue);
      }
    },
    isDirty() {
      return this.internalArrayValue.length > 0;
    },
    isLabelActive() {
      return this.isDirty;
    },
    isMultiple() {
      return this.$attrs.hasOwnProperty("multiple");
    },
    text() {
      if (!this.isDirty && (this.isFocused || !this.hasLabel))
        return [this.placeholder];
      return this.internalArrayValue.map((file) => {
        const {
          name = "",
          size = 0
        } = file;
        const truncatedText = this.truncateText(name);
        return !this.showSize ? truncatedText : `${truncatedText} (${humanReadableFileSize(size, this.base === 1024)})`;
      });
    },
    base() {
      return typeof this.showSize !== "boolean" ? this.showSize : void 0;
    },
    hasChips() {
      return this.chips || this.smallChips;
    }
  },
  watch: {
    readonly: {
      handler(v) {
        if (v === true)
          consoleError("readonly is not supported on <v-file-input>", this);
      },
      immediate: true
    },
    value(v) {
      const value = this.isMultiple ? v : v ? [v] : [];
      if (!deepEqual(value, this.$refs.input.files)) {
        this.$refs.input.value = "";
      }
    }
  },
  methods: {
    clearableCallback() {
      this.internalValue = this.isMultiple ? [] : null;
      this.$refs.input.value = "";
    },
    genChips() {
      if (!this.isDirty)
        return [];
      return this.text.map((text, index) => this.$createElement(VChip, {
        props: {
          small: this.smallChips
        },
        on: {
          "click:close": () => {
            const internalValue = this.internalValue;
            internalValue.splice(index, 1);
            this.internalValue = internalValue;
          }
        }
      }, [text]));
    },
    genControl() {
      const render = VTextField.options.methods.genControl.call(this);
      if (this.hideInput) {
        render.data.style = mergeStyles(render.data.style, {
          display: "none"
        });
      }
      return render;
    },
    genInput() {
      const input = VTextField.options.methods.genInput.call(this);
      delete input.data.domProps.value;
      delete input.data.on.input;
      input.data.on.change = this.onInput;
      return [this.genSelections(), input];
    },
    genPrependSlot() {
      if (!this.prependIcon)
        return null;
      const icon = this.genIcon("prepend", () => {
        this.$refs.input.click();
      });
      return this.genSlot("prepend", "outer", [icon]);
    },
    genSelectionText() {
      const length = this.text.length;
      if (length < 2)
        return this.text;
      if (this.showSize && !this.counter)
        return [this.computedCounterValue];
      return [this.$vuetify.lang.t(this.counterString, length)];
    },
    genSelections() {
      const children = [];
      if (this.isDirty && this.$scopedSlots.selection) {
        this.internalArrayValue.forEach((file, index) => {
          if (!this.$scopedSlots.selection)
            return;
          children.push(this.$scopedSlots.selection({
            text: this.text[index],
            file,
            index
          }));
        });
      } else {
        children.push(this.hasChips && this.isDirty ? this.genChips() : this.genSelectionText());
      }
      return this.$createElement("div", {
        staticClass: "v-file-input__text",
        class: {
          "v-file-input__text--placeholder": this.placeholder && !this.isDirty,
          "v-file-input__text--chips": this.hasChips && !this.$scopedSlots.selection
        }
      }, children);
    },
    genTextFieldSlot() {
      const node = VTextField.options.methods.genTextFieldSlot.call(this);
      node.data.on = __spreadProps(__spreadValues({}, node.data.on || {}), {
        click: () => this.$refs.input.click()
      });
      return node;
    },
    onInput(e) {
      const files = [...e.target.files || []];
      this.internalValue = this.isMultiple ? files : files[0];
      this.initialValue = this.internalValue;
    },
    onKeyDown(e) {
      this.$emit("keydown", e);
    },
    truncateText(str) {
      if (str.length < Number(this.truncateLength))
        return str;
      const charsKeepOneSide = Math.floor((Number(this.truncateLength) - 1) / 2);
      return `${str.slice(0, charsKeepOneSide)}\u2026${str.slice(str.length - charsKeepOneSide)}`;
    }
  }
});
var VFooter = mixins(VSheet, applicationable("footer", ["height", "inset"]), SSRBootable).extend({
  name: "v-footer",
  props: {
    height: {
      default: "auto",
      type: [Number, String]
    },
    inset: Boolean,
    padless: Boolean,
    tag: {
      type: String,
      default: "footer"
    }
  },
  computed: {
    applicationProperty() {
      return this.inset ? "insetFooter" : "footer";
    },
    classes() {
      return __spreadProps(__spreadValues({}, VSheet.options.computed.classes.call(this)), {
        "v-footer--absolute": this.absolute,
        "v-footer--fixed": !this.absolute && (this.app || this.fixed),
        "v-footer--padless": this.padless,
        "v-footer--inset": this.inset
      });
    },
    computedBottom() {
      if (!this.isPositioned)
        return void 0;
      return this.app ? this.$vuetify.application.bottom : 0;
    },
    computedLeft() {
      if (!this.isPositioned)
        return void 0;
      return this.app && this.inset ? this.$vuetify.application.left : 0;
    },
    computedRight() {
      if (!this.isPositioned)
        return void 0;
      return this.app && this.inset ? this.$vuetify.application.right : 0;
    },
    isPositioned() {
      return Boolean(this.absolute || this.fixed || this.app);
    },
    styles() {
      const height = parseInt(this.height);
      return __spreadProps(__spreadValues({}, VSheet.options.computed.styles.call(this)), {
        height: isNaN(height) ? height : convertToUnit(height),
        left: convertToUnit(this.computedLeft),
        right: convertToUnit(this.computedRight),
        bottom: convertToUnit(this.computedBottom)
      });
    }
  },
  methods: {
    updateApplication() {
      const height = parseInt(this.height);
      return isNaN(height) ? this.$el ? this.$el.clientHeight : 0 : height;
    }
  },
  render(h) {
    const data = this.setBackgroundColor(this.color, {
      staticClass: "v-footer",
      class: this.classes,
      style: this.styles
    });
    return h(this.tag, data, this.$slots.default);
  }
});
var VForm = mixins(BindsAttrs, provide("form")).extend({
  name: "v-form",
  provide() {
    return {
      form: this
    };
  },
  inheritAttrs: false,
  props: {
    disabled: Boolean,
    lazyValidation: Boolean,
    readonly: Boolean,
    value: Boolean
  },
  data: () => ({
    inputs: [],
    watchers: [],
    errorBag: {}
  }),
  watch: {
    errorBag: {
      handler(val) {
        const errors = Object.values(val).includes(true);
        this.$emit("input", !errors);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    watchInput(input) {
      const watcher = (input2) => {
        return input2.$watch("hasError", (val) => {
          this.$set(this.errorBag, input2._uid, val);
        }, {
          immediate: true
        });
      };
      const watchers = {
        _uid: input._uid,
        valid: () => {
        },
        shouldValidate: () => {
        }
      };
      if (this.lazyValidation) {
        watchers.shouldValidate = input.$watch("shouldValidate", (val) => {
          if (!val)
            return;
          if (this.errorBag.hasOwnProperty(input._uid))
            return;
          watchers.valid = watcher(input);
        });
      } else {
        watchers.valid = watcher(input);
      }
      return watchers;
    },
    validate() {
      return this.inputs.filter((input) => !input.validate(true)).length === 0;
    },
    reset() {
      this.inputs.forEach((input) => input.reset());
      this.resetErrorBag();
    },
    resetErrorBag() {
      if (this.lazyValidation) {
        setTimeout(() => {
          this.errorBag = {};
        }, 0);
      }
    },
    resetValidation() {
      this.inputs.forEach((input) => input.resetValidation());
      this.resetErrorBag();
    },
    register(input) {
      this.inputs.push(input);
      this.watchers.push(this.watchInput(input));
    },
    unregister(input) {
      const found = this.inputs.find((i) => i._uid === input._uid);
      if (!found)
        return;
      const unwatch = this.watchers.find((i) => i._uid === found._uid);
      if (unwatch) {
        unwatch.valid();
        unwatch.shouldValidate();
      }
      this.watchers = this.watchers.filter((i) => i._uid !== found._uid);
      this.inputs = this.inputs.filter((i) => i._uid !== found._uid);
      this.$delete(this.errorBag, found._uid);
    }
  },
  render(h) {
    return h("form", {
      staticClass: "v-form",
      attrs: __spreadValues({
        novalidate: true
      }, this.attrs$),
      on: {
        submit: (e) => this.$emit("submit", e)
      }
    }, this.$slots.default);
  }
});
function VGrid(name) {
  return Vue.extend({
    name: `v-${name}`,
    functional: true,
    props: {
      id: String,
      tag: {
        type: String,
        default: "div"
      }
    },
    render(h, {
      props: props2,
      data,
      children
    }) {
      data.staticClass = `${name} ${data.staticClass || ""}`.trim();
      const {
        attrs
      } = data;
      if (attrs) {
        data.attrs = {};
        const classes = Object.keys(attrs).filter((key) => {
          if (key === "slot")
            return false;
          const value = attrs[key];
          if (key.startsWith("data-")) {
            data.attrs[key] = value;
            return false;
          }
          return value || typeof value === "string";
        });
        if (classes.length)
          data.staticClass += ` ${classes.join(" ")}`;
      }
      if (props2.id) {
        data.domProps = data.domProps || {};
        data.domProps.id = props2.id;
      }
      return h(props2.tag, data, children);
    }
  });
}
var VContainer = VGrid("container").extend({
  name: "v-container",
  functional: true,
  props: {
    id: String,
    tag: {
      type: String,
      default: "div"
    },
    fluid: {
      type: Boolean,
      default: false
    }
  },
  render(h, {
    props: props2,
    data,
    children
  }) {
    let classes;
    const {
      attrs
    } = data;
    if (attrs) {
      data.attrs = {};
      classes = Object.keys(attrs).filter((key) => {
        if (key === "slot")
          return false;
        const value = attrs[key];
        if (key.startsWith("data-")) {
          data.attrs[key] = value;
          return false;
        }
        return value || typeof value === "string";
      });
    }
    if (props2.id) {
      data.domProps = data.domProps || {};
      data.domProps.id = props2.id;
    }
    return h(props2.tag, mergeData(data, {
      staticClass: "container",
      class: Array({
        "container--fluid": props2.fluid
      }).concat(classes || [])
    }), children);
  }
});
const breakpoints$1 = ["sm", "md", "lg", "xl"];
const breakpointProps = (() => {
  return breakpoints$1.reduce((props2, val) => {
    props2[val] = {
      type: [Boolean, String, Number],
      default: false
    };
    return props2;
  }, {});
})();
const offsetProps = (() => {
  return breakpoints$1.reduce((props2, val) => {
    props2["offset" + upperFirst(val)] = {
      type: [String, Number],
      default: null
    };
    return props2;
  }, {});
})();
const orderProps = (() => {
  return breakpoints$1.reduce((props2, val) => {
    props2["order" + upperFirst(val)] = {
      type: [String, Number],
      default: null
    };
    return props2;
  }, {});
})();
const propMap$1 = {
  col: Object.keys(breakpointProps),
  offset: Object.keys(offsetProps),
  order: Object.keys(orderProps)
};
function breakpointClass$1(type2, prop, val) {
  let className = type2;
  if (val == null || val === false) {
    return void 0;
  }
  if (prop) {
    const breakpoint = prop.replace(type2, "");
    className += `-${breakpoint}`;
  }
  if (type2 === "col" && (val === "" || val === true)) {
    return className.toLowerCase();
  }
  className += `-${val}`;
  return className.toLowerCase();
}
const cache$1 = new Map();
var VCol = Vue.extend({
  name: "v-col",
  functional: true,
  props: __spreadProps(__spreadValues(__spreadProps(__spreadValues(__spreadProps(__spreadValues({
    cols: {
      type: [Boolean, String, Number],
      default: false
    }
  }, breakpointProps), {
    offset: {
      type: [String, Number],
      default: null
    }
  }), offsetProps), {
    order: {
      type: [String, Number],
      default: null
    }
  }), orderProps), {
    alignSelf: {
      type: String,
      default: null,
      validator: (str) => ["auto", "start", "end", "center", "baseline", "stretch"].includes(str)
    },
    tag: {
      type: String,
      default: "div"
    }
  }),
  render(h, {
    props: props2,
    data,
    children,
    parent
  }) {
    let cacheKey = "";
    for (const prop in props2) {
      cacheKey += String(props2[prop]);
    }
    let classList = cache$1.get(cacheKey);
    if (!classList) {
      classList = [];
      let type2;
      for (type2 in propMap$1) {
        propMap$1[type2].forEach((prop) => {
          const value = props2[prop];
          const className = breakpointClass$1(type2, prop, value);
          if (className)
            classList.push(className);
        });
      }
      const hasColClasses = classList.some((className) => className.startsWith("col-"));
      classList.push({
        col: !hasColClasses || !props2.cols,
        [`col-${props2.cols}`]: props2.cols,
        [`offset-${props2.offset}`]: props2.offset,
        [`order-${props2.order}`]: props2.order,
        [`align-self-${props2.alignSelf}`]: props2.alignSelf
      });
      cache$1.set(cacheKey, classList);
    }
    return h(props2.tag, mergeData(data, {
      class: classList
    }), children);
  }
});
const breakpoints = ["sm", "md", "lg", "xl"];
const ALIGNMENT = ["start", "end", "center"];
function makeProps(prefix, def) {
  return breakpoints.reduce((props2, val) => {
    props2[prefix + upperFirst(val)] = def();
    return props2;
  }, {});
}
const alignValidator = (str) => [...ALIGNMENT, "baseline", "stretch"].includes(str);
const alignProps = makeProps("align", () => ({
  type: String,
  default: null,
  validator: alignValidator
}));
const justifyValidator = (str) => [...ALIGNMENT, "space-between", "space-around"].includes(str);
const justifyProps = makeProps("justify", () => ({
  type: String,
  default: null,
  validator: justifyValidator
}));
const alignContentValidator = (str) => [...ALIGNMENT, "space-between", "space-around", "stretch"].includes(str);
const alignContentProps = makeProps("alignContent", () => ({
  type: String,
  default: null,
  validator: alignContentValidator
}));
const propMap = {
  align: Object.keys(alignProps),
  justify: Object.keys(justifyProps),
  alignContent: Object.keys(alignContentProps)
};
const classMap = {
  align: "align",
  justify: "justify",
  alignContent: "align-content"
};
function breakpointClass(type2, prop, val) {
  let className = classMap[type2];
  if (val == null) {
    return void 0;
  }
  if (prop) {
    const breakpoint = prop.replace(type2, "");
    className += `-${breakpoint}`;
  }
  className += `-${val}`;
  return className.toLowerCase();
}
const cache = new Map();
var VRow = Vue.extend({
  name: "v-row",
  functional: true,
  props: __spreadValues(__spreadProps(__spreadValues(__spreadProps(__spreadValues({
    tag: {
      type: String,
      default: "div"
    },
    dense: Boolean,
    noGutters: Boolean,
    align: {
      type: String,
      default: null,
      validator: alignValidator
    }
  }, alignProps), {
    justify: {
      type: String,
      default: null,
      validator: justifyValidator
    }
  }), justifyProps), {
    alignContent: {
      type: String,
      default: null,
      validator: alignContentValidator
    }
  }), alignContentProps),
  render(h, {
    props: props2,
    data,
    children
  }) {
    let cacheKey = "";
    for (const prop in props2) {
      cacheKey += String(props2[prop]);
    }
    let classList = cache.get(cacheKey);
    if (!classList) {
      classList = [];
      let type2;
      for (type2 in propMap) {
        propMap[type2].forEach((prop) => {
          const value = props2[prop];
          const className = breakpointClass(type2, prop, value);
          if (className)
            classList.push(className);
        });
      }
      classList.push({
        "no-gutters": props2.noGutters,
        "row--dense": props2.dense,
        [`align-${props2.align}`]: props2.align,
        [`justify-${props2.justify}`]: props2.justify,
        [`align-content-${props2.alignContent}`]: props2.alignContent
      });
      cache.set(cacheKey, classList);
    }
    return h(props2.tag, mergeData(data, {
      staticClass: "row",
      class: classList
    }), children);
  }
});
var VSpacer = createSimpleFunctional("spacer", "div", "v-spacer");
var VLayout = VGrid("layout");
var VFlex = VGrid("flex");
var VHover = mixins(Delayable, Toggleable$1).extend({
  name: "v-hover",
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    value: {
      type: Boolean,
      default: void 0
    }
  },
  methods: {
    onMouseEnter() {
      this.runDelay("open");
    },
    onMouseLeave() {
      this.runDelay("close");
    }
  },
  render() {
    if (!this.$scopedSlots.default && this.value === void 0) {
      consoleWarn("v-hover is missing a default scopedSlot or bound value", this);
      return null;
    }
    let element;
    if (this.$scopedSlots.default) {
      element = this.$scopedSlots.default({
        hover: this.isActive
      });
    }
    if (Array.isArray(element) && element.length === 1) {
      element = element[0];
    }
    if (!element || Array.isArray(element) || !element.tag) {
      consoleWarn("v-hover should only contain a single element", this);
      return element;
    }
    if (!this.disabled) {
      element.data = element.data || {};
      this._g(element.data, {
        mouseenter: this.onMouseEnter,
        mouseleave: this.onMouseLeave
      });
    }
    return element;
  }
});
const BaseItem = Vue.extend({
  props: {
    activeClass: String,
    value: {
      required: false
    }
  },
  data: () => ({
    isActive: false
  }),
  methods: {
    toggle() {
      this.isActive = !this.isActive;
    }
  },
  render() {
    if (!this.$scopedSlots.default) {
      consoleWarn("v-item is missing a default scopedSlot", this);
      return null;
    }
    let element;
    if (this.$scopedSlots.default) {
      element = this.$scopedSlots.default({
        active: this.isActive,
        toggle: this.toggle
      });
    }
    if (Array.isArray(element) && element.length === 1) {
      element = element[0];
    }
    if (!element || Array.isArray(element) || !element.tag) {
      consoleWarn("v-item should only contain a single element", this);
      return element;
    }
    element.data = this._b(element.data || {}, element.tag, {
      class: {
        [this.activeClass]: this.isActive
      }
    });
    return element;
  }
});
var VItem = mixins(BaseItem, factory$1("itemGroup", "v-item", "v-item-group")).extend({
  name: "v-item"
});
var VLazy = mixins(Measurable, Toggleable$1).extend({
  name: "VLazy",
  directives: {
    intersect
  },
  props: {
    options: {
      type: Object,
      default: () => ({
        root: void 0,
        rootMargin: void 0,
        threshold: void 0
      })
    },
    tag: {
      type: String,
      default: "div"
    },
    transition: {
      type: String,
      default: "fade-transition"
    }
  },
  computed: {
    styles() {
      return __spreadValues({}, this.measurableStyles);
    }
  },
  methods: {
    genContent() {
      const children = this.isActive && getSlot(this);
      return this.transition ? this.$createElement("transition", {
        props: {
          name: this.transition
        }
      }, children) : children;
    },
    onObserve(entries, observer, isIntersecting) {
      if (this.isActive)
        return;
      this.isActive = isIntersecting;
    }
  },
  render(h) {
    return h(this.tag, {
      staticClass: "v-lazy",
      attrs: this.$attrs,
      directives: [{
        name: "intersect",
        value: {
          handler: this.onObserve,
          options: this.options
        }
      }],
      on: this.$listeners,
      style: this.styles
    }, [this.genContent()]);
  }
});
const baseMixins$b = mixins(applicationable("left", ["isActive", "isMobile", "miniVariant", "expandOnHover", "permanent", "right", "temporary", "width"]), Colorable, Dependent, Mobile, Overlayable, SSRBootable, Themeable$1);
var VNavigationDrawer = baseMixins$b.extend({
  name: "v-navigation-drawer",
  directives: {
    ClickOutside: ClickOutside$1,
    Resize: Resize$1,
    Touch: Touch$1
  },
  provide() {
    return {
      isInNav: this.tag === "nav"
    };
  },
  props: {
    bottom: Boolean,
    clipped: Boolean,
    disableResizeWatcher: Boolean,
    disableRouteWatcher: Boolean,
    expandOnHover: Boolean,
    floating: Boolean,
    height: {
      type: [Number, String],
      default() {
        return this.app ? "100vh" : "100%";
      }
    },
    miniVariant: Boolean,
    miniVariantWidth: {
      type: [Number, String],
      default: 56
    },
    permanent: Boolean,
    right: Boolean,
    src: {
      type: [String, Object],
      default: ""
    },
    stateless: Boolean,
    tag: {
      type: String,
      default() {
        return this.app ? "nav" : "aside";
      }
    },
    temporary: Boolean,
    touchless: Boolean,
    width: {
      type: [Number, String],
      default: 256
    },
    value: null
  },
  data: () => ({
    isMouseover: false,
    touchArea: {
      left: 0,
      right: 0
    },
    stackMinZIndex: 6
  }),
  computed: {
    applicationProperty() {
      return this.right ? "right" : "left";
    },
    classes() {
      return __spreadValues({
        "v-navigation-drawer": true,
        "v-navigation-drawer--absolute": this.absolute,
        "v-navigation-drawer--bottom": this.bottom,
        "v-navigation-drawer--clipped": this.clipped,
        "v-navigation-drawer--close": !this.isActive,
        "v-navigation-drawer--fixed": !this.absolute && (this.app || this.fixed),
        "v-navigation-drawer--floating": this.floating,
        "v-navigation-drawer--is-mobile": this.isMobile,
        "v-navigation-drawer--is-mouseover": this.isMouseover,
        "v-navigation-drawer--mini-variant": this.isMiniVariant,
        "v-navigation-drawer--custom-mini-variant": Number(this.miniVariantWidth) !== 56,
        "v-navigation-drawer--open": this.isActive,
        "v-navigation-drawer--open-on-hover": this.expandOnHover,
        "v-navigation-drawer--right": this.right,
        "v-navigation-drawer--temporary": this.temporary
      }, this.themeClasses);
    },
    computedMaxHeight() {
      if (!this.hasApp)
        return null;
      const computedMaxHeight = this.$vuetify.application.bottom + this.$vuetify.application.footer + this.$vuetify.application.bar;
      if (!this.clipped)
        return computedMaxHeight;
      return computedMaxHeight + this.$vuetify.application.top;
    },
    computedTop() {
      if (!this.hasApp)
        return 0;
      let computedTop = this.$vuetify.application.bar;
      computedTop += this.clipped ? this.$vuetify.application.top : 0;
      return computedTop;
    },
    computedTransform() {
      if (this.isActive)
        return 0;
      if (this.isBottom)
        return 100;
      return this.right ? 100 : -100;
    },
    computedWidth() {
      return this.isMiniVariant ? this.miniVariantWidth : this.width;
    },
    hasApp() {
      return this.app && !this.isMobile && !this.temporary;
    },
    isBottom() {
      return this.bottom && this.isMobile;
    },
    isMiniVariant() {
      return !this.expandOnHover && this.miniVariant || this.expandOnHover && !this.isMouseover;
    },
    isMobile() {
      return !this.stateless && !this.permanent && Mobile.options.computed.isMobile.call(this);
    },
    reactsToClick() {
      return !this.stateless && !this.permanent && (this.isMobile || this.temporary);
    },
    reactsToMobile() {
      return this.app && !this.disableResizeWatcher && !this.permanent && !this.stateless && !this.temporary;
    },
    reactsToResize() {
      return !this.disableResizeWatcher && !this.stateless;
    },
    reactsToRoute() {
      return !this.disableRouteWatcher && !this.stateless && (this.temporary || this.isMobile);
    },
    showOverlay() {
      return !this.hideOverlay && this.isActive && (this.isMobile || this.temporary);
    },
    styles() {
      const translate = this.isBottom ? "translateY" : "translateX";
      return {
        height: convertToUnit(this.height),
        top: !this.isBottom ? convertToUnit(this.computedTop) : "auto",
        maxHeight: this.computedMaxHeight != null ? `calc(100% - ${convertToUnit(this.computedMaxHeight)})` : void 0,
        transform: `${translate}(${convertToUnit(this.computedTransform, "%")})`,
        width: convertToUnit(this.computedWidth)
      };
    }
  },
  watch: {
    $route: "onRouteChange",
    isActive(val) {
      this.$emit("input", val);
    },
    isMobile(val, prev) {
      !val && this.isActive && !this.temporary && this.removeOverlay();
      if (prev == null || !this.reactsToResize || !this.reactsToMobile)
        return;
      this.isActive = !val;
    },
    permanent(val) {
      if (val)
        this.isActive = true;
    },
    showOverlay(val) {
      if (val)
        this.genOverlay();
      else
        this.removeOverlay();
    },
    value(val) {
      if (this.permanent)
        return;
      if (val == null) {
        this.init();
        return;
      }
      if (val !== this.isActive)
        this.isActive = val;
    },
    expandOnHover: "updateMiniVariant",
    isMouseover(val) {
      this.updateMiniVariant(!val);
    }
  },
  beforeMount() {
    this.init();
  },
  methods: {
    calculateTouchArea() {
      const parent = this.$el.parentNode;
      if (!parent)
        return;
      const parentRect = parent.getBoundingClientRect();
      this.touchArea = {
        left: parentRect.left + 50,
        right: parentRect.right - 50
      };
    },
    closeConditional() {
      return this.isActive && !this._isDestroyed && this.reactsToClick;
    },
    genAppend() {
      return this.genPosition("append");
    },
    genBackground() {
      const props2 = {
        height: "100%",
        width: "100%",
        src: this.src
      };
      const image = this.$scopedSlots.img ? this.$scopedSlots.img(props2) : this.$createElement(VImg, {
        props: props2
      });
      return this.$createElement("div", {
        staticClass: "v-navigation-drawer__image"
      }, [image]);
    },
    genDirectives() {
      const directives = [{
        name: "click-outside",
        value: {
          handler: () => {
            this.isActive = false;
          },
          closeConditional: this.closeConditional,
          include: this.getOpenDependentElements
        }
      }];
      if (!this.touchless && !this.stateless) {
        directives.push({
          name: "touch",
          value: {
            parent: true,
            left: this.swipeLeft,
            right: this.swipeRight
          }
        });
      }
      return directives;
    },
    genListeners() {
      const on = {
        transitionend: (e) => {
          if (e.target !== e.currentTarget)
            return;
          this.$emit("transitionend", e);
          const resizeEvent = document.createEvent("UIEvents");
          resizeEvent.initUIEvent("resize", true, false, window, 0);
          window.dispatchEvent(resizeEvent);
        }
      };
      if (this.miniVariant) {
        on.click = () => this.$emit("update:mini-variant", false);
      }
      if (this.expandOnHover) {
        on.mouseenter = () => this.isMouseover = true;
        on.mouseleave = () => this.isMouseover = false;
      }
      return on;
    },
    genPosition(name) {
      const slot = getSlot(this, name);
      if (!slot)
        return slot;
      return this.$createElement("div", {
        staticClass: `v-navigation-drawer__${name}`
      }, slot);
    },
    genPrepend() {
      return this.genPosition("prepend");
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-navigation-drawer__content"
      }, this.$slots.default);
    },
    genBorder() {
      return this.$createElement("div", {
        staticClass: "v-navigation-drawer__border"
      });
    },
    init() {
      if (this.permanent) {
        this.isActive = true;
      } else if (this.stateless || this.value != null) {
        this.isActive = this.value;
      } else if (!this.temporary) {
        this.isActive = !this.isMobile;
      }
    },
    onRouteChange() {
      if (this.reactsToRoute && this.closeConditional()) {
        this.isActive = false;
      }
    },
    swipeLeft(e) {
      if (this.isActive && this.right)
        return;
      this.calculateTouchArea();
      if (Math.abs(e.touchendX - e.touchstartX) < 100)
        return;
      if (this.right && e.touchstartX >= this.touchArea.right)
        this.isActive = true;
      else if (!this.right && this.isActive)
        this.isActive = false;
    },
    swipeRight(e) {
      if (this.isActive && !this.right)
        return;
      this.calculateTouchArea();
      if (Math.abs(e.touchendX - e.touchstartX) < 100)
        return;
      if (!this.right && e.touchstartX <= this.touchArea.left)
        this.isActive = true;
      else if (this.right && this.isActive)
        this.isActive = false;
    },
    updateApplication() {
      if (!this.isActive || this.isMobile || this.temporary || !this.$el)
        return 0;
      const width = Number(this.computedWidth);
      return isNaN(width) ? this.$el.clientWidth : width;
    },
    updateMiniVariant(val) {
      if (this.miniVariant !== val)
        this.$emit("update:mini-variant", val);
    }
  },
  render(h) {
    const children = [this.genPrepend(), this.genContent(), this.genAppend(), this.genBorder()];
    if (this.src || getSlot(this, "img"))
      children.unshift(this.genBackground());
    return h(this.tag, this.setBackgroundColor(this.color, {
      class: this.classes,
      style: this.styles,
      directives: this.genDirectives(),
      on: this.genListeners()
    }), children);
  }
});
var VOverflowBtn = VAutocomplete.extend({
  name: "v-overflow-btn",
  props: {
    editable: Boolean,
    segmented: Boolean
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VAutocomplete.options.computed.classes.call(this)), {
        "v-overflow-btn": true,
        "v-overflow-btn--segmented": this.segmented,
        "v-overflow-btn--editable": this.editable
      });
    },
    isAnyValueAllowed() {
      return this.editable || VAutocomplete.options.computed.isAnyValueAllowed.call(this);
    },
    isSingle() {
      return true;
    },
    computedItems() {
      return this.segmented ? this.allItems : this.filteredItems;
    }
  },
  methods: {
    genSelections() {
      return this.editable ? VAutocomplete.options.methods.genSelections.call(this) : VSelect.options.methods.genSelections.call(this);
    },
    genCommaSelection(item, index, last) {
      return this.segmented ? this.genSegmentedBtn(item) : VSelect.options.methods.genCommaSelection.call(this, item, index, last);
    },
    genInput() {
      const input = VTextField.options.methods.genInput.call(this);
      input.data = input.data || {};
      input.data.domProps.value = this.editable ? this.internalSearch : "";
      input.data.attrs.readonly = !this.isAnyValueAllowed;
      return input;
    },
    genLabel() {
      if (this.editable && this.isFocused)
        return null;
      const label = VTextField.options.methods.genLabel.call(this);
      if (!label)
        return label;
      label.data = label.data || {};
      label.data.style = {};
      return label;
    },
    genSegmentedBtn(item) {
      const itemValue = this.getValue(item);
      const itemObj = this.computedItems.find((i) => this.getValue(i) === itemValue) || item;
      if (!itemObj.text || !itemObj.callback) {
        consoleWarn('When using "segmented" prop without a selection slot, items must contain both a text and callback property', this);
        return null;
      }
      return this.$createElement(VBtn, {
        props: {
          text: true
        },
        on: {
          click(e) {
            e.stopPropagation();
            itemObj.callback(e);
          }
        }
      }, [itemObj.text]);
    },
    updateValue(val) {
      if (val) {
        this.initialValue = this.lazyValue;
      } else if (this.initialValue !== this.lazyValue) {
        this.$emit("change", this.lazyValue);
      }
    }
  }
});
var VPagination = mixins(Colorable, intersectable({
  onVisible: ["init"]
}), Themeable$1).extend({
  name: "v-pagination",
  directives: {
    Resize: Resize$1
  },
  props: {
    circle: Boolean,
    disabled: Boolean,
    length: {
      type: Number,
      default: 0,
      validator: (val) => val % 1 === 0
    },
    nextIcon: {
      type: String,
      default: "$next"
    },
    prevIcon: {
      type: String,
      default: "$prev"
    },
    totalVisible: [Number, String],
    value: {
      type: Number,
      default: 0
    },
    pageAriaLabel: {
      type: String,
      default: "$vuetify.pagination.ariaLabel.page"
    },
    currentPageAriaLabel: {
      type: String,
      default: "$vuetify.pagination.ariaLabel.currentPage"
    },
    previousAriaLabel: {
      type: String,
      default: "$vuetify.pagination.ariaLabel.previous"
    },
    nextAriaLabel: {
      type: String,
      default: "$vuetify.pagination.ariaLabel.next"
    },
    wrapperAriaLabel: {
      type: String,
      default: "$vuetify.pagination.ariaLabel.wrapper"
    }
  },
  data() {
    return {
      maxButtons: 0,
      selected: null
    };
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-pagination": true,
        "v-pagination--circle": this.circle,
        "v-pagination--disabled": this.disabled
      }, this.themeClasses);
    },
    items() {
      const totalVisible = parseInt(this.totalVisible, 10);
      if (totalVisible === 0) {
        return [];
      }
      const maxLength = Math.min(Math.max(0, totalVisible) || this.length, Math.max(0, this.maxButtons) || this.length, this.length);
      if (this.length <= maxLength) {
        return this.range(1, this.length);
      }
      const even = maxLength % 2 === 0 ? 1 : 0;
      const left = Math.floor(maxLength / 2);
      const right = this.length - left + 1 + even;
      if (this.value > left && this.value < right) {
        const start = this.value - left + 2;
        const end = this.value + left - 2 - even;
        return [1, "...", ...this.range(start, end), "...", this.length];
      } else if (this.value === left) {
        const end = this.value + left - 1 - even;
        return [...this.range(1, end), "...", this.length];
      } else if (this.value === right) {
        const start = this.value - left + 1;
        return [1, "...", ...this.range(start, this.length)];
      } else {
        return [...this.range(1, left), "...", ...this.range(right, this.length)];
      }
    }
  },
  watch: {
    value() {
      this.init();
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.selected = null;
      this.$nextTick(this.onResize);
      setTimeout(() => this.selected = this.value, 100);
    },
    onResize() {
      const width = this.$el && this.$el.parentElement ? this.$el.parentElement.clientWidth : window.innerWidth;
      this.maxButtons = Math.floor((width - 96) / 42);
    },
    next(e) {
      e.preventDefault();
      this.$emit("input", this.value + 1);
      this.$emit("next");
    },
    previous(e) {
      e.preventDefault();
      this.$emit("input", this.value - 1);
      this.$emit("previous");
    },
    range(from, to) {
      const range = [];
      from = from > 0 ? from : 1;
      for (let i = from; i <= to; i++) {
        range.push(i);
      }
      return range;
    },
    genIcon(h, icon, disabled, fn, label) {
      return h("li", [h("button", {
        staticClass: "v-pagination__navigation",
        class: {
          "v-pagination__navigation--disabled": disabled
        },
        attrs: {
          disabled,
          type: "button",
          "aria-label": label
        },
        on: disabled ? {} : {
          click: fn
        }
      }, [h(VIcon$1, [icon])])]);
    },
    genItem(h, i) {
      const color = i === this.value && (this.color || "primary");
      const isCurrentPage = i === this.value;
      const ariaLabel = isCurrentPage ? this.currentPageAriaLabel : this.pageAriaLabel;
      return h("button", this.setBackgroundColor(color, {
        staticClass: "v-pagination__item",
        class: {
          "v-pagination__item--active": i === this.value
        },
        attrs: {
          type: "button",
          "aria-current": isCurrentPage,
          "aria-label": this.$vuetify.lang.t(ariaLabel, i)
        },
        on: {
          click: () => this.$emit("input", i)
        }
      }), [i.toString()]);
    },
    genItems(h) {
      return this.items.map((i, index) => {
        return h("li", {
          key: index
        }, [isNaN(Number(i)) ? h("span", {
          class: "v-pagination__more"
        }, [i.toString()]) : this.genItem(h, i)]);
      });
    },
    genList(h, children) {
      return h("ul", {
        directives: [{
          modifiers: {
            quiet: true
          },
          name: "resize",
          value: this.onResize
        }],
        class: this.classes
      }, children);
    }
  },
  render(h) {
    const children = [this.genIcon(h, this.$vuetify.rtl ? this.nextIcon : this.prevIcon, this.value <= 1, this.previous, this.$vuetify.lang.t(this.previousAriaLabel)), this.genItems(h), this.genIcon(h, this.$vuetify.rtl ? this.prevIcon : this.nextIcon, this.value >= this.length, this.next, this.$vuetify.lang.t(this.nextAriaLabel))];
    return h("nav", {
      attrs: {
        role: "navigation",
        "aria-label": this.$vuetify.lang.t(this.wrapperAriaLabel)
      }
    }, [this.genList(h, children)]);
  }
});
var Translatable = Vue.extend({
  name: "translatable",
  props: {
    height: Number
  },
  data: () => ({
    elOffsetTop: 0,
    parallax: 0,
    parallaxDist: 0,
    percentScrolled: 0,
    scrollTop: 0,
    windowHeight: 0,
    windowBottom: 0
  }),
  computed: {
    imgHeight() {
      return this.objHeight();
    }
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.translate, false);
    window.removeEventListener("resize", this.translate, false);
  },
  methods: {
    calcDimensions() {
      const offset = this.$el.getBoundingClientRect();
      this.scrollTop = window.pageYOffset;
      this.parallaxDist = this.imgHeight - this.height;
      this.elOffsetTop = offset.top + this.scrollTop;
      this.windowHeight = window.innerHeight;
      this.windowBottom = this.scrollTop + this.windowHeight;
    },
    listeners() {
      window.addEventListener("scroll", this.translate, false);
      window.addEventListener("resize", this.translate, false);
    },
    objHeight() {
      throw new Error("Not implemented !");
    },
    translate() {
      this.calcDimensions();
      this.percentScrolled = (this.windowBottom - this.elOffsetTop) / (parseInt(this.height) + this.windowHeight);
      this.parallax = Math.round(this.parallaxDist * this.percentScrolled);
    }
  }
});
const baseMixins$a = mixins(Translatable);
var VParallax = baseMixins$a.extend().extend({
  name: "v-parallax",
  props: {
    alt: {
      type: String,
      default: ""
    },
    height: {
      type: [String, Number],
      default: 500
    },
    src: String,
    srcset: String
  },
  data: () => ({
    isBooted: false
  }),
  computed: {
    styles() {
      return {
        display: "block",
        opacity: this.isBooted ? 1 : 0,
        transform: `translate(-50%, ${this.parallax}px)`
      };
    }
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      const img = this.$refs.img;
      if (!img)
        return;
      if (img.complete) {
        this.translate();
        this.listeners();
      } else {
        img.addEventListener("load", () => {
          this.translate();
          this.listeners();
        }, false);
      }
      this.isBooted = true;
    },
    objHeight() {
      return this.$refs.img.naturalHeight;
    }
  },
  render(h) {
    const imgData = {
      staticClass: "v-parallax__image",
      style: this.styles,
      attrs: {
        src: this.src,
        srcset: this.srcset,
        alt: this.alt
      },
      ref: "img"
    };
    const container = h("div", {
      staticClass: "v-parallax__image-container"
    }, [h("img", imgData)]);
    const content = h("div", {
      staticClass: "v-parallax__content"
    }, this.$slots.default);
    return h("div", {
      staticClass: "v-parallax",
      style: {
        height: `${this.height}px`
      },
      on: this.$listeners
    }, [container, content]);
  }
});
const baseMixins$9 = mixins(Comparable, BaseItemGroup, VInput);
var VRadioGroup = baseMixins$9.extend({
  name: "v-radio-group",
  provide() {
    return {
      radioGroup: this
    };
  },
  props: {
    column: {
      type: Boolean,
      default: true
    },
    height: {
      type: [Number, String],
      default: "auto"
    },
    name: String,
    row: Boolean,
    value: null
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VInput.options.computed.classes.call(this)), {
        "v-input--selection-controls v-input--radio-group": true,
        "v-input--radio-group--column": this.column && !this.row,
        "v-input--radio-group--row": this.row
      });
    }
  },
  methods: {
    genDefaultSlot() {
      return this.$createElement("div", {
        staticClass: "v-input--radio-group__input",
        attrs: {
          id: this.id,
          role: "radiogroup",
          "aria-labelledby": this.computedId
        }
      }, VInput.options.methods.genDefaultSlot.call(this));
    },
    genInputSlot() {
      const render = VInput.options.methods.genInputSlot.call(this);
      delete render.data.on.click;
      return render;
    },
    genLabel() {
      const label = VInput.options.methods.genLabel.call(this);
      if (!label)
        return null;
      label.data.attrs.id = this.computedId;
      delete label.data.attrs.for;
      label.tag = "legend";
      return label;
    },
    onClick: BaseItemGroup.options.methods.onClick
  }
});
const baseMixins$8 = mixins(BindsAttrs, Colorable, Rippleable, factory$1("radioGroup"), Themeable$1);
var VRadio = baseMixins$8.extend().extend({
  name: "v-radio",
  inheritAttrs: false,
  props: {
    disabled: Boolean,
    id: String,
    label: String,
    name: String,
    offIcon: {
      type: String,
      default: "$radioOff"
    },
    onIcon: {
      type: String,
      default: "$radioOn"
    },
    readonly: Boolean,
    value: {
      default: null
    }
  },
  data: () => ({
    isFocused: false
  }),
  computed: {
    classes() {
      return __spreadValues(__spreadValues({
        "v-radio--is-disabled": this.isDisabled,
        "v-radio--is-focused": this.isFocused
      }, this.themeClasses), this.groupClasses);
    },
    computedColor() {
      return Selectable.options.computed.computedColor.call(this);
    },
    computedIcon() {
      return this.isActive ? this.onIcon : this.offIcon;
    },
    computedId() {
      return VInput.options.computed.computedId.call(this);
    },
    hasLabel: VInput.options.computed.hasLabel,
    hasState() {
      return (this.radioGroup || {}).hasState;
    },
    isDisabled() {
      return this.disabled || !!this.radioGroup && this.radioGroup.isDisabled;
    },
    isReadonly() {
      return this.readonly || !!this.radioGroup && this.radioGroup.isReadonly;
    },
    computedName() {
      if (this.name || !this.radioGroup) {
        return this.name;
      }
      return this.radioGroup.name || `radio-${this.radioGroup._uid}`;
    },
    rippleState() {
      return Selectable.options.computed.rippleState.call(this);
    },
    validationState() {
      return (this.radioGroup || {}).validationState || this.computedColor;
    }
  },
  methods: {
    genInput(args) {
      return Selectable.options.methods.genInput.call(this, "radio", args);
    },
    genLabel() {
      if (!this.hasLabel)
        return null;
      return this.$createElement(VLabel, {
        on: {
          click: prevent
        },
        attrs: {
          for: this.computedId
        },
        props: {
          color: this.validationState,
          focused: this.hasState
        }
      }, getSlot(this, "label") || this.label);
    },
    genRadio() {
      return this.$createElement("div", {
        staticClass: "v-input--selection-controls__input"
      }, [this.$createElement(VIcon$1, this.setTextColor(this.validationState, {
        props: {
          dense: this.radioGroup && this.radioGroup.dense
        }
      }), this.computedIcon), this.genInput(__spreadValues({
        name: this.computedName,
        value: this.value
      }, this.attrs$)), this.genRipple(this.setTextColor(this.rippleState))]);
    },
    onFocus(e) {
      this.isFocused = true;
      this.$emit("focus", e);
    },
    onBlur(e) {
      this.isFocused = false;
      this.$emit("blur", e);
    },
    onChange() {
      if (this.isDisabled || this.isReadonly || this.isActive)
        return;
      this.toggle();
    },
    onKeydown: () => {
    }
  },
  render(h) {
    const data = {
      staticClass: "v-radio",
      class: this.classes,
      on: mergeListeners({
        click: this.onChange
      }, this.listeners$)
    };
    return h("div", data, [this.genRadio(), this.genLabel()]);
  }
});
var VRangeSlider = VSlider.extend({
  name: "v-range-slider",
  props: {
    value: {
      type: Array,
      default: () => [0, 0]
    }
  },
  data() {
    return {
      activeThumb: null,
      lazyValue: this.value
    };
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VSlider.options.computed.classes.call(this)), {
        "v-input--range-slider": true
      });
    },
    internalValue: {
      get() {
        return this.lazyValue;
      },
      set(val) {
        let value = val.map((v = 0) => this.roundValue(Math.min(Math.max(v, this.minValue), this.maxValue)));
        if (value[0] > value[1] || value[1] < value[0]) {
          if (this.activeThumb !== null) {
            const toFocus = this.activeThumb === 1 ? 0 : 1;
            const el = this.$refs[`thumb_${toFocus}`];
            el.focus();
          }
          value = [value[1], value[0]];
        }
        this.lazyValue = value;
        if (!deepEqual(value, this.value))
          this.$emit("input", value);
        this.validate();
      }
    },
    inputWidth() {
      return this.internalValue.map((v) => (this.roundValue(v) - this.minValue) / (this.maxValue - this.minValue) * 100);
    }
  },
  methods: {
    getTrackStyle(startLength, endLength, startPadding = 0, endPadding = 0) {
      const startDir = this.vertical ? this.$vuetify.rtl ? "top" : "bottom" : this.$vuetify.rtl ? "right" : "left";
      const endDir = this.vertical ? "height" : "width";
      const start = `calc(${startLength}% + ${startPadding}px)`;
      const end = `calc(${endLength}% + ${endPadding}px)`;
      return {
        transition: this.trackTransition,
        [startDir]: start,
        [endDir]: end
      };
    },
    getIndexOfClosestValue(arr, v) {
      if (Math.abs(arr[0] - v) < Math.abs(arr[1] - v))
        return 0;
      else
        return 1;
    },
    genInput() {
      return createRange(2).map((i) => {
        const input = VSlider.options.methods.genInput.call(this);
        input.data = input.data || {};
        input.data.attrs = input.data.attrs || {};
        input.data.attrs.value = this.internalValue[i];
        input.data.attrs.id = `input-${i ? "max" : "min"}-${this._uid}`;
        return input;
      });
    },
    genTrackContainer() {
      const children = [];
      const padding = this.isDisabled ? 10 : 0;
      const sections = [{
        class: "v-slider__track-background",
        color: this.computedTrackColor,
        styles: [0, this.inputWidth[0], 0, -padding]
      }, {
        class: this.isDisabled ? "v-slider__track-background" : "v-slider__track-fill",
        color: this.isDisabled ? this.computedTrackColor : this.computedTrackFillColor,
        styles: [this.inputWidth[0], Math.abs(this.inputWidth[1] - this.inputWidth[0]), padding, padding * -2]
      }, {
        class: "v-slider__track-background",
        color: this.computedTrackColor,
        styles: [this.inputWidth[1], Math.abs(100 - this.inputWidth[1]), padding, -padding]
      }];
      if (this.$vuetify.rtl)
        sections.reverse();
      children.push(...sections.map((section) => this.$createElement("div", this.setBackgroundColor(section.color, {
        staticClass: section.class,
        style: this.getTrackStyle(...section.styles)
      }))));
      return this.$createElement("div", {
        staticClass: "v-slider__track-container",
        ref: "track"
      }, children);
    },
    genChildren() {
      return [this.genInput(), this.genTrackContainer(), this.genSteps(), createRange(2).map((index) => {
        const value = this.internalValue[index];
        const onFocus = (e) => {
          this.isFocused = true;
          this.activeThumb = index;
          this.$emit("focus", e);
        };
        const onBlur = (e) => {
          this.isFocused = false;
          this.activeThumb = null;
          this.$emit("blur", e);
        };
        const valueWidth = this.inputWidth[index];
        const isActive = this.isActive && this.activeThumb === index;
        const isFocused = this.isFocused && this.activeThumb === index;
        return this.genThumbContainer(value, valueWidth, isActive, isFocused, onFocus, onBlur, `thumb_${index}`);
      })];
    },
    reevaluateSelected(value) {
      this.activeThumb = this.getIndexOfClosestValue(this.internalValue, value);
      const refName = `thumb_${this.activeThumb}`;
      const thumbRef = this.$refs[refName];
      thumbRef.focus();
    },
    onSliderMouseDown(e) {
      var _e$target;
      const value = this.parseMouseMove(e);
      this.reevaluateSelected(value);
      this.oldValue = this.internalValue;
      this.isActive = true;
      if ((_e$target = e.target) == null ? void 0 : _e$target.matches(".v-slider__thumb-container, .v-slider__thumb-container *")) {
        this.thumbPressed = true;
      } else {
        window.clearTimeout(this.mouseTimeout);
        this.mouseTimeout = window.setTimeout(() => {
          this.thumbPressed = true;
        }, 300);
      }
      const mouseUpOptions = passiveSupported ? {
        passive: true,
        capture: true
      } : true;
      const mouseMoveOptions = passiveSupported ? {
        passive: true
      } : false;
      const isTouchEvent2 = "touches" in e;
      this.onMouseMove(e);
      this.app.addEventListener(isTouchEvent2 ? "touchmove" : "mousemove", this.onMouseMove, mouseMoveOptions);
      addOnceEventListener(this.app, isTouchEvent2 ? "touchend" : "mouseup", this.onSliderMouseUp, mouseUpOptions);
      this.$emit("start", this.internalValue);
    },
    onSliderClick(e) {
      if (!this.isActive) {
        if (this.noClick) {
          this.noClick = false;
          return;
        }
        const value = this.parseMouseMove(e);
        this.reevaluateSelected(value);
        this.setInternalValue(value);
        this.$emit("change", this.internalValue);
      }
    },
    onMouseMove(e) {
      const value = this.parseMouseMove(e);
      if (e.type === "mousemove") {
        this.thumbPressed = true;
      }
      this.activeThumb = this.getIndexOfClosestValue(this.internalValue, value);
      this.setInternalValue(value);
    },
    onKeyDown(e) {
      if (this.activeThumb === null)
        return;
      const value = this.parseKeyDown(e, this.internalValue[this.activeThumb]);
      if (value == null)
        return;
      this.setInternalValue(value);
      this.$emit("change", this.internalValue);
    },
    setInternalValue(value) {
      this.internalValue = this.internalValue.map((v, i) => {
        if (i === this.activeThumb)
          return value;
        else
          return Number(v);
      });
    }
  }
});
var VRating = mixins(Colorable, Delayable, Rippleable, Sizeable, Themeable$1).extend({
  name: "v-rating",
  props: {
    backgroundColor: {
      type: String,
      default: "accent"
    },
    color: {
      type: String,
      default: "primary"
    },
    clearable: Boolean,
    dense: Boolean,
    emptyIcon: {
      type: String,
      default: "$ratingEmpty"
    },
    fullIcon: {
      type: String,
      default: "$ratingFull"
    },
    halfIcon: {
      type: String,
      default: "$ratingHalf"
    },
    halfIncrements: Boolean,
    hover: Boolean,
    length: {
      type: [Number, String],
      default: 5
    },
    readonly: Boolean,
    size: [Number, String],
    value: {
      type: Number,
      default: 0
    },
    iconLabel: {
      type: String,
      default: "$vuetify.rating.ariaLabel.icon"
    }
  },
  data() {
    return {
      hoverIndex: -1,
      internalValue: this.value
    };
  },
  computed: {
    directives() {
      if (this.readonly || !this.ripple)
        return [];
      return [{
        name: "ripple",
        value: {
          circle: true
        }
      }];
    },
    iconProps() {
      const {
        dark,
        large,
        light,
        medium,
        small,
        size,
        xLarge,
        xSmall
      } = this.$props;
      return {
        dark,
        large,
        light,
        medium,
        size,
        small,
        xLarge,
        xSmall
      };
    },
    isHovering() {
      return this.hover && this.hoverIndex >= 0;
    }
  },
  watch: {
    internalValue(val) {
      val !== this.value && this.$emit("input", val);
    },
    value(val) {
      this.internalValue = val;
    }
  },
  methods: {
    createClickFn(i) {
      return (e) => {
        if (this.readonly)
          return;
        const newValue = this.genHoverIndex(e, i);
        if (this.clearable && this.internalValue === newValue) {
          this.internalValue = 0;
        } else {
          this.internalValue = newValue;
        }
      };
    },
    createProps(i) {
      const props2 = {
        index: i,
        value: this.internalValue,
        click: this.createClickFn(i),
        isFilled: Math.floor(this.internalValue) > i,
        isHovered: Math.floor(this.hoverIndex) > i
      };
      if (this.halfIncrements) {
        props2.isHalfHovered = !props2.isHovered && (this.hoverIndex - i) % 1 > 0;
        props2.isHalfFilled = !props2.isFilled && (this.internalValue - i) % 1 > 0;
      }
      return props2;
    },
    genHoverIndex(e, i) {
      let isHalf = this.isHalfEvent(e);
      if (this.halfIncrements && this.$vuetify.rtl) {
        isHalf = !isHalf;
      }
      return i + (isHalf ? 0.5 : 1);
    },
    getIconName(props2) {
      const isFull = this.isHovering ? props2.isHovered : props2.isFilled;
      const isHalf = this.isHovering ? props2.isHalfHovered : props2.isHalfFilled;
      return isFull ? this.fullIcon : isHalf ? this.halfIcon : this.emptyIcon;
    },
    getColor(props2) {
      if (this.isHovering) {
        if (props2.isHovered || props2.isHalfHovered)
          return this.color;
      } else {
        if (props2.isFilled || props2.isHalfFilled)
          return this.color;
      }
      return this.backgroundColor;
    },
    isHalfEvent(e) {
      if (this.halfIncrements) {
        const rect = e.target && e.target.getBoundingClientRect();
        if (rect && e.pageX - rect.left < rect.width / 2)
          return true;
      }
      return false;
    },
    onMouseEnter(e, i) {
      this.runDelay("open", () => {
        this.hoverIndex = this.genHoverIndex(e, i);
      });
    },
    onMouseLeave() {
      this.runDelay("close", () => this.hoverIndex = -1);
    },
    genItem(i) {
      const props2 = this.createProps(i);
      if (this.$scopedSlots.item)
        return this.$scopedSlots.item(props2);
      const listeners = {
        click: props2.click
      };
      if (this.hover) {
        listeners.mouseenter = (e) => this.onMouseEnter(e, i);
        listeners.mouseleave = this.onMouseLeave;
        if (this.halfIncrements) {
          listeners.mousemove = (e) => this.onMouseEnter(e, i);
        }
      }
      return this.$createElement(VIcon$1, this.setTextColor(this.getColor(props2), {
        attrs: {
          "aria-label": this.$vuetify.lang.t(this.iconLabel, i + 1, Number(this.length))
        },
        directives: this.directives,
        props: this.iconProps,
        on: listeners
      }), [this.getIconName(props2)]);
    }
  },
  render(h) {
    const children = createRange(Number(this.length)).map((i) => this.genItem(i));
    return h("div", {
      staticClass: "v-rating",
      class: {
        "v-rating--readonly": this.readonly,
        "v-rating--dense": this.dense
      }
    }, children);
  }
});
var VSkeletonLoader = mixins(Elevatable, Measurable, Themeable$1).extend({
  name: "VSkeletonLoader",
  props: {
    boilerplate: Boolean,
    loading: Boolean,
    tile: Boolean,
    transition: String,
    type: String,
    types: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    attrs() {
      if (!this.isLoading)
        return this.$attrs;
      return !this.boilerplate ? __spreadValues({
        "aria-busy": true,
        "aria-live": "polite",
        role: "alert"
      }, this.$attrs) : {};
    },
    classes() {
      return __spreadValues(__spreadValues({
        "v-skeleton-loader--boilerplate": this.boilerplate,
        "v-skeleton-loader--is-loading": this.isLoading,
        "v-skeleton-loader--tile": this.tile
      }, this.themeClasses), this.elevationClasses);
    },
    isLoading() {
      return !("default" in this.$scopedSlots) || this.loading;
    },
    rootTypes() {
      return __spreadValues({
        actions: "button@2",
        article: "heading, paragraph",
        avatar: "avatar",
        button: "button",
        card: "image, card-heading",
        "card-avatar": "image, list-item-avatar",
        "card-heading": "heading",
        chip: "chip",
        "date-picker": "list-item, card-heading, divider, date-picker-options, date-picker-days, actions",
        "date-picker-options": "text, avatar@2",
        "date-picker-days": "avatar@28",
        heading: "heading",
        image: "image",
        "list-item": "text",
        "list-item-avatar": "avatar, text",
        "list-item-two-line": "sentences",
        "list-item-avatar-two-line": "avatar, sentences",
        "list-item-three-line": "paragraph",
        "list-item-avatar-three-line": "avatar, paragraph",
        paragraph: "text@3",
        sentences: "text@2",
        table: "table-heading, table-thead, table-tbody, table-tfoot",
        "table-heading": "heading, text",
        "table-thead": "heading@6",
        "table-tbody": "table-row-divider@6",
        "table-row-divider": "table-row, divider",
        "table-row": "table-cell@6",
        "table-cell": "text",
        "table-tfoot": "text@2, avatar@2",
        text: "text"
      }, this.types);
    }
  },
  methods: {
    genBone(text, children) {
      return this.$createElement("div", {
        staticClass: `v-skeleton-loader__${text} v-skeleton-loader__bone`
      }, children);
    },
    genBones(bone) {
      const [type2, length] = bone.split("@");
      const generator = () => this.genStructure(type2);
      return Array.from({
        length
      }).map(generator);
    },
    genStructure(type2) {
      let children = [];
      type2 = type2 || this.type || "";
      const bone = this.rootTypes[type2] || "";
      if (type2 === bone)
        ;
      else if (type2.indexOf(",") > -1)
        return this.mapBones(type2);
      else if (type2.indexOf("@") > -1)
        return this.genBones(type2);
      else if (bone.indexOf(",") > -1)
        children = this.mapBones(bone);
      else if (bone.indexOf("@") > -1)
        children = this.genBones(bone);
      else if (bone)
        children.push(this.genStructure(bone));
      return [this.genBone(type2, children)];
    },
    genSkeleton() {
      const children = [];
      if (!this.isLoading)
        children.push(getSlot(this));
      else
        children.push(this.genStructure());
      if (!this.transition)
        return children;
      return this.$createElement("transition", {
        props: {
          name: this.transition
        },
        on: {
          afterEnter: this.resetStyles,
          beforeEnter: this.onBeforeEnter,
          beforeLeave: this.onBeforeLeave,
          leaveCancelled: this.resetStyles
        }
      }, children);
    },
    mapBones(bones) {
      return bones.replace(/\s/g, "").split(",").map(this.genStructure);
    },
    onBeforeEnter(el) {
      this.resetStyles(el);
      if (!this.isLoading)
        return;
      el._initialStyle = {
        display: el.style.display,
        transition: el.style.transition
      };
      el.style.setProperty("transition", "none", "important");
    },
    onBeforeLeave(el) {
      el.style.setProperty("display", "none", "important");
    },
    resetStyles(el) {
      if (!el._initialStyle)
        return;
      el.style.display = el._initialStyle.display || "";
      el.style.transition = el._initialStyle.transition;
      delete el._initialStyle;
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-skeleton-loader",
      attrs: this.attrs,
      on: this.$listeners,
      class: this.classes,
      style: this.isLoading ? this.measurableStyles : void 0
    }, [this.genSkeleton()]);
  }
});
var VSlideItem = mixins(BaseItem, factory$1("slideGroup")).extend({
  name: "v-slide-item"
});
var VSnackbar = mixins(VSheet, Colorable, Toggleable$1, factory$3(["absolute", "bottom", "left", "right", "top"])).extend({
  name: "v-snackbar",
  props: {
    app: Boolean,
    centered: Boolean,
    contentClass: {
      type: String,
      default: ""
    },
    multiLine: Boolean,
    text: Boolean,
    timeout: {
      type: [Number, String],
      default: 5e3
    },
    transition: {
      type: [Boolean, String],
      default: "v-snack-transition",
      validator: (v) => typeof v === "string" || v === false
    },
    vertical: Boolean
  },
  data: () => ({
    activeTimeout: -1
  }),
  computed: {
    classes() {
      return {
        "v-snack--absolute": this.absolute,
        "v-snack--active": this.isActive,
        "v-snack--bottom": this.bottom || !this.top,
        "v-snack--centered": this.centered,
        "v-snack--has-background": this.hasBackground,
        "v-snack--left": this.left,
        "v-snack--multi-line": this.multiLine && !this.vertical,
        "v-snack--right": this.right,
        "v-snack--text": this.text,
        "v-snack--top": this.top,
        "v-snack--vertical": this.vertical
      };
    },
    hasBackground() {
      return !this.text && !this.outlined;
    },
    isDark() {
      return this.hasBackground ? !this.light : Themeable$1.options.computed.isDark.call(this);
    },
    styles() {
      if (this.absolute)
        return {};
      const {
        bar,
        bottom,
        footer,
        insetFooter,
        left,
        right,
        top
      } = this.$vuetify.application;
      return {
        paddingBottom: convertToUnit(bottom + footer + insetFooter),
        paddingLeft: !this.app ? void 0 : convertToUnit(left),
        paddingRight: !this.app ? void 0 : convertToUnit(right),
        paddingTop: convertToUnit(bar + top)
      };
    }
  },
  watch: {
    isActive: "setTimeout",
    timeout: "setTimeout"
  },
  mounted() {
    if (this.isActive)
      this.setTimeout();
  },
  created() {
    if (this.$attrs.hasOwnProperty("auto-height")) {
      removed("auto-height", this);
    }
    if (this.timeout == 0) {
      deprecate('timeout="0"', "-1", this);
    }
  },
  methods: {
    genActions() {
      return this.$createElement("div", {
        staticClass: "v-snack__action "
      }, [getSlot(this, "action", {
        attrs: {
          class: "v-snack__btn"
        }
      })]);
    },
    genContent() {
      return this.$createElement("div", {
        staticClass: "v-snack__content",
        class: {
          [this.contentClass]: true
        },
        attrs: {
          role: "status",
          "aria-live": "polite"
        }
      }, [getSlot(this)]);
    },
    genWrapper() {
      const setColor = this.hasBackground ? this.setBackgroundColor : this.setTextColor;
      const data = setColor(this.color, {
        staticClass: "v-snack__wrapper",
        class: VSheet.options.computed.classes.call(this),
        style: VSheet.options.computed.styles.call(this),
        directives: [{
          name: "show",
          value: this.isActive
        }],
        on: {
          mouseenter: () => window.clearTimeout(this.activeTimeout),
          mouseleave: this.setTimeout
        }
      });
      return this.$createElement("div", data, [this.genContent(), this.genActions()]);
    },
    genTransition() {
      return this.$createElement("transition", {
        props: {
          name: this.transition
        }
      }, [this.genWrapper()]);
    },
    setTimeout() {
      window.clearTimeout(this.activeTimeout);
      const timeout = Number(this.timeout);
      if (!this.isActive || [0, -1].includes(timeout)) {
        return;
      }
      this.activeTimeout = window.setTimeout(() => {
        this.isActive = false;
      }, timeout);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-snack",
      class: this.classes,
      style: this.styles
    }, [this.transition !== false ? this.genTransition() : this.genWrapper()]);
  }
});
function genPoints(values, boundary) {
  const {
    minX,
    maxX,
    minY,
    maxY
  } = boundary;
  const totalValues = values.length;
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const gridX = (maxX - minX) / (totalValues - 1);
  const gridY = (maxY - minY) / (maxValue - minValue || 1);
  return values.map((value, index) => {
    return {
      x: minX + index * gridX,
      y: maxY - (value - minValue) * gridY,
      value
    };
  });
}
function genBars(values, boundary) {
  const {
    minX,
    maxX,
    minY,
    maxY
  } = boundary;
  const totalValues = values.length;
  let maxValue = Math.max(...values);
  let minValue = Math.min(...values);
  if (minValue > 0)
    minValue = 0;
  if (maxValue < 0)
    maxValue = 0;
  const gridX = maxX / totalValues;
  const gridY = (maxY - minY) / (maxValue - minValue || 1);
  const horizonY = maxY - Math.abs(minValue * gridY);
  return values.map((value, index) => {
    const height = Math.abs(gridY * value);
    return {
      x: minX + index * gridX,
      y: horizonY - height + +(value < 0) * height,
      height,
      value
    };
  });
}
function int(value) {
  return parseInt(value, 10);
}
function checkCollinear(p0, p1, p2) {
  return int(p0.x + p2.x) === int(2 * p1.x) && int(p0.y + p2.y) === int(2 * p1.y);
}
function getDistance(p1, p2) {
  return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
}
function moveTo(to, from, radius) {
  const vector = {
    x: to.x - from.x,
    y: to.y - from.y
  };
  const length = Math.sqrt(vector.x * vector.x + vector.y * vector.y);
  const unitVector = {
    x: vector.x / length,
    y: vector.y / length
  };
  return {
    x: from.x + unitVector.x * radius,
    y: from.y + unitVector.y * radius
  };
}
function genPath(points, radius, fill = false, height = 75) {
  const start = points.shift();
  const end = points[points.length - 1];
  return (fill ? `M${start.x} ${height - start.x + 2} L${start.x} ${start.y}` : `M${start.x} ${start.y}`) + points.map((point, index) => {
    const next = points[index + 1];
    const prev = points[index - 1] || start;
    const isCollinear = next && checkCollinear(next, point, prev);
    if (!next || isCollinear) {
      return `L${point.x} ${point.y}`;
    }
    const threshold = Math.min(getDistance(prev, point), getDistance(next, point));
    const isTooCloseForRadius = threshold / 2 < radius;
    const radiusForPoint = isTooCloseForRadius ? threshold / 2 : radius;
    const before = moveTo(prev, point, radiusForPoint);
    const after = moveTo(next, point, radiusForPoint);
    return `L${before.x} ${before.y}S${point.x} ${point.y} ${after.x} ${after.y}`;
  }).join("") + (fill ? `L${end.x} ${height - start.x + 2} Z` : "");
}
var VSparkline = mixins(Colorable).extend({
  name: "VSparkline",
  inheritAttrs: false,
  props: {
    autoDraw: Boolean,
    autoDrawDuration: {
      type: Number,
      default: 2e3
    },
    autoDrawEasing: {
      type: String,
      default: "ease"
    },
    autoLineWidth: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: "primary"
    },
    fill: {
      type: Boolean,
      default: false
    },
    gradient: {
      type: Array,
      default: () => []
    },
    gradientDirection: {
      type: String,
      validator: (val) => ["top", "bottom", "left", "right"].includes(val),
      default: "top"
    },
    height: {
      type: [String, Number],
      default: 75
    },
    labels: {
      type: Array,
      default: () => []
    },
    labelSize: {
      type: [Number, String],
      default: 7
    },
    lineWidth: {
      type: [String, Number],
      default: 4
    },
    padding: {
      type: [String, Number],
      default: 8
    },
    showLabels: Boolean,
    smooth: {
      type: [Boolean, Number, String],
      default: false
    },
    type: {
      type: String,
      default: "trend",
      validator: (val) => ["trend", "bar"].includes(val)
    },
    value: {
      type: Array,
      default: () => []
    },
    width: {
      type: [Number, String],
      default: 300
    }
  },
  data: () => ({
    lastLength: 0
  }),
  computed: {
    parsedPadding() {
      return Number(this.padding);
    },
    parsedWidth() {
      return Number(this.width);
    },
    parsedHeight() {
      return parseInt(this.height, 10);
    },
    parsedLabelSize() {
      return parseInt(this.labelSize, 10) || 7;
    },
    totalHeight() {
      let height = this.parsedHeight;
      if (this.hasLabels)
        height += parseInt(this.labelSize, 10) * 1.5;
      return height;
    },
    totalWidth() {
      let width = this.parsedWidth;
      if (this.type === "bar")
        width = Math.max(this.value.length * this._lineWidth, width);
      return width;
    },
    totalValues() {
      return this.value.length;
    },
    _lineWidth() {
      if (this.autoLineWidth && this.type !== "trend") {
        const totalPadding = this.parsedPadding * (this.totalValues + 1);
        return (this.parsedWidth - totalPadding) / this.totalValues;
      } else {
        return parseFloat(this.lineWidth) || 4;
      }
    },
    boundary() {
      if (this.type === "bar")
        return {
          minX: 0,
          maxX: this.totalWidth,
          minY: 0,
          maxY: this.parsedHeight
        };
      const padding = this.parsedPadding;
      return {
        minX: padding,
        maxX: this.totalWidth - padding,
        minY: padding,
        maxY: this.parsedHeight - padding
      };
    },
    hasLabels() {
      return Boolean(this.showLabels || this.labels.length > 0 || this.$scopedSlots.label);
    },
    parsedLabels() {
      const labels = [];
      const points = this._values;
      const len = points.length;
      for (let i = 0; labels.length < len; i++) {
        const item = points[i];
        let value = this.labels[i];
        if (!value) {
          value = typeof item === "object" ? item.value : item;
        }
        labels.push({
          x: item.x,
          value: String(value)
        });
      }
      return labels;
    },
    normalizedValues() {
      return this.value.map((item) => typeof item === "number" ? item : item.value);
    },
    _values() {
      return this.type === "trend" ? genPoints(this.normalizedValues, this.boundary) : genBars(this.normalizedValues, this.boundary);
    },
    textY() {
      let y = this.parsedHeight;
      if (this.type === "trend")
        y -= 4;
      return y;
    },
    _radius() {
      return this.smooth === true ? 8 : Number(this.smooth);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          if (!this.autoDraw || this.type === "bar" || !this.$refs.path)
            return;
          const path = this.$refs.path;
          const length = path.getTotalLength();
          if (!this.fill) {
            path.style.transition = "none";
            path.style.strokeDasharray = length + " " + length;
            path.style.strokeDashoffset = Math.abs(length - (this.lastLength || 0)).toString();
            path.getBoundingClientRect();
            path.style.transition = `stroke-dashoffset ${this.autoDrawDuration}ms ${this.autoDrawEasing}`;
            path.style.strokeDashoffset = "0";
          } else {
            path.style.transformOrigin = "bottom center";
            path.style.transition = "none";
            path.style.transform = `scaleY(0)`;
            path.getBoundingClientRect();
            path.style.transition = `transform ${this.autoDrawDuration}ms ${this.autoDrawEasing}`;
            path.style.transform = `scaleY(1)`;
          }
          this.lastLength = length;
        });
      }
    }
  },
  methods: {
    genGradient() {
      const gradientDirection = this.gradientDirection;
      const gradient = this.gradient.slice();
      if (!gradient.length)
        gradient.push("");
      const len = Math.max(gradient.length - 1, 1);
      const stops = gradient.reverse().map((color, index) => this.$createElement("stop", {
        attrs: {
          offset: index / len,
          "stop-color": color || "currentColor"
        }
      }));
      return this.$createElement("defs", [this.$createElement("linearGradient", {
        attrs: {
          id: this._uid,
          gradientUnits: "userSpaceOnUse",
          x1: gradientDirection === "left" ? "100%" : "0",
          y1: gradientDirection === "top" ? "100%" : "0",
          x2: gradientDirection === "right" ? "100%" : "0",
          y2: gradientDirection === "bottom" ? "100%" : "0"
        }
      }, stops)]);
    },
    genG(children) {
      return this.$createElement("g", {
        style: {
          fontSize: "8",
          textAnchor: "middle",
          dominantBaseline: "mathematical",
          fill: "currentColor"
        }
      }, children);
    },
    genPath() {
      const points = genPoints(this.normalizedValues, this.boundary);
      return this.$createElement("path", {
        attrs: {
          d: genPath(points, this._radius, this.fill, this.parsedHeight),
          fill: this.fill ? `url(#${this._uid})` : "none",
          stroke: this.fill ? "none" : `url(#${this._uid})`
        },
        ref: "path"
      });
    },
    genLabels(offsetX) {
      const children = this.parsedLabels.map((item, i) => this.$createElement("text", {
        attrs: {
          x: item.x + offsetX + this._lineWidth / 2,
          y: this.textY + this.parsedLabelSize * 0.75,
          "font-size": Number(this.labelSize) || 7
        }
      }, [this.genLabel(item, i)]));
      return this.genG(children);
    },
    genLabel(item, index) {
      return this.$scopedSlots.label ? this.$scopedSlots.label({
        index,
        value: item.value
      }) : item.value;
    },
    genBars() {
      if (!this.value || this.totalValues < 2)
        return void 0;
      const bars = genBars(this.normalizedValues, this.boundary);
      const offsetX = (Math.abs(bars[0].x - bars[1].x) - this._lineWidth) / 2;
      return this.$createElement("svg", {
        attrs: {
          display: "block",
          viewBox: `0 0 ${this.totalWidth} ${this.totalHeight}`
        }
      }, [this.genGradient(), this.genClipPath(bars, offsetX, this._lineWidth, "sparkline-bar-" + this._uid), this.hasLabels ? this.genLabels(offsetX) : void 0, this.$createElement("g", {
        attrs: {
          "clip-path": `url(#sparkline-bar-${this._uid}-clip)`,
          fill: `url(#${this._uid})`
        }
      }, [this.$createElement("rect", {
        attrs: {
          x: 0,
          y: 0,
          width: this.totalWidth,
          height: this.height
        }
      })])]);
    },
    genClipPath(bars, offsetX, lineWidth, id) {
      const rounding = typeof this.smooth === "number" ? this.smooth : this.smooth ? 2 : 0;
      return this.$createElement("clipPath", {
        attrs: {
          id: `${id}-clip`
        }
      }, bars.map((item) => {
        return this.$createElement("rect", {
          attrs: {
            x: item.x + offsetX,
            y: item.y,
            width: lineWidth,
            height: item.height,
            rx: rounding,
            ry: rounding
          }
        }, [this.autoDraw ? this.$createElement("animate", {
          attrs: {
            attributeName: "height",
            from: 0,
            to: item.height,
            dur: `${this.autoDrawDuration}ms`,
            fill: "freeze"
          }
        }) : void 0]);
      }));
    },
    genTrend() {
      return this.$createElement("svg", this.setTextColor(this.color, {
        attrs: __spreadProps(__spreadValues({}, this.$attrs), {
          display: "block",
          "stroke-width": this._lineWidth || 1,
          viewBox: `0 0 ${this.width} ${this.totalHeight}`
        })
      }), [this.genGradient(), this.hasLabels && this.genLabels(-(this._lineWidth / 2)), this.genPath()]);
    }
  },
  render(h) {
    if (this.totalValues < 2)
      return void 0;
    return this.type === "trend" ? this.genTrend() : this.genBars();
  }
});
var VSpeedDial = mixins(Positionable, Toggleable$1, Transitionable).extend({
  name: "v-speed-dial",
  directives: {
    ClickOutside: ClickOutside$1
  },
  props: {
    direction: {
      type: String,
      default: "top",
      validator: (val) => {
        return ["top", "right", "bottom", "left"].includes(val);
      }
    },
    openOnHover: Boolean,
    transition: {
      type: String,
      default: "scale-transition"
    }
  },
  computed: {
    classes() {
      return {
        "v-speed-dial": true,
        "v-speed-dial--top": this.top,
        "v-speed-dial--right": this.right,
        "v-speed-dial--bottom": this.bottom,
        "v-speed-dial--left": this.left,
        "v-speed-dial--absolute": this.absolute,
        "v-speed-dial--fixed": this.fixed,
        [`v-speed-dial--direction-${this.direction}`]: true,
        "v-speed-dial--is-active": this.isActive
      };
    }
  },
  render(h) {
    let children = [];
    const data = {
      class: this.classes,
      directives: [{
        name: "click-outside",
        value: () => this.isActive = false
      }],
      on: {
        click: () => this.isActive = !this.isActive
      }
    };
    if (this.openOnHover) {
      data.on.mouseenter = () => this.isActive = true;
      data.on.mouseleave = () => this.isActive = false;
    }
    if (this.isActive) {
      let btnCount = 0;
      children = (this.$slots.default || []).map((b, i) => {
        if (b.tag && typeof b.componentOptions !== "undefined" && (b.componentOptions.Ctor.options.name === "v-btn" || b.componentOptions.Ctor.options.name === "v-tooltip")) {
          btnCount++;
          return h("div", {
            style: {
              transitionDelay: btnCount * 0.05 + "s"
            },
            key: i
          }, [b]);
        } else {
          b.key = i;
          return b;
        }
      });
    }
    const list = h("transition-group", {
      class: "v-speed-dial__list",
      props: {
        name: this.transition,
        mode: this.mode,
        origin: this.origin,
        tag: "div"
      }
    }, children);
    return h("div", data, [this.$slots.activator, list]);
  }
});
const baseMixins$7 = mixins(provide("stepper"), Proxyable$1, Themeable$1);
var VStepper = baseMixins$7.extend({
  name: "v-stepper",
  provide() {
    return {
      stepClick: this.stepClick,
      isVertical: this.vertical
    };
  },
  props: {
    altLabels: Boolean,
    nonLinear: Boolean,
    vertical: Boolean
  },
  data() {
    const data = {
      isBooted: false,
      steps: [],
      content: [],
      isReverse: false
    };
    data.internalLazyValue = this.value != null ? this.value : (data[0] || {}).step || 1;
    return data;
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-stepper--is-booted": this.isBooted,
        "v-stepper--vertical": this.vertical,
        "v-stepper--alt-labels": this.altLabels,
        "v-stepper--non-linear": this.nonLinear
      }, this.themeClasses);
    }
  },
  watch: {
    internalValue(val, oldVal) {
      this.isReverse = Number(val) < Number(oldVal);
      oldVal && (this.isBooted = true);
      this.updateView();
    }
  },
  created() {
    if (this.$listeners.input) {
      breaking("@input", "@change", this);
    }
  },
  mounted() {
    this.updateView();
  },
  methods: {
    register(item) {
      if (item.$options.name === "v-stepper-step") {
        this.steps.push(item);
      } else if (item.$options.name === "v-stepper-content") {
        item.isVertical = this.vertical;
        this.content.push(item);
      }
    },
    unregister(item) {
      if (item.$options.name === "v-stepper-step") {
        this.steps = this.steps.filter((i) => i !== item);
      } else if (item.$options.name === "v-stepper-content") {
        item.isVertical = this.vertical;
        this.content = this.content.filter((i) => i !== item);
      }
    },
    stepClick(step) {
      this.$nextTick(() => this.internalValue = step);
    },
    updateView() {
      for (let index = this.steps.length; --index >= 0; ) {
        this.steps[index].toggle(this.internalValue);
      }
      for (let index = this.content.length; --index >= 0; ) {
        this.content[index].toggle(this.internalValue, this.isReverse);
      }
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-stepper",
      class: this.classes
    }, this.$slots.default);
  }
});
const baseMixins$6 = mixins(Colorable, inject("stepper", "v-stepper-step", "v-stepper"));
var VStepperStep = baseMixins$6.extend().extend({
  name: "v-stepper-step",
  directives: {
    ripple
  },
  inject: ["stepClick"],
  props: {
    color: {
      type: String,
      default: "primary"
    },
    complete: Boolean,
    completeIcon: {
      type: String,
      default: "$complete"
    },
    editable: Boolean,
    editIcon: {
      type: String,
      default: "$edit"
    },
    errorIcon: {
      type: String,
      default: "$error"
    },
    rules: {
      type: Array,
      default: () => []
    },
    step: [Number, String]
  },
  data() {
    return {
      isActive: false,
      isInactive: true
    };
  },
  computed: {
    classes() {
      return {
        "v-stepper__step--active": this.isActive,
        "v-stepper__step--editable": this.editable,
        "v-stepper__step--inactive": this.isInactive,
        "v-stepper__step--error error--text": this.hasError,
        "v-stepper__step--complete": this.complete
      };
    },
    hasError() {
      return this.rules.some((validate) => validate() !== true);
    }
  },
  mounted() {
    this.stepper && this.stepper.register(this);
  },
  beforeDestroy() {
    this.stepper && this.stepper.unregister(this);
  },
  methods: {
    click(e) {
      e.stopPropagation();
      this.$emit("click", e);
      if (this.editable) {
        this.stepClick(this.step);
      }
    },
    genIcon(icon) {
      return this.$createElement(VIcon$1, icon);
    },
    genLabel() {
      return this.$createElement("div", {
        staticClass: "v-stepper__label"
      }, this.$slots.default);
    },
    genStep() {
      const color = !this.hasError && (this.complete || this.isActive) ? this.color : false;
      return this.$createElement("span", this.setBackgroundColor(color, {
        staticClass: "v-stepper__step__step"
      }), this.genStepContent());
    },
    genStepContent() {
      const children = [];
      if (this.hasError) {
        children.push(this.genIcon(this.errorIcon));
      } else if (this.complete) {
        if (this.editable) {
          children.push(this.genIcon(this.editIcon));
        } else {
          children.push(this.genIcon(this.completeIcon));
        }
      } else {
        children.push(String(this.step));
      }
      return children;
    },
    toggle(step) {
      this.isActive = step.toString() === this.step.toString();
      this.isInactive = Number(step) < Number(this.step);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-stepper__step",
      class: this.classes,
      directives: [{
        name: "ripple",
        value: this.editable
      }],
      on: {
        click: this.click
      }
    }, [this.genStep(), this.genLabel()]);
  }
});
const baseMixins$5 = mixins(inject("stepper", "v-stepper-content", "v-stepper"));
var VStepperContent = baseMixins$5.extend().extend({
  name: "v-stepper-content",
  inject: {
    isVerticalProvided: {
      from: "isVertical"
    }
  },
  props: {
    step: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      height: 0,
      isActive: null,
      isReverse: false,
      isVertical: this.isVerticalProvided
    };
  },
  computed: {
    computedTransition() {
      const reverse = this.$vuetify.rtl ? !this.isReverse : this.isReverse;
      return reverse ? VTabReverseTransition : VTabTransition;
    },
    styles() {
      if (!this.isVertical)
        return {};
      return {
        height: convertToUnit(this.height)
      };
    }
  },
  watch: {
    isActive(current, previous) {
      if (current && previous == null) {
        this.height = "auto";
        return;
      }
      if (!this.isVertical)
        return;
      if (this.isActive)
        this.enter();
      else
        this.leave();
    }
  },
  mounted() {
    this.$refs.wrapper.addEventListener("transitionend", this.onTransition, false);
    this.stepper && this.stepper.register(this);
  },
  beforeDestroy() {
    this.$refs.wrapper.removeEventListener("transitionend", this.onTransition, false);
    this.stepper && this.stepper.unregister(this);
  },
  methods: {
    onTransition(e) {
      if (!this.isActive || e.propertyName !== "height")
        return;
      this.height = "auto";
    },
    enter() {
      let scrollHeight = 0;
      requestAnimationFrame(() => {
        scrollHeight = this.$refs.wrapper.scrollHeight;
      });
      this.height = 0;
      setTimeout(() => this.isActive && (this.height = scrollHeight || "auto"), 450);
    },
    leave() {
      this.height = this.$refs.wrapper.clientHeight;
      setTimeout(() => this.height = 0, 10);
    },
    toggle(step, reverse) {
      this.isActive = step.toString() === this.step.toString();
      this.isReverse = reverse;
    }
  },
  render(h) {
    const contentData = {
      staticClass: "v-stepper__content"
    };
    const wrapperData = {
      staticClass: "v-stepper__wrapper",
      style: this.styles,
      ref: "wrapper"
    };
    if (!this.isVertical) {
      contentData.directives = [{
        name: "show",
        value: this.isActive
      }];
    }
    const wrapper = h("div", wrapperData, [this.$slots.default]);
    const content = h("div", contentData, [wrapper]);
    return h(this.computedTransition, {
      on: this.$listeners
    }, [content]);
  }
});
const VStepperHeader = createSimpleFunctional("v-stepper__header");
const VStepperItems = createSimpleFunctional("v-stepper__items");
var VSwitch = Selectable.extend({
  name: "v-switch",
  directives: {
    Touch: Touch$1
  },
  props: {
    inset: Boolean,
    loading: {
      type: [Boolean, String],
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VInput.options.computed.classes.call(this)), {
        "v-input--selection-controls v-input--switch": true,
        "v-input--switch--flat": this.flat,
        "v-input--switch--inset": this.inset
      });
    },
    attrs() {
      return {
        "aria-checked": String(this.isActive),
        "aria-disabled": String(this.isDisabled),
        role: "switch"
      };
    },
    validationState() {
      if (this.hasError && this.shouldValidate)
        return "error";
      if (this.hasSuccess)
        return "success";
      if (this.hasColor !== null)
        return this.computedColor;
      return void 0;
    },
    switchData() {
      return this.setTextColor(this.loading ? void 0 : this.validationState, {
        class: this.themeClasses
      });
    }
  },
  methods: {
    genDefaultSlot() {
      return [this.genSwitch(), this.genLabel()];
    },
    genSwitch() {
      return this.$createElement("div", {
        staticClass: "v-input--selection-controls__input"
      }, [this.genInput("checkbox", __spreadValues(__spreadValues({}, this.attrs), this.attrs$)), this.genRipple(this.setTextColor(this.validationState, {
        directives: [{
          name: "touch",
          value: {
            left: this.onSwipeLeft,
            right: this.onSwipeRight
          }
        }]
      })), this.$createElement("div", __spreadValues({
        staticClass: "v-input--switch__track"
      }, this.switchData)), this.$createElement("div", __spreadValues({
        staticClass: "v-input--switch__thumb"
      }, this.switchData), [this.genProgress()])]);
    },
    genProgress() {
      return this.$createElement(VFabTransition, {}, [this.loading === false ? null : this.$slots.progress || this.$createElement(VProgressCircular, {
        props: {
          color: this.loading === true || this.loading === "" ? this.color || "primary" : this.loading,
          size: 16,
          width: 2,
          indeterminate: true
        }
      })]);
    },
    onSwipeLeft() {
      if (this.isActive)
        this.onChange();
    },
    onSwipeRight() {
      if (!this.isActive)
        this.onChange();
    },
    onKeydown(e) {
      if (e.keyCode === keyCodes.left && this.isActive || e.keyCode === keyCodes.right && !this.isActive)
        this.onChange();
    }
  }
});
var VSystemBar = mixins(applicationable("bar", ["height", "window"]), Colorable, Themeable$1).extend({
  name: "v-system-bar",
  props: {
    height: [Number, String],
    lightsOut: Boolean,
    window: Boolean
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-system-bar--lights-out": this.lightsOut,
        "v-system-bar--absolute": this.absolute,
        "v-system-bar--fixed": !this.absolute && (this.app || this.fixed),
        "v-system-bar--window": this.window
      }, this.themeClasses);
    },
    computedHeight() {
      if (this.height) {
        return isNaN(parseInt(this.height)) ? this.height : parseInt(this.height);
      }
      return this.window ? 32 : 24;
    },
    styles() {
      return {
        height: convertToUnit(this.computedHeight)
      };
    }
  },
  methods: {
    updateApplication() {
      return this.$el ? this.$el.clientHeight : this.computedHeight;
    }
  },
  render(h) {
    const data = {
      staticClass: "v-system-bar",
      class: this.classes,
      style: this.styles,
      on: this.$listeners
    };
    return h("div", this.setBackgroundColor(this.color, data), getSlot(this));
  }
});
var VTabsBar = mixins(BaseSlideGroup, SSRBootable, Themeable$1).extend({
  name: "v-tabs-bar",
  provide() {
    return {
      tabsBar: this
    };
  },
  computed: {
    classes() {
      return __spreadValues(__spreadProps(__spreadValues({}, BaseSlideGroup.options.computed.classes.call(this)), {
        "v-tabs-bar": true,
        "v-tabs-bar--is-mobile": this.isMobile,
        "v-tabs-bar--show-arrows": this.showArrows
      }), this.themeClasses);
    }
  },
  watch: {
    items: "callSlider",
    internalValue: "callSlider",
    $route: "onRouteChange"
  },
  methods: {
    callSlider() {
      if (!this.isBooted)
        return;
      this.$emit("call:slider");
    },
    genContent() {
      const render = BaseSlideGroup.options.methods.genContent.call(this);
      render.data = render.data || {};
      render.data.staticClass += " v-tabs-bar__content";
      return render;
    },
    onRouteChange(val, oldVal) {
      if (this.mandatory)
        return;
      const items = this.items;
      const newPath = val.path;
      const oldPath = oldVal.path;
      let hasNew = false;
      let hasOld = false;
      for (const item of items) {
        if (item.to === newPath)
          hasNew = true;
        else if (item.to === oldPath)
          hasOld = true;
        if (hasNew && hasOld)
          break;
      }
      if (!hasNew && hasOld)
        this.internalValue = void 0;
    }
  },
  render(h) {
    const render = BaseSlideGroup.options.render.call(this, h);
    render.data.attrs = {
      role: "tablist"
    };
    return render;
  }
});
var VTabsItems = VWindow.extend({
  name: "v-tabs-items",
  props: {
    mandatory: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      return __spreadProps(__spreadValues({}, VWindow.options.computed.classes.call(this)), {
        "v-tabs-items": true
      });
    },
    isDark() {
      return this.rootIsDark;
    }
  },
  methods: {
    getValue(item, i) {
      return item.id || BaseItemGroup.options.methods.getValue.call(this, item, i);
    }
  }
});
var VTabsSlider = mixins(Colorable).extend({
  name: "v-tabs-slider",
  render(h) {
    return h("div", this.setBackgroundColor(this.color, {
      staticClass: "v-tabs-slider"
    }));
  }
});
const baseMixins$4 = mixins(Colorable, Proxyable$1, Themeable$1);
var VTabs = baseMixins$4.extend().extend({
  name: "v-tabs",
  directives: {
    Resize: Resize$1
  },
  props: {
    activeClass: {
      type: String,
      default: ""
    },
    alignWithTitle: Boolean,
    backgroundColor: String,
    centerActive: Boolean,
    centered: Boolean,
    fixedTabs: Boolean,
    grow: Boolean,
    height: {
      type: [Number, String],
      default: void 0
    },
    hideSlider: Boolean,
    iconsAndText: Boolean,
    mobileBreakpoint: [String, Number],
    nextIcon: {
      type: String,
      default: "$next"
    },
    optional: Boolean,
    prevIcon: {
      type: String,
      default: "$prev"
    },
    right: Boolean,
    showArrows: [Boolean, String],
    sliderColor: String,
    sliderSize: {
      type: [Number, String],
      default: 2
    },
    vertical: Boolean
  },
  data() {
    return {
      resizeTimeout: 0,
      slider: {
        height: null,
        left: null,
        right: null,
        top: null,
        width: null
      },
      transitionTime: 300
    };
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-tabs--align-with-title": this.alignWithTitle,
        "v-tabs--centered": this.centered,
        "v-tabs--fixed-tabs": this.fixedTabs,
        "v-tabs--grow": this.grow,
        "v-tabs--icons-and-text": this.iconsAndText,
        "v-tabs--right": this.right,
        "v-tabs--vertical": this.vertical
      }, this.themeClasses);
    },
    isReversed() {
      return this.$vuetify.rtl && this.vertical;
    },
    sliderStyles() {
      return {
        height: convertToUnit(this.slider.height),
        left: this.isReversed ? void 0 : convertToUnit(this.slider.left),
        right: this.isReversed ? convertToUnit(this.slider.right) : void 0,
        top: this.vertical ? convertToUnit(this.slider.top) : void 0,
        transition: this.slider.left != null ? null : "none",
        width: convertToUnit(this.slider.width)
      };
    },
    computedColor() {
      if (this.color)
        return this.color;
      else if (this.isDark && !this.appIsDark)
        return "white";
      else
        return "primary";
    }
  },
  watch: {
    alignWithTitle: "callSlider",
    centered: "callSlider",
    centerActive: "callSlider",
    fixedTabs: "callSlider",
    grow: "callSlider",
    iconsAndText: "callSlider",
    right: "callSlider",
    showArrows: "callSlider",
    vertical: "callSlider",
    "$vuetify.application.left": "onResize",
    "$vuetify.application.right": "onResize",
    "$vuetify.rtl": "onResize"
  },
  mounted() {
    this.$nextTick(() => {
      window.setTimeout(this.callSlider, 30);
    });
  },
  methods: {
    callSlider() {
      if (this.hideSlider || !this.$refs.items || !this.$refs.items.selectedItems.length) {
        this.slider.width = 0;
        return false;
      }
      this.$nextTick(() => {
        const activeTab = this.$refs.items.selectedItems[0];
        if (!activeTab || !activeTab.$el) {
          this.slider.width = 0;
          this.slider.left = 0;
          return;
        }
        const el = activeTab.$el;
        this.slider = {
          height: !this.vertical ? Number(this.sliderSize) : el.scrollHeight,
          left: this.vertical ? 0 : el.offsetLeft,
          right: this.vertical ? 0 : el.offsetLeft + el.offsetWidth,
          top: el.offsetTop,
          width: this.vertical ? Number(this.sliderSize) : el.scrollWidth
        };
      });
      return true;
    },
    genBar(items, slider) {
      const data = {
        style: {
          height: convertToUnit(this.height)
        },
        props: {
          activeClass: this.activeClass,
          centerActive: this.centerActive,
          dark: this.dark,
          light: this.light,
          mandatory: !this.optional,
          mobileBreakpoint: this.mobileBreakpoint,
          nextIcon: this.nextIcon,
          prevIcon: this.prevIcon,
          showArrows: this.showArrows,
          value: this.internalValue
        },
        on: {
          "call:slider": this.callSlider,
          change: (val) => {
            this.internalValue = val;
          }
        },
        ref: "items"
      };
      this.setTextColor(this.computedColor, data);
      this.setBackgroundColor(this.backgroundColor, data);
      return this.$createElement(VTabsBar, data, [this.genSlider(slider), items]);
    },
    genItems(items, item) {
      if (items)
        return items;
      if (!item.length)
        return null;
      return this.$createElement(VTabsItems, {
        props: {
          value: this.internalValue
        },
        on: {
          change: (val) => {
            this.internalValue = val;
          }
        }
      }, item);
    },
    genSlider(slider) {
      if (this.hideSlider)
        return null;
      if (!slider) {
        slider = this.$createElement(VTabsSlider, {
          props: {
            color: this.sliderColor
          }
        });
      }
      return this.$createElement("div", {
        staticClass: "v-tabs-slider-wrapper",
        style: this.sliderStyles
      }, [slider]);
    },
    onResize() {
      if (this._isDestroyed)
        return;
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(this.callSlider, 0);
    },
    parseNodes() {
      let items = null;
      let slider = null;
      const item = [];
      const tab = [];
      const slot = this.$slots.default || [];
      const length = slot.length;
      for (let i = 0; i < length; i++) {
        const vnode = slot[i];
        if (vnode.componentOptions) {
          switch (vnode.componentOptions.Ctor.options.name) {
            case "v-tabs-slider":
              slider = vnode;
              break;
            case "v-tabs-items":
              items = vnode;
              break;
            case "v-tab-item":
              item.push(vnode);
              break;
            default:
              tab.push(vnode);
          }
        } else {
          tab.push(vnode);
        }
      }
      return {
        tab,
        slider,
        items,
        item
      };
    }
  },
  render(h) {
    const {
      tab,
      slider,
      items,
      item
    } = this.parseNodes();
    return h("div", {
      staticClass: "v-tabs",
      class: this.classes,
      directives: [{
        name: "resize",
        modifiers: {
          quiet: true
        },
        value: this.onResize
      }]
    }, [this.genBar(tab, slider), this.genItems(items, item)]);
  }
});
const baseMixins$3 = mixins(Routable, factory$1("tabsBar"), Themeable$1);
var VTab = baseMixins$3.extend().extend().extend({
  name: "v-tab",
  props: {
    ripple: {
      type: [Boolean, Object],
      default: true
    }
  },
  data: () => ({
    proxyClass: "v-tab--active"
  }),
  computed: {
    classes() {
      return __spreadValues(__spreadProps(__spreadValues({
        "v-tab": true
      }, Routable.options.computed.classes.call(this)), {
        "v-tab--disabled": this.disabled
      }), this.groupClasses);
    },
    value() {
      let to = this.to || this.href || "";
      if (this.$router && this.to === Object(this.to)) {
        const resolve = this.$router.resolve(this.to, this.$route, this.append);
        to = resolve.href;
      }
      return to.replace("#", "");
    }
  },
  mounted() {
    this.onRouteChange();
  },
  methods: {
    click(e) {
      if (this.href && this.href.indexOf("#") > -1)
        e.preventDefault();
      if (e.detail)
        this.$el.blur();
      this.$emit("click", e);
      this.to || this.toggle();
    }
  },
  render(h) {
    const {
      tag,
      data
    } = this.generateRouteLink();
    data.attrs = __spreadProps(__spreadValues({}, data.attrs), {
      "aria-selected": String(this.isActive),
      role: "tab",
      tabindex: 0
    });
    data.on = __spreadProps(__spreadValues({}, data.on), {
      keydown: (e) => {
        if (e.keyCode === keyCodes.enter)
          this.click(e);
        this.$emit("keydown", e);
      }
    });
    return h(tag, data, this.$slots.default);
  }
});
var VTabItem = VWindowItem.extend({
  name: "v-tab-item",
  props: {
    id: String
  },
  methods: {
    genWindowItem() {
      const item = VWindowItem.options.methods.genWindowItem.call(this);
      item.data.domProps = item.data.domProps || {};
      item.data.domProps.id = this.id || this.value;
      return item;
    }
  }
});
const baseMixins$2 = mixins(VTextField);
var VTextarea = baseMixins$2.extend({
  name: "v-textarea",
  props: {
    autoGrow: Boolean,
    noResize: Boolean,
    rowHeight: {
      type: [Number, String],
      default: 24,
      validator: (v) => !isNaN(parseFloat(v))
    },
    rows: {
      type: [Number, String],
      default: 5,
      validator: (v) => !isNaN(parseInt(v, 10))
    }
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-textarea": true,
        "v-textarea--auto-grow": this.autoGrow,
        "v-textarea--no-resize": this.noResizeHandle
      }, VTextField.options.computed.classes.call(this));
    },
    noResizeHandle() {
      return this.noResize || this.autoGrow;
    }
  },
  watch: {
    lazyValue() {
      this.autoGrow && this.$nextTick(this.calculateInputHeight);
    },
    rowHeight() {
      this.autoGrow && this.$nextTick(this.calculateInputHeight);
    }
  },
  mounted() {
    setTimeout(() => {
      this.autoGrow && this.calculateInputHeight();
    }, 0);
  },
  methods: {
    calculateInputHeight() {
      const input = this.$refs.input;
      if (!input)
        return;
      input.style.height = "0";
      const height = input.scrollHeight;
      const minHeight = parseInt(this.rows, 10) * parseFloat(this.rowHeight);
      input.style.height = Math.max(minHeight, height) + "px";
    },
    genInput() {
      const input = VTextField.options.methods.genInput.call(this);
      input.tag = "textarea";
      delete input.data.attrs.type;
      input.data.attrs.rows = this.rows;
      return input;
    },
    onInput(e) {
      VTextField.options.methods.onInput.call(this, e);
      this.autoGrow && this.calculateInputHeight();
    },
    onKeyDown(e) {
      if (this.isFocused && e.keyCode === 13) {
        e.stopPropagation();
      }
      this.$emit("keydown", e);
    }
  }
});
var VTimeline = mixins(Themeable$1).extend({
  name: "v-timeline",
  provide() {
    return {
      timeline: this
    };
  },
  props: {
    alignTop: Boolean,
    dense: Boolean,
    reverse: Boolean
  },
  computed: {
    classes() {
      return __spreadValues({
        "v-timeline--align-top": this.alignTop,
        "v-timeline--dense": this.dense,
        "v-timeline--reverse": this.reverse
      }, this.themeClasses);
    }
  },
  render(h) {
    return h("div", {
      staticClass: "v-timeline",
      class: this.classes
    }, this.$slots.default);
  }
});
const baseMixins$1 = mixins(Colorable, Themeable$1);
var VTimelineItem = baseMixins$1.extend().extend({
  name: "v-timeline-item",
  inject: ["timeline"],
  props: {
    color: {
      type: String,
      default: "primary"
    },
    fillDot: Boolean,
    hideDot: Boolean,
    icon: String,
    iconColor: String,
    large: Boolean,
    left: Boolean,
    right: Boolean,
    small: Boolean
  },
  computed: {
    hasIcon() {
      return !!this.icon || !!this.$slots.icon;
    }
  },
  methods: {
    genBody() {
      return this.$createElement("div", {
        staticClass: "v-timeline-item__body"
      }, this.$slots.default);
    },
    genIcon() {
      if (this.$slots.icon) {
        return this.$slots.icon;
      }
      return this.$createElement(VIcon$1, {
        props: {
          color: this.iconColor,
          dark: !this.theme.isDark,
          small: this.small
        }
      }, this.icon);
    },
    genInnerDot() {
      const data = this.setBackgroundColor(this.color);
      return this.$createElement("div", __spreadValues({
        staticClass: "v-timeline-item__inner-dot"
      }, data), [this.hasIcon && this.genIcon()]);
    },
    genDot() {
      return this.$createElement("div", {
        staticClass: "v-timeline-item__dot",
        class: {
          "v-timeline-item__dot--small": this.small,
          "v-timeline-item__dot--large": this.large
        }
      }, [this.genInnerDot()]);
    },
    genDivider() {
      const children = [];
      if (!this.hideDot)
        children.push(this.genDot());
      return this.$createElement("div", {
        staticClass: "v-timeline-item__divider"
      }, children);
    },
    genOpposite() {
      return this.$createElement("div", {
        staticClass: "v-timeline-item__opposite"
      }, this.$slots.opposite);
    }
  },
  render(h) {
    const children = [this.genBody(), this.genDivider()];
    if (this.$slots.opposite)
      children.push(this.genOpposite());
    return h("div", {
      staticClass: "v-timeline-item",
      class: __spreadValues({
        "v-timeline-item--fill-dot": this.fillDot,
        "v-timeline-item--before": this.timeline.reverse ? this.right : this.left,
        "v-timeline-item--after": this.timeline.reverse ? this.left : this.right
      }, this.themeClasses)
    }, children);
  }
});
var SelectingTimes;
(function(SelectingTimes2) {
  SelectingTimes2[SelectingTimes2["Hour"] = 1] = "Hour";
  SelectingTimes2[SelectingTimes2["Minute"] = 2] = "Minute";
  SelectingTimes2[SelectingTimes2["Second"] = 3] = "Second";
})(SelectingTimes || (SelectingTimes = {}));
var VTimePickerTitle = mixins(PickerButton).extend({
  name: "v-time-picker-title",
  props: {
    ampm: Boolean,
    ampmReadonly: Boolean,
    disabled: Boolean,
    hour: Number,
    minute: Number,
    second: Number,
    period: {
      type: String,
      validator: (period) => period === "am" || period === "pm"
    },
    readonly: Boolean,
    useSeconds: Boolean,
    selecting: Number
  },
  methods: {
    genTime() {
      let hour = this.hour;
      if (this.ampm) {
        hour = hour ? (hour - 1) % 12 + 1 : 12;
      }
      const displayedHour = this.hour == null ? "--" : this.ampm ? String(hour) : pad(hour);
      const displayedMinute = this.minute == null ? "--" : pad(this.minute);
      const titleContent = [this.genPickerButton("selecting", SelectingTimes.Hour, displayedHour, this.disabled), this.$createElement("span", ":"), this.genPickerButton("selecting", SelectingTimes.Minute, displayedMinute, this.disabled)];
      if (this.useSeconds) {
        const displayedSecond = this.second == null ? "--" : pad(this.second);
        titleContent.push(this.$createElement("span", ":"));
        titleContent.push(this.genPickerButton("selecting", SelectingTimes.Second, displayedSecond, this.disabled));
      }
      return this.$createElement("div", {
        class: "v-time-picker-title__time"
      }, titleContent);
    },
    genAmPm() {
      return this.$createElement("div", {
        staticClass: "v-time-picker-title__ampm",
        class: {
          "v-time-picker-title__ampm--readonly": this.ampmReadonly
        }
      }, [!this.ampmReadonly || this.period === "am" ? this.genPickerButton("period", "am", this.$vuetify.lang.t("$vuetify.timePicker.am"), this.disabled || this.readonly) : null, !this.ampmReadonly || this.period === "pm" ? this.genPickerButton("period", "pm", this.$vuetify.lang.t("$vuetify.timePicker.pm"), this.disabled || this.readonly) : null]);
    }
  },
  render(h) {
    const children = [this.genTime()];
    this.ampm && children.push(this.genAmPm());
    return h("div", {
      staticClass: "v-time-picker-title"
    }, children);
  }
});
var VTimePickerClock = mixins(Colorable, Themeable$1).extend({
  name: "v-time-picker-clock",
  props: {
    allowedValues: Function,
    ampm: Boolean,
    disabled: Boolean,
    double: Boolean,
    format: {
      type: Function,
      default: (val) => val
    },
    max: {
      type: Number,
      required: true
    },
    min: {
      type: Number,
      required: true
    },
    scrollable: Boolean,
    readonly: Boolean,
    rotate: {
      type: Number,
      default: 0
    },
    step: {
      type: Number,
      default: 1
    },
    value: Number
  },
  data() {
    return {
      inputValue: this.value,
      isDragging: false,
      valueOnMouseDown: null,
      valueOnMouseUp: null
    };
  },
  computed: {
    count() {
      return this.max - this.min + 1;
    },
    degreesPerUnit() {
      return 360 / this.roundCount;
    },
    degrees() {
      return this.degreesPerUnit * Math.PI / 180;
    },
    displayedValue() {
      return this.value == null ? this.min : this.value;
    },
    innerRadiusScale() {
      return 0.62;
    },
    roundCount() {
      return this.double ? this.count / 2 : this.count;
    }
  },
  watch: {
    value(value) {
      this.inputValue = value;
    }
  },
  methods: {
    wheel(e) {
      e.preventDefault();
      const delta2 = Math.sign(-e.deltaY || 1);
      let value = this.displayedValue;
      do {
        value = value + delta2;
        value = (value - this.min + this.count) % this.count + this.min;
      } while (!this.isAllowed(value) && value !== this.displayedValue);
      if (value !== this.displayedValue) {
        this.update(value);
      }
    },
    isInner(value) {
      return this.double && value - this.min >= this.roundCount;
    },
    handScale(value) {
      return this.isInner(value) ? this.innerRadiusScale : 1;
    },
    isAllowed(value) {
      return !this.allowedValues || this.allowedValues(value);
    },
    genValues() {
      const children = [];
      for (let value = this.min; value <= this.max; value = value + this.step) {
        const color = value === this.value && (this.color || "accent");
        children.push(this.$createElement("span", this.setBackgroundColor(color, {
          staticClass: "v-time-picker-clock__item",
          class: {
            "v-time-picker-clock__item--active": value === this.displayedValue,
            "v-time-picker-clock__item--disabled": this.disabled || !this.isAllowed(value)
          },
          style: this.getTransform(value),
          domProps: {
            innerHTML: `<span>${this.format(value)}</span>`
          }
        })));
      }
      return children;
    },
    genHand() {
      const scale = `scaleY(${this.handScale(this.displayedValue)})`;
      const angle = this.rotate + this.degreesPerUnit * (this.displayedValue - this.min);
      const color = this.value != null && (this.color || "accent");
      return this.$createElement("div", this.setBackgroundColor(color, {
        staticClass: "v-time-picker-clock__hand",
        class: {
          "v-time-picker-clock__hand--inner": this.isInner(this.value)
        },
        style: {
          transform: `rotate(${angle}deg) ${scale}`
        }
      }));
    },
    getTransform(i) {
      const {
        x,
        y
      } = this.getPosition(i);
      return {
        left: `${50 + x * 50}%`,
        top: `${50 + y * 50}%`
      };
    },
    getPosition(value) {
      const rotateRadians = this.rotate * Math.PI / 180;
      return {
        x: Math.sin((value - this.min) * this.degrees + rotateRadians) * this.handScale(value),
        y: -Math.cos((value - this.min) * this.degrees + rotateRadians) * this.handScale(value)
      };
    },
    onMouseDown(e) {
      e.preventDefault();
      this.valueOnMouseDown = null;
      this.valueOnMouseUp = null;
      this.isDragging = true;
      this.onDragMove(e);
    },
    onMouseUp(e) {
      e.stopPropagation();
      this.isDragging = false;
      if (this.valueOnMouseUp !== null && this.isAllowed(this.valueOnMouseUp)) {
        this.$emit("change", this.valueOnMouseUp);
      }
    },
    onDragMove(e) {
      e.preventDefault();
      if (!this.isDragging && e.type !== "click")
        return;
      const {
        width,
        top,
        left
      } = this.$refs.clock.getBoundingClientRect();
      const {
        width: innerWidth
      } = this.$refs.innerClock.getBoundingClientRect();
      const {
        clientX,
        clientY
      } = "touches" in e ? e.touches[0] : e;
      const center = {
        x: width / 2,
        y: -width / 2
      };
      const coords = {
        x: clientX - left,
        y: top - clientY
      };
      const handAngle = Math.round(this.angle(center, coords) - this.rotate + 360) % 360;
      const insideClick = this.double && this.euclidean(center, coords) < (innerWidth + innerWidth * this.innerRadiusScale) / 4;
      const checksCount = Math.ceil(15 / this.degreesPerUnit);
      let value;
      for (let i = 0; i < checksCount; i++) {
        value = this.angleToValue(handAngle + i * this.degreesPerUnit, insideClick);
        if (this.isAllowed(value))
          return this.setMouseDownValue(value);
        value = this.angleToValue(handAngle - i * this.degreesPerUnit, insideClick);
        if (this.isAllowed(value))
          return this.setMouseDownValue(value);
      }
    },
    angleToValue(angle, insideClick) {
      const value = (Math.round(angle / this.degreesPerUnit) + (insideClick ? this.roundCount : 0)) % this.count + this.min;
      if (angle < 360 - this.degreesPerUnit / 2)
        return value;
      return insideClick ? this.max - this.roundCount + 1 : this.min;
    },
    setMouseDownValue(value) {
      if (this.valueOnMouseDown === null) {
        this.valueOnMouseDown = value;
      }
      this.valueOnMouseUp = value;
      this.update(value);
    },
    update(value) {
      if (this.inputValue !== value) {
        this.inputValue = value;
        this.$emit("input", value);
      }
    },
    euclidean(p0, p1) {
      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    angle(center, p1) {
      const value = 2 * Math.atan2(p1.y - center.y - this.euclidean(center, p1), p1.x - center.x);
      return Math.abs(value * 180 / Math.PI);
    }
  },
  render(h) {
    const data = {
      staticClass: "v-time-picker-clock",
      class: __spreadValues({
        "v-time-picker-clock--indeterminate": this.value == null
      }, this.themeClasses),
      on: this.readonly || this.disabled ? void 0 : {
        mousedown: this.onMouseDown,
        mouseup: this.onMouseUp,
        mouseleave: (e) => this.isDragging && this.onMouseUp(e),
        touchstart: this.onMouseDown,
        touchend: this.onMouseUp,
        mousemove: this.onDragMove,
        touchmove: this.onDragMove
      },
      ref: "clock"
    };
    if (this.scrollable && data.on) {
      data.on.wheel = this.wheel;
    }
    return h("div", data, [h("div", {
      staticClass: "v-time-picker-clock__inner",
      ref: "innerClock"
    }, [this.genHand(), this.genValues()])]);
  }
});
const rangeHours24 = createRange(24);
const rangeHours12am = createRange(12);
const rangeHours12pm = rangeHours12am.map((v) => v + 12);
const range60 = createRange(60);
const selectingNames = {
  1: "hour",
  2: "minute",
  3: "second"
};
var VTimePicker = mixins(Picker, PickerButton).extend({
  name: "v-time-picker",
  props: {
    allowedHours: [Function, Array],
    allowedMinutes: [Function, Array],
    allowedSeconds: [Function, Array],
    disabled: Boolean,
    format: {
      type: String,
      default: "ampm",
      validator(val) {
        return ["ampm", "24hr"].includes(val);
      }
    },
    min: String,
    max: String,
    readonly: Boolean,
    scrollable: Boolean,
    useSeconds: Boolean,
    value: null,
    ampmInTitle: Boolean
  },
  data() {
    return {
      inputHour: null,
      inputMinute: null,
      inputSecond: null,
      lazyInputHour: null,
      lazyInputMinute: null,
      lazyInputSecond: null,
      period: "am",
      selecting: SelectingTimes.Hour
    };
  },
  computed: {
    selectingHour: {
      get() {
        return this.selecting === SelectingTimes.Hour;
      },
      set(v) {
        this.selecting = SelectingTimes.Hour;
      }
    },
    selectingMinute: {
      get() {
        return this.selecting === SelectingTimes.Minute;
      },
      set(v) {
        this.selecting = SelectingTimes.Minute;
      }
    },
    selectingSecond: {
      get() {
        return this.selecting === SelectingTimes.Second;
      },
      set(v) {
        this.selecting = SelectingTimes.Second;
      }
    },
    isAllowedHourCb() {
      let cb;
      if (this.allowedHours instanceof Array) {
        cb = (val) => this.allowedHours.includes(val);
      } else {
        cb = this.allowedHours;
      }
      if (!this.min && !this.max)
        return cb;
      const minHour = this.min ? Number(this.min.split(":")[0]) : 0;
      const maxHour = this.max ? Number(this.max.split(":")[0]) : 23;
      return (val) => {
        return val >= minHour * 1 && val <= maxHour * 1 && (!cb || cb(val));
      };
    },
    isAllowedMinuteCb() {
      let cb;
      const isHourAllowed = !this.isAllowedHourCb || this.inputHour === null || this.isAllowedHourCb(this.inputHour);
      if (this.allowedMinutes instanceof Array) {
        cb = (val) => this.allowedMinutes.includes(val);
      } else {
        cb = this.allowedMinutes;
      }
      if (!this.min && !this.max) {
        return isHourAllowed ? cb : () => false;
      }
      const [minHour, minMinute] = this.min ? this.min.split(":").map(Number) : [0, 0];
      const [maxHour, maxMinute] = this.max ? this.max.split(":").map(Number) : [23, 59];
      const minTime = minHour * 60 + minMinute * 1;
      const maxTime = maxHour * 60 + maxMinute * 1;
      return (val) => {
        const time = 60 * this.inputHour + val;
        return time >= minTime && time <= maxTime && isHourAllowed && (!cb || cb(val));
      };
    },
    isAllowedSecondCb() {
      let cb;
      const isHourAllowed = !this.isAllowedHourCb || this.inputHour === null || this.isAllowedHourCb(this.inputHour);
      const isMinuteAllowed = isHourAllowed && (!this.isAllowedMinuteCb || this.inputMinute === null || this.isAllowedMinuteCb(this.inputMinute));
      if (this.allowedSeconds instanceof Array) {
        cb = (val) => this.allowedSeconds.includes(val);
      } else {
        cb = this.allowedSeconds;
      }
      if (!this.min && !this.max) {
        return isMinuteAllowed ? cb : () => false;
      }
      const [minHour, minMinute, minSecond] = this.min ? this.min.split(":").map(Number) : [0, 0, 0];
      const [maxHour, maxMinute, maxSecond] = this.max ? this.max.split(":").map(Number) : [23, 59, 59];
      const minTime = minHour * 3600 + minMinute * 60 + (minSecond || 0) * 1;
      const maxTime = maxHour * 3600 + maxMinute * 60 + (maxSecond || 0) * 1;
      return (val) => {
        const time = 3600 * this.inputHour + 60 * this.inputMinute + val;
        return time >= minTime && time <= maxTime && isMinuteAllowed && (!cb || cb(val));
      };
    },
    isAmPm() {
      return this.format === "ampm";
    }
  },
  watch: {
    value: "setInputData"
  },
  mounted() {
    this.setInputData(this.value);
    this.$on("update:period", this.setPeriod);
  },
  methods: {
    genValue() {
      if (this.inputHour != null && this.inputMinute != null && (!this.useSeconds || this.inputSecond != null)) {
        return `${pad(this.inputHour)}:${pad(this.inputMinute)}` + (this.useSeconds ? `:${pad(this.inputSecond)}` : "");
      }
      return null;
    },
    emitValue() {
      const value = this.genValue();
      if (value !== null)
        this.$emit("input", value);
    },
    setPeriod(period) {
      this.period = period;
      if (this.inputHour != null) {
        const newHour = this.inputHour + (period === "am" ? -12 : 12);
        this.inputHour = this.firstAllowed("hour", newHour);
        this.emitValue();
      }
    },
    setInputData(value) {
      if (value == null || value === "") {
        this.inputHour = null;
        this.inputMinute = null;
        this.inputSecond = null;
      } else if (value instanceof Date) {
        this.inputHour = value.getHours();
        this.inputMinute = value.getMinutes();
        this.inputSecond = value.getSeconds();
      } else {
        const [, hour, minute, , second, period] = value.trim().toLowerCase().match(/^(\d+):(\d+)(:(\d+))?([ap]m)?$/) || new Array(6);
        this.inputHour = period ? this.convert12to24(parseInt(hour, 10), period) : parseInt(hour, 10);
        this.inputMinute = parseInt(minute, 10);
        this.inputSecond = parseInt(second || 0, 10);
      }
      this.period = this.inputHour == null || this.inputHour < 12 ? "am" : "pm";
    },
    convert24to12(hour) {
      return hour ? (hour - 1) % 12 + 1 : 12;
    },
    convert12to24(hour, period) {
      return hour % 12 + (period === "pm" ? 12 : 0);
    },
    onInput(value) {
      if (this.selecting === SelectingTimes.Hour) {
        this.inputHour = this.isAmPm ? this.convert12to24(value, this.period) : value;
      } else if (this.selecting === SelectingTimes.Minute) {
        this.inputMinute = value;
      } else {
        this.inputSecond = value;
      }
      this.emitValue();
    },
    onChange(value) {
      this.$emit(`click:${selectingNames[this.selecting]}`, value);
      const emitChange = this.selecting === (this.useSeconds ? SelectingTimes.Second : SelectingTimes.Minute);
      if (this.selecting === SelectingTimes.Hour) {
        this.selecting = SelectingTimes.Minute;
      } else if (this.useSeconds && this.selecting === SelectingTimes.Minute) {
        this.selecting = SelectingTimes.Second;
      }
      if (this.inputHour === this.lazyInputHour && this.inputMinute === this.lazyInputMinute && (!this.useSeconds || this.inputSecond === this.lazyInputSecond))
        return;
      const time = this.genValue();
      if (time === null)
        return;
      this.lazyInputHour = this.inputHour;
      this.lazyInputMinute = this.inputMinute;
      this.useSeconds && (this.lazyInputSecond = this.inputSecond);
      emitChange && this.$emit("change", time);
    },
    firstAllowed(type2, value) {
      const allowedFn = type2 === "hour" ? this.isAllowedHourCb : type2 === "minute" ? this.isAllowedMinuteCb : this.isAllowedSecondCb;
      if (!allowedFn)
        return value;
      const range = type2 === "minute" ? range60 : type2 === "second" ? range60 : this.isAmPm ? value < 12 ? rangeHours12am : rangeHours12pm : rangeHours24;
      const first = range.find((v) => allowedFn((v + value) % range.length + range[0]));
      return ((first || 0) + value) % range.length + range[0];
    },
    genClock() {
      return this.$createElement(VTimePickerClock, {
        props: {
          allowedValues: this.selecting === SelectingTimes.Hour ? this.isAllowedHourCb : this.selecting === SelectingTimes.Minute ? this.isAllowedMinuteCb : this.isAllowedSecondCb,
          color: this.color,
          dark: this.dark,
          disabled: this.disabled,
          double: this.selecting === SelectingTimes.Hour && !this.isAmPm,
          format: this.selecting === SelectingTimes.Hour ? this.isAmPm ? this.convert24to12 : (val) => val : (val) => pad(val, 2),
          light: this.light,
          max: this.selecting === SelectingTimes.Hour ? this.isAmPm && this.period === "am" ? 11 : 23 : 59,
          min: this.selecting === SelectingTimes.Hour && this.isAmPm && this.period === "pm" ? 12 : 0,
          readonly: this.readonly,
          scrollable: this.scrollable,
          size: Number(this.width) - (!this.fullWidth && this.landscape ? 80 : 20),
          step: this.selecting === SelectingTimes.Hour ? 1 : 5,
          value: this.selecting === SelectingTimes.Hour ? this.inputHour : this.selecting === SelectingTimes.Minute ? this.inputMinute : this.inputSecond
        },
        on: {
          input: this.onInput,
          change: this.onChange
        },
        ref: "clock"
      });
    },
    genClockAmPm() {
      return this.$createElement("div", this.setTextColor(this.color || "primary", {
        staticClass: "v-time-picker-clock__ampm"
      }), [this.genPickerButton("period", "am", this.$vuetify.lang.t("$vuetify.timePicker.am"), this.disabled || this.readonly), this.genPickerButton("period", "pm", this.$vuetify.lang.t("$vuetify.timePicker.pm"), this.disabled || this.readonly)]);
    },
    genPickerBody() {
      return this.$createElement("div", {
        staticClass: "v-time-picker-clock__container",
        key: this.selecting
      }, [!this.ampmInTitle && this.isAmPm && this.genClockAmPm(), this.genClock()]);
    },
    genPickerTitle() {
      return this.$createElement(VTimePickerTitle, {
        props: {
          ampm: this.isAmPm,
          ampmReadonly: this.isAmPm && !this.ampmInTitle,
          disabled: this.disabled,
          hour: this.inputHour,
          minute: this.inputMinute,
          second: this.inputSecond,
          period: this.period,
          readonly: this.readonly,
          useSeconds: this.useSeconds,
          selecting: this.selecting
        },
        on: {
          "update:selecting": (value) => this.selecting = value,
          "update:period": (period) => this.$emit("update:period", period)
        },
        ref: "title",
        slot: "title"
      });
    }
  },
  render() {
    return this.genPicker("v-picker--time");
  }
});
const VToolbarTitle = createSimpleFunctional("v-toolbar__title");
const VToolbarItems = createSimpleFunctional("v-toolbar__items");
var VTooltip = mixins(Colorable, Delayable, Dependent, Detachable, Menuable, Toggleable$1).extend({
  name: "v-tooltip",
  props: {
    closeDelay: {
      type: [Number, String],
      default: 0
    },
    disabled: Boolean,
    fixed: {
      type: Boolean,
      default: true
    },
    openDelay: {
      type: [Number, String],
      default: 0
    },
    openOnHover: {
      type: Boolean,
      default: true
    },
    tag: {
      type: String,
      default: "span"
    },
    transition: String
  },
  data: () => ({
    calculatedMinWidth: 0,
    closeDependents: false
  }),
  computed: {
    calculatedLeft() {
      const {
        activator,
        content
      } = this.dimensions;
      const unknown = !this.bottom && !this.left && !this.top && !this.right;
      const activatorLeft = this.attach !== false ? activator.offsetLeft : activator.left;
      let left = 0;
      if (this.top || this.bottom || unknown) {
        left = activatorLeft + activator.width / 2 - content.width / 2;
      } else if (this.left || this.right) {
        left = activatorLeft + (this.right ? activator.width : -content.width) + (this.right ? 10 : -10);
      }
      if (this.nudgeLeft)
        left -= parseInt(this.nudgeLeft);
      if (this.nudgeRight)
        left += parseInt(this.nudgeRight);
      return `${this.calcXOverflow(left, this.dimensions.content.width)}px`;
    },
    calculatedTop() {
      const {
        activator,
        content
      } = this.dimensions;
      const activatorTop = this.attach !== false ? activator.offsetTop : activator.top;
      let top = 0;
      if (this.top || this.bottom) {
        top = activatorTop + (this.bottom ? activator.height : -content.height) + (this.bottom ? 10 : -10);
      } else if (this.left || this.right) {
        top = activatorTop + activator.height / 2 - content.height / 2;
      }
      if (this.nudgeTop)
        top -= parseInt(this.nudgeTop);
      if (this.nudgeBottom)
        top += parseInt(this.nudgeBottom);
      return `${this.calcYOverflow(top + this.pageYOffset)}px`;
    },
    classes() {
      return {
        "v-tooltip--top": this.top,
        "v-tooltip--right": this.right,
        "v-tooltip--bottom": this.bottom,
        "v-tooltip--left": this.left,
        "v-tooltip--attached": this.attach === "" || this.attach === true || this.attach === "attach"
      };
    },
    computedTransition() {
      if (this.transition)
        return this.transition;
      return this.isActive ? "scale-transition" : "fade-transition";
    },
    offsetY() {
      return this.top || this.bottom;
    },
    offsetX() {
      return this.left || this.right;
    },
    styles() {
      return {
        left: this.calculatedLeft,
        maxWidth: convertToUnit(this.maxWidth),
        minWidth: convertToUnit(this.minWidth),
        opacity: this.isActive ? 0.9 : 0,
        top: this.calculatedTop,
        zIndex: this.zIndex || this.activeZIndex
      };
    }
  },
  beforeMount() {
    this.$nextTick(() => {
      this.value && this.callActivate();
    });
  },
  mounted() {
    if (getSlotType(this, "activator", true) === "v-slot") {
      consoleError(`v-tooltip's activator slot must be bound, try '<template #activator="data"><v-btn v-on="data.on>'`, this);
    }
  },
  methods: {
    activate() {
      this.updateDimensions();
      requestAnimationFrame(this.startTransition);
    },
    deactivate() {
      this.runDelay("close");
    },
    genActivatorListeners() {
      const listeners = Activatable.options.methods.genActivatorListeners.call(this);
      listeners.focus = (e) => {
        this.getActivator(e);
        this.runDelay("open");
      };
      listeners.blur = (e) => {
        this.getActivator(e);
        this.runDelay("close");
      };
      listeners.keydown = (e) => {
        if (e.keyCode === keyCodes.esc) {
          this.getActivator(e);
          this.runDelay("close");
        }
      };
      return listeners;
    },
    genActivatorAttributes() {
      return {
        "aria-haspopup": true,
        "aria-expanded": String(this.isActive)
      };
    },
    genTransition() {
      const content = this.genContent();
      if (!this.computedTransition)
        return content;
      return this.$createElement("transition", {
        props: {
          name: this.computedTransition
        }
      }, [content]);
    },
    genContent() {
      return this.$createElement("div", this.setBackgroundColor(this.color, {
        staticClass: "v-tooltip__content",
        class: {
          [this.contentClass]: true,
          menuable__content__active: this.isActive,
          "v-tooltip__content--fixed": this.activatorFixed
        },
        style: this.styles,
        attrs: this.getScopeIdAttrs(),
        directives: [{
          name: "show",
          value: this.isContentActive
        }],
        ref: "content"
      }), this.getContentSlot());
    }
  },
  render(h) {
    return h(this.tag, {
      staticClass: "v-tooltip",
      class: this.classes
    }, [this.showLazyContent(() => [this.genTransition()]), this.genActivator()]);
  }
});
const baseMixins = mixins(Colorable, inject("treeview"));
const VTreeviewNodeProps = {
  activatable: Boolean,
  activeClass: {
    type: String,
    default: "v-treeview-node--active"
  },
  color: {
    type: String,
    default: "primary"
  },
  expandIcon: {
    type: String,
    default: "$subgroup"
  },
  indeterminateIcon: {
    type: String,
    default: "$checkboxIndeterminate"
  },
  itemChildren: {
    type: String,
    default: "children"
  },
  itemDisabled: {
    type: String,
    default: "disabled"
  },
  itemKey: {
    type: String,
    default: "id"
  },
  itemText: {
    type: String,
    default: "name"
  },
  loadChildren: Function,
  loadingIcon: {
    type: String,
    default: "$loading"
  },
  offIcon: {
    type: String,
    default: "$checkboxOff"
  },
  onIcon: {
    type: String,
    default: "$checkboxOn"
  },
  openOnClick: Boolean,
  rounded: Boolean,
  selectable: Boolean,
  selectedColor: {
    type: String,
    default: "accent"
  },
  shaped: Boolean,
  transition: Boolean,
  selectionType: {
    type: String,
    default: "leaf",
    validator: (v) => ["leaf", "independent"].includes(v)
  }
};
const VTreeviewNode = baseMixins.extend().extend({
  name: "v-treeview-node",
  inject: {
    treeview: {
      default: null
    }
  },
  props: __spreadValues({
    level: Number,
    item: {
      type: Object,
      default: () => null
    },
    parentIsDisabled: Boolean
  }, VTreeviewNodeProps),
  data: () => ({
    hasLoaded: false,
    isActive: false,
    isIndeterminate: false,
    isLoading: false,
    isOpen: false,
    isSelected: false
  }),
  computed: {
    disabled() {
      return getObjectValueByPath(this.item, this.itemDisabled) || this.parentIsDisabled && this.selectionType === "leaf";
    },
    key() {
      return getObjectValueByPath(this.item, this.itemKey);
    },
    children() {
      const children = getObjectValueByPath(this.item, this.itemChildren);
      return children && children.filter((child) => !this.treeview.isExcluded(getObjectValueByPath(child, this.itemKey)));
    },
    text() {
      return getObjectValueByPath(this.item, this.itemText);
    },
    scopedProps() {
      return {
        item: this.item,
        leaf: !this.children,
        selected: this.isSelected,
        indeterminate: this.isIndeterminate,
        active: this.isActive,
        open: this.isOpen
      };
    },
    computedIcon() {
      if (this.isIndeterminate)
        return this.indeterminateIcon;
      else if (this.isSelected)
        return this.onIcon;
      else
        return this.offIcon;
    },
    hasChildren() {
      return !!this.children && (!!this.children.length || !!this.loadChildren);
    }
  },
  created() {
    this.treeview.register(this);
  },
  beforeDestroy() {
    this.treeview.unregister(this);
  },
  methods: {
    checkChildren() {
      return new Promise((resolve) => {
        if (!this.children || this.children.length || !this.loadChildren || this.hasLoaded)
          return resolve();
        this.isLoading = true;
        resolve(this.loadChildren(this.item));
      }).then(() => {
        this.isLoading = false;
        this.hasLoaded = true;
      });
    },
    open() {
      this.isOpen = !this.isOpen;
      this.treeview.updateOpen(this.key, this.isOpen);
      this.treeview.emitOpen();
    },
    genLabel() {
      const children = [];
      if (this.$scopedSlots.label)
        children.push(this.$scopedSlots.label(this.scopedProps));
      else
        children.push(this.text);
      return this.$createElement("div", {
        slot: "label",
        staticClass: "v-treeview-node__label"
      }, children);
    },
    genPrependSlot() {
      if (!this.$scopedSlots.prepend)
        return null;
      return this.$createElement("div", {
        staticClass: "v-treeview-node__prepend"
      }, this.$scopedSlots.prepend(this.scopedProps));
    },
    genAppendSlot() {
      if (!this.$scopedSlots.append)
        return null;
      return this.$createElement("div", {
        staticClass: "v-treeview-node__append"
      }, this.$scopedSlots.append(this.scopedProps));
    },
    genContent() {
      const children = [this.genPrependSlot(), this.genLabel(), this.genAppendSlot()];
      return this.$createElement("div", {
        staticClass: "v-treeview-node__content"
      }, children);
    },
    genToggle() {
      return this.$createElement(VIcon$1, {
        staticClass: "v-treeview-node__toggle",
        class: {
          "v-treeview-node__toggle--open": this.isOpen,
          "v-treeview-node__toggle--loading": this.isLoading
        },
        slot: "prepend",
        on: {
          click: (e) => {
            e.stopPropagation();
            if (this.isLoading)
              return;
            this.checkChildren().then(() => this.open());
          }
        }
      }, [this.isLoading ? this.loadingIcon : this.expandIcon]);
    },
    genCheckbox() {
      return this.$createElement(VIcon$1, {
        staticClass: "v-treeview-node__checkbox",
        props: {
          color: this.isSelected || this.isIndeterminate ? this.selectedColor : void 0,
          disabled: this.disabled
        },
        on: {
          click: (e) => {
            e.stopPropagation();
            if (this.isLoading)
              return;
            this.checkChildren().then(() => {
              this.$nextTick(() => {
                this.isSelected = !this.isSelected;
                this.isIndeterminate = false;
                this.treeview.updateSelected(this.key, this.isSelected);
                this.treeview.emitSelected();
              });
            });
          }
        }
      }, [this.computedIcon]);
    },
    genLevel(level) {
      return createRange(level).map(() => this.$createElement("div", {
        staticClass: "v-treeview-node__level"
      }));
    },
    genNode() {
      const children = [this.genContent()];
      if (this.selectable)
        children.unshift(this.genCheckbox());
      if (this.hasChildren) {
        children.unshift(this.genToggle());
      } else {
        children.unshift(...this.genLevel(1));
      }
      children.unshift(...this.genLevel(this.level));
      return this.$createElement("div", this.setTextColor(this.isActive && this.color, {
        staticClass: "v-treeview-node__root",
        class: {
          [this.activeClass]: this.isActive
        },
        on: {
          click: () => {
            if (this.openOnClick && this.hasChildren) {
              this.checkChildren().then(this.open);
            } else if (this.activatable && !this.disabled) {
              this.isActive = !this.isActive;
              this.treeview.updateActive(this.key, this.isActive);
              this.treeview.emitActive();
            }
          }
        }
      }), children);
    },
    genChild(item, parentIsDisabled) {
      return this.$createElement(VTreeviewNode, {
        key: getObjectValueByPath(item, this.itemKey),
        props: {
          activatable: this.activatable,
          activeClass: this.activeClass,
          item,
          selectable: this.selectable,
          selectedColor: this.selectedColor,
          color: this.color,
          expandIcon: this.expandIcon,
          indeterminateIcon: this.indeterminateIcon,
          offIcon: this.offIcon,
          onIcon: this.onIcon,
          loadingIcon: this.loadingIcon,
          itemKey: this.itemKey,
          itemText: this.itemText,
          itemDisabled: this.itemDisabled,
          itemChildren: this.itemChildren,
          loadChildren: this.loadChildren,
          transition: this.transition,
          openOnClick: this.openOnClick,
          rounded: this.rounded,
          shaped: this.shaped,
          level: this.level + 1,
          selectionType: this.selectionType,
          parentIsDisabled
        },
        scopedSlots: this.$scopedSlots
      });
    },
    genChildrenWrapper() {
      if (!this.isOpen || !this.children)
        return null;
      const children = [this.children.map((c) => this.genChild(c, this.disabled))];
      return this.$createElement("div", {
        staticClass: "v-treeview-node__children"
      }, children);
    },
    genTransition() {
      return this.$createElement(VExpandTransition, [this.genChildrenWrapper()]);
    }
  },
  render(h) {
    const children = [this.genNode()];
    if (this.transition)
      children.push(this.genTransition());
    else
      children.push(this.genChildrenWrapper());
    return h("div", {
      staticClass: "v-treeview-node",
      class: {
        "v-treeview-node--leaf": !this.hasChildren,
        "v-treeview-node--click": this.openOnClick,
        "v-treeview-node--disabled": this.disabled,
        "v-treeview-node--rounded": this.rounded,
        "v-treeview-node--shaped": this.shaped,
        "v-treeview-node--selected": this.isSelected
      },
      attrs: {
        "aria-expanded": String(this.isOpen)
      }
    }, children);
  }
});
var VTreeviewNode$1 = VTreeviewNode;
function filterTreeItem(item, search, textKey) {
  const text = getObjectValueByPath(item, textKey);
  return text.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) > -1;
}
function filterTreeItems(filter, item, search, idKey, textKey, childrenKey, excluded) {
  if (filter(item, search, textKey)) {
    return true;
  }
  const children = getObjectValueByPath(item, childrenKey);
  if (children) {
    let match = false;
    for (let i = 0; i < children.length; i++) {
      if (filterTreeItems(filter, children[i], search, idKey, textKey, childrenKey, excluded)) {
        match = true;
      }
    }
    if (match)
      return true;
  }
  excluded.add(getObjectValueByPath(item, idKey));
  return false;
}
var VTreeview = mixins(provide("treeview"), Themeable$1).extend({
  name: "v-treeview",
  provide() {
    return {
      treeview: this
    };
  },
  props: __spreadValues({
    active: {
      type: Array,
      default: () => []
    },
    dense: Boolean,
    filter: Function,
    hoverable: Boolean,
    items: {
      type: Array,
      default: () => []
    },
    multipleActive: Boolean,
    open: {
      type: Array,
      default: () => []
    },
    openAll: Boolean,
    returnObject: {
      type: Boolean,
      default: false
    },
    search: String,
    value: {
      type: Array,
      default: () => []
    }
  }, VTreeviewNodeProps),
  data: () => ({
    level: -1,
    activeCache: new Set(),
    nodes: {},
    openCache: new Set(),
    selectedCache: new Set()
  }),
  computed: {
    excludedItems() {
      const excluded = new Set();
      if (!this.search)
        return excluded;
      for (let i = 0; i < this.items.length; i++) {
        filterTreeItems(this.filter || filterTreeItem, this.items[i], this.search, this.itemKey, this.itemText, this.itemChildren, excluded);
      }
      return excluded;
    }
  },
  watch: {
    items: {
      handler() {
        const oldKeys = Object.keys(this.nodes).map((k) => getObjectValueByPath(this.nodes[k].item, this.itemKey));
        const newKeys = this.getKeys(this.items);
        const diff = arrayDiff(newKeys, oldKeys);
        if (!diff.length && newKeys.length < oldKeys.length)
          return;
        diff.forEach((k) => delete this.nodes[k]);
        const oldSelectedCache = [...this.selectedCache];
        this.selectedCache = new Set();
        this.activeCache = new Set();
        this.openCache = new Set();
        this.buildTree(this.items);
        if (!deepEqual(oldSelectedCache, [...this.selectedCache]))
          this.emitSelected();
      },
      deep: true
    },
    active(value) {
      this.handleNodeCacheWatcher(value, this.activeCache, this.updateActive, this.emitActive);
    },
    value(value) {
      this.handleNodeCacheWatcher(value, this.selectedCache, this.updateSelected, this.emitSelected);
    },
    open(value) {
      this.handleNodeCacheWatcher(value, this.openCache, this.updateOpen, this.emitOpen);
    }
  },
  created() {
    const getValue = (key) => this.returnObject ? getObjectValueByPath(key, this.itemKey) : key;
    this.buildTree(this.items);
    for (const value of this.value.map(getValue)) {
      this.updateSelected(value, true, true);
    }
    for (const active of this.active.map(getValue)) {
      this.updateActive(active, true);
    }
  },
  mounted() {
    if (this.$slots.prepend || this.$slots.append) {
      consoleWarn("The prepend and append slots require a slot-scope attribute", this);
    }
    if (this.openAll) {
      this.updateAll(true);
    } else {
      this.open.forEach((key) => this.updateOpen(this.returnObject ? getObjectValueByPath(key, this.itemKey) : key, true));
      this.emitOpen();
    }
  },
  methods: {
    updateAll(value) {
      Object.keys(this.nodes).forEach((key) => this.updateOpen(getObjectValueByPath(this.nodes[key].item, this.itemKey), value));
      this.emitOpen();
    },
    getKeys(items, keys2 = []) {
      for (let i = 0; i < items.length; i++) {
        const key = getObjectValueByPath(items[i], this.itemKey);
        keys2.push(key);
        const children = getObjectValueByPath(items[i], this.itemChildren);
        if (children) {
          keys2.push(...this.getKeys(children));
        }
      }
      return keys2;
    },
    buildTree(items, parent = null) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const key = getObjectValueByPath(item, this.itemKey);
        const children = getObjectValueByPath(item, this.itemChildren, []);
        const oldNode = this.nodes.hasOwnProperty(key) ? this.nodes[key] : {
          isSelected: false,
          isIndeterminate: false,
          isActive: false,
          isOpen: false,
          vnode: null
        };
        const node = {
          vnode: oldNode.vnode,
          parent,
          children: children.map((c) => getObjectValueByPath(c, this.itemKey)),
          item
        };
        this.buildTree(children, key);
        if (!this.nodes.hasOwnProperty(key) && parent !== null && this.nodes.hasOwnProperty(parent)) {
          node.isSelected = this.nodes[parent].isSelected;
        } else {
          node.isSelected = oldNode.isSelected;
          node.isIndeterminate = oldNode.isIndeterminate;
        }
        node.isActive = oldNode.isActive;
        node.isOpen = oldNode.isOpen;
        this.nodes[key] = node;
        if (children.length) {
          const {
            isSelected,
            isIndeterminate
          } = this.calculateState(key, this.nodes);
          node.isSelected = isSelected;
          node.isIndeterminate = isIndeterminate;
        }
        if (this.nodes[key].isSelected && (this.selectionType === "independent" || node.children.length === 0))
          this.selectedCache.add(key);
        if (this.nodes[key].isActive)
          this.activeCache.add(key);
        if (this.nodes[key].isOpen)
          this.openCache.add(key);
        this.updateVnodeState(key);
      }
    },
    calculateState(node, state) {
      const children = state[node].children;
      const counts = children.reduce((counts2, child) => {
        counts2[0] += +Boolean(state[child].isSelected);
        counts2[1] += +Boolean(state[child].isIndeterminate);
        return counts2;
      }, [0, 0]);
      const isSelected = !!children.length && counts[0] === children.length;
      const isIndeterminate = !isSelected && (counts[0] > 0 || counts[1] > 0);
      return {
        isSelected,
        isIndeterminate
      };
    },
    emitOpen() {
      this.emitNodeCache("update:open", this.openCache);
    },
    emitSelected() {
      this.emitNodeCache("input", this.selectedCache);
    },
    emitActive() {
      this.emitNodeCache("update:active", this.activeCache);
    },
    emitNodeCache(event, cache2) {
      this.$emit(event, this.returnObject ? [...cache2].map((key) => this.nodes[key].item) : [...cache2]);
    },
    handleNodeCacheWatcher(value, cache2, updateFn, emitFn) {
      value = this.returnObject ? value.map((v) => getObjectValueByPath(v, this.itemKey)) : value;
      const old = [...cache2];
      if (deepEqual(old, value))
        return;
      old.forEach((key) => updateFn(key, false));
      value.forEach((key) => updateFn(key, true));
      emitFn();
    },
    getDescendants(key, descendants = []) {
      const children = this.nodes[key].children;
      descendants.push(...children);
      for (let i = 0; i < children.length; i++) {
        descendants = this.getDescendants(children[i], descendants);
      }
      return descendants;
    },
    getParents(key) {
      let parent = this.nodes[key].parent;
      const parents = [];
      while (parent !== null) {
        parents.push(parent);
        parent = this.nodes[parent].parent;
      }
      return parents;
    },
    register(node) {
      const key = getObjectValueByPath(node.item, this.itemKey);
      this.nodes[key].vnode = node;
      this.updateVnodeState(key);
    },
    unregister(node) {
      const key = getObjectValueByPath(node.item, this.itemKey);
      if (this.nodes[key])
        this.nodes[key].vnode = null;
    },
    isParent(key) {
      return this.nodes[key].children && this.nodes[key].children.length;
    },
    updateActive(key, isActive) {
      if (!this.nodes.hasOwnProperty(key))
        return;
      if (!this.multipleActive) {
        this.activeCache.forEach((active) => {
          this.nodes[active].isActive = false;
          this.updateVnodeState(active);
          this.activeCache.delete(active);
        });
      }
      const node = this.nodes[key];
      if (!node)
        return;
      if (isActive)
        this.activeCache.add(key);
      else
        this.activeCache.delete(key);
      node.isActive = isActive;
      this.updateVnodeState(key);
    },
    updateSelected(key, isSelected, isForced = false) {
      if (!this.nodes.hasOwnProperty(key))
        return;
      const changed = new Map();
      if (this.selectionType !== "independent") {
        for (const descendant of this.getDescendants(key)) {
          if (!getObjectValueByPath(this.nodes[descendant].item, this.itemDisabled) || isForced) {
            this.nodes[descendant].isSelected = isSelected;
            this.nodes[descendant].isIndeterminate = false;
            changed.set(descendant, isSelected);
          }
        }
        const calculated = this.calculateState(key, this.nodes);
        this.nodes[key].isSelected = isSelected;
        this.nodes[key].isIndeterminate = calculated.isIndeterminate;
        changed.set(key, isSelected);
        for (const parent of this.getParents(key)) {
          const calculated2 = this.calculateState(parent, this.nodes);
          this.nodes[parent].isSelected = calculated2.isSelected;
          this.nodes[parent].isIndeterminate = calculated2.isIndeterminate;
          changed.set(parent, calculated2.isSelected);
        }
      } else {
        this.nodes[key].isSelected = isSelected;
        this.nodes[key].isIndeterminate = false;
        changed.set(key, isSelected);
      }
      for (const [key2, value] of changed.entries()) {
        this.updateVnodeState(key2);
        if (this.selectionType === "leaf" && this.isParent(key2))
          continue;
        value === true ? this.selectedCache.add(key2) : this.selectedCache.delete(key2);
      }
    },
    updateOpen(key, isOpen) {
      if (!this.nodes.hasOwnProperty(key))
        return;
      const node = this.nodes[key];
      const children = getObjectValueByPath(node.item, this.itemChildren);
      if (children && !children.length && node.vnode && !node.vnode.hasLoaded) {
        node.vnode.checkChildren().then(() => this.updateOpen(key, isOpen));
      } else if (children && children.length) {
        node.isOpen = isOpen;
        node.isOpen ? this.openCache.add(key) : this.openCache.delete(key);
        this.updateVnodeState(key);
      }
    },
    updateVnodeState(key) {
      const node = this.nodes[key];
      if (node && node.vnode) {
        node.vnode.isSelected = node.isSelected;
        node.vnode.isIndeterminate = node.isIndeterminate;
        node.vnode.isActive = node.isActive;
        node.vnode.isOpen = node.isOpen;
      }
    },
    isExcluded(key) {
      return !!this.search && this.excludedItems.has(key);
    }
  },
  render(h) {
    const children = this.items.length ? this.items.filter((item) => {
      return !this.isExcluded(getObjectValueByPath(item, this.itemKey));
    }).map((item) => {
      const genChild = VTreeviewNode$1.options.methods.genChild.bind(this);
      return genChild(item, getObjectValueByPath(item, this.itemDisabled));
    }) : this.$slots.default;
    return h("div", {
      staticClass: "v-treeview",
      class: __spreadValues({
        "v-treeview--hoverable": this.hoverable,
        "v-treeview--dense": this.dense
      }, this.themeClasses)
    }, children);
  }
});
var VVirtualScroll = Measurable.extend({
  name: "v-virtual-scroll",
  directives: {
    Scroll: Scroll$1
  },
  props: {
    bench: {
      type: [Number, String],
      default: 0
    },
    itemHeight: {
      type: [Number, String],
      required: true
    },
    items: {
      type: Array,
      default: () => []
    }
  },
  data: () => ({
    first: 0,
    last: 0,
    scrollTop: 0
  }),
  computed: {
    __bench() {
      return parseInt(this.bench, 10);
    },
    __itemHeight() {
      return parseInt(this.itemHeight, 10);
    },
    firstToRender() {
      return Math.max(0, this.first - this.__bench);
    },
    lastToRender() {
      return Math.min(this.items.length, this.last + this.__bench);
    }
  },
  watch: {
    height: "onScroll",
    itemHeight: "onScroll"
  },
  mounted() {
    this.last = this.getLast(0);
  },
  methods: {
    getChildren() {
      return this.items.slice(this.firstToRender, this.lastToRender).map(this.genChild);
    },
    genChild(item, index) {
      index += this.firstToRender;
      const top = convertToUnit(index * this.__itemHeight);
      return this.$createElement("div", {
        staticClass: "v-virtual-scroll__item",
        style: {
          top
        },
        key: index
      }, getSlot(this, "default", {
        index,
        item
      }));
    },
    getFirst() {
      return Math.floor(this.scrollTop / this.__itemHeight);
    },
    getLast(first) {
      const height = parseInt(this.height || 0, 10) || this.$el.clientHeight;
      return first + Math.ceil(height / this.__itemHeight);
    },
    onScroll() {
      this.scrollTop = this.$el.scrollTop;
      this.first = this.getFirst();
      this.last = this.getLast(this.first);
    }
  },
  render(h) {
    const content = h("div", {
      staticClass: "v-virtual-scroll__container",
      style: {
        height: convertToUnit(this.items.length * this.__itemHeight)
      }
    }, this.getChildren());
    return h("div", {
      staticClass: "v-virtual-scroll",
      style: this.measurableStyles,
      directives: [{
        name: "scroll",
        modifiers: {
          self: true
        },
        value: this.onScroll
      }],
      on: this.$listeners
    }, [content]);
  }
});
const TOKEN_COLOR_INDIGO_10 = "#F2F4FC";
const TOKEN_COLOR_INDIGO_20 = "#D8E0FF";
const TOKEN_COLOR_INDIGO_40 = "#4461D7";
const TOKEN_COLOR_INDIGO_50 = "#3C55BD";
const TOKEN_COLOR_INDIGO_60 = "#364DAA";
const TOKEN_COLOR_RED_10 = "#FCEAEC";
const TOKEN_COLOR_RED_20 = "#FFD0DA";
const TOKEN_COLOR_RED_40 = "#E12D42";
const TOKEN_COLOR_RED_50 = "#CB283B";
const TOKEN_COLOR_RED_60 = "#B42435";
const TOKEN_COLOR_YELLOW_10 = "#FFF9EC";
const TOKEN_COLOR_YELLOW_20 = "#FFF0CA";
const TOKEN_COLOR_YELLOW_40 = "#FFC63C";
const TOKEN_COLOR_YELLOW_50 = "#E2B13A";
const TOKEN_COLOR_YELLOW_60 = "#CB9F34";
const TOKEN_COLOR_GREEN_10 = "#EDFAF5";
const TOKEN_COLOR_GREEN_20 = "#D2FDE8";
const TOKEN_COLOR_GREEN_40 = "#4ECD97";
const TOKEN_COLOR_GREEN_50 = "#46B988";
const TOKEN_COLOR_GREEN_60 = "#318160";
var theme = {
  primary: {
    lighten2: TOKEN_COLOR_INDIGO_10,
    lighten1: TOKEN_COLOR_INDIGO_20,
    base: TOKEN_COLOR_INDIGO_40,
    darken1: TOKEN_COLOR_INDIGO_50,
    darken2: TOKEN_COLOR_INDIGO_60
  },
  secondary: {
    lighten2: TOKEN_COLOR_INDIGO_10,
    lighten1: TOKEN_COLOR_INDIGO_20,
    base: TOKEN_COLOR_INDIGO_40,
    darken1: TOKEN_COLOR_INDIGO_50,
    darken2: TOKEN_COLOR_INDIGO_60
  },
  accent: {
    base: TOKEN_COLOR_INDIGO_40
  },
  error: {
    lighten2: TOKEN_COLOR_RED_10,
    lighten1: TOKEN_COLOR_RED_20,
    base: TOKEN_COLOR_RED_40,
    darken1: TOKEN_COLOR_RED_50,
    darken2: TOKEN_COLOR_RED_60
  },
  info: {
    lighten2: TOKEN_COLOR_INDIGO_10,
    lighten1: TOKEN_COLOR_INDIGO_20,
    base: TOKEN_COLOR_INDIGO_40,
    darken1: TOKEN_COLOR_INDIGO_50,
    darken2: TOKEN_COLOR_INDIGO_60
  },
  success: {
    lighten2: TOKEN_COLOR_GREEN_10,
    lighten1: TOKEN_COLOR_GREEN_20,
    base: TOKEN_COLOR_GREEN_40,
    darken1: TOKEN_COLOR_GREEN_50,
    darken2: TOKEN_COLOR_GREEN_60
  },
  warning: {
    lighten2: TOKEN_COLOR_YELLOW_10,
    lighten1: TOKEN_COLOR_YELLOW_20,
    base: TOKEN_COLOR_YELLOW_40,
    darken1: TOKEN_COLOR_YELLOW_50,
    darken2: TOKEN_COLOR_YELLOW_60
  }
};
Vue.use(Vuetify);
const options = {
  icons: {
    iconfont: "mdiSvg"
  },
  theme: {
    options: {
      customProperties: false
    },
    themes: {
      light: theme,
      dark: false
    }
  }
};
var vuetify = new Vuetify(options);
const teamworkVuetify = {
  vuetify
};

var script$V = {
  name: 'TAlert',
  extends: VAlert,
  props: {
    border: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    link: {
      type: String,
      default: '',
    },
    linkLabel: {
      type: String,
      default: '',
    },
    closeLabel: {
      type: String,
      default: '$vuetify.close',
    },
    coloredBorder: Boolean,
    dense: Boolean,
    dismissible: Boolean,
    closeIcon: {
      type: String,
      default: '$close',
    },
    icon: {
      default: '',
      type: [Boolean, String],
    },
    outlined: Boolean,
    prominent: Boolean,
    text: Boolean,
    type: {
      type: String,
      default: '',
    },
    value: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    hasTitle() {
      return this.$props.title ? 't-block' : 't-hidden';
    },

    linkStyle() {
      const whiteText = ['info', 'error'].includes(this.type);

      return {
        color: whiteText ? 'var(--token-ui-white)' : 'var(--token-ui-text-01)',
      };
    },

    textStyles() {
      let textStyles = '';

      if (this.type) {
        if (['info', 'error'].includes(this.type)) {
          textStyles = 't-text-white';
        } else {
          textStyles = 't-text-text-01';
        }
      }

      return textStyles;
    },
  },
  methods: {
    // Here we overwrite the required methods of VAlert
    genWrapper() {
      const children = [
        this.$slots.prepend || this.__cachedIcon,
        this.genContent(),
        this.__cachedBorder,
        this.$slots.append,
        this.$scopedSlots.close
          ? this.$scopedSlots.close({ toggle: this.toggle })
          : this.__cachedDismissible,
      ];

      const data = {
        staticClass: `v-alert__wrapper ${this.textStyles}`,
      };

      return this.$createElement('div', data, children);
    },
    genContent() {
      const { hasTitle, link, linkLabel } = this;

      // Using $createElement to create 2 new blocks.
      // INFO: $createElement
      // --- First goes the desired HTML tag
      // --- Options about attributes
      // --- Data that goes in. For appending, just use array
      const titleContent = this.$createElement(
        'strong',
        {
          staticClass: `t-w-full t-font-semibold t-leading-none t-mb-2 ${hasTitle}`,
        },
        this.$props.title,
      );

      const children = [titleContent, this.$slots.default];

      if (link) {
        children.push(
          this.$createElement(
            'a',
            {
              style: this.linkStyle,
              staticClass: 't-no-underline t-font-semibold t-ml-2',

              attrs: {
                href: link,
              },
            },
            linkLabel || link,
          ),
        );
      }

      const defaultContent = this.$createElement(
        'div',
        {
          staticClass: 'v-alert__content t-leading-tight',
        },
        children,
      );

      return [defaultContent];
    },
  },
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__$V = script$V;

/* template */

  /* style */
  const __vue_inject_styles__$$ = undefined;
  /* scoped */
  const __vue_scope_id__$$ = undefined;
  /* module identifier */
  const __vue_module_identifier__$$ = undefined;
  /* functional template */
  const __vue_is_functional_template__$$ = undefined;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$$ = /*#__PURE__*/normalizeComponent(
    {},
    __vue_inject_styles__$$,
    __vue_script__$V,
    __vue_scope_id__$$,
    __vue_is_functional_template__$$,
    __vue_module_identifier__$$,
    false,
    undefined,
    undefined,
    undefined
  );

var script$U = {
  name: 'TBtn',
  extends: VBtn,
  /**
   * Adds Variant Prop and Set default Vuetify props
   */
  props: {
    variant: {
      default: 'primary',
      type: String,
    },
    depressed: {
      default: true,
      type: Boolean,
    },
    visuallyDisabled: {
      default: false,
      type: Boolean,
    },
  },
  computed: {
    classes() {
      return {
        'v-btn': true,
        'v-btn--absolute': this.absolute,
        'v-btn--block': this.block,
        'v-btn--bottom': this.bottom,
        'v-btn--disabled': this.disabled,
        'v-btn--visually-disabled v-btn--disabled': this.visuallyDisabled,
        'v-btn--is-elevated': this.isElevated,
        'v-btn--fab': this.fab,
        'v-btn--fixed': this.fixed,
        'v-btn--has-bg': this.hasBg,
        'v-btn--icon': this.icon,
        'v-btn--left': this.left,
        'v-btn--loading': this.loading,
        'v-btn--outlined': this.outlined,
        'v-btn--plain': this.plain,
        'v-btn--right': this.right,
        'v-btn--round': this.isRound,
        'v-btn--rounded': this.rounded,
        'v-btn--router': this.to,
        'v-btn--text': this.text,
        'v-btn--tile': this.tile,
        'v-btn--top': this.top,
        ...this.themeClasses,
        ...this.groupClasses,
        ...this.elevationClasses,
        ...this.sizeableClasses,
      };
    },
  },
  render(h) {
    const children = [this.genContent(), this.loading && this.genLoader()];
    const { tag, data } = this.generateRouteLink();
    const setColor = this.hasBg ? this.setBackgroundColor : this.setTextColor;

    if (tag === 'button') {
      data.attrs.type = this.type;
      data.attrs.disabled = this.disabled;
    }
    data.attrs.value = ['string', 'number'].includes(typeof this.value)
      ? this.value
      : JSON.stringify(this.value);

    return h(tag, this.disabled ? data : setColor(this.color, data), children);
  },
};

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$U = script$U;

/* template */

  /* style */
  const __vue_inject_styles__$_ = function (inject) {
    if (!inject) return
    inject("data-v-63769040_0", { source: ".v-btn:not(.v-btn--round).v-size--default,.v-btn:not(.v-btn--round).v-size--large,.v-btn:not(.v-btn--round).v-size--small,.v-btn:not(.v-btn--round).v-size--x-large{padding:0 12px!important;min-width:auto!important}.v-btn__content{line-height:1!important}.v-btn--disabled.v-btn--visually-disabled{pointer-events:auto}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$_ = undefined;
  /* module identifier */
  const __vue_module_identifier__$_ = undefined;
  /* functional template */
  const __vue_is_functional_template__$_ = undefined;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$_ = /*#__PURE__*/normalizeComponent(
    {},
    __vue_inject_styles__$_,
    __vue_script__$U,
    __vue_scope_id__$_,
    __vue_is_functional_template__$_,
    __vue_module_identifier__$_,
    false,
    createInjector,
    undefined,
    undefined
  );

var script$T = {
  name: 'TTooltip',
  extends: VTooltip,
  props: {
    maxWidth: {
      type: [Number, String],
      default: 200,
    },
  },
  computed: {
    customisedStyles() {
      return {
        ...this.styles,
        opacity: this.isActive ? 1 : 0,
      };
    },
  },
  methods: {
    genContent() {
      return this.$createElement(
        'div',
        this.setBackgroundColor(this.color, {
          staticClass: 'v-tooltip__content',
          class: {
            [this.contentClass]: true,
            menuable__content__active: this.isActive,
            'v-tooltip__content--fixed': this.activatorFixed,
          },
          style: this.customisedStyles,
          attrs: this.getScopeIdAttrs(),
          directives: [
            {
              name: 'show',
              value: this.isContentActive,
            },
          ],
          ref: 'content',
        }),
        this.getContentSlot(),
      );
    },
  },
};

/* script */
const __vue_script__$T = script$T;

/* template */

  /* style */
  const __vue_inject_styles__$Z = undefined;
  /* scoped */
  const __vue_scope_id__$Z = undefined;
  /* module identifier */
  const __vue_module_identifier__$Z = undefined;
  /* functional template */
  const __vue_is_functional_template__$Z = undefined;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Z = /*#__PURE__*/normalizeComponent(
    {},
    __vue_inject_styles__$Z,
    __vue_script__$T,
    __vue_scope_id__$Z,
    __vue_is_functional_template__$Z,
    __vue_module_identifier__$Z,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$S = {
  name: 'TAppBase',
  components: {
    TApp: VApp,
  },
};

/* script */
const __vue_script__$S = script$S;

/* template */
var __vue_render__$Y = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('t-app',{staticClass:"app-base t-h-screen t-max-h-screen t-flex t-overflow-hidden"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$Y = [];

  /* style */
  const __vue_inject_styles__$Y = function (inject) {
    if (!inject) return
    inject("data-v-6b04b1ca_0", { source: ".app-base[data-v-6b04b1ca]{backface-visibility:hidden}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$Y = "data-v-6b04b1ca";
  /* module identifier */
  const __vue_module_identifier__$Y = undefined;
  /* functional template */
  const __vue_is_functional_template__$Y = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Y, staticRenderFns: __vue_staticRenderFns__$Y },
    __vue_inject_styles__$Y,
    __vue_script__$S,
    __vue_scope_id__$Y,
    __vue_is_functional_template__$Y,
    __vue_module_identifier__$Y,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$R = {
  name: 'TAppHeader',
};

/* script */
const __vue_script__$R = script$R;

/* template */
var __vue_render__$X = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('header',{staticClass:"\n    t-app-header\n    t-relative\n    t-sticky\n    t-top-0\n    t-z-20\n    t-flex\n    t-items-center\n    t-flex-none\n    t-text-white\n    t-text-lg\n    t-px-2\n  "},[_vm._t("default")],2)};
var __vue_staticRenderFns__$X = [];

  /* style */
  const __vue_inject_styles__$X = function (inject) {
    if (!inject) return
    inject("data-v-0cef1314_0", { source: ".t-app-header[data-v-0cef1314]{height:56px;background-color:#1d1b39}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$X = "data-v-0cef1314";
  /* module identifier */
  const __vue_module_identifier__$X = undefined;
  /* functional template */
  const __vue_is_functional_template__$X = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$X = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$X, staticRenderFns: __vue_staticRenderFns__$X },
    __vue_inject_styles__$X,
    __vue_script__$R,
    __vue_scope_id__$X,
    __vue_is_functional_template__$X,
    __vue_module_identifier__$X,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//

var script$Q = {
  name: 'TAppBody',
};

/* script */
const __vue_script__$Q = script$Q;

/* template */
var __vue_render__$W = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('main',{staticClass:"t-app-body t-relative t-flex-shrink-0 t-flex-grow t-overflow-y-auto"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$W = [];

  /* style */
  const __vue_inject_styles__$W = undefined;
  /* scoped */
  const __vue_scope_id__$W = undefined;
  /* module identifier */
  const __vue_module_identifier__$W = undefined;
  /* functional template */
  const __vue_is_functional_template__$W = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$W = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$W, staticRenderFns: __vue_staticRenderFns__$W },
    __vue_inject_styles__$W,
    __vue_script__$Q,
    __vue_scope_id__$W,
    __vue_is_functional_template__$W,
    __vue_module_identifier__$W,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$P = {
  name: 'TAppPageContent',
};

/* script */
const __vue_script__$P = script$P;

/* template */
var __vue_render__$V = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('section',{staticClass:"t-app-page-content t-min-h-full"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$V = [];

  /* style */
  const __vue_inject_styles__$V = undefined;
  /* scoped */
  const __vue_scope_id__$V = undefined;
  /* module identifier */
  const __vue_module_identifier__$V = undefined;
  /* functional template */
  const __vue_is_functional_template__$V = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$V = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$V, staticRenderFns: __vue_staticRenderFns__$V },
    __vue_inject_styles__$V,
    __vue_script__$P,
    __vue_scope_id__$V,
    __vue_is_functional_template__$V,
    __vue_module_identifier__$V,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$O = {
  name: 'TAppSidebar',
};

/* script */
const __vue_script__$O = script$O;

/* template */
var __vue_render__$U = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('aside',{staticClass:"t-app-sidebar"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$U = [];

  /* style */
  const __vue_inject_styles__$U = undefined;
  /* scoped */
  const __vue_scope_id__$U = undefined;
  /* module identifier */
  const __vue_module_identifier__$U = undefined;
  /* functional template */
  const __vue_is_functional_template__$U = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$U = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$U, staticRenderFns: __vue_staticRenderFns__$U },
    __vue_inject_styles__$U,
    __vue_script__$O,
    __vue_scope_id__$U,
    __vue_is_functional_template__$U,
    __vue_module_identifier__$U,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$N = {
  name: 'TAppFilters',
};

/* script */
const __vue_script__$N = script$N;

/* template */
var __vue_render__$T = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('aside',{staticClass:"t-app-sidebar-filters"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$T = [];

  /* style */
  const __vue_inject_styles__$T = undefined;
  /* scoped */
  const __vue_scope_id__$T = undefined;
  /* module identifier */
  const __vue_module_identifier__$T = undefined;
  /* functional template */
  const __vue_is_functional_template__$T = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$T = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$T, staticRenderFns: __vue_staticRenderFns__$T },
    __vue_inject_styles__$T,
    __vue_script__$N,
    __vue_scope_id__$T,
    __vue_is_functional_template__$T,
    __vue_module_identifier__$T,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$M = {
  name: 'TAppFooter',
};

/* script */
const __vue_script__$M = script$M;

/* template */
var __vue_render__$S = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('footer',{staticClass:"t-app-footer"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$S = [];

  /* style */
  const __vue_inject_styles__$S = undefined;
  /* scoped */
  const __vue_scope_id__$S = undefined;
  /* module identifier */
  const __vue_module_identifier__$S = undefined;
  /* functional template */
  const __vue_is_functional_template__$S = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$S = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$S, staticRenderFns: __vue_staticRenderFns__$S },
    __vue_inject_styles__$S,
    __vue_script__$M,
    __vue_scope_id__$S,
    __vue_is_functional_template__$S,
    __vue_module_identifier__$S,
    false,
    undefined,
    undefined,
    undefined
  );

/* script */

/* template */
var __vue_render__$R = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{staticStyle:{"enable-background":"new 0 0 119.1 119.1"},attrs:{"id":"Layer_1","version":"1.1","xmlns":"http://www.w3.org/2000/svg","xmlns:xlink":"http://www.w3.org/1999/xlink","x":"0px","y":"0px","viewBox":"0 0 119.1 119.1","xml:space":"preserve"}},[_c('path',{staticClass:"logo",attrs:{"d":"M58.7,52.1c3.3,0,5.2-1.8,5.2-4.9c0.1-2.6-1.9-4.8-4.5-4.9c-0.2,0-0.4,0-0.5,0h-7.5v-5.5c0.2-3.1-2.2-5.9-5.4-6\n    c-3.1-0.2-5.9,2.2-6,5.4c0,0.2,0,0.5,0,0.7v38.2c0,9.2,3.7,13.3,11.9,13.3c3.1,0.1,6.2-0.7,8.9-2.2c1.3-0.8,2.1-2.3,2-3.8\n    c0-2.5-1.7-5.2-4-5.2c-0.3,0-0.7,0.1-1,0.2l-0.4,0.2c-0.8,0.4-1.8,0.7-2.7,0.7c-1.5,0-3.2-0.5-3.2-4.6V52.1H58.7z"}}),_vm._v(" "),_c('path',{staticClass:"logo__dot",attrs:{"d":"M83.2,68.2c-5.5,0-10,4.5-10,10s4.5,10,10,10s10-4.5,10-10S88.7,68.2,83.2,68.2"}})])};
var __vue_staticRenderFns__$R = [];

  /* style */
  const __vue_inject_styles__$R = function (inject) {
    if (!inject) return
    inject("data-v-1d2cadb2_0", { source: ".logo[data-v-1d2cadb2]{fill:#fff}.logo__dot[data-v-1d2cadb2]{fill:#ff22b1}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$R = "data-v-1d2cadb2";
  /* module identifier */
  const __vue_module_identifier__$R = undefined;
  /* functional template */
  const __vue_is_functional_template__$R = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$R = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$R, staticRenderFns: __vue_staticRenderFns__$R },
    __vue_inject_styles__$R,
    {},
    __vue_scope_id__$R,
    __vue_is_functional_template__$R,
    __vue_module_identifier__$R,
    false,
    createInjector,
    undefined,
    undefined
  );

//
// import AppFooter from '@/layouts/AppFooter.vue';
// import AppFilters from '@/layouts/AppFilters.vue';

var script$L = {
  name: 'TheLayout',
  components: {
    TLogoSVG: __vue_component__$R,
    AppBase: __vue_component__$Y,
    AppHeader: __vue_component__$X,
    AppBody: __vue_component__$W,
    AppPageContent: __vue_component__$V,
    // AppFooter,
    // AppFilters,
  },
  props: {
    useHeader: {
      type: Boolean,
      default: true,
    },
  },
};

/* script */
const __vue_script__$L = script$L;

/* template */
var __vue_render__$Q = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('app-base',[(_vm.useHeader)?_c('app-header',[_c('TLogoSVG',{staticClass:"t-w-10 t-h-10 t-flex-none"}),_vm._v(" "),_vm._t("header")],2):_vm._e(),_vm._v(" "),_c('app-body',{staticClass:"t-flex-1 t-h-full"},[_c('app-page-content',{staticClass:"t-h-full"},[_vm._t("default")],2)],1)],1)};
var __vue_staticRenderFns__$Q = [];

  /* style */
  const __vue_inject_styles__$Q = undefined;
  /* scoped */
  const __vue_scope_id__$Q = undefined;
  /* module identifier */
  const __vue_module_identifier__$Q = undefined;
  /* functional template */
  const __vue_is_functional_template__$Q = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$Q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$Q, staticRenderFns: __vue_staticRenderFns__$Q },
    __vue_inject_styles__$Q,
    __vue_script__$L,
    __vue_scope_id__$Q,
    __vue_is_functional_template__$Q,
    __vue_module_identifier__$Q,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$K = {
  props: {
    cols: {
      type: String,
      default: undefined,
    },
    rows: {
      type: String,
      default: undefined,
    },
  },
  computed: {
    setColumns() {
      if (this.cols) {
        return `t-grid-cols-${this.cols}`;
      }
      return 't-grid-cols-none';
    },
    setRows() {
      if (this.rows) {
        return `t-grid-rows-${this.rows}`;
      }
      return 't-grid-rows-none';
    },
  },
};

/* script */
const __vue_script__$K = script$K;

/* template */
var __vue_render__$P = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('section',{staticClass:"t-grid",class:[_vm.setColumns, _vm.setRows]},[_vm._t("default")],2)};
var __vue_staticRenderFns__$P = [];

  /* style */
  const __vue_inject_styles__$P = undefined;
  /* scoped */
  const __vue_scope_id__$P = undefined;
  /* module identifier */
  const __vue_module_identifier__$P = undefined;
  /* functional template */
  const __vue_is_functional_template__$P = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$P = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$P, staticRenderFns: __vue_staticRenderFns__$P },
    __vue_inject_styles__$P,
    __vue_script__$K,
    __vue_scope_id__$P,
    __vue_is_functional_template__$P,
    __vue_module_identifier__$P,
    false,
    undefined,
    undefined,
    undefined
  );

/* script */

/* template */
var __vue_render__$O = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[_vm._t("default")],2)};
var __vue_staticRenderFns__$O = [];

  /* style */
  const __vue_inject_styles__$O = undefined;
  /* scoped */
  const __vue_scope_id__$O = undefined;
  /* module identifier */
  const __vue_module_identifier__$O = undefined;
  /* functional template */
  const __vue_is_functional_template__$O = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$O = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$O, staticRenderFns: __vue_staticRenderFns__$O },
    __vue_inject_styles__$O,
    {},
    __vue_scope_id__$O,
    __vue_is_functional_template__$O,
    __vue_module_identifier__$O,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$J = {
  components: {
    GridContainer: __vue_component__$P,
    GridBlock: __vue_component__$O,
  },
  props: {
    isLeftSidebarCollapsed: {
      type: Boolean,
      default: false,
    },
    isRightSidebarCollapsed: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const contentBlockSpan = computed(() => {
      if (props.isLeftSidebarCollapsed) {
        if (!props.isRightSidebarCollapsed) {
          return 't-col-span-2';
        }
        return 't-col-span-3';
      }

      if (!props.isRightSidebarCollapsed) {
        return 't-col-span-1';
      }
      return 't-col-span-2';
    });

    return {
      contentBlockSpan,
    };
  },
};

/* script */
const __vue_script__$J = script$J;

/* template */
var __vue_render__$N = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('GridContainer',{staticClass:"t-grid-cols-app-body"},[_c('GridBlock',{directives:[{name:"show",rawName:"v-show",value:(!_vm.isLeftSidebarCollapsed),expression:"!isLeftSidebarCollapsed"}],staticClass:"t-col-span-1"},[_vm._t("sidebar")],2),_vm._v(" "),_c('GridBlock',{class:_vm.contentBlockSpan},[_vm._t("default")],2),_vm._v(" "),_c('GridBlock',{directives:[{name:"show",rawName:"v-show",value:(!_vm.isRightSidebarCollapsed),expression:"!isRightSidebarCollapsed"}],staticClass:"t-col-span-1"},[_vm._t("filters")],2)],1)};
var __vue_staticRenderFns__$N = [];

  /* style */
  const __vue_inject_styles__$N = undefined;
  /* scoped */
  const __vue_scope_id__$N = undefined;
  /* module identifier */
  const __vue_module_identifier__$N = undefined;
  /* functional template */
  const __vue_is_functional_template__$N = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$N = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$N, staticRenderFns: __vue_staticRenderFns__$N },
    __vue_inject_styles__$N,
    __vue_script__$J,
    __vue_scope_id__$N,
    __vue_is_functional_template__$N,
    __vue_module_identifier__$N,
    false,
    undefined,
    undefined,
    undefined
  );

/* script */

/* template */
var __vue_render__$M = function () {var _vm=this;var _h=_vm.$createElement;_vm._self._c||_h;return _vm._m(0)};
var __vue_staticRenderFns__$M = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('td',{staticClass:"t-data-table__body-cell t-bg-white t-px-3"},[_c('span',{staticClass:"t-h-8 t-w-full t-bg-bg t-block t-rounded"})])}];

  /* style */
  const __vue_inject_styles__$M = undefined;
  /* scoped */
  const __vue_scope_id__$M = undefined;
  /* module identifier */
  const __vue_module_identifier__$M = undefined;
  /* functional template */
  const __vue_is_functional_template__$M = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$M = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$M, staticRenderFns: __vue_staticRenderFns__$M },
    __vue_inject_styles__$M,
    {},
    __vue_scope_id__$M,
    __vue_is_functional_template__$M,
    __vue_module_identifier__$M,
    false,
    undefined,
    undefined,
    undefined
  );

var script$I = {
  components: {
    TDataTableSkeletonCell: __vue_component__$M,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup() {
    const root = ref(null);
    const state = inject$1('table.state');

    const show = ref(false);
    const visible = ref(false);

    const { addToQueue } = inject$1('table.body');

    onMounted(() => {
      show.value = true;

      addToQueue(() => {
        visible.value = true;
      });
    });

    return {
      root,
      show,
      state,
      visible,
    };
  },
};

/* script */
const __vue_script__$I = script$I;

/* template */
var __vue_render__$L = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('tr',{ref:"root",staticClass:"t-group t-bg-white t-h-12"},[(_vm.show)?_vm._l((_vm.state.headers),function(header){return _c(_vm.visible ? header.itemComponent : 'TDataTableSkeletonCell',{key:header.keyId,tag:"Component",staticClass:"t-h-12 group-hover:t-bg-neutral-10",attrs:{"item":_vm.item,"header":header}})}):_vm._e()],2)};
var __vue_staticRenderFns__$L = [];

  /* style */
  const __vue_inject_styles__$L = undefined;
  /* scoped */
  const __vue_scope_id__$L = undefined;
  /* module identifier */
  const __vue_module_identifier__$L = undefined;
  /* functional template */
  const __vue_is_functional_template__$L = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$L = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$L, staticRenderFns: __vue_staticRenderFns__$L },
    __vue_inject_styles__$L,
    __vue_script__$I,
    __vue_scope_id__$L,
    __vue_is_functional_template__$L,
    __vue_module_identifier__$L,
    false,
    undefined,
    undefined,
    undefined
  );

const useTableBody = () => {
  const table = inject$1('table');

  /**
   * Callback queue.
   */
  const queue = [];

  let next = null;

  const runQueue = () => {
    const promise = Promise.resolve();
    const queueSplice = Promise.resolve().then(() => queue.splice(0, 20));

    return raf(async () => {
      await queueSplice.then((callbacks) =>
        Promise.all(callbacks.map((cb) => promise.then(cb))),
      );

      /**
       * Recursive call queue if it has items left.
       */
      if (queue.length) {
        next = runQueue();

        return;
      }

      next = null;
    });
  };

  const run = () => {
    if (next) {
      return;
    }

    next = runQueue();
  };

  const addToQueue = (callback) => {
    queue.push(callback);

    run();
  };

  if (table.fetcher) {
    watch(
      () => table.fetcher.params,
      () => {
        document.querySelector('.t-data-table__wrapper')?.scrollTo?.(0, 0);
      },
      {
        deep: true,
      },
    );
  }

  return {
    addToQueue,
  };
};

var script$H = {
  components: {
    TDataTableBodyRow: __vue_component__$L,
    TDataTableSkeletonCell: __vue_component__$M,
  },

  setup() {
    const state = inject$1('table.state');

    const body = useTableBody();

    provide$1('table.body', body);

    return {
      state,
    };
  },
};

/* script */
const __vue_script__$H = script$H;

/* template */
var __vue_render__$K = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('tbody',[_vm._l((_vm.state.items),function(item){return _c('TDataTableBodyRow',{key:item.id,attrs:{"item":item}})}),_vm._v(" "),_vm._l((3),function(row){return _c('tr',{directives:[{name:"show",rawName:"v-show",value:(_vm.state.isLoading),expression:"state.isLoading"}],key:("loading" + row),staticClass:"t-group t-bg-white t-h-12"},_vm._l((_vm.state.headers.length),function(header){return _c('TDataTableSkeletonCell',{key:("" + row + header),staticClass:"t-h-12 t-sticky t-animate-pulse group-hover:t-bg-bg--hover"})}),1)})],2)};
var __vue_staticRenderFns__$K = [];

  /* style */
  const __vue_inject_styles__$K = undefined;
  /* scoped */
  const __vue_scope_id__$K = undefined;
  /* module identifier */
  const __vue_module_identifier__$K = undefined;
  /* functional template */
  const __vue_is_functional_template__$K = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$K = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$K, staticRenderFns: __vue_staticRenderFns__$K },
    __vue_inject_styles__$K,
    __vue_script__$H,
    __vue_scope_id__$K,
    __vue_is_functional_template__$K,
    __vue_module_identifier__$K,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$G = {
  components: { TTooltip: __vue_component__$Z },
  props: {
    text: {
      type: String,
      required: true,
    },
    clamp: {
      type: String,
      default: '...',
    },
    customClass: {
      type: String,
      default: '',
    },
    tooltipWidth: {
      type: [Number, String],
      default: '200px',
    },
    truncate: {
      type: Number,
      default: null,
    },
  },

  setup(props) {
    const getTruncateValue = () =>
      props.truncate ? props.truncate : parseInt(props.tooltipWidth, 10) / 10;

    const shouldTruncate = computed(
      () => props.text.length > getTruncateValue(),
    );

    const truncateText = computed(
      () => `${props.text.slice(0, getTruncateValue())}${props.clamp}`,
    );

    return {
      shouldTruncate,
      truncateText,
    };
  },
};

/* script */
const __vue_script__$G = script$G;

/* template */
var __vue_render__$J = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.shouldTruncate)?_c('TTooltip',{staticClass:"t-z-50",attrs:{"top":"","max-width":_vm.tooltipWidth},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('span',_vm._g(_vm._b({class:_vm.customClass},'span',attrs,false),on),[_vm._v("\n      "+_vm._s(_vm.truncateText)+"\n    ")])]}}],null,false,3158729320)},[_vm._v("\n  "+_vm._s(_vm.text)+"\n")]):_c('span',{class:_vm.customClass},[_vm._v("\n  "+_vm._s(_vm.text)+"\n")])};
var __vue_staticRenderFns__$J = [];

  /* style */
  const __vue_inject_styles__$J = undefined;
  /* scoped */
  const __vue_scope_id__$J = undefined;
  /* module identifier */
  const __vue_module_identifier__$J = undefined;
  /* functional template */
  const __vue_is_functional_template__$J = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$J = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$J, staticRenderFns: __vue_staticRenderFns__$J },
    __vue_inject_styles__$J,
    __vue_script__$G,
    __vue_scope_id__$J,
    __vue_is_functional_template__$J,
    __vue_module_identifier__$J,
    false,
    undefined,
    undefined,
    undefined
  );

var script$F = {
  components: { TTruncateText: __vue_component__$J },

  props: {
    item: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    header: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    empty: {
      type: String,
      required: false,
      default: 'Not Set',
    },
  },

  setup(props) {
    const model = computed(() => props.item[props.header.propKey]);

    const text = computed(() => {
      if (model.value) {
        return model.value.toString();
      }

      return props.empty;
    });

    const state = inject$1('table.state');

    const isEmpty = computed(() => !model.value);
    const link = computed(() => props.header.link?.(toRaw(props.item)) || '');
    const isLink = computed(() => model.value && link.value);

    return {
      isLink,
      isEmpty,

      style: computed(() => {
        if (props.header.stickyTo) {
          return {
            [props.header.stickyTo]: `${props.header.stickyMargin}px`,
          };
        }

        return null;
      }),

      classes: computed(() => [
        props.header.align,
        isLink.value ? 't-px-0' : 't-px-3',
        model.value ? 't-text-text-01' : 't-text-text-02',
        props.header.stickyTo &&
          't-sticky t-z-10 t-duration-300 t-transition-shadow',
      ]),

      state: reactive({
        text,
        link,
        model,
      }),

      handleLinkClick(event) {
        if (link.value?.startsWith?.('http')) {
          event.preventDefault();

          state.emit('link:click', link.value, props.header.keyId, props.item);
        }
      },
    };
  },
};

/* script */
const __vue_script__$F = script$F;

/* template */
var __vue_render__$I = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('td',{staticClass:"\n    t-data-table__body-cell\n    t-align-middle t-text-default t-font-normal t-bg-white t-relative\n  ",class:_vm.classes,style:(_vm.style)},[(_vm.isLink)?_c('a',{staticClass:"\n      t-h-full\n      t-w-full\n      t-flex\n      t-items-center\n      t-px-3\n      t-text-primary\n      t-whitespace-nowrap\n      t-no-underline\n      hover:t-underline\n    ",attrs:{"href":_vm.state.link,"target":"_parent"},on:{"click":function($event){return _vm.handleLinkClick($event)}}},[_vm._t("default",function(){return [_c('TTruncateText',{attrs:{"text":_vm.state.text,"tooltip-width":_vm.header.width}})]},{"value":_vm.state.model})],2):_vm._t("default",function(){return [_c('TTruncateText',{attrs:{"text":_vm.state.text,"tooltip-width":_vm.header.width}})]},{"value":_vm.state.model})],2)};
var __vue_staticRenderFns__$I = [];

  /* style */
  const __vue_inject_styles__$I = function (inject) {
    if (!inject) return
    inject("data-v-c76a0f76_0", { source: ".t-data-table__body-cell:first-of-type::after{border-left:0}.t-data-table__body-cell::after{top:0;left:0;right:0;bottom:0;content:'';position:absolute;pointer-events:none;border:1px solid var(--token-ui-divider);border-style:solid;border-top-width:0;border-right-width:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$I = undefined;
  /* module identifier */
  const __vue_module_identifier__$I = undefined;
  /* functional template */
  const __vue_is_functional_template__$I = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$I = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$I, staticRenderFns: __vue_staticRenderFns__$I },
    __vue_inject_styles__$I,
    __vue_script__$F,
    __vue_scope_id__$I,
    __vue_is_functional_template__$I,
    __vue_module_identifier__$I,
    false,
    createInjector,
    undefined,
    undefined
  );

// Material Design Icons v5.9.55
var mdiAccount = "M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z";
var mdiAlertCircleOutline = "M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z";
var mdiAlphabetical = "M6,11A2,2 0 0,1 8,13V17H4A2,2 0 0,1 2,15V13A2,2 0 0,1 4,11H6M4,13V15H6V13H4M20,13V15H22V17H20A2,2 0 0,1 18,15V13A2,2 0 0,1 20,11H22V13H20M12,7V11H14A2,2 0 0,1 16,13V15A2,2 0 0,1 14,17H12A2,2 0 0,1 10,15V7H12M12,15H14V13H12V15Z";
var mdiArrowDown = "M11,4H13V16L18.5,10.5L19.92,11.92L12,19.84L4.08,11.92L5.5,10.5L11,16V4Z";
var mdiArrowUp = "M13,20H11V8L5.5,13.5L4.08,12.08L12,4.16L19.92,12.08L18.5,13.5L13,8V20Z";
var mdiCalendarBlankOutline = "M19,4H18V2H16V4H8V2H6V4H5C3.89,4 3,4.9 3,6V20A2,2 0 0,0 5,22H19A2,2 0 0,0 21,20V6A2,2 0 0,0 19,4M19,20H5V10H19V20M19,8H5V6H19V8Z";
var mdiCheck = "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z";
var mdiCheckBold = "M9,20.42L2.79,14.21L5.62,11.38L9,14.77L18.88,4.88L21.71,7.71L9,20.42Z";
var mdiChevronDown = "M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z";
var mdiClose = "M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z";
var mdiDotsHorizontal = "M16,12A2,2 0 0,1 18,10A2,2 0 0,1 20,12A2,2 0 0,1 18,14A2,2 0 0,1 16,12M10,12A2,2 0 0,1 12,10A2,2 0 0,1 14,12A2,2 0 0,1 12,14A2,2 0 0,1 10,12M4,12A2,2 0 0,1 6,10A2,2 0 0,1 8,12A2,2 0 0,1 6,14A2,2 0 0,1 4,12Z";
var mdiDragHorizontal = "M3,15V13H5V15H3M3,11V9H5V11H3M7,15V13H9V15H7M7,11V9H9V11H7M11,15V13H13V15H11M11,11V9H13V11H11M15,15V13H17V15H15M15,11V9H17V11H15M19,15V13H21V15H19M19,11V9H21V11H19Z";
var mdiEmail = "M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z";
var mdiFileExcel = "M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M15.8,20H14L12,16.6L10,20H8.2L11.1,15.5L8.2,11H10L12,14.4L14,11H15.8L12.9,15.5L15.8,20M13,9V3.5L18.5,9H13Z";
var mdiFilter = "M14,12V19.88C14.04,20.18 13.94,20.5 13.71,20.71C13.32,21.1 12.69,21.1 12.3,20.71L10.29,18.7C10.06,18.47 9.96,18.16 10,17.87V12H9.97L4.21,4.62C3.87,4.19 3.95,3.56 4.38,3.22C4.57,3.08 4.78,3 5,3V3H19V3C19.22,3 19.43,3.08 19.62,3.22C20.05,3.56 20.13,4.19 19.79,4.62L14.03,12H14Z";
var mdiFilterVariant = "M6,13H18V11H6M3,6V8H21V6M10,18H14V16H10V18Z";
var mdiGrid = "M10,4V8H14V4H10M16,4V8H20V4H16M16,10V14H20V10H16M16,16V20H20V16H16M14,20V16H10V20H14M8,20V16H4V20H8M8,14V10H4V14H8M8,8V4H4V8H8M10,14H14V10H10V14M4,2H20A2,2 0 0,1 22,4V20A2,2 0 0,1 20,22H4C2.92,22 2,21.1 2,20V4A2,2 0 0,1 4,2Z";
var mdiHelpCircleOutline = "M11,18H13V16H11V18M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,6A4,4 0 0,0 8,10H10A2,2 0 0,1 12,8A2,2 0 0,1 14,10C14,12 11,11.75 11,15H13C13,12.75 16,12.5 16,10A4,4 0 0,0 12,6Z";
var mdiImport = "M14,12L10,8V11H2V13H10V16M20,18V6C20,4.89 19.1,4 18,4H6A2,2 0 0,0 4,6V9H6V6H18V18H6V15H4V18A2,2 0 0,0 6,20H18A2,2 0 0,0 20,18Z";
var mdiInformationOutline = "M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z";
var mdiMagnify = "M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z";
var mdiPaletteOutline = "M12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2C17.5,2 22,6 22,11A6,6 0 0,1 16,17H14.2C13.9,17 13.7,17.2 13.7,17.5C13.7,17.6 13.8,17.7 13.8,17.8C14.2,18.3 14.4,18.9 14.4,19.5C14.5,20.9 13.4,22 12,22M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C12.3,20 12.5,19.8 12.5,19.5C12.5,19.3 12.4,19.2 12.4,19.1C12,18.6 11.8,18.1 11.8,17.5C11.8,16.1 12.9,15 14.3,15H16A4,4 0 0,0 20,11C20,7.1 16.4,4 12,4M6.5,10C7.3,10 8,10.7 8,11.5C8,12.3 7.3,13 6.5,13C5.7,13 5,12.3 5,11.5C5,10.7 5.7,10 6.5,10M9.5,6C10.3,6 11,6.7 11,7.5C11,8.3 10.3,9 9.5,9C8.7,9 8,8.3 8,7.5C8,6.7 8.7,6 9.5,6M14.5,6C15.3,6 16,6.7 16,7.5C16,8.3 15.3,9 14.5,9C13.7,9 13,8.3 13,7.5C13,6.7 13.7,6 14.5,6M17.5,10C18.3,10 19,10.7 19,11.5C19,12.3 18.3,13 17.5,13C16.7,13 16,12.3 16,11.5C16,10.7 16.7,10 17.5,10Z";
var mdiPlus = "M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z";
var mdiShuffleVariant = "M17,3L22.25,7.5L17,12L22.25,16.5L17,21V18H14.26L11.44,15.18L13.56,13.06L15.5,15H17V12L17,9H15.5L6.5,18H2V15H5.26L14.26,6H17V3M2,6H6.5L9.32,8.82L7.2,10.94L5.26,9H2V6Z";
var mdiTable = "M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z";
var mdiTrashCan = "M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M9,8H11V17H9V8M13,8H15V17H13V8Z";
var mdiTuneVariant = "M8 13C6.14 13 4.59 14.28 4.14 16H2V18H4.14C4.59 19.72 6.14 21 8 21S11.41 19.72 11.86 18H22V16H11.86C11.41 14.28 9.86 13 8 13M8 19C6.9 19 6 18.1 6 17C6 15.9 6.9 15 8 15S10 15.9 10 17C10 18.1 9.1 19 8 19M19.86 6C19.41 4.28 17.86 3 16 3S12.59 4.28 12.14 6H2V8H12.14C12.59 9.72 14.14 11 16 11S19.41 9.72 19.86 8H22V6H19.86M16 9C14.9 9 14 8.1 14 7C14 5.9 14.9 5 16 5S18 5.9 18 7C18 8.1 17.1 9 16 9Z";

var script$E = {
  name: 'TDataTableHeaderCell',

  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TTooltip: __vue_component__$Z,
  },

  props: {
    header: {
      type: Object,
      required: true,
    },
  },

  setup(props, { emit }) {
    const table = inject$1('table');
    const state = inject$1('table.state');

    const root = ref(null);
    const stickyMaxOffset = toRef(props.header, 'stickyMaxOffset');

    const sorted = computed(() => table.sort === props.header.propKey);

    const sortable = computed(() => {
      if (props.header.sortable) {
        return sorted.value && table.sortOrder === 'asc'
          ? mdiArrowUp
          : mdiArrowDown;
      }

      return null;
    });

    if (props.header.stickyTo === 'left') {
      watchEffect(
        () => {
          const localHeaderKeyId = props.header.keyId;

          let offset = 0;
          let margin = 0;
          let found = false;

          for (const header of state.headers) {
            if (localHeaderKeyId === header.keyId) {
              header.stickyMargin = margin;

              found = true;

              continue;
            }

            const width = parseInt(header.width, 10);

            if (header.stickyTo === 'left') {
              if (found) {
                break;
              }

              margin += width;
            }

            offset += width;
          }

          stickyMaxOffset.value = offset;
        },
        {
          immediate: true,
        },
      );
    }

    return {
      root,
      sorted,
      sortable,
      mdiHelpCircleOutline,

      style: computed(() => {
        const margin = `${props.header.stickyMargin}px`;

        if (props.header.stickyTo === 'right') {
          return {
            zIndex: 40,
            right: margin,
          };
        }

        if (props.header.stickyTo === 'left') {
          return {
            zIndex: 30,
            left: margin,
          };
        }

        return null;
      }),

      handleClick(action) {
        if (props.header.sortable) {
          emit('click', action, props.header.propKey);
        }
      },
    };
  },
};

/* script */
const __vue_script__$E = script$E;

/* template */
var __vue_render__$H = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('th',{staticClass:"\n    t-data-table__header-cell\n    t-z-20\n    t-top-0\n    t-sticky\n    t-text-default\n    t-align-middle\n    t-font-normal\n    t-bg-white\n    t-relative\n    t-whitespace-nowrap\n    t-relative\n    t-group\n    t-text-text-01\n    t-h-9\n    t-px-3\n  ",class:[
    _vm.header.align,
    { 't-cursor-pointer': _vm.sortable, 't-is-fixed': !_vm.header.draggable } ],style:(_vm.style),attrs:{"width":_vm.header.width},on:{"click":function($event){return _vm.handleClick('sort')}}},[_c('span',{staticClass:"t-h-px t-bg-bg t--bottom-px t-absolute t--left-px t-z-30 t-right-0"}),_vm._v(" "),_vm._t("default",function(){return [_c('div',{staticClass:"t-flex t-items-center"},[_c('span',{staticClass:"t-truncate t-w-full"},[_vm._v("\n        "+_vm._s(_vm.header.text)+"\n      ")]),_vm._v(" "),(_vm.header.tooltip)?_c('TTooltip',{attrs:{"bottom":"","offset-y":"","allow-overflow":""},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
  var on = ref.on;
return [_c('TBtn',_vm._g({staticClass:"t-ml-1",attrs:{"icon":"","x-small":"","color":"grey darken-2"}},on),[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n              "+_vm._s(_vm.mdiHelpCircleOutline)+"\n            ")])],1)]}}],null,false,4042483397)},[_vm._v(" "),_c('span',{domProps:{"textContent":_vm._s(_vm.header.tooltip)}})]):_vm._e(),_vm._v(" "),(_vm.sorted)?_c('TBtn',{attrs:{"icon":"","x-small":"","color":"grey darken-2"},on:{"click":function($event){$event.stopPropagation();return _vm.handleClick('sort')}}},[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n          "+_vm._s(_vm.sortable)+"\n        ")])],1):_vm._e()],1)]})],2)};
var __vue_staticRenderFns__$H = [];

  /* style */
  const __vue_inject_styles__$H = undefined;
  /* scoped */
  const __vue_scope_id__$H = undefined;
  /* module identifier */
  const __vue_module_identifier__$H = undefined;
  /* functional template */
  const __vue_is_functional_template__$H = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$H = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$H, staticRenderFns: __vue_staticRenderFns__$H },
    __vue_inject_styles__$H,
    __vue_script__$E,
    __vue_scope_id__$H,
    __vue_is_functional_template__$H,
    __vue_module_identifier__$H,
    false,
    undefined,
    undefined,
    undefined
  );

var script$D = {
  components: {
    TDataTableBodyCell: __vue_component__$I,
  },
  inheritAttrs: false,
  props: __vue_component__$I.props,
};

/* script */
const __vue_script__$D = script$D;

/* template */
var __vue_render__$G = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TDataTableBodyCell',_vm._b({attrs:{"empty":""}},'TDataTableBodyCell',_vm.$props,false),[_vm._t("default")],2)};
var __vue_staticRenderFns__$G = [];

  /* style */
  const __vue_inject_styles__$G = undefined;
  /* scoped */
  const __vue_scope_id__$G = undefined;
  /* module identifier */
  const __vue_module_identifier__$G = undefined;
  /* functional template */
  const __vue_is_functional_template__$G = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$G = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$G, staticRenderFns: __vue_staticRenderFns__$G },
    __vue_inject_styles__$G,
    __vue_script__$D,
    __vue_scope_id__$G,
    __vue_is_functional_template__$G,
    __vue_module_identifier__$G,
    false,
    undefined,
    undefined,
    undefined
  );

var script$C = {
  components: {
    TCheckbox: VCheckbox,
    TDataTableBodyCell: __vue_component__$I,
  },
  inheritAttrs: false,

  props: __vue_component__$I.props,

  setup(props) {
    const table = inject$1('table');
    const key = computed(() => table.handleSelectItem(props.item));
    const selected = computed(() => table.selected[unref(key)] === true);

    const handleToggle = () => {
      const localKey = unref(key);
      const isActive = table.selected[localKey];

      if (isActive) {
        del(table.selected, localKey);

        return;
      }

      set(table.selected, localKey, true);
    };

    return {
      selected,
      handleToggle,
      disabled: !unref(key),
    };
  },
};

/* script */
const __vue_script__$C = script$C;

/* template */
var __vue_render__$F = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TDataTableBodyCell',_vm._b({},'TDataTableBodyCell',_vm.$props,false),[_c('TCheckbox',{staticClass:"t-mt-0 t-mx-auto t-pt-0 t-w-6",attrs:{"dense":"","hide-details":"","disabled":_vm.disabled,"input-value":_vm.selected},on:{"change":_vm.handleToggle}})],1)};
var __vue_staticRenderFns__$F = [];

  /* style */
  const __vue_inject_styles__$F = undefined;
  /* scoped */
  const __vue_scope_id__$F = undefined;
  /* module identifier */
  const __vue_module_identifier__$F = undefined;
  /* functional template */
  const __vue_is_functional_template__$F = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$F = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$F, staticRenderFns: __vue_staticRenderFns__$F },
    __vue_inject_styles__$F,
    __vue_script__$C,
    __vue_scope_id__$F,
    __vue_is_functional_template__$F,
    __vue_module_identifier__$F,
    false,
    undefined,
    undefined,
    undefined
  );

var script$B = {
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TSwitch: VSwitch,
    TTruncateText: __vue_component__$J,
  },

  props: {
    header: {
      type: Object,
      required: true,
    },
  },

  setup() {
    return {
      IconDrag: mdiDragHorizontal,
    };
  },
};

/* script */
const __vue_script__$B = script$B;

/* template */
var __vue_render__$E = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('li',{staticClass:"t-flex hover:t-bg-bg--hover t-w-64 t-py-2 t-px-3 t-items-center",class:{ 't-is-fixed': !_vm.header.draggable }},[_c('TBtn',{staticClass:"t-is-handle t-mr-2 t-cursor-move t-transition-250",attrs:{"icon":"","x-small":"","color":"grey darken-2","disabled":!_vm.header.draggable},on:{"click":function($event){$event.preventDefault();}}},[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n      "+_vm._s(_vm.IconDrag)+"\n    ")])],1),_vm._v(" "),_c('TTruncateText',{attrs:{"text":_vm.header.label || _vm.header.text,"custom-class":"t-text-default t-text-current t-mr-auto"}}),_vm._v(" "),_c('TSwitch',{staticClass:"t-ml-6 t-p-0 t-mt-0",attrs:{"dense":"","color":"primary","hide-details":"","input-value":_vm.header.visible},on:{"change":function($event){return _vm.$emit('change', $event)}}})],1)};
var __vue_staticRenderFns__$E = [];

  /* style */
  const __vue_inject_styles__$E = undefined;
  /* scoped */
  const __vue_scope_id__$E = undefined;
  /* module identifier */
  const __vue_module_identifier__$E = undefined;
  /* functional template */
  const __vue_is_functional_template__$E = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$E = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$E, staticRenderFns: __vue_staticRenderFns__$E },
    __vue_inject_styles__$E,
    __vue_script__$B,
    __vue_scope_id__$E,
    __vue_is_functional_template__$E,
    __vue_module_identifier__$E,
    false,
    undefined,
    undefined,
    undefined
  );

var script$A = {
  components: {
    Draggable,
    TDataTableHeaderOptionsItem: __vue_component__$E,
  },
  inheritAttrs: false,

  model: {
    prop: 'headers',
  },

  props: {
    headers: {
      type: Array,
      required: true,
    },

    limit: {
      type: Number,
      required: false,
      default: -1,
    },

    skip: {
      type: Number,
      required: false,
      default: 0,
    },
  },

  setup(props) {
    const list = computed(() => props.headers.slice(props.skip, props.limit));

    return {
      list,
    };
  },
};

/* script */
const __vue_script__$A = script$A;

/* template */
var __vue_render__$D = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('Draggable',_vm._g(_vm._b({staticClass:"t-bg-white t-overflow-y-auto t-max-h-96",staticStyle:{"padding":"0"},attrs:{"tag":"ol","handle":".t-is-handle","ghost-class":"t-bg-bg","group":"table.headers","value":_vm.list}},'Draggable',_vm.$attrs,false),_vm.$listeners),_vm._l((_vm.list),function(header){return _c('TDataTableHeaderOptionsItem',{key:header.keyId,staticClass:"t-flex hover:t-bg-bg--hover t-py-2 t-px-3 t-items-center",attrs:{"header":header},on:{"change":function($event){return _vm.$emit('change', header)}}})}),1)};
var __vue_staticRenderFns__$D = [];

  /* style */
  const __vue_inject_styles__$D = undefined;
  /* scoped */
  const __vue_scope_id__$D = undefined;
  /* module identifier */
  const __vue_module_identifier__$D = undefined;
  /* functional template */
  const __vue_is_functional_template__$D = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$D = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$D, staticRenderFns: __vue_staticRenderFns__$D },
    __vue_inject_styles__$D,
    __vue_script__$A,
    __vue_scope_id__$D,
    __vue_is_functional_template__$D,
    __vue_module_identifier__$D,
    false,
    undefined,
    undefined,
    undefined
  );

var script$z = {
  components: {
    TBtn: __vue_component__$_,
    TMenu: VMenu,
    TIcon: VIcon$1,
    TDataTableHeaderCell: __vue_component__$H,
    TDataTableHeaderOptionsList: __vue_component__$D,
  },
  inheritAttrs: false,

  setup() {
    const table = inject$1('table');
    const state = inject$1('table.state');

    const filtered = computed(() =>
      table.headers.filter((header) => !header.state.protected),
    );

    const handleHeaderOrder = (headers, skip = 0) => {
      state.emit(
        'headers:order',
        headers.map(({ keyId }) => keyId),
        skip,
      );
    };

    const handleResetSettings = () => {
      state.emit('headers:reset');
    };

    return {
      filtered,
      limit: 8,
      IconOptions: mdiTuneVariant,
      handleHeaderOrder,
      handleResetSettings,

      handleHeaderToggle(header) {
        raf(() => {
          header.visible = !header.visible;
        });
      },
    };
  },
};

/* script */
const __vue_script__$z = script$z;

/* template */
var __vue_render__$C = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TDataTableHeaderCell',_vm._g(_vm._b({staticClass:"t-data-table-header__options"},'TDataTableHeaderCell',_vm.$attrs,false),_vm.$listeners),[_c('TMenu',{attrs:{"left":"","offset-y":"","transition":"slide-y-transition","close-on-content-click":false,"content-class":"t-data-table-header__options"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
return [_c('TBtn',_vm._g({staticClass:"t-is-handle",attrs:{"icon":"","x-small":"","color":"grey darken-2"}},on),[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n          "+_vm._s(_vm.IconOptions)+"\n        ")])],1)]}}])},[_vm._v(" "),_c('div',{staticClass:"t-bg-white t-text-left t-text-text-01"},[_c('h3',{staticClass:"t-text-current t-text-base t-font-bold t-px-3 t-pt-4 t-pb-2"},[_vm._v("\n        Column view\n      ")]),_vm._v(" "),_c('TDataTableHeaderOptionsList',{staticClass:"t-data-table-header__options",attrs:{"limit":_vm.limit,"headers":_vm.filtered},on:{"input":function($event){return _vm.handleHeaderOrder($event)},"change":_vm.handleHeaderToggle}}),_vm._v(" "),(_vm.filtered.length > _vm.limit)?_c('TMenu',{attrs:{"top":"","left":"","offset-x":"","nudge-right":"-8","transition":"slide-x-reverse-transition","close-on-content-click":false,"close-delay":"15000","open-on-hover":true,"close-on-click":false,"content-class":"t-data-table-header__options"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
return [_c('button',_vm._g({staticClass:"\n              t-w-full t-py-2 t-text-default t-text-text-01 t-text-center\n              hover:t-bg-bg--hover\n            "},on),[_vm._v("\n            View more\n            "),_c('span',{staticClass:"t-text-xs"},[_vm._v("(+"+_vm._s(_vm.filtered.length - _vm.limit)+")")])])]}}],null,false,908253744)},[_vm._v(" "),_c('div',{staticClass:"t-bg-white t-text-left t-text-text-01"},[_c('TDataTableHeaderOptionsList',{attrs:{"skip":_vm.limit,"limit":_vm.filtered.length,"headers":_vm.filtered},on:{"input":function($event){return _vm.handleHeaderOrder($event, _vm.limit)},"change":_vm.handleHeaderToggle}}),_vm._v(" "),_c('button',{staticClass:"\n              t-w-full t-py-2 t-text-default t-text-text-01 t-text-center\n              hover:t-bg-bg--hover\n            ",on:{"click":_vm.handleResetSettings}},[_vm._v("\n            Reset settings\n          ")])],1)]):_vm._e()],1)])],1)};
var __vue_staticRenderFns__$C = [];

  /* style */
  const __vue_inject_styles__$C = undefined;
  /* scoped */
  const __vue_scope_id__$C = undefined;
  /* module identifier */
  const __vue_module_identifier__$C = undefined;
  /* functional template */
  const __vue_is_functional_template__$C = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$C = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$C, staticRenderFns: __vue_staticRenderFns__$C },
    __vue_inject_styles__$C,
    __vue_script__$z,
    __vue_scope_id__$C,
    __vue_is_functional_template__$C,
    __vue_module_identifier__$C,
    false,
    undefined,
    undefined,
    undefined
  );

var script$y = {
  components: {
    TCheckbox: VCheckbox,
    TDataTableHeaderCell: __vue_component__$H,
  },
  inheritAttrs: false,

  setup() {
    const table = inject$1('table');

    const handleToggle = () => {
      const availableItems = table.items.filter(
        (item) => table.handleSelectItem(item) !== null,
      );

      if (availableItems.length > table.selectedEntries.length) {
        table.handleSelectAll();

        return;
      }

      table.selected = {};
    };

    return {
      table,
      handleToggle,
    };
  },
};

/* script */
const __vue_script__$y = script$y;

/* template */
var __vue_render__$B = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TDataTableHeaderCell',_vm._g(_vm._b({},'TDataTableHeaderCell',_vm.$attrs,false),_vm.$listeners),[_c('TCheckbox',{staticClass:"t-mt-0 t-mx-auto t-pt-0 t-w-6",attrs:{"dense":"","hide-details":"","value":_vm.table.selectedStatus === 'all'},on:{"change":_vm.handleToggle}})],1)};
var __vue_staticRenderFns__$B = [];

  /* style */
  const __vue_inject_styles__$B = undefined;
  /* scoped */
  const __vue_scope_id__$B = undefined;
  /* module identifier */
  const __vue_module_identifier__$B = undefined;
  /* functional template */
  const __vue_is_functional_template__$B = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$B = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$B, staticRenderFns: __vue_staticRenderFns__$B },
    __vue_inject_styles__$B,
    __vue_script__$y,
    __vue_scope_id__$B,
    __vue_is_functional_template__$B,
    __vue_module_identifier__$B,
    false,
    undefined,
    undefined,
    undefined
  );

/* eslint-disable no-param-reassign */

const TableHeaderAlignments = readonly({
  left: 't-text-left',
  right: 't-text-right',
  center: 't-text-center',
});

const Status = readonly({
  IDLE: 'idle',
  DONE: 'done',
  FETCHED: 'fetched',
  FETCHING: 'fetching',
  REFETCHING: 'refetching',
});

const useTableItems = (state, { props }) => {
  props.items = ref(props.items || []);
  props.status = ref(props.status || Status.IDLE);

  state.items = props.items;
  state.status = props.status;
};

/**
 * @param {object|string} header - required parameter of type
 * - object to be merged with default header options
 * -- it must have at least the `keyId` string property
 * - string to be used as `keyId`
 */
const useTableHeader = (header, index) => {
  const state = reactive(
    typeof header === 'string' ? { keyId: header } : header,
  );

  const {
    keyId,
    text = null,
    link = null,
    label = null,
    order = index,
    align = 'left',
    stickyTo = null,
    propKey = keyId,
    draggable = true,
    tooltip = ref(''),
    visible = ref(true),
    width = ref('180px'),
    sticked = ref(false),
    stickyMargin = ref(0),
    stickyMaxOffset = ref(0),
    component = __vue_component__$H,
    itemComponent = __vue_component__$I,
  } = toRefs(state);

  return reactive({
    link,
    state,
    keyId,
    width,
    tooltip,
    propKey,
    visible,
    stickyTo,
    sticked,
    draggable,
    component,
    stickyMargin,
    stickyMaxOffset,
    itemComponent,

    el: ref(null),
    order: ref(order),

    align: TableHeaderAlignments[align],

    text: text || keyId.value,
    label: label || text || keyId.value,
  });
};

/**
 * Table headers plugin.
 */
const useTableHeaders = (state, { props, listeners }) => {
  const handleSortHeaders = (a, b) => a.order - b.order;

  props.headers = (props.headers || []).map(useTableHeader);

  /**
   * Store default state of headers.
   */
  const defs = props.headers.reduce(
    (target, { keyId, order, width, visible }) => {
      target[keyId] = { keyId, order, width, visible };

      return target;
    },
    {},
  );

  const sorted = computed(() => props.headers.sort(handleSortHeaders));

  state.headers = sorted;

  /**
   * Listen headers order event.
   */
  listeners['headers:order'] = (keys) =>
    raf(() => {
      sorted.value.forEach((header) => {
        const index = keys.indexOf(header.keyId);

        if (index === -1) {
          return;
        }

        set(header, 'order', index);
      });
    });

  /**
   * Listen to headers hard reset settings.
   */
  listeners['headers:reset'] = () => {
    props.headers.forEach((header) => {
      const { keyId, order, width, visible } = defs[header.keyId] || {};

      if (keyId !== header.keyId) {
        return;
      }

      header.order = order;
      header.width = width;
      header.visible = visible;
    });
  };
};

const useTableSortableHeader = (state, { props, listeners }) => {
  props.sort = ref(props.sort || '');
  props.sortOrder = ref(props.sortOrder || 'asc');

  /**
   * Sync sort props with fetcher params if available.
   */
  if (props.fetcher) {
    set(props.fetcher.state.params, 'sort', props.sort);
    set(props.fetcher.state.params, 'sortOrder', props.sortOrder);
  }

  /**
   * Add sortable option to each header.
   */
  props.headers.forEach((header) => {
    set(header, 'sortable', header.state.sortable || false);
  });

  const sortables = props.headers.filter((header) => header.sortable);

  /**
   * List visible headers only.
   */
  props.sortables = computed(() =>
    sortables.filter((header) => header.visible),
  );

  /**
   * Listen sortable action click.
   */
  listeners['sort:click'] = (sort) =>
    raf(() => {
      if (sort === props.sort.value) {
        props.sortOrder.value =
          props.sortOrder.value === 'desc' ? 'asc' : 'desc';

        return;
      }

      props.sort.value = sort;
    });
};

const useTableHeaderCheckbox = (state, { props }) => {
  props.selected = ref(props.selected || {});
  props.canSelectRows = ref(props.canSelectRows || false);
  props.selectedEntries = computed(() => Object.keys(props.selected.value));

  props.handleSelectItem = props.handleSelectItem
    ? props.handleSelectItem
    : ({ id }) => id;

  props.selectedStatus = computed(() => {
    const selected = props.selectedEntries.value.length;
    const available = state.items.filter(
      (item) => props.handleSelectItem(item) !== null,
    ).length;

    if (selected === 0) {
      return '';
    }

    if (available > selected) {
      return 'indeterminate';
    }

    return 'all';
  });

  props.handleSelectAll = () => {
    const availableItems = state.items.filter(
      (item) => props.handleSelectItem(item) !== null,
    );

    props.selected.value = availableItems.reduce((selected, item) => {
      const key = props.handleSelectItem(item);

      if (key !== null) {
        selected[key] = true;
      }

      return selected;
    }, {});
  };

  const checkbox = useTableHeader({
    keyId: 'checkbox',

    width: '48px',
    order: -1, // first
    stickyTo: 'left',
    align: 'center',
    protected: true,
    draggable: false,
    visible: props.canSelectRows,
    component: __vue_component__$B,
    itemComponent: __vue_component__$F,
  });

  props.headers.unshift(checkbox);

  /**
   * Watch the table fetcher params and clear selected users when the data is stale / scroll to top.
   * Setup watch on next tick to handle overwrites.
   */
  if (props.fetcher) {
    nextTick(() => {
      watch(
        () => props.fetcher.params,
        () => {
          until(props.items)
            .changed()
            .then(() => {
              document
                .querySelector('.t-data-table__wrapper')
                ?.scrollTo?.(0, 0);
            });

          props.selected.value = [];
        },
        {
          deep: true,
        },
      );
    });
  }
};

const useTableHeaderOptions = (state, { props }) => {
  props.headers.push(
    useTableHeader({
      keyId: 'options',

      width: '48px',
      order: 999, // last
      protected: true,
      draggable: false,
      align: 'center',
      stickyTo: 'right',

      /**
       * Custom options header component.
       */
      component: __vue_component__$C,
      itemComponent: props.optionsComponent || __vue_component__$G,
    }),
  );

  if (!props.dataview) {
    return;
  }

  /**
   * Update local settings with dataview options.
   */
  const {
    displaySettings: { properties },
  } = props.dataview;

  properties.forEach(({ key, options }, index) => {
    const header = props.headers.find(({ keyId }) => keyId === key);

    /**
     * Bail if header is not available anymore.
     */
    if (!header) {
      return;
    }

    if (header.draggable) {
      header.order =
        typeof options.order === 'undefined' ? index : Number(options.order);
    }

    header.visible = options.visible === 'YES';
  });

  /**
   * Sync dataview settings with header options.
   */
  debouncedWatch(
    () => props.headers,
    (headers) => {
      props.dataview.displaySettings = {
        properties: headers.map(({ keyId, propKey, order, visible }) => ({
          key: keyId,
          options: {
            order,
            visible,
          },
          property: propKey,
        })),
      };
    },
    {
      deep: true,
      debounce: 1000,
    },
  );
};

var script$x = {
  components: {
    TBtn: __vue_component__$_,
  },

  setup() {
    const state = inject$1('table.state');

    const fetched = computed(() => state.status === Status.FETCHED);
    const show = computed(
      () =>
        state.status === Status.FETCHED || state.status === Status.REFETCHING,
    );

    const handleLoadMore = () => {
      state.emit('paginate');
    };

    const footer = useIntersectionRef(
      ([{ isIntersecting }]) => {
        /**
         * We can't paginate if current state is not fetched.
         */
        if (!isIntersecting || !fetched.value) {
          return;
        }

        handleLoadMore();
      },
      {
        threshold: [0.01, 0.9],
      },
    );

    return {
      show,
      footer,
      fetched,

      handleLoadMore,
    };
  },
};

/* script */
const __vue_script__$x = script$x;

/* template */
var __vue_render__$A = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.show)?_c('footer',{staticClass:"t-py-3 t-px-4 t-text-center t-sticky t-right-0 t-left-0"},[(_vm.fetched)?[_c('span',{ref:"footer",staticClass:"t-absolute t-w-4 t-bottom-0 t-pointer-events-none",staticStyle:{"height":"2700px"}}),_vm._v(" "),_c('TBtn',{on:{"click":function($event){$event.preventDefault();return _vm.handleLoadMore.apply(null, arguments)}}},[_vm._v("\n      "+_vm._s('Load more')+"\n    ")])]:_vm._e()],2):_vm._e()};
var __vue_staticRenderFns__$A = [];

  /* style */
  const __vue_inject_styles__$A = undefined;
  /* scoped */
  const __vue_scope_id__$A = undefined;
  /* module identifier */
  const __vue_module_identifier__$A = undefined;
  /* functional template */
  const __vue_is_functional_template__$A = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$A = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$A, staticRenderFns: __vue_staticRenderFns__$A },
    __vue_inject_styles__$A,
    __vue_script__$x,
    __vue_scope_id__$A,
    __vue_is_functional_template__$A,
    __vue_module_identifier__$A,
    false,
    undefined,
    undefined,
    undefined
  );

var script$w = {
  components: {
    Draggable,
  },

  setup() {
    const state = inject$1('table.state');

    return {
      state,

      handleHeaderClick(action, value) {
        state.emit(`${action}:click`, value);
      },

      handleHeaderOrder(headers) {
        state.emit(
          'headers:order',
          headers.map((header) => header.keyId),
        );
      },
    };
  },
};

/* script */
const __vue_script__$w = script$w;

/* template */
var __vue_render__$z = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('thead',[_c('Draggable',{attrs:{"direction":"horizontal","tag":"tr","filter":".t-is-fixed","drag-class":"t-is-drag","ghost-class":"t-is-ghost","chosen-class":"t-is-chosen","value":_vm.state.headers},on:{"input":_vm.handleHeaderOrder}},_vm._l((_vm.state.headers),function(header){return _c(header.component,{key:header.keyId,tag:"Component",attrs:{"header":header},on:{"click":_vm.handleHeaderClick}})}),1)],1)};
var __vue_staticRenderFns__$z = [];

  /* style */
  const __vue_inject_styles__$z = undefined;
  /* scoped */
  const __vue_scope_id__$z = undefined;
  /* module identifier */
  const __vue_module_identifier__$z = undefined;
  /* functional template */
  const __vue_is_functional_template__$z = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$z = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$z, staticRenderFns: __vue_staticRenderFns__$z },
    __vue_inject_styles__$z,
    __vue_script__$w,
    __vue_scope_id__$z,
    __vue_is_functional_template__$z,
    __vue_module_identifier__$z,
    false,
    undefined,
    undefined,
    undefined
  );

/**
 * Utility that handles a lot of table features.
 *
 * @returns a reactive object that contains:
 *
 * - table.state: table props to be used with v-bind.
 * - table.listeners: table listeners to be used with v-on.
 * - table.*: utility state, contains user defined values with `useTable` plugins custom state, like `sort` and `sortOrder` from `useTableSortableHeader`.
 */
const useTable = ({ listeners = {}, ...props }, plugins = []) => {
  const state = {};

  /**
   * Install each plugin.
   * Uses `useTableHeaders` and `useTableItems` by default.
   */
  [useTableHeaders, useTableItems, ...plugins].forEach((plugin) => {
    plugin(state, { props, listeners });
  });

  const table = reactive({
    state,
    listeners,

    ...props,
  });

  provide$1('table', table);

  return table;
};

const usePaginatedTable = (state, { props, listeners }) => {
  const { items, status, nextPage } = props.fetcher;

  if (props.params) {
    Object.entries(props.params).forEach(([key, value]) => {
      set(props.fetcher.state.params, key, value);
    });
  }

  props.params = props.fetcher.state.params;
  props.items = state.items = items;
  props.status = state.status = status;

  listeners.paginate = nextPage;
};

var script$v = {
  name: 'TDataTable',

  components: {
    TDataTableBody: __vue_component__$K,
    TDataTableFooter: __vue_component__$A,
    TDataTableHeader: __vue_component__$z,
  },

  props: {
    /**
     * The array of items to display.
     */
    items: {
      type: Array,
      required: true,
    },

    /**
     * An array of objects that each describe a header column.
     */
    headers: {
      type: Array,
      required: true,
    },

    /**
     * Table status.
     * Current instance status.
     */
    status: {
      type: String,
      required: false,
      default: () => Status.IDLE,
    },
  },

  setup(props, { emit }) {
    const el = ref(null);

    const headers = computed(() =>
      props.headers.filter((header) => header.visible),
    );

    const items = computed(() => {
      if (props.status === Status.FETCHING) {
        return [];
      }

      return props.items;
    });

    const isLoading = computed(() =>
      [Status.FETCHING, Status.REFETCHING].includes(props.status),
    );

    const state = reactive({
      el,
      emit,
      items,
      headers,
      isLoading,
      status: toRef(props, 'status'),
    });

    provide$1('table.state', state);

    return {
      el,
      state,
    };
  },
};

/* script */
const __vue_script__$v = script$v;

/* template */
var __vue_render__$y = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('section',{staticClass:"\n    t-relative\n    t-flex\n    t-flex-col\n    t-w-full\n    t-h-full\n    t-overflow-y-hidden\n    t-flex-1\n  "},[_c('div',{ref:"el",staticClass:"\n      t-data-table__wrapper\n      t-flex-grow t-overflow-y-auto t-h-full t-w-full\n    "},[_c('table',{staticClass:"\n        t-relative\n        t-table-fixed\n        t-overflow-x-auto\n        t-max-w-full\n        t-w-full\n        t-border-collapse\n        t-text-default\n        t-text-text-01\n      ",staticStyle:{"border-spacing":"0"}},[_c('TDataTableHeader'),_vm._v(" "),_c('TDataTableBody')],1),_vm._v(" "),_c('TDataTableFooter')],1)])};
var __vue_staticRenderFns__$y = [];

  /* style */
  const __vue_inject_styles__$y = undefined;
  /* scoped */
  const __vue_scope_id__$y = undefined;
  /* module identifier */
  const __vue_module_identifier__$y = undefined;
  /* functional template */
  const __vue_is_functional_template__$y = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$y = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$y, staticRenderFns: __vue_staticRenderFns__$y },
    __vue_inject_styles__$y,
    __vue_script__$v,
    __vue_scope_id__$y,
    __vue_is_functional_template__$y,
    __vue_module_identifier__$y,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$u = {
  name: 'TIconBtn',
  components: { TBtn: __vue_component__$_, TIcon: VIcon$1 },
  props: {
    icon: {
      type: String,
      required: false,
      default: null,
    },
    href: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: 'button',
    },
    xSmall: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    large: {
      type: Boolean,
      default: false,
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    text: {
      type: Boolean,
      default: false,
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    fab: {
      type: Boolean,
      default: false,
    },
  },
};

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$x = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TBtn',_vm._g(_vm._b({attrs:{"icon":true}},'TBtn',_vm.$props,false),_vm.$listeners),[(_vm.icon)?_c('TIcon',{attrs:{"small":_vm.small,"x-small":_vm.xSmall}},[_vm._v("\n    "+_vm._s(_vm.icon)+"\n  ")]):_vm._t("default")],2)};
var __vue_staticRenderFns__$x = [];

  /* style */
  const __vue_inject_styles__$x = function (inject) {
    if (!inject) return
    inject("data-v-71577453_0", { source: ".t-btn.v-btn{letter-spacing:.3px;text-transform:none}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$x = undefined;
  /* module identifier */
  const __vue_module_identifier__$x = undefined;
  /* functional template */
  const __vue_is_functional_template__$x = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$x = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$x, staticRenderFns: __vue_staticRenderFns__$x },
    __vue_inject_styles__$x,
    __vue_script__$u,
    __vue_scope_id__$x,
    __vue_is_functional_template__$x,
    __vue_module_identifier__$x,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//

var script$t = {
  name: 'THeader',
};

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$w = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('header',{staticClass:"t-p-2 t-text-white t-text-lg t-header"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$w = [];

  /* style */
  const __vue_inject_styles__$w = undefined;
  /* scoped */
  const __vue_scope_id__$w = undefined;
  /* module identifier */
  const __vue_module_identifier__$w = undefined;
  /* functional template */
  const __vue_is_functional_template__$w = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$w = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$w, staticRenderFns: __vue_staticRenderFns__$w },
    __vue_inject_styles__$w,
    __vue_script__$t,
    __vue_scope_id__$w,
    __vue_is_functional_template__$w,
    __vue_module_identifier__$w,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$s = {
  name: 'BlankSlate',
  props: {
    isInPopover: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'No Matching data',
    },
    msg: {
      type: String,
      default: '',
    },
    msg2: {
      type: String,
      default: '',
    },
    hasActiveFilter: {
      type: Boolean,
      default: false,
    },
    allowClearFilter: {
      type: Boolean,
      default: false,
    },
  },
};

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$v = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"\n    blank-slate\n    t-w-full t-flex t-flex-col t-items-center t-justify-center t-text-center\n  "},[_c('div',{staticClass:"blank-slate__holder"},[(_vm.$slots.image)?_c('div',{staticClass:"t-flex t-items-center t-justify-center t-mb-6"},[_vm._t("image")],2):_vm._e(),_vm._v(" "),(_vm.$slots.icon)?_c('div',{staticClass:"t-text-8xl t-mb-4"},[_vm._t("icon")],2):_vm._e(),_vm._v(" "),_c('div',{staticClass:"t-text-center"},[(_vm.title)?_c('h3',{staticClass:"t-text-md t-font-semibold t-mb-2"},[_vm._v("\n        "+_vm._s(_vm.title)+"\n      ")]):_vm._e(),_vm._v(" "),_c('p',[(_vm.hasActiveFilter && _vm.allowClearFilter)?_c('span',[_c('a',{attrs:{"href":"#"},on:{"click":function($event){return _vm.$emit('clear-filters', $event)}}},[_vm._v("\n            "+_vm._s(_vm.$t('Clear the filter'))+"\n          ")])]):_vm._e(),_vm._v(" "),(!_vm.hasActiveFilter)?_c('span',[(_vm.msg)?_c('span',{staticClass:"t-block t-pt-2 t-text-default t-leading-tight t-mb-6"},[_vm._v(_vm._s(_vm.msg))]):_vm._e(),_vm._v(" "),(_vm.msg2 && _vm.$slots.action)?_c('span',[_c('span',[_vm._v(_vm._s(_vm.msg2))])]):_vm._e()]):_vm._e()])]),_vm._v(" "),(_vm.$slots.action)?_c('div',{staticClass:"blank-slate__action t-mb-2"},[_vm._t("action")],2):_vm._e(),_vm._v(" "),(_vm.$slots.helpDocsLink)?_c('div',{staticClass:"blank-slate__help-docs t-mb-2"},[_vm._t("helpDocsLink")],2):_vm._e()])])};
var __vue_staticRenderFns__$v = [];

  /* style */
  const __vue_inject_styles__$v = function (inject) {
    if (!inject) return
    inject("data-v-2670ef07_0", { source: ".blank-slate[data-v-2670ef07]{background-repeat:no-repeat;background-position:center top;text-align:center}.blank-slate__holder[data-v-2670ef07]{max-width:440px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$v = "data-v-2670ef07";
  /* module identifier */
  const __vue_module_identifier__$v = undefined;
  /* functional template */
  const __vue_is_functional_template__$v = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$v = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$v, staticRenderFns: __vue_staticRenderFns__$v },
    __vue_inject_styles__$v,
    __vue_script__$s,
    __vue_scope_id__$v,
    __vue_is_functional_template__$v,
    __vue_module_identifier__$v,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$r = {
  name: 'TLetterSelect',
  components: { TBtn: __vue_component__$_, TMenu: VMenu, TIcon: VIcon$1 },
  props: {
    letters: {
      type: Array,
      default: () => [],
    },
    letter: {
      type: String,
      default: null,
    },
    text: {
      type: String,
      default: 'Starting with',
    },
    emptyText: {
      type: String,
      default: 'any character',
    },
    clearText: {
      type: String,
      default: 'Clear',
    },
    btnClass: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      selected: false,
      mdiAlphabetical,
      mdiFilter,
    };
  },
  methods: {
    selectLetter(letter) {
      this.selected = letter;
      this.$emit('update:letter', letter);
    },
  },
};

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$u = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"inline-flex"},[_c('t-menu',{attrs:{"offset-y":"","transition":"slide-y-transition"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('t-btn',_vm._g(_vm._b({class:_vm.btnClass,attrs:{"outlined":"","color":"grey"}},'t-btn',attrs,false),on),[(_vm.letter)?_c('span',{staticClass:"\n            t-mr-4 t-cursor-pointer t-text-lg\n            t-text-primary--hover\n            t-font-bold\n          "},[_vm._v(_vm._s(_vm.letter))]):_c('t-icon',{staticClass:"t-text-icons",attrs:{"dense":"","small":""}},[_vm._v("\n          "+_vm._s(_vm.mdiFilter)+"\n        ")]),_vm._v(" "),_c('t-icon',{attrs:{"dense":""}},[_vm._v("\n          "+_vm._s(_vm.mdiAlphabetical)+"\n        ")])],1)]}}])},[_vm._v(" "),_c('div',{staticClass:"\n        t-flex t-flex-wrap t-max-w-sm t-p-2 t-mt-2 t-bg-white t-text-default\n      "},[_vm._l((_vm.letters),function(letterOption){return _c('span',{key:letterOption,staticClass:"\n          t-p-2 t-rounded-full\n          hover:t-bg-bg\n          t-w-10 t-h-10 t-text-center t-cursor-pointer\n        ",on:{"click":function($event){return _vm.selectLetter(letterOption)}}},[_vm._v("\n        "+_vm._s(letterOption)+"\n      ")])}),_vm._v(" "),(_vm.letter)?_c('span',{staticClass:"\n          p-2\n          cursor-pointer\n          rounded-full\n          hover:bg-bg--hover hover:text-icons--hover\n        ",on:{"click":function($event){return _vm.selectLetter(null)}}},[_vm._v("\n        "+_vm._s(_vm.clearText)+"\n      ")]):_vm._e()],2)]),_vm._v(" "),(_vm.letter)?_c('t-btn',{staticClass:"ml-2",class:_vm.btnClass,attrs:{"outlined":"","color":"grey"},on:{"click":function($event){return _vm.selectLetter(null)}}},[_vm._v("\n    "+_vm._s(_vm.clearText)+"\n  ")]):_vm._e()],1)};
var __vue_staticRenderFns__$u = [];

  /* style */
  const __vue_inject_styles__$u = undefined;
  /* scoped */
  const __vue_scope_id__$u = undefined;
  /* module identifier */
  const __vue_module_identifier__$u = undefined;
  /* functional template */
  const __vue_is_functional_template__$u = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$u = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$u, staticRenderFns: __vue_staticRenderFns__$u },
    __vue_inject_styles__$u,
    __vue_script__$r,
    __vue_scope_id__$u,
    __vue_is_functional_template__$u,
    __vue_module_identifier__$u,
    false,
    undefined,
    undefined,
    undefined
  );

/**
 * Do not edit directly
 * Generated on Fri, 19 May 2023 11:22:00 GMT
 */

const TOKEN_UI_WHITE = "#FFFFFF";
const TOKEN_UI_TEXT_01 = "#0B0E1F";
const TOKEN_COLOR_WHITE = "#FFFFFF";
const TOKEN_COLOR_BLACK = "#000000";
const TOKEN_COLOR_NEUTRAL_50 = "#646D7D";
const TOKEN_COLOR_INDIGO_BASE = "#4461D7";
const TOKEN_COLOR_GREY_40 = "#CACDDC";
const TOKEN_COLOR_GREY_BASE = "#CACDDC";
const TOKEN_COLOR_RED_BASE = "#E12D42";
const TOKEN_COLOR_YELLOW_BASE = "#FFC63C";
const TOKEN_COLOR_GREEN_BASE = "#4ECD97";
const TOKEN_COLOR_PURPLE_BASE = "#895EF7";
const TOKEN_COLOR_ORANGE_40 = "#FF7641";
const TOKEN_COLOR_BROWN_BASE = "#CA6419";
const TOKEN_COLOR_BLUE_BASE = "#4DA2F8";
const TOKEN_COLOR_TEAL_BASE = "#4CD5E3";
const TOKEN_COLOR_PINK_BASE = "#FF61C7";

//

// @TODO - move this to a variables file
const sizes$1 = {
  xs: 24,
  sm: 36,
  md: 48,
  lg: 62,
};

const defaultSize = 'md';

var script$q = {
  name: 'TAvatar',
  components: {
    VAvatar,
    VIcon: VIcon$1,
  },
  props: {
    /**
     * Sets the height and width of the component. Can be specified in pixels or a defined size ('sm', 'md', 'lg')
     */
    size: {
      type: String,
      default: defaultSize,
      validator(value) {
        return (
          Object.keys(sizes$1).includes(value) || !Number.isNaN(Number(value))
        );
      },
    },
    /**
     * Sets the image 'src'
     * Note: `url` takes precedence over all other props and will render the image (icon,
     * colour, title will be ignored)
     */
    url: {
      type: String,
      default: '',
    },
    /**
     * Sets the background colour
     * Note: if `url` is specified this prop will be ignored
     */
    color: {
      type: String,
      default: TOKEN_UI_WHITE,
    },
    /**
     * Sets the icon
     * Note: if `url` is specified this prop will be ignored
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * Sets the text. This will be truncated and used when no `url` or `icon` has been specified
     * Note: if `url` is specified this prop will be ignored
     */
    title: {
      type: String,
      default: '',
    },
  },
  computed: {
    avatarSize() {
      if (this.size && !Number.isNaN(Number(this.size))) {
        return Number(this.size);
      }

      return sizes$1[this.size] || sizes$1[defaultSize];
    },

    avatarBackgroundColor() {
      return this.color || TOKEN_UI_WHITE;
    },

    avatarStyles() {
      if (this.url) {
        return '';
      }

      return {
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: !this.color ? TOKEN_COLOR_GREY_40 : 'transparent',
      };
    },

    iconSize() {
      const scale = 0.375;

      if (!Number.isNaN(Number(this.avatarSize))) {
        return Number(this.avatarSize) * scale;
      }

      return sizes$1[this.avatarSize] * scale;
    },

    iconColor() {
      return this.color ? TOKEN_UI_WHITE : TOKEN_COLOR_GREY_40;
    },

    truncatedTitle() {
      return this.title.substring(0, 2) || 'Aa';
    },

    textStyles() {
      return {
        color: this.iconColor,
        fontSize: `${this.iconSize}px`,
      };
    },
  },
};

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$t = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('v-avatar',{staticClass:"t-cursor-default",style:(_vm.avatarStyles),attrs:{"size":_vm.avatarSize,"color":_vm.avatarBackgroundColor},on:{"mouseenter":function($event){return _vm.$emit('mouseenter', $event)},"mouseleave":function($event){return _vm.$emit('mouseleave', $event)}}},[_vm._t("default",function(){return [(_vm.url)?[_c('img',{attrs:{"src":_vm.url,"alt":_vm.title,"loading":"lazy","width":(_vm.avatarSize + "px"),"height":(_vm.avatarSize + "px;")}})]:(_vm.icon)?[_vm._t("icon",function(){return [_c('VIcon',{attrs:{"color":_vm.iconColor,"size":_vm.iconSize}},[_vm._v("\n          "+_vm._s(_vm.icon)+"\n        ")])]},null,{ icon: _vm.icon, iconColor: _vm.iconColor, iconSize: _vm.iconSize })]:[_c('span',{style:(_vm.textStyles)},[_vm._v(_vm._s(_vm.truncatedTitle))])]]},null,{ iconSize: _vm.iconSize })],2)};
var __vue_staticRenderFns__$t = [];

  /* style */
  const __vue_inject_styles__$t = undefined;
  /* scoped */
  const __vue_scope_id__$t = undefined;
  /* module identifier */
  const __vue_module_identifier__$t = undefined;
  /* functional template */
  const __vue_is_functional_template__$t = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$t = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$t, staticRenderFns: __vue_staticRenderFns__$t },
    __vue_inject_styles__$t,
    __vue_script__$q,
    __vue_scope_id__$t,
    __vue_is_functional_template__$t,
    __vue_module_identifier__$t,
    false,
    undefined,
    undefined,
    undefined
  );

//

const tooltipPlacements = Object.freeze([
  'top',
  'right',
  'bottom',
  'left',
]);

var script$p = {
  name: 'TAvatarStack',

  components: {
    TAvatar: __vue_component__$t,
    TTooltip: __vue_component__$Z,
  },
  props: {
    /**
     * Array of objects. Each item can have `url`, `icon`, `color`, `title` properties. These will
     * be bound directly into the nested `TAvatar` component
     */
    avatars: {
      type: Array,
      required: true,
    },
    /**
     * Sets the maximum number of avatars to be displayed
     */
    maxAvatars: {
      type: Number,
      default: null,
      validator(value) {
        return value === null || value > 0;
      },
    },
    /**
     * Sets the height and width of the individual avatars. Can be specified in pixels or a defined size ('sm', 'md', 'lg')
     */
    size: {
      type: String,
      default: defaultSize,
      validator(value) {
        return (
          Object.keys(sizes$1).includes(value) || !Number.isNaN(Number(value))
        );
      },
    },
    /**
     * Sets the position of the tooltip displaying the `title` prop
     */
    tooltipPlacement: {
      type: String,
      default: 'bottom',
      validator(value) {
        return tooltipPlacements.includes(value);
      },
    },
  },

  computed: {
    avatarsToShow() {
      if (this.maxAvatars === null) {
        return this.avatars;
      }

      return this.avatars.slice(0, this.maxAvatars);
    },

    computedMaxAvatars() {
      if (this.maxAvatars === null) {
        return this.avatars.length;
      }

      return this.maxAvatars;
    },

    hiddenAvatars() {
      if (this.maxAvatars === null) {
        return [];
      }

      return this.avatars.slice(this.maxAvatars);
    },

    hiddenCount() {
      return this.hiddenAvatars.length;
    },

    tooltipBottom() {
      return this.tooltipPlacement === 'bottom';
    },

    tooltipLeft() {
      return this.tooltipPlacement === 'left';
    },

    tooltipRight() {
      return this.tooltipPlacement === 'right';
    },

    tooltipTop() {
      return this.tooltipPlacement === 'top';
    },
  },
};

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$s = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-flex t--space-x-2"},[_vm._l((_vm.avatarsToShow),function(avatar,index){return _c('div',{key:("avatar-id-" + index),staticClass:"t-text-white t-font-semibold",style:({ zIndex: index })},[_c('TTooltip',{attrs:{"bottom":_vm.tooltipBottom,"left":_vm.tooltipLeft,"right":_vm.tooltipRight,"top":_vm.tooltipTop,"disabled":!avatar.name},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('div',_vm._g(_vm._b({},'div',attrs,false),on),[_c('TAvatar',{attrs:{"size":_vm.size,"url":avatar.url,"title":avatar.name,"color":avatar.color,"icon":avatar.icon},scopedSlots:_vm._u([{key:"icon",fn:function(ref){
var icon = ref.icon;
var iconColor = ref.iconColor;
var iconSize = ref.iconSize;
return [_vm._t("icon",null,null,{ icon: icon, iconColor: iconColor, iconSize: iconSize })]}}],null,true)})],1)]}}],null,true)},[_vm._v(" "),_c('span',[_vm._v(_vm._s(avatar.name))])])],1)}),_vm._v(" "),_c('div',{style:({ zIndex: _vm.avatarsToShow.length })},[(_vm.hiddenAvatars.length > 0)?_c('TTooltip',{attrs:{"bottom":_vm.tooltipBottom,"left":_vm.tooltipLeft,"right":_vm.tooltipRight,"top":_vm.tooltipTop},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('TAvatar',_vm._g(_vm._b({staticClass:"t-p-0 t-text-white t-font-semibold",attrs:{"size":_vm.size,"color":"#0B0E1F"},scopedSlots:_vm._u([{key:"default",fn:function(ref){
var iconSize = ref.iconSize;
return [_c('span',{style:({
                color: 'var(--token-ui-white)',
                fontSize: (iconSize + "px"),
              })},[_vm._v("+"+_vm._s(_vm.hiddenCount))])]}}],null,true)},'TAvatar',attrs,false),on))]}}],null,false,1726330799)},[_vm._v(" "),_vm._t("hiddenAvatars",function(){return [_c('ul',{staticClass:"t-p-0 t-m-0 t-list-none"},_vm._l((_vm.hiddenAvatars),function(hiddenAvatar,index){return _c('li',{key:index},[_vm._v("\n            "+_vm._s(hiddenAvatar.name)+"\n          ")])}),0)]},{"hiddenAvatars":_vm.hiddenAvatars})],2):_vm._e()],1)],2)};
var __vue_staticRenderFns__$s = [];

  /* style */
  const __vue_inject_styles__$s = undefined;
  /* scoped */
  const __vue_scope_id__$s = undefined;
  /* module identifier */
  const __vue_module_identifier__$s = undefined;
  /* functional template */
  const __vue_is_functional_template__$s = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$s = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$s, staticRenderFns: __vue_staticRenderFns__$s },
    __vue_inject_styles__$s,
    __vue_script__$p,
    __vue_scope_id__$s,
    __vue_is_functional_template__$s,
    __vue_module_identifier__$s,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$o = {
  name: 'TCurrencyTextField',
  components: {
    TTextField: VTextField,
  },
  props: {
    decimalPoints: {
      type: Number,
      required: true,
    },
    value: {
      type: Number,
      default: null,
      validator: (value) => value === null || Number.isInteger(value),
    },
  },
  setup(props, { emit }) {
    const { decimalPoints } = toRefs(props);
    const { convertFromCentValue, convertToCentValue } = useConvertCentValue(
      decimalPoints.value,
    );

    const textField = ref(null);
    const localValue = ref(
      props.value !== null ? convertFromCentValue(props.value) : '',
    );
    const inputValue = ref(localValue.value);

    const onChange = (value) => {
      const centValue = convertToCentValue(value);
      emit('change', centValue);
    };

    const onKeydown = (event) => {
      if (
        (event.code === 'Minus' && !!localValue.value) ||
        event.code === 'KeyE'
      ) {
        event.preventDefault();
      }
      emit('keydown', event);
    };

    const showValue = () => {
      if (!textField.value?.isFocused) {
        // Store the value before change
        inputValue.value = convertFromCentValue(props.value);
        localValue.value = inputValue.value;
      } else {
        // Show unformatted value on focus
        localValue.value = inputValue.value;
      }
    };

    watch(
      () => props.value,
      () => {
        if (!textField.value.isFocused) {
          showValue();
        }
      },
    );

    watch(localValue, (val) => {
      const centValue = convertToCentValue(val);
      if (
        centValue !== props.value &&
        (centValue === null || Number.isInteger(centValue))
      ) {
        emit('input', centValue);
      }
    });

    // Need to use flush:post to watch the ref changes
    watch(() => textField.value?.isFocused, showValue, {
      flush: 'post',
      immediate: true,
    });

    return {
      localValue,
      onChange,
      onKeydown,
      textField,
    };
  },
};

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$r = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TTextField',_vm._g(_vm._b({ref:"textField",scopedSlots:_vm._u([_vm._l((_vm.$scopedSlots),function(_,name){return {key:name,fn:function(){return [_vm._t(name)]},proxy:true}})],null,true)},'TTextField',Object.assign({}, {type: 'number',
    step: '0.50'},
    _vm.$attrs,
    {value: _vm.localValue}),false),Object.assign({}, _vm.$listeners,
    {change: _vm.onChange,
    input: function (value) { return (_vm.localValue = value); },
    keydown: _vm.onKeydown})))};
var __vue_staticRenderFns__$r = [];

  /* style */
  const __vue_inject_styles__$r = function (inject) {
    if (!inject) return
    inject("data-v-fd636676_0", { source: "[data-v-fd636676] .v-text-field__slot input[type=number]::-webkit-inner-spin-button,[data-v-fd636676] .v-text-field__slot input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$r = "data-v-fd636676";
  /* module identifier */
  const __vue_module_identifier__$r = undefined;
  /* functional template */
  const __vue_is_functional_template__$r = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$r = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$r, staticRenderFns: __vue_staticRenderFns__$r },
    __vue_inject_styles__$r,
    __vue_script__$o,
    __vue_scope_id__$r,
    __vue_is_functional_template__$r,
    __vue_module_identifier__$r,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$n = {
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TMenu: VMenu,
    TList: VList,
    TListItem: VListItem,
    TListItemTitle: VListItemTitle,
  },
  props: {
    text: {
      type: String,
      default: 'Button',
    },
    dropdown: {
      type: Array,
      required: false,
      default: () => [],
    },
    icon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: {
        mdiChevronDown,
      },
    };
  },
};

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$q = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-mr-3 t-inline-flex"},[_c('t-btn',{attrs:{"color":"primary","depressed":""},on:{"click":function($event){return _vm.$emit('click')}}},[(_vm.icon)?_c('span',{staticClass:"t-mr-3"},[_vm._t("icon")],2):_vm._e(),_vm._v("\n    "+_vm._s(_vm.text)+"\n  ")]),_vm._v(" "),(Array.isArray(_vm.dropdown) && _vm.dropdown.length >= 1)?_c('t-menu',{attrs:{"offset-y":"","transition":"slide-y-transition"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
return [_c('t-btn',_vm._g({attrs:{"depressed":"","color":"primary","min-width":"40","max-width":"45"}},on),[_c('t-icon',[_vm._v(_vm._s(_vm.icons.mdiChevronDown))])],1)]}}],null,false,575090072)},[_vm._v(" "),_c('t-list',_vm._l((_vm.dropdown),function(ref){
var key = ref.key;
var title = ref.title;
return _c('t-list-item',{key:key,attrs:{"dense":""},on:{"click":function($event){return _vm.$emit('selected', key)}}},[_c('t-list-item-title',[_vm._v("\n          "+_vm._s(title)+"\n        ")])],1)}),1)],1):_vm._e()],1)};
var __vue_staticRenderFns__$q = [];

  /* style */
  const __vue_inject_styles__$q = function (inject) {
    if (!inject) return
    inject("data-v-39ebc48f_0", { source: ".icon-holder[data-v-39ebc48f]{fill:currentColor}.v-btn[data-v-39ebc48f]:not(:first-of-type){border-top-left-radius:0;border-bottom-left-radius:0}.v-btn[data-v-39ebc48f]:not(:last-of-type){border-top-right-radius:0;border-bottom-right-radius:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$q = "data-v-39ebc48f";
  /* module identifier */
  const __vue_module_identifier__$q = undefined;
  /* functional template */
  const __vue_is_functional_template__$q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$q = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$q, staticRenderFns: __vue_staticRenderFns__$q },
    __vue_inject_styles__$q,
    __vue_script__$n,
    __vue_scope_id__$q,
    __vue_is_functional_template__$q,
    __vue_module_identifier__$q,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$m = {
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TMenu: VMenu,
    TList: VList,
    TListItem: VListItem,
    TListItemTitle: VListItemTitle,
  },
  props: {
    options: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      icons: {
        mdiPlus,
        mdiChevronDown,
        mdiAccount,
        mdiDotsHorizontal,
        mdiImport,
        mdiEmail,
        mdiTrashCan,
        mdiFileExcel,
        mdiTable,
        mdiGrid,
      },
    };
  },
};

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$p = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('t-menu',{attrs:{"offset-y":"","transition":"slide-y-transition"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('t-btn',_vm._g(_vm._b({attrs:{"icon":""}},'t-btn',attrs,false),on),[_c('t-icon',{attrs:{"small":""}},[_vm._v("\n        "+_vm._s(_vm.icons.mdiDotsHorizontal)+"\n      ")])],1)]}}])},[_vm._v(" "),_c('t-list',_vm._l((_vm.options),function(item,index){return _c('t-list-item',{key:index,attrs:{"dense":""},on:{"click":function($event){return _vm.$emit('selected', item.key)}}},[_c('t-list-item-title',[(item.icon)?_c('t-icon',{attrs:{"left":"","small":"","color":item.iconColor}},[_vm._v("\n          "+_vm._s(_vm.icons[item.icon])+"\n        ")]):_vm._e(),_vm._v("\n        "+_vm._s(item.title)+"\n      ")],1)],1)}),1)],1)};
var __vue_staticRenderFns__$p = [];

  /* style */
  const __vue_inject_styles__$p = undefined;
  /* scoped */
  const __vue_scope_id__$p = undefined;
  /* module identifier */
  const __vue_module_identifier__$p = undefined;
  /* functional template */
  const __vue_is_functional_template__$p = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$p = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$p, staticRenderFns: __vue_staticRenderFns__$p },
    __vue_inject_styles__$p,
    __vue_script__$m,
    __vue_scope_id__$p,
    __vue_is_functional_template__$p,
    __vue_module_identifier__$p,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$l = {
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TMenu: VMenu,
    TList: VList,
    TListItem: VListItem,
    TListItemTitle: VListItemTitle,
  },
  props: {
    views: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      settings: {
        current: {},
        views: [],
      },
      icons: {
        mdiTable,
        mdiGrid,
        mdiChevronDown,
      },
    };
  },
  created() {
    // set first array item to be shown as "current" selected
    this.settings.current = this.views[0];
  },
  methods: {
    selectItem(payload) {
      this.$emit('selectedView', { ...payload });
      this.settings.current = { ...payload };
    },
  },
};

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$o = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('t-menu',{attrs:{"offset-y":"","transition":"slide-y-transition"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('t-btn',_vm._g(_vm._b({staticClass:"t-mr-3"},'t-btn',attrs,false),on),[_c('t-icon',{attrs:{"left":""}},[_vm._v("\n        "+_vm._s(_vm.icons[_vm.settings.current.icon])+"\n      ")]),_vm._v("\n      "+_vm._s(_vm.settings.current.name)+"\n      "),_c('t-icon',{attrs:{"right":""}},[_vm._v("\n        "+_vm._s(_vm.icons.mdiChevronDown)+"\n      ")])],1)]}}])},[_vm._v(" "),_c('t-list',_vm._l((_vm.views),function(item,index){return _c('t-list-item',{key:index,attrs:{"dense":""},on:{"click":function($event){return _vm.selectItem(item)}}},[_c('t-list-item-title',[(item.icon)?_c('t-icon',{attrs:{"left":"","small":"","color":item.iconColor}},[_vm._v("\n          "+_vm._s(_vm.icons[item.icon])+"\n        ")]):_vm._e(),_vm._v("\n        "+_vm._s(item.name)+"\n      ")],1)],1)}),1)],1)};
var __vue_staticRenderFns__$o = [];

  /* style */
  const __vue_inject_styles__$o = undefined;
  /* scoped */
  const __vue_scope_id__$o = undefined;
  /* module identifier */
  const __vue_module_identifier__$o = undefined;
  /* functional template */
  const __vue_is_functional_template__$o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$o = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$o, staticRenderFns: __vue_staticRenderFns__$o },
    __vue_inject_styles__$o,
    __vue_script__$l,
    __vue_scope_id__$o,
    __vue_is_functional_template__$o,
    __vue_module_identifier__$o,
    false,
    undefined,
    undefined,
    undefined
  );

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

var freeGlobal$1 = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal$1 || freeSelf || Function('return this')();

var root$1 = root;

/** Built-in value references. */
var Symbol$1 = root$1.Symbol;

var Symbol$2 = Symbol$1;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$b.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$b.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$8.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$a.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag$1);
}

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;

var isArray$1 = isArray;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol$2 ? Symbol$2.prototype : undefined,
    symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray$1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap(value, baseToString) + '';
  }
  if (isSymbol(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$2) ? '-0' : result;
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag$1 = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag(value);
  return tag == funcTag$1 || tag == genTag || tag == asyncTag || tag == proxyTag;
}

/** Used to detect overreaching core-js shims. */
var coreJsData = root$1['__core-js_shared__'];

var coreJsData$1 = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData$1 && coreJsData$1.keys && coreJsData$1.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/** Used for built-in method references. */
var funcProto$1 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString$1.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto$9 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$9.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty$7).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/* Built-in method references that are verified to be native. */
var WeakMap = getNative(root$1, 'WeakMap');

var WeakMap$1 = WeakMap;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */
function noop() {
  // No operation performed.
}

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

var defineProperty = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var defineProperty$1 = defineProperty;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !defineProperty$1 ? identity : function(func, string) {
  return defineProperty$1(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

var baseSetToString$1 = baseSetToString;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = shortOut(baseSetToString$1);

var setToString$1 = setToString;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */
function baseIsNaN(value) {
  return value !== value;
}

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function strictIndexOf(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf(array, value, fromIndex) {
  return value === value
    ? strictIndexOf(array, value, fromIndex)
    : baseFindIndex(array, baseIsNaN, fromIndex);
}

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && baseIndexOf(array, value, 0) > -1;
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return setToString$1(overRest(func, start, identity), func + '');
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(value.length) && !isFunction(value);
}

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike(value) && baseGetTag(value) == argsTag$2;
}

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$7.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike(value) && hasOwnProperty$6.call(value, 'callee') &&
    !propertyIsEnumerable$1.call(value, 'callee');
};

var isArguments$1 = isArguments;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

/** Detect free variable `exports`. */
var freeExports$1 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$1 = freeExports$1 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$1 = freeModule$1 && freeModule$1.exports === freeExports$1;

/** Built-in value references. */
var Buffer = moduleExports$1 ? root$1.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse;

var isBuffer$1 = isBuffer;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag = '[object Function]',
    mapTag$2 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$2 = '[object Set]',
    stringTag$1 = '[object String]',
    weakMapTag$1 = '[object WeakMap]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] =
typedArrayTags[int8Tag] = typedArrayTags[int16Tag] =
typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] =
typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] =
typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag$1] =
typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] =
typedArrayTags[dataViewTag$2] = typedArrayTags[dateTag$1] =
typedArrayTags[errorTag$1] = typedArrayTags[funcTag] =
typedArrayTags[mapTag$2] = typedArrayTags[numberTag$1] =
typedArrayTags[objectTag$2] = typedArrayTags[regexpTag$1] =
typedArrayTags[setTag$2] = typedArrayTags[stringTag$1] =
typedArrayTags[weakMapTag$1] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike(value) &&
    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
}

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

/** Detect free variable `exports`. */
var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && freeGlobal$1.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

var nodeUtil$1 = nodeUtil;

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil$1 && nodeUtil$1.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

var isTypedArray$1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray$1(value),
      isArg = !isArr && isArguments$1(value),
      isBuff = !isArr && !isArg && isBuffer$1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray$1(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = overArg(Object.keys, Object);

var nativeKeys$1 = nativeKeys;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!isPrototype(object)) {
    return nativeKeys$1(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$4.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
}

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray$1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

/* Built-in method references that are verified to be native. */
var nativeCreate = getNative(Object, 'create');

var nativeCreate$1 = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate$1 ? nativeCreate$1(null) : {};
  this.size = 0;
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate$1) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }
  return hasOwnProperty$3.call(data, key) ? data[key] : undefined;
}

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate$1 ? (data[key] !== undefined) : hasOwnProperty$2.call(data, key);
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate$1 && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/* Built-in method references that are verified to be native. */
var Map$1 = getNative(root$1, 'Map');

var Map$2 = Map$1;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$2 || ListCache),
    'string': new Hash
  };
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = MapCache;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var stringToPath$1 = stringToPath;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : baseToString(value);
}

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray$1(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath$1(toString(value));
}

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = castPath(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet(object, path);
  return result === undefined ? defaultValue : result;
}

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new ListCache;
  this.size = 0;
}

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE$2 = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache) {
    var pairs = data.__data__;
    if (!Map$2 || (pairs.length < LARGE_ARRAY_SIZE$2 - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = stackClear;
Stack.prototype['delete'] = stackDelete;
Stack.prototype.get = stackGet;
Stack.prototype.has = stackHas;
Stack.prototype.set = stackSet;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$2.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};

var getSymbols$1 = getSymbols;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$1(object) ? result : arrayPush(result, symbolsFunc(object));
}

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return baseGetAllKeys(object, keys, getSymbols$1);
}

/* Built-in method references that are verified to be native. */
var DataView = getNative(root$1, 'DataView');

var DataView$1 = DataView;

/* Built-in method references that are verified to be native. */
var Promise$1 = getNative(root$1, 'Promise');

var Promise$2 = Promise$1;

/* Built-in method references that are verified to be native. */
var Set$1 = getNative(root$1, 'Set');

var Set$2 = Set$1;

/** `Object#toString` result references. */
var mapTag$1 = '[object Map]',
    objectTag$1 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$1 = '[object Set]',
    weakMapTag = '[object WeakMap]';

var dataViewTag$1 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = toSource(DataView$1),
    mapCtorString = toSource(Map$2),
    promiseCtorString = toSource(Promise$2),
    setCtorString = toSource(Set$2),
    weakMapCtorString = toSource(WeakMap$1);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((DataView$1 && getTag(new DataView$1(new ArrayBuffer(1))) != dataViewTag$1) ||
    (Map$2 && getTag(new Map$2) != mapTag$1) ||
    (Promise$2 && getTag(Promise$2.resolve()) != promiseTag) ||
    (Set$2 && getTag(new Set$2) != setTag$1) ||
    (WeakMap$1 && getTag(new WeakMap$1) != weakMapTag)) {
  getTag = function(value) {
    var result = baseGetTag(value),
        Ctor = result == objectTag$1 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$1;
        case mapCtorString: return mapTag$1;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$1;
        case weakMapCtorString: return weakMapTag;
      }
    }
    return result;
  };
}

var getTag$1 = getTag;

/** Built-in value references. */
var Uint8Array = root$1.Uint8Array;

var Uint8Array$1 = Uint8Array;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED);
  return this;
}

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
SetCache.prototype.has = setCacheHas;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$5,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG$3) ? new SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!arraySome(other, function(othValue, othIndex) {
            if (!cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol$2 ? Symbol$2.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new Uint8Array$1(object), new Uint8Array$1(other))) {
        return false;
      }
      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag:
      var convert = mapToArray;

    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$4;
      convert || (convert = setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$2;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      objProps = getAllKeys(object),
      objLength = objProps.length,
      othProps = getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$1.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    objectTag = '[object Object]';

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$1(object),
      othIsArr = isArray$1(other),
      objTag = objIsArr ? arrayTag : getTag$1(object),
      othTag = othIsArr ? arrayTag : getTag$1(other);

  objTag = objTag == argsTag ? objectTag : objTag;
  othTag = othTag == argsTag ? objectTag : othTag;

  var objIsObj = objTag == objectTag,
      othIsObj = othTag == objectTag,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$1(object)) {
    if (!isBuffer$1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack);
    return (objIsArr || isTypedArray$1(object))
      ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$2)) {
    var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new Stack);
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$1 | COMPARE_UNORDERED_FLAG$1, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject(value);
}

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, isStrictComparable(value)];
  }
  return result;
}

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex(key, length) &&
    (isArray$1(object) || isArguments$1(object));
}

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (isKey(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey(path), srcValue);
  }
  return function(object) {
    var objValue = get(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn(object, path)
      : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
  };
}

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function(object) {
    return baseGet(object, path);
  };
}

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return isKey(path) ? baseProperty(toKey(path)) : basePropertyDeep(path);
}

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity;
  }
  if (typeof value == 'object') {
    return isArray$1(value)
      ? baseMatchesProperty(value[0], value[1])
      : baseMatches(value);
  }
  return property(value);
}

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike(value) && isArrayLike(value);
}

/**
 * This function is like `arrayIncludes` except that it accepts a comparator.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @param {Function} comparator The comparator invoked per element.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludesWith(array, value, comparator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (comparator(value, array[index])) {
      return true;
    }
  }
  return false;
}

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE$1 = 200;

/**
 * The base implementation of methods like `_.difference` without support
 * for excluding multiple arrays or iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Array} values The values to exclude.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new array of filtered values.
 */
function baseDifference(array, values, iteratee, comparator) {
  var index = -1,
      includes = arrayIncludes,
      isCommon = true,
      length = array.length,
      result = [],
      valuesLength = values.length;

  if (!length) {
    return result;
  }
  if (iteratee) {
    values = arrayMap(values, baseUnary(iteratee));
  }
  if (comparator) {
    includes = arrayIncludesWith;
    isCommon = false;
  }
  else if (values.length >= LARGE_ARRAY_SIZE$1) {
    includes = cacheHas;
    isCommon = false;
    values = new SetCache(values);
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee == null ? value : iteratee(value);

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var valuesIndex = valuesLength;
      while (valuesIndex--) {
        if (values[valuesIndex] === computed) {
          continue outer;
        }
      }
      result.push(value);
    }
    else if (!includes(values, computed, comparator)) {
      result.push(value);
    }
  }
  return result;
}

/**
 * The base implementation of `_.values` and `_.valuesIn` which creates an
 * array of `object` property values corresponding to the property names
 * of `props`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array} props The property names to get values for.
 * @returns {Object} Returns the array of property values.
 */
function baseValues(object, props) {
  return arrayMap(props, function(key) {
    return object[key];
  });
}

/**
 * Creates an array of the own enumerable string keyed property values of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property values.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.values(new Foo);
 * // => [1, 2] (iteration order is not guaranteed)
 *
 * _.values('hi');
 * // => ['h', 'i']
 */
function values(object) {
  return object == null ? [] : baseValues(object, keys(object));
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeFloor = Math.floor,
    nativeRandom = Math.random;

/**
 * The base implementation of `_.random` without support for returning
 * floating-point numbers.
 *
 * @private
 * @param {number} lower The lower bound.
 * @param {number} upper The upper bound.
 * @returns {number} Returns the random number.
 */
function baseRandom(lower, upper) {
  return lower + nativeFloor(nativeRandom() * (upper - lower + 1));
}

/**
 * A specialized version of `_.sample` for arrays.
 *
 * @private
 * @param {Array} array The array to sample.
 * @returns {*} Returns the random element.
 */
function arraySample(array) {
  var length = array.length;
  return length ? array[baseRandom(0, length - 1)] : undefined;
}

/**
 * The base implementation of `_.sample`.
 *
 * @private
 * @param {Array|Object} collection The collection to sample.
 * @returns {*} Returns the random element.
 */
function baseSample(collection) {
  return arraySample(values(collection));
}

/**
 * Gets a random element from `collection`.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Collection
 * @param {Array|Object} collection The collection to sample.
 * @returns {*} Returns the random element.
 * @example
 *
 * _.sample([1, 2, 3, 4]);
 * // => 2
 */
function sample(collection) {
  var func = isArray$1(collection) ? arraySample : baseSample;
  return func(collection);
}

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Creates a set object of `values`.
 *
 * @private
 * @param {Array} values The values to add to the set.
 * @returns {Object} Returns the new set.
 */
var createSet = !(Set$2 && (1 / setToArray(new Set$2([,-0]))[1]) == INFINITY) ? noop : function(values) {
  return new Set$2(values);
};

var createSet$1 = createSet;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * The base implementation of `_.uniqBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 */
function baseUniq(array, iteratee, comparator) {
  var index = -1,
      includes = arrayIncludes,
      length = array.length,
      isCommon = true,
      result = [],
      seen = result;

  if (comparator) {
    isCommon = false;
    includes = arrayIncludesWith;
  }
  else if (length >= LARGE_ARRAY_SIZE) {
    var set = iteratee ? null : createSet$1(array);
    if (set) {
      return setToArray(set);
    }
    isCommon = false;
    includes = cacheHas;
    seen = new SetCache;
  }
  else {
    seen = iteratee ? [] : result;
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value) : value;

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var seenIndex = seen.length;
      while (seenIndex--) {
        if (seen[seenIndex] === computed) {
          continue outer;
        }
      }
      if (iteratee) {
        seen.push(computed);
      }
      result.push(value);
    }
    else if (!includes(seen, computed, comparator)) {
      if (seen !== result) {
        seen.push(computed);
      }
      result.push(value);
    }
  }
  return result;
}

/**
 * This method is like `_.uniq` except that it accepts `iteratee` which is
 * invoked for each element in `array` to generate the criterion by which
 * uniqueness is computed. The order of result values is determined by the
 * order they occur in the array. The iteratee is invoked with one argument:
 * (value).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 * @example
 *
 * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
 * // => [2.1, 1.2]
 *
 * // The `_.property` iteratee shorthand.
 * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
 * // => [{ 'x': 1 }, { 'x': 2 }]
 */
function uniqBy(array, iteratee) {
  return (array && array.length) ? baseUniq(array, baseIteratee(iteratee)) : [];
}

/**
 * Creates an array excluding all given values using
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * **Note:** Unlike `_.pull`, this method returns a new array.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {...*} [values] The values to exclude.
 * @returns {Array} Returns the new array of filtered values.
 * @see _.difference, _.xor
 * @example
 *
 * _.without([2, 1, 2, 3], 1, 2);
 * // => [3]
 */
var without = baseRest(function(array, values) {
  return isArrayLikeObject(array)
    ? baseDifference(array, values)
    : [];
});

var without$1 = without;

//

var script$k = {
  components: {
    TAvatar: __vue_component__$t,
    TIcon: VIcon$1,
    TListItem: VListItem,
    TListItemAction: VListItemAction,
    TListItemAvatar: VListItemAvatar,
    TListItemTitle: VListItemTitle,
  },
  props: {
    person: {
      type: Object,
      required: true,
    },
    isSelected: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const { person, isSelected } = toRefs(props);

    const fullName = computed(() => {
      // TODO: Normalise this
      const firstName = person.value.firstName ?? person.value['first-name'];
      const lastName = person.value.lastName ?? person.value['last-name'];
      return `${firstName} ${lastName}`;
    });

    const activeClass = computed(() =>
      isSelected.value ? 't-bg-neutral-10 t-transition-bg' : undefined,
    );

    return { activeClass, fullName, icons: { mdiCheck } };
  },
};

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$n = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TListItem',{class:_vm.activeClass,on:{"click":function($event){return _vm.$emit('select')}}},[_c('TListItemAvatar',{staticClass:"t-m-0"},[_c('TAvatar',{attrs:{"url":_vm.person.avatarUrl,"title":_vm.fullName,"size":"xs"}})],1),_vm._v(" "),_c('TListItemTitle',{staticClass:"t-text-default"},[_vm._v("\n    "+_vm._s(_vm.fullName)+"\n  ")]),_vm._v(" "),(_vm.isSelected)?_c('TListItemAction',[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n      "+_vm._s(_vm.icons.mdiCheck)+"\n    ")])],1):_vm._e()],1)};
var __vue_staticRenderFns__$n = [];

  /* style */
  const __vue_inject_styles__$n = undefined;
  /* scoped */
  const __vue_scope_id__$n = undefined;
  /* module identifier */
  const __vue_module_identifier__$n = undefined;
  /* functional template */
  const __vue_is_functional_template__$n = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$n = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$n, staticRenderFns: __vue_staticRenderFns__$n },
    __vue_inject_styles__$n,
    __vue_script__$k,
    __vue_scope_id__$n,
    __vue_is_functional_template__$n,
    __vue_module_identifier__$n,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$j = {
  name: 'TPeoplePicker',
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TList: VList,
    TListItemGroup: VListItemGroup,
    TMenu: VMenu,
    TProgressCircular: VProgressCircular,
    TSubheader: VSubheader,
    TTextField: VTextField,
    TPeoplePickerPerson: __vue_component__$n,
    TTooltip: __vue_component__$Z,
  },
  inheritAttrs: false,
  model: {
    prop: 'selectedPeople',
    event: 'selected-people:update',
  },
  props: {
    /**
     * A boolean to tell the component if a user can select more than one person
     */
    canSelectMultiple: {
      type: Boolean,
      default: false,
    },
    /**
     * Text for clear button
     */
    clearButtonText: {
      type: String,
      default: 'Clear',
    },
    /**
     * A boolean to tell the component if it should be user-interactable
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Text for empty state
     */
    emptyStateText: {
      type: String,
      default: 'No users match your search',
    },
    /**
     * An array of error messages.
     */
    errors: {
      type: Array,
      default: () => [],
    },
    /**
     * An array of people to render in the people picker.
     * This does no object transformations but does expect each person to have at least the following properties: id, avatarUrl, firstName, lastName
     * */
    groupedPeople: {
      type: Array,
      default: () => [],
    },
    /**
     * A boolean to tell the component if we can load more
     */
    hasMore: {
      type: Boolean,
      default: true,
    },
    /**
     * A boolean to tell the component if data is loading or not
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Text for load more button
     */
    loadMoreButtonText: {
      type: String,
      default: 'Load more',
    },
    /**
     * Text for search input
     */
    searchPlaceholderText: {
      type: String,
      default: 'Search users',
    },
    /**
     * An array of selected people
     * This does no object transformations but does expect each person to have at least the following properties: id, avatarUrl, firstName, lastName
     */
    selectedPeople: {
      type: Array,
      default: () => [],
    },
    /**
     * A function to customise how the selected text should be rendered, by default its a comma seperated string of names
     */
    selectedTextFunction: {
      type: Function,
      default: (people) =>
        people
          .map((person) => {
            // TODO: Normalise this
            const firstName = person.firstName ?? person['first-name'];
            const lastName = person.lastName ?? person['last-name'];
            return `${firstName} ${lastName}`;
          })
          .join(', '),
    },
    /**
     * A boolean to close the modal when a user clears the selected users
     */
    shouldCloseOnClear: {
      type: Boolean,
      default: true,
    },
    /**
     * Ovveride the width of the popover (defaults to input width)
     */
    width: {
      type: [String, Number],
      default: undefined,
    },
  },

  // Not a thing in Vue 2 but will be in Vue 3.
  // Documents what events are emitted from this component
  emits: [
    'menu:close',
    'menu:open',
    'people:clear',
    'people:load-more',
    'people:remove',
    'people:select',
    'selected-people:update',
    'search:update',
  ],

  setup(props, { emit }) {
    const { selectedPeople, groupedPeople, loading } = toRefs(props);
    const isOpen = ref(false);
    const searchTerm = ref('');
    const listRoot = ref(null);
    const listSentinel = ref(null);
    const searchInput = ref(null);
    const inputElement = ref(null);
    const isTextOverflowing = ref(false);
    const hasPeople = computed(() => groupedPeople.value.length);

    // Menu
    const onOpenMenu = async () => {
      isOpen.value = true;
      emit('menu:open');
      await nextTick();
      // setTimeout hack to focus
      setTimeout(() => {
        searchInput.value?.$refs?.input?.focus?.();
      });
    };

    const onCloseMenu = () => {
      isOpen.value = false;
      searchTerm.value = '';
      emit('menu:close');
    };

    // Truncated input
    const checkTextOverflow = () => {
      window.requestAnimationFrame(() => {
        isTextOverflowing.value =
          inputElement.value?.clientWidth < inputElement.value?.scrollWidth;
      });
    };
    watch(selectedPeople, checkTextOverflow);
    onMounted(checkTextOverflow);

    // Search
    const onSearchTermUpdate = () => {
      emit('search:update', searchTerm.value);
    };

    // Data fetching
    const onLoadMore = () => {
      if (loading.value) {
        return;
      }
      emit('people:load-more');
    };

    const isSelected = (person) =>
      // TODO: Normalise this earlier
      selectedPeople.value.some(
        ({ id }) => parseInt(person.id, 10) === parseInt(id, 10),
      );

    const selectedText = computed(() => {
      if (selectedPeople.value.length) {
        return props.selectedTextFunction(selectedPeople.value);
      }
      return 'Select people';
    });

    const onToggleMenu = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen.value) {
        onCloseMenu();
      } else {
        onOpenMenu();
      }
    };

    // Selection events
    const onClearPeople = () => {
      emit('selected-people:update', []);
      emit('search:update', '');
      emit('person:clear');
      if (props.shouldCloseOnClear) {
        onCloseMenu();
      }
    };

    const onSelectPerson = (person) => {
      let newPeople = [person];
      if (props.canSelectMultiple) {
        newPeople = uniqBy([...selectedPeople.value, person], ({ id }) =>
          parseInt(id, 10),
        );
      }
      emit('person:select', person);
      emit('selected-people:update', newPeople);
    };

    const onDeselectPerson = (person) => {
      let newPeople = [];
      if (props.canSelectMultiple) {
        newPeople = [...selectedPeople.value].filter(
          ({ id }) => parseInt(id, 10) !== parseInt(person.id, 10),
        );
      }
      emit('person:remove', person);
      emit('selected-people:update', newPeople);
    };

    const onTogglePerson = (person) => {
      if (isSelected(person)) {
        onDeselectPerson(person);
      } else {
        onSelectPerson(person);
      }
      if (!props.canSelectMultiple) {
        onCloseMenu();
      }
    };

    // Infinite scroll
    let stopInfiniteScroll = null;
    const setupInfiniteScroll = () => {
      if (!hasPeople.value) {
        stopInfiniteScroll?.();
        return;
      }
      const { stop } = useIntersectionObserver(
        listSentinel,
        ([{ isIntersecting }]) => {
          if (isIntersecting) {
            onLoadMore();
          }
        },
        {
          root: listRoot.value.$refs.content,
          rootMargin: '0px 0px 200px 0px',
        },
      );
      stopInfiniteScroll = stop;
    };
    watch(hasPeople, setupInfiniteScroll);

    onUnmounted(() => stopInfiniteScroll?.());

    return {
      icons: {
        mdiMagnify,
      },
      isSelected,
      isOpen,
      listRoot,
      listSentinel,
      onCloseMenu,
      onOpenMenu,
      onClearPeople,
      onLoadMore,
      onSearchTermUpdate,
      onToggleMenu,
      onTogglePerson,
      isTextOverflowing,
      searchTerm,
      searchInput,
      selectedText,
      inputElement,
    };
  },
};

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$m = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-relative"},[_c('TMenu',{ref:"listRoot",attrs:{"offset-y":"","top":"","max-height":"380","min-width":_vm.width,"max-width":_vm.width || 'auto',"bottom":"","close-on-content-click":false,"disabled":_vm.disabled},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var menuOn = ref.on;
var menuAttrs = ref.attrs;
return [_c('div',{staticClass:"t-relative"},[_c('TTextField',_vm._g(_vm._b({attrs:{"disabled":_vm.disabled,"flat":"","solo":"","dense":"","hide-details":"auto","error":!!_vm.errors.length,"outlined":!!_vm.errors.length,"error-messages":_vm.errors,"readonly":"","background-color":"var(--token-ui-bg)"},on:{"focus":_vm.onToggleMenu}},'TTextField',Object.assign({}, menuAttrs, _vm.$attrs),false),Object.assign({}, menuOn,
            _vm.$listeners))),_vm._v(" "),_c('TTooltip',{attrs:{"top":"","disabled":!_vm.isTextOverflowing || _vm.disabled},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
          var on = ref.on;
          var attrs = ref.attrs;
return [_c('div',_vm._g(_vm._b({staticClass:"\n                t-absolute\n                t-top-0\n                t-left-0\n                t-items-center\n                t-text-default\n                t-w-full\n                t-flex\n              ",class:{
                't-opacity-40': _vm.disabled,
                't-cursor-pointer': !_vm.disabled,
              },style:({
                height: '38px',
              }),on:{"click":function (event) { return (_vm.disabled ? null : _vm.onToggleMenu(event)); }}},'div',attrs,false),on),[_c('span',{ref:"inputElement",staticClass:"t-ml-3 t-truncate t-flex-grow"},[_vm._v("\n                "+_vm._s(_vm.selectedText)+"\n              ")]),_vm._v(" "),_c('TIcon',{staticClass:"t-transition-transform t-mx-3 t-transform",class:{ 't-rotate-180': _vm.isOpen }},[_vm._v("\n                $dropdown\n              ")])],1)]}},{key:"default",fn:function(){return [_vm._v("\n            "+_vm._s(_vm.selectedText)+"\n          ")]},proxy:true}],null,true)})],1)]}}]),model:{value:(_vm.isOpen),callback:function ($$v) {_vm.isOpen=$$v;},expression:"isOpen"}},[_vm._v(" "),_c('div',{staticClass:"t-flex t-flex-col t-w-full t-bg-white"},[_c('div',{staticClass:"t-bg-white t-sticky t-z-10 t-top-0 t-p-2 t-w-full"},[_c('TTextField',{ref:"searchInput",staticClass:"t-text-default t-people-picker__search",attrs:{"clearable":"","dense":"","flat":"","hide-details":"","solo":"","placeholder":_vm.searchPlaceholderText},on:{"input":_vm.onSearchTermUpdate},model:{value:(_vm.searchTerm),callback:function ($$v) {_vm.searchTerm=$$v;},expression:"searchTerm"}})],1),_vm._v(" "),_c('div',{staticClass:"t-h-72 t-w-full t-overflow-hidden t-overflow-y-scroll"},[(_vm.groupedPeople.length)?[_c('TList',{staticClass:"t-w-full t-overflow-x-hidden"},[_c('TListItemGroup',[_vm._l((_vm.groupedPeople),function(group){return [(_vm.groupedPeople.length > 1)?_c('TSubheader',{key:("group-" + (group.name)),staticClass:"t-text-default"},[_vm._v("\n                  "+_vm._s(group.name)+"\n                ")]):_vm._e(),_vm._v(" "),_vm._l((group.people),function(person){return _c('TPeoplePickerPerson',{key:("group-" + (group.name) + "-person-" + (person.id)),attrs:{"is-selected":_vm.isSelected(person),"person":person},on:{"select":function($event){return _vm.onTogglePerson(person)}}})})]})],2),_vm._v(" "),_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.hasMore),expression:"hasMore"}],ref:"listSentinel",staticClass:"t-flex t-justify-center t-py-3"},[_c('TProgressCircular',{directives:[{name:"show",rawName:"v-show",value:(_vm.loading),expression:"loading"}],attrs:{"indeterminate":"","color":"primary","size":20}}),_vm._v(" "),_c('TBtn',{directives:[{name:"show",rawName:"v-show",value:(!_vm.loading),expression:"!loading"}],attrs:{"color":"primary","primary":""},on:{"click":_vm.onLoadMore}},[_vm._v("\n                "+_vm._s(_vm.loadMoreButtonText)+"\n              ")])],1)],1)]:_c('div',{staticClass:"t-flex t-justify-center t-items-center t-p-3"},[(_vm.loading)?_c('TProgressCircular',{attrs:{"size":20,"color":"var(--token-color-indigo-30)","indeterminate":""}}):[_c('TIcon',{attrs:{"small":""}},[_vm._v("\n              "+_vm._s(_vm.icons.mdiMagnify)+"\n            ")]),_vm._v(" "),_c('p',{staticClass:"t-text-default t-ml-2"},[_vm._v("\n              "+_vm._s(_vm.emptyStateText)+"\n            ")])]],2)],2)]),_vm._v(" "),_c('div',{staticClass:"t-sticky t-bottom-0 t-z-10 t-bg-bg"},[_c('TBtn',{attrs:{"disabled":!_vm.selectedPeople.length,"text":"","color":"var(--token-ui-primary)"},on:{"click":_vm.onClearPeople}},[_vm._v("\n        "+_vm._s(_vm.clearButtonText)+"\n      ")])],1)])],1)};
var __vue_staticRenderFns__$m = [];

  /* style */
  const __vue_inject_styles__$m = function (inject) {
    if (!inject) return
    inject("data-v-364b8898_0", { source: ".t-people-picker__search[data-v-364b8898] .v-input__control{border:1px solid var(--token-ui-primary)}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$m = "data-v-364b8898";
  /* module identifier */
  const __vue_module_identifier__$m = undefined;
  /* functional template */
  const __vue_is_functional_template__$m = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$m = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$m, staticRenderFns: __vue_staticRenderFns__$m },
    __vue_inject_styles__$m,
    __vue_script__$j,
    __vue_scope_id__$m,
    __vue_is_functional_template__$m,
    __vue_module_identifier__$m,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const variants = ['bg', 'outline'];
const sizes = ['sm', 'md', 'lg'];

var script$i = {
  components: {
    TIcon: VIcon$1,
  },

  props: {
    canClearSearch: {
      type: Boolean,
      default: true,
    },

    label: {
      type: String,
      default: 'Label',
    },

    variant: {
      type: String,
      default: 'bg',
      validator: (variant) => variants.includes(variant),
    },

    size: {
      type: String,
      default: 'md',
      validator: (size) => sizes.includes(size),
    },

    searchTerm: {
      type: String,
      default: '',
    },
  },

  setup(props, { emit }) {
    const handleInput = (event) => {
      emit('update:search-term', event?.target?.value ?? '');
    };

    const handleClearSearch = () => {
      emit('update:search-term', '');
    };

    return {
      handleClearSearch,
      handleInput,
      mdiClose,
      mdiMagnify,
    };
  },
};

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$l = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-flex t-items-center t-relative",class:{
    't-h-8': _vm.size === 'sm',
    't-h-10': _vm.size === 'md',
    't-h-12': _vm.size === 'lg',
  }},[_c('label',{staticClass:"\n      t-absolute\n      t-z-0\n      t-mt-px\n      t-left-0\n      t-flex\n      t-justify-center\n      t-items-center\n      t-mr-2\n      t-text-text-02\n    ",class:{
      't-w-3 t-h-3 t-ml-5': _vm.size === 'sm',
      't-w-4 t-h-4 t-ml-4 ': _vm.size === 'md' || _vm.size === 'lg',
    },attrs:{"for":"t-search-input"}},[_c('TIcon',{class:{ 't-mt-px': _vm.size === 'sm' },attrs:{"size":_vm.size === 'sm' ? 20 : 24}},[_vm._v("\n      "+_vm._s(_vm.mdiMagnify)+"\n    ")])],1),_vm._v(" "),_c('input',{staticClass:"\n      t-px-10\n      t-w-full\n      t-rounded-full\n      t-h-full\n      t-outline-none\n      t-text-default\n      t-text-text-01\n      t-transition-shadow\n      t-border-none\n      t-ring-1\n      t-ring-inset\n      t-ring-input\n      hover:t-ring-input--active\n      focus:t-ring-input--active\n    ",class:{
      't-bg-input': _vm.variant === 'bg',
    },attrs:{"id":"t-search-input","type":"text","placeholder":_vm.label},domProps:{"value":_vm.searchTerm},on:{"input":_vm.handleInput}}),_vm._v(" "),(_vm.searchTerm && _vm.canClearSearch)?_c('button',{staticClass:"\n      t-absolute\n      t-z-0\n      t-top-0\n      t-h-full\n      t-ml-2\n      t-right-0\n      t-flex\n      t-justify-center\n      t-items-center\n      t-text-text-02\n    ",class:{
      't-mr-5': _vm.size === 'sm',
      't-mr-4 ': _vm.size === 'md' || _vm.size === 'lg',
    },attrs:{"type":"button"},on:{"click":_vm.handleClearSearch}},[_c('TIcon',{attrs:{"small":_vm.size === 'sm'}},[_vm._v("\n      "+_vm._s(_vm.mdiClose)+"\n    ")])],1):_vm._e()])};
var __vue_staticRenderFns__$l = [];

  /* style */
  const __vue_inject_styles__$l = undefined;
  /* scoped */
  const __vue_scope_id__$l = undefined;
  /* module identifier */
  const __vue_module_identifier__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$l = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$l = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$l, staticRenderFns: __vue_staticRenderFns__$l },
    __vue_inject_styles__$l,
    __vue_script__$i,
    __vue_scope_id__$l,
    __vue_is_functional_template__$l,
    __vue_module_identifier__$l,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$h = {
  name: 'SectionHeader',
  components: { TRow: VRow, TCol: VCol },
  props: {
    secondRow: {
      type: Boolean,
      default: false,
    },
  },
};

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$k = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('header',{staticClass:"t-section-header"},[_c('t-row',{staticClass:"t-mb-4",attrs:{"no-gutters":"","align-content":"end"}},[_c('t-col',{staticClass:"t-flex t-align-center"},[_c('h1',[_vm._t("title")],2)]),_vm._v(" "),_c('t-col',{staticClass:"t-flex t-align-center t-justify-end"},[_vm._t("actions")],2)],1),_vm._v(" "),(_vm.secondRow)?_c('t-row',{attrs:{"no-gutters":""}},[_c('t-col',{staticClass:"t-flex t-flex-col t-relative",attrs:{"cols":"12"}},[_vm._t("secondRow")],2)],1):_vm._e(),_vm._v(" "),_vm._t("default")],2)};
var __vue_staticRenderFns__$k = [];

  /* style */
  const __vue_inject_styles__$k = function (inject) {
    if (!inject) return
    inject("data-v-30f46e25_0", { source: "h1[data-v-30f46e25]{font-family:'Open Sans',sans-serif;font-weight:600;font-size:24px;padding:0;margin:0;color:#333}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$k = "data-v-30f46e25";
  /* module identifier */
  const __vue_module_identifier__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$k = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$k = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$k, staticRenderFns: __vue_staticRenderFns__$k },
    __vue_inject_styles__$k,
    __vue_script__$h,
    __vue_scope_id__$k,
    __vue_is_functional_template__$k,
    __vue_module_identifier__$k,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$g = {
  components: {
    TCheckbox: VCheckbox,
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
  },
  props: {
    status: {
      type: String,
      required: false,
      default: '',
    },

    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      icons: {
        mdiClose,
      },
    };
  },
};

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$j = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"\n    t-section-header-selecting\n    t-flex\n    t-items-center\n    t-w-full\n    t-bg-indigo-10\n    t-px-3\n    t-rounded\n  "},[_c('div',{staticClass:"t-flex-none t-flex t-items-center t-ml-0 t-mr-auto"},[_c('span',{staticClass:"t-mr-5"},[_c('t-checkbox',{staticClass:"t-mt-0 t-pt-0",attrs:{"hide-details":"true","input-value":_vm.status === 'all',"label":_vm.$t('Select All')},on:{"change":function($event){return _vm.$emit(_vm.status === 'all' ? 'clear' : 'select-all', $event)}}})],1),_vm._v(" "),_c('span',[_c('strong',{staticClass:"t-mr-3"},[_vm._v("\n        "+_vm._s(_vm.selectedItems.length)+" "+_vm._s(_vm.$t('selected'))+"\n      ")]),_vm._v(" "),_c('t-btn',{attrs:{"depressed":""},on:{"click":function($event){_vm.$emit('clear');}}},[_c('t-icon',{staticClass:"t-mr-2",attrs:{"small":""}},[_vm._v(_vm._s(_vm.icons.mdiClose))]),_vm._v("\n        "+_vm._s(_vm.$t('Clear Selection'))+"\n      ")],1)],1)]),_vm._v(" "),_c('div',{staticClass:"t-flex t-flex-none t-items-center t-ml-auto t-mr-0"},[_vm._t("default")],2)])};
var __vue_staticRenderFns__$j = [];

  /* style */
  const __vue_inject_styles__$j = function (inject) {
    if (!inject) return
    inject("data-v-348d2b97_0", { source: ".t-section-header-selecting[data-v-348d2b97]{height:50px;font-size:14px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$j = "data-v-348d2b97";
  /* module identifier */
  const __vue_module_identifier__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$j = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$j = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$j, staticRenderFns: __vue_staticRenderFns__$j },
    __vue_inject_styles__$j,
    __vue_script__$g,
    __vue_scope_id__$j,
    __vue_is_functional_template__$j,
    __vue_module_identifier__$j,
    false,
    createInjector,
    undefined,
    undefined
  );

//

var script$f = {
  components: {
    TBtn: __vue_component__$_,
    TMenu: VMenu,
    TCard: VCard,
    TList: VList,
    TListItem: VListItem,
    TSubheader: VSubheader,
    TListItemTitle: VListItemTitle,
    TListItemGroup: VListItemGroup,
    TListItemContent: VListItemContent,
    TListItemAction: VListItemAction,
    TIcon: VIcon$1,
  },
  props: {
    btnClass: {
      type: [String, Array],
      required: false,
      default: '',
    },
    sort: {
      type: String,
      required: false,
      default: '',
    },
    sortOrder: {
      type: String,
      required: false,
      default: 'asc',
    },
    options: {
      type: Array,
      required: false,
      default: () => [
        //
      ],
    },
  },
  setup(props, { emit }) {
    const toggleSortOrder = () => {
      emit('update:sortOrder', props.sortOrder === 'desc' ? 'asc' : 'desc');
    };

    const updateSort = ({ propKey }) => {
      emit('update:sort', propKey);
    };

    return {
      updateSort,
      toggleSortOrder,

      icons: {
        mdiCheck,
        mdiChevronDown,
      },
    };
  },
};

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$i = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('t-menu',{attrs:{"offset-y":"","close-on-content-click":false,"nudge-width":120,"max-width":260,"z-index":"50","bottom":"","right":"","transition":"slide-y-transition"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('t-btn',_vm._g(_vm._b({class:_vm.btnClass,attrs:{"text":""}},'t-btn',attrs,false),on),[_c('span',{staticClass:"t-mr-1 t-text-default"},[_vm._v(_vm._s(_vm.$t('Sort by'))+":")]),_vm._v(" "),_c('strong',[_vm._t("default")],2),_vm._v(" "),_c('t-icon',{staticClass:"t-ml-1 t-top-0.5",attrs:{"small":""}},[_vm._v(_vm._s(_vm.icons.mdiChevronDown))])],1)]}}],null,true),model:{value:(_vm.menu),callback:function ($$v) {_vm.menu=$$v;},expression:"menu"}},[_vm._v(" "),_c('t-card',[_c('t-list',{attrs:{"dense":""}},[_c('t-subheader',[_vm._v("Sort by")]),_vm._v(" "),_c('t-list-item-group',{attrs:{"value":_vm.sort}},_vm._l((_vm.options),function(item){return _c('t-list-item',{key:item.propKey,staticClass:"t-px-2",attrs:{"link":"","ripple":false,"value":item.propKey}},[_c('t-list-item-content',{staticClass:"t-py-0"},[_c('t-list-item-title',{staticClass:"t-py-1.5",on:{"click":function($event){return _vm.updateSort(item)}}},[_vm._v("\n              "+_vm._s(item.text)+"\n            ")])],1),_vm._v(" "),(_vm.sort == item.propKey)?_c('t-list-item-action',{staticClass:"t-text-xs t-flex-row t-items-center",on:{"click":_vm.toggleSortOrder}},[_c('span',{staticClass:"mr-1"},[_vm._v("\n              ("),_c('span',{staticClass:"t-underline",domProps:{"textContent":_vm._s(_vm.sortOrder)}}),_vm._v(")\n            ")]),_vm._v(" "),_c('t-icon',{attrs:{"x-small":"","color":"blue"}},[_vm._v("\n              "+_vm._s(_vm.icons.mdiCheck)+"\n            ")])],1):_vm._e()],1)}),1)],1)],1)],1)};
var __vue_staticRenderFns__$i = [];

  /* style */
  const __vue_inject_styles__$i = undefined;
  /* scoped */
  const __vue_scope_id__$i = undefined;
  /* module identifier */
  const __vue_module_identifier__$i = undefined;
  /* functional template */
  const __vue_is_functional_template__$i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$i = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$i, staticRenderFns: __vue_staticRenderFns__$i },
    __vue_inject_styles__$i,
    __vue_script__$f,
    __vue_scope_id__$i,
    __vue_is_functional_template__$i,
    __vue_module_identifier__$i,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$e = {
  props: {
    src: {
      type: String,
      default: '',
    },
    width: {
      type: [String, Number],
      default: 360,
    },
    height: {
      type: [String, Number],
      default: 200,
    },
  },
};

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$h = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('video',{staticClass:"t-inline-block t-align-baseline",attrs:{"autoplay":"","muted":"","playsinline":"","disablepictureinpicture":"","disableremoteplayback":"","height":_vm.height,"width":_vm.width},domProps:{"muted":true}},[_c('source',{attrs:{"type":"video/mp4","src":(_vm.src + ".mp4")}}),_vm._v(" "),_c('img',{attrs:{"loading":"lazy","src":(_vm.src + ".gif"),"height":_vm.height,"width":_vm.width}})])};
var __vue_staticRenderFns__$h = [];

  /* style */
  const __vue_inject_styles__$h = undefined;
  /* scoped */
  const __vue_scope_id__$h = undefined;
  /* module identifier */
  const __vue_module_identifier__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$h = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$h = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$h, staticRenderFns: __vue_staticRenderFns__$h },
    __vue_inject_styles__$h,
    __vue_script__$e,
    __vue_scope_id__$h,
    __vue_is_functional_template__$h,
    __vue_module_identifier__$h,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$d = defineComponent({
  name: 'TUpload',
  components: {
    Dashboard,
  },
  props: {
    s3PresignFunction: { type: Function, required: false, default: undefined },
    dropSelector: { type: String, required: false, default: undefined },
    disableDoneButton: { type: Boolean, required: false, default: false },
    disableImageEditing: { type: Boolean, required: false, default: false },
    autoProceed: { type: Boolean, required: false, default: false },
    clearUploadedFilesAfterComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
    showRemoveButtonAfterComplete: {
      type: Boolean,
      required: false,
      default: false,
    },
    note: { type: String, required: false, default: undefined },
    width: { type: String, required: false, default: '100%' }, // Uppy default = 750px
    height: { type: String, required: false, default: '550px' }, // Uppy default = 550px
    maxFileSize: {
      type: Number,
      required: false,
      default: undefined,
      validator(value) {
        return value >= 1;
      },
    },
    minFileSize: {
      type: Number,
      required: false,
      default: 1,
      validator(value) {
        return value >= 1;
      },
    },
    maxTotalFileSize: { type: Number, required: false, default: null },
    maxNumberOfFiles: { type: Number, required: false, default: null },
    minNumberOfFiles: { type: Number, required: false, default: null },
    allowedFileTypes: { type: Array, required: false, default: null },
    color: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: [
    'file-added',
    'files-added',
    'file-removed',
    'upload',
    'upload-success',
    'complete',
    'upload-error',
    'upload-retry',
    'restriction-failed',
    'file-editor:start',
    'file-editor:complete',
  ],
  setup(props, { emit }) {
    const root = ref(null);

    // Need to wait until the component is mounted so that DropTarget target exists.
    const isMounted = ref(false);

    onMounted(() => {
      isMounted.value = true;
    });

    // Events

    const onFileAdded = (file) => {
      emit('file-added', file);
    };
    const onFilesAdded = (files) => {
      emit('files-added', files);
    };
    const onFileRemoved = (file, reason) => {
      emit('file-removed', { file, reason });
    };
    const onUpload = (idObj) => {
      emit('upload', idObj);
    };
    const onUploadSuccess = (file, response) => {
      emit('upload-success', { file, response });
    };
    const onComplete = (result) => {
      emit('complete', result);
      if (props.clearUploadedFilesAfterComplete) {
        result.successful.forEach((file) => {
          // eslint-disable-next-line no-use-before-define
          uppy.value.removeFile(file.id);
        });
      }
    };
    const onUploadError = (file, error, response) => {
      emit('upload-error', { file, error, response });
    };
    const onUploadRetry = (fileId) => {
      emit('upload-retry', fileId);
    };
    const onRestrictionFailed = (file, error) => {
      emit('restriction-failed', { file, error });
    };
    const onFileEditorStart = (file) => {
      emit('file-editor:start', file);
    };
    const onFileEditorComplete = (updatedFile) => {
      emit('file-editor:complete', updatedFile);
    };

    // Options

    const uppyOptions = {
      autoProceed: props.autoProceed,
      restrictions: {
        maxFileSize: props.maxFileSize,
        minFileSize: props.minFileSize,
        maxTotalFileSize: props.maxTotalFileSize,
        maxNumberOfFiles: props.maxNumberOfFiles,
        minNumberOfFiles: props.minNumberOfFiles,
        allowedFileTypes: props.allowedFileTypes,
      },
    };

    const dropTargetOptions = {
      target: props.dropSelector,
    };

    const imageEditorOptions = {
      id: 'ImageEditor',
      quality: 1,
      cropperOptions: {
        viewMode: 1,
        background: false,
        responsive: true,
      },
    };

    const awsOptions = {
      getUploadParameters: props.s3PresignFunction,
    };

    // Instance

    const uppy = computed(() => {
      const instance = new Uppy(uppyOptions)
        .on('file-added', onFileAdded)
        .on('files-added', onFilesAdded)
        .on('file-removed', onFileRemoved)
        .on('upload', onUpload)
        .on('upload-success', onUploadSuccess)
        .on('complete', onComplete)
        .on('upload-error', onUploadError)
        .on('upload-retry', onUploadRetry)
        .on('restriction-failed', onRestrictionFailed)
        .on('file-editor:start', onFileEditorStart)
        .on('file-editor:complete', onFileEditorComplete);

      if (props.dropSelector) {
        instance.use(DropTarget, dropTargetOptions);
      }

      if (!props.disableImageEditing && !props.autoProceed) {
        instance.use(ImageEditor, imageEditorOptions);
      }

      if (props.s3PresignFunction) {
        instance.use(AwsS3, awsOptions);
      }

      return instance;
    });

    const plugins = computed(() => {
      const pluginList = [];

      if (!props.disableImageEditing && !props.autoProceed) {
        pluginList.push('ImageEditor');
      }

      return pluginList;
    });

    const textChanges = {
      dropPaste: 'Drag and drop, paste or %{browse}',
    };

    const dashboardOptions = {
      width: props.width,
      height: props.height,
      hidePauseResumeButton: true,
      hideCancelButton: true,
      proudlyDisplayPoweredByUppy: false,
      ...(props.disableDoneButton && { doneButtonHandler: null }),
      showRemoveButtonAfterComplete: props.showRemoveButtonAfterComplete,
      note: props.note,
      showLinkToFileUploadResult: false,
      locale: { strings: textChanges },
      disableInformer: true,
    };

    const style = computed(() => {
      return {
        '--primary': props.color || 'var(--token-ui-primary)',
      };
    });

    return {
      root,
      isMounted,
      uppy,
      plugins,
      dashboardOptions,
      style,
    };
  },
});

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$g = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.isMounted)?_c('Dashboard',{ref:"root",style:(_vm.style),attrs:{"uppy":_vm.uppy,"plugins":_vm.plugins,"props":_vm.dashboardOptions}}):_vm._e()};
var __vue_staticRenderFns__$g = [];

  /* style */
  const __vue_inject_styles__$g = function (inject) {
    if (!inject) return
    inject("data-v-123c273f_0", { source: ".uppy-is-drag-over.t-upload-drop-selector-is-not-overlay{background-color:rgba(174,187,239,.2)!important;border:1px solid var(--primary)!important}.uppy-is-drag-over:not(.t-upload-drop-selector-is-not-overlay){position:relative}.uppy-is-drag-over:not(.t-upload-drop-selector-is-not-overlay)::after{display:block;content:'';position:absolute;inset:0;z-index:40;background-color:rgba(174,187,239,.2);border:1px solid var(--primary)!important;border-radius:.5rem}.uppy-Dashboard *{font-family:'Open Sans',sans-serif!important}.uppy-Dashboard-AddFiles-info{user-select:none!important;display:block!important;position:static!important;padding-top:15px!important}.uppy-Dashboard-AddFiles-title{user-select:none!important;font-size:18px!important;color:#0b0e1f!important}.uppy-Dashboard-browse{user-select:none!important;font-weight:600!important;color:var(--primary)!important}.uppy-Dashboard-browse:focus,.uppy-Dashboard-browse:hover{border-color:var(--primary)!important}.uppy-size--md .uppy-Dashboard-note{font-size:14px!important;color:#6d6f80!important}.uppy-Dashboard-AddFiles{border:0!important}.uppy-Dashboard-dropFilesHereHint{background-color:#f2f4fc!important;border:1px solid var(--primary)!important;border-radius:8px!important;background-image:url(\"data:image/svg+xml,%3Csvg width='56' height='55' viewBox='0 0 56 55' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M40.8032 54.0649C41.3597 54.0694 41.8355 53.6745 41.9374 53.1299C42.813 48.4509 42.4536 30.7003 42.395 30.6348C42.4231 29.5922 41.8355 29.5644 41.196 29.5644C30.3613 29.6122 5.94579 29.2184 5.34239 29.477H5.29438C5.10157 29.4739 4.91423 29.5403 4.76689 29.6641C4.63301 29.7762 4.5532 29.9396 4.54695 30.1134C4.2433 33.5111 4.97472 50.3731 4.97472 53.7786L40.8032 54.0649Z' fill='%234DA2F8'/%3E%3Cmask id='mask0' mask-type='alpha' maskUnits='userSpaceOnUse' x='26' y='0' width='30' height='37'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M26.6289 0.759766H55.363V36.0129H26.6289V0.759766Z' fill='white'/%3E%3C/mask%3E%3Cg mask='url(%23mask0)'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M55.307 12.342C55.3281 12.2893 55.3445 12.2349 55.3558 12.1796C55.5511 11.374 51.7034 5.62124 51.1991 5.45534C46.0448 3.76797 38.15 1.07645 36.5246 0.779226L36.467 0.762131C36.3251 0.751058 36.1828 0.779032 36.0558 0.842556C35.8653 0.945904 35.7194 1.1155 35.646 1.31889C32.4701 9.02687 29.939 18.0145 27.5043 26.0214C27.4454 26.2172 26.69 28.381 26.6382 28.6568C26.3379 30.2117 33.297 31.3911 46.1904 35.9057L46.2624 35.9271C46.3914 35.9642 46.5272 35.9708 46.6591 35.9465L46.7407 35.9708C47.171 36.1134 47.6362 35.882 47.7795 35.4535C47.7806 35.4502 47.7816 35.4469 47.7828 35.4436C51.317 26.6143 52.3087 21.3881 55.307 12.342Z' fill='%2379E3AA'/%3E%3C/g%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M51.1848 5.45101C50.2338 5.13631 48.698 11.215 48.9064 11.6517C49.115 12.0886 50.6552 12.6518 54.5504 12.9803C54.8966 12.995 55.2147 12.7924 55.346 12.4732C55.9215 11.2552 51.774 5.64741 51.1848 5.45101Z' fill='%23BAF8DD'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M44.1491 26.0776L32.9766 22.7473L37.6711 19.4196C37.9172 19.2449 38.2612 19.3248 38.4045 19.5896L39.6445 21.8793C39.7348 22.0456 39.9483 22.1004 40.1079 21.9982L43.6033 19.7621C43.8701 19.5913 44.227 19.7065 44.3425 20.0004L47.0752 26.9498L44.1491 26.0776Z' fill='white'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M38.4321 14.8202C38.1944 15.6106 38.6454 16.4434 39.4394 16.6802C40.2335 16.9168 41.0703 16.4679 41.308 15.6774C41.5459 14.887 41.0949 14.0542 40.3006 13.8174C39.5064 13.5806 38.67 14.0297 38.4321 14.8202Z' fill='white'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M34.8685 5.54779C34.866 5.49107 34.8584 5.43493 34.8455 5.37976C34.6888 4.56579 28.7956 0.911892 28.2676 0.968422C22.8738 1.55374 14.5674 2.35274 12.9641 2.75137L12.9045 2.75934C12.7709 2.8079 12.653 2.89163 12.5636 3.00178C12.433 3.17448 12.3706 3.38875 12.3882 3.60419C12.6983 11.9295 14.1271 21.1549 15.2364 29.4472C15.2641 29.6497 15.4752 31.9307 15.5428 32.2033C15.9151 33.7428 22.7413 31.9519 36.3562 30.7552L36.4305 30.7451C36.5634 30.7257 36.6899 30.676 36.7997 30.5994L36.8842 30.588C37.3354 30.5408 37.6625 30.1384 37.6153 29.6893C37.6149 29.686 37.6145 29.6823 37.6141 29.679C37.1643 20.1852 35.8964 15.0185 34.8685 5.54779Z' fill='%23FFD56A'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M28.2531 0.970488C27.2566 1.07539 28.3832 7.24228 28.7544 7.55427C29.1258 7.86626 30.7619 7.74504 34.445 6.44056C34.7664 6.31176 34.9719 5.99608 34.9588 5.65146C34.977 4.30541 28.8711 0.90677 28.2531 0.970488Z' fill='%23FFEAB6'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M23.6327 11.5692L17.013 12.5423C16.6566 12.559 16.3539 12.2851 16.3372 11.9301C16.3223 11.6187 16.5339 11.3417 16.8393 11.2728L23.4582 10.2946C23.8118 10.2472 24.1371 10.4942 24.1849 10.846C24.2326 11.1976 23.9855 11.521 23.6327 11.5692Z' fill='%23CE8D4F'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M18.7893 20.06L18.1592 20.1453C17.8026 20.1622 17.5001 19.8881 17.4832 19.5333C17.4683 19.2221 17.6801 18.9449 17.9855 18.8757C20.1044 18.5886 22.6269 18.2215 25.0706 17.87C27.5145 17.5186 30.027 17.1528 32.1708 16.8622C32.5185 16.8121 32.8409 17.0524 32.8911 17.3984C32.8916 17.4017 32.892 17.405 32.8924 17.4085C32.943 17.7572 32.6998 18.0808 32.3493 18.1309C32.3478 18.1311 32.3462 18.1313 32.3444 18.1317C30.2255 18.419 27.6997 18.7613 25.2591 19.1376C23.0487 19.4572 20.7687 19.7866 18.7893 20.06Z' fill='%23CE8D4F'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M18.7776 23.2968C18.4384 23.3459 18.1203 23.121 18.0555 22.7859C17.9907 22.4389 18.2169 22.104 18.5641 22.0327C20.5874 21.6376 30.1328 20.1775 32.184 19.9498C32.5343 19.882 32.8733 20.1097 32.9414 20.4584C33.0095 20.8071 32.7808 21.1445 32.4305 21.2125C32.395 21.2193 32.3591 21.223 32.323 21.224C30.2914 21.4489 20.8208 22.8991 18.8123 23.2921L18.7776 23.2968Z' fill='%23CE8D4F'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.0649 26.7297C18.7258 26.7788 18.4077 26.5538 18.3429 26.2187C18.2892 25.8677 18.5318 25.5396 18.8846 25.4862L27.4521 24.1937C27.8001 24.1151 28.1463 24.332 28.2253 24.6784C28.3043 25.0248 28.0864 25.3694 27.7384 25.4481C27.7013 25.4564 27.6637 25.4617 27.6258 25.4632L19.0634 26.7551L19.0649 26.7297Z' fill='%23CE8D4F'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M40.2479 39.7627C40.2479 32.3032 40.2959 28.3524 40.1999 27.6919C40.268 26.9559 40.0522 26.4547 39.3528 26.4986C33.3577 25.9892 23.9017 26.3909 21.2081 26.0409C20.8443 25.5515 20.4686 24.1153 20.0809 23.6538C19.6933 23.1924 18.782 22.0626 18.3624 21.4497C17.719 20.4115 17.1634 20.3637 14.9254 20.4391C2.98726 20.7894 1.5086 20.153 0.781277 20.6542C-0.441731 23.4867 0.581443 52.3544 0.649355 52.4459C0.617351 53.8145 1.20885 53.8544 1.84836 53.8505C12.6831 53.7986 38.5532 54.3317 39.1568 53.9936H39.2048C39.4074 53.9878 39.5985 53.8984 39.7323 53.7469C39.8711 53.5814 39.9486 53.3736 39.9521 53.1581C40.268 48.7103 40.252 44.2303 40.2479 39.7627Z' fill='%2363C0F9'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M7.62343 26.4707C5.96075 26.4707 4.57401 26.4707 4.19425 26.4229C3.75282 26.3746 3.43433 25.9792 3.48292 25.5398C3.53152 25.1002 3.92865 24.7831 4.37027 24.8315C5.0935 24.9071 11.1203 24.8556 14.9933 24.7997C15.4345 24.793 15.7979 25.1441 15.8045 25.5835C15.8112 26.0229 15.4585 26.3847 15.0171 26.3911C14.3817 26.407 10.5649 26.4627 7.62343 26.4707Z' fill='white'/%3E%3Cmask id='mask1' mask-type='alpha' maskUnits='userSpaceOnUse' x='33' y='49' width='8' height='6'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M33.7915 49.712H40.8975V54.1575H33.7915V49.712Z' fill='white'/%3E%3C/mask%3E%3Cg mask='url(%23mask1)'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M40.1294 49.712L40.1128 52.8574C40.1128 52.8574 40.1169 53.8667 40.8975 54.061C38.3606 54.2553 33.7915 54.0964 33.7915 54.0964L36.9904 50.1377L40.1128 49.7491' fill='%2363C0F9'/%3E%3C/g%3E%3C/svg%3E%0A\")!important;background-position-y:45%!important;color:#0b0e1f!important}.uppy-Dashboard-inner{background-color:#fff!important;border:1px solid #e1e6ee!important;border-radius:8px!important}.uppy-size--md .uppy-Dashboard-Item{height:190px!important}.uppy-Dashboard-Item-action:focus{box-shadow:none!important}.uppy-StatusBar-actionBtn--retry{background-color:#e12d42!important}.uppy-DashboardContent-bar,.uppy-StatusBar-actions{background-color:transparent!important}.uppy-Dashboard-Item.is-complete .uppy-Dashboard-Item-progress{left:-8px!important;right:unset!important}.uppy-StatusBar-actions{justify-content:flex-end!important;right:10px!important}.uppy-StatusBar-spinner{fill:var(--primary)!important}.uppy-c-icon .uppy-Dashboard-Item-progressIcon--circle{fill:var(--primary)!important}.uppy-StatusBar-progress{background-color:var(--primary)!important}.uppy-Dashboard-uploadCount,.uppy-StatusBar.is-complete .uppy-StatusBar-progress,.uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn--upload{background-color:var(--primary)!important}.uppy-StatusBar.is-waiting .uppy-StatusBar-actionBtn--upload:hover{background-color:#3c55bd!important}.uppy-StatusBar.is-complete .uppy-StatusBar-statusIndicator{color:var(--primary)!important}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$g = undefined;
  /* module identifier */
  const __vue_module_identifier__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$g = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$g = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$g, staticRenderFns: __vue_staticRenderFns__$g },
    __vue_inject_styles__$g,
    __vue_script__$d,
    __vue_scope_id__$g,
    __vue_is_functional_template__$g,
    __vue_module_identifier__$g,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const HOVER_CLOSE_DELAY_MS = 100;

var script$c = defineComponent({
  name: 'TViewMorePopover',

  components: { TMenu: VMenu, TList: VList, TListItem: VListItem },
  props: {
    /**
     * A boolean to tell the popover to not open on trigger
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * A string to tell the component where to position the popover
     */
    position: {
      type: String,
      required: false,
      default: 'right',
      validator: (value) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
    /**
     * A number to add a offset based on the positioning
     */
    positionOffset: {
      type: Number,
      required: false,
      default: 5,
    },
    /**
     * A string to tell the component when to trigger the popover
     */
    openOn: {
      type: String,
      required: false,
      default: 'hover',
      validator: (value) => ['click', 'hover'].includes(value),
    },
    /**
     * An array of items that will be displayed in the popover
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * A number to set how many items to display in the popover list
     */
    listSize: {
      type: Number,
      required: false,
      default: 4,
    },
    /**
     * A number/string to set a minimum width for the popover
     */
    minWidth: {
      type: [Number, String],
      required: false,
      default: 'auto',
    },
    /**
     * A number/string to set a maximum width for the popover
     */
    maxWidth: {
      type: [Number, String],
      required: false,
      default: 'auto',
    },
  },

  // Documents what events are emitted from this component. Required in Vue 3
  emits: ['click:more'],

  setup(props, { emit }) {
    // Used to configure the TMenu component
    const menuProperties = computed(() => {
      const baseProps = {
        'min-width': props.minWidth,
        disabled: props.disabled,
        'max-width': props.maxWidth,
        [`open-on-${props.openOn}`]: true,
        [props.position]: true,
        [`nudge-${props.position}`]: props.positionOffset,
        [`offset-${
          ['top', 'bottom'].includes(props.position) ? 'y' : 'x'
        }`]: true,
        'z-index': 999,
      };

      if (props.openOn === 'hover')
        baseProps['close-delay'] = HOVER_CLOSE_DELAY_MS;

      return baseProps;
    });

    const itemsToDisplay = computed(() => props.items.slice(0, props.listSize));

    const remainingItems = computed(
      () => props.items.length - itemsToDisplay.value.length,
    );

    const onClickMore = () => {
      emit('click:more', { items: props.items });
    };

    return {
      menuProperties,
      itemsToDisplay,
      remainingItems,
      onClickMore,
    };
  },
});

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$f = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('t-menu',_vm._b({scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
return [_vm._t("trigger",null,{"on":on})]}}],null,true)},'t-menu',_vm.menuProperties,false),[_vm._v(" "),_c('div',{staticClass:"view-more-menu t-flex t-flex-col t-px-4 t-bg-white"},[_c('TList',{staticClass:"t-pt-3 t-pb-1"},_vm._l((_vm.itemsToDisplay),function(item,index){return _c('TListItem',{key:index,staticClass:"t-box-border t-select-none t-px-0 t-min-h-0 t-pb-3"},[_vm._t("list-item",null,{"item":item})],2)}),1),_vm._v(" "),(!!_vm.remainingItems)?_c('div',{staticClass:"t-border-0 t-border-t-2 t-border-solid t-border-default t-py-4"},[_c('span',{staticClass:"\n          t-select-none t-text-default t-font-semibold t-cursor-pointer\n          hover:t-underline\n        ",on:{"click":_vm.onClickMore}},[_vm._v("\n        +"+_vm._s(_vm.remainingItems)+" More\n      ")])]):_vm._e()],1)])};
var __vue_staticRenderFns__$f = [];

  /* style */
  const __vue_inject_styles__$f = function (inject) {
    if (!inject) return
    inject("data-v-5227d748_0", { source: ".view-more-menu[data-v-5227d748]{min-width:10rem;max-width:20rem}.v-menu__content[data-v-5227d748]{box-shadow:0 4px 12px rgba(11,14,31,.12)}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$f = "data-v-5227d748";
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$f = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$c,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f,
    __vue_module_identifier__$f,
    false,
    createInjector,
    undefined,
    undefined
  );

//

/*
  slots for inline and advanced filters in popover menu
*/

var script$b = {
  name: 'TFilterSection',
  components: { TMenu: VMenu, TBtn: __vue_component__$_, TChip: VChip, TIcon: VIcon$1 },
  props: {
    filterCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const popoverOpen = ref(false);
    const { filterCount } = toRefs(props);

    const closePopover = () => {
      popoverOpen.value = false;
    };

    const hasFilters = computed(() => filterCount.value > 0);

    return {
      popoverOpen,
      closePopover,
      mdiFilterVariant,
      hasFilters,
    };
  },
};

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$e = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-flex t-items-center t-relative t-space-between"},[_vm._t("inline"),_vm._v(" "),_c('TMenu',{attrs:{"close-on-content-click":false,"offset-y":"","nudge-bottom":5,"content-class":"t-p-5 t-bg-white"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('TBtn',_vm._g(_vm._b({staticClass:"focus:t-ring-2",attrs:{"fab":!_vm.hasFilters,"small":!_vm.hasFilters,"rounded":_vm.hasFilters,"color":_vm.hasFilters ? 'primary' : 'gray',"depressed":"","outlined":!_vm.hasFilters}},'TBtn',attrs,false),on),[_c('span',{staticClass:"t-leading-none"},[_c('TIcon',{staticClass:"t-fill-currentColor",attrs:{"small":""}},[_vm._v("\n            "+_vm._s(_vm.mdiFilterVariant)+"\n          ")]),_vm._v(" "),(_vm.hasFilters)?_c('span',[_c('TChip',{staticClass:"t-ml-1 t-font-semibold",attrs:{"color":"white","x-small":""}},[_vm._v("\n              "+_vm._s(_vm.filterCount)+"\n            ")])],1):_vm._e()],1)])]}}]),model:{value:(_vm.popoverOpen),callback:function ($$v) {_vm.popoverOpen=$$v;},expression:"popoverOpen"}},[_vm._v(" "),_vm._t("default"),_vm._v(" "),_c('div',{staticClass:"t-flex t-mt-5"},[_c('TBtn',{attrs:{"text":""},on:{"click":function($event){return _vm.$emit('clear-filters')}}},[_vm._v(" Clear all ")]),_vm._v(" "),_c('TBtn',{staticClass:"t-mr-0 t-ml-auto",attrs:{"depressed":"","color":"primary"},on:{"click":function($event){return _vm.closePopover()}}},[_vm._v("\n        Done\n      ")])],1)],2)],2)};
var __vue_staticRenderFns__$e = [];

  /* style */
  const __vue_inject_styles__$e = undefined;
  /* scoped */
  const __vue_scope_id__$e = undefined;
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$e = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$b,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e,
    __vue_module_identifier__$e,
    false,
    undefined,
    undefined,
    undefined
  );

/* script */

/* template */
var __vue_render__$d = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-inline-block t-shadow-lg t-rounded-md overflow-hidden"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$d = [];

  /* style */
  const __vue_inject_styles__$d = undefined;
  /* scoped */
  const __vue_scope_id__$d = undefined;
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$d = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    {},
    __vue_scope_id__$d,
    __vue_is_functional_template__$d,
    __vue_module_identifier__$d,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//

var script$a = {
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  computed: {
    shouldShowAsterisk() {
      return !!this.context.rules.find((i) => i.ruleName === 'required');
    },
  },
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$c = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('label',{attrs:{"for":_vm.context.id}},[_vm._v("\n  "+_vm._s(_vm.context.label)+"\n  "),(_vm.shouldShowAsterisk)?_c('span',{staticClass:"asterisk"},[_vm._v("*")]):_vm._e()])};
var __vue_staticRenderFns__$c = [];

  /* style */
  const __vue_inject_styles__$c = undefined;
  /* scoped */
  const __vue_scope_id__$c = undefined;
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$c = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$a,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$c,
    false,
    undefined,
    undefined,
    undefined
  );

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */

function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Now `isValid` doesn't throw an exception
 *   if the first argument is not an instance of Date.
 *   Instead, argument is converted beforehand using `toDate`.
 *
 *   Examples:
 *
 *   | `isValid` argument        | Before v2.0.0 | v2.0.0 onward |
 *   |---------------------------|---------------|---------------|
 *   | `new Date()`              | `true`        | `true`        |
 *   | `new Date('2016-01-01')`  | `true`        | `true`        |
 *   | `new Date('')`            | `false`       | `false`       |
 *   | `new Date(1488370835081)` | `true`        | `true`        |
 *   | `new Date(NaN)`           | `false`       | `false`       |
 *   | `'2016-01-01'`            | `TypeError`   | `false`       |
 *   | `''`                      | `TypeError`   | `false`       |
 *   | `1488370835081`           | `TypeError`   | `true`        |
 *   | `NaN`                     | `TypeError`   | `false`       |
 *
 *   We introduce this change to make *date-fns* consistent with ECMAScript behavior
 *   that try to coerce arguments to the expected type
 *   (which is also the case with other *date-fns* functions).
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * var result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * var result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * var result = isValid(new Date(''))
 * //=> false
 */

function isValid(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  return !isNaN(date);
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};
function formatDistance(token, count, options) {
  options = options || {};
  var result;

  if (typeof formatDistanceLocale[token] === 'string') {
    result = formatDistanceLocale[token];
  } else if (count === 1) {
    result = formatDistanceLocale[token].one;
  } else {
    result = formatDistanceLocale[token].other.replace('{{count}}', count);
  }

  if (options.addSuffix) {
    if (options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
}

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
var formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};
function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
}

function buildLocalizeFn(args) {
  return function (dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};
    var context = options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex; // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challange you to try to remove it!

    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
}; // Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.

var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

function ordinalNumber(dirtyNumber, _dirtyOptions) {
  var number = Number(dirtyNumber); // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`:
  //
  //   var options = dirtyOptions || {}
  //   var unit = String(options.unit)
  //
  // where `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'

  var rem100 = number % 100;

  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';

      case 2:
        return number + 'nd';

      case 3:
        return number + 'rd';
    }
  }

  return number + 'th';
}

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return Number(quarter) - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
var localize$1 = localize;

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }

  return undefined;
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }

  return undefined;
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
var match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */

var locale = {
  code: 'en-US',
  formatDistance: formatDistance,
  formatLong: formatLong$1,
  formatRelative: formatRelative,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0
    /* Sunday */
    ,
    firstWeekContainsDate: 1
  }
};
var defaultLocale = locale;

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */

function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();

  while (output.length < targetLength) {
    output = '0' + output;
  }

  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters$2 = {
  // Year
  y: function (date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function (date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function (date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function (date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();

      case 'aaa':
        return dayPeriodEnumValue;

      case 'aaaaa':
        return dayPeriodEnumValue[0];

      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function (date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function (date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function (date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function (date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function (date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};
var formatters$3 = formatters$2;

var MILLISECONDS_IN_DAY = 86400000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeek(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn = locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate, dirtyOptions);
  var year = date.getUTCFullYear();
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, dirtyOptions);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, dirtyOptions);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate);
  var year = getUTCWeekYear(dirtyDate, dirtyOptions);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, dirtyOptions);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};
/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

var formatters = {
  // Era
  G: function (date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;

    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B

      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ

      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }

    return formatters$3.y(date, token);
  },
  // Local week-numbering year
  Y: function (date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear; // Two digit year

    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    } // Ordinal number


    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    } // Padding


    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function (date, token) {
    var isoWeekYear = getUTCISOWeekYear(date); // Padding

    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...

      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...

      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      case 'M':
      case 'MM':
        return formatters$3.M(date, token);
      // 1st, 2nd, ..., 12th

      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D

      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December

      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12

      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th

      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D

      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December

      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function (date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }

    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function (date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }

    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function (date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }

    return formatters$3.d(date, token);
  },
  // Day of year
  D: function (date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();

    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th

      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th

      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T

      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu

      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday

      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02

      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd

      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue

      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return formatters$3.h(date, token);
  },
  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }

    return formatters$3.H(date, token);
  },
  // Hour [0-11]
  K: function (date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function (date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function (date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }

    return formatters$3.m(date, token);
  },
  // Second
  s: function (date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }

    return formatters$3.s(date, token);
  },
  // Fraction of second
  S: function (date, token) {
    return formatters$3.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z';
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`

      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`

      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`

      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`

      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;

  if (minutes === 0) {
    return sign + String(hours);
  }

  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }

  return formatTimezone(offset, dirtyDelimiter);
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

var formatters$1 = formatters;

function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });

    case 'PP':
      return formatLong.date({
        width: 'medium'
      });

    case 'PPP':
      return formatLong.date({
        width: 'long'
      });

    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
}

function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });

    case 'pp':
      return formatLong.time({
        width: 'medium'
      });

    case 'ppp':
      return formatLong.time({
        width: 'long'
      });

    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
}

function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/);
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;

    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;

    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;

    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }

  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
}

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
var longFormatters$1 = longFormatters;

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  }
}

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Su, Sa        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 9. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The second argument is now required for the sake of explicitness.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   format(new Date(2016, 0, 1))
 *
 *   // v2.0.0 onward
 *   format(new Date(2016, 0, 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
 *   ```
 *
 * - New format string API for `format` function
 *   which is based on [Unicode Technical Standard #35](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
 *   See [this post](https://blog.date-fns.org/post/unicode-tokens-in-date-fns-v2-sreatyki91jg) for more details.
 *
 * - Characters are now escaped using single quote symbols (`'`) instead of square brackets.
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * var result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * var result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * var result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, dirtyOptions) {
  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var options = dirtyOptions || {};
  var locale = options.locale || defaultLocale;
  var localeFirstWeekContainsDate = locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var localeWeekStartsOn = locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (!locale.localize) {
    throw new RangeError('locale must contain localize property');
  }

  if (!locale.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376


  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong, formatterOptions);
    }

    return substring;
  }).join('').match(formattingTokensRegExp).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }

    var firstCharacter = substring[0];

    if (firstCharacter === "'") {
      return cleanEscapedString(substring);
    }

    var formatter = formatters$1[firstCharacter];

    if (formatter) {
      if (!options.useAdditionalWeekYearTokens && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      if (!options.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      return formatter(utcDate, substring, locale.localize, formatterOptions);
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return substring;
  }).join('');
  return result;
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'");
}

var MILLISECONDS_IN_HOUR = 3600000;
var MILLISECONDS_IN_MINUTE = 60000;
var DEFAULT_ADDITIONAL_DIGITS = 2;
var patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/
};
var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;
/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The previous `parse` implementation was renamed to `parseISO`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward
 *   parseISO('2016-01-01')
 *   ```
 *
 * - `parseISO` now validates separate date and time values in ISO-8601 strings
 *   and returns `Invalid Date` if the date is invalid.
 *
 *   ```javascript
 *   parseISO('2018-13-32')
 *   //=> Invalid Date
 *   ```
 *
 * - `parseISO` now doesn't fall back to `new Date` constructor
 *   if it fails to parse a string argument. Instead, it returns `Invalid Date`.
 *
 * @param {String} argument - the value to convert
 * @param {Object} [options] - an object with options.
 * @param {0|1|2} [options.additionalDigits=2] - the additional number of digits in the extended year format
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.additionalDigits` must be 0, 1 or 2
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * var result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * var result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */

function parseISO(argument, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var additionalDigits = options.additionalDigits == null ? DEFAULT_ADDITIONAL_DIGITS : toInteger(options.additionalDigits);

  if (additionalDigits !== 2 && additionalDigits !== 1 && additionalDigits !== 0) {
    throw new RangeError('additionalDigits must be 0, 1 or 2');
  }

  if (!(typeof argument === 'string' || Object.prototype.toString.call(argument) === '[object String]')) {
    return new Date(NaN);
  }

  var dateStrings = splitDateString(argument);
  var date;

  if (dateStrings.date) {
    var parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate(parseYearResult.restDateString, parseYearResult.year);
  }

  if (isNaN(date) || !date) {
    return new Date(NaN);
  }

  var timestamp = date.getTime();
  var time = 0;
  var offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);

    if (isNaN(time) || time === null) {
      return new Date(NaN);
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);

    if (isNaN(offset)) {
      return new Date(NaN);
    }
  } else {
    var dirtyDate = new Date(timestamp + time); // js parsed string assuming it's in UTC timezone
    // but we need it to be parsed in our timezone
    // so we use utc values to build date in our timezone.
    // Year values from 0 to 99 map to the years 1900 to 1999
    // so set year explicitly with setFullYear.

    var result = new Date(0);
    result.setFullYear(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate());
    result.setHours(dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds(), dirtyDate.getUTCMilliseconds());
    return result;
  }

  return new Date(timestamp + time + offset);
}

function splitDateString(dateString) {
  var dateStrings = {};
  var array = dateString.split(patterns.dateTimeDelimiter);
  var timeString; // The regex match should only return at maximum two array elements.
  // [date], [time], or [date, time].

  if (array.length > 2) {
    return dateStrings;
  }

  if (/:/.test(array[0])) {
    dateStrings.date = null;
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];

    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(dateStrings.date.length, dateString.length);
    }
  }

  if (timeString) {
    var token = patterns.timezone.exec(timeString);

    if (token) {
      dateStrings.time = timeString.replace(token[1], '');
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings;
}

function parseYear(dateString, additionalDigits) {
  var regex = new RegExp('^(?:(\\d{4}|[+-]\\d{' + (4 + additionalDigits) + '})|(\\d{2}|[+-]\\d{' + (2 + additionalDigits) + '})$)');
  var captures = dateString.match(regex); // Invalid ISO-formatted year

  if (!captures) return {
    year: null
  };
  var year = captures[1] && parseInt(captures[1]);
  var century = captures[2] && parseInt(captures[2]);
  return {
    year: century == null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length)
  };
}

function parseDate(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return null;
  var captures = dateString.match(dateRegex); // Invalid ISO-formatted string

  if (!captures) return null;
  var isWeekDate = !!captures[4];
  var dayOfYear = parseDateUnit(captures[1]);
  var month = parseDateUnit(captures[2]) - 1;
  var day = parseDateUnit(captures[3]);
  var week = parseDateUnit(captures[4]);
  var dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN);
    }

    return dayOfISOWeekYear(year, week, dayOfWeek);
  } else {
    var date = new Date(0);

    if (!validateDate(year, month, day) || !validateDayOfYearDate(year, dayOfYear)) {
      return new Date(NaN);
    }

    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date;
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1;
}

function parseTime(timeString) {
  var captures = timeString.match(timeRegex);
  if (!captures) return null; // Invalid ISO-formatted time

  var hours = parseTimeUnit(captures[1]);
  var minutes = parseTimeUnit(captures[2]);
  var seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN;
  }

  return hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE + seconds * 1000;
}

function parseTimeUnit(value) {
  return value && parseFloat(value.replace(',', '.')) || 0;
}

function parseTimezone(timezoneString) {
  if (timezoneString === 'Z') return 0;
  var captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0;
  var sign = captures[1] === '+' ? -1 : 1;
  var hours = parseInt(captures[2]);
  var minutes = captures[3] && parseInt(captures[3]) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN;
  }

  return sign * (hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE);
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  var date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  var fourthOfJanuaryDay = date.getUTCDay() || 7;
  var diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
} // Validation functions
// February is null to handle the leap year (using ||)


var daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100;
}

function validateDate(year, month, date) {
  return month >= 0 && month <= 11 && date >= 1 && date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28));
}

function validateDayOfYearDate(year, dayOfYear) {
  return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}

function validateWeekDate(_year, week, day) {
  return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}

function validateTime(hours, minutes, seconds) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}

function validateTimezone(_hours, minutes) {
  return minutes >= 0 && minutes <= 59;
}

//

var script$9 = {
  name: 'VFDate',
  components: {
    TDatePicker: VDatePicker,
    TIcon: VIcon$1,
    TMenu: VMenu,
    TTextField: VTextField,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const formConfig = inject$1('form-config');

    const isActive = ref(false);

    const color = computed(() => {
      return formConfig.color || null;
    });

    function onDatePickerInput(value) {
      isActive.value = false;
      // Note: VueFormulate expects direct modification of `context.model`
      // eslint-disable-next-line no-param-reassign, vue/no-mutating-props
      props.context.model = value;
    }

    const displayedDate = computed(() => {
      if (!props.context.model) {
        return null;
      }
      const parsedDate = parseISO(props.context.model);
      return format(parsedDate, 'EEEE do MMMM yyyy');
    });

    return {
      color,
      isActive,
      displayedDate,
      onDatePickerInput,
      mdiCalendarBlankOutline,
    };
  },
};

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$b = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:("formulate-input-element formulate-input-element--" + (_vm.context.type)),attrs:{"data-type":_vm.context.type}},[_c('TMenu',{attrs:{"close-on-content-click":false,"nudge-right":40,"transition":"scale-transition","origin":"top right","offset-y":"","rounded":"lg","min-width":"auto"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('TTextField',_vm._g(_vm._b({attrs:{"value":_vm.displayedDate,"background-color":"var(--token-ui-bg)","clearable":"","readonly":"","flat":"","solo":"","dense":"","hide-details":"","show-current":false,"placeholder":"Choose a date"},on:{"blur":_vm.context.blurHandler,"click:clear":function($event){_vm.context.model = null;}},scopedSlots:_vm._u([{key:"prepend-inner",fn:function(){return [_c('TIcon',{staticClass:"t-mr-2",attrs:{"small":"","color":_vm.color}},[_vm._v("\n            "+_vm._s(_vm.mdiCalendarBlankOutline)+"\n          ")])]},proxy:true}],null,true)},'TTextField',attrs,false),on))]}}]),model:{value:(_vm.isActive),callback:function ($$v) {_vm.isActive=$$v;},expression:"isActive"}},[_vm._v(" "),_c('TDatePicker',{staticClass:"t-border-none",attrs:{"value":_vm.context.model,"color":_vm.color,"flat":"","solo":"","dense":"","no-title":"","hide-details":"","max":"9999-12-31"},on:{"input":_vm.onDatePickerInput}})],1)],1)};
var __vue_staticRenderFns__$b = [];

  /* style */
  const __vue_inject_styles__$b = undefined;
  /* scoped */
  const __vue_scope_id__$b = undefined;
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$b = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$9,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b,
    __vue_module_identifier__$b,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$8 = {
  name: 'VFAttachment',
  components: {
    TUpload: __vue_component__$g,
  },
  props: {
    s3PresignFunction: {
      type: Function,
      required: true,
    },
    context: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    maxNumOfFiles: { type: Number, required: false, default: 1 },
    // Note: `maxFileSize` is expected to be in MB
    maxFileSize: { type: Number, required: false, default: 1 },
    allowedFileTypes: { type: Array, required: false, default: undefined },
  },
  setup() {
    const formConfig = inject$1('form-config');

    const color = computed(() => {
      return formConfig.color || null;
    });

    return {
      color,
    };
  },
  computed: {
    maxFileSizeInBytes() {
      return this.maxFileSize * 1024 * 1024;
    },
    allowedFileTypesExtensions() {
      if (!this.allowedFileTypes?.length) {
        return Object.values(fileTypeGroups).flat();
      }

      const resolvedArray = [];

      Object.entries(fileTypeGroups).forEach(([group, items]) => {
        if (this.allowedFileTypes.includes(group)) {
          resolvedArray.push(...items);
        }
      });

      // Handle specific item from form builder
      if (this.allowedFileTypes.includes('pdf')) {
        resolvedArray.push('.pdf');
      }

      return resolvedArray;
    },
    allowedFileTypesString() {
      return this.allowedFileTypes
        .map((x, index) => {
          const alteredString = x;
          if (index === 0) {
            return x.charAt(0).toUpperCase() + x.slice(1);
          }
          if (index === this.allowedFileTypes.length - 1) {
            return ` and ${alteredString}`;
          }
          return `, ${alteredString}`;
        })
        .join('');
    },
    note() {
      const allowedFileTypesSentence = this.allowedFileTypes?.length
        ? `${this.allowedFileTypesString} files only.`
        : '';
      const maxNumOfFilesSentence =
        this.maxNumOfFiles > 1
          ? `Up to ${this.maxNumOfFiles} files.`
          : 'Only 1 file.';
      const maxFileSizeSentence =
        this.maxFileSize > 0
          ? `Files can only be up to ${this.maxFileSize}MB in size.`
          : '';

      return `${allowedFileTypesSentence} ${maxNumOfFilesSentence} ${maxFileSizeSentence}`.trim();
    },
  },
  methods: {
    onUpload(val) {
      this.context.rootEmit('upload', val);
    },
    onComplete(val) {
      this.context.rootEmit('upload-complete', val);
    },
    onUploadSuccess({ file }) {
      if (!Array.isArray(this.context.model)) {
        // eslint-disable-next-line vue/no-mutating-props
        this.context.model = [];
      }

      // eslint-disable-next-line vue/no-mutating-props
      this.context.model.push({
        ref: file.meta.ref,
        filename: file.name,
      });
    },
    onFileRemoved({ file }) {
      // eslint-disable-next-line vue/no-mutating-props
      this.context.model = this.context.model.filter(
        (i) => i.ref !== file.meta.ref,
      );
    },
    onUploadError({ error }) {
      this.context.rootEmit('alert', {
        type: 'error',
        message: `Upload failed: ${error.message}`,
      });
    },
    onRestrictionFailed({ error }) {
      let { message } = error;

      // Prevent showing the full list of extensions
      const fileTypeRestrictionPrefix = 'You can only upload:';
      if (message.includes(fileTypeRestrictionPrefix)) {
        message = `${fileTypeRestrictionPrefix} ${this.allowedFileTypesString} files`;
      }

      this.context.rootEmit('alert', { type: 'error', message });
    },
  },
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$a = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[(_vm.isReadOnly)?_c('div',{staticClass:"\n      t-h-28\n      t-flex\n      t-flex-col\n      t-justify-center\n      t-items-center\n      t-border\n      t-border-solid\n      t-rounded-md\n      t-my-4\n      t-p-1\n      custom-border-color\n    "},[_c('p',{staticClass:"t-text-default t-text-neutral-30"},[_vm._v("File upload box")])]):_c('TUpload',{attrs:{"s3-presign-function":_vm.s3PresignFunction,"auto-proceed":"","disable-done-button":"","show-remove-button-after-complete":"","height":"320px","max-number-of-files":_vm.maxNumOfFiles,"max-file-size":_vm.maxFileSizeInBytes,"allowed-file-types":_vm.allowedFileTypesExtensions,"note":_vm.note,"color":_vm.color},on:{"upload":_vm.onUpload,"complete":_vm.onComplete,"upload-success":_vm.onUploadSuccess,"upload-error":_vm.onUploadError,"file-removed":_vm.onFileRemoved,"restriction-failed":_vm.onRestrictionFailed}})],1)};
var __vue_staticRenderFns__$a = [];

  /* style */
  const __vue_inject_styles__$a = function (inject) {
    if (!inject) return
    inject("data-v-503f9403_0", { source: ".custom-border-color[data-v-503f9403]{border-color:#e1e6ee}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$a = "data-v-503f9403";
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$a = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$8,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$a,
    false,
    createInjector,
    undefined,
    undefined
  );

var e,t,n="function"==typeof Map?new Map:(e=[],t=[],{has:function(t){return e.indexOf(t)>-1},get:function(n){return t[e.indexOf(n)]},set:function(n,o){-1===e.indexOf(n)&&(e.push(n),t.push(o));},delete:function(n){var o=e.indexOf(n);o>-1&&(e.splice(o,1),t.splice(o,1));}}),o=function(e){return new Event(e,{bubbles:!0})};try{new Event("test");}catch(e){o=function(e){var t=document.createEvent("Event");return t.initEvent(e,!0,!1),t};}function r(e){var t=n.get(e);t&&t.destroy();}function i(e){var t=n.get(e);t&&t.update();}var l=null;"undefined"==typeof window||"function"!=typeof window.getComputedStyle?((l=function(e){return e}).destroy=function(e){return e},l.update=function(e){return e}):((l=function(e,t){return e&&Array.prototype.forEach.call(e.length?e:[e],function(e){return function(e){if(e&&e.nodeName&&"TEXTAREA"===e.nodeName&&!n.has(e)){var t,r=null,i=null,l=null,d=function(){e.clientWidth!==i&&c();},a=function(t){window.removeEventListener("resize",d,!1),e.removeEventListener("input",c,!1),e.removeEventListener("keyup",c,!1),e.removeEventListener("autosize:destroy",a,!1),e.removeEventListener("autosize:update",c,!1),Object.keys(t).forEach(function(n){e.style[n]=t[n];}),n.delete(e);}.bind(e,{height:e.style.height,resize:e.style.resize,overflowY:e.style.overflowY,overflowX:e.style.overflowX,wordWrap:e.style.wordWrap});e.addEventListener("autosize:destroy",a,!1),"onpropertychange"in e&&"oninput"in e&&e.addEventListener("keyup",c,!1),window.addEventListener("resize",d,!1),e.addEventListener("input",c,!1),e.addEventListener("autosize:update",c,!1),e.style.overflowX="hidden",e.style.wordWrap="break-word",n.set(e,{destroy:a,update:c}),"vertical"===(t=window.getComputedStyle(e,null)).resize?e.style.resize="none":"both"===t.resize&&(e.style.resize="horizontal"),r="content-box"===t.boxSizing?-(parseFloat(t.paddingTop)+parseFloat(t.paddingBottom)):parseFloat(t.borderTopWidth)+parseFloat(t.borderBottomWidth),isNaN(r)&&(r=0),c();}function u(t){var n=e.style.width;e.style.width="0px",e.style.width=n,e.style.overflowY=t;}function s(){if(0!==e.scrollHeight){var t=function(e){for(var t=[];e&&e.parentNode&&e.parentNode instanceof Element;)e.parentNode.scrollTop&&t.push({node:e.parentNode,scrollTop:e.parentNode.scrollTop}),e=e.parentNode;return t}(e),n=document.documentElement&&document.documentElement.scrollTop;e.style.height="",e.style.height=e.scrollHeight+r+"px",i=e.clientWidth,t.forEach(function(e){e.node.scrollTop=e.scrollTop;}),n&&(document.documentElement.scrollTop=n);}}function c(){s();var t=Math.round(parseFloat(e.style.height)),n=window.getComputedStyle(e,null),r="content-box"===n.boxSizing?Math.round(parseFloat(n.height)):e.offsetHeight;if(r<t?"hidden"===n.overflowY&&(u("scroll"),s(),r="content-box"===n.boxSizing?Math.round(parseFloat(window.getComputedStyle(e,null).height)):e.offsetHeight):"hidden"!==n.overflowY&&(u("hidden"),s(),r="content-box"===n.boxSizing?Math.round(parseFloat(window.getComputedStyle(e,null).height)):e.offsetHeight),l!==r){l=r;var i=o("autosize:resized");try{e.dispatchEvent(i);}catch(e){}}}}(e)}),e}).destroy=function(e){return e&&Array.prototype.forEach.call(e.length?e:[e],r),e},l.update=function(e){return e&&Array.prototype.forEach.call(e.length?e:[e],i),e});var d=l;

//

var script$7 = {
  name: 'VFTextarea',
  props: {
    context: {
      type: Object,
      required: true,
    },
  },
  computed: {
    type() {
      return this.context.type;
    },
    attributes() {
      return this.context.attributes || {};
    },
    hasValue() {
      return this.context.hasValue;
    },
  },
  mounted() {
    d(this.$refs.textarea);
  },
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$9 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{class:_vm.context.classes.element,attrs:{"data-type":"textarea"}},[_c('FormulateSlot',{attrs:{"name":"prefix","context":_vm.context}},[(_vm.context.slotComponents.prefix)?_c(_vm.context.slotComponents.prefix,{tag:"component",attrs:{"context":_vm.context}}):_vm._e()],1),_vm._v(" "),_c('textarea',_vm._g(_vm._b({directives:[{name:"model",rawName:"v-model",value:(_vm.context.model),expression:"context.model"}],ref:"textarea",domProps:{"value":(_vm.context.model)},on:{"blur":_vm.context.blurHandler,"input":function($event){if($event.target.composing){ return; }_vm.$set(_vm.context, "model", $event.target.value);}}},'textarea',_vm.attributes,false),_vm.$listeners)),_vm._v(" "),_c('FormulateSlot',{attrs:{"name":"suffix","context":_vm.context}},[(_vm.context.slotComponents.suffix)?_c(_vm.context.slotComponents.suffix,{tag:"component",attrs:{"context":_vm.context}}):_vm._e()],1)],1)};
var __vue_staticRenderFns__$9 = [];

  /* style */
  const __vue_inject_styles__$9 = function (inject) {
    if (!inject) return
    inject("data-v-7f887d48_0", { source: "textarea[data-v-7f887d48]{resize:none;min-height:75px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$9 = "data-v-7f887d48";
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$9 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$7,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9,
    __vue_module_identifier__$9,
    false,
    createInjector,
    undefined,
    undefined
  );

var COLORS$1 = Object.freeze({
  black: TOKEN_COLOR_BLACK,
  blue: TOKEN_COLOR_BLUE_BASE,
  brown: TOKEN_COLOR_BROWN_BASE,
  green: TOKEN_COLOR_GREEN_BASE,
  grey: TOKEN_COLOR_GREY_BASE,
  indigo: TOKEN_COLOR_INDIGO_BASE,
  orange: TOKEN_COLOR_ORANGE_40,
  pink: TOKEN_COLOR_PINK_BASE,
  purple: TOKEN_COLOR_PURPLE_BASE,
  red: TOKEN_COLOR_RED_BASE,
  teal: TOKEN_COLOR_TEAL_BASE,
  white: TOKEN_COLOR_WHITE,
  yellow: TOKEN_COLOR_YELLOW_BASE,
});

//

// Globally register components for VueFormulate to use
Vue.component('FormLabel', __vue_component__$c);
Vue.component('VFDate', __vue_component__$b);
Vue.component('VFAttachment', __vue_component__$a);
Vue.component('VFTextarea', __vue_component__$9);

Vue.use(VueFormulate, {
  slotComponents: {
    // Use the string name of the globally registered component.
    label: 'FormLabel',
  },
  library: {
    date: {
      component: 'VFDate',
    },
    file: {
      component: 'VFAttachment',
      slotProps: {
        component: [
          'maxNumOfFiles',
          'maxFileSize',
          'allowedFileTypes',
          's3PresignFunction',
          'isReadOnly',
        ],
      },
    },
    textarea: {
      component: 'VFTextarea',
    },
  },
});

const getPlaceholderForInput = (type) => {
  switch (type) {
    case 'text':
    case 'textarea':
      return 'Enter your answer';
    case 'number':
      return 'Enter a number';
    case 'email':
      return 'Enter an email address';
    case 'select':
      return 'Choose one';
    default:
      return '';
  }
};

const compact = (array) => {
  let index = -1;
  const length = array == null ? 0 : array.length;
  let resIndex = 0;
  const result = [];

  /* eslint-disable no-plusplus */
  while (++index < length) {
    const value = array[index];
    if (value) {
      result[resIndex++] = value;
    }
  }
  return result;
};

const DEFAULT_COLOR = 'indigo';

var script$6 = {
  name: 'TFormOutput',
  components: {
    TProgressCircular: VProgressCircular,
    TSheet: VSheet,
  },
  props: {
    s3PresignFunction: {
      type: Function,
      required: false,
      default: () => {},
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    color: {
      type: String,
      required: false,
      default: DEFAULT_COLOR,
      validator(value) {
        return !!(value && COLORS$1[value.toLowerCase()]);
      },
    },
    schema: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSubmitting: {
      type: Boolean,
      required: false,
      default: false,
    },
    disableSubmit: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideSubmit: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props) {
    const form = ref(null);
    const values = ref({});
    const colorHex = ref(null);

    const hasChanges = computed(() => {
      return Object.keys(values.value).length > 0;
    });

    function beforeUnload(event) {
      if (props.isReadOnly) {
        return;
      }

      if (hasChanges.value) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    }

    onMounted(() => {
      if (!props.isReadOnly) {
        window.addEventListener('beforeunload', beforeUnload);
      }

      watch(
        () => props.color,
        () => {
          function getHexCodeForColor() {
            const invalidColors = ['white', 'grey'];

            if (invalidColors.includes(props.color) || !COLORS$1[props.color]) {
              return COLORS$1.indigo;
            }

            return COLORS$1[props.color];
          }

          const hex = getHexCodeForColor();

          colorHex.value = hex;

          form.value.$el.style.setProperty('--form-primary', hex);
        },
        {
          immediate: true,
        },
      );
    });

    onUnmounted(() => {
      if (!props.isReadOnly) {
        window.removeEventListener('beforeunload', beforeUnload);
      }
    });

    // Note: 'form' seems to be provided by Vuetify so naming this the same would cause a conflict
    provide$1(
      'form-config',
      reactive({
        color: colorHex,
      }),
    );

    return {
      colorHex,
      form,
      values,
      hasChanges,
    };
  },
  data: () => ({
    isUploading: false,
  }),
  computed: {
    transformedSchema() {
      const schema = this.schema.map((field) => {
        let validation = field.validation.split('|');
        // Check if validator is unique and that its not falsey
        validation.unshift('bail');
        validation = validation.filter(
          (value, index, self) => self.indexOf(value) === index,
        );
        validation = compact(validation).join('|');

        const options = Array.isArray(field.options)
          ? field.options.map((option) => {
              return { ...option, id: `${field.tw_id}-${option.id}` };
            })
          : undefined;

        const newField = {
          ...field,
          ...(options ? { options } : {}),
          id: field.tw_id,
          name: field.tw_id,
          'validation-name': field.label,
          label: field.label,
          placeholder: getPlaceholderForInput(field.tw_type),
          errorBehaviour: 'submit',
          validation,
          isReadOnly: this.isReadOnly,
          s3PresignFunction: this.s3PresignFunction,
          'data-lpignore': true,
        };

        if (field.tw_type !== 'attachment') {
          delete newField.s3PresignFunction;
          delete newField.isReadOnly;
          delete newField.maxNumOfFiles;
          delete newField.maxFileSize;
          delete newField.allowedFileTypes;
        }

        return newField;
      });

      return schema;
    },

    shouldShowEmptyMessage() {
      if (this.transformedSchema?.length === 0) {
        return true;
      }

      return false;
    },

    emptyMessageText() {
      if (!this.isReadOnly) {
        return 'This form has not got any fields';
      }

      return 'Add some form fields!';
    },

    textSizeClass() {
      return this.isReadOnly ? 't-text-default' : 't-text-h-04';
    },
  },
  mounted() {
    // Restrict number input keypress to digits
    const selector = '.formulate-input-element--number input[type="number"]';
    this.$el.addEventListener('keypress', (e) => {
      if (!e.target.matches(selector)) {
        return;
      }

      if (
        e.which !== 0 && // No keycode
        e.which !== 8 && // Backspace/delete
        !(e.which >= 37 && e.which <= 40) && // Arrow keys
        !(e.which >= 48 && e.which <= 57) // Numbers
      ) {
        e.preventDefault();
      }
    });

    this.$el.addEventListener('paste', (e) => {
      if (!e.target.matches(selector)) {
        return;
      }

      e.stopPropagation();
      e.preventDefault();

      const text = (e.originalEvent || e).clipboardData.getData('text/plain');

      e.target.value = text.replace(/[^0-9]/g, '');
    });
  },
  methods: {
    parseSubmittedData(data = {}) {
      const transformedData = data;

      this.transformedSchema.forEach((field) => {
        if (field.tw_type === 'number') {
          // protect against parsing the letter e, which VueFormulate seems to allow
          const value = transformedData[field.tw_id];
          if (Number.isNaN(value) || value === null || value === undefined) {
            return;
          }

          const parsedSubmission = Number(value);
          transformedData[field.tw_id] = parsedSubmission;
        }

        if (transformedData[field.tw_id] === '') {
          delete transformedData[field.tw_id];
        }
      });

      return transformedData;
    },

    onSubmit(data) {
      if (this.isReadOnly) {
        return;
      }

      const transformedData = this.parseSubmittedData(data);

      this.$emit('submit', transformedData);
    },

    onSubmitAttempt(submission) {
      if (!this.isReadOnly && submission.hasValidationErrors()) {
        const firstError = submission.form.$el.querySelector(
          '[data-has-errors="true"]',
        );
        if (firstError) {
          this.scrollToError(firstError);
        }
      }
    },

    scrollToError(el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },

    onUpload() {
      this.isUploading = true;
    },

    onUploadComplete() {
      this.isUploading = false;
    },

    handleAlert({ type = 'error', message }) {
      this.$emit('alert', { type, message });
    },
  },
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$8 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-px-6"},[(_vm.shouldShowEmptyMessage)?_c('TSheet',{staticClass:"t-text-default t-text-center t-p-4",attrs:{"rounded":""}},[_vm._v("\n    "+_vm._s(_vm.emptyMessageText)+"\n  ")]):_c('FormulateForm',{ref:"form",attrs:{"novalidate":"","validate":"submit"},on:{"submit":_vm.onSubmit,"submit-raw":_vm.onSubmitAttempt},model:{value:(_vm.values),callback:function ($$v) {_vm.values=$$v;},expression:"values"}},[_c('div',{staticClass:"t-mb-8"},[(_vm.title)?_c('div',{staticClass:"t-text-text-01 t-text-default t-font-semibold t-break-words",class:_vm.textSizeClass},[_vm._v("\n        "+_vm._s(_vm.title)+"\n      ")]):_vm._e(),_vm._v(" "),(_vm.description)?_c('div',{staticClass:"t-text-text-02 t-text-default t-font-normal t-break-words"},[_vm._v("\n        "+_vm._s(_vm.description)+"\n      ")]):_vm._e()]),_vm._v(" "),_vm._l((_vm.transformedSchema),function(item){return _c('FormulateInput',_vm._b({key:item.name,on:{"alert":_vm.handleAlert,"upload":_vm.onUpload,"upload-complete":_vm.onUploadComplete}},'FormulateInput',item,false))}),_vm._v(" "),_vm._t("captcha"),_vm._v(" "),(!_vm.isReadOnly && _vm.schema.length > 0 && !_vm.hideSubmit)?_c('FormulateInput',{staticClass:"t-m-0 t-mt-6 t-leading-default",attrs:{"type":"submit","disabled":_vm.isSubmitting || _vm.isUploading || _vm.disableSubmit}},[(_vm.isSubmitting)?[_vm._v("\n        Sending\n        "),_c('TProgressCircular',{staticClass:"t-ml-2",attrs:{"width":2,"size":20,"indeterminate":true}})]:[_vm._v(" Send ")]],2):_vm._e()],2)],1)};
var __vue_staticRenderFns__$8 = [];

  /* style */
  const __vue_inject_styles__$8 = function (inject) {
    if (!inject) return
    inject("data-v-453ea77a_0", { source: ".formulate-input{margin-bottom:1.5em}.formulate-input .formulate-input-label{display:block;line-height:1.5;font-size:.9em;font-weight:600;margin-bottom:.1em}.formulate-input .formulate-input-label--before+.formulate-input-help--before{margin-top:-.25em;margin-bottom:.75em}.formulate-input .formulate-input-element{max-width:20em;margin-bottom:.1em}.formulate-input .formulate-input-help{color:#6d6d6d;font-size:.7em;font-weight:400;line-height:1.5;margin-bottom:.25em}.formulate-input .formulate-input-errors{list-style-type:none;padding:0;margin:0}.formulate-input .formulate-file-upload-error,.formulate-input .formulate-input-error{color:#960505;font-size:.8em;font-weight:300;line-height:1.5;margin-bottom:.25em}.formulate-input:last-child{margin-bottom:0}.formulate-input[data-classification=text] input{-webkit-appearance:none;-moz-appearance:none;appearance:none;border-radius:.3em;border:1px solid #cecece;box-sizing:border-box;background-color:transparent;font-size:.9em;padding:.75em;display:block;width:100%;font-weight:400;line-height:1.2em;margin:0}.formulate-input[data-classification=text] input::-moz-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=text] input:-ms-input-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=text] input::placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=text] input:focus{outline:0;border:1px solid #41b883}.formulate-input[data-classification=text] .formulate-input-element--date input,.formulate-input[data-classification=text] .formulate-input-element--datetime-local input,.formulate-input[data-classification=text] .formulate-input-element--month input,.formulate-input[data-classification=text] .formulate-input-element--week input{min-height:2.2em}.formulate-input[data-classification=text] .formulate-input-element--search{position:relative}.formulate-input[data-classification=text] .formulate-input-element--search:before{content:'';width:2em;height:100%;position:absolute;left:0;top:0;background-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 72.99 72.9\"><path fill=\"%236d6d6d\" d=\"M71.77,66,53.87,48.1A29.94,29.94,0,1,0,30,60a29.52,29.52,0,0,0,18.1-6.1l18,17.8A4,4,0,0,0,69,72.9a4.18,4.18,0,0,0,2.9-1.2A4.09,4.09,0,0,0,71.77,66ZM30.07,51.9a21.9,21.9,0,1,1,15.5-37.4A21.37,21.37,0,0,1,52,30a22,22,0,0,1-6.4,15.5A21.54,21.54,0,0,1,30.07,51.9Z\"/></svg>');background-size:1em 1em;background-repeat:no-repeat;background-position:50%;pointer-events:none}.formulate-input[data-classification=text] .formulate-input-element--search input{padding-left:2em}.formulate-input[data-classification=text] input[type=color]{height:1.1em;box-sizing:content-box;width:auto;min-width:5em}.formulate-input[data-classification=text] input[type=color]::-webkit-color-swatch-wrapper{padding:0 0 0 1.5em;display:flex;align-items:center;background-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 79.17 79.17\"><path fill=\"%236d6d6d\" d=\"M40.8,22.92c-3.4-3.4-4.76-8.44-1-12.24s8.84-2.44,12.24,1c5-5,10.69-13.33,18.81-11.31a11,11,0,0,1,7.62,14.34c-1.26,3.45-4.63,6.06-7.16,8.59-.92.93-3,2.26-3.46,3.46-.42,1,1.82,2.63,2.36,4a8,8,0,0,1-12.42,9.19c-.21-.16-1.35-1.51-1.59-1.51s-.83.83-1,1L49.71,44.9,32.43,62.18c-3.37,3.38-6.6,7.14-10.26,10.21a11,11,0,0,1-4.48,2.28c-1.25.3-3.11-.22-4.18.18-1.28.48-2.42,2.65-3.68,3.4-6.05,3.61-12.64-4-8.46-9.57.73-1,2.53-1.92,3-3a14.46,14.46,0,0,0-.09-2.52,10.75,10.75,0,0,1,3.14-6.77c.92-1,1.93-1.93,2.89-2.9Zm4.4-1.5c4.19,4,8.24,8.24,12.36,12.36,2.06,2.06,5,5.59,8,2.61,4.65-4.62-5-6.8-2.42-10.78C66.3,20.7,76.4,16.48,74.84,9.45,73.62,4,67.12,2.78,63.29,6.32c-2.55,2.36-4.93,4.94-7.39,7.4-.79.78-1.8,2.28-2.88,2.73-2.14.88-3.4-1.62-4.79-2.77-2.58-2.14-6.89-.82-6.53,3C41.89,18.68,43.87,20.09,45.2,21.42Zm-1.45,4.44L27.82,41.79C22,47.57,15.89,53.14,10.41,59.2a8.23,8.23,0,0,0-1.44,2c-.93,2,.25,4.14-.5,6S4.92,69.94,4.3,72a2.34,2.34,0,0,0,2.56,3c1.11-.17,2-1.33,2.71-2.07a11.17,11.17,0,0,1,2.08-2c1.68-.94,4,.17,5.93-.57C20,69.41,22,66.73,23.76,65L34.42,54.3,53.3,35.42Z\"/></svg>');background-repeat:no-repeat;background-size:.9em .9em;background-position:left .1em}.formulate-input[data-classification=text] input[type=color]::-webkit-color-swatch{display:block;height:1em;border-radius:.2em;border:0;flex:auto}.formulate-input[data-classification=text] input[type=color]::-moz-color-swatch{display:block;height:1em;border-radius:.2em;border:0;flex:auto}.formulate-input[data-classification=slider] .formulate-input-element--range{display:flex;align-items:center}.formulate-input[data-classification=slider] .formulate-input-element-range-value{font-size:.9em;line-height:1;margin-left:.5em;background-color:#efefef;padding:.25em .3em;border-radius:.25em;color:#6d6d6d;font-variant-numeric:tabular-nums}.formulate-input[data-classification=slider] input{-webkit-appearance:none;-moz-appearance:none;appearance:none;width:100%;font-size:1em;padding:.5em 0}.formulate-input[data-classification=slider] input:focus{outline:0}.formulate-input[data-classification=slider] input::-webkit-slider-thumb{cursor:pointer;-webkit-appearance:none;appearance:none;width:1em;height:1em;border-radius:1em;border:0;background-color:#41b883;margin-top:calc(-.5em + 2px)}.formulate-input[data-classification=slider] input::-moz-range-thumb{cursor:pointer;-moz-appearance:none;appearance:none;width:1em;height:1em;border-radius:1em;border:0;background-color:#41b883;margin-top:calc(-.5em + 2px)}.formulate-input[data-classification=slider] input::-ms-thumb{cursor:pointer;appearance:none;width:1em;height:1em;border-radius:1em;border:0;background-color:#41b883;margin-top:calc(-.5em + 2px)}.formulate-input[data-classification=slider] input::-webkit-slider-runnable-track{-webkit-appearance:none;appearance:none;width:100%;height:4px;background-color:#efefef;border-radius:3px;margin:0;padding:0}.formulate-input[data-classification=slider] input::-moz-range-track{-moz-appearance:none;appearance:none;width:100%;height:4px;background-color:#efefef;border-radius:3px;margin:0;padding:0}.formulate-input[data-classification=textarea] textarea{-webkit-appearance:none;-moz-appearance:none;appearance:none;border-radius:.3em;border:1px solid #cecece;box-sizing:border-box;background-color:transparent;font-size:.9em;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol;padding:.75em;display:block;width:100%;font-weight:400;line-height:1.2em;margin:0}.formulate-input[data-classification=textarea] textarea::-moz-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=textarea] textarea:-ms-input-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=textarea] textarea::placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=textarea] textarea:focus{outline:0;border:1px solid #41b883}.formulate-input[data-classification=button] button{-webkit-appearance:none;-moz-appearance:none;appearance:none;border-radius:.3em;box-sizing:border-box;background-color:transparent;font-size:.9em;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol;padding:.75em;display:block;width:100%;font-weight:400;line-height:1.2em;margin:0;border:1px solid #41b883;background-color:#41b883;color:#fff;min-width:0;width:auto;font-weight:700;cursor:pointer;display:inline-flex;align-items:center}.formulate-input[data-classification=button] button::-moz-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=button] button:-ms-input-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=button] button::placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=button] button:focus{outline:0;border:1px solid #41b883}.formulate-input[data-classification=button] button[disabled],.formulate-input[data-classification=button] button[disabled]:active{background-color:#cecece;border-color:#cecece}.formulate-input[data-classification=button] button[data-ghost]{color:#41b883;background-color:transparent;border-color:currentColor}.formulate-input[data-classification=button] button[data-minor]{font-size:.75em;display:inline-block}.formulate-input[data-classification=button] button[data-danger]{background-color:#960505;border-color:#960505}.formulate-input[data-classification=button] button[data-danger][data-ghost]{color:#960505;background-color:transparent}.formulate-input[data-classification=button] button:active{background-color:#64c89b;border-color:#64c89b}.formulate-input[data-classification=select] .formulate-input-element{position:relative}.formulate-input[data-classification=select] .formulate-input-element:before{content:'';width:0;height:0;border-color:#cecece transparent transparent;border-style:solid;border-width:.3em .3em 0;top:50%;margin-top:-.1em;right:1em;position:absolute}.formulate-input[data-classification=select] .formulate-input-element[data-multiple]:before{display:none}.formulate-input[data-classification=select] select{-webkit-appearance:none;-moz-appearance:none;appearance:none;border-radius:.3em;border:1px solid #cecece;box-sizing:border-box;background-color:transparent;font-size:.9em;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol;display:block;width:100%;font-weight:400;line-height:1.2em;margin:0;padding:.75em 2em .75em .75em}.formulate-input[data-classification=select] select::-moz-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=select] select:-ms-input-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=select] select::placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=select] select:focus{outline:0;border:1px solid #41b883}.formulate-input[data-classification=select] select option{color:#000}.formulate-input[data-classification=select] select option:disabled{color:#a8a8a8}.formulate-input[data-classification=select] select[multiple]{padding:0;overflow-y:auto}.formulate-input[data-classification=select] select[multiple] option{padding:.75em}.formulate-input[data-classification=select] select[data-placeholder-selected]{color:#a8a8a8}.formulate-input[data-classification=box] .formulate-input-wrapper{display:flex;align-items:center}.formulate-input[data-classification=box] .formulate-input-element{overflow:hidden;display:flex;align-items:center}.formulate-input[data-classification=box] .formulate-input-element input{position:absolute;left:-999px;opacity:0;pointer-events:none}.formulate-input[data-classification=box] .formulate-input-element-decorator{display:block;width:1em;height:1em;border-radius:.25em;border:1px solid #cecece;position:relative}.formulate-input[data-classification=box] .formulate-input-element-decorator:before{content:'';display:block;background-size:contain;background-position:100%;width:calc(100% - .125em);height:calc(100% - .125em);box-sizing:border-box;position:absolute;top:.0625em;left:.0625em}.formulate-input[data-classification=box] .formulate-input-element[data-type=radio] .formulate-input-element-decorator{border-radius:1em}.formulate-input[data-classification=box] .formulate-input-element[data-type=radio] .formulate-input-element-decorator:before{border-radius:1em;width:calc(100% - .5em);height:calc(100% - .5em);top:.25em;left:.25em}.formulate-input[data-classification=box] .formulate-input-element input[type=checkbox]:checked~.formulate-input-element-decorator{border-color:#41b883}.formulate-input[data-classification=box] .formulate-input-element input[type=checkbox]:checked~.formulate-input-element-decorator:before{background-color:#41b883;-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><path d=\"M8.76,56.2c-6.38-6.34,3.26-16,9.64-9.69L38,65.88,80.56,23.29c6.38-6.38,16.07,3.32,9.69,9.69L42.84,80.37a6.83,6.83,0,0,1-9.65,0Z\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><path d=\"M8.76,56.2c-6.38-6.34,3.26-16,9.64-9.69L38,65.88,80.56,23.29c6.38-6.38,16.07,3.32,9.69,9.69L42.84,80.37a6.83,6.83,0,0,1-9.65,0Z\"/></svg>')}.formulate-input[data-classification=box] .formulate-input-element input[type=radio]:checked~.formulate-input-element-decorator{border-color:#41b883}.formulate-input[data-classification=box] .formulate-input-element input[type=radio]:checked~.formulate-input-element-decorator:before{background-color:#41b883}.formulate-input[data-classification=box] .formulate-input-element input:focus~.formulate-input-element-decorator{border-color:#41b883}.formulate-input[data-classification=box] .formulate-input-label--after{margin-left:.5em}.formulate-input[data-classification=box] .formulate-input-label--before{margin-right:.5em}.formulate-input[data-classification=group] .formulate-input-group-item,.formulate-input[data-classification=group]>.formulate-input-wrapper>.formulate-input-label{margin-bottom:.5em}.formulate-input[data-classification=group] [data-is-repeatable]{padding:1em;border:1px solid #efefef;border-radius:.3em}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-grouping{margin:-1em -1em 0}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable{padding:1em 3em 1em 1em;border-bottom:1px solid #efefef;position:relative}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove{position:absolute;display:block;top:calc(50% - .15em);width:1.3em;height:1.3em;background-color:#cecece;right:.85em;border-radius:1.3em;cursor:pointer;transition:background-color .2s;overflow:hidden;text-indent:-1000px}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove[data-disabled]{opacity:.2;pointer-events:none}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:after,.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:before{content:'';position:absolute;top:calc(50% - .1em);left:.325em;display:block;width:.65em;height:.2em;background-color:#fff;transform-origin:center center;transition:transform .25s}@media (pointer:fine){.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:hover{background-color:#dc2c2c}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:hover:after,.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:hover:before{height:.2em;width:.75em;left:.25em;top:calc(50% - .075em)}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:hover:after{transform:rotate(45deg)}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable-remove:hover:before{transform:rotate(-45deg)}}.formulate-input[data-classification=group] [data-is-repeatable] .formulate-input-group-repeatable:last-child{margin-bottom:1em}.formulate-input[data-classification=file] .formulate-input-upload-area{width:100%;position:relative;padding:2em 0}.formulate-input[data-classification=file] .formulate-input-upload-area input{cursor:pointer;-webkit-appearance:none;-moz-appearance:none;appearance:none;opacity:0;position:absolute;left:0;right:0;bottom:0;top:0;width:100%;height:100%;z-index:5}.formulate-input[data-classification=file] .formulate-input-upload-area[data-has-files]{padding:0}.formulate-input[data-classification=file] .formulate-input-upload-area[data-has-files] input{display:none}.formulate-input[data-classification=file] .formulate-input-upload-area-mask{border-radius:.4em;position:absolute;pointer-events:none;display:flex;justify-content:center;align-items:center;left:0;right:0;top:0;bottom:0;border:2px dashed #a8a8a8;z-index:2}.formulate-input[data-classification=file] .formulate-input-upload-area-mask:before{content:'';background-color:#a8a8a8;-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 58 58\"><path d=\"M29,58A29,29,0,1,0,0,29,29,29,0,0,0,29,58ZM29,4A25,25,0,1,1,4,29,25,25,0,0,1,29,4Z\"/><polygon points=\"27 22 27 44.4 31 44.4 31 22 41.7 31.1 44.3 28.1 29 15 13.7 28.1 16.3 31.1 27 22\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 58 58\"><path d=\"M29,58A29,29,0,1,0,0,29,29,29,0,0,0,29,58ZM29,4A25,25,0,1,1,4,29,25,25,0,0,1,29,4Z\"/><polygon points=\"27 22 27 44.4 31 44.4 31 22 41.7 31.1 44.3 28.1 29 15 13.7 28.1 16.3 31.1 27 22\"/></svg>');-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;width:2em;height:2em;position:absolute;pointer-events:none}.formulate-input[data-classification=file] .formulate-input-upload-area input:focus~.formulate-input-upload-area-mask,.formulate-input[data-classification=file] .formulate-input-upload-area input:hover~.formulate-input-upload-area-mask,.formulate-input[data-classification=file] .formulate-input-upload-area input[data-is-drag-hover]~.formulate-input-upload-area-mask{border-color:#41b883}.formulate-input[data-classification=file] .formulate-input-upload-area input:focus~.formulate-input-upload-area-mask:before,.formulate-input[data-classification=file] .formulate-input-upload-area input:hover~.formulate-input-upload-area-mask:before,.formulate-input[data-classification=file] .formulate-input-upload-area input[data-is-drag-hover]~.formulate-input-upload-area-mask:before{background-color:#41b883}.formulate-input[data-classification=file] .formulate-files{list-style-type:none;margin:0;padding:0}.formulate-input[data-classification=file] .formulate-files .formulate-file-add{-webkit-appearance:none;-moz-appearance:none;appearance:none;display:block;min-width:75px;border:1px solid #41b883;color:#41b883;padding:5px;border-radius:5px;text-align:center;position:relative;font-size:13px;background-color:transparent;cursor:pointer;margin:.5em 0}.formulate-input[data-classification=file] .formulate-files .formulate-file-add input{position:absolute;display:block;cursor:pointer}.formulate-input[data-classification=file] .formulate-files .formulate-file-add input::-webkit-file-upload-button{display:none}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress{background-color:#cecece;height:.3em;border-radius:1.25em;width:5em;overflow:hidden;position:relative;flex:0 0 5em;right:.75em;transition:height .25s,width .25s,flex-basis .25s;z-index:2}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress:before{content:'';position:absolute;top:0;left:0;right:0;bottom:0;display:block;opacity:0;transform:scale(.08);background-color:#fff;-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><path d=\"M8.76,56.2c-6.38-6.34,3.26-16,9.64-9.69L38,65.88,80.56,23.29c6.38-6.38,16.07,3.32,9.69,9.69L42.84,80.37a6.83,6.83,0,0,1-9.65,0Z\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 100 100\"><path d=\"M8.76,56.2c-6.38-6.34,3.26-16,9.64-9.69L38,65.88,80.56,23.29c6.38-6.38,16.07,3.32,9.69,9.69L42.84,80.37a6.83,6.83,0,0,1-9.65,0Z\"/></svg>');-webkit-mask-size:77%;mask-size:77%;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;z-index:3}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress[data-just-finished]{width:1.25em;height:1.25em;flex:0 0 1.25em}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress[data-just-finished]:before{transition:transform .25s .2s,opacity .25s .2s;transform:scale(1);opacity:1}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress[data-is-finished]{transition:height .25s,width .25s,left .25s,top,.25s,border-radius .25s;width:.3em;flex:0 0 .3em;height:100%;right:0;border-radius:0 .23em .23em 0}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress[data-is-finished]:before{transition:opacity .1s;opacity:0}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress .formulate-file-progress-inner{background-color:#41b883;width:1%;position:absolute;left:0;bottom:0;top:0;z-index:2}.formulate-input[data-classification=file] .formulate-files .formulate-file-name{padding-left:1.5em;padding-right:2em;max-width:100%;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin-right:auto}.formulate-input[data-classification=file] .formulate-files .formulate-file-name:before{position:absolute;left:.7em;top:50%;margin-top:-.7em;background-color:#a8a8a8;content:'';-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 64.06 83.59\"><path d=\"M55.94,83.59a8.16,8.16,0,0,0,8.12-8.16V19.12a1.77,1.77,0,0,0-.52-1.25L46.21.59A1.69,1.69,0,0,0,45.14.08L44.69,0l-.18,0H8.13A8.18,8.18,0,0,0,0,8.16V75.41a8.16,8.16,0,0,0,8.13,8.16H55.94ZM46.68,6,58.11,17.38H46.68ZM3.52,75.43V8.16A4.64,4.64,0,0,1,8.13,3.52h35V19.16a1.75,1.75,0,0,0,1.76,1.74H60.55V75.43a4.65,4.65,0,0,1-4.61,4.65H8.13A4.65,4.65,0,0,1,3.52,75.43Z\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 64.06 83.59\"><path d=\"M55.94,83.59a8.16,8.16,0,0,0,8.12-8.16V19.12a1.77,1.77,0,0,0-.52-1.25L46.21.59A1.69,1.69,0,0,0,45.14.08L44.69,0l-.18,0H8.13A8.18,8.18,0,0,0,0,8.16V75.41a8.16,8.16,0,0,0,8.13,8.16H55.94ZM46.68,6,58.11,17.38H46.68ZM3.52,75.43V8.16A4.64,4.64,0,0,1,8.13,3.52h35V19.16a1.75,1.75,0,0,0,1.76,1.74H60.55V75.43a4.65,4.65,0,0,1-4.61,4.65H8.13A4.65,4.65,0,0,1,3.52,75.43Z\"/></svg>');-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-size:contain;mask-size:contain;width:1.25em;height:1.25em;display:inline-block;margin-right:.5em}.formulate-input[data-classification=file] .formulate-files .formulate-file-remove{width:1.25em;height:1.25em;border-radius:1em;border:1px solid #a8a8a8;background-color:#a8a8a8;-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 59.1 59.2\"><path d=\"M1.6,57.7a5,5,0,0,0,3.5,1.5,4.85,4.85,0,0,0,3.5-1.5l21-21,21,21a5,5,0,0,0,3.5,1.5,4.85,4.85,0,0,0,3.5-1.5,5,5,0,0,0,0-7.1l-21-21,21-21a5,5,0,0,0,0-7.1,5,5,0,0,0-7.1,0l-21,21L8.6,1.7a5,5,0,0,0-7.1,0,5,5,0,0,0,0,7.1l21,21L1.6,50.7A4.83,4.83,0,0,0,1.6,57.7Z\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 59.1 59.2\"><path d=\"M1.6,57.7a5,5,0,0,0,3.5,1.5,4.85,4.85,0,0,0,3.5-1.5l21-21,21,21a5,5,0,0,0,3.5,1.5,4.85,4.85,0,0,0,3.5-1.5,5,5,0,0,0,0-7.1l-21-21,21-21a5,5,0,0,0,0-7.1,5,5,0,0,0-7.1,0l-21,21L8.6,1.7a5,5,0,0,0-7.1,0,5,5,0,0,0,0,7.1l21,21L1.6,50.7A4.83,4.83,0,0,0,1.6,57.7Z\"/></svg>');-webkit-mask-size:.6em;mask-size:.6em;-webkit-mask-repeat:no-repeat;mask-repeat:no-repeat;-webkit-mask-position:center;mask-position:center;cursor:pointer;position:absolute;right:.75em;z-index:1;transition:transform .25s}@media (pointer:fine){.formulate-input[data-classification=file] .formulate-files .formulate-file-remove:hover{transform:scale(1.5)}}.formulate-input[data-classification=file] .formulate-files li{display:block}.formulate-input[data-classification=file] .formulate-files li[data-has-error] .formulate-file-progress{background-color:#dc2c2c}.formulate-input[data-classification=file] .formulate-files li[data-has-preview] .formulate-file-name:before{display:none}.formulate-input[data-classification=file] .formulate-files li+li{margin-top:.5em}.formulate-input[data-classification=file] .formulate-files .formulate-file{-webkit-appearance:none;-moz-appearance:none;appearance:none;border-radius:.3em;border:1px solid #cecece;box-sizing:border-box;background-color:transparent;font-size:.9em;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica,Arial,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol;padding:.75em;display:block;font-weight:400;line-height:1.2em;margin:0;width:100%;display:flex;justify-content:space-between;align-items:center;position:relative}.formulate-input[data-classification=file] .formulate-files .formulate-file::-moz-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=file] .formulate-files .formulate-file:-ms-input-placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=file] .formulate-files .formulate-file::placeholder{color:#a8a8a8;line-height:normal}.formulate-input[data-classification=file] .formulate-files .formulate-file:focus{outline:0;border:1px solid #41b883}.formulate-input[data-classification=file] .formulate-files .formulate-file ::-webkit-progress-bar{-webkit-appearance:none;appearance:none;height:.5em;border-radius:.5em;overflow:hidden}.formulate-input[data-classification=file] .formulate-files .formulate-file-image-preview{width:3em;height:3em;flex:0 0 3em;position:relative;z-index:2;left:-1px;box-shadow:0 0 0 1px #efefef;transition:transform .25s,box-shadow .25s,background-color .25s}@media (pointer:fine){.formulate-input[data-classification=file] .formulate-files .formulate-file-image-preview:hover{transition-delay:.2s;transform:scale(3);background-color:#fff;box-shadow:0 0 2px 0 rgba(0,0,0,.1)}}.formulate-input[data-classification=file] .formulate-files .formulate-file-image-preview img{display:block;position:absolute;width:100%;height:100%;left:0;right:0;bottom:0;top:0;-o-object-fit:contain;object-fit:contain;transition:all .25s}.formulate-input[data-classification=file] [data-type=image] .formulate-input-upload-area .formulate-input-upload-area-mask:before{-webkit-mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 90 71.05\"><path d=\"M82.89,0H7.1A7.12,7.12,0,0,0,0,7.11V64a7.11,7.11,0,0,0,7.1,7.1H82.9A7.11,7.11,0,0,0,90,64V7.11A7.12,7.12,0,0,0,82.89,0ZM69.28,39.35a5.44,5.44,0,0,0-8,0L50.58,50.74,32.38,30.88a5.31,5.31,0,0,0-7.92,0L4.74,52.4V7.11A2.37,2.37,0,0,1,7.11,4.74H82.9a2.37,2.37,0,0,1,2.36,2.37V56.3Z\"/><circle cx=\"67.74\" cy=\"22.26\" r=\"8.53\"/></svg>');mask-image:url('data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 90 71.05\"><path d=\"M82.89,0H7.1A7.12,7.12,0,0,0,0,7.11V64a7.11,7.11,0,0,0,7.1,7.1H82.9A7.11,7.11,0,0,0,90,64V7.11A7.12,7.12,0,0,0,82.89,0ZM69.28,39.35a5.44,5.44,0,0,0-8,0L50.58,50.74,32.38,30.88a5.31,5.31,0,0,0-7.92,0L4.74,52.4V7.11A2.37,2.37,0,0,1,7.11,4.74H82.9a2.37,2.37,0,0,1,2.36,2.37V56.3Z\"/><circle cx=\"67.74\" cy=\"22.26\" r=\"8.53\"/></svg>')}.formulate-form-errors{margin:.75em 0;padding:0;list-style-type:none}.formulate-form-errors:first-child{margin-top:0}.formulate-form-errors:last-child{margin-bottom:0}.formulate-form-errors .formulate-form-error{color:#960505;font-size:.9em;font-weight:300;line-height:1.5;margin-bottom:.25em}.formulate-form{--primary:var(--token-ui-primary)}.formulate-form *{transition-property:background-color,border-color,color,fill,stroke!important;transition-timing-function:cubic-bezier(.4,0,.2,1)!important;transition-duration:150ms!important}.formulate-input .formulate-input-element{max-width:100%!important}.formulate-input{margin-bottom:1rem!important}.formulate-input input,.formulate-input select,.formulate-input-element--select,.formulate-input-element--textarea,.formulate-input-element-decorator{background-color:var(--token-color-neutral-10)!important;border-radius:.3em!important}.formulate-input-element-decorator{border-color:var(--token-color-neutral-20)!important}.formulate-input[data-classification=box] .formulate-input-element input[type=checkbox]~.formulate-input-element-decorator{width:1.2rem!important;height:1.2rem!important}.formulate-input[data-classification=box] .formulate-input-element input[type=radio]~.formulate-input-element-decorator{border-radius:9999px!important;width:1.2rem!important;height:1.2rem!important}.formulate-input[data-classification=box] .formulate-input-element input[type=checkbox]:checked~.formulate-input-element-decorator:before{background-color:#fff!important}.formulate-input-element input[type=radio]:checked~.formulate-input-element-decorator::before{background-color:var(--form-primary)!important;border-color:var(--form-primary)!important}.formulate-input-element input[type=checkbox]:focus~.formulate-input-element-decorator,.formulate-input-element input[type=radio]:focus~.formulate-input-element-decorator{border-color:var(--form-primary)!important}.formulate-input[data-classification=text] input:focus{border-color:var(--form-primary)}.formulate-input[data-classification=slider] input::-webkit-slider-thumb{background-color:var(--form-primary)}.formulate-input[data-classification=slider] input::-moz-range-thumb{background-color:var(--form-primary)}.formulate-input[data-classification=slider] input::-ms-thumb{background-color:var(--form-primary)}.formulate-input[data-classification=textarea] textarea:focus{border-color:var(--form-primary)}.formulate-input[data-classification=button] button{border-color:var(--form-primary);background-color:var(--form-primary)}.formulate-input[data-classification=button] button:focus{border-color:var(--form-primary)}.formulate-input[data-classification=button] button[data-ghost]{color:var(--form-primary)}.formulate-input[data-classification=select] select:focus{border-color:var(--form-primary)}.formulate-input[data-classification=box] .formulate-input-element input[type=checkbox]:checked~.formulate-input-element-decorator{border-color:#fff!important;background-color:var(--form-primary)!important;color:#fff!important}.formulate-input[data-classification=box] .formulate-input-element input[type=radio]:checked~.formulate-input-element-decorator{border-color:var(--form-primary)!important}.formulate-input[data-classification=box] .formulate-input-element input[type=radio]:checked~.formulate-input-element-decorator:before{background-color:var(--form-primary)!important;width:100%!important;height:100%!important;position:static!important;top:0!important;left:0!important;transform:scale(.5)!important}.formulate-input[data-classification=box] .formulate-input-element input:focus~.formulate-input-element-decorator{border-color:var(--form-primary)}.formulate-input[data-classification=file] .formulate-input-upload-area input:focus~.formulate-input-upload-area-mask,.formulate-input[data-classification=file] .formulate-input-upload-area input:hover~.formulate-input-upload-area-mask,.formulate-input[data-classification=file] .formulate-input-upload-area input[data-is-drag-hover]~.formulate-input-upload-area-mask{border-color:var(--form-primary)}.formulate-input[data-classification=file] .formulate-input-upload-area input:focus~.formulate-input-upload-area-mask:before,.formulate-input[data-classification=file] .formulate-input-upload-area input:hover~.formulate-input-upload-area-mask:before,.formulate-input[data-classification=file] .formulate-input-upload-area input[data-is-drag-hover]~.formulate-input-upload-area-mask:before{background-color:var(--form-primary)}.formulate-input[data-classification=file] .formulate-files .formulate-file-add{border-color:var(--form-primary);color:var(--form-primary)}.formulate-input[data-classification=file] .formulate-files .formulate-file-progress .formulate-file-progress-inner{background-color:var(--form-primary)}.formulate-input[data-classification=file] .formulate-files .formulate-file:focus{border-color:var(--form-primary)}.formulate-input[data-classification=box] .formulate-input-element-decorator,.formulate-input[data-classification=file] .formulate-files .formulate-file,.formulate-input[data-classification=select] select,.formulate-input[data-classification=text] input,.formulate-input[data-classification=textarea] textarea{border-color:transparent}.formulate-input .formulate-input-element--date input{border:0!important}.formulate-input .formulate-input-element--date .v-input{border:solid 1px transparent!important}.formulate-input .formulate-input-element--date .v-input.v-input--is-focused{border-color:var(--form-primary)!important}.formulate-input[data-is-showing-errors=true] .formulate-input-element--date .v-input:not(.v-input--is-focused){border-color:var(--token-ui-error)!important}.formulate-input[data-is-showing-errors=true][data-classification=box] .formulate-input-element-decorator,.formulate-input[data-is-showing-errors=true][data-classification=file] .formulate-files .formulate-file,.formulate-input[data-is-showing-errors=true][data-classification=select] select,.formulate-input[data-is-showing-errors=true][data-classification=text] input,.formulate-input[data-is-showing-errors=true][data-classification=textarea] textarea{border-color:var(--token-ui-error)!important}.formulate-input[data-is-showing-errors=true][data-classification=box] .formulate-input-element-decorator:focus,.formulate-input[data-is-showing-errors=true][data-classification=file] .formulate-files .formulate-file:focus,.formulate-input[data-is-showing-errors=true][data-classification=select] select:focus,.formulate-input[data-is-showing-errors=true][data-classification=text] input:focus,.formulate-input[data-is-showing-errors=true][data-classification=textarea] textarea:focus{border-color:var(--form-primary)!important}.formulate-input-error{color:var(--token-ui-error)!important}.formulate-input[data-is-showing-errors=true] .formulate-input-element-decorator{border-color:var(--token-ui-error)!important;color:var(--token-ui-error)!important}.formulate-input label{display:block;font-size:12px;font-weight:600;margin-bottom:.75rem;overflow-wrap:break-word}.formulate-input .formulate-input-help{overflow-wrap:break-word;margin-top:8px}.formulate-input-group-item.formulate-input label{font-size:14px;font-weight:400}.formulate-input .formulate-input-group,.formulate-input input{margin-top:.5rem!important}.formulate-input-element.formulate-input-element--submit{margin:0!important}.formulate-input-element.formulate-input-element--submit button{user-select:none;background-color:transparent;border-color:var(--form-primary)!important;color:var(--form-primary);font-size:14px;padding:8px 14px 8px 14px;font-weight:600;transition-property:background-color,border-color,color,fill,stroke;transition-timing-function:cubic-bezier(.4,0,.2,1);transition-duration:150ms}.formulate-input-element.formulate-input-element--submit button:focus,.formulate-input-element.formulate-input-element--submit button:hover{background-color:var(--form-primary)!important;color:var(--token-ui-white)!important}.formulate-input-element.formulate-input-element--submit button:active{background-color:var(--form-primary)!important;color:var(--token-ui-white)!important}.formulate-input-element.formulate-input-element--submit button[disabled],.formulate-input-element.formulate-input-element--submit button[disabled]:active,.formulate-input-element.formulate-input-element--submit button[disabled]:focus,.formulate-input-element.formulate-input-element--submit button[disabled]:hover{background-color:var(--token-ui-white)!important;border-color:var(--token-ui-disabled)!important;color:var(--token-ui-disabled)!important;cursor:default!important}.formulate-input-element.formulate-input-element--checkbox,.formulate-input-element.formulate-input-element--radio{margin-right:6px;margin-bottom:0;flex:0 0 auto}.formulate-input-element--date{margin:0!important;border-radius:.3rem!important}.formulate-input-element--date .v-text-field input{margin:0!important;padding:0!important;font-size:.9rem!important;height:100%!important;min-height:inherit!important}.formulate-input-element--date .v-text-field fieldset{border:none!important}.formulate-input[data-classification=select] .formulate-input-element:before{border-color:var(--form-primary) transparent transparent!important}.formulate-input label .asterisk{color:var(--form-primary)!important;margin-left:-.2rem!important;font-weight:1000!important}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$8 = undefined;
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$8 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$6,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8,
    __vue_module_identifier__$8,
    false,
    createInjector,
    undefined,
    undefined
  );

var COLORS = Object.freeze({
  black: TOKEN_COLOR_BLACK,
  blue: TOKEN_COLOR_BLUE_BASE,
  green: TOKEN_COLOR_GREEN_BASE,
  grey: TOKEN_COLOR_GREY_BASE,
  indigo: TOKEN_COLOR_INDIGO_BASE,
  orange: TOKEN_COLOR_ORANGE_40,
  pink: TOKEN_COLOR_PINK_BASE,
  purple: TOKEN_COLOR_PURPLE_BASE,
  red: TOKEN_COLOR_RED_BASE,
  teal: TOKEN_COLOR_TEAL_BASE,
  white: TOKEN_COLOR_WHITE,
  yellow: TOKEN_COLOR_YELLOW_BASE,
});

/* script */

/* template */
var __vue_render__$7 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('svg',{attrs:{"viewBox":"0 0 629 169","preserveAspectRatio":"xMinYMin slice","fill":"none","xmlns":"http://www.w3.org/2000/svg","xmlns:bx":"https://boxy-svg.com"}},[_c('defs',[_c('clipPath',{attrs:{"id":"clip0"}},[_c('rect',{attrs:{"width":"37.1169","height":"36.9041","fill":"white","transform":"translate(88.0664 62.428) rotate(6.86404)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip1"}},[_c('rect',{attrs:{"width":"42.5681","height":"53.6256","fill":"white","transform":"translate(-35 33.5846) rotate(-15.7918)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip2"}},[_c('rect',{attrs:{"width":"50.624","height":"42.683","fill":"white","transform":"translate(140 124.272)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip3"}},[_c('rect',{attrs:{"width":"46.532","height":"39.2329","fill":"white","transform":"translate(8.21713 176.885) rotate(-36.761)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip4"}},[_c('rect',{attrs:{"width":"46.532","height":"39.2329","fill":"white","transform":"translate(93 27.7609) rotate(-41.3816)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip5"}},[_c('rect',{attrs:{"width":"43.1793","height":"35.7346","fill":"white","transform":"translate(328.029 71.706) rotate(-8.82317)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip6"}},[_c('rect',{attrs:{"width":"37.5686","height":"35.0695","fill":"white","transform":"translate(394.5 35.2638) rotate(-14.2754)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip7"}},[_c('rect',{attrs:{"width":"37.1169","height":"36.9041","fill":"white","transform":"translate(476.066 17.428) rotate(6.86404)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip8"}},[_c('rect',{attrs:{"width":"37.1169","height":"36.9041","fill":"white","transform":"translate(278.812 81.5668) rotate(6.86404)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip9"}},[_c('rect',{attrs:{"width":"42.5681","height":"53.6256","fill":"white","transform":"translate(193 -10.4154) rotate(-15.7918)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip10"}},[_c('rect',{attrs:{"width":"45.9136","height":"57.8401","fill":"white","transform":"translate(194 82.0505) rotate(-19.1353)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip11"}},[_c('rect',{attrs:{"width":"50.624","height":"42.683","fill":"white","transform":"translate(428.781 -27.7278)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip12"}},[_c('rect',{attrs:{"width":"46.532","height":"39.2329","fill":"white","transform":"translate(302.717 31.8848) rotate(-36.761)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip13"}},[_c('rect',{attrs:{"width":"46.532","height":"39.2329","fill":"white","transform":"translate(475 147.601) rotate(-5.67513)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip14"}},[_c('rect',{attrs:{"width":"43.1793","height":"35.7346","fill":"white","transform":"translate(497 106.891) rotate(-18.7661)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip15"}},[_c('rect',{attrs:{"width":"37.1169","height":"36.9041","fill":"white","transform":"translate(592.43 -16.2779) rotate(6.86404)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip16"}},[_c('rect',{attrs:{"width":"42.5681","height":"53.6256","fill":"white","transform":"translate(562 97.5846) rotate(-15.7918)"}})]),_vm._v(" "),_c('clipPath',{attrs:{"id":"clip17"}},[_c('rect',{attrs:{"width":"46.532","height":"39.2329","fill":"white","transform":"translate(521.15 83.882) rotate(-78.2413)"}})]),_vm._v(" "),_c('mask',{attrs:{"id":"mask-0"}},[_c('rect',{staticStyle:{"fill":"rgb(255, 255, 255)"},attrs:{"x":"0.168","y":"0.937","width":"629","height":"169"}})])]),_vm._v(" "),_c('g',{staticStyle:{"mask":"url(#mask-0)"},attrs:{"transform":"matrix(1.004216, 0, 0, 1.016257, -2.724191, -3.051472)"}},[_c('rect',{staticClass:"primary",attrs:{"x":"-37.119","y":"-41.277","width":"700.049","height":"264.697","bx:origin":"0.411042 0.318019"}}),_vm._v(" "),_c('g',[_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 47.402 143.633 C 47.945 138.448 47.794 133.083 47.653 127.888 L 47.569 124.859 C 47.536 123.542 46.752 122.355 45.552 121.81 C 45.559 121.087 45.581 120.456 45.622 119.744 C 45.71 117.981 44.356 116.472 42.592 116.377 L 31.557 115.776 C 31.015 114.848 30.063 114.23 28.993 114.112 C 26.119 113.797 18.976 113.07 14.666 112.853 C 12.958 112.783 11.456 113.968 11.133 115.649 C 9.854 122.491 9.272 134.017 9.472 140.761 C 9.507 142.208 10.46 143.479 11.838 143.922 C 12.041 143.987 12.251 144.033 12.467 144.058 C 23.468 145.417 33.417 146.582 43.945 146.686 C 45.707 146.7 47.193 145.386 47.402 143.633 Z M 42.482 118.387 C 43.134 118.429 43.635 118.989 43.598 119.645 C 43.571 120.223 43.542 120.831 43.534 121.444 C 40.698 121.259 38.837 121.169 36.002 121.027 L 34.412 120.951 C 33.862 120.927 33.379 120.583 33.166 120.072 C 33.052 119.774 32.805 119.079 32.565 118.429 L 32.368 117.872 L 42.482 118.387 Z M 43.962 144.697 C 33.542 144.591 23.649 143.458 12.717 142.086 C 12.016 142.013 11.487 141.43 11.482 140.725 C 11.282 134.09 11.852 122.763 13.106 116.043 C 13.24 115.354 13.861 114.864 14.567 114.895 C 18.847 115.095 25.92 115.837 28.768 116.143 C 28.862 116.165 28.954 116.195 29.043 116.231 C 29.418 116.351 29.728 116.625 29.888 116.981 C 30.018 117.255 30.375 118.283 30.652 119.112 C 30.928 119.941 31.146 120.526 31.284 120.86 C 31.792 122.097 32.96 122.922 34.291 122.994 L 35.888 123.071 C 38.98 123.224 40.876 123.318 44.205 123.568 C 44.94 123.601 45.522 124.201 45.523 124.941 C 45.547 125.95 45.594 126.973 45.602 127.984 C 45.745 133.106 45.874 138.406 45.364 143.464 C 45.28 144.177 44.673 144.715 43.962 144.697 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 24.755 119.125 L 17.025 118.76 C 16.469 118.734 15.998 119.163 15.971 119.72 C 15.948 120.176 16.235 120.588 16.668 120.727 C 16.749 120.767 16.828 120.793 16.918 120.807 L 24.656 121.168 C 25.206 121.236 25.708 120.846 25.783 120.297 C 25.851 119.746 25.462 119.244 24.914 119.169 C 24.86 119.159 24.81 119.157 24.757 119.162 L 24.755 119.125 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 26.129 16.42 C 28.895 12.001 31.116 7.115 33.271 2.386 L 34.527 -0.372 C 35.075 -1.569 34.892 -2.98 34.053 -3.997 C 34.378 -4.643 34.675 -5.201 35.024 -5.823 C 35.878 -7.367 35.324 -9.317 33.781 -10.178 L 24.131 -15.565 C 24.053 -16.637 23.468 -17.61 22.56 -18.187 C 20.115 -19.731 14.019 -23.523 10.242 -25.611 C 8.739 -26.424 6.868 -26.019 5.84 -24.651 C 1.686 -19.067 -3.901 -8.968 -6.683 -2.821 C -7.288 -1.506 -6.99 0.054 -5.947 1.057 C -5.793 1.205 -5.624 1.338 -5.442 1.457 C 3.845 7.509 12.27 12.926 21.683 17.644 C 23.259 18.432 25.171 17.904 26.129 16.42 Z M 32.799 -8.421 C 33.366 -8.096 33.57 -7.373 33.249 -6.801 C 32.971 -6.292 32.678 -5.759 32.401 -5.212 C 29.935 -6.625 28.303 -7.523 25.818 -8.896 L 24.423 -9.662 C 23.94 -9.926 23.657 -10.447 23.69 -10.999 C 23.718 -11.316 23.802 -12.05 23.872 -12.739 L 23.939 -13.326 L 32.799 -8.421 Z M 22.571 15.865 C 13.257 11.193 4.867 5.829 -4.351 -0.205 C -4.948 -0.578 -5.167 -1.335 -4.862 -1.971 C -2.127 -8.019 3.361 -17.945 7.439 -23.431 C 7.862 -23.991 8.635 -24.158 9.256 -23.82 C 13.013 -21.76 19.041 -17.987 21.465 -16.461 C 21.539 -16.399 21.609 -16.332 21.674 -16.261 C 21.957 -15.988 22.115 -15.606 22.103 -15.216 C 22.099 -14.913 21.968 -13.832 21.853 -12.966 C 21.738 -12.1 21.676 -11.479 21.654 -11.118 C 21.566 -9.784 22.254 -8.529 23.417 -7.88 L 24.817 -7.109 C 27.529 -5.614 29.19 -4.697 32.072 -3.01 C 32.717 -2.657 32.976 -1.862 32.652 -1.197 C 32.23 -0.281 31.824 0.66 31.387 1.572 C 29.265 6.236 27.052 11.054 24.372 15.374 C 23.984 15.977 23.202 16.193 22.571 15.865 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 73.36 13.964 C 69.837 10.253 64.654 8.608 59.634 9.599 C 52.918 10.801 46.37 16.031 43.354 22.62 C 39.74 30.511 42.067 37.777 49.582 42.044 C 54.239 44.823 59.807 45.649 65.076 44.351 C 69.086 43.267 72.733 41.146 75.653 38.196 C 78.046 35.837 79.356 32.589 79.265 29.227 C 79.315 22.913 75.769 16.387 73.36 13.964 Z M 77.238 29.207 C 77.32 32.029 76.235 34.755 74.236 36.749 C 71.281 39.737 67.512 41.796 63.395 42.657 C 58.976 43.453 54.411 42.608 50.568 40.278 C 43.964 36.53 42.005 30.4 45.187 23.454 C 47.943 17.438 53.873 12.672 59.982 11.582 C 64.349 10.721 68.856 12.153 71.927 15.379 C 73.744 17.216 77.321 23.328 77.245 29.205 L 77.238 29.207 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 63.728 17.63 C 61.601 22.493 59.968 27.558 58.86 32.748 L 54.137 30.02 C 53.621 29.816 53.027 30.067 52.825 30.59 C 52.66 31.01 52.79 31.492 53.152 31.764 L 59.09 35.196 C 59.315 35.325 59.576 35.365 59.832 35.307 L 60.007 35.263 C 60.302 35.131 60.519 34.861 60.58 34.539 C 61.641 28.997 63.323 23.59 65.586 18.421 C 65.813 17.907 65.583 17.315 65.069 17.089 C 64.554 16.862 63.962 17.092 63.736 17.606 L 63.728 17.63 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 58.632 101.14 C 57.521 101.502 55.902 101.906 53.568 102.472 C 53.084 102.608 52.569 102.558 52.118 102.329 C 51.314 101.926 49.685 99.504 48.962 98.373 C 47.972 98.399 46.981 98.331 46.003 98.172 C 44.599 98.97 43.111 99.621 41.567 100.103 C 40.718 100.314 39.641 100.007 35.495 94.789 L 35.387 94.658 C 34.41 93.435 35.048 92.33 35.937 90.796 L 36.395 89.981 C 36.18 89.364 36.01 88.732 35.879 88.087 C 33.146 86.605 32.536 86.087 32.327 85.823 C 31.647 85.089 31.636 83.48 33.261 77.922 L 33.326 77.72 C 33.766 76.209 34.97 76.106 36.983 75.934 L 38.128 75.829 L 38.381 75.805 L 38.934 75.747 C 39.265 75.321 39.607 74.906 39.977 74.51 C 39.969 74.151 39.981 73.634 40.01 72.835 C 40.038 71.922 40.063 70.778 40.057 69.825 C 40.012 68.067 41.79 67.574 44.255 66.901 C 45.356 66.593 46.724 66.22 48.279 65.662 L 48.338 65.643 C 50.012 65.098 51.107 66.702 52.907 69.342 L 53.093 69.621 C 53.864 69.768 54.617 69.971 55.364 70.219 C 55.992 69.928 56.497 69.677 56.964 69.445 C 58.633 68.619 59.557 68.166 60.638 68.465 C 61.719 68.763 62.358 69.59 63.806 71.281 L 64.443 72.014 C 64.872 72.511 65.244 72.896 65.563 73.233 C 67.4 75.116 67.414 75.85 65.706 78.844 L 65.244 79.646 C 65.338 79.934 65.432 80.222 65.521 80.496 C 66.113 80.846 66.609 81.126 67.023 81.353 C 69.526 82.751 69.723 83.2 69.107 86.172 C 68.934 86.995 68.801 87.719 68.681 88.329 C 67.809 92.868 67.448 93.202 63.136 93.515 L 61.957 93.603 C 61.718 93.825 61.493 94.022 61.254 94.222 C 61.37 95.863 61.326 97.505 61.119 99.135 C 60.963 100.054 60.464 100.543 58.632 101.14 Z M 53.037 100.532 L 53.096 100.513 C 54.887 100.081 58.582 99.188 59.162 98.746 C 59.173 98.692 59.191 98.635 59.217 98.583 C 59.324 97 59.328 95.414 59.238 93.83 C 59.223 93.495 59.373 93.178 59.64 92.976 C 60.06 92.665 60.454 92.32 60.817 91.948 C 60.988 91.784 61.206 91.677 61.446 91.657 L 63.015 91.543 C 64.521 91.436 65.518 91.364 65.826 91.068 C 66.135 90.772 66.384 89.649 66.698 87.992 C 66.817 87.36 66.979 86.634 67.128 85.804 C 67.293 85.222 67.379 84.622 67.386 84.02 C 66.981 83.645 66.524 83.331 66.032 83.086 C 65.529 82.809 64.901 82.457 64.134 81.99 C 63.917 81.858 63.758 81.657 63.685 81.413 C 63.55 80.907 63.386 80.404 63.197 79.915 C 63.073 79.623 63.104 79.295 63.262 79.026 L 63.374 78.837 C 63.577 78.504 63.779 78.17 63.973 77.832 C 64.412 77.161 64.75 76.421 64.962 75.65 C 64.957 75.457 64.611 75.106 64.134 74.618 C 63.805 74.277 63.395 73.861 62.92 73.313 C 62.695 73.047 62.477 72.799 62.287 72.572 C 61.215 71.322 60.505 70.498 60.127 70.382 C 59.749 70.266 59.037 70.657 57.891 71.226 C 57.348 71.49 56.676 71.825 55.877 72.194 C 55.639 72.307 55.367 72.316 55.122 72.23 C 54.228 71.906 53.31 71.663 52.375 71.504 C 52.11 71.468 51.863 71.331 51.699 71.117 L 51.27 70.49 C 50.385 69.194 49.386 67.725 48.962 67.581 C 47.349 68.158 45.947 68.564 44.812 68.854 C 43.677 69.145 42.349 69.534 42.089 69.821 C 42.112 70.826 42.074 71.997 42.044 72.925 C 42.026 73.604 41.999 74.452 42.021 74.676 C 42.093 75.007 41.997 75.357 41.758 75.601 C 41.233 76.141 40.741 76.721 40.304 77.334 C 40.113 77.591 39.816 77.746 39.499 77.748 C 39.212 77.755 38.905 77.79 38.633 77.821 L 38.345 77.849 L 37.179 77.954 C 36.546 77.965 35.916 78.055 35.306 78.225 L 35.279 78.298 L 35.217 78.507 C 33.91 82.977 33.882 84.245 33.928 84.585 C 35.006 85.319 36.126 85.981 37.286 86.565 C 37.566 86.713 37.755 86.984 37.805 87.293 C 37.933 88.127 38.153 88.938 38.459 89.721 C 38.57 89.995 38.551 90.313 38.403 90.571 C 38.141 91.054 37.897 91.459 37.692 91.83 C 37.369 92.304 37.111 92.822 36.918 93.363 C 36.918 93.363 36.918 93.363 36.942 93.434 L 37.052 93.572 C 39.994 97.251 41.04 97.995 41.326 98.141 C 42.726 97.598 44.084 96.967 45.407 96.254 C 45.614 96.136 45.862 96.098 46.095 96.146 C 47.214 96.36 48.357 96.428 49.493 96.362 C 49.894 96.347 50.269 96.565 50.445 96.927 C 51.213 98.196 52.077 99.405 53.037 100.532 Z M 52.948 91.134 C 50.525 92.003 47.817 91.417 45.967 89.625 C 43.831 87.666 42.874 84.73 43.445 81.888 C 43.971 79.684 45.634 77.926 47.805 77.284 C 49.526 76.217 54.477 76.557 57.116 79.546 C 59.976 82.817 58.69 86.796 56.283 89.114 C 55.331 90.032 54.189 90.723 52.935 91.138 L 52.948 91.134 Z M 48.425 79.187 C 46.915 79.614 45.761 80.823 45.396 82.345 C 44.996 84.494 45.736 86.698 47.362 88.158 C 49.792 90.462 52.983 89.509 54.91 87.652 C 57.412 85.252 57.074 82.504 55.593 80.867 C 53.425 78.39 49.467 78.522 48.871 78.977 C 48.774 79.059 48.656 79.119 48.53 79.153 L 48.425 79.187 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 64.537 -0.079 C 63.425 0.283 61.806 0.688 59.472 1.253 C 58.988 1.389 58.473 1.34 58.022 1.111 C 57.218 0.707 55.59 -1.714 54.866 -2.846 C 53.876 -2.82 52.885 -2.887 51.907 -3.046 C 50.503 -2.249 49.015 -1.597 47.472 -1.116 C 46.622 -0.904 45.545 -1.212 41.399 -6.43 L 41.291 -6.561 C 40.314 -7.784 40.952 -8.889 41.841 -10.423 L 42.299 -11.238 C 42.084 -11.855 41.914 -12.487 41.783 -13.132 C 39.05 -14.614 38.44 -15.131 38.231 -15.396 C 37.551 -16.129 37.54 -17.739 39.165 -23.297 L 39.23 -23.499 C 39.671 -25.01 40.874 -25.113 42.888 -25.284 L 44.032 -25.39 L 44.285 -25.414 L 44.838 -25.471 C 45.169 -25.898 45.511 -26.313 45.882 -26.708 C 45.873 -27.067 45.885 -27.585 45.915 -28.383 C 45.943 -29.297 45.968 -30.441 45.961 -31.394 C 45.916 -33.152 47.695 -33.644 50.159 -34.317 C 51.26 -34.625 52.629 -34.999 54.183 -35.556 L 54.242 -35.575 C 55.917 -36.121 57.011 -34.517 58.812 -31.877 L 58.997 -31.597 C 59.768 -31.451 60.522 -31.248 61.268 -30.999 C 61.897 -31.291 62.401 -31.542 62.868 -31.774 C 64.538 -32.6 65.461 -33.053 66.542 -32.754 C 67.623 -32.455 68.262 -31.629 69.71 -29.937 L 70.347 -29.204 C 70.776 -28.708 71.148 -28.322 71.468 -27.985 C 73.304 -26.102 73.319 -25.369 71.61 -22.374 L 71.148 -21.572 C 71.242 -21.285 71.336 -20.997 71.425 -20.722 C 72.017 -20.372 72.513 -20.093 72.927 -19.866 C 75.43 -18.468 75.627 -18.018 75.011 -15.047 C 74.838 -14.223 74.705 -13.5 74.586 -12.889 C 73.713 -8.351 73.352 -8.016 69.041 -7.704 L 67.861 -7.616 C 67.623 -7.393 67.397 -7.197 67.159 -6.996 C 67.274 -5.355 67.23 -3.713 67.023 -2.083 C 66.867 -1.164 66.368 -0.676 64.537 -0.079 Z M 58.941 -0.687 L 59 -0.706 C 60.791 -1.137 64.487 -2.031 65.066 -2.473 C 65.077 -2.527 65.095 -2.583 65.122 -2.635 C 65.228 -4.218 65.232 -5.804 65.143 -7.388 C 65.127 -7.723 65.278 -8.04 65.544 -8.243 C 65.964 -8.553 66.358 -8.899 66.721 -9.27 C 66.892 -9.434 67.111 -9.542 67.35 -9.562 L 68.919 -9.675 C 70.425 -9.783 71.422 -9.854 71.731 -10.15 C 72.039 -10.446 72.288 -11.569 72.602 -13.227 C 72.721 -13.859 72.883 -14.585 73.032 -15.415 C 73.197 -15.996 73.284 -16.596 73.29 -17.199 C 72.885 -17.573 72.429 -17.888 71.937 -18.132 C 71.434 -18.41 70.805 -18.762 70.038 -19.228 C 69.822 -19.36 69.662 -19.562 69.59 -19.806 C 69.454 -20.311 69.29 -20.815 69.102 -21.303 C 68.977 -21.596 69.008 -21.924 69.166 -22.193 L 69.279 -22.381 C 69.481 -22.715 69.683 -23.048 69.877 -23.386 C 70.317 -24.058 70.654 -24.797 70.866 -25.568 C 70.861 -25.762 70.515 -26.112 70.038 -26.601 C 69.709 -26.942 69.299 -27.358 68.824 -27.905 C 68.6 -28.172 68.382 -28.419 68.192 -28.647 C 67.119 -29.896 66.409 -30.721 66.031 -30.837 C 65.653 -30.952 64.942 -30.561 63.796 -29.992 C 63.252 -29.729 62.58 -29.394 61.781 -29.025 C 61.543 -28.911 61.271 -28.902 61.026 -28.989 C 60.132 -29.312 59.214 -29.556 58.279 -29.714 C 58.014 -29.751 57.767 -29.887 57.603 -30.102 L 57.175 -30.729 C 56.289 -32.025 55.29 -33.493 54.867 -33.637 C 53.253 -33.061 51.851 -32.655 50.716 -32.364 C 49.581 -32.074 48.254 -31.685 47.993 -31.397 C 48.016 -30.392 47.978 -29.222 47.948 -28.293 C 47.93 -27.615 47.903 -26.767 47.925 -26.542 C 47.997 -26.211 47.901 -25.862 47.662 -25.618 C 47.137 -25.077 46.646 -24.498 46.209 -23.885 C 46.018 -23.627 45.721 -23.473 45.403 -23.47 C 45.116 -23.464 44.809 -23.429 44.537 -23.398 L 44.249 -23.369 L 43.083 -23.264 C 42.45 -23.253 41.82 -23.164 41.21 -22.994 L 41.184 -22.92 L 41.121 -22.712 C 39.814 -18.242 39.786 -16.974 39.832 -16.634 C 40.91 -15.9 42.03 -15.238 43.19 -14.654 C 43.47 -14.506 43.659 -14.235 43.71 -13.926 C 43.837 -13.092 44.057 -12.281 44.363 -11.498 C 44.475 -11.223 44.455 -10.906 44.308 -10.648 C 44.045 -10.165 43.801 -9.759 43.596 -9.389 C 43.273 -8.914 43.015 -8.396 42.823 -7.856 C 42.823 -7.856 42.823 -7.856 42.846 -7.784 L 42.956 -7.646 C 45.898 -3.968 46.944 -3.223 47.23 -3.078 C 48.631 -3.621 49.988 -4.251 51.311 -4.964 C 51.518 -5.083 51.767 -5.12 51.999 -5.073 C 53.118 -4.859 54.262 -4.79 55.398 -4.857 C 55.798 -4.871 56.173 -4.653 56.349 -4.291 C 57.117 -3.022 57.981 -1.813 58.941 -0.687 Z M 58.852 -10.085 C 56.43 -9.215 53.721 -9.801 51.871 -11.593 C 49.735 -13.552 48.778 -16.489 49.35 -19.33 C 49.876 -21.535 51.538 -23.292 53.709 -23.935 C 55.431 -25.002 60.382 -24.662 63.02 -21.673 C 65.88 -18.401 64.594 -14.423 62.188 -12.105 C 61.235 -11.187 60.093 -10.496 58.839 -10.08 L 58.852 -10.085 Z M 54.33 -22.031 C 52.819 -21.604 51.665 -20.396 51.3 -18.874 C 50.9 -16.725 51.64 -14.521 53.266 -13.061 C 55.696 -10.756 58.887 -11.709 60.814 -13.567 C 63.316 -15.967 62.978 -18.715 61.497 -20.352 C 59.329 -22.829 55.372 -22.697 54.775 -22.242 C 54.678 -22.16 54.561 -22.099 54.434 -22.065 L 54.33 -22.031 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip0)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M122.055 79.1972C119.887 71.6935 114.118 66.1563 106.987 64.755C100.526 63.4774 94.3053 65.907 89.9271 71.4227C85.3058 77.2377 84.2447 86.0526 87.4049 92.3757C89.8913 97.3535 94.6471 100.39 100.998 101.155C102.371 101.32 103.749 101.379 105.133 101.338C113.015 101.13 117.234 97.5306 119.406 94.5565C122.536 90.0906 123.507 84.4576 122.055 79.1972ZM117.739 93.3344C115.011 97.0702 110.614 99.1194 105.075 99.2669C95.3653 99.5267 91.1127 95.1577 89.2592 91.449C86.4382 85.8095 87.4007 77.9327 91.5506 72.7109C95.1643 68.1461 100.176 65.9709 105.435 66.604C105.823 66.6506 106.203 66.6964 106.585 66.7852C112.948 68.0369 118.113 73.0157 120.065 79.7648C121.348 84.412 120.492 89.3874 117.739 93.3344Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M106.632 74.1054C104.773 72.3389 102.527 72.6042 99.8948 74.8873C99.4568 75.2631 99.4061 75.9213 99.7749 76.3585C100.151 76.7966 100.809 76.8473 101.246 76.4785C103.569 74.451 104.504 74.9851 105.197 75.6398C106.805 77.1762 107.562 78.4244 107.45 79.3538C107.338 80.2832 106.218 81.3983 103.732 82.8276C103.095 83.1295 102.701 83.7963 102.752 84.5024L102.724 86.8062C102.725 87.3348 103.115 87.7746 103.635 87.8444L103.748 87.8579C104.319 87.8624 104.788 87.4046 104.799 86.8346L104.819 84.5941C107.12 83.2783 109.246 81.7557 109.505 79.6083C109.717 77.9053 108.804 76.174 106.632 74.1054Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M104.799 92.1773C104.779 92.1105 104.75 92.05 104.722 91.9894C104.693 91.9288 104.657 91.8745 104.614 91.8193C104.577 91.7649 104.526 91.7159 104.474 91.6739C104.423 91.6248 104.37 91.5899 104.31 91.5541L104.127 91.4749C103.996 91.4306 103.855 91.4137 103.718 91.4257L103.521 91.4592C103.261 91.5422 103.04 91.7156 102.898 91.9484C102.869 92.0093 102.84 92.0771 102.818 92.1388C102.803 92.2013 102.795 92.2717 102.794 92.3359C102.779 92.4055 102.771 92.4689 102.77 92.5401C102.776 92.6052 102.79 92.6711 102.803 92.737C102.824 92.8038 102.845 92.8635 102.881 92.9249C102.909 92.9854 102.944 93.0469 102.989 93.095L103.127 93.2475L103.292 93.3602C103.352 93.396 103.414 93.4177 103.476 93.4394C103.606 93.4836 103.747 93.5006 103.884 93.4885C103.949 93.4821 104.014 93.4756 104.081 93.4551L104.268 93.3848C104.329 93.3493 104.384 93.313 104.439 93.2696C104.493 93.2333 104.542 93.1821 104.584 93.13C104.626 93.0779 104.668 93.0258 104.704 92.9658C104.74 92.9059 104.763 92.8371 104.785 92.7684C104.801 92.6989 104.808 92.6355 104.81 92.5643C104.824 92.5017 104.833 92.4313 104.833 92.3671C104.827 92.3021 104.813 92.2432 104.799 92.1773Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip1)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M13.3639 75.6278C13.3083 75.6513 13.2434 75.6697 13.1838 75.6788L-21.1898 79.1139C-21.7875 79.174 -22.3241 78.7345 -22.3842 78.1368L-27.0112 32.259C-27.0454 31.973 -26.9562 31.6832 -26.7705 31.4596C-26.5848 31.2359 -26.3222 31.0916 -26.0342 31.0646L-1.0714 28.5483C-0.771022 28.51 -0.466856 28.5951 -0.226727 28.784L10.1047 37.3922C10.3263 37.5707 10.4706 37.8333 10.4976 38.1213L14.1466 74.434C14.1736 74.722 14.0915 75.0097 13.9058 75.2334C13.7717 75.4191 13.5852 75.5575 13.3639 75.6278ZM-20.3333 76.8332L11.87 73.5918L8.36916 38.7933L-1.32108 30.774L-24.7265 33.1299L-20.3333 76.8332Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M5.23596 53.2712C5.17317 53.2967 5.11553 53.313 5.04866 53.3242L-17.647 55.6039C-18.2446 55.664 -18.7813 55.2245 -18.8414 54.6268C-18.9231 54.0353 -18.502 53.4883 -17.9105 53.4066L4.82114 51.1167C5.42394 51.0474 5.96059 51.4869 6.02789 52.0825C6.0861 52.6184 5.75057 53.1101 5.23189 53.2568L5.23596 53.2712Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M5.89815 59.8215C5.83535 59.8471 5.77772 59.8634 5.71085 59.8745L-16.9848 62.1542C-17.5856 62.2307 -18.1418 61.8045 -18.2183 61.2038C-18.2949 60.603 -17.8687 60.0468 -17.2679 59.9702L5.4874 57.6815C6.0902 57.6121 6.62685 58.0516 6.69415 58.6472C6.75236 59.1832 6.41887 59.682 5.89815 59.8215Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M6.44296 65.2691L6.25566 65.3221L-5.95102 66.5337C-6.55585 66.5958 -7.0925 66.1563 -7.1526 65.5587C-7.2127 64.9611 -6.77523 64.4172 -6.17759 64.3571L6.03222 63.1291C6.63501 63.0597 7.17166 63.4993 7.23897 64.0948C7.29718 64.6308 6.96368 65.1297 6.44296 65.2691Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M9.70666 39.2863L9.49775 39.3454L2.03764 39.8992C1.23328 39.9711 0.426664 39.7324 -0.201306 39.2176C-0.829277 38.7028 -1.23553 37.9541 -1.30945 37.1425L-2.04869 29.7971C-2.07073 29.1965 -1.60145 28.6825 -1.00083 28.6605C-0.447517 28.6363 0.0388485 29.0355 0.122743 29.5798L0.861982 36.9252C0.881917 37.1607 1.00475 37.375 1.18739 37.5256C1.37003 37.6762 1.59917 37.7437 1.83268 37.7165L9.33397 37.1433C9.93568 37.0976 10.46 37.5484 10.5057 38.1501C10.5454 38.6757 10.213 39.1509 9.70666 39.2863Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 125.667 148.164 C 125.632 148.217 125.585 148.271 125.538 148.313 L 97.305 171.301 C 96.814 171.701 96.087 171.626 95.687 171.135 L 64.969 133.478 C 64.773 133.246 64.683 132.939 64.716 132.635 C 64.75 132.33 64.897 132.051 65.134 131.86 L 85.625 115.147 C 85.867 114.939 86.184 114.837 86.505 114.863 L 100.591 116.432 C 100.889 116.461 101.169 116.608 101.36 116.845 L 125.662 146.659 C 125.853 146.897 125.948 147.198 125.914 147.502 C 125.904 147.744 125.821 147.974 125.667 148.164 Z M 96.733 168.798 L 123.171 147.241 L 99.878 118.673 L 86.698 117.249 L 67.484 132.916 L 96.733 168.798 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 90.43 138.092 C 90.39 138.151 90.348 138.199 90.296 138.247 L 83.089 144.119 C 82.623 144.55 81.893 144.515 81.461 144.049 C 81.03 143.583 81.065 142.853 81.531 142.421 C 81.568 142.391 81.6 142.366 81.636 142.335 L 88.844 136.464 C 89.346 136.074 90.069 136.166 90.459 136.669 C 90.781 137.092 90.78 137.686 90.442 138.102 L 90.43 138.092 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 105.533 133.225 C 105.492 133.283 105.451 133.331 105.399 133.38 L 86.764 148.568 C 86.273 148.968 85.546 148.893 85.146 148.403 C 84.731 147.93 84.783 147.204 85.256 146.789 L 103.916 131.571 C 104.406 131.16 105.133 131.234 105.539 131.719 C 105.901 132.157 105.892 132.784 105.521 133.214 L 105.533 133.225 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 109.92 138.6 C 109.879 138.659 109.838 138.707 109.786 138.756 L 91.151 153.944 C 90.667 154.36 89.931 154.308 89.514 153.824 C 89.098 153.341 89.15 152.604 89.634 152.188 L 108.316 136.957 C 108.805 136.546 109.533 136.621 109.938 137.105 C 110.3 137.543 110.297 138.175 109.92 138.6 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 113.563 143.075 L 113.429 143.23 L 103.398 151.386 C 102.903 151.792 102.175 151.717 101.775 151.226 C 101.375 150.736 101.444 150.003 101.935 149.603 L 111.959 141.431 C 112.449 141.02 113.176 141.095 113.581 141.579 C 113.944 142.017 113.94 142.649 113.563 143.075 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 101.341 118.329 L 101.191 118.502 L 94.953 123.323 C 94.287 123.853 93.439 124.112 92.588 124.024 C 91.736 123.936 90.944 123.514 90.408 122.843 L 85.491 116.813 C 85.123 116.297 85.237 115.573 85.752 115.204 C 86.225 114.862 86.884 114.93 87.274 115.36 L 92.191 121.39 C 92.345 121.586 92.578 121.703 92.826 121.729 C 93.074 121.756 93.315 121.682 93.504 121.522 L 99.768 116.661 C 100.271 116.271 100.994 116.363 101.383 116.866 C 101.724 117.305 101.707 117.916 101.341 118.329 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip2)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M179.374 166.955H179.094C176.508 166.568 171.37 165.334 165.352 163.659C162.906 162.992 160.6 162.325 158.854 161.751C157.688 161.384 156.915 161.13 156.408 160.924C156.228 161.064 156.028 161.244 155.862 161.391C155.608 161.631 155.335 161.858 155.049 162.058C154.442 162.471 148.957 166.128 146.898 164.88C146.285 164.493 145.985 163.773 146.131 163.065C146.418 160.984 146.884 158.928 147.518 156.92C145.365 156.179 140.853 154.311 140.187 152.536C138.92 148.987 144.279 129.657 150.423 124.713C150.823 124.392 151.556 123.799 157.301 124.966C166.018 126.741 187.758 133.327 190.271 138.011C190.751 138.905 191.157 141.72 188.498 150.461C187.058 155.199 184.699 161.424 182.4 164.687C181.707 165.681 180.654 166.955 179.374 166.955ZM157.081 159.062C158.927 159.843 173.796 164.16 179.381 165C179.381 165 180.194 164.867 181.84 161.904C184.033 157.634 185.779 153.144 187.052 148.513C188.745 142.575 188.885 139.525 188.558 138.925C187.725 137.363 181.753 134.147 170.29 130.478C159.967 127.141 152.743 125.9 151.63 126.267C149.27 128.209 146.578 133.24 144.425 139.759C142.326 146.111 141.699 150.875 142.059 151.876C142.419 152.877 147.458 154.998 148.99 155.325C149.517 155.439 149.85 155.953 149.737 156.48C149.73 156.52 149.717 156.566 149.704 156.606C148.97 158.742 148.437 160.95 148.111 163.185C149.024 163.152 151.683 161.984 153.996 160.41C154.209 160.256 154.409 160.089 154.602 159.909C155.035 159.469 155.542 159.115 156.095 158.855C156.435 158.735 156.815 158.815 157.081 159.062Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M163.333 152.336C162.946 152.329 162.566 152.216 162.246 152.002C161.966 151.816 157.681 146.364 157.648 146.318C154.895 142.314 155.968 138.818 158.248 137.163C160.554 135.528 163.713 135.862 165.632 137.93C168.091 136.516 170.497 136.356 172.296 137.49C174.029 138.658 174.909 140.74 174.536 142.795C174.322 144.336 172.923 149.574 163.779 152.269C163.633 152.309 163.486 152.336 163.333 152.336ZM161.48 138.064C160.727 138.057 159.994 138.291 159.387 138.731C157.981 139.745 157.101 142.067 159.254 145.203C159.447 145.464 162.586 149.407 163.373 150.354C167.518 149.107 172.097 146.404 172.623 142.514C172.883 141.22 172.363 139.899 171.29 139.132C169.957 138.304 168.031 138.618 165.958 139.986C165.525 140.279 164.945 140.186 164.626 139.772C163.906 138.738 162.739 138.104 161.48 138.064Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip3)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M60.6918 186.656L60.4857 186.81C58.3686 187.948 53.9058 189.865 48.5529 191.943C46.3847 192.797 44.3196 193.574 42.7181 194.112C41.6574 194.483 40.9486 194.722 40.4619 194.848C40.4064 195.05 40.3583 195.293 40.3164 195.493C40.262 195.809 40.1856 196.126 40.0847 196.431C39.8657 197.07 37.8381 202.779 35.6352 202.993C34.9708 203.046 34.3536 202.68 34.0725 202.079C33.1383 200.388 32.3513 198.618 31.7128 196.791C29.7202 197.43 25.3699 198.536 23.9028 197.595C21.0177 195.678 14.3301 178.496 16.1351 171.475C16.2534 171.02 16.4666 170.179 21.3393 167.879C28.7349 164.39 48.3665 157.281 52.7933 159.348C53.6385 159.742 55.4868 161.592 58.337 169.491C59.883 173.772 61.5702 179.654 61.6719 183.321C61.7084 184.435 61.6341 185.952 60.6918 186.656ZM39.9335 193.107C41.7224 192.666 55.0462 187.666 59.6213 185.213C59.6213 185.213 60.1466 184.667 59.7291 181.58C58.9946 177.229 57.8102 172.962 56.2002 168.852C54.1801 163.548 52.6057 161.226 52.0349 160.963C50.5626 160.271 44.3961 161.188 33.9362 164.792C24.499 168.014 18.4963 171.074 17.8786 171.957C17.2094 174.684 17.9943 179.87 19.9951 185.855C21.9435 191.687 24.1029 195.54 24.9185 196.079C25.7341 196.618 30.6115 195.409 31.9201 194.806C32.3702 194.6 32.8982 194.795 33.1048 195.246C33.1219 195.279 33.1378 195.321 33.15 195.357C33.7847 197.333 34.607 199.253 35.5961 201.078C36.2501 200.551 37.5659 198.229 38.4027 195.797C38.4753 195.567 38.5308 195.334 38.574 195.095C38.6507 194.532 38.8292 193.993 39.0934 193.497C39.2776 193.222 39.6014 193.072 39.9335 193.107Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M40.8369 184.715C40.5486 184.923 40.2064 185.048 39.8534 185.067C39.5445 185.083 33.3901 183.427 33.3399 183.41C29.1107 181.977 27.9775 178.812 28.7457 176.339C29.5445 173.867 32.0542 172.375 34.6055 172.842C35.6383 170.448 37.3219 169.006 39.2709 168.852C41.1892 168.758 42.9822 169.807 43.8379 171.526C44.5287 172.778 46.3794 177.405 41.129 184.42C41.0431 184.53 40.9498 184.631 40.8369 184.715ZM31.6214 175.225C31.0632 175.634 30.6518 176.209 30.4475 176.867C29.9699 178.387 30.5994 180.581 33.9097 181.706C34.1951 181.792 38.6759 182.969 39.7762 183.234C42.1424 180.035 44.0274 175.526 42.2752 172.372C41.7546 171.276 40.645 170.589 39.4328 170.614C37.9961 170.738 36.7503 172.029 35.9765 174.176C35.819 174.63 35.3406 174.881 34.8775 174.752C33.7785 174.386 32.571 174.561 31.6214 175.225Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip4)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M146.091 33.2734L145.898 33.4435C143.88 34.7478 139.586 37.0187 134.418 39.5205C132.325 40.5466 130.33 41.4876 128.777 42.1528C127.749 42.6084 127.062 42.9033 126.587 43.0684C126.548 43.2744 126.52 43.5201 126.494 43.7226C126.465 44.0421 126.415 44.3646 126.339 44.6768C126.172 45.3306 124.611 51.1852 122.432 51.576C121.774 51.6817 121.129 51.367 120.801 50.7901C119.734 49.1802 118.807 47.4794 118.023 45.7096C116.088 46.5068 111.841 47.9599 110.303 47.1408C107.273 45.4621 99.2229 28.8749 100.456 21.7313C100.538 21.2674 100.682 20.4124 105.354 17.7269C112.445 13.6541 131.44 4.98615 136.019 6.68987C136.893 7.01493 138.884 8.70984 142.361 16.3539C144.247 20.4959 146.403 26.2229 146.8 29.8703C146.926 30.9771 146.974 32.4958 146.091 33.2734ZM125.92 41.3755C127.668 40.7922 140.545 34.7348 144.908 31.921C144.908 31.921 145.388 31.3349 144.723 28.2915C143.64 24.014 142.116 19.8561 140.18 15.889C137.739 10.7648 135.983 8.57678 135.393 8.36106C133.869 7.79045 127.797 9.20087 117.661 13.6353C108.514 17.6073 102.778 21.1411 102.233 22.0705C101.786 24.8432 102.986 29.9489 105.462 35.7529C107.874 41.4094 110.337 45.0757 111.193 45.5473C112.05 46.0189 116.814 44.4207 118.07 43.7148C118.502 43.4731 119.044 43.6249 119.286 44.0573C119.306 44.089 119.325 44.1293 119.34 44.165C120.132 46.083 121.106 47.9302 122.239 49.6702C122.848 49.0924 123.973 46.6713 124.611 44.18C124.665 43.9446 124.701 43.7081 124.725 43.4664C124.756 42.8994 124.891 42.3478 125.114 41.8322C125.276 41.5428 125.586 41.3672 125.92 41.3755Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M126.145 32.9381C125.874 33.1684 125.543 33.321 125.193 33.3681C124.886 33.4094 118.618 32.2536 118.567 32.2417C114.236 31.1531 112.851 28.0898 113.418 25.5636C114.015 23.035 116.396 21.3456 118.977 21.6058C119.813 19.136 121.375 17.5636 123.306 17.2525C125.21 17.0049 127.082 17.9061 128.073 19.5502C128.863 20.7428 131.08 25.2056 126.412 32.6208C126.335 32.7375 126.25 32.845 126.145 32.9381ZM116.194 24.2208C115.671 24.6738 115.307 25.2803 115.157 25.9525C114.803 27.5065 115.607 29.6424 118.997 30.4972C119.289 30.5593 123.85 31.3715 124.968 31.5471C127.069 28.1677 128.584 23.5219 126.584 20.5192C125.977 19.4685 124.815 18.8732 123.609 18.996C122.187 19.2353 121.049 20.6219 120.451 22.8247C120.331 23.2904 119.874 23.5783 119.402 23.4874C118.277 23.2115 117.088 23.483 116.194 24.2208Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip5)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M374.407 89.3833C372.569 84.5046 370.029 79.7767 367.574 75.1963L366.141 72.5257C365.522 71.3641 364.288 70.6541 362.971 70.7048C362.654 70.0552 362.391 69.4812 362.108 68.8263C361.396 67.2107 359.51 66.4693 357.89 67.175L347.756 71.585C346.856 70.998 345.728 70.8727 344.719 71.2459C342.008 72.2536 335.297 74.8047 331.347 76.5429C329.79 77.2459 328.977 78.9792 329.442 80.6261C331.366 87.3153 336.012 97.8797 339.215 103.818C339.895 105.097 341.316 105.805 342.746 105.583C342.957 105.551 343.165 105.497 343.37 105.424C353.813 101.706 363.228 98.2882 372.686 93.6615C374.267 92.885 375.007 91.0442 374.407 89.3833ZM358.692 69.021C359.294 68.7668 359.993 69.0427 360.254 69.6451C360.489 70.1746 360.736 70.7303 361.003 71.2828C358.385 72.388 356.682 73.1416 354.083 74.2856L352.628 74.9308C352.126 75.1554 351.54 75.0647 351.121 74.7035C350.885 74.4885 350.353 73.9772 349.847 73.5037L349.421 73.0946L358.692 69.021ZM371.809 91.876C362.448 96.4526 353.096 99.8749 342.709 103.549C342.05 103.798 341.316 103.513 340.995 102.885C337.842 97.0436 333.274 86.6629 331.382 80.0943C331.194 79.4178 331.529 78.7019 332.175 78.413C336.089 76.6733 342.745 74.1658 345.428 73.1624C345.522 73.1408 345.617 73.126 345.714 73.118C346.102 73.0577 346.502 73.1634 346.805 73.4098C347.043 73.5964 347.824 74.3557 348.443 74.9725C349.061 75.5892 349.518 76.0144 349.792 76.2514C350.8 77.1292 352.214 77.3429 353.436 76.8109L354.898 76.1647C357.731 74.9143 359.467 74.1487 362.555 72.8797C363.227 72.5797 364.016 72.8556 364.349 73.5167C364.822 74.4076 365.324 75.3011 365.784 76.2009C368.208 80.7163 370.699 85.3958 372.51 90.146C372.754 90.8208 372.453 91.5734 371.809 91.876Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M343.177 77.6285L336.103 80.7669C335.594 80.9926 335.366 81.5872 335.591 82.0972C335.775 82.5159 336.217 82.7548 336.666 82.6851C336.757 82.685 336.839 82.6723 336.925 82.6449L344.004 79.4986C344.526 79.3127 344.8 78.7392 344.621 78.2149C344.435 77.6917 343.862 77.4172 343.339 77.5963C343.286 77.6116 343.24 77.6326 343.196 77.6605L343.177 77.6285Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 180.865 105.021 C 181.408 99.836 181.257 94.471 181.116 89.276 L 181.032 86.246 C 180.999 84.93 180.215 83.743 179.015 83.197 C 179.022 82.475 179.044 81.844 179.085 81.131 C 179.173 79.368 177.819 77.86 176.055 77.764 L 165.02 77.164 C 164.478 76.236 163.526 75.618 162.456 75.499 C 159.582 75.185 152.439 74.457 148.129 74.24 C 146.422 74.171 144.919 75.356 144.596 77.036 C 143.317 83.878 142.735 95.404 142.935 102.149 C 142.97 103.596 143.923 104.867 145.301 105.309 C 145.504 105.375 145.714 105.42 145.93 105.446 C 156.931 106.804 166.88 107.97 177.408 108.073 C 179.17 108.088 180.656 106.774 180.865 105.021 Z M 175.945 79.774 C 176.597 79.817 177.098 80.377 177.061 81.032 C 177.034 81.611 177.005 82.218 176.997 82.832 C 174.161 82.646 172.3 82.556 169.465 82.414 L 167.875 82.339 C 167.325 82.314 166.842 81.971 166.629 81.46 C 166.515 81.162 166.268 80.466 166.028 79.816 L 165.831 79.26 L 175.945 79.774 Z M 177.425 106.084 C 167.005 105.979 157.112 104.846 146.18 103.474 C 145.479 103.401 144.95 102.818 144.945 102.113 C 144.745 95.477 145.315 84.15 146.569 77.431 C 146.703 76.741 147.324 76.252 148.03 76.283 C 152.31 76.483 159.383 77.225 162.231 77.53 C 162.325 77.553 162.417 77.583 162.507 77.619 C 162.881 77.739 163.191 78.013 163.351 78.369 C 163.481 78.642 163.838 79.671 164.115 80.5 C 164.391 81.328 164.609 81.913 164.748 82.248 C 165.255 83.484 166.423 84.309 167.754 84.381 L 169.351 84.459 C 172.443 84.611 174.339 84.705 177.668 84.955 C 178.403 84.988 178.985 85.589 178.986 86.329 C 179.01 87.337 179.057 88.361 179.065 89.372 C 179.208 94.494 179.337 99.794 178.827 104.852 C 178.743 105.565 178.136 106.102 177.425 106.084 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 158.218 80.513 L 150.488 80.148 C 149.932 80.121 149.461 80.55 149.434 81.107 C 149.411 81.564 149.698 81.975 150.131 82.115 C 150.212 82.155 150.291 82.18 150.381 82.195 L 158.119 82.556 C 158.669 82.624 159.171 82.233 159.246 81.684 C 159.314 81.134 158.925 80.631 158.378 80.557 C 158.323 80.547 158.273 80.545 158.22 80.55 L 158.218 80.513 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 451.902 160.633 C 452.445 155.448 452.294 150.083 452.153 144.888 L 452.069 141.859 C 452.036 140.542 451.252 139.355 450.052 138.81 C 450.059 138.087 450.081 137.456 450.122 136.744 C 450.21 134.981 448.856 133.472 447.092 133.377 L 436.057 132.776 C 435.515 131.848 434.563 131.23 433.493 131.112 C 430.619 130.797 423.476 130.07 419.166 129.853 C 417.458 129.783 415.956 130.968 415.633 132.649 C 414.354 139.491 413.772 151.017 413.972 157.761 C 414.007 159.208 414.96 160.479 416.338 160.922 C 416.541 160.987 416.751 161.033 416.967 161.058 C 427.968 162.417 437.917 163.582 448.445 163.686 C 450.207 163.7 451.693 162.386 451.902 160.633 Z M 446.982 135.387 C 447.634 135.429 448.134 135.989 448.098 136.645 C 448.071 137.223 448.042 137.831 448.033 138.444 C 445.198 138.259 443.337 138.169 440.502 138.027 L 438.912 137.951 C 438.362 137.927 437.879 137.583 437.666 137.072 C 437.552 136.774 437.305 136.079 437.065 135.429 L 436.868 134.872 L 446.982 135.387 Z M 448.462 161.697 C 438.042 161.591 428.149 160.458 417.217 159.086 C 416.516 159.013 415.987 158.43 415.982 157.725 C 415.782 151.09 416.352 139.763 417.606 133.043 C 417.74 132.354 418.361 131.864 419.067 131.895 C 423.347 132.095 430.42 132.837 433.268 133.143 C 433.362 133.165 433.454 133.195 433.543 133.231 C 433.917 133.351 434.228 133.625 434.388 133.981 C 434.518 134.255 434.875 135.283 435.152 136.112 C 435.428 136.941 435.646 137.526 435.784 137.86 C 436.292 139.097 437.46 139.922 438.791 139.994 L 440.388 140.071 C 443.48 140.224 445.376 140.318 448.705 140.568 C 449.44 140.601 450.022 141.201 450.023 141.941 C 450.046 142.95 450.094 143.973 450.102 144.984 C 450.245 150.106 450.374 155.406 449.864 160.464 C 449.78 161.177 449.173 161.715 448.462 161.697 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 429.255 136.125 L 421.525 135.76 C 420.969 135.734 420.498 136.163 420.471 136.72 C 420.448 137.176 420.735 137.588 421.168 137.727 C 421.249 137.767 421.328 137.793 421.418 137.807 L 429.156 138.168 C 429.706 138.236 430.208 137.846 430.283 137.297 C 430.351 136.746 429.962 136.244 429.414 136.169 C 429.36 136.159 429.31 136.157 429.257 136.162 L 429.255 136.125 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 314.629 176.92 C 317.395 172.501 319.616 167.615 321.771 162.886 L 323.027 160.128 C 323.575 158.931 323.392 157.52 322.553 156.503 C 322.878 155.857 323.175 155.299 323.524 154.677 C 324.378 153.133 323.824 151.183 322.281 150.322 L 312.631 144.935 C 312.553 143.863 311.968 142.89 311.06 142.313 C 308.616 140.769 302.519 136.977 298.742 134.889 C 297.239 134.076 295.368 134.481 294.34 135.849 C 290.186 141.434 284.599 151.532 281.817 157.679 C 281.212 158.994 281.51 160.554 282.553 161.557 C 282.707 161.705 282.876 161.838 283.058 161.957 C 292.345 168.009 300.77 173.426 310.183 178.145 C 311.759 178.932 313.671 178.404 314.629 176.92 Z M 321.299 152.079 C 321.866 152.404 322.07 153.127 321.749 153.699 C 321.471 154.208 321.178 154.741 320.901 155.288 C 318.435 153.875 316.803 152.977 314.318 151.604 L 312.923 150.838 C 312.44 150.574 312.157 150.053 312.19 149.501 C 312.219 149.184 312.302 148.45 312.372 147.761 L 312.439 147.174 L 321.299 152.079 Z M 311.071 176.365 C 301.757 171.693 293.367 166.329 284.149 160.295 C 283.552 159.922 283.333 159.165 283.638 158.529 C 286.373 152.481 291.861 142.555 295.939 137.069 C 296.362 136.509 297.135 136.342 297.756 136.68 C 301.513 138.74 307.541 142.513 309.965 144.039 C 310.04 144.101 310.109 144.168 310.174 144.24 C 310.457 144.512 310.616 144.894 310.603 145.284 C 310.599 145.587 310.468 146.668 310.353 147.534 C 310.238 148.4 310.176 149.021 310.154 149.383 C 310.067 150.716 310.754 151.971 311.917 152.62 L 313.317 153.391 C 316.029 154.886 317.691 155.803 320.572 157.49 C 321.217 157.843 321.476 158.638 321.152 159.303 C 320.73 160.219 320.324 161.16 319.887 162.072 C 317.765 166.736 315.552 171.554 312.872 175.874 C 312.484 176.477 311.702 176.693 311.071 176.365 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 305.05 144.956 L 298.266 141.232 C 297.778 140.964 297.167 141.142 296.898 141.631 C 296.676 142.031 296.754 142.527 297.082 142.842 C 297.137 142.914 297.196 142.971 297.271 143.024 L 304.064 146.747 C 304.528 147.05 305.15 146.92 305.459 146.46 C 305.762 145.995 305.633 145.373 305.174 145.065 C 305.129 145.032 305.085 145.008 305.036 144.99 L 305.05 144.956 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 473.346 123.268 C 473.283 123.267 473.213 123.256 473.151 123.239 L 438.651 111.609 C 438.051 111.407 437.727 110.752 437.928 110.152 L 453.411 64.086 C 453.502 63.797 453.714 63.557 453.989 63.423 C 454.264 63.289 454.579 63.265 454.868 63.364 L 479.932 71.789 C 480.237 71.883 480.492 72.097 480.641 72.382 L 486.823 85.136 C 486.958 85.403 486.983 85.718 486.884 86.006 L 474.617 122.462 C 474.517 122.75 474.314 122.991 474.038 123.125 C 473.829 123.245 473.59 123.297 473.346 123.268 Z M 440.464 109.791 L 472.796 120.664 L 484.548 85.727 L 478.725 73.818 L 455.226 65.915 L 440.464 109.791 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 463.081 88.087 C 463.01 88.084 462.947 88.075 462.878 88.057 L 454.069 85.089 C 453.455 84.925 453.096 84.289 453.26 83.675 C 453.424 83.062 454.06 82.702 454.674 82.867 C 454.72 82.881 454.757 82.895 454.803 82.91 L 463.613 85.877 C 464.21 86.094 464.518 86.755 464.301 87.353 C 464.114 87.851 463.612 88.167 463.079 88.103 L 463.081 88.087 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 475.251 98.269 C 475.18 98.267 475.117 98.257 475.048 98.239 L 452.264 90.571 C 451.664 90.369 451.339 89.714 451.541 89.114 C 451.72 88.51 452.362 88.168 452.965 88.346 L 475.788 96.02 C 476.397 96.215 476.722 96.871 476.528 97.472 C 476.351 98.012 475.815 98.338 475.253 98.254 L 475.251 98.269 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 473.042 104.847 C 472.971 104.844 472.908 104.835 472.839 104.817 L 450.055 97.149 C 449.445 96.961 449.096 96.31 449.283 95.7 C 449.47 95.09 450.121 94.741 450.731 94.929 L 473.577 102.614 C 474.186 102.809 474.511 103.464 474.316 104.065 C 474.14 104.605 473.603 104.939 473.042 104.847 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 471.199 110.315 L 470.996 110.285 L 458.748 106.146 C 458.14 105.943 457.816 105.288 458.018 104.688 C 458.22 104.088 458.876 103.756 459.476 103.958 L 471.734 108.082 C 472.343 108.277 472.668 108.932 472.474 109.533 C 472.297 110.073 471.76 110.407 471.199 110.315 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 485.618 86.782 L 485.392 86.748 L 477.987 84.039 C 477.184 83.759 476.513 83.179 476.133 82.412 C 475.754 81.645 475.689 80.75 475.971 79.939 L 478.451 72.564 C 478.691 71.977 479.364 71.688 479.951 71.927 C 480.493 72.144 480.786 72.739 480.63 73.298 L 478.15 80.673 C 478.067 80.908 478.092 81.167 478.202 81.391 C 478.312 81.615 478.503 81.779 478.739 81.854 L 486.191 84.562 C 486.789 84.779 487.096 85.44 486.879 86.038 C 486.689 86.559 486.163 86.872 485.618 86.782 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip6)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M430.302 34.061C426.779 30.3501 421.596 28.7045 416.577 29.6959C409.861 30.8975 403.313 36.128 400.297 42.7171C396.683 50.6083 399.01 57.8736 406.524 62.1404C411.182 64.9198 416.749 65.7461 422.019 64.4481C426.029 63.3635 429.675 61.2429 432.596 58.2925C434.989 55.9336 436.298 52.686 436.207 49.3234C436.258 43.0103 432.712 36.4838 430.302 34.061ZM434.181 49.3034C434.263 52.1254 433.178 54.8515 431.178 56.8461C428.224 59.8336 424.455 61.8926 420.338 62.7543C415.919 63.5501 411.354 62.7045 407.511 60.375C400.907 56.6266 398.947 50.4964 402.13 43.5507C404.885 37.5351 410.816 32.769 416.924 31.679C421.291 30.8179 425.798 32.2498 428.869 35.4757C430.687 37.3133 434.263 43.4249 434.187 49.3017L434.181 49.3034Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M420.67 37.7268C418.543 42.5896 416.911 47.6551 415.802 52.8444L411.08 50.1173C410.563 49.913 409.97 50.1639 409.768 50.6869C409.603 51.1074 409.733 51.5887 410.095 51.8609L416.033 55.2931C416.258 55.4214 416.518 55.4623 416.775 55.4042L416.95 55.3598C417.244 55.2276 417.462 54.958 417.522 54.6354C418.584 49.0937 420.265 43.6871 422.529 38.5182C422.755 38.0035 422.526 37.4118 422.011 37.1856C421.497 36.9594 420.905 37.1885 420.679 37.7032L420.67 37.7268Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 295.801 -12.374 C 292.278 -16.085 287.095 -17.731 282.076 -16.739 C 275.36 -15.538 268.812 -10.307 265.796 -3.718 C 262.182 4.173 264.509 11.438 272.023 15.705 C 276.681 18.484 282.248 19.311 287.518 18.013 C 291.528 16.928 295.174 14.808 298.095 11.857 C 300.488 9.498 301.797 6.251 301.706 2.888 C 301.757 -3.425 298.211 -9.951 295.801 -12.374 Z M 299.68 2.868 C 299.762 5.69 298.677 8.416 296.677 10.411 C 293.723 13.398 289.954 15.457 285.837 16.319 C 281.418 17.115 276.853 16.269 273.01 13.94 C 266.406 10.191 264.446 4.061 267.629 -2.885 C 270.384 -8.9 276.315 -13.666 282.423 -14.756 C 286.79 -15.617 291.297 -14.186 294.368 -10.96 C 296.186 -9.122 299.762 -3.01 299.686 2.866 L 299.68 2.868 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 286.169 -8.709 C 284.042 -3.846 282.41 1.22 281.301 6.409 L 276.579 3.682 C 276.062 3.478 275.469 3.729 275.266 4.252 C 275.102 4.672 275.232 5.153 275.594 5.425 L 281.531 8.858 C 281.757 8.986 282.017 9.027 282.274 8.969 L 282.448 8.924 C 282.743 8.792 282.96 8.523 283.021 8.2 C 284.083 2.658 285.764 -2.748 288.028 -7.917 C 288.254 -8.432 288.025 -9.024 287.51 -9.25 C 286.996 -9.476 286.404 -9.247 286.178 -8.732 L 286.169 -8.709 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 182.845 62.325 C 181.733 62.687 180.114 63.092 177.781 63.657 C 177.297 63.793 176.782 63.744 176.331 63.514 C 175.526 63.111 173.898 60.69 173.175 59.558 C 172.185 59.584 171.193 59.517 170.216 59.358 C 168.812 60.155 167.324 60.807 165.78 61.288 C 164.93 61.5 163.854 61.192 159.708 55.974 L 159.6 55.843 C 158.623 54.62 159.261 53.515 160.15 51.981 L 160.608 51.166 C 160.392 50.549 160.223 49.917 160.092 49.272 C 157.359 47.79 156.749 47.273 156.54 47.008 C 155.86 46.275 155.849 44.665 157.474 39.107 L 157.538 38.905 C 157.979 37.394 159.183 37.291 161.196 37.12 L 162.341 37.014 L 162.594 36.99 L 163.147 36.933 C 163.478 36.506 163.82 36.091 164.19 35.695 C 164.182 35.336 164.194 34.819 164.223 34.021 C 164.251 33.107 164.276 31.963 164.27 31.01 C 164.225 29.252 166.003 28.76 168.468 28.087 C 169.569 27.779 170.937 27.405 172.492 26.848 L 172.551 26.829 C 174.225 26.283 175.319 27.887 177.12 30.527 L 177.305 30.806 C 178.077 30.953 178.83 31.156 179.577 31.405 C 180.205 31.113 180.709 30.862 181.177 30.63 C 182.846 29.804 183.769 29.351 184.851 29.65 C 185.932 29.949 186.57 30.775 188.019 32.466 L 188.655 33.199 C 189.085 33.696 189.457 34.081 189.776 34.419 C 191.613 36.302 191.627 37.035 189.919 40.03 L 189.457 40.831 C 189.551 41.119 189.644 41.407 189.734 41.682 C 190.325 42.032 190.822 42.311 191.236 42.538 C 193.739 43.936 193.936 44.386 193.32 47.357 C 193.147 48.181 193.014 48.904 192.894 49.515 C 192.022 54.053 191.661 54.388 187.349 54.7 L 186.17 54.788 C 185.931 55.01 185.706 55.207 185.467 55.408 C 185.582 57.049 185.539 58.691 185.332 60.321 C 185.176 61.24 184.676 61.728 182.845 62.325 Z M 177.25 61.717 L 177.309 61.698 C 179.1 61.266 182.795 60.373 183.375 59.931 C 183.386 59.877 183.404 59.82 183.43 59.768 C 183.536 58.186 183.541 56.6 183.451 55.016 C 183.436 54.68 183.586 54.364 183.853 54.161 C 184.273 53.851 184.666 53.505 185.03 53.134 C 185.201 52.969 185.419 52.862 185.659 52.842 L 187.228 52.729 C 188.734 52.621 189.73 52.55 190.039 52.254 C 190.348 51.958 190.597 50.835 190.91 49.177 C 191.03 48.545 191.191 47.819 191.341 46.989 C 191.505 46.408 191.592 45.808 191.598 45.205 C 191.194 44.831 190.737 44.516 190.245 44.272 C 189.742 43.994 189.114 43.642 188.347 43.176 C 188.13 43.044 187.971 42.842 187.898 42.598 C 187.762 42.093 187.598 41.589 187.41 41.101 C 187.286 40.808 187.317 40.48 187.475 40.211 L 187.587 40.023 C 187.79 39.689 187.992 39.355 188.186 39.017 C 188.625 38.346 188.963 37.607 189.175 36.836 C 189.17 36.642 188.824 36.292 188.346 35.803 C 188.018 35.462 187.608 35.046 187.133 34.499 C 186.908 34.232 186.69 33.985 186.5 33.757 C 185.428 32.508 184.717 31.683 184.34 31.567 C 183.962 31.452 183.25 31.843 182.104 32.411 C 181.561 32.675 180.889 33.01 180.09 33.379 C 179.852 33.493 179.58 33.502 179.335 33.415 C 178.441 33.092 177.522 32.848 176.588 32.69 C 176.323 32.653 176.076 32.517 175.912 32.302 L 175.483 31.675 C 174.598 30.379 173.598 28.911 173.175 28.767 C 171.562 29.343 170.16 29.749 169.025 30.039 C 167.89 30.33 166.562 30.719 166.301 31.007 C 166.325 32.012 166.287 33.182 166.257 34.11 C 166.239 34.789 166.212 35.637 166.234 35.861 C 166.306 36.193 166.21 36.542 165.971 36.786 C 165.445 37.327 164.954 37.906 164.517 38.519 C 164.326 38.777 164.029 38.931 163.712 38.933 C 163.425 38.94 163.118 38.975 162.845 39.006 L 162.558 39.034 L 161.391 39.14 C 160.758 39.151 160.129 39.24 159.519 39.41 L 159.492 39.484 L 159.43 39.692 C 158.123 44.162 158.095 45.43 158.14 45.77 C 159.219 46.504 160.339 47.166 161.499 47.75 C 161.779 47.898 161.968 48.169 162.018 48.478 C 162.145 49.312 162.366 50.123 162.672 50.906 C 162.783 51.181 162.764 51.498 162.616 51.756 C 162.354 52.239 162.11 52.645 161.905 53.015 C 161.582 53.489 161.324 54.008 161.131 54.548 C 161.131 54.548 161.131 54.548 161.155 54.62 L 161.265 54.758 C 164.207 58.436 165.253 59.181 165.539 59.326 C 166.939 58.783 168.296 58.153 169.619 57.439 C 169.827 57.321 170.075 57.284 170.308 57.331 C 171.426 57.545 172.57 57.614 173.706 57.547 C 174.107 57.533 174.481 57.751 174.657 58.113 C 175.425 59.382 176.29 60.591 177.25 61.717 Z M 177.161 52.319 C 174.738 53.188 172.03 52.602 170.18 50.811 C 168.044 48.852 167.087 45.915 167.658 43.073 C 168.184 40.869 169.847 39.112 172.018 38.469 C 173.739 37.402 178.69 37.742 181.328 40.731 C 184.189 44.003 182.902 47.981 180.496 50.299 C 179.544 51.217 178.401 51.908 177.148 52.324 L 177.161 52.319 Z M 172.638 40.373 C 171.128 40.8 169.973 42.008 169.609 43.53 C 169.209 45.679 169.949 47.883 171.575 49.343 C 174.005 51.648 177.196 50.695 179.122 48.837 C 181.625 46.437 181.287 43.689 179.805 42.052 C 177.638 39.575 173.68 39.707 173.083 40.162 C 172.987 40.244 172.869 40.305 172.743 40.338 L 172.638 40.373 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 273.182 66.795 C 272.07 67.158 270.451 67.562 268.117 68.127 C 267.633 68.263 267.118 68.214 266.667 67.985 C 265.863 67.582 264.235 65.16 263.511 64.029 C 262.521 64.055 261.53 63.987 260.552 63.828 C 259.148 64.626 257.66 65.277 256.117 65.758 C 255.267 65.97 254.19 65.663 250.044 60.445 L 249.936 60.313 C 248.959 59.091 249.597 57.986 250.487 56.451 L 250.944 55.637 C 250.729 55.02 250.559 54.388 250.429 53.743 C 247.695 52.26 247.086 51.743 246.876 51.478 C 246.196 50.745 246.185 49.135 247.81 43.577 L 247.875 43.375 C 248.316 41.864 249.519 41.762 251.533 41.59 L 252.678 41.485 L 252.93 41.46 L 253.483 41.403 C 253.814 40.977 254.156 40.562 254.527 40.166 C 254.518 39.807 254.53 39.289 254.56 38.491 C 254.588 37.578 254.613 36.434 254.606 35.481 C 254.561 33.723 256.34 33.23 258.805 32.557 C 259.905 32.249 261.274 31.875 262.828 31.318 L 262.887 31.299 C 264.562 30.753 265.656 32.357 267.457 34.997 L 267.642 35.277 C 268.413 35.424 269.167 35.627 269.913 35.875 C 270.542 35.584 271.046 35.332 271.513 35.101 C 273.183 34.274 274.106 33.822 275.187 34.12 C 276.268 34.419 276.907 35.246 278.355 36.937 L 278.992 37.67 C 279.421 38.167 279.793 38.552 280.113 38.889 C 281.949 40.772 281.964 41.505 280.256 44.5 L 279.793 45.302 C 279.887 45.59 279.981 45.877 280.07 46.152 C 280.662 46.502 281.158 46.782 281.572 47.008 C 284.075 48.407 284.272 48.856 283.656 51.828 C 283.483 52.651 283.35 53.375 283.231 53.985 C 282.358 58.523 281.997 58.858 277.686 59.171 L 276.506 59.258 C 276.268 59.481 276.042 59.677 275.804 59.878 C 275.919 61.519 275.875 63.161 275.668 64.791 C 275.512 65.71 275.013 66.199 273.182 66.795 Z M 267.586 66.188 L 267.645 66.169 C 269.436 65.737 273.132 64.844 273.711 64.402 C 273.722 64.347 273.74 64.291 273.767 64.239 C 273.873 62.656 273.877 61.07 273.788 59.486 C 273.773 59.151 273.923 58.834 274.189 58.632 C 274.609 58.321 275.003 57.976 275.367 57.604 C 275.537 57.44 275.756 57.333 275.995 57.312 L 277.564 57.199 C 279.07 57.092 280.067 57.02 280.376 56.724 C 280.684 56.428 280.933 55.305 281.247 53.648 C 281.366 53.015 281.528 52.29 281.677 51.46 C 281.842 50.878 281.929 50.278 281.935 49.676 C 281.531 49.301 281.074 48.987 280.582 48.742 C 280.079 48.465 279.45 48.112 278.683 47.646 C 278.467 47.514 278.307 47.313 278.235 47.069 C 278.099 46.563 277.935 46.06 277.747 45.571 C 277.623 45.279 277.653 44.95 277.811 44.682 L 277.924 44.493 C 278.126 44.16 278.328 43.826 278.522 43.488 C 278.962 42.817 279.299 42.077 279.511 41.306 C 279.506 41.113 279.16 40.762 278.683 40.274 C 278.355 39.932 277.944 39.516 277.469 38.969 C 277.245 38.702 277.027 38.455 276.837 38.228 C 275.764 36.978 275.054 36.153 274.676 36.038 C 274.298 35.922 273.587 36.313 272.441 36.882 C 271.897 37.146 271.225 37.481 270.426 37.849 C 270.189 37.963 269.917 37.972 269.671 37.886 C 268.777 37.562 267.859 37.319 266.924 37.16 C 266.659 37.124 266.412 36.987 266.248 36.773 L 265.82 36.146 C 264.934 34.85 263.935 33.381 263.512 33.237 C 261.898 33.814 260.497 34.22 259.361 34.51 C 258.226 34.8 256.899 35.19 256.638 35.477 C 256.662 36.482 256.623 37.652 256.593 38.581 C 256.575 39.26 256.548 40.108 256.57 40.332 C 256.642 40.663 256.546 41.013 256.308 41.257 C 255.782 41.797 255.291 42.377 254.854 42.989 C 254.663 43.247 254.366 43.402 254.048 43.404 C 253.761 43.411 253.454 43.446 253.182 43.476 L 252.895 43.505 L 251.728 43.61 C 251.095 43.621 250.466 43.71 249.855 43.88 L 249.829 43.954 L 249.766 44.163 C 248.459 48.633 248.431 49.901 248.477 50.24 C 249.555 50.974 250.676 51.637 251.835 52.221 C 252.115 52.368 252.305 52.639 252.355 52.949 C 252.482 53.783 252.703 54.593 253.008 55.376 C 253.12 55.651 253.1 55.969 252.953 56.227 C 252.691 56.71 252.446 57.115 252.242 57.486 C 251.919 57.96 251.661 58.478 251.468 59.018 C 251.468 59.018 251.468 59.018 251.491 59.09 L 251.601 59.228 C 254.544 62.907 255.589 63.651 255.875 63.797 C 257.276 63.254 258.633 62.623 259.956 61.91 C 260.163 61.792 260.412 61.754 260.644 61.801 C 261.763 62.016 262.907 62.084 264.043 62.018 C 264.443 62.003 264.818 62.221 264.994 62.583 C 265.762 63.852 266.626 65.061 267.586 66.188 Z M 267.497 56.79 C 265.075 57.659 262.366 57.073 260.516 55.281 C 258.38 53.322 257.423 50.385 257.995 47.544 C 258.521 45.34 260.184 43.582 262.354 42.94 C 264.076 41.872 269.027 42.212 271.665 45.202 C 274.525 48.473 273.239 52.452 270.833 54.77 C 269.88 55.688 268.738 56.378 267.484 56.794 L 267.497 56.79 Z M 262.975 44.843 C 261.464 45.27 260.31 46.478 259.945 48.001 C 259.546 50.15 260.286 52.354 261.912 53.813 C 264.341 56.118 267.532 55.165 269.459 53.307 C 271.962 50.907 271.623 48.16 270.142 46.523 C 267.975 44.046 264.017 44.178 263.42 44.633 C 263.324 44.715 263.206 44.775 263.079 44.809 L 262.975 44.843 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 242.943 181.868 C 241.832 182.23 240.213 182.635 237.879 183.2 C 237.395 183.336 236.88 183.287 236.429 183.058 C 235.625 182.654 233.996 180.233 233.273 179.101 C 232.283 179.127 231.292 179.06 230.314 178.901 C 228.91 179.698 227.422 180.35 225.878 180.831 C 225.029 181.043 223.952 180.735 219.806 175.517 L 219.698 175.386 C 218.721 174.163 219.359 173.058 220.248 171.524 L 220.706 170.709 C 220.491 170.092 220.321 169.46 220.19 168.816 C 217.457 167.333 216.847 166.816 216.638 166.551 C 215.958 165.818 215.947 164.208 217.572 158.65 L 217.637 158.448 C 218.077 156.937 219.281 156.834 221.294 156.663 L 222.439 156.558 L 222.692 156.533 L 223.245 156.476 C 223.576 156.05 223.918 155.634 224.288 155.239 C 224.28 154.88 224.292 154.362 224.321 153.564 C 224.349 152.65 224.374 151.506 224.368 150.554 C 224.323 148.796 226.101 148.303 228.566 147.63 C 229.667 147.322 231.035 146.948 232.59 146.391 L 232.649 146.372 C 234.323 145.826 235.418 147.43 237.218 150.07 L 237.404 150.35 C 238.175 150.496 238.928 150.699 239.675 150.948 C 240.303 150.656 240.808 150.405 241.275 150.173 C 242.944 149.347 243.868 148.894 244.949 149.193 C 246.03 149.492 246.669 150.318 248.117 152.01 L 248.754 152.743 C 249.183 153.239 249.555 153.625 249.874 153.962 C 251.711 155.845 251.725 156.578 250.017 159.573 L 249.555 160.375 C 249.649 160.662 249.743 160.95 249.832 161.225 C 250.424 161.575 250.92 161.854 251.334 162.081 C 253.837 163.479 254.034 163.929 253.418 166.9 C 253.245 167.724 253.112 168.447 252.992 169.058 C 252.12 173.596 251.759 173.931 247.447 174.243 L 246.268 174.331 C 246.029 174.554 245.804 174.75 245.565 174.951 C 245.681 176.592 245.637 178.234 245.43 179.864 C 245.274 180.783 244.775 181.271 242.943 181.868 Z M 237.348 181.261 L 237.407 181.241 C 239.198 180.81 242.893 179.916 243.473 179.474 C 243.484 179.42 243.502 179.364 243.528 179.312 C 243.635 177.729 243.639 176.143 243.549 174.559 C 243.534 174.224 243.684 173.907 243.951 173.704 C 244.371 173.394 244.765 173.049 245.128 172.677 C 245.299 172.513 245.517 172.405 245.757 172.385 L 247.326 172.272 C 248.832 172.164 249.829 172.093 250.137 171.797 C 250.446 171.501 250.695 170.378 251.009 168.72 C 251.128 168.088 251.29 167.363 251.439 166.533 C 251.604 165.951 251.69 165.351 251.697 164.748 C 251.292 164.374 250.835 164.06 250.343 163.815 C 249.841 163.537 249.212 163.185 248.445 162.719 C 248.228 162.587 248.069 162.386 247.997 162.141 C 247.861 161.636 247.697 161.132 247.508 160.644 C 247.384 160.351 247.415 160.023 247.573 159.754 L 247.685 159.566 C 247.888 159.232 248.09 158.899 248.284 158.561 C 248.723 157.889 249.061 157.15 249.273 156.379 C 249.268 156.185 248.922 155.835 248.445 155.347 C 248.116 155.005 247.706 154.589 247.231 154.042 C 247.006 153.775 246.788 153.528 246.598 153.3 C 245.526 152.051 244.816 151.226 244.438 151.11 C 244.06 150.995 243.348 151.386 242.203 151.955 C 241.659 152.219 240.987 152.553 240.188 152.922 C 239.95 153.036 239.678 153.045 239.433 152.958 C 238.539 152.635 237.621 152.392 236.686 152.233 C 236.421 152.196 236.174 152.06 236.01 151.846 L 235.581 151.218 C 234.696 149.923 233.697 148.454 233.273 148.31 C 231.66 148.886 230.258 149.292 229.123 149.583 C 227.988 149.873 226.66 150.262 226.4 150.55 C 226.423 151.555 226.385 152.725 226.355 153.654 C 226.337 154.332 226.31 155.18 226.332 155.405 C 226.404 155.736 226.308 156.085 226.069 156.33 C 225.544 156.87 225.052 157.45 224.615 158.062 C 224.424 158.32 224.128 158.474 223.81 158.477 C 223.523 158.483 223.216 158.518 222.944 158.549 L 222.656 158.578 L 221.49 158.683 C 220.857 158.694 220.227 158.783 219.617 158.953 L 219.59 159.027 L 219.528 159.235 C 218.221 163.706 218.193 164.974 218.239 165.313 C 219.317 166.047 220.437 166.709 221.597 167.294 C 221.877 167.441 222.066 167.712 222.116 168.021 C 222.244 168.855 222.464 169.666 222.77 170.449 C 222.881 170.724 222.862 171.041 222.714 171.299 C 222.452 171.783 222.208 172.188 222.003 172.558 C 221.68 173.033 221.422 173.551 221.229 174.091 C 221.229 174.091 221.229 174.091 221.253 174.163 L 221.363 174.301 C 224.305 177.98 225.351 178.724 225.637 178.869 C 227.037 178.326 228.395 177.696 229.718 176.983 C 229.925 176.864 230.173 176.827 230.406 176.874 C 231.525 177.088 232.668 177.157 233.804 177.091 C 234.205 177.076 234.58 177.294 234.756 177.656 C 235.524 178.925 236.388 180.134 237.348 181.261 Z M 237.259 171.863 C 234.836 172.732 232.128 172.146 230.278 170.354 C 228.142 168.395 227.185 165.458 227.756 162.617 C 228.282 160.412 229.945 158.655 232.116 158.013 C 233.838 156.945 238.788 157.285 241.427 160.274 C 244.287 163.546 243.001 167.524 240.594 169.842 C 239.642 170.76 238.5 171.451 237.246 171.867 L 237.259 171.863 Z M 232.736 159.916 C 231.226 160.343 230.072 161.551 229.707 163.074 C 229.307 165.222 230.047 167.426 231.673 168.886 C 234.103 171.191 237.294 170.238 239.221 168.38 C 241.723 165.98 241.385 163.233 239.904 161.595 C 237.736 159.118 233.779 159.251 233.182 159.706 C 233.085 159.788 232.967 159.848 232.841 159.882 L 232.736 159.916 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 409.542 113.786 C 408.43 114.148 406.811 114.553 404.478 115.118 C 403.994 115.254 403.479 115.204 403.028 114.975 C 402.223 114.572 400.595 112.151 399.872 111.019 C 398.882 111.045 397.89 110.977 396.913 110.818 C 395.509 111.616 394.021 112.267 392.477 112.749 C 391.627 112.96 390.551 112.653 386.405 107.435 L 386.297 107.304 C 385.32 106.081 385.958 104.976 386.847 103.442 L 387.305 102.627 C 387.089 102.01 386.92 101.378 386.789 100.733 C 384.056 99.251 383.446 98.733 383.237 98.469 C 382.556 97.735 382.546 96.125 384.171 90.568 L 384.235 90.366 C 384.676 88.855 385.88 88.752 387.893 88.581 L 389.038 88.475 L 389.291 88.451 L 389.843 88.393 C 390.175 87.967 390.517 87.552 390.887 87.156 C 390.879 86.797 390.891 86.28 390.92 85.481 C 390.948 84.568 390.973 83.424 390.967 82.471 C 390.922 80.713 392.7 80.22 395.165 79.547 C 396.266 79.239 397.634 78.866 399.189 78.308 L 399.248 78.289 C 400.922 77.744 402.016 79.348 403.817 81.987 L 404.002 82.267 C 404.774 82.414 405.527 82.617 406.274 82.866 C 406.902 82.574 407.406 82.323 407.873 82.091 C 409.543 81.265 410.466 80.812 411.548 81.111 C 412.629 81.409 413.267 82.236 414.716 83.927 L 415.352 84.66 C 415.782 85.157 416.153 85.542 416.473 85.879 C 418.31 87.762 418.324 88.496 416.616 91.491 L 416.154 92.292 C 416.248 92.58 416.341 92.868 416.431 93.142 C 417.022 93.492 417.519 93.772 417.933 93.999 C 420.436 95.397 420.633 95.846 420.017 98.818 C 419.844 99.641 419.711 100.365 419.591 100.975 C 418.719 105.514 418.358 105.848 414.046 106.161 L 412.867 106.249 C 412.628 106.471 412.403 106.668 412.164 106.868 C 412.279 108.509 412.236 110.151 412.029 111.781 C 411.873 112.701 411.373 113.189 409.542 113.786 Z M 403.947 113.178 L 404.006 113.159 C 405.797 112.727 409.492 111.834 410.072 111.392 C 410.083 111.338 410.101 111.281 410.127 111.229 C 410.233 109.646 410.238 108.06 410.148 106.476 C 410.133 106.141 410.283 105.825 410.55 105.622 C 410.97 105.311 411.363 104.966 411.727 104.594 C 411.898 104.43 412.116 104.323 412.355 104.303 L 413.925 104.189 C 415.431 104.082 416.427 104.01 416.736 103.714 C 417.045 103.418 417.294 102.295 417.607 100.638 C 417.727 100.006 417.888 99.28 418.038 98.45 C 418.202 97.868 418.289 97.268 418.295 96.666 C 417.891 96.291 417.434 95.977 416.942 95.732 C 416.439 95.455 415.811 95.103 415.044 94.636 C 414.827 94.504 414.668 94.303 414.595 94.059 C 414.459 93.553 414.295 93.05 414.107 92.561 C 413.983 92.269 414.013 91.941 414.172 91.672 L 414.284 91.483 C 414.486 91.15 414.689 90.816 414.883 90.478 C 415.322 89.807 415.66 89.067 415.872 88.296 C 415.867 88.103 415.521 87.753 415.043 87.264 C 414.715 86.923 414.305 86.506 413.83 85.959 C 413.605 85.693 413.387 85.445 413.197 85.218 C 412.124 83.969 411.414 83.144 411.037 83.028 C 410.659 82.912 409.947 83.303 408.801 83.872 C 408.258 84.136 407.586 84.471 406.787 84.84 C 406.549 84.953 406.277 84.963 406.032 84.876 C 405.138 84.552 404.219 84.309 403.285 84.151 C 403.02 84.114 402.773 83.977 402.609 83.763 L 402.18 83.136 C 401.295 81.84 400.295 80.372 399.872 80.227 C 398.259 80.804 396.857 81.21 395.722 81.5 C 394.587 81.791 393.259 82.18 392.998 82.467 C 393.022 83.473 392.984 84.643 392.954 85.571 C 392.936 86.25 392.908 87.098 392.931 87.322 C 393.003 87.653 392.907 88.003 392.668 88.247 C 392.142 88.787 391.651 89.367 391.214 89.98 C 391.023 90.237 390.726 90.392 390.409 90.394 C 390.121 90.401 389.815 90.436 389.542 90.467 L 389.255 90.495 L 388.088 90.6 C 387.455 90.612 386.826 90.701 386.216 90.871 L 386.189 90.945 L 386.127 91.153 C 384.82 95.623 384.792 96.891 384.837 97.231 C 385.916 97.965 387.036 98.627 388.196 99.211 C 388.475 99.359 388.665 99.63 388.715 99.939 C 388.842 100.773 389.063 101.584 389.369 102.367 C 389.48 102.642 389.461 102.959 389.313 103.217 C 389.051 103.7 388.807 104.105 388.602 104.476 C 388.279 104.95 388.021 105.468 387.828 106.009 C 387.828 106.009 387.828 106.009 387.852 106.081 L 387.962 106.218 C 390.904 109.897 391.95 110.641 392.236 110.787 C 393.636 110.244 394.993 109.613 396.316 108.9 C 396.524 108.782 396.772 108.744 397.005 108.792 C 398.123 109.006 399.267 109.074 400.403 109.008 C 400.803 108.993 401.178 109.211 401.354 109.574 C 402.122 110.843 402.987 112.051 403.947 113.178 Z M 403.858 103.78 C 401.435 104.649 398.727 104.063 396.877 102.271 C 394.74 100.312 393.784 97.376 394.355 94.534 C 394.881 92.33 396.544 90.572 398.715 89.93 C 400.436 88.863 405.387 89.203 408.025 92.192 C 410.886 95.463 409.599 99.442 407.193 101.76 C 406.241 102.678 405.098 103.369 403.845 103.784 L 403.858 103.78 Z M 399.335 91.833 C 397.825 92.26 396.67 93.469 396.305 94.991 C 395.906 97.14 396.646 99.344 398.272 100.804 C 400.702 103.108 403.893 102.155 405.819 100.298 C 408.322 97.898 407.984 95.15 406.502 93.513 C 404.335 91.036 400.377 91.168 399.78 91.623 C 399.684 91.705 399.566 91.765 399.44 91.799 L 399.335 91.833 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip7)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M510.055 34.1972C507.887 26.6935 502.118 21.1563 494.987 19.755C488.526 18.4774 482.305 20.907 477.927 26.4227C473.306 32.2377 472.245 41.0526 475.405 47.3757C477.891 52.3535 482.647 55.3902 488.998 56.1546C490.371 56.3199 491.749 56.3787 493.133 56.3381C501.015 56.1298 505.234 52.5306 507.406 49.5565C510.536 45.0906 511.507 39.4576 510.055 34.1972ZM505.739 48.3344C503.011 52.0702 498.614 54.1194 493.075 54.2669C483.365 54.5267 479.113 50.1577 477.259 46.449C474.438 40.8095 475.401 32.9327 479.551 27.7109C483.164 23.1461 488.176 20.9709 493.435 21.604C493.823 21.6506 494.203 21.6964 494.585 21.7852C500.948 23.0369 506.113 28.0157 508.065 34.7648C509.348 39.412 508.492 44.3874 505.739 48.3344Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M494.632 29.1054C492.773 27.3389 490.527 27.6042 487.895 29.8873C487.457 30.2631 487.406 30.9213 487.775 31.3585C488.151 31.7966 488.809 31.8473 489.246 31.4785C491.569 29.451 492.504 29.9851 493.197 30.6398C494.805 32.1762 495.562 33.4244 495.45 34.3538C495.338 35.2832 494.218 36.3983 491.732 37.8276C491.095 38.1295 490.701 38.7963 490.752 39.5024L490.724 41.8062C490.725 42.3348 491.115 42.7746 491.635 42.8444L491.748 42.8579C492.319 42.8624 492.788 42.4046 492.799 41.8346L492.819 39.5941C495.12 38.2783 497.246 36.7557 497.505 34.6083C497.717 32.9053 496.804 31.174 494.632 29.1054Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M492.799 47.1773C492.779 47.1105 492.75 47.05 492.722 46.9894C492.693 46.9288 492.657 46.8745 492.614 46.8193C492.577 46.7649 492.526 46.7159 492.474 46.6739C492.423 46.6248 492.37 46.5899 492.31 46.5541L492.127 46.4749C491.996 46.4306 491.855 46.4137 491.718 46.4257L491.521 46.4592C491.261 46.5422 491.04 46.7156 490.898 46.9484C490.869 47.0093 490.84 47.0771 490.818 47.1388C490.803 47.2013 490.795 47.2717 490.794 47.3359C490.779 47.4055 490.771 47.4689 490.77 47.5401C490.776 47.6052 490.79 47.6711 490.803 47.737C490.824 47.8038 490.845 47.8635 490.881 47.9249C490.909 47.9854 490.944 48.0469 490.989 48.095L491.127 48.2475L491.292 48.3602C491.352 48.396 491.414 48.4177 491.476 48.4394C491.606 48.4836 491.747 48.5006 491.884 48.4885C491.949 48.4821 492.014 48.4756 492.081 48.4551L492.268 48.3848C492.329 48.3493 492.384 48.313 492.439 48.2696C492.493 48.2333 492.542 48.1821 492.584 48.13C492.626 48.0779 492.668 48.0258 492.704 47.9658C492.74 47.9059 492.763 47.8371 492.785 47.7684C492.801 47.6989 492.808 47.6355 492.81 47.5643C492.824 47.5017 492.833 47.4313 492.833 47.3671C492.827 47.3021 492.813 47.2432 492.799 47.1773Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip8)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M312.801 98.3359C310.633 90.8323 304.864 85.2951 297.733 83.8938C291.272 82.6162 285.051 85.0458 280.673 90.5615C276.052 96.3765 274.991 105.191 278.151 111.514C280.637 116.492 285.393 119.529 291.744 120.293C293.117 120.459 294.495 120.518 295.879 120.477C303.761 120.269 307.98 116.669 310.152 113.695C313.282 109.229 314.253 103.596 312.801 98.3359ZM308.485 112.473C305.757 116.209 301.36 118.258 295.821 118.406C286.111 118.665 281.859 114.296 280.005 110.588C277.184 104.948 278.147 97.0715 282.297 91.8497C285.91 87.2849 290.922 85.1097 296.181 85.7428C296.569 85.7894 296.949 85.8351 297.331 85.924C303.694 87.1757 308.859 92.1545 310.811 98.9036C312.094 103.551 311.238 108.526 308.485 112.473Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M297.378 93.2442C295.519 91.4777 293.273 91.743 290.641 94.0261C290.203 94.4019 290.152 95.0601 290.521 95.4973C290.897 95.9354 291.555 95.9861 291.992 95.6173C294.315 93.5898 295.25 94.1238 295.943 94.7786C297.551 96.315 298.308 97.5632 298.196 98.4926C298.084 99.422 296.964 100.537 294.478 101.966C293.841 102.268 293.447 102.935 293.498 103.641L293.47 105.945C293.471 106.474 293.861 106.913 294.381 106.983L294.494 106.997C295.065 107.001 295.534 106.543 295.545 105.973L295.565 103.733C297.866 102.417 299.992 100.895 300.251 98.7471C300.463 97.0441 299.55 95.3128 297.378 93.2442Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M295.545 111.316C295.525 111.249 295.496 111.189 295.468 111.128C295.439 111.068 295.403 111.013 295.36 110.958C295.323 110.904 295.272 110.855 295.22 110.813C295.169 110.764 295.116 110.729 295.056 110.693L294.873 110.614C294.742 110.569 294.601 110.552 294.464 110.565L294.267 110.598C294.007 110.681 293.786 110.854 293.644 111.087C293.615 111.148 293.586 111.216 293.564 111.278C293.549 111.34 293.541 111.411 293.54 111.475C293.525 111.544 293.517 111.608 293.516 111.679C293.522 111.744 293.536 111.81 293.549 111.876C293.57 111.943 293.591 112.002 293.627 112.064C293.655 112.124 293.69 112.186 293.735 112.234L293.873 112.386L294.038 112.499C294.098 112.535 294.16 112.556 294.222 112.578C294.352 112.622 294.493 112.639 294.63 112.627C294.695 112.621 294.76 112.614 294.827 112.594L295.014 112.524C295.075 112.488 295.13 112.452 295.185 112.408C295.239 112.372 295.288 112.321 295.33 112.269C295.372 112.217 295.414 112.165 295.45 112.105C295.486 112.045 295.509 111.976 295.531 111.907C295.547 111.838 295.554 111.774 295.556 111.703C295.57 111.641 295.579 111.57 295.579 111.506C295.573 111.441 295.559 111.382 295.545 111.316Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 418.667 3.164 C 418.632 3.217 418.585 3.271 418.538 3.313 L 390.305 26.301 C 389.814 26.701 389.087 26.626 388.687 26.135 L 357.969 -11.522 C 357.773 -11.754 357.683 -12.061 357.716 -12.365 C 357.75 -12.67 357.897 -12.949 358.134 -13.14 L 378.625 -29.853 C 378.867 -30.061 379.184 -30.163 379.505 -30.137 L 393.591 -28.568 C 393.889 -28.54 394.169 -28.392 394.36 -28.155 L 418.662 1.659 C 418.853 1.897 418.948 2.198 418.914 2.502 C 418.904 2.744 418.821 2.974 418.667 3.164 Z M 389.733 23.798 L 416.171 2.241 L 392.878 -26.327 L 379.698 -27.751 L 360.484 -12.084 L 389.733 23.798 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 383.43 -6.908 C 383.39 -6.849 383.348 -6.801 383.296 -6.753 L 376.089 -0.881 C 375.623 -0.45 374.893 -0.485 374.461 -0.951 C 374.03 -1.417 374.065 -2.147 374.531 -2.579 C 374.568 -2.609 374.599 -2.634 374.636 -2.665 L 381.844 -8.536 C 382.346 -8.926 383.069 -8.834 383.459 -8.331 C 383.781 -7.908 383.78 -7.314 383.442 -6.898 L 383.43 -6.908 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 398.533 -11.775 C 398.492 -11.717 398.451 -11.669 398.399 -11.62 L 379.764 3.568 C 379.273 3.968 378.546 3.893 378.146 3.403 C 377.731 2.93 377.783 2.204 378.256 1.789 L 396.916 -13.429 C 397.406 -13.84 398.133 -13.766 398.539 -13.281 C 398.901 -12.843 398.892 -12.216 398.521 -11.786 L 398.533 -11.775 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 402.92 -6.4 C 402.879 -6.341 402.838 -6.293 402.786 -6.244 L 384.151 8.944 C 383.667 9.36 382.931 9.308 382.514 8.824 C 382.098 8.341 382.15 7.604 382.634 7.188 L 401.315 -8.043 C 401.805 -8.454 402.533 -8.379 402.938 -7.895 C 403.3 -7.457 403.297 -6.825 402.92 -6.4 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 406.563 -1.925 L 406.429 -1.77 L 396.398 6.386 C 395.903 6.792 395.175 6.717 394.775 6.226 C 394.375 5.736 394.444 5.003 394.935 4.603 L 404.959 -3.569 C 405.449 -3.98 406.176 -3.905 406.581 -3.421 C 406.944 -2.983 406.94 -2.351 406.563 -1.925 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip9)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M241.364 31.6278C241.308 31.6513 241.243 31.6697 241.184 31.6788L206.81 35.1139C206.213 35.174 205.676 34.7345 205.616 34.1368L200.989 -11.741C200.955 -12.027 201.044 -12.3168 201.23 -12.5404C201.415 -12.7641 201.678 -12.9084 201.966 -12.9354L226.929 -15.4517C227.229 -15.49 227.533 -15.4049 227.773 -15.216L238.105 -6.60778C238.326 -6.42928 238.471 -6.16667 238.498 -5.87865L242.147 30.434C242.174 30.722 242.092 31.0097 241.906 31.2334C241.772 31.4191 241.585 31.5575 241.364 31.6278ZM207.667 32.8332L239.87 29.5918L236.369 -5.20668L226.679 -13.226L203.274 -10.8701L207.667 32.8332Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M218.734 5.22694C218.671 5.25248 218.613 5.26878 218.546 5.27992L209.77 6.15929C209.175 6.25744 208.616 5.84769 208.518 5.25304C208.42 4.65839 208.83 4.09904 209.424 4.00088C209.469 3.99586 209.508 3.99288 209.553 3.98786L218.329 3.10849C218.931 3.0628 219.455 3.51359 219.501 4.1153C219.534 4.61928 219.224 5.0884 218.738 5.24135L218.734 5.22694Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M233.236 9.2712C233.173 9.29675 233.116 9.31304 233.049 9.32418L210.353 11.6039C209.755 11.664 209.219 11.2245 209.159 10.6268C209.077 10.0353 209.498 9.48833 210.089 9.40661L232.821 7.11673C233.424 7.04738 233.961 7.4869 234.028 8.0825C234.086 8.61842 233.751 9.1101 233.232 9.2568L233.236 9.2712Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M233.898 15.8215C233.835 15.8471 233.778 15.8634 233.711 15.8745L211.015 18.1542C210.414 18.2307 209.858 17.8045 209.782 17.2038C209.705 16.603 210.131 16.0468 210.732 15.9702L233.487 13.6815C234.09 13.6121 234.627 14.0516 234.694 14.6472C234.752 15.1832 234.419 15.682 233.898 15.8215Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M234.443 21.2691L234.256 21.3221L222.049 22.5337C221.444 22.5958 220.907 22.1563 220.847 21.5587C220.787 20.9611 221.225 20.4172 221.822 20.3571L234.032 19.1291C234.635 19.0597 235.172 19.4993 235.239 20.0948C235.297 20.6308 234.964 21.1297 234.443 21.2691Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip10)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M248.721 124.279C248.663 124.307 248.594 124.331 248.53 124.345L211.734 130.206C211.095 130.308 210.489 129.869 210.387 129.229L202.519 80.1207C202.464 79.8149 202.542 79.4973 202.728 79.2448C202.914 78.9922 203.187 78.8203 203.496 78.7732L230.216 74.4934C230.537 74.4333 230.87 74.5059 231.14 74.6941L242.806 83.3132C243.056 83.4914 243.228 83.7651 243.275 84.0735L249.489 122.944C249.536 123.252 249.466 123.567 249.28 123.82C249.147 124.028 248.955 124.189 248.721 124.279ZM212.513 127.696L246.984 122.18L241.026 84.931L230.087 76.9057L205.034 80.9148L212.513 127.696Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M222.693 97.2746C222.627 97.3061 222.566 97.3273 222.494 97.3435L213.1 98.8424C212.466 98.9855 211.838 98.5795 211.695 97.9454C211.552 97.3112 211.958 96.6832 212.592 96.5401C212.64 96.5318 212.681 96.5262 212.729 96.518L222.124 95.019C222.769 94.932 223.362 95.3844 223.449 96.0294C223.517 96.57 223.212 97.0947 222.698 97.2899L222.693 97.2746Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M238.563 100.717C238.497 100.749 238.436 100.77 238.364 100.786L214.07 104.668C213.43 104.771 212.825 104.331 212.723 103.691C212.597 103.06 213.016 102.444 213.648 102.319L237.98 98.4233C238.625 98.3108 239.231 98.7503 239.341 99.3873C239.437 99.9607 239.107 100.511 238.557 100.702L238.563 100.717Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M239.688 107.729C239.622 107.76 239.561 107.781 239.489 107.797L215.195 111.68C214.553 111.8 213.927 111.376 213.807 110.734C213.687 110.092 214.111 109.466 214.753 109.346L239.111 105.45C239.756 105.337 240.361 105.777 240.471 106.414C240.567 106.987 240.24 107.546 239.688 107.729Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M240.617 113.56L240.419 113.629L227.351 115.701C226.704 115.806 226.098 115.367 225.996 114.727C225.894 114.087 226.331 113.474 226.97 113.372L240.04 111.281C240.685 111.169 241.29 111.608 241.4 112.245C241.497 112.819 241.169 113.377 240.617 113.56Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M242.497 85.3776L242.276 85.4543L234.278 86.5199C233.416 86.6479 232.533 86.4417 231.824 85.9268C231.115 85.412 230.631 84.6314 230.5 83.7622L229.242 75.8994C229.181 75.2541 229.654 74.6712 230.299 74.6097C230.893 74.5488 231.442 74.9481 231.567 75.5288L232.825 83.3916C232.861 83.6439 233.007 83.8669 233.213 84.0176C233.419 84.1683 233.67 84.2265 233.92 84.1826L241.961 83.0935C242.606 83.0064 243.199 83.4588 243.286 84.1038C243.361 84.6673 243.033 85.1999 242.497 85.3776Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 388.265 172.508 C 388.207 172.536 388.138 172.56 388.074 172.574 L 351.278 178.435 C 350.639 178.537 350.033 178.098 349.931 177.458 L 342.063 128.35 C 342.008 128.044 342.086 127.726 342.272 127.474 C 342.458 127.221 342.731 127.049 343.04 127.002 L 369.76 122.723 C 370.081 122.662 370.414 122.735 370.685 122.923 L 382.35 131.542 C 382.6 131.721 382.772 131.994 382.819 132.303 L 389.033 171.173 C 389.08 171.481 389.01 171.796 388.824 172.049 C 388.691 172.257 388.499 172.418 388.265 172.508 Z M 352.057 175.925 L 386.528 170.409 L 380.57 133.16 L 369.631 125.135 L 344.578 129.144 L 352.057 175.925 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 362.237 145.504 C 362.171 145.535 362.11 145.556 362.038 145.573 L 352.644 147.071 C 352.01 147.215 351.382 146.809 351.239 146.174 C 351.095 145.54 351.501 144.912 352.136 144.769 C 352.184 144.761 352.225 144.755 352.273 144.747 L 361.668 143.248 C 362.313 143.161 362.906 143.613 362.993 144.258 C 363.061 144.799 362.755 145.324 362.242 145.519 L 362.237 145.504 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 378.107 148.946 C 378.041 148.978 377.979 148.999 377.908 149.015 L 353.614 152.897 C 352.974 153 352.369 152.56 352.266 151.92 C 352.141 151.289 352.56 150.673 353.192 150.548 L 377.524 146.652 C 378.169 146.54 378.775 146.979 378.884 147.616 C 378.981 148.19 378.651 148.74 378.101 148.931 L 378.107 148.946 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 379.232 155.958 C 379.166 155.989 379.105 156.01 379.033 156.026 L 354.739 159.909 C 354.097 160.029 353.471 159.605 353.351 158.963 C 353.231 158.321 353.655 157.695 354.297 157.575 L 378.655 153.679 C 379.299 153.566 379.905 154.006 380.015 154.643 C 380.111 155.216 379.783 155.775 379.232 155.958 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 380.161 161.789 L 379.963 161.858 L 366.895 163.93 C 366.248 164.035 365.642 163.596 365.54 162.956 C 365.438 162.316 365.875 161.703 366.514 161.601 L 379.584 159.511 C 380.229 159.398 380.834 159.837 380.944 160.475 C 381.041 161.048 380.713 161.606 380.161 161.789 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 382.041 133.607 L 381.819 133.683 L 373.822 134.749 C 372.96 134.877 372.076 134.671 371.368 134.156 C 370.659 133.641 370.175 132.86 370.044 131.991 L 368.786 124.128 C 368.725 123.483 369.198 122.9 369.843 122.839 C 370.437 122.778 370.986 123.177 371.111 123.758 L 372.369 131.621 C 372.405 131.873 372.551 132.096 372.757 132.247 C 372.963 132.397 373.214 132.455 373.464 132.412 L 381.505 131.322 C 382.15 131.235 382.742 131.688 382.829 132.333 C 382.905 132.896 382.577 133.429 382.041 133.607 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip11)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M468.155 14.9553H467.875C465.289 14.5683 460.151 13.3339 454.133 11.6592C451.687 10.9919 449.381 10.3247 447.635 9.75087C446.469 9.38389 445.695 9.13034 445.189 8.9235C445.009 9.06362 444.809 9.24378 444.642 9.39057C444.389 9.63077 444.116 9.85763 443.829 10.0578C443.223 10.4715 437.738 14.1279 435.679 12.8802C435.066 12.4932 434.766 11.7726 434.912 11.0653C435.199 8.98355 435.665 6.92847 436.298 4.9201C434.146 4.17947 429.634 2.31121 428.967 0.536367C427.701 -3.01332 433.059 -22.3431 439.204 -27.2873C439.604 -27.6076 440.337 -28.2014 446.082 -27.0338C454.799 -25.2589 476.539 -18.6733 479.052 -13.9893C479.531 -13.0952 479.938 -10.2795 477.279 -1.53873C475.839 3.19863 473.48 9.42393 471.181 12.6867C470.488 13.6809 469.435 14.9553 468.155 14.9553ZM445.862 7.06192C447.708 7.84258 462.577 12.1596 468.162 13.0003C468.162 13.0003 468.975 12.8669 470.621 9.90434C472.814 5.63404 474.56 1.14355 475.833 -3.48705C477.525 -9.42544 477.665 -12.4747 477.339 -13.0752C476.506 -14.6365 470.534 -17.8526 459.071 -21.5224C448.748 -24.8586 441.523 -26.0996 440.41 -25.7327C438.051 -23.791 435.359 -18.7601 433.206 -12.2412C431.107 -5.8891 430.48 -1.12505 430.84 -0.124197C431.2 0.876656 436.238 2.99846 437.771 3.32541C438.298 3.43884 438.631 3.95261 438.518 4.47972C438.511 4.51976 438.498 4.56646 438.484 4.60649C437.751 6.74165 437.218 8.95019 436.892 11.1854C437.805 11.1521 440.464 9.98441 442.776 8.40973C442.99 8.25627 443.19 8.08946 443.383 7.90931C443.816 7.46893 444.323 7.1153 444.876 6.85508C445.216 6.73497 445.596 6.81504 445.862 7.06192Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip12)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M355.192 41.6561L354.986 41.8101C352.869 42.9476 348.406 44.8653 343.053 46.9426C340.885 47.7967 338.82 48.5739 337.218 49.1119C336.157 49.4832 335.449 49.7218 334.962 49.8481C334.906 50.0503 334.858 50.2929 334.816 50.4927C334.762 50.8089 334.686 51.1263 334.585 51.4313C334.366 52.0696 332.338 57.7794 330.135 57.9934C329.471 58.0457 328.854 57.6801 328.572 57.0786C327.638 55.388 326.851 53.618 326.213 51.7908C324.22 52.4296 319.87 53.5358 318.403 52.5955C315.518 50.6781 308.83 33.4964 310.635 26.4754C310.753 26.0195 310.967 25.179 315.839 22.8786C323.235 19.3901 342.866 12.2805 347.293 14.3476C348.139 14.742 349.987 16.5918 352.837 24.4912C354.383 28.7716 356.07 34.6536 356.172 38.3211C356.208 39.4345 356.134 40.9522 355.192 41.6561ZM334.433 48.107C336.222 47.6663 349.546 42.666 354.121 40.2128C354.121 40.2128 354.647 39.6673 354.229 36.5802C353.495 32.2295 352.31 27.9622 350.7 23.8521C348.68 18.5479 347.106 16.2255 346.535 15.9629C345.063 15.2715 338.896 16.1881 328.436 19.7916C318.999 23.0138 312.996 26.0741 312.379 26.9566C311.709 29.6842 312.494 34.8701 314.495 40.8546C316.444 46.687 318.603 50.5398 319.418 51.0789C320.234 51.6179 325.111 50.4087 326.42 49.8063C326.87 49.6002 327.398 49.7952 327.605 50.2457C327.622 50.2788 327.638 50.3205 327.65 50.3573C328.285 52.3329 329.107 54.2525 330.096 56.0782C330.75 55.5513 332.066 53.2287 332.903 50.797C332.975 50.5666 333.031 50.3338 333.074 50.0948C333.151 49.5322 333.329 48.9932 333.593 48.4973C333.778 48.2219 334.101 48.0719 334.433 48.107Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M335.337 39.7151C335.049 39.9228 334.706 40.0483 334.353 40.067C334.045 40.0834 327.89 38.4266 327.84 38.4105C323.611 36.9766 322.478 33.8117 323.246 31.3394C324.044 28.8671 326.554 27.375 329.105 27.8423C330.138 25.4478 331.822 24.0064 333.771 23.8518C335.689 23.7584 337.482 24.8075 338.338 26.5261C339.029 27.7784 340.879 32.4053 335.629 39.4203C335.543 39.5305 335.45 39.6308 335.337 39.7151ZM326.121 30.2246C325.563 30.6339 325.152 31.2092 324.947 31.8671C324.47 33.3875 325.099 35.5813 328.41 36.7064C328.695 36.7917 333.176 37.9687 334.276 38.2338C336.642 35.0347 338.527 30.5261 336.775 27.3719C336.255 26.2757 335.145 25.5889 333.933 25.6141C332.496 25.738 331.25 27.0285 330.476 29.1759C330.319 29.6304 329.841 29.8806 329.377 29.7519C328.279 29.3863 327.071 29.5611 326.121 30.2246Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip13)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M514.894 183.063L514.638 183.089C512.237 182.97 507.425 182.308 501.769 181.323C499.471 180.935 497.301 180.534 495.652 180.168C494.552 179.939 493.821 179.777 493.339 179.634C493.187 179.778 493.021 179.961 492.882 180.111C492.672 180.353 492.443 180.586 492.199 180.795C491.682 181.228 486.997 185.071 485 184.117C484.404 183.819 484.064 183.187 484.134 182.527C484.207 180.597 484.447 178.675 484.844 176.78C482.807 176.298 478.511 175 477.74 173.437C476.259 170.305 479.403 152.138 484.574 147.057C484.911 146.728 485.527 146.118 490.888 146.664C499.023 147.495 519.506 151.542 522.23 155.598C522.75 156.373 523.378 158.911 521.74 167.148C520.854 171.612 519.262 177.52 517.455 180.713C516.912 181.686 516.064 182.947 514.894 183.063ZM493.786 177.87C495.545 178.416 509.538 181.013 514.722 181.275C514.722 181.275 515.454 181.079 516.69 178.219C518.308 174.114 519.497 169.848 520.24 165.497C521.248 159.912 521.099 157.11 520.746 156.59C519.842 155.238 514.088 152.839 503.27 150.524C493.524 148.411 486.803 147.933 485.819 148.369C483.837 150.36 481.832 155.206 480.455 161.364C479.112 167.365 478.972 171.78 479.393 172.662C479.813 173.545 484.614 175.028 486.046 175.188C486.538 175.244 486.889 175.683 486.833 176.176C486.831 176.213 486.823 176.257 486.815 176.295C486.338 178.314 486.051 180.383 485.956 182.457C486.788 182.343 489.114 181.034 491.086 179.383C491.267 179.223 491.435 179.053 491.595 178.87C491.951 178.428 492.383 178.059 492.865 177.77C493.165 177.63 493.52 177.668 493.786 177.87Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M498.892 171.15C498.538 171.179 498.18 171.109 497.868 170.943C497.595 170.798 493.18 166.201 493.146 166.161C490.264 162.75 490.928 159.454 492.862 157.734C494.823 156.029 497.742 156.047 499.686 157.764C501.807 156.247 503.993 155.882 505.742 156.756C507.433 157.666 508.427 159.49 508.272 161.404C508.217 162.833 507.413 167.751 499.295 171.048C499.164 171.098 499.033 171.136 498.892 171.15ZM495.9 158.264C495.211 158.326 494.562 158.606 494.047 159.064C492.853 160.12 492.259 162.324 494.513 164.996C494.714 165.217 497.943 168.538 498.749 169.333C502.427 167.815 506.369 164.928 506.497 161.322C506.617 160.114 506.022 158.953 504.971 158.349C503.676 157.713 501.943 158.175 500.172 159.614C499.802 159.922 499.263 159.89 498.933 159.54C498.181 158.66 497.056 158.186 495.9 158.264Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip14)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M545.734 116.295C543.081 111.807 539.762 107.589 536.554 103.501L534.681 101.118C533.87 100.081 532.533 99.5941 531.244 99.8716C530.82 99.2865 530.461 98.7666 530.069 98.1704C529.09 96.7018 527.104 96.2973 525.63 97.2722L516.41 103.366C515.422 102.943 514.289 103.014 513.359 103.556C510.864 105.017 504.694 108.688 501.104 111.082C499.69 112.044 499.19 113.891 499.932 115.433C502.982 121.69 509.383 131.293 513.563 136.59C514.453 137.731 515.975 138.184 517.345 137.718C517.548 137.65 517.743 137.561 517.932 137.453C527.577 131.989 536.26 126.996 544.777 120.806C546.201 119.768 546.611 117.827 545.734 116.295ZM526.739 98.9519C527.288 98.5975 528.024 98.7487 528.385 99.297C528.708 99.7778 529.047 100.283 529.406 100.781C527.018 102.321 525.47 103.358 523.108 104.933L521.786 105.82C521.33 106.128 520.737 106.14 520.263 105.856C519.993 105.685 519.38 105.274 518.8 104.895L518.31 104.565L526.739 98.9519ZM543.605 119.199C535.175 125.323 526.554 130.309 516.958 135.721C516.352 136.08 515.579 135.926 515.155 135.363C511.041 130.154 504.749 120.718 501.751 114.574C501.449 113.941 501.655 113.178 502.241 112.782C505.797 110.392 511.919 106.773 514.389 105.321C514.478 105.284 514.569 105.253 514.663 105.228C515.035 105.102 515.447 105.137 515.788 105.327C516.055 105.47 516.955 106.083 517.671 106.584C518.387 107.084 518.91 107.424 519.221 107.61C520.365 108.301 521.795 108.267 522.907 107.532L524.235 106.643C526.81 104.923 528.388 103.869 531.21 102.086C531.82 101.674 532.645 101.81 533.087 102.403C533.707 103.199 534.355 103.993 534.964 104.799C538.131 108.828 541.392 113.008 543.997 117.374C544.354 117.996 544.187 118.79 543.605 119.199Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M512.943 110.109L506.517 114.422C506.055 114.732 505.933 115.357 506.243 115.82C506.496 116.201 506.972 116.36 507.403 116.214C507.492 116.198 507.571 116.171 507.651 116.13L514.081 111.808C514.563 111.535 514.733 110.923 514.466 110.437C514.193 109.954 513.582 109.783 513.097 110.049C513.047 110.074 513.006 110.102 512.967 110.137L512.943 110.109Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 610.156 46.036 C 606.633 42.325 601.45 40.679 596.43 41.671 C 589.714 42.872 583.166 48.103 580.15 54.692 C 576.536 62.583 578.863 69.849 586.378 74.115 C 591.035 76.895 596.602 77.721 601.872 76.423 C 605.882 75.338 609.529 73.218 612.449 70.267 C 614.842 67.909 616.152 64.661 616.06 61.298 C 616.111 54.985 612.565 48.459 610.156 46.036 Z M 614.034 61.278 C 614.116 64.1 613.031 66.826 611.031 68.821 C 608.077 71.808 604.308 73.868 600.191 74.729 C 595.772 75.525 591.207 74.679 587.364 72.35 C 580.76 68.601 578.8 62.471 581.983 55.526 C 584.739 49.51 590.669 44.744 596.778 43.654 C 601.144 42.793 605.652 44.225 608.723 47.451 C 610.54 49.288 614.117 55.4 614.04 61.277 L 614.034 61.278 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 600.524 49.702 C 598.396 54.564 596.764 59.63 595.656 64.819 L 590.933 62.092 C 590.417 61.888 589.823 62.139 589.621 62.662 C 589.456 63.082 589.586 63.564 589.948 63.836 L 595.886 67.268 C 596.111 67.396 596.372 67.437 596.628 67.379 L 596.803 67.335 C 597.098 67.202 597.315 66.933 597.376 66.61 C 598.437 61.069 600.119 55.662 602.382 50.493 C 602.608 49.978 602.379 49.387 601.864 49.16 C 601.35 48.934 600.758 49.163 600.532 49.678 L 600.524 49.702 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 640.301 151.126 C 636.778 147.415 631.595 145.769 626.576 146.761 C 619.86 147.962 613.312 153.193 610.296 159.782 C 606.682 167.673 609.009 174.938 616.523 179.205 C 621.181 181.985 626.748 182.811 632.018 181.513 C 636.028 180.428 639.674 178.308 642.595 175.357 C 644.988 172.998 646.297 169.751 646.206 166.388 C 646.257 160.075 642.711 153.549 640.301 151.126 Z M 644.18 166.368 C 644.262 169.19 643.177 171.916 641.177 173.911 C 638.223 176.898 634.454 178.957 630.337 179.819 C 625.918 180.615 621.353 179.769 617.51 177.44 C 610.906 173.691 608.946 167.561 612.129 160.615 C 614.884 154.6 620.815 149.834 626.923 148.744 C 631.29 147.883 635.797 149.314 638.868 152.54 C 640.686 154.378 644.262 160.49 644.186 166.366 L 644.18 166.368 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 630.669 154.791 C 628.542 159.654 626.91 164.72 625.801 169.909 L 621.079 167.182 C 620.562 166.978 619.969 167.229 619.766 167.752 C 619.602 168.172 619.732 168.653 620.094 168.925 L 626.031 172.358 C 626.257 172.486 626.517 172.527 626.774 172.469 L 626.948 172.424 C 627.243 172.292 627.46 172.023 627.521 171.7 C 628.583 166.158 630.264 160.752 632.528 155.583 C 632.754 155.068 632.525 154.476 632.01 154.25 C 631.495 154.024 630.904 154.253 630.678 154.768 L 630.669 154.791 Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 572.682 188.295 C 571.57 188.658 569.951 189.062 567.617 189.627 C 567.133 189.763 566.618 189.714 566.167 189.485 C 565.363 189.081 563.735 186.66 563.011 185.529 C 562.021 185.555 561.03 185.487 560.052 185.328 C 558.648 186.126 557.16 186.777 555.617 187.258 C 554.767 187.47 553.69 187.163 549.544 181.945 L 549.436 181.813 C 548.459 180.591 549.097 179.486 549.987 177.951 L 550.444 177.137 C 550.229 176.52 550.059 175.888 549.929 175.243 C 547.195 173.76 546.586 173.243 546.376 172.978 C 545.696 172.245 545.685 170.635 547.31 165.077 L 547.375 164.876 C 547.816 163.364 549.019 163.262 551.033 163.09 L 552.178 162.985 L 552.43 162.96 L 552.983 162.903 C 553.314 162.477 553.656 162.062 554.027 161.666 C 554.018 161.307 554.03 160.789 554.06 159.991 C 554.088 159.078 554.113 157.934 554.106 156.981 C 554.061 155.223 555.84 154.73 558.305 154.057 C 559.405 153.749 560.774 153.376 562.328 152.818 L 562.387 152.799 C 564.062 152.253 565.156 153.857 566.957 156.497 L 567.142 156.777 C 567.913 156.924 568.667 157.127 569.413 157.375 C 570.042 157.084 570.546 156.832 571.013 156.601 C 572.683 155.774 573.606 155.322 574.687 155.62 C 575.768 155.919 576.407 156.746 577.855 158.437 L 578.492 159.17 C 578.921 159.667 579.293 160.052 579.613 160.389 C 581.449 162.272 581.464 163.005 579.756 166 L 579.293 166.802 C 579.387 167.09 579.481 167.378 579.57 167.652 C 580.162 168.002 580.658 168.282 581.072 168.509 C 583.575 169.907 583.772 170.356 583.156 173.328 C 582.983 174.151 582.85 174.875 582.731 175.485 C 581.858 180.023 581.497 180.358 577.186 180.671 L 576.006 180.758 C 575.768 180.981 575.542 181.177 575.304 181.378 C 575.419 183.019 575.375 184.661 575.168 186.291 C 575.012 187.21 574.513 187.699 572.682 188.295 Z M 567.086 187.688 L 567.145 187.669 C 568.936 187.237 572.632 186.344 573.211 185.902 C 573.222 185.847 573.24 185.791 573.267 185.739 C 573.373 184.156 573.377 182.57 573.288 180.986 C 573.273 180.651 573.423 180.334 573.689 180.132 C 574.109 179.821 574.503 179.476 574.867 179.104 C 575.037 178.94 575.256 178.833 575.495 178.812 L 577.064 178.699 C 578.57 178.592 579.567 178.52 579.876 178.224 C 580.184 177.928 580.433 176.805 580.747 175.148 C 580.866 174.515 581.028 173.79 581.177 172.96 C 581.342 172.378 581.429 171.778 581.435 171.176 C 581.031 170.801 580.574 170.487 580.082 170.242 C 579.579 169.965 578.95 169.612 578.183 169.146 C 577.967 169.014 577.807 168.813 577.735 168.569 C 577.599 168.063 577.435 167.56 577.247 167.071 C 577.123 166.779 577.153 166.45 577.311 166.182 L 577.424 165.993 C 577.626 165.66 577.828 165.326 578.022 164.988 C 578.462 164.317 578.799 163.577 579.011 162.806 C 579.006 162.613 578.66 162.262 578.183 161.774 C 577.855 161.432 577.444 161.016 576.969 160.469 C 576.745 160.202 576.527 159.955 576.337 159.728 C 575.264 158.478 574.554 157.653 574.176 157.538 C 573.798 157.422 573.087 157.813 571.941 158.382 C 571.397 158.646 570.725 158.981 569.926 159.35 C 569.689 159.463 569.417 159.472 569.171 159.386 C 568.277 159.062 567.359 158.819 566.424 158.66 C 566.159 158.624 565.912 158.487 565.748 158.273 L 565.32 157.646 C 564.434 156.35 563.435 154.881 563.012 154.737 C 561.398 155.314 559.997 155.72 558.861 156.01 C 557.726 156.3 556.399 156.69 556.138 156.977 C 556.162 157.982 556.123 159.152 556.093 160.081 C 556.075 160.76 556.048 161.608 556.07 161.832 C 556.142 162.163 556.046 162.513 555.808 162.757 C 555.282 163.297 554.791 163.877 554.354 164.49 C 554.163 164.747 553.866 164.902 553.548 164.904 C 553.261 164.911 552.954 164.946 552.682 164.976 L 552.395 165.005 L 551.228 165.11 C 550.595 165.121 549.966 165.211 549.355 165.38 L 549.329 165.454 L 549.266 165.663 C 547.959 170.133 547.931 171.401 547.977 171.741 C 549.055 172.474 550.175 173.137 551.335 173.721 C 551.615 173.868 551.805 174.14 551.855 174.449 C 551.982 175.283 552.203 176.093 552.508 176.876 C 552.62 177.151 552.6 177.469 552.453 177.727 C 552.191 178.21 551.946 178.615 551.742 178.986 C 551.419 179.46 551.161 179.978 550.968 180.518 C 550.968 180.518 550.968 180.518 550.991 180.59 L 551.101 180.728 C 554.044 184.407 555.089 185.151 555.375 185.297 C 556.776 184.754 558.133 184.123 559.456 183.41 C 559.663 183.292 559.912 183.254 560.144 183.301 C 561.263 183.516 562.407 183.584 563.543 183.518 C 563.943 183.503 564.318 183.721 564.494 184.083 C 565.262 185.352 566.126 186.561 567.086 187.688 Z M 566.997 178.29 C 564.575 179.159 561.866 178.573 560.016 176.781 C 557.88 174.822 556.923 171.885 557.495 169.044 C 558.021 166.84 559.684 165.082 561.854 164.44 C 563.576 163.372 568.527 163.712 571.165 166.702 C 574.025 169.973 572.739 173.952 570.333 176.27 C 569.38 177.188 568.238 177.878 566.984 178.294 L 566.997 178.29 Z M 562.475 166.343 C 560.964 166.77 559.81 167.978 559.445 169.501 C 559.046 171.65 559.786 173.854 561.412 175.313 C 563.841 177.618 567.032 176.665 568.959 174.807 C 571.461 172.407 571.123 169.66 569.642 168.023 C 567.475 165.546 563.517 165.678 562.92 166.133 C 562.824 166.215 562.706 166.275 562.579 166.309 L 562.475 166.343 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip15)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M626.419 0.491278C624.251 -7.01239 618.482 -12.5496 611.351 -13.9508C604.89 -15.2285 598.669 -12.7989 594.291 -7.28318C589.67 -1.46821 588.609 7.3467 591.769 13.6698C594.255 18.6476 599.011 21.6843 605.362 22.4488C606.735 22.614 608.113 22.6728 609.497 22.6322C617.379 22.4239 621.598 18.8247 623.77 15.8506C626.9 11.3847 627.871 5.75171 626.419 0.491278ZM622.103 14.6285C619.375 18.3643 614.978 20.4136 609.439 20.561C599.729 20.8208 595.477 16.4518 593.623 12.7431C590.802 7.10361 591.765 -0.77319 595.915 -5.99493C599.528 -10.5598 604.54 -12.735 609.799 -12.1019C610.187 -12.0553 610.567 -12.0095 610.949 -11.9207C617.312 -10.669 622.477 -5.69019 624.429 1.05889C625.712 5.70612 624.856 10.6815 622.103 14.6285Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M610.996 -4.60043C609.137 -6.367 606.891 -6.10168 604.259 -3.81857C603.821 -3.44274 603.77 -2.78457 604.139 -2.34733C604.515 -1.90924 605.173 -1.85858 605.61 -2.22737C607.933 -4.25487 608.868 -3.72082 609.561 -3.06603C611.169 -1.52966 611.926 -0.281433 611.814 0.647934C611.702 1.5773 610.582 2.69246 608.096 4.1217C607.459 4.42366 607.065 5.09043 607.116 5.79652L607.088 8.10032C607.089 8.62896 607.479 9.06874 607.999 9.1385L608.112 9.15206C608.683 9.15649 609.152 8.69872 609.163 8.12869L609.183 5.88826C611.484 4.57245 613.61 3.04986 613.869 0.902454C614.081 -0.800539 613.168 -2.53183 610.996 -4.60043Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M609.163 13.4714C609.143 13.4046 609.114 13.344 609.086 13.2835C609.057 13.2229 609.021 13.1685 608.978 13.1133C608.941 13.0589 608.89 13.0099 608.838 12.9679C608.787 12.9189 608.734 12.884 608.674 12.8482L608.491 12.769C608.36 12.7247 608.219 12.7078 608.082 12.7198L607.885 12.7533C607.625 12.8362 607.404 13.0097 607.262 13.2425C607.233 13.3033 607.204 13.3712 607.182 13.4329C607.167 13.4954 607.159 13.5658 607.158 13.63C607.143 13.6996 607.135 13.7629 607.134 13.8342C607.14 13.8993 607.154 13.9652 607.167 14.0311C607.188 14.0978 607.209 14.1575 607.245 14.219C607.273 14.2795 607.309 14.3409 607.353 14.3891L607.492 14.5415L607.657 14.6542C607.716 14.69 607.778 14.7117 607.84 14.7334C607.97 14.7777 608.111 14.7947 608.248 14.7826C608.313 14.7762 608.378 14.7697 608.445 14.7492L608.632 14.6788C608.694 14.6434 608.748 14.607 608.803 14.5637C608.857 14.5274 608.906 14.4761 608.948 14.4241C608.99 14.372 609.032 14.3199 609.068 14.2599C609.104 14.1999 609.127 14.1312 609.149 14.0625C609.165 13.9929 609.172 13.9296 609.174 13.8583C609.188 13.7958 609.197 13.7254 609.197 13.6612C609.191 13.5961 609.177 13.5373 609.163 13.4714Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip16)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M610.364 139.628C610.308 139.651 610.243 139.67 610.184 139.679L575.81 143.114C575.213 143.174 574.676 142.734 574.616 142.137L569.989 96.259C569.955 95.973 570.044 95.6832 570.23 95.4596C570.415 95.2359 570.678 95.0916 570.966 95.0646L595.929 92.5483C596.229 92.51 596.533 92.5951 596.773 92.784L607.105 101.392C607.326 101.571 607.471 101.833 607.498 102.121L611.147 138.434C611.174 138.722 611.092 139.01 610.906 139.233C610.772 139.419 610.585 139.557 610.364 139.628ZM576.667 140.833L608.87 137.592L605.369 102.793L595.679 94.774L572.274 97.1299L576.667 140.833Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M587.734 113.227C587.671 113.252 587.613 113.269 587.546 113.28L578.77 114.159C578.175 114.257 577.616 113.848 577.518 113.253C577.42 112.658 577.83 112.099 578.424 112.001C578.469 111.996 578.508 111.993 578.553 111.988L587.329 111.108C587.931 111.063 588.455 111.514 588.501 112.115C588.534 112.619 588.224 113.088 587.738 113.241L587.734 113.227Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M602.236 117.271C602.173 117.297 602.116 117.313 602.049 117.324L579.353 119.604C578.755 119.664 578.219 119.224 578.159 118.627C578.077 118.035 578.498 117.488 579.089 117.407L601.821 115.117C602.424 115.047 602.961 115.487 603.028 116.082C603.086 116.618 602.751 117.11 602.232 117.257L602.236 117.271Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M602.898 123.822C602.835 123.847 602.778 123.863 602.711 123.874L580.015 126.154C579.414 126.231 578.858 125.805 578.782 125.204C578.705 124.603 579.131 124.047 579.732 123.97L602.487 121.681C603.09 121.612 603.627 122.052 603.694 122.647C603.752 123.183 603.419 123.682 602.898 123.822Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M603.443 129.269L603.256 129.322L591.049 130.534C590.444 130.596 589.907 130.156 589.847 129.559C589.787 128.961 590.225 128.417 590.822 128.357L603.032 127.129C603.635 127.06 604.172 127.499 604.239 128.095C604.297 128.631 603.964 129.13 603.443 129.269Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M606.707 103.286L606.498 103.345L599.038 103.899C598.233 103.971 597.427 103.732 596.799 103.218C596.171 102.703 595.764 101.954 595.691 101.143L594.951 93.7971C594.929 93.1965 595.399 92.6825 595.999 92.6605C596.552 92.6363 597.039 93.0355 597.123 93.5798L597.862 100.925C597.882 101.161 598.005 101.375 598.187 101.526C598.37 101.676 598.599 101.744 598.833 101.717L606.334 101.143C606.936 101.098 607.46 101.548 607.506 102.15C607.545 102.676 607.213 103.151 606.707 103.286Z"}})]),_vm._v(" "),_c('g',[_c('path',{staticClass:"accent",attrs:{"d":"M 568.25 18.967 C 568.206 19.012 568.15 19.056 568.095 19.089 L 535.976 36.232 C 535.418 36.531 534.719 36.318 534.42 35.76 L 511.498 -7.092 C 511.349 -7.357 511.32 -7.675 511.411 -7.968 C 511.502 -8.26 511.7 -8.506 511.97 -8.648 L 535.287 -21.12 C 535.564 -21.277 535.895 -21.316 536.205 -21.229 L 549.728 -16.987 C 550.016 -16.902 550.262 -16.704 550.404 -16.434 L 568.534 17.489 C 568.676 17.759 568.711 18.072 568.62 18.365 C 568.564 18.6 568.438 18.809 568.25 18.967 Z M 535.896 33.666 L 565.977 17.582 L 548.599 -14.925 L 535.937 -18.85 L 514.074 -7.16 L 535.896 33.666 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 535.6 2.321 C 535.549 2.371 535.5 2.41 535.439 2.448 L 527.239 6.827 C 526.699 7.162 525.99 6.987 525.655 6.447 C 525.321 5.907 525.496 5.197 526.036 4.863 C 526.078 4.84 526.114 4.822 526.156 4.799 L 534.356 0.419 C 534.924 0.133 535.616 0.362 535.902 0.93 C 536.137 1.408 536.022 1.99 535.61 2.334 L 535.6 2.321 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 551.356 0.442 C 551.305 0.492 551.256 0.531 551.195 0.569 L 529.993 11.899 C 529.434 12.198 528.735 11.985 528.437 11.427 C 528.119 10.883 528.31 10.181 528.854 9.864 L 550.087 -1.491 C 550.647 -1.801 551.346 -1.588 551.651 -1.034 C 551.923 -0.535 551.793 0.078 551.347 0.43 L 551.356 0.442 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 554.631 6.56 C 554.58 6.61 554.53 6.649 554.469 6.686 L 533.267 18.017 C 532.712 18.333 531.999 18.141 531.683 17.586 C 531.368 17.031 531.56 16.318 532.115 16.002 L 553.371 4.639 C 553.931 4.329 554.63 4.542 554.935 5.096 C 555.207 5.595 555.082 6.215 554.631 6.56 Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M 557.348 11.65 L 557.187 11.776 L 545.777 17.856 C 545.213 18.16 544.513 17.947 544.215 17.388 C 543.917 16.83 544.125 16.125 544.683 15.826 L 556.089 9.729 C 556.648 9.419 557.348 9.632 557.652 10.186 C 557.924 10.685 557.799 11.305 557.348 11.65 Z"}})]),_vm._v(" "),_c('g',{attrs:{"clip-path":"url(#clip17)"}},[_c('path',{staticClass:"accent",attrs:{"d":"M566.935 56.4454L566.883 56.6973C566.05 58.9517 563.977 63.3444 561.342 68.4463C560.284 70.5223 559.251 72.4724 558.408 73.9362C557.859 74.917 557.486 75.5652 557.205 75.9822C557.298 76.1704 557.422 76.384 557.523 76.5615C557.692 76.8344 557.845 77.1228 557.971 77.4181C558.23 78.0414 560.493 83.6621 558.984 85.2815C558.521 85.7608 557.817 85.8957 557.208 85.6313C555.388 84.9834 553.626 84.1786 551.937 83.2327C550.868 85.0311 548.341 88.7413 546.619 89.0086C543.188 89.4831 526.797 81.0405 523.499 74.5848C523.285 74.165 522.888 73.3941 525.015 68.4431C528.245 60.9311 538.244 42.6015 542.93 41.2179C543.824 40.9536 546.434 41.1152 553.802 45.1454C557.795 47.3282 562.955 50.6174 565.46 53.2976C566.225 54.1076 567.175 55.2939 566.935 56.4454ZM555.656 75.0278C556.704 73.5127 563.374 60.9414 565.177 56.0732C565.177 56.0732 565.209 55.3165 562.852 53.2802C559.42 50.5072 555.706 48.0948 551.777 46.0819C546.751 43.4462 544.033 42.7491 543.431 42.9304C541.87 43.3876 537.858 48.1588 532.408 57.7867C527.472 66.4516 525.002 72.7202 525.124 73.7905C526.429 76.2773 530.452 79.6426 535.915 82.8008C541.238 85.8798 545.408 87.336 546.376 87.1996C547.344 87.0632 550.197 82.9267 550.778 81.6086C550.979 81.156 551.504 80.9524 551.957 81.1531C551.991 81.1666 552.031 81.1873 552.065 81.2068C553.849 82.2665 555.736 83.16 557.686 83.8726C557.827 83.0447 557.275 80.433 556.291 78.057C556.193 77.8363 556.08 77.6252 555.954 77.4175C555.639 76.9452 555.416 76.4231 555.285 75.8766C555.241 75.5483 555.384 75.2214 555.656 75.0278Z"}}),_vm._v(" "),_c('path',{staticClass:"accent",attrs:{"d":"M550.774 68.1423C550.696 68.4889 550.523 68.8095 550.271 69.0574C550.05 69.2743 544.342 72.1094 544.294 72.1307C540.176 73.8576 537.23 72.2371 536.168 69.8761C535.129 67.4948 536.021 64.7146 538.242 63.3748C537.43 60.8968 537.736 58.7018 539.094 57.295C540.469 55.9544 542.507 55.5528 544.287 56.2736C545.634 56.7542 550.085 58.9948 550.798 67.728C550.807 67.8674 550.803 68.0044 550.774 68.1423ZM537.584 67.1361C537.437 67.8126 537.51 68.516 537.793 69.1443C538.442 70.5997 540.367 71.8263 543.592 70.4766C543.862 70.3514 547.999 68.2653 548.999 67.7351C548.652 63.7711 547.078 59.1447 543.676 57.9423C542.56 57.4659 541.274 57.6862 540.383 58.5081C539.388 59.5526 539.31 61.3445 540.152 63.4659C540.335 63.9107 540.143 64.415 539.711 64.6254C538.645 65.0794 537.856 66.0101 537.584 67.1361Z"}})])])])])};
var __vue_staticRenderFns__$7 = [];

  /* style */
  const __vue_inject_styles__$7 = undefined;
  /* scoped */
  const __vue_scope_id__$7 = undefined;
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$7 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    {},
    __vue_scope_id__$7,
    __vue_is_functional_template__$7,
    __vue_module_identifier__$7,
    false,
    undefined,
    undefined,
    undefined
  );

//

const DEFAULT_PRIMARY_COLOR = 'grey';
const DEFAULT_ACCENT_COLOR = 'white';

var script$5 = {
  name: 'TFormBanner',
  components: {
    TFormBannerSVG: __vue_component__$7,
    TIcon: VIcon$1,
    TIconBtn: __vue_component__$x,
    TMenu: VMenu,
    TTooltip: __vue_component__$Z,
  },
  props: {
    primary: {
      type: String,
      required: false,
      default: DEFAULT_PRIMARY_COLOR,
      validator(value) {
        return !!(value && COLORS[value.toLowerCase()]);
      },
    },
    accent: {
      type: String,
      required: false,
      default: DEFAULT_ACCENT_COLOR,
      validator(value) {
        return !!(value && COLORS[value.toLowerCase()]);
      },
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup(props, { emit }) {
    const svg = ref(null);

    const colors = computed(() => {
      // Note: we need to return the colours in a specific order, hence this explicit Array
      return [
        'black',
        'yellow',
        'indigo',
        'pink',
        'purple',
        'orange',
        'blue',
        'red',
        'teal',
        'grey',
        'green',
        'white',
      ];
    });

    function getHexCodeForColor(color) {
      return COLORS[color] || null;
    }

    function randomizeColors() {
      emit(
        'update:primary',
        sample(without$1(Object.keys(COLORS), props.primary)),
      );
      emit('update:accent', sample(without$1(Object.keys(COLORS), props.accent)));
    }

    onMounted(() => {
      watch(
        () => props.primary,
        () => {
          const color = COLORS[props.primary] || COLORS[DEFAULT_PRIMARY_COLOR];
          svg.value.$el.style.setProperty(
            '--form-primary',
            `${color.toLowerCase()}`,
          );
        },
        {
          immediate: true,
        },
      );

      watch(
        () => props.accent,
        () => {
          const color = COLORS[props.accent] || COLORS[DEFAULT_ACCENT_COLOR];
          svg.value.$el.style.setProperty(
            '--form-accent',
            `${color.toLowerCase()}`,
          );
        },
        {
          immediate: true,
        },
      );
    });

    return {
      icons: {
        mdiCheckBold,
        mdiPaletteOutline,
        mdiShuffleVariant,
      },
      svg,
      colors,
      getHexCodeForColor,
      randomizeColors,
      TOKEN_UI_TEXT_01,
      TOKEN_COLOR_NEUTRAL_50,
    };
  },
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$6 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-block t-z-0 t-rounded-md t-overflow-hidden t-relative t-h-44"},[(!_vm.readonly)?_c('TMenu',{attrs:{"fixed":"","offset-y":"","transition":"slide-y-transition","bottom":"","nudge-left":20,"nudge-bottom":8,"close-on-content-click":false,"content-class":"form-banner-color-popover","z-index":"10"},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var on = ref.on;
var attrs = ref.attrs;
return [_c('TTooltip',{attrs:{"top":"","offset-y":"","allow-overflow":"","nudge-top":0,"fixed":""},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
var tooltipOn = ref.on;
return [_c('TIconBtn',_vm._g(_vm._b({staticClass:"\n              t-absolute\n              t-right-4\n              t-top-4\n              t-shadow-xs\n              t-bg-white\n              t-fill-current\n              t-text-text-01\n            ",attrs:{"data-identifier":"form-banner-color-popover-activator","icon":_vm.icons.mdiPaletteOutline,"color":_vm.TOKEN_UI_TEXT_01}},'TIconBtn',attrs,false),Object.assign({}, on, tooltipOn)))]}}],null,true)},[_vm._v(" "),_c('span',[_vm._v(_vm._s('Customize banner colors'))])])]}}],null,false,3615679230)},[_vm._v(" "),_c('div',{staticClass:"t-flex t-flex-col t-bg-white t-p-3"},[_c('div',{staticClass:"t-flex t-flex-col"},[_c('ul',{staticClass:"t-flex t-items-center t-flex-wrap t-mb-2",style:({
            'max-width': '56px',
          })},[_vm._l((_vm.colors),function(color){return [_c('li',{key:color,staticClass:"\n                t-inline-block\n                t-rounded-full\n                t-w-6\n                t-h-6\n                t-m-0.5\n                t-cursor-pointer\n                t-box-border\n                hover:t-scale-125\n                t-transform t-duration-200 t-linear t-relative\n              ",style:(Object.assign({}, {'background-color': ("" + (_vm.getHexCodeForColor(color)))},
                (color === 'white'
                  ? { border: 'solid 1px var(--token-color-neutral-20)' }
                  : {}))),attrs:{"data-color":color},on:{"click":function($event){return _vm.$emit('update:primary', color)}}},[_c('TIcon',{directives:[{name:"show",rawName:"v-show",value:(_vm.primary === color),expression:"primary === color"}],style:({
                  color:
                    _vm.primary === 'white'
                      ? 'var(--token-color-neutral-50)'
                      : '#FFFFFF',
                  width: '24px',
                  height: '24px',
                  position: 'absolute',
                  top: 'calc(50% - 12px)',
                  left: 'calc(50% - 12px)',
                  padding: '4px',
                }),attrs:{"color":_vm.TOKEN_COLOR_NEUTRAL_50}},[_vm._v("\n                "+_vm._s(_vm.icons.mdiCheckBold)+"\n              ")])],1)]})],2),_vm._v(" "),_c('ul',{staticClass:"t-flex t-items-center t-flex-wrap t-mb-2",style:({
            'max-width': '56px',
          })},[_vm._l((_vm.colors),function(color){return [_c('li',{key:color,staticClass:"\n                t-inline-block\n                t-rounded-full\n                t-w-6\n                t-h-6\n                t-m-0.5\n                t-cursor-pointer\n                t-box-border\n                hover:t-scale-125\n                t-transform t-duration-200 t-linear t-relative\n              ",style:(Object.assign({}, {'border-width': '2px',
                'border-style': 'solid',
                'border-color': ("" + (_vm.getHexCodeForColor(color))),
                'background-color': '#FFFFFF'},
                (color === 'white'
                  ? { border: 'solid 1px var(--token-color-neutral-20)' }
                  : {}))),attrs:{"data-color":color},on:{"click":function($event){return _vm.$emit('update:accent', color)}}},[_c('TIcon',{directives:[{name:"show",rawName:"v-show",value:(_vm.accent === color),expression:"accent === color"}],style:({
                  color: 'var(--token-color-neutral-50)',
                  width: '24px',
                  height: '24px',
                  position: 'absolute',
                  top: 'calc(50% - 12px)',
                  left: 'calc(50% - 12px)',
                  padding: '4px',
                }),attrs:{"color":_vm.TOKEN_COLOR_NEUTRAL_50}},[_vm._v("\n                "+_vm._s(_vm.icons.mdiCheckBold)+"\n              ")])],1)]})],2)]),_vm._v(" "),_c('div',{staticClass:"\n          t-flex\n          t-items-center\n          t-justify-center\n          t-pt-2\n          t-pb-0\n          t-border-t\n          t-border-r-0\n          t-border-b-0\n          t-border-l-0\n          t-border-input\n          t-border-solid\n        "},[_c('TTooltip',{attrs:{"top":"","offset-y":"","allow-overflow":"","nudge-top":0,"fixed":""},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
                var on = ref.on;
return [_c('TIconBtn',_vm._g({staticClass:"t-transform hover:t-scale-125",attrs:{"icon":_vm.icons.mdiShuffleVariant,"data-identifier":"form-banner-color-randomize","color":_vm.TOKEN_UI_TEXT_01},on:{"click":_vm.randomizeColors}},on))]}}],null,false,608803357)},[_vm._v(" "),_c('span',[_vm._v(_vm._s('Randomize colors'))])])],1)])]):_vm._e(),_vm._v(" "),_c('TFormBannerSVG',{ref:"svg",staticClass:"t-w-full t-h-full t-bg-neutral-20"})],1)};
var __vue_staticRenderFns__$6 = [];

  /* style */
  const __vue_inject_styles__$6 = function (inject) {
    if (!inject) return
    inject("data-v-5573618a_0", { source: "svg[data-v-5573618a]{--form-primary:#000000;--form-accent:#ffffff}svg[data-v-5573618a] .primary{fill:var(--form-primary)}svg[data-v-5573618a] .accent{fill:var(--form-accent)}", map: undefined, media: undefined })
,inject("data-v-5573618a_1", { source: ".form-banner-color-popover[data-v-5573618a]{position:fixed}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$6 = "data-v-5573618a";
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$6 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$5,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6,
    __vue_module_identifier__$6,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//

var script$4 = {
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    // Required to work with the max width set on the modal
    modalConfig: {
      type: Object,
      required: true,
    },
  },
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$5 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-fixed t-inset-0 t-overflow-y-auto",class:{
    't-pointer-events-none': !_vm.isVisible,
  }},[_c('div',{staticClass:"\n      t-flex\n      t-items-end\n      t-justify-center\n      t-min-h-screen\n      t-pt-4\n      t-px-4\n      t-pb-20\n      t-text-center\n    ",class:_vm.modalConfig.containerClasses},[_c('transition',{attrs:{"enter-active-class":"t-duration-300 t-ease-out","enter-class":"t-opacity-0","enter-to-class":"t-opacity-100","leave-active-class":"t-duration-200 t-ease-in","leave-class":"t-opacity-100","leave-to-class":"t-opacity-0"}},[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.isVisible),expression:"isVisible"}],staticClass:"t-fixed t-inset-0 t-transition-opacity opacity-100"},[_c('div',{staticClass:"t-absolute t-inset-0 t-bg-overlay",on:{"click":function($event){if($event.target !== $event.currentTarget){ return null; }return _vm.$emit('click-backdrop')}}})])]),_vm._v(" "),_c('span',{staticClass:"t-hidden",class:_vm.modalConfig.centeringElementClasses,attrs:{"aria-hidden":"true"}},[_vm._v("\n      ​\n    ")]),_vm._v(" "),_vm._t("default")],2)])};
var __vue_staticRenderFns__$5 = [];

  /* style */
  const __vue_inject_styles__$5 = undefined;
  /* scoped */
  const __vue_scope_id__$5 = undefined;
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$5 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$4,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$5,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script$3 = defineComponent({
  components: { TIconBtn: __vue_component__$x, TTooltip: __vue_component__$Z },
  props: {
    heading: { type: String, default: '' },
    description: { type: String, default: '' },
    infoTooltipHtml: { type: String, default: null },
  },
  setup() {
    return {
      icons: {
        mdiInformationOutline,
      },
    };
  },
});

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$4 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-py-7 t-px-8 sm:t-py-14 sm:t-px-30",class:{
    't-text-center': _vm.$scopedSlots.headerSlate,
  }},[_vm._t("headerSlate"),_vm._v(" "),(_vm.heading)?_c('h3',{staticClass:"t-text-md t-font-semibold t-leading-6 t-text-text-01 t-mb-1",class:{
      't-mt-4': _vm.$scopedSlots.headerSlate,
    },attrs:{"id":"modal-headline"}},[_vm._v("\n    "+_vm._s(_vm.heading)+"\n  ")]):_vm._e(),_vm._v(" "),(_vm.description)?_c('p',{staticClass:"t-text-xs t-text-text-02 t-inline-block"},[_c('span',[_vm._v(_vm._s(_vm.description))]),_vm._v(" "),(_vm.infoTooltipHtml)?_c('TTooltip',{attrs:{"top":""},scopedSlots:_vm._u([{key:"activator",fn:function(ref){
    var on = ref.on;
    var attrs = ref.attrs;
return [_c('TIconBtn',_vm._g(_vm._b({staticClass:"t-text-text-01",attrs:{"icon":_vm.icons.mdiInformationOutline,"x-small":""}},'TIconBtn',attrs,false),on))]}}],null,false,1661291694)},[_vm._v(" "),_c('div',{domProps:{"innerHTML":_vm._s(_vm.infoTooltipHtml)}})]):_vm._e()],1):_vm._e()],2)};
var __vue_staticRenderFns__$4 = [];

  /* style */
  const __vue_inject_styles__$4 = undefined;
  /* scoped */
  const __vue_scope_id__$4 = undefined;
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$4 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$3,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4,
    __vue_module_identifier__$4,
    false,
    undefined,
    undefined,
    undefined
  );

//

const modalConfigMap = {
  xs: {
    modalClasses: 't-align-bottom sm:t-my-14 sm:t-align-middle t-max-w-xs',
    containerClasses: 'sm:t-block sm:t-p-0',
    centeringElementClasses:
      'sm:t-inline-block sm:t-align-middle sm:t-h-screen',
  },
  sm: {
    modalClasses: 't-align-bottom md:t-my-14 md:t-align-middle md:t-max-w-lg',
    containerClasses: 'md:t-block md:t-p-0',
    centeringElementClasses:
      'md:t-inline-block md:t-align-middle md:t-h-screen',
  },
  md: {
    modalClasses: 't-align-bottom lg:t-my-14 lg:t-align-middle lg:t-max-w-3xl',
    containerClasses: 'lg:t-block lg:t-p-0',
    centeringElementClasses:
      'lg:t-inline-block lg:t-align-middle lg:t-h-screen',
  },
  lg: {
    modalClasses: 't-align-bottom lg:t-my-14 lg:t-align-middle lg:t-max-w-4xl',
    containerClasses: 'lg:t-block lg:t-p-0',
    centeringElementClasses:
      'lg:t-inline-block lg:t-align-middle lg:t-h-screen',
  },
  xl: {
    modalClasses: 'lg:t-my-14 lg:t-align-middle lg:t-max-w-4xl xl:t-max-w-5xl',
    containerClasses: 'lg:t-block lg:t-p-0',
    centeringElementClasses:
      'lg:t-inline-block lg:t-align-middle lg:t-h-screen',
  },
  xxl: {
    modalClasses:
      't-modal-dialog--xxl t-align-middle t-max-w-8xl t-h-screen-xl',
    containerClasses: 'lg:t-block lg:t-p-0',
    centeringElementClasses: '',
  },
};

var script$2 = defineComponent({
  components: {
    TModalDialogContainer: __vue_component__$5,
    TModalDialogHeader: __vue_component__$4,
    TIconBtn: __vue_component__$x,
  },
  props: {
    /**
     * The modal heading.
     */
    heading: { type: String, default: '' },

    /**
     * The modal description.
     */
    description: { type: String, default: '' },

    /**
     * Whether the user can close the modal or not.
     */
    persistent: { type: Boolean, default: false },

    /**
     * This triggers the visibilty of the modal.
     */
    isVisible: { type: Boolean, default: false },

    /**
     * A tooltip to show more info about the modal.
     */
    infoTooltipHtml: { type: String, default: null },

    /**
     * Whether the close button is shown or not
     */
    shouldShowCloseBtn: { type: Boolean, default: true },

    /**
     * The size of the modal
     */
    size: {
      type: String,
      default: 'md',
      validator: (size) => Object.keys(modalConfigMap).includes(size),
    },
  },
  setup(props, context) {
    const modalConfig = computed(() => modalConfigMap[props.size]);

    const closeModal = (canClosePersistent = false) => {
      if (!props.persistent || canClosePersistent) {
        context.emit('close');
      }
    };

    const handleEscKey = (event) => {
      // TODO: Check if foremost modal
      if (event.code === 'Escape') {
        closeModal();
      }
    };

    useEventListener(document, 'keyup', handleEscKey, { passive: true });

    return {
      closeModal,
      icons: {
        mdiClose,
      },
      modalConfig,
    };
  },
});

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$3 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TModalDialogContainer',{attrs:{"modal-config":_vm.modalConfig,"is-visible":_vm.isVisible},on:{"click-backdrop":_vm.closeModal}},[_c('transition',{attrs:{"enter-active-class":"t-duration-300 t-ease-out","enter-class":"t-translate-y-4 t-opacity-0","enter-to-class":"t-translate-y-0 t-opacity-100","leave-active-class":"t-duration-200 t-ease-in","leave-class":"t-translate-y-0 t-opacity-100","leave-to-class":"t-translate-y-4 t-opacity-0"}},[_c('div',{directives:[{name:"show",rawName:"v-show",value:(_vm.isVisible),expression:"isVisible"}],staticClass:"\n        t-inline-block\n        t-bg-white\n        t-rounded-md\n        t-text-left\n        t-overflow-hidden\n        t-shadow-lg\n        t-transform\n        t-transition-all\n        t-w-full\n      ",class:_vm.modalConfig.modalClasses,attrs:{"role":"dialog","aria-modal":"true","aria-labelledby":"modal-headline"}},[_c('div',{staticClass:"t-bg-white t-h-full"},[(!_vm.persistent && _vm.shouldShowCloseBtn)?_c('TIconBtn',{staticClass:"t-absolute t-top-3 t-right-3",attrs:{"icon":_vm.icons.mdiClose,"small":""},on:{"click":_vm.closeModal}}):_vm._e(),_vm._v(" "),_vm._t("header",function(){return [(_vm.heading || _vm.description)?_c('TModalDialogHeader',{attrs:{"heading":_vm.heading,"description":_vm.description,"info-tooltip-html":_vm.infoTooltipHtml},scopedSlots:_vm._u([(_vm.$scopedSlots.headerSlate)?{key:"headerSlate",fn:function(){return [_vm._t("headerSlate")]},proxy:true}:null],null,true)}):_vm._e()]}),_vm._v(" "),_vm._t("default",null,null,{
            closeModal: _vm.closeModal,
            persistent: _vm.persistent,
          })],2)])])],1)};
var __vue_staticRenderFns__$3 = [];

  /* style */
  const __vue_inject_styles__$3 = function (inject) {
    if (!inject) return
    inject("data-v-2e921994_0", { source: ".t-modal-dialog--xxl[data-v-2e921994]{height:98vh;margin-top:.5vh}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$3 = "data-v-2e921994";
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$3 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$2,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3,
    __vue_module_identifier__$3,
    false,
    createInjector,
    undefined,
    undefined
  );

//

const dialogTypes = ['information', 'warning'];

var script$1 = {
  name: 'TDialog',
  components: {
    TBtn: __vue_component__$_,
    TIcon: VIcon$1,
    TModalDialog: __vue_component__$3,
  },
  props: {
    confirmButtonText: {
      type: String,
      default: 'Confirm',
    },
    message: {
      type: String,
      required: true,
    },
    heading: {
      type: String,
      required: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    rejectButtonText: {
      type: String,
      default: 'Cancel',
    },
    type: {
      type: String,
      default: 'informative',
      validator: (type) => dialogTypes.includes(type),
    },
    /**
     * The size of the modal
     */
    size: {
      type: String,
      default: 'sm',
    },
  },
  setup(props, { emit }) {
    const dialogColor = computed(() =>
      props.type === 'warning' ? 'error' : 'primary',
    );

    const icon = computed(() =>
      props.type === 'warning' ? mdiAlertCircleOutline : mdiInformationOutline,
    );

    const reject = () => {
      emit('reject');
    };

    const confirm = () => {
      emit('confirm');
    };

    return { dialogColor, reject, confirm, icon };
  },
};

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$2 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('TModalDialog',{attrs:{"size":_vm.size,"is-visible":_vm.isVisible},on:{"close":_vm.reject},scopedSlots:_vm._u([{key:"header",fn:function(){return undefined},proxy:true}])},[_vm._v(" "),_c('div',{staticClass:"t-p-6"},[_c('div',{staticClass:"t-mr-4 t-mb-6 t-flex"},[_c('TIcon',{staticClass:"t-mr-2 t-self-start t-pt-1",attrs:{"color":_vm.dialogColor}},[_vm._v("\n        "+_vm._s(_vm.icon)+"\n      ")]),_vm._v(" "),_c('h4',{staticClass:"t-text-h-04 t-font-semibold"},[_vm._v(_vm._s(_vm.heading))])],1),_vm._v(" "),_c('div',{staticClass:"t-ml-8"},[_c('div',{staticClass:"t-text-default t-mb-6 t-leading-relaxed"},[_c('p',{domProps:{"innerHTML":_vm._s(_vm.message)}})]),_vm._v(" "),_c('div',{staticClass:"t-flex t-justify-end"},[_c('TBtn',{attrs:{"large":"","text":""},on:{"click":_vm.reject}},[_vm._v(_vm._s(_vm.rejectButtonText))]),_vm._v(" "),_c('TBtn',{staticClass:"t-ml-2",attrs:{"color":_vm.dialogColor,"large":""},on:{"click":_vm.confirm}},[_vm._v("\n          "+_vm._s(_vm.confirmButtonText)+"\n        ")])],1)])])])};
var __vue_staticRenderFns__$2 = [];

  /* style */
  const __vue_inject_styles__$2 = undefined;
  /* scoped */
  const __vue_scope_id__$2 = undefined;
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$2 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$1,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    false,
    undefined,
    undefined,
    undefined
  );

/* script */

/* template */
var __vue_render__$1 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-px-8 sm:t-px-30"},[_vm._t("default")],2)};
var __vue_staticRenderFns__$1 = [];

  /* style */
  const __vue_inject_styles__$1 = undefined;
  /* scoped */
  const __vue_scope_id__$1 = undefined;
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__$1 = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    {},
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    undefined,
    undefined,
    undefined
  );

//

var script = defineComponent({
  components: {
    TBtn: __vue_component__$_,
  },
  props: {
    // TODO: i18n
    cancelText: {
      type: String,
      default: 'Cancel',
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    submitText: {
      type: String,
      default: 'Submit',
    },
    // Vuelidate object
    v$: {
      type: Object,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const isSubmitDisabled = computed(() => props.v$?.$invalid ?? false);

    return {
      isSubmitDisabled,
    };
  },
});

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"t-flex t-justify-between t-px-6 t-pt-8 t-pb-4"},[(!_vm.persistent)?_vm._t("cancel-button",function(){return [_c('TBtn',{attrs:{"elevation":0,"large":"","text":"","disabled":_vm.isLoading},on:{"click":function($event){return _vm.$emit('cancel')}}},[_vm._v("\n      "+_vm._s(_vm.cancelText)+"\n    ")])]}):_vm._e(),_vm._v(" "),_vm._t("submit-button",function(){return [_c('TBtn',{staticClass:"t-ml-auto",attrs:{"color":"primary","large":"","type":"submit","loading":_vm.isLoading,"disabled":_vm.isLoading,"visually-disabled":_vm.isSubmitDisabled,"elevation":0}},[_vm._v("\n      "+_vm._s(_vm.submitText)+"\n    ")])]})],2)};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

export { __vue_component__$Y as AppBase, __vue_component__$W as AppBody, __vue_component__$T as AppFilters, __vue_component__$S as AppFooter, __vue_component__$X as AppHeader, __vue_component__$V as AppPageContent, __vue_component__$U as AppSidebar, __vue_component__$O as GridBlock, __vue_component__$P as GridContainer, __vue_component__$N as GridLayout, Status, __vue_component__$$ as TAlert, VApp as TApp, VAppBar as TAppBar, VAppBarNavIcon as TAppBarNavIcon, VAppBarTitle as TAppBarTitle, VAutocomplete as TAutocomplete, __vue_component__$t as TAvatar, __vue_component__$s as TAvatarStack, VBadge as TBadge, VBanner as TBanner, __vue_component__$d as TBaseCard, __vue_component__$v as TBlankSlate, VBottomNavigation as TBottomNavigation, VBottomSheet as TBottomSheet, VBreadcrumbs as TBreadcrumbs, VBreadcrumbsDivider as TBreadcrumbsDivider, VBreadcrumbsItem as TBreadcrumbsItem, __vue_component__$_ as TBtn, VBtnToggle as TBtnToggle, VCalendar as TCalendar, VCalendarCategory as TCalendarCategory, VCalendarDaily as TCalendarDaily, VCalendarMonthly as TCalendarMonthly, VCalendarWeekly as TCalendarWeekly, VCard as TCard, VCardActions as TCardActions, VCardSubtitle as TCardSubtitle, VCardText as TCardText, VCardTitle as TCardTitle, VCarousel as TCarousel, VCarouselItem as TCarouselItem, VCarouselReverseTransition as TCarouselReverseTransition, VCarouselTransition as TCarouselTransition, VCheckbox as TCheckbox, VChip as TChip, VChipGroup as TChipGroup, VCol as TCol, VColorPicker as TColorPicker, VColorPickerCanvas as TColorPickerCanvas, VColorPickerSwatches as TColorPickerSwatches, VCombobox as TCombobox, VContainer as TContainer, VContent as TContent, VCounter as TCounter, __vue_component__$r as TCurrencyTextField, VData as TData, VDataFooter as TDataFooter, VDataIterator as TDataIterator, __vue_component__$y as TDataTable, __vue_component__$I as TDataTableBodyCell, VDataTableHeader as TDataTableHeader, __vue_component__$H as TDataTableHeaderCell, VDatePicker as TDatePicker, VDatePickerDateTable as TDatePickerDateTable, VDatePickerHeader as TDatePickerHeader, VDatePickerMonthTable as TDatePickerMonthTable, VDatePickerTitle as TDatePickerTitle, VDatePickerYears as TDatePickerYears, __vue_component__$2 as TDialog, VDialogBottomTransition as TDialogBottomTransition, VDialogTopTransition as TDialogTopTransition, VDialogTransition as TDialogTransition, VDivider as TDivider, VEditDialog as TEditDialog, VExpandTransition as TExpandTransition, VExpandXTransition as TExpandXTransition, VExpansionPanel as TExpansionPanel, VExpansionPanelContent as TExpansionPanelContent, VExpansionPanelHeader as TExpansionPanelHeader, VExpansionPanels as TExpansionPanels, VFabTransition as TFabTransition, VFadeTransition as TFadeTransition, VFileInput as TFileInput, __vue_component__$e as TFilterSection, VFlex as TFlex, VFooter as TFooter, VForm as TForm, __vue_component__$6 as TFormBanner, __vue_component__$8 as TFormOutput, __vue_component__$w as THeader, __vue_component__$q as THeaderActionButton, __vue_component__$p as THeaderMoreButton, __vue_component__$o as THeaderViewsButton, VHover as THover, VIcon$1 as TIcon, __vue_component__$x as TIconBtn, VImg as TImg, VInput as TInput, VItem as TItem, VItemGroup as TItemGroup, VLabel as TLabel, VLayout as TLayout, VLazy as TLazy, __vue_component__$u as TLetterSelect, VList as TList, VListGroup as TListGroup, VListItem as TListItem, VListItemAction as TListItemAction, VListItemActionText as TListItemActionText, VListItemAvatar as TListItemAvatar, VListItemContent as TListItemContent, VListItemGroup as TListItemGroup, VListItemIcon as TListItemIcon, VListItemSubtitle as TListItemSubtitle, VListItemTitle as TListItemTitle, __vue_component__$R as TLogoSVG, VMain as TMain, VMenu as TMenu, VMenuTransition as TMenuTransition, VMessages as TMessages, __vue_component__$3 as TModalDialog, __vue_component__$1 as TModalDialogContent, __vue_component__ as TModalDialogFooter, __vue_component__$h as TMp4, VNavigationDrawer as TNavigationDrawer, VOverflowBtn as TOverflowBtn, VOverlay as TOverlay, VPagination as TPagination, VParallax as TParallax, __vue_component__$m as TPeoplePicker, VPicker as TPicker, VProgressCircular as TProgressCircular, VProgressLinear as TProgressLinear, VRadio as TRadio, VRadioGroup as TRadioGroup, VRangeSlider as TRangeSlider, VRating as TRating, VResponsive as TResponsive, VRow as TRow, VScaleTransition as TScaleTransition, VScrollXReverseTransition as TScrollXReverseTransition, VScrollXTransition as TScrollXTransition, VScrollYReverseTransition as TScrollYReverseTransition, VScrollYTransition as TScrollYTransition, __vue_component__$l as TSearchInput, __vue_component__$k as TSectionHeader, __vue_component__$j as TSectionHeaderSelecting, VSelect as TSelect, VSheet as TSheet, VSimpleCheckbox as TSimpleCheckbox, VSimpleTable as TSimpleTable, VSkeletonLoader as TSkeletonLoader, VSlideGroup as TSlideGroup, VSlideItem as TSlideItem, VSlideXReverseTransition as TSlideXReverseTransition, VSlideXTransition as TSlideXTransition, VSlideYReverseTransition as TSlideYReverseTransition, VSlideYTransition as TSlideYTransition, VSlider as TSlider, VSnackbar as TSnackbar, __vue_component__$i as TSortSelect, VSpacer as TSpacer, VSparkline as TSparkline, VSpeedDial as TSpeedDial, VStepper as TStepper, VStepperContent as TStepperContent, VStepperHeader as TStepperHeader, VStepperItems as TStepperItems, VStepperStep as TStepperStep, VSubheader as TSubheader, VSwitch as TSwitch, VSystemBar as TSystemBar, VTab as TTab, VTabItem as TTabItem, VTabReverseTransition as TTabReverseTransition, VTabTransition as TTabTransition, VTableOverflow as TTableOverflow, VTabs as TTabs, VTabsItems as TTabsItems, VTabsSlider as TTabsSlider, VTextField as TTextField, VTextarea as TTextarea, VThemeProvider as TThemeProvider, VTimePicker as TTimePicker, VTimePickerClock as TTimePickerClock, VTimePickerTitle as TTimePickerTitle, VTimeline as TTimeline, VTimelineItem as TTimelineItem, VToolbar as TToolbar, VToolbarItems as TToolbarItems, VToolbarTitle as TToolbarTitle, __vue_component__$Z as TTooltip, VTreeview as TTreeview, VTreeviewNode$1 as TTreeviewNode, __vue_component__$J as TTruncateText, __vue_component__$g as TUpload, __vue_component__$f as TViewMorePopover, VVirtualScroll as TVirtualScroll, VVirtualTable as TVirtualTable, VWindow as TWindow, VWindowItem as TWindowItem, TableHeaderAlignments, __vue_component__$Q as TheLayout, teamworkVuetify as teamworkUI, usePaginatedTable, useTable, useTableHeader, useTableHeaderCheckbox, useTableHeaderOptions, useTableHeaders, useTableItems, useTableSortableHeader };
